import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { supabase } from "./supabaseClient";
import emailjs from "@emailjs/browser";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from "recharts";

const COLORS = {
  teal:   { bg: "#DCFCE7", text: "#166534", mid: "#16A34A" },
  blue:   { bg: "#EBF2FF", text: "#1E3A5F", mid: "#2E5FA3" },
  purple: { bg: "#EEEDFE", text: "#534AB7", mid: "#7F77DD" },
  amber:  { bg: "#FEF3C7", text: "#92400E", mid: "#D97706" },
  coral:  { bg: "#FEE2E2", text: "#991B1B", mid: "#DC2626" },
  gray:   { bg: "#F1F5F9", text: "#475569", mid: "#64748B" },
};


const WAITLIST_MODE = process.env.REACT_APP_WAITLIST_MODE === "true";

const fmt = (n) => "£" + Number(n).toLocaleString();

// Comma-format a number for display in inputs (digits only → "200,000")
function fmtInput(val) {
  const raw = String(val).replace(/[^0-9]/g, "");
  if (!raw) return "";
  return Number(raw).toLocaleString("en-GB");
}
// Strip commas/non-digits before sending to API or parsing
function parseInput(val) {
  return String(val).replace(/[^0-9]/g, "");
}

// ─── SAVED PROFILES (localStorage) ───────────────────────────────────────────
function getSavedProfiles(userId) {
  try { return JSON.parse(localStorage.getItem(`lb_saved_${userId}`) || "[]"); } catch { return []; }
}
function setSavedProfiles(userId, list) {
  localStorage.setItem(`lb_saved_${userId}`, JSON.stringify(list));
}
function isSaved(userId, targetUserId) {
  return getSavedProfiles(userId).some(p => p.user_id === targetUserId);
}
function toggleSavedProfile(userId, profile) {
  const list = getSavedProfiles(userId);
  const idx  = list.findIndex(p => p.user_id === profile.user_id);
  if (idx >= 0) { list.splice(idx, 1); } else { list.unshift({ ...profile, saved_at: Date.now() }); }
  setSavedProfiles(userId, list);
  return idx < 0; // true = now saved
}

// ─── FILTER PRESETS (localStorage) ───────────────────────────────────────────
function getFilterPresets(userId, type) {
  try { return JSON.parse(localStorage.getItem(`lb_presets_${type}_${userId}`) || "[]"); } catch { return []; }
}
function saveFilterPreset(userId, type, name, filters) {
  const list = getFilterPresets(userId, type).filter(p => p.name !== name);
  list.unshift({ name, filters, saved_at: Date.now() });
  localStorage.setItem(`lb_presets_${type}_${userId}`, JSON.stringify(list.slice(0, 5)));
}
function deleteFilterPreset(userId, type, name) {
  const list = getFilterPresets(userId, type).filter(p => p.name !== name);
  localStorage.setItem(`lb_presets_${type}_${userId}`, JSON.stringify(list));
}
const fmtId = (n) => n ? "#" + String(n).padStart(4, "0") : "";

function HeartButton({ userId, targetUserId, profileSnap, size = 18 }) {
  const [saved, setSaved] = useState(() => userId ? isSaved(userId, targetUserId) : false);
  if (!userId || !targetUserId) return null;
  return (
    <button
      onClick={e => { e.stopPropagation(); setSaved(toggleSavedProfile(userId, profileSnap)); }}
      title={saved ? "Remove from saved" : "Save profile"}
      style={{ background: "none", border: "none", cursor: "pointer", padding: 4, lineHeight: 1, flexShrink: 0, color: saved ? "#DC2626" : "#cbd5e1" }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill={saved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
      </svg>
    </button>
  );
}

// ─── COMPATIBILITY SCORING ────────────────────────────────────────────────────

const SPEC_TO_PROJECTS = {
  "Developer":              ["Residential", "Commercial", "Mixed use"],
  "Residential developer":  ["Residential"],
  "Commercial developer":   ["Commercial", "Mixed use"],
  "Renovation specialist":  ["Renovation", "Residential"],
  "Mixed use developer":    ["Mixed use", "Residential", "Commercial"],
  "HMO specialist":         ["Residential", "Renovation"],
};

function ukRegion(location = "") {
  const l = location.toLowerCase();
  if (!l) return null;
  if (/london|hackney|camden|islington|lambeth|southwark|tower hamlets|wandsworth|westminster|croydon|bromley/.test(l)) return "London";
  if (/manchester|salford|bolton|stockport|oldham|rochdale|bury|wigan|tameside/.test(l)) return "Greater Manchester";
  if (/birmingham|coventry|wolverhampton|walsall|dudley|sandwell|solihull/.test(l)) return "West Midlands";
  if (/leeds|bradford|sheffield|hull|york|harrogate|wakefield|doncaster|rotherham/.test(l)) return "Yorkshire";
  if (/liverpool|wirral|sefton|knowsley/.test(l)) return "Merseyside";
  if (/newcastle|sunderland|middlesbrough|durham|gateshead|hartlepool/.test(l)) return "North East";
  if (/nottingham|leicester|derby|lincoln|northampton/.test(l)) return "East Midlands";
  if (/norwich|cambridge|ipswich|colchester|chelmsford/.test(l)) return "East of England";
  if (/brighton|southampton|portsmouth|reading|oxford|guildford|crawley|maidstone/.test(l)) return "South East";
  if (/bristol|bath|swindon|gloucester|exeter|plymouth|truro|bournemouth|dorset/.test(l)) return "South West";
  if (/edinburgh|glasgow|aberdeen|dundee|inverness/.test(l)) return "Scotland";
  if (/cardiff|swansea|newport|bangor/.test(l)) return "Wales";
  if (/belfast|derry|londonderry/.test(l)) return "Northern Ireland";
  return "Other";
}

function inRegionBucket(location, bucket) {
  const r = ukRegion(location || "");
  if (bucket === "London")   return r === "London";
  if (bucket === "North")    return ["Greater Manchester", "Merseyside", "North East", "Yorkshire"].includes(r);
  if (bucket === "Midlands") return ["West Midlands", "East Midlands"].includes(r);
  if (bucket === "South")    return ["South East", "South West", "East of England"].includes(r);
  if (bucket === "Scotland") return r === "Scotland";
  if (bucket === "Wales")    return r === "Wales";
  return false;
}

function activityScoreCalc(updated_at) {
  if (!updated_at) return { pts: 10, detail: "Activity unknown" };
  const days = (Date.now() - new Date(updated_at).getTime()) / 86400000;
  if (days <= 7)   return { pts: 20, detail: "Active this week" };
  if (days <= 30)  return { pts: 16, detail: "Active this month" };
  if (days <= 90)  return { pts: 10, detail: "Active in last 3 months" };
  if (days <= 180) return { pts: 5,  detail: "Active in last 6 months" };
  return { pts: 0, detail: "Not recently active" };
}

// ─── PROFILE COMPLETENESS ────────────────────────────────────────────────────

function computeProfileCompleteness(data, role) {
  const items = [
    { label: "Profile photo",  done: !!data.avatar_url,         weight: 20 },
    { label: "Bio",            done: !!(data.bio?.trim()),       weight: 15 },
    { label: "Location",       done: !!(data.location?.trim()),  weight: 10 },
  ];

  if (role === "lender") {
    items.push(
      { label: "Budget & return type",  done: !!(data.budget_max && data.return_type),       weight: 25 },
      { label: "Preferred projects",    done: !!(data.preferred_projects?.length > 0),        weight: 10 },
    );
  } else if (role === "builder") {
    items.push(
      { label: "Specialisation",        done: !!(data.specialization),                        weight: 20 },
      { label: "Projects completed",    done: !!(data.projects_completed > 0),                weight: 15 },
      { label: "Site photos",           done: !!(data.verified_documents?.includes("site_photos")), weight: 10 },
    );
  }

  const maxPossible = items.reduce((s, i) => s + i.weight, 0);
  const earned      = items.filter(i => i.done).reduce((s, i) => s + i.weight, 0);
  const score       = Math.round((earned / maxPossible) * 100);
  const missing     = items.filter(i => !i.done);
  return { score, items, missing };
}

function computeCredibilityScore(builder) {
  const docs = builder.verified_documents || [];
  let score = 0;
  if (docs.includes("photo_id"))                      score += 20;
  if (docs.includes("companies_house"))               score += 15;
  if (docs.includes("planning"))                      score += 15;
  if (docs.includes("insurance") || docs.includes("site_insurance")) score += 10;
  const p = builder.props || 0;
  if (p >= 10) score += 25; else if (p >= 6) score += 20; else if (p >= 1) score += 10;
  // Reviews: up to 15 pts based on count + avg rating
  const reviews = builder.reviews || [];
  if (reviews.length > 0) {
    const avg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
    if (reviews.length >= 5 && avg >= 4.5) score += 15;
    else if (reviews.length >= 3 && avg >= 4.0) score += 10;
    else if (reviews.length >= 1 && avg >= 3.5) score += 5;
  }
  return Math.min(score, 100);
}

// ─── DARK MODE HOOK ───────────────────────────────────────────────────────────
// ─── AUTH-GATED PREFERENCE HELPERS ───────────────────────────────────────────
// lb_logged_in is set when a session is confirmed and cleared on sign-out.
// Preference hooks check this flag so they start from defaults when no user is
// present, avoiding any flash of a previous user's theme on the logged-out site.
function getIsLoggedIn() {
  try { return localStorage.getItem("lb_logged_in") === "true"; } catch { return false; }
}
function setIsLoggedIn(val) {
  try {
    if (val) localStorage.setItem("lb_logged_in", "true");
    else localStorage.removeItem("lb_logged_in");
  } catch {}
}
function clearPreferenceStorage() {
  try {
    localStorage.removeItem("lb_accent_colour");
    localStorage.removeItem("lb_dark_mode");
  } catch {}
}

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      if (!getIsLoggedIn()) return false;
      const stored = localStorage.getItem("lb_dark_mode");
      if (stored !== null) return stored === "true";
    } catch (_) {}
    return false;
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    try { localStorage.setItem("lb_dark_mode", String(dark)); } catch (_) {}
  }, [dark]);
  return [dark, setDark];
}

const ACCENT_PRESETS = [
  { label: "Deep Blue",    value: "#3B82F6", bg: "#EBF2FF", nav: "#1E3A5F" },
  { label: "Forest Green", value: "#16A34A", bg: "#DCFCE7", nav: "#14532D" },
  { label: "Royal Purple", value: "#7C3AED", bg: "#EDE9FE", nav: "#4C1D95" },
  { label: "Burnt Orange", value: "#EA580C", bg: "#FFEDD5", nav: "#7C2D12" },
  { label: "Rose Pink",    value: "#DB2777", bg: "#FCE7F3", nav: "#831843" },
  { label: "Teal",         value: "#0D9488", bg: "#CCFBF1", nav: "#134E4A" },
  { label: "Crimson Red",  value: "#DC2626", bg: "#FEE2E2", nav: "#7F1D1D" },
  { label: "Slate Grey",   value: "#475569", bg: "#F1F5F9", nav: "#1E293B" },
];

function useAccentColor() {
  const [accent, setAccent] = useState(() => {
    try {
      if (!getIsLoggedIn()) return "#3B82F6";
      return localStorage.getItem("lb_accent_colour") || "#3B82F6";
    } catch { return "#3B82F6"; }
  });
  useEffect(() => {
    const preset = ACCENT_PRESETS.find(p => p.value === accent) || ACCENT_PRESETS[0];
    document.documentElement.style.setProperty("--accent", preset.value);
    document.documentElement.style.setProperty("--color-primary", preset.value);
    document.documentElement.style.setProperty("--accent-bg", preset.bg);
    document.documentElement.style.setProperty("--color-primary-bg", preset.bg);
    document.documentElement.style.setProperty("--nav-bg", preset.nav || "#1E3A5F");
    let el = document.getElementById("lb-accent-style");
    if (!el) { el = document.createElement("style"); el.id = "lb-accent-style"; document.head.appendChild(el); }
    const p = preset.value, bg = preset.bg, nav = preset.nav || "#1E3A5F";
    // Include both hex and rgb() selectors: Chrome normalises hex colours to
    // rgb() when styles are set via JavaScript, so both forms are needed.
    el.textContent = `
      /* Primary buttons and active elements */
      [style*="background: #3B82F6"]:not(#lb-accent-style):not([data-color-swatch]) { background: ${p} !important; }
      [style*="background: rgb(59, 130, 246)"]:not(#lb-accent-style):not([data-color-swatch]) { background: ${p} !important; }
      [style*="background: #2E5FA3"]:not(nav):not(nav *) { background: ${p} !important; }
      [style*="background: rgb(46, 95, 163)"]:not(nav):not(nav *) { background: ${p} !important; }
      /* Accent background tints */
      [style*="background: #EBF2FF"] { background: ${bg} !important; }
      [style*="background: rgb(235, 242, 255)"] { background: ${bg} !important; }
      /* Text colours */
      [style*="color: #3B82F6"] { color: ${p} !important; }
      [style*="color: rgb(59, 130, 246)"] { color: ${p} !important; }
      [style*="color: #2E5FA3"] { color: ${p} !important; }
      [style*="color: rgb(46, 95, 163)"] { color: ${p} !important; }
      /* Border colours — shorthand and longhand */
      [style*="borderColor: #3B82F6"], [style*="border-color: #3B82F6"] { border-color: ${p} !important; }
      [style*="border-color: rgb(59, 130, 246)"] { border-color: ${p} !important; }
      [style*="border: 2px solid #3B82F6"] { border-color: ${p} !important; }
      [style*="border: 1.5px solid #3B82F6"] { border-color: ${p} !important; }
      [style*="border: 0.5px solid #3B82F6"] { border-color: ${p} !important; }
      [style*="border: 1px solid #3B82F6"] { border-color: ${p} !important; }
      /* Accent-tinted borders */
      [style*="border: 0.5px solid #BFDBFE"] { border-color: ${p}55 !important; }
      [style*="border: 0.5px solid #A8DFC9"] { border-color: ${p}55 !important; }
      /* Shadows and outlines */
      [style*="boxShadow"][style*="#3B82F6"]:not([data-color-swatch]) { box-shadow: 0 0 0 3px ${p}55 !important; }
      [style*="box-shadow"][style*="59, 130, 246"]:not([data-color-swatch]) { box-shadow: 0 0 0 3px ${p}55 !important; }
      [style*="outline"][style*="#3B82F6"] { outline-color: ${p} !important; }
      [style*="outline"][style*="rgb(59, 130, 246)"] { outline-color: ${p} !important; }
      /* SVG attribute overrides */
      [stroke="#3B82F6"] { stroke: ${p} !important; }
      [stroke="#2E5FA3"] { stroke: ${p} !important; }
      [fill="#3B82F6"] { fill: ${p} !important; }
      [fill="#EBF2FF"] { fill: ${bg} !important; }
      /* Progress bars and charts */
      .recharts-bar-rectangle path { fill: ${p} !important; }
      .recharts-line path { stroke: ${p} !important; }
      .recharts-active-dot circle { fill: ${p} !important; }
      /* Checkbox/radio accent */
      input[type="checkbox"], input[type="radio"] { accent-color: ${p} !important; }
      [style*="accentColor: \\"#3B82F6\\""] { accent-color: ${p} !important; }
      /* Input focus ring */
      input:focus, textarea:focus, select:focus { box-shadow: 0 0 0 3px ${p}44 !important; border-color: ${p} !important; }
      /* Badge / pill active states */
      [style*="background: #1E3A5F"][style*="color: #fff"]:not(nav):not(nav *) { background: ${nav} !important; }
      /* Tour glow pulse animation */
      @keyframes tourGlowPulse {
        0%, 100% { box-shadow: 0 0 0 2.5px ${p}, 0 0 14px 3px ${p}59; }
        50%       { box-shadow: 0 0 0 2.5px ${p}99, 0 0 30px 9px ${p}9E; }
      }
    `;
    try { localStorage.setItem("lb_accent_colour", accent); } catch {}
  }, [accent]);
  return [accent, setAccent];
}

// ─── AI DEAL RISK ANALYSIS ────────────────────────────────────────────────────
function computeAIRiskAnalysis(listing) {
  let score = 50;
  const desc = (listing.description || "").toLowerCase();
  const type = (listing.project_type || "").toLowerCase();
  const funding = listing.funding_needed || 0;

  // Planning permission signals
  if (desc.includes("planning permission") || desc.includes("planning granted") || desc.includes("full planning")) score += 15;
  else if (desc.includes("planning pending") || desc.includes("awaiting planning")) score -= 5;

  // Project type risk
  if (type === "residential") score += 10;
  else if (type === "renovation") score += 8;
  else if (type === "commercial") score += 0;
  else if (type === "mixed use") score += 3;

  // Location demand (London/SE premium)
  const region = ukRegion(listing.location);
  if (region === "London" || region === "South East") score += 10;
  else if (region === "Greater Manchester" || region === "West Midlands" || region === "Yorkshire") score += 5;

  // Funding size (smaller = lower risk)
  if (funding > 0 && funding <= 200000) score += 8;
  else if (funding > 200000 && funding <= 500000) score += 4;
  else if (funding > 500000) score -= 5;

  // Description quality
  if ((listing.description || "").length > 200) score += 5;
  if (listing.photos?.length >= 2) score += 5;
  if (listing.timeline) score += 3;

  score = Math.max(10, Math.min(100, score));

  const isLow = score >= 70;
  const isMed = score >= 45 && score < 70;

  const riskLabel = isLow ? "Low risk" : isMed ? "Medium risk" : "Higher risk";
  const riskColor = isLow ? "#16A34A" : isMed ? "#D97706" : "#DC2626";
  const riskBg    = isLow ? "#DCFCE7" : isMed ? "#FEF3C7" : "#FEE2E2";

  const baseReturn = isLow ? 7 : isMed ? 9 : 12;
  const returnRange = `${baseReturn}–${baseReturn + 2}%`;

  const typeLabel = listing.project_type || "property";
  const locLabel  = listing.location ? ` in ${listing.location}` : "";
  const planNote  = desc.includes("planning granted") || desc.includes("planning permission") ? " with planning permission in place" : "";
  const summary = isLow
    ? `This appears to be a low-risk ${typeLabel.toLowerCase()} project${locLabel}${planNote}. Suggested return: ${returnRange} p.a.`
    : isMed
    ? `This is a medium-risk ${typeLabel.toLowerCase()} project${locLabel}. Returns may be higher to reflect the additional risk. Suggested return: ${returnRange} p.a.`
    : `This project carries higher risk. Thorough due diligence is recommended before committing. Suggested return: ${returnRange} p.a.`;

  const structure = isLow
    ? `Fixed interest at ${baseReturn}–${baseReturn+2}% p.a. with milestone-based drawdown.`
    : isMed
    ? `Fixed interest at ${baseReturn}–${baseReturn+2}% p.a. or a profit share arrangement. Milestone-based drawdown recommended.`
    : `Consider equity stake or high-yield fixed interest (${baseReturn}%+). Ensure full documentation and legal charge in place.`;

  return { score, riskLabel, riskColor, riskBg, returnRange, summary, structure };
}

// ─── DEAL TEMPLATES ───────────────────────────────────────────────────────────
const DEAL_TEMPLATES = [
  {
    id: "residential",
    name: "Standard residential development",
    desc: "New build house — fixed interest, milestone drawdown",
    icon: "home",
    typical: "7–9% p.a. fixed interest",
    fields: { returnType: "fixed_interest", interestRate: "8", loanTermMonths: "18", dealTitle: "Residential development project" },
  },
  {
    id: "btr",
    name: "Buy to let renovation",
    desc: "Renovation with rental income split",
    icon: "key",
    typical: "60/40 rental income split",
    fields: { returnType: "rental_split", rentalSplitPct: "40", loanTermMonths: "12", dealTitle: "Buy to let renovation" },
  },
  {
    id: "commercial",
    name: "Commercial development",
    desc: "Office, retail or mixed-use commercial project",
    icon: "building",
    typical: "10–14% p.a. fixed or equity stake",
    fields: { returnType: "fixed_interest", interestRate: "12", loanTermMonths: "24", dealTitle: "Commercial development project" },
  },
  {
    id: "quickflip",
    name: "Quick flip",
    desc: "Short-term renovation and sale — fast fixed return",
    icon: "zap",
    typical: "10–12% p.a. fixed, 6-month term",
    fields: { returnType: "fixed_interest", interestRate: "11", loanTermMonths: "6", dealTitle: "Quick flip renovation" },
  },
];

function CredibilityRing({ score, size = 48, children }) {
  const r = (size / 2) - 3;
  const circ = 2 * Math.PI * r;
  const color = score >= 80 ? "#16A34A" : score >= 60 ? "var(--accent)" : score >= 40 ? "#D97706" : "#DC2626";
  const dash = (score / 100) * circ;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    function h(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  function handleClick(e) {
    e.stopPropagation();
    if (!open && wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const TIP_W = 224;
      const TIP_H = 180;
      let left = rect.left + rect.width / 2 - TIP_W / 2;
      let top = rect.bottom + 8;
      if (left < 8) left = 8;
      if (left + TIP_W > window.innerWidth - 8) left = window.innerWidth - TIP_W - 8;
      if (top + TIP_H > window.innerHeight - 8) top = rect.top - TIP_H - 8;
      setTooltipPos({ top, left });
    }
    setOpen(o => !o);
  }

  const BREAKDOWN = [
    { label: "ID verified",           pts: 20 },
    { label: "Companies House",       pts: 15 },
    { label: "Planning permission",   pts: 15 },
    { label: "Insurance uploaded",    pts: 10 },
    { label: "Projects completed",    pts: 25 },
    { label: "4★+ platform reviews", pts: 15 },
  ];

  const tooltip = open ? createPortal(
    <div
      onClick={e => e.stopPropagation()}
      style={{ position: "fixed", top: tooltipPos.top, left: tooltipPos.left, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 10, padding: "12px 14px", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", zIndex: 9999, width: 224, cursor: "default" }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "#1E3A5F", marginBottom: 8 }}>Credibility score — {score}/100</div>
      <div style={{ fontSize: 11, color: "#64748B", marginBottom: 10, lineHeight: 1.5 }}>Calculated from verified documents, completed projects, reviews and platform activity. Higher scores get more matches.</div>
      {BREAKDOWN.map(b => (
        <div key={b.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#555", padding: "2px 0" }}>
          <span>{b.label}</span>
          <span style={{ fontWeight: 600, color: "#94A3B8" }}>+{b.pts} pts</span>
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div
      ref={wrapRef}
      onClick={handleClick}
      title="Builder credibility score"
      style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: size, height: size, cursor: "pointer", flexShrink: 0 }}
    >
      {children}
      <svg width={size} height={size} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", transform: "rotate(-90deg)", pointerEvents: "none" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" style={{ transition: "stroke-dasharray 0.6s ease" }} />
      </svg>
      <div style={{ position: "absolute", bottom: 0, right: 0, background: color, borderRadius: "50%", width: Math.max(14, size * 0.32), height: Math.max(14, size * 0.32), display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff", pointerEvents: "none" }}>
        <span style={{ fontSize: Math.max(8, size * 0.18), fontWeight: 700, color: "#fff", lineHeight: 1 }}>{score}</span>
      </div>
      {tooltip}
    </div>
  );
}

const RETURN_TYPE_INFO = {
  "Fixed interest": {
    example: "You lend £100,000 and get back £108,000 after 12 months. Simple and predictable.",
    quiz: ["fast", "predictable"],
  },
  "Rental split": {
    example: "You lend money and receive a percentage of the monthly rental income until your investment is repaid.",
    quiz: ["medium", "income"],
  },
  "Equity stake": {
    example: "You own a percentage of the property. When it sells you receive that percentage of the sale price.",
    quiz: ["long", "upside"],
  },
};

const RETURN_TYPE_TOOLTIPS = {
  "Fixed interest": "You lend a fixed amount and receive it back with agreed interest. Simple and predictable.",
  "Rental split": "You receive a percentage of monthly rental income until your investment is repaid.",
  "Equity stake": "You own a percentage of the property and receive that share when it sells.",
};

function ReturnTypeQuiz({ onResult }) {
  const [q1, setQ1] = useState(null); // "fast" | "medium" | "long"
  const [q2, setQ2] = useState(null); // "predictable" | "income" | "upside"
  const recommend = q1 && q2
    ? (q1 === "fast" || q2 === "predictable" ? "Fixed interest"
      : q1 === "long" || q2 === "upside" ? "Equity stake"
      : "Rental split")
    : null;
  return (
    <div style={{ background: "#EFF6FF", border: "0.5px solid #BFDBFE", borderRadius: 10, padding: "14px 16px", marginTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1D4ED8", marginBottom: 10 }}>Which is right for me?</div>
      <div style={{ fontSize: 12, color: "#1E3A5F", marginBottom: 6, fontWeight: 500 }}>How soon do you want your money back?</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {[["fast","Within 2 years"],["medium","3–5 years"],["long","5+ years / flexible"]].map(([v,l]) => (
          <button key={v} type="button" onClick={() => setQ1(v)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 12, border: "0.5px solid #BFDBFE", background: q1===v ? "#3B82F6" : "#fff", color: q1===v ? "#fff" : "#1E3A5F", cursor: "pointer" }}>{l}</button>
        ))}
      </div>
      <div style={{ fontSize: 12, color: "#1E3A5F", marginBottom: 6, fontWeight: 500 }}>Do you prefer predictable returns or higher upside?</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {[["predictable","Predictable payments"],["income","Regular income"],["upside","Maximum long-term gain"]].map(([v,l]) => (
          <button key={v} type="button" onClick={() => setQ2(v)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 12, border: "0.5px solid #BFDBFE", background: q2===v ? "#3B82F6" : "#fff", color: q2===v ? "#fff" : "#1E3A5F", cursor: "pointer" }}>{l}</button>
        ))}
      </div>
      {recommend && (
        <div style={{ background: "#DBEAFE", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#1D4ED8", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Recommendation: {recommend}</span>
          {onResult && <button type="button" onClick={() => onResult(recommend)} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "none", background: "#3B82F6", color: "#fff", cursor: "pointer" }}>Apply</button>}
        </div>
      )}
    </div>
  );
}

function ReturnTypeExplainer({ returnType, children, onApply }) {
  const [quizOpen, setQuizOpen] = useState(false);
  const info = RETURN_TYPE_INFO[returnType] || RETURN_TYPE_INFO["Fixed interest"];
  return (
    <div>
      {children}
      <div style={{ fontSize: 12, color: "#64748B", marginTop: 8, lineHeight: 1.55, background: "#f9f9f7", borderRadius: 8, padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <span>{info.example}</span>
        <button type="button" onClick={() => setQuizOpen(o => !o)} style={{ flexShrink: 0, fontSize: 11, color: "#3B82F6", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 500, whiteSpace: "nowrap" }}>
          {quizOpen ? "Close quiz" : "Which is right for me?"}
        </button>
      </div>
      {quizOpen && <ReturnTypeQuiz onResult={r => { onApply && onApply(r); setQuizOpen(false); }} />}
    </div>
  );
}

function computeMatch(viewerProfile, target, viewerRole) {
  if (!viewerProfile) return null;
  const components = [];
  let total = 0;

  if (viewerRole === "builder") {
    // Budget (30): does lender budget cover builder's typical project?
    const avgProject = viewerProfile.projects_completed > 0
      ? viewerProfile.total_value / viewerProfile.projects_completed
      : (viewerProfile.total_value || 0);
    const budget = target.budget_max_num || 0;
    let bPts = 15, bDetail = "No data to compare";
    if (budget === 0) {
      bPts = 22; bDetail = "Lender's budget is flexible";
    } else if (avgProject > 0) {
      if (budget >= avgProject)        { bPts = 30; bDetail = `Budget covers your ~${fmt(Math.round(avgProject))} typical project`; }
      else if (budget >= avgProject * 0.6) { bPts = 18; bDetail = `Budget partially covers your ~${fmt(Math.round(avgProject))} typical project`; }
      else                             { bPts = 0;  bDetail = `Budget may be low for your ~${fmt(Math.round(avgProject))} typical project`; }
    }
    components.push({ label: "Budget alignment", earned: bPts, max: 30, detail: bDetail });
    total += bPts;

    // Project type (30): builder specialization vs lender preferred projects
    const lPref = target.preferred_projects || [];
    const bProj = SPEC_TO_PROJECTS[viewerProfile.specialization] || ["Residential", "Commercial", "Mixed use", "Renovation"];
    let pPts, pDetail;
    if (lPref.length === 0) {
      pPts = 25; pDetail = "Lender is open to any project type";
    } else {
      const overlap = bProj.filter(p => lPref.includes(p));
      pPts    = overlap.length > 0 ? 30 : 5;
      pDetail = overlap.length > 0 ? `${overlap.join(", ")} matches` : "No direct project type overlap";
    }
    components.push({ label: "Project type", earned: pPts, max: 30, detail: pDetail });
    total += pPts;

  } else if (viewerRole === "lender") {
    // Budget (30): does lender budget cover builder's typical project?
    const avgProject = target.props > 0 ? target.value / target.props : (target.value || 0);
    const budget = viewerProfile.budget_max || 0;
    let bPts = 15, bDetail = "No data to compare";
    if (budget === 0) {
      bPts = 22; bDetail = "Your budget is flexible";
    } else if (avgProject > 0) {
      if (budget >= avgProject)        { bPts = 30; bDetail = `Your budget covers their ~${fmt(Math.round(avgProject))} typical project`; }
      else if (budget >= avgProject * 0.6) { bPts = 18; bDetail = `Your budget partially covers their ~${fmt(Math.round(avgProject))} typical project`; }
      else                             { bPts = 0;  bDetail = `Your budget may be low for their ~${fmt(Math.round(avgProject))} typical project`; }
    }
    components.push({ label: "Budget alignment", earned: bPts, max: 30, detail: bDetail });
    total += bPts;

    // Project type (30): lender preferred projects vs builder specialization
    const lPref = viewerProfile.preferred_projects || [];
    const bProj = SPEC_TO_PROJECTS[target.type] || ["Residential", "Commercial", "Mixed use", "Renovation"];
    let pPts, pDetail;
    if (lPref.length === 0) {
      pPts = 25; pDetail = "You're open to any project type";
    } else {
      const overlap = bProj.filter(p => lPref.includes(p));
      pPts    = overlap.length > 0 ? 30 : 5;
      pDetail = overlap.length > 0 ? `${overlap.join(", ")} matches` : "No direct project type overlap";
    }
    components.push({ label: "Project type", earned: pPts, max: 30, detail: pDetail });
    total += pPts;
  }

  // Location (20): same UK region?
  const vRegion = ukRegion(viewerProfile.location);
  const tRegion = ukRegion(target.location);
  let lPts = 10, lDetail = "Location not specified";
  if (vRegion && tRegion) {
    if (vRegion === tRegion) { lPts = 20; lDetail = `Both in ${vRegion}`; }
    else                     { lPts = 6;  lDetail = `${vRegion} vs ${tRegion}`; }
  }
  components.push({ label: "Location", earned: lPts, max: 20, detail: lDetail });
  total += lPts;

  // Activity (20): how recently was the target profile updated?
  const act = activityScoreCalc(target.updated_at);
  components.push({ label: "Activity level", earned: act.pts, max: 20, detail: act.detail });
  total += act.pts;

  return { score: Math.round(total), components };
}

// Deterministically pick a color and generate initials for real users
const AVATAR_COLORS = ["teal", "blue", "purple", "amber", "coral", "gray"];
function pickColor(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function nameInitials(name = "") {
  return name.split(" ").filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}

// ─── DATA ────────────────────────────────────────────────────────────────────


// ─── SHARED COMPONENTS ───────────────────────────────────────────────────────

function Avatar({ initials, color, size = 40, url }) {
  if (url) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, overflow: "hidden" }}>
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </div>
    );
  }
  const c = COLORS[color] || COLORS.gray;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: c.bg, color: c.text,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.33, fontWeight: 500, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div style={{ background: "var(--color-bg-secondary, #f5f5f3)", borderRadius: 8, padding: "1rem" }}>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 500 }}>{value}</div>
    </div>
  );
}


function RoleBadge({ userRole, authRole }) {
  if (userRole === "founder") return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#FEF9C3", color: "#92400E", flexShrink: 0, whiteSpace: "nowrap" }}>
      <Icon name="crown" size={10} style={{marginRight:3,verticalAlign:"middle"}} /> Founder
    </span>
  );
  if (userRole === "verified_pro") return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#FEF3C7", color: "#B45309", flexShrink: 0, whiteSpace: "nowrap" }}>
      <Icon name="sparkles" size={10} style={{marginRight:3,verticalAlign:"middle"}} /> Verified Pro
    </span>
  );
  if (authRole === "lender") return (
    <span style={{ fontSize: 10, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: "#EBF2FF", color: "#1E3A5F", flexShrink: 0, whiteSpace: "nowrap" }}>
      Lender
    </span>
  );
  if (authRole === "builder") return (
    <span style={{ fontSize: 10, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: "#E1F5EE", color: "#0F6E56", flexShrink: 0, whiteSpace: "nowrap" }}>
      Builder
    </span>
  );
  return (
    <span style={{ fontSize: 10, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: "#F1EFE8", color: "#5F5E5A", flexShrink: 0, whiteSpace: "nowrap" }}>
      Member
    </span>
  );
}

function MatchBadge({ score, breakdown }) {
  const [show, setShow] = useState(false);
  const [cardPos, setCardPos] = useState({ top: 0, left: 0 });
  const badgeRef = useRef(null);
  const cardRef = useRef(null);

  let label;
  if (score >= 80)      label = "Great match";
  else if (score >= 60) label = "Good match";
  else                  label = "Possible match";
  const bg = "var(--accent)";
  const color = "#fff";

  useEffect(() => {
    if (!show) return;
    const handle = e => {
      if (cardRef.current && !cardRef.current.contains(e.target) &&
          badgeRef.current && !badgeRef.current.contains(e.target)) setShow(false);
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("touchstart", handle);
    return () => { document.removeEventListener("mousedown", handle); document.removeEventListener("touchstart", handle); };
  }, [show]);

  function handleToggle(e) {
    e.stopPropagation();
    if (!show && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const CARD_W = 220;
      const CARD_H = 220;
      const PAD = 8;
      let left = rect.left + rect.width / 2 - CARD_W / 2;
      if (left < PAD) left = PAD;
      if (left + CARD_W > window.innerWidth - PAD) left = window.innerWidth - CARD_W - PAD;
      let top = rect.top - CARD_H - 8;
      if (top < PAD) top = rect.bottom + 8;
      setCardPos({ top, left });
    }
    setShow(s => !s);
  }

  const card = show ? createPortal(
    <div ref={cardRef} onClick={e => e.stopPropagation()}
      style={{ position: "fixed", top: cardPos.top, left: cardPos.left, width: 220, background: "#fff", borderRadius: 10, padding: "12px 14px 14px", boxShadow: "0 4px 20px rgba(0,0,0,0.13)", zIndex: 9999, border: "0.5px solid #e8e8e8" }}>
      <button onClick={() => setShow(false)}
        style={{ position: "absolute", top: 8, right: 8, width: 20, height: 20, borderRadius: "50%", background: "#e5e7eb", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, color: "#6b7280", fontSize: 12, fontWeight: 700, lineHeight: 1 }}>×</button>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#1E3A5F", marginBottom: 10, paddingRight: 24 }}>Why this match?</div>
      {breakdown.map(({ label: lbl, earned, max }) => {
        const ratio = earned / max;
        const barColor = ratio >= 0.7 ? "#16A34A" : ratio >= 0.4 ? "#D97706" : "#DC2626";
        return (
          <div key={lbl} style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: "#555" }}>{lbl}</span>
              <span style={{ fontWeight: 700, fontSize: 11, color: barColor }}>{earned}/{max}</span>
            </div>
            <div style={{ height: 3, background: "#f0f0f0", borderRadius: 2 }}>
              <div style={{ height: 3, borderRadius: 2, background: barColor, width: `${ratio * 100}%` }} />
            </div>
          </div>
        );
      })}
    </div>,
    document.body
  ) : null;

  return (
    <span ref={badgeRef} onClick={handleToggle}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20, background: bg, color, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
      {score}% · {label}
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", background: "rgba(0,0,0,0.08)", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>?</span>
      {card}
    </span>
  );
}

// ─── SAVED ACCOUNTS (switch account) ─────────────────────────────────────────

function getSavedAccounts() {
  try { return JSON.parse(localStorage.getItem("lb_saved_accounts") || "[]"); } catch { return []; }
}
function upsertSavedAccount(acct) {
  const list = getSavedAccounts().filter(a => a.user_id !== acct.user_id);
  list.unshift(acct);
  localStorage.setItem("lb_saved_accounts", JSON.stringify(list.slice(0, 3)));
}
function removeSavedAccount(userId) {
  localStorage.setItem("lb_saved_accounts", JSON.stringify(getSavedAccounts().filter(a => a.user_id !== userId)));
}

// ─── SKELETON SHIMMER ────────────────────────────────────────────────────────
(function injectSkeletonStyles() {
  if (typeof document === "undefined" || document.getElementById("lb-skel-styles")) return;
  const s = document.createElement("style");
  s.id = "lb-skel-styles";
  s.textContent = "@keyframes lb-shimmer{0%{background-position:-600px 0}100%{background-position:600px 0}}.lb-skel{background:linear-gradient(90deg,#e8ecef 25%,#f3f5f7 50%,#e8ecef 75%);background-size:1200px 100%;animation:lb-shimmer 0.8s infinite linear;border-radius:6px}";
  document.head.appendChild(s);
})();

function Skel({ w, h, r, style }) {
  return <div className="lb-skel" style={{ width: w || "100%", height: h || 16, borderRadius: r !== undefined ? r : 6, flexShrink: 0, ...style }} />;
}

function SkeletonPostCard() {
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "16px 18px", marginBottom: 12, border: "1px solid #f0f0f0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <Skel w={36} h={36} r="50%" />
        <div style={{ flex: 1 }}>
          <Skel w="45%" h={13} style={{ marginBottom: 6 }} />
          <Skel w="28%" h={11} />
        </div>
      </div>
      <Skel h={13} style={{ marginBottom: 8 }} />
      <Skel w="88%" h={13} style={{ marginBottom: 8 }} />
      <Skel w="62%" h={13} style={{ marginBottom: 14 }} />
      <div style={{ display: "flex", gap: 10 }}>
        <Skel w={58} h={26} r={20} />
        <Skel w={58} h={26} r={20} />
      </div>
    </div>
  );
}

function SkeletonProfileCard() {
  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", border: "1px solid #f0f0f0", marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <Skel w={52} h={52} r="50%" />
        <div style={{ flex: 1 }}>
          <Skel w="52%" h={16} style={{ marginBottom: 8 }} />
          <Skel w="34%" h={12} style={{ marginBottom: 10 }} />
          <Skel h={12} style={{ marginBottom: 6 }} />
          <Skel w="78%" h={12} style={{ marginBottom: 14 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <Skel w={82} h={32} r={20} />
            <Skel w={82} h={32} r={20} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonConversationRow() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid #f5f5f5" }}>
      <Skel w={42} h={42} r="50%" />
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <Skel w="44%" h={13} />
          <Skel w="18%" h={11} />
        </div>
        <Skel w="68%" h={12} />
      </div>
    </div>
  );
}

function SkeletonDashboard() {
  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: "1.75rem" }}>
        <Skel w={52} h={52} r="50%" />
        <div style={{ flex: 1 }}>
          <Skel w={220} h={28} style={{ marginBottom: 8 }} />
          <Skel w={90} h={20} r={20} />
        </div>
      </div>
      <Skel h={52} r={12} style={{ marginBottom: "1.5rem" }} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: "1.5rem" }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ background: "#fff", borderRadius: 12, padding: "16px 18px", border: "1px solid #f0f0f0" }}>
            <Skel w="55%" h={11} style={{ marginBottom: 10 }} />
            <Skel w="40%" h={28} />
          </div>
        ))}
      </div>
      {[0,1].map(i => (
        <div key={i} style={{ background: "#fff", borderRadius: 12, padding: "18px 20px", border: "1px solid #f0f0f0", marginBottom: 12 }}>
          <Skel w="38%" h={14} style={{ marginBottom: 12 }} />
          <Skel h={12} style={{ marginBottom: 8 }} />
          <Skel w="72%" h={12} />
        </div>
      ))}
    </div>
  );
}

// ─── NAVBAR ──────────────────────────────────────────────────────────────────

// ─── NAVBAR HELPERS ──────────────────────────────────────────────────────────
function navLinkStyle(active) {
  return {
    padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 500,
    border: "none", cursor: "pointer", whiteSpace: "nowrap",
    background: active ? "rgba(255,255,255,0.12)" : "transparent",
    color: active ? "var(--accent)" : "rgba(255,255,255,0.75)",
    transition: "color 0.15s", display: "flex", alignItems: "center", gap: 5,
  };
}
const NAV_DROPDOWN_STYLE = {
  position: "absolute", top: "calc(100% + 8px)", left: 0,
  background: "#fff", border: "0.5px solid #e0e0e0",
  borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
  minWidth: 192, zIndex: 200, overflow: "visible",
};
function ddItemStyle(active) {
  return {
    display: "flex", alignItems: "center", gap: 9,
    width: "100%", textAlign: "left", padding: "10px 14px", fontSize: 13,
    fontWeight: active ? 500 : 400,
    background: active ? "#EBF2FF" : "transparent",
    color: active ? "#1E3A5F" : "#333",
    border: "none", cursor: "pointer",
  };
}
function mobileItemStyle(active) {
  return {
    display: "block", width: "100%", textAlign: "left",
    padding: "12px 1.25rem", fontSize: 14, minHeight: 44,
    fontWeight: active ? 500 : 400, background: "transparent",
    color: active ? "var(--accent)" : "rgba(255,255,255,0.75)",
    border: "none", borderBottom: "0.5px solid rgba(255,255,255,0.06)", cursor: "pointer",
  };
}
function NavChevron({ open }) {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none"
      style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", opacity: 0.6, flexShrink: 0 }}>
      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function NavDashboardIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
      <rect x="0.75" y="0.75" width="4.75" height="4.75" rx="1.25" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="7.5" y="0.75" width="4.75" height="4.75" rx="1.25" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="0.75" y="7.5" width="4.75" height="4.75" rx="1.25" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="7.5" y="7.5" width="4.75" height="4.75" rx="1.25" stroke="currentColor" strokeWidth="1.4"/>
    </svg>
  );
}
function NavInboxIcon() {
  return (
    <svg width="16" height="13" viewBox="0 0 16 13" fill="none" style={{ flexShrink: 0 }}>
      <rect x="1" y="1" width="14" height="11" rx="2" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M1 4.5l7 4 7-4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    </svg>
  );
}
const UNREAD_BADGE = {
  position: "absolute", top: 0, right: 0,
  transform: "translate(45%,-45%)",
  background: "var(--accent)", color: "#FFFFFF",
  fontSize: 9, fontWeight: 800, borderRadius: 8,
  minWidth: 16, height: 16, padding: "0 3px",
  display: "flex", alignItems: "center", justifyContent: "center",
  lineHeight: 1, pointerEvents: "none",
};

function scrollMainToTop() {
  const el = document.getElementById("main-scroll");
  if (el) el.scrollTop = 0;
}

function fmtTimeAgo(ts) {
  if (!ts) return "";
  const min = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (min < 1)  return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `${h}h ago`;
  const day = Math.floor(h / 24);
  if (day < 7)  return `${day}d ago`;
  return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function BellIcon() {
  return (
    <svg width="15" height="17" viewBox="0 0 15 17" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7.5 1.5a4 4 0 00-4 4c0 4.5-2 5.5-2 5.5h12s-2-1-2-5.5a4 4 0 00-4-4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
      <path d="M9 13.5a1.5 1.5 0 01-3 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

// ─── ICON SYSTEM ─────────────────────────────────────────────────────────────
// Thin-line SVG icons, 18×18 default. Color is currentColor (set via CSS).
function Icon({ name, size = 18, style, className }) {
  const s = { width: size, height: size, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round",
    strokeLinejoin: "round", style, className, flexShrink: 0 };
  switch (name) {
    case "user":         return <svg {...s}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
    case "users":        return <svg {...s}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>;
    case "heart":        return <svg {...s}><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>;
    case "heart-fill":   return <svg {...s} fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>;
    case "pencil":       return <svg {...s}><path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>;
    case "bar-chart":    return <svg {...s}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
    case "trending-up":  return <svg {...s}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
    case "bell":         return <svg {...s}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
    case "settings":     return <svg {...s}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>;
    case "palette":      return <svg {...s}><circle cx="13.5" cy="6.5" r="0.5" fill="currentColor"/><circle cx="17.5" cy="10.5" r="0.5" fill="currentColor"/><circle cx="8.5" cy="7.5" r="0.5" fill="currentColor"/><circle cx="6.5" cy="12.5" r="0.5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.477-1.125-.29-.289-.472-.686-.472-1.187 0-.9.78-1.875 1.875-1.875H16c2.761 0 5-2.239 5-5 0-4.42-4.03-8-9-8z"/></svg>;
    case "lock":         return <svg {...s}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>;
    case "lock-open":    return <svg {...s}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 019.9-1"/></svg>;
    case "handshake":    return <svg {...s}><path d="M20.42 4.58a5.4 5.4 0 00-7.65 0l-.77.78-.77-.78a5.4 5.4 0 00-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/><path d="M8 14l-1.5-1.5"/><path d="M11.5 17.5L10 16"/></svg>;
    case "circle-help":  return <svg {...s}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="2"/></svg>;
    case "home":         return <svg {...s}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
    case "key":          return <svg {...s}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>;
    case "building":     return <svg {...s}><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22V12h6v10"/><path d="M8 7h.01M16 7h.01M8 12h.01M16 12h.01"/></svg>;
    case "zap":          return <svg {...s}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
    case "trophy":       return <svg {...s}><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="18" width="12" height="4" rx="2"/></svg>;
    case "dollar":       return <svg {...s}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>;
    case "message":      return <svg {...s}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
    case "clipboard":    return <svg {...s}><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>;
    case "check-circle": return <svg {...s}><polyline points="20 6 9 17 4 12"/></svg>;
    case "moon":         return <svg {...s}><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>;
    case "sun":          return <svg {...s}><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
    case "refresh":      return <svg {...s}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>;
    case "smartphone":   return <svg {...s}><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2"/></svg>;
    case "monitor":      return <svg {...s}><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
    case "bank":         return <svg {...s}><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>;
    case "file":         return <svg {...s}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
    case "search":       return <svg {...s}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
    case "warning":      return <svg {...s}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="2"/></svg>;
    case "briefcase":    return <svg {...s}><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>;
    case "hard-hat":     return <svg {...s}><path d="M2 18a1 1 0 001 1h18a1 1 0 001-1v-2a1 1 0 00-1-1H3a1 1 0 00-1 1v2z"/><path d="M10 10V5a1 1 0 011-1h2a1 1 0 011 1v5"/><path d="M4 15v-3a8 8 0 0116 0v3"/></svg>;
    case "book":         return <svg {...s}><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>;
    case "shield":       return <svg {...s}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
    case "mail":         return <svg {...s}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
    case "film":         return <svg {...s}><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>;
    case "circle-green": return <svg {...s} fill="currentColor" style={{ ...style, color: "#22C55E" }}><circle cx="12" cy="12" r="10"/></svg>;
    case "hammer":       return <svg {...s}><path d="M15 12l-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 010-3L12 9"/><path d="M17.64 15L22 10.64"/><path d="M20.91 11.7l-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 00-3.94-1.64H9l.92.82A6.18 6.18 0 0112 8.4v1.56l2 2h2.47l2.26 1.91"/></svg>;
    case "calculator":   return <svg {...s}><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="8" y2="10" strokeWidth="2"/><line x1="12" y1="10" x2="12" y2="10" strokeWidth="2"/><line x1="16" y1="10" x2="16" y2="10" strokeWidth="2"/><line x1="8" y1="14" x2="8" y2="14" strokeWidth="2"/><line x1="12" y1="14" x2="12" y2="14" strokeWidth="2"/><line x1="16" y1="14" x2="16" y2="14" strokeWidth="2"/><line x1="8" y1="18" x2="12" y2="18"/><line x1="16" y1="18" x2="16" y2="18" strokeWidth="2"/></svg>;
    case "plus":         return <svg {...s}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
    case "edit":         return <svg {...s}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
    case "star":         return <svg {...s}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
    case "chart":        return <svg {...s}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><rect x="1" y="1" width="22" height="22" rx="2" strokeOpacity="0"/></svg>;
    case "scale":        return <svg {...s}><line x1="12" y1="3" x2="12" y2="21"/><path d="M3 6l9 6 9-6"/><path d="M3 18a6 6 0 006-6M15 18a6 6 0 01-6-6"/></svg>;
    case "columns":      return <svg {...s}><rect x="9" y="3" width="13" height="18" rx="2" ry="2"/><path d="M5 3H4a2 2 0 00-2 2v14a2 2 0 002 2h1"/></svg>;
    case "layout":       return <svg {...s}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
    case "crown":        return <svg {...s}><path d="M12 2l3.5 7h5L16 13l2 7-6-4-6 4 2-7-4.5-4H8.5L12 2z"/></svg>;
    case "inbox":        return <svg {...s}><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/></svg>;
    case "clock":        return <svg {...s}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case "trash":        return <svg {...s}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>;
    case "id-card":      return <svg {...s}><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M16 11h2M16 14h2M7 11h4a1 1 0 011 1v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-1a1 1 0 011-1z"/><circle cx="9" cy="8" r="2"/></svg>;
    case "flag":         return <svg {...s}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>;
    case "map-pin":      return <svg {...s}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
    case "thumbs-up":    return <svg {...s}><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg>;
    case "sparkles":     return <svg {...s}><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>;
    case "wrench":       return <svg {...s}><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>;
    case "party":        return <svg {...s}><path d="M5.8 11.3L2 22l10.7-3.79"/><path d="M4 3h.01"/><path d="M22 8h.01"/><path d="M15 2h.01"/><path d="M22 20h.01"/><path d="M22 2l-2.24 2.24"/><path d="M16.78 10.78l-4.56 4.56"/><path d="M11.78 5.78l-4.56 4.56"/><path d="M10.22 17.22L12 22"/></svg>;
    case "globe":        return <svg {...s}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>;
    default:             return <svg {...s}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/></svg>;
  }
}

function NotificationBell({ user, onReplayTour }) {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen]     = useState(false);
  const bellRef = useRef(null);
  const myId = user?.id;
  const unread = notifs.filter(n => !n.read).length;

  useEffect(() => {
    if (!myId) return;
    loadNotifs();
    const channel = supabase.channel(`notifs-${myId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${myId}` },
        payload => setNotifs(prev => [payload.new, ...prev].slice(0, 20)))
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [myId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleOutside(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  async function loadNotifs() {
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", myId)
      .order("created_at", { ascending: false })
      .limit(20);
    setNotifs(data || []);
  }

  async function markRead(id) {
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    await supabase.from("notifications").update({ read: true }).eq("id", id);
  }

  async function clearAll() {
    setNotifs([]);
    await supabase.from("notifications").delete().eq("user_id", myId);
  }

  const typeIcon = {
    message:             "message",
    milestone_complete:  "clipboard",
    milestone_approved:  "dollar",
    deal_created:        "bar-chart",
  };

  return (
    <div ref={bellRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        style={{
          position: "relative",
          background: open ? "rgba(255,255,255,0.12)" : "transparent",
          border: "none", borderRadius: 8,
          padding: "7px 10px", minHeight: 44,
          cursor: "pointer",
          color: open ? "var(--accent)" : "rgba(255,255,255,0.7)",
          lineHeight: 0, display: "flex", alignItems: "center",
        }}
      >
        <BellIcon />
        {unread > 0 && <span style={{ ...UNREAD_BADGE, background: "#EF4444" }}>{unread > 9 ? "9+" : unread}</span>}
        <span style={{ position: "absolute", top: 5, left: 5, lineHeight: 0, color: "rgba(250,204,21,0.9)", pointerEvents: "none" }}>
          <Icon name="sparkles" size={8} />
        </span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          width: 300, maxWidth: "calc(100vw - 1.5rem)", maxHeight: 560,
          background: "#fff", borderRadius: 12,
          border: "0.5px solid #e0e0e0",
          boxShadow: "0 8px 30px rgba(0,0,0,0.14)",
          zIndex: 200, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "11px 16px", borderBottom: "0.5px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F" }}>Notifications</span>
            {notifs.length > 0 && (
              <button onClick={clearAll} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#64748B", padding: 0 }}>Clear all</button>
            )}
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {notifs.length === 0 ? (
              <div style={{ padding: "2.5rem 1.5rem", textAlign: "center", color: "#aaa", fontSize: 13 }}>No notifications yet</div>
            ) : (
              notifs.map(n => (
                <div
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  style={{
                    padding: "11px 16px", cursor: "pointer",
                    borderBottom: "0.5px solid #f5f5f3",
                    background: n.read ? "transparent" : "#EBF5FF",
                    display: "flex", gap: 10, alignItems: "flex-start",
                  }}
                >
                  <Icon name={typeIcon[n.type] || "bell"} size={16} className="lb-icon" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, lineHeight: 1.45, color: "#222" }}>{n.message}</div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>{fmtTimeAgo(n.created_at)}</div>
                  </div>
                  {!n.read && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#3B82F6", flexShrink: 0, marginTop: 5 }} />}
                </div>
              ))
            )}
          </div>

        </div>
      )}
    </div>
  );
}

// ─── CELEBRATION OVERLAY ─────────────────────────────────────────────────────
function CelebrationOverlay({ message, subtitle, onClose, fullScreen = false }) {
  useEffect(() => {
    if (!fullScreen) {
      const t = setTimeout(onClose, 4500);
      return () => clearTimeout(t);
    }
  }, [fullScreen, onClose]);

  const confettiPieces = Array.from({ length: 60 }, (_, i) => ({
    key: i,
    left: Math.random() * 100,
    color: ["#3B82F6","#1E3A5F","#22C55E","#D97706","#E879F9","#F43F5E"][i % 6],
    delay: Math.random() * 1.5,
    duration: 2 + Math.random() * 2,
    size: 6 + Math.random() * 10,
    endRotate: 360 + Math.random() * 360,
  }));

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: fullScreen ? "auto" : "none", display: "flex", alignItems: fullScreen ? "center" : "flex-start", justifyContent: "center", paddingTop: fullScreen ? 0 : 80 }}>
      {confettiPieces.map(p => (
        <div key={p.key} style={{
          position: "fixed", top: -20, left: `${p.left}%`,
          width: p.size, height: p.size,
          background: p.color, borderRadius: p.size > 10 ? "50%" : 2,
          animation: `confettiFall ${p.duration}s ease-in ${p.delay}s forwards`,
          "--r-end": `${p.endRotate}deg`,
          opacity: 0,
        }} />
      ))}
      <div style={{
        background: "#fff", borderRadius: 16, padding: "20px 28px", textAlign: "center",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)", maxWidth: 360, pointerEvents: "auto",
        animation: "slideUp 0.4s ease",
      }}>
        <div style={{ marginBottom: 8, color: "var(--accent)" }}><Icon name="party" size={40} /></div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#1E3A5F", marginBottom: 6 }}>{message}</div>
        {subtitle && <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16, lineHeight: 1.5 }}>{subtitle}</div>}
        <button onClick={onClose} style={{ padding: "8px 24px", background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
          {fullScreen ? "Download badge" : "Awesome!"}
        </button>
        {fullScreen && <button onClick={onClose} style={{ display: "block", margin: "8px auto 0", fontSize: 12, color: "#94A3B8", background: "none", border: "none", cursor: "pointer" }}>Close</button>}
      </div>
    </div>,
    document.body
  );
}



// Feature flag: "My Repayments" page is hidden from navigation (LenderBuild no longer positions itself around loan repayment tracking). Component + API actions left intact — flip to true to restore.
const SHOW_MY_REPAYMENTS = false;

// ─── DESKTOP SIDEBAR ─────────────────────────────────────────────────────────
function DesktopSidebar({ page, setPage, onRefreshPage, user, collapsed, onToggle, unreadCount = 0 }) {
  const role = user?.user_metadata?.role;
  const navGroups = [
    {
      items: [
        { key: "home",              label: "Dashboard",       icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
        { key: role === "lender" ? "find-builder" : "search", label: role === "lender" ? "Find Builders" : "Find Lenders", icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> },
        { key: "messages",          label: "Messages",        icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
        { key: "my-deals",          label: "My Deals",        icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 15 11 17 15 13"/></svg> },
        ...(SHOW_MY_REPAYMENTS && role === "lender" ? [{ key: "my-repayments", label: "My Repayments", icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg> }] : []),
      ],
    },
    {
      label: "Discover",
      items: [
        { key: "posts",             label: "Posts",           icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
        { key: "community-groups",   label: "Community",       icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg> },
        { key: "browse-projects",   label: "Browse Projects", icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg> },
        { key: "market",            label: "Market",          icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> },
        { key: "legal-resources",   label: "Legal Resources", icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
      ],
    },
    {
      label: "Account",
      items: [
        { key: "notifications",     label: "Notifications",   icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg> },
        { key: "account",           label: "Settings",        icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg> },
      ],
    },
  ];

  return (
    <div style={{
      position: "sticky", top: 60, height: "calc(100vh - 60px)",
      width: collapsed ? 58 : 224, flexShrink: 0,
      background: "var(--bg-card)", borderRight: "var(--border-light)",
      overflow: "hidden",
      transition: "width 0.22s ease",
      display: "flex", flexDirection: "column",
      zIndex: 50,
    }}>
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: collapsed ? "12px 8px" : "14px 10px" }}>
      {navGroups.map((group, gi) => (
        <div key={gi} style={{ marginBottom: collapsed ? 8 : 16 }}>
          {!collapsed && group.label && (
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", padding: "0 8px", marginBottom: 4 }}>{group.label}</div>
          )}
          {group.items.map(item => {
            const isActive = page === item.key;
            return (
              <button key={item.key} onClick={() => { if (page === item.key) { onRefreshPage?.(item.key); } else { setPage(item.key); window.scrollTo({ top: 0, behavior: "instant" }); } }}
                title={collapsed ? item.label : undefined}
                style={{
                  display: "flex", alignItems: "center", gap: collapsed ? 0 : 9,
                  width: "100%", padding: collapsed ? "9px 0" : "9px 10px",
                  borderRadius: 8, border: "none", cursor: "pointer",
                  background: isActive ? "var(--accent-bg)" : "transparent",
                  color: isActive ? "var(--accent)" : "var(--text-muted)",
                  fontSize: 13, fontWeight: isActive ? 600 : 400,
                  textAlign: "left", marginBottom: 2,
                  whiteSpace: "nowrap", overflow: "hidden",
                  justifyContent: collapsed ? "center" : "flex-start",
                  transition: "background 0.15s, color 0.15s",
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "var(--bg-secondary)"; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ flexShrink: 0, lineHeight: 0, color: isActive ? "var(--accent)" : "var(--text-muted)" }}>{item.icon}</span>
                {!collapsed && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>}
              </button>
            );
          })}
          {gi < navGroups.length - 1 && !collapsed && (
            <div style={{ borderTop: "var(--border-light)", margin: "10px 0 0" }} />
          )}
        </div>
      ))}
      </div>

      {/* Bottom utility bar */}
      <div style={{ borderTop: "var(--border-light)", padding: collapsed ? "8px" : "8px 10px", display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
        <button
          onClick={() => { setPage("help"); window.scrollTo({ top: 0, behavior: "instant" }); }}
          title={collapsed ? "Help Centre" : undefined}
          style={{ display: "flex", alignItems: "center", gap: collapsed ? 0 : 9, width: "100%", padding: collapsed ? "9px 0" : "9px 10px", borderRadius: 8, border: "none", cursor: "pointer", background: page === "help" ? "var(--accent-bg)" : "transparent", color: page === "help" ? "var(--accent)" : "var(--text-muted)", fontSize: 13, fontWeight: page === "help" ? 600 : 400, justifyContent: collapsed ? "center" : "flex-start", transition: "background 0.15s, color 0.15s" }}
          onMouseEnter={e => { if (page !== "help") e.currentTarget.style.background = "var(--bg-secondary)"; }}
          onMouseLeave={e => { if (page !== "help") e.currentTarget.style.background = "transparent"; }}
        >
          <span style={{ flexShrink: 0, lineHeight: 1, fontSize: 15, fontWeight: 700, color: page === "help" ? "var(--accent)" : "var(--text-muted)" }}>?</span>
          {!collapsed && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>Help</span>}
        </button>

        <button
          id="tour-inbox-btn"
          onClick={() => { if (page === "messages") { onRefreshPage?.("messages"); } else { setPage("messages"); window.scrollTo({ top: 0, behavior: "instant" }); } }}
          title={collapsed ? "Inbox" : undefined}
          style={{ position: "relative", display: "flex", alignItems: "center", gap: collapsed ? 0 : 9, width: "100%", padding: collapsed ? "9px 0" : "9px 10px", borderRadius: 8, border: "none", cursor: "pointer", background: page === "messages" ? "var(--accent-bg)" : "transparent", color: page === "messages" ? "var(--accent)" : "var(--text-muted)", fontSize: 13, fontWeight: page === "messages" ? 600 : 400, justifyContent: collapsed ? "center" : "flex-start", transition: "background 0.15s, color 0.15s" }}
          onMouseEnter={e => { if (page !== "messages") e.currentTarget.style.background = "var(--bg-secondary)"; }}
          onMouseLeave={e => { if (page !== "messages") e.currentTarget.style.background = "transparent"; }}
        >
          <span style={{ flexShrink: 0, lineHeight: 0, color: page === "messages" ? "var(--accent)" : "var(--text-muted)" }}><NavInboxIcon /></span>
          {!collapsed && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>Inbox</span>}
          {unreadCount > 0 && <span style={UNREAD_BADGE}>{unreadCount > 9 ? "9+" : unreadCount}</span>}
        </button>

        <button
          onClick={onToggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{ display: "flex", alignItems: "center", gap: collapsed ? 0 : 9, width: "100%", padding: collapsed ? "9px 0" : "9px 10px", borderRadius: 8, border: "none", cursor: "pointer", background: "transparent", color: "var(--text-muted)", fontSize: 13, justifyContent: collapsed ? "center" : "flex-start", transition: "background 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--bg-secondary)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
          <span style={{ flexShrink: 0, lineHeight: 0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {collapsed ? <path d="M9 18l6-6-6-6"/> : <path d="M15 18l-6-6 6-6"/>}
            </svg>
          </span>
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </div>
  );
}

// ─── BOTTOM NAV ───────────────────────────────────────────────────────────────
function BottomNav({ page, setPage, onRefreshPage, user, unreadCount = 0, communityBadge = false }) {
  const role = user?.user_metadata?.role;
  const activeColor = "var(--accent, #1E3A5F)";
  const inactiveColor = "#94A3B8";

  const tabs = [
    {
      key: "home",
      label: "Dashboard",
      icon: (active) => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? activeColor : inactiveColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
        </svg>
      ),
    },
    {
      key: role === "lender" ? "find-builder" : "search",
      label: "Find",
      icon: (active) => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? activeColor : inactiveColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      ),
    },
    {
      key: "posts",
      label: "Posts",
      icon: (active) => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? activeColor : inactiveColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
        </svg>
      ),
      dotBadge: communityBadge,
    },
    {
      key: "messages",
      label: "Messages",
      icon: (active) => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill={active ? activeColor : "none"} stroke={active ? activeColor : inactiveColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
      ),
      badge: unreadCount,
    },
    {
      key: "profile-menu",
      label: "Profile",
      icon: (active) => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill={active ? activeColor : "none"} stroke={active ? activeColor : inactiveColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
        </svg>
      ),
    },
  ];

  const profilePages = ["profile-menu", "profile-setup", "account", "saved-profiles", "saved-searches", "notifications", "lender-dashboard", "disputes"];
  const postsPages = ["community", "posts", "post-detail", "my-posts", "create-post", "edit-post", "community-posts", "community-groups", "community-chat"];

  return (
    <nav style={{
      position: "fixed", bottom: 0, left: 0, right: 0, width: "100%",
      height: "calc(60px + env(safe-area-inset-bottom, 0px))",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
      background: "#fff", zIndex: 9999, boxShadow: "0 -2px 12px rgba(0,0,0,0.1)", borderTop: "1px solid #e5e7eb",
      display: "flex", alignItems: "flex-start",
      WebkitTransform: "translateZ(0)", transform: "translateZ(0)",
    }}>
      {tabs.map(tab => {
        const isActive =
          tab.key === "profile-menu" ? profilePages.includes(page) :
          tab.key === "posts" ? postsPages.includes(page) :
          page === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => {
              if (page === tab.key) { onRefreshPage?.(tab.key); }
              else { setPage(tab.key); window.scrollTo({ top: 0, behavior: "instant" }); }
            }}
            style={{
              flex: 1, minWidth: 0, height: 60, display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", gap: 3, background: "transparent", border: "none", cursor: "pointer",
              position: "relative", overflow: "hidden",
            }}
          >
            {tab.badge > 0 && (
              <span style={{ position: "absolute", top: 8, left: "50%", marginLeft: 4, background: "#3B82F6", color: "#fff", borderRadius: 10, fontSize: 9, fontWeight: 700, padding: "1px 4px", minWidth: 14, textAlign: "center" }}>
                {tab.badge > 9 ? "9+" : tab.badge}
              </span>
            )}
            {tab.dotBadge && (
              <span style={{ position: "absolute", top: 8, right: "calc(50% - 16px)", width: 8, height: 8, borderRadius: "50%", background: "#22C55E", border: "1.5px solid #fff" }} />
            )}
            {tab.icon(isActive)}
            <span style={{ fontSize: 9, fontWeight: isActive ? 600 : 400, color: isActive ? activeColor : inactiveColor, textAlign: "center", width: "100%", display: "block" }}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function Navbar({ page, setPage, user, onLogout, unreadCount = 0, userProfile, tourForceProfileOpen = false, darkMode, setDarkMode, onReplayTour }) {
  const displayName = user?.user_metadata?.name || user?.email?.split("@")[0] || "";
  const role        = user?.user_metadata?.role;
  const avatarUrl   = user?.user_metadata?.avatar_url;

  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [helpOpen,    setHelpOpen]    = useState(false);
  const [moreOpen,    setMoreOpen]    = useState(false);
  const [width,       setWidth]       = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [scrolled,     setScrolled]     = useState(false);
  const [onlineCount,  setOnlineCount]  = useState(0);
  const profileRef      = useRef(null);
  const helpRef         = useRef(null);
  const moreRef         = useRef(null);
  const presenceChanRef = useRef(null);
  const [navAddingAcct,    setNavAddingAcct]    = useState(false);
  const [navAddEmail,      setNavAddEmail]      = useState("");
  const [navAddPassword,   setNavAddPassword]   = useState("");
  const [navAddLoading,    setNavAddLoading]    = useState(false);
  const [navAddError,      setNavAddError]      = useState("");
  const [navAccounts,      setNavAccounts]      = useState(getSavedAccounts);
  const [navExpiredAcct,   setNavExpiredAcct]   = useState(null);
  const [navExpiredPw,     setNavExpiredPw]     = useState("");
  const [navExpiredErr,    setNavExpiredErr]    = useState("");
  const [navSwitchLoading, setNavSwitchLoading] = useState(null);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function handleOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (helpRef.current    && !helpRef.current.contains(e.target))    setHelpOpen(false);
      if (moreRef.current    && !moreRef.current.contains(e.target))    setMoreOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false); setProfileOpen(false); setMoreOpen(false);
  }, [page]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    if (!user) { setOnlineCount(0); return; }
    presenceChanRef.current = supabase.channel("lb-online-users", {
      config: { presence: { key: user.id } },
    });
    presenceChanRef.current
      .on("presence", { event: "sync" }, () => {
        const state = presenceChanRef.current.presenceState();
        setOnlineCount(Object.keys(state).length);
      })
      .subscribe(async status => {
        if (status === "SUBSCRIBED") {
          await presenceChanRef.current.track({ user_id: user.id, online_at: new Date().toISOString() });
        }
      });
    return () => {
      if (presenceChanRef.current) supabase.removeChannel(presenceChanRef.current);
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!profileOpen) {
      setNavAddingAcct(false); setNavAddEmail(""); setNavAddPassword(""); setNavAddError("");
      setNavExpiredAcct(null); setNavExpiredPw(""); setNavExpiredErr("");
      return;
    }
    const accts = getSavedAccounts();
    const missing = accts.filter(a => !a.member_id && a.user_id);
    if (!missing.length) { setNavAccounts(accts); return; }
    (async () => {
      const { data } = await supabase.from("profiles").select("id, sequential_id").in("id", missing.map(a => a.user_id));
      if (data?.length) {
        data.forEach(p => {
          const acct = accts.find(a => a.user_id === p.id);
          if (acct && p.sequential_id) { acct.member_id = p.sequential_id; upsertSavedAccount(acct); }
        });
      }
      setNavAccounts(getSavedAccounts());
    })();
  }, [profileOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleNavAddAccount(e) {
    e.preventDefault();
    setNavAddLoading(true);
    setNavAddError("");
    const { data: { session: curSess } } = await supabase.auth.getSession();
    if (curSess && user) {
      upsertSavedAccount({
        user_id: user.id, email: user.email, name: displayName,
        avatar_url: avatarUrl || null, role: user.user_metadata?.role || null,
        member_id: userProfile?.sequential_id || null,
        access_token: curSess.access_token, refresh_token: curSess.refresh_token,
      });
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email: navAddEmail, password: navAddPassword });
    setNavAddLoading(false);
    if (error) { setNavAddError(error.message); return; }
    let newRole = data.user.user_metadata?.role || null;
    const { data: newProf } = await supabase.from("profiles").select("role, sequential_id").eq("id", data.user.id).maybeSingle();
    if (!newRole) newRole = newProf?.role || null;
    upsertSavedAccount({
      user_id: data.user.id, email: data.user.email,
      name: data.user.user_metadata?.name || data.user.email.split("@")[0],
      avatar_url: data.user.user_metadata?.avatar_url || null,
      role: newRole, member_id: newProf?.sequential_id || null,
      access_token: data.session.access_token, refresh_token: data.session.refresh_token,
    });
    const newFirstName = (data.user.user_metadata?.name || data.user.email.split("@")[0]).split(" ")[0];
    sessionStorage.setItem("lb_switching", JSON.stringify({ name: newFirstName }));
    window.location.href = "/";
  }

  const isMobile = width < 820;

  function go(p, data) {
    setPage(p, data);
    setMobileOpen(false); setProfileOpen(false);
  }

  const isWaitlistLogo = WAITLIST_MODE && !user;
  const Logo = (
    <div onClick={() => go("home")} style={{
      fontFamily: "'Playfair Display', Georgia, serif",
      fontSize: 20, fontWeight: 700, cursor: "pointer",
      letterSpacing: -0.3, flexShrink: 0,
      display: "flex", alignItems: "center", gap: 6,
      ...(isWaitlistLogo && { background: "rgba(0,0,0,0.28)", backdropFilter: "blur(4px)", borderRadius: 8, padding: "4px 10px 4px 8px" }),
    }}>
      <span style={{ color: "#FFFFFF", textShadow: isWaitlistLogo ? "0 1px 4px rgba(0,0,0,0.7)" : "none" }}>Lender</span><span style={{ color: "#3B82F6", textShadow: isWaitlistLogo ? "0 1px 4px rgba(0,0,0,0.5)" : "none" }}>Build</span>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginBottom: 1 }}>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    </div>
  );

  const HamburgerIcon = mobileOpen
    ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
    : <svg width="16" height="12" viewBox="0 0 16 12" fill="none"><path d="M0 1h16M0 6h16M0 11h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;

  // ── LOGGED OUT ──────────────────────────────────────────────────────────────
  if (!user) {
    function scrollToSection(id) {
      const doScroll = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - 70;
        window.scrollTo({ top, behavior: "smooth" });
      };
      if (page !== "home") {
        go("home");
        setTimeout(doScroll, 350);
        return;
      }
      doScroll();
    }

    if (isMobile) {
      return (
        <nav style={{ background: "var(--nav-bg)", borderBottom: "1px solid rgba(255,255,255,0.08)", position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.35)" : "none", transition: "box-shadow 0.25s ease" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1.25rem", height: 56 }}>
            {Logo}
            {!WAITLIST_MODE && <>
            <button onClick={() => setMobileOpen(o => !o)} aria-label="Menu"
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "7px 11px", minHeight: 44, cursor: "pointer", color: "rgba(255,255,255,0.7)", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {HamburgerIcon}
            </button>
            {mobileOpen && (
              <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1000, overflowY: "auto", background: "var(--nav-bg)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1.25rem", height: 56, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  {Logo}
                  <button onClick={() => setMobileOpen(false)} aria-label="Close menu" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "7px 11px", minHeight: 44, cursor: "pointer", color: "rgba(255,255,255,0.7)", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✕</button>
                </div>
                <div style={{ paddingBottom: "1rem" }}>
                  <button onClick={() => go("how-it-works")} style={mobileItemStyle(page === "how-it-works")}>How it works</button>
                  <button onClick={() => go("for-builders")} style={mobileItemStyle(page === "for-builders")}>For builders</button>
                  <button onClick={() => go("for-lenders")} style={mobileItemStyle(page === "for-lenders")}>For lenders</button>
                  <button onClick={() => go("safety")} style={mobileItemStyle(page === "safety")}>Trust &amp; Safety</button>
                  <div style={{ padding: "12px 1.25rem 0", borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                    <button onClick={() => go("auth", { authTab: "login" })} style={{ width: "100%", padding: "11px", fontSize: 14, fontWeight: 500, background: "transparent", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, cursor: "pointer" }}>Log in</button>
                    <button onClick={() => go("auth", { authTab: "signup" })} style={{ width: "100%", padding: "11px", fontSize: 14, fontWeight: 600, background: "#3B82F6", color: "#FFFFFF", border: "none", borderRadius: 8, cursor: "pointer" }}>Sign up</button>
                  </div>
                </div>
              </div>
            )}
            </>}
          </div>
        </nav>
      );
    }

    return (
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, overflow: "hidden", height: 60, background: "var(--nav-bg)", borderBottom: "1px solid rgba(255,255,255,0.08)", boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.35)" : "none", transition: "box-shadow 0.25s ease" }}>
        <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2rem", height: "100%" }}>
        {Logo}
        {!WAITLIST_MODE && (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button onClick={() => go("how-it-works")} style={navLinkStyle(page === "how-it-works")}>How it works</button>
          <button onClick={() => go("for-builders")} style={navLinkStyle(page === "for-builders")}>For builders</button>
          <button onClick={() => go("for-lenders")} style={navLinkStyle(page === "for-lenders")}>For lenders</button>
          {/* Help dropdown */}
          <div ref={helpRef} style={{ position: "relative" }}>
            <button onClick={() => setHelpOpen(o => !o)}
              style={{ ...navLinkStyle(page === "safety"), gap: 5 }}>
              Help
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ transition: "transform 0.18s", transform: helpOpen ? "rotate(180deg)" : "none" }}><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            {helpOpen && (
              <div style={{ ...NAV_DROPDOWN_STYLE, left: "50%", transform: "translateX(-50%)" }}>
                <button onClick={() => { go("help"); setHelpOpen(false); }} style={ddItemStyle(page === "help")}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9.5" stroke="#2E5FA3" strokeWidth="1.5"/><path d="M12 8v4M12 16h.01" stroke="#2E5FA3" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  Help Centre
                </button>
                <button onClick={() => { go("how-it-works"); setHelpOpen(false); }} style={ddItemStyle(page === "how-it-works")}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9.5" stroke="#2E5FA3" strokeWidth="1.5"/><path d="M8 12l3 3 5-6" stroke="#2E5FA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  How it works
                </button>
                <button onClick={() => { go("safety"); setHelpOpen(false); }} style={ddItemStyle(page === "safety")}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z" stroke="#2E5FA3" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                  Trust &amp; Safety
                </button>
              </div>
            )}
          </div>
        </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {!WAITLIST_MODE && <button onClick={() => go("auth", { authTab: "login" })} style={{ padding: "7px 16px", fontSize: 13, fontWeight: 500, background: "transparent", color: "rgba(255,255,255,0.8)", border: "none", borderRadius: 8, cursor: "pointer" }}>Log in</button>}
          {!WAITLIST_MODE && <button onClick={() => go("auth", { authTab: "signup" })} style={{ padding: "8px 20px", fontSize: 13, fontWeight: 600, background: "#3B82F6", color: "#FFFFFF", border: "none", borderRadius: 8, cursor: "pointer" }}>Sign up</button>}
        </div>
        </div>
      </nav>
    );
  }

  // ── LOGGED IN ───────────────────────────────────────────────────────────────

  const primaryNavItems = role === "builder"
    ? [
        { label: "Find a lender",   target: "search",          tourId: "tour-find-lender" },
        { label: "Posts",           target: "posts" },
        { label: "Browse projects", target: "browse-projects", tourId: "tour-browse-projects" },
      ]
    : role === "lender"
    ? [
        { label: "Find a builder",  target: "find-builder",    tourId: "tour-find-builder" },
        { label: "Posts",           target: "posts" },
        { label: "Browse projects", target: "browse-projects", tourId: "tour-browse-projects" },
      ]
    : [
        { label: "Find a lender",   target: "search",          tourId: "tour-find-lender" },
        { label: "Find a builder",  target: "find-builder",    tourId: "tour-find-builder" },
        { label: "Browse projects", target: "browse-projects", tourId: "tour-browse-projects" },
      ];

  const moreNavItems = [
    { label: "Build Calculator", target: "build-calculator" },
    { label: "Community",        target: "community-groups" },
    { label: "Market",           target: "market" },
  ];

  const profileItems = [
    { label: "My profile",      icon: "user",        target: "profile-setup" },
    { label: "Saved profiles",  icon: "heart",       target: "saved-profiles" },
    { label: "My posts",        icon: "pencil",      target: "my-posts" },
    { label: "Project tracker", icon: "bar-chart",   target: "deals",         tourId: "tour-project-tracker" },
    { label: "Disputes",        icon: "scale",       target: "disputes" },
    ...(role === "lender" ? [{ label: "Requests", icon: "inbox",       target: "lender-dashboard" }] : []),
    ...(role === "admin"  ? [{ label: "Admin",    icon: "settings",    target: "admin" }, { label: "Analytics", icon: "trending-up", target: "analytics" }] : []),
    { divider: true },
    { label: "Notifications",   icon: "bell",        target: "notifications" },
    { label: "Settings",        icon: "settings",    target: "account" },
  ];

  if (isMobile && width < 768) {
    // Bottom nav handles navigation; top nav shows only logo + notification bell
    return (
      <nav style={{ background: "var(--nav-bg)", borderBottom: "1px solid rgba(255,255,255,0.08)", position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.35)" : "none", transition: "box-shadow 0.25s ease" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1.25rem", height: 56 }}>
          {Logo}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button onClick={() => go("help")} aria-label="Help Centre"
              style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.18)", background: page === "help" ? "rgba(255,255,255,0.14)" : "transparent", color: "rgba(255,255,255,0.7)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, lineHeight: 1 }}>
              ?
            </button>
            <NotificationBell user={user} />
          </div>
        </div>
      </nav>
    );
  }

  if (isMobile) {
    return (
      <nav style={{ background: "var(--nav-bg)", borderBottom: "1px solid rgba(255,255,255,0.08)", position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.35)" : "none", transition: "box-shadow 0.25s ease" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1.25rem", height: 56 }}>
          {Logo}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button id="tour-inbox-btn" onClick={() => go("messages")} aria-label="Inbox"
              style={{ position: "relative", background: page === "messages" ? "rgba(255,255,255,0.12)" : "transparent", border: "none", borderRadius: 8, padding: "8px 10px", minHeight: 44, cursor: "pointer", color: page === "messages" ? "var(--accent)" : "rgba(255,255,255,0.7)", lineHeight: 0, display: "flex", alignItems: "center" }}>
              <NavInboxIcon />
              {unreadCount > 0 && <span style={UNREAD_BADGE}>{unreadCount > 9 ? "9+" : unreadCount}</span>}
            </button>
            <button onClick={() => go("notifications")} aria-label="Notifications"
              style={{ position: "relative", background: page === "notifications" ? "rgba(255,255,255,0.12)" : "transparent", border: "none", borderRadius: 8, padding: "8px 10px", minHeight: 44, cursor: "pointer", color: page === "notifications" ? "var(--accent)" : "rgba(255,255,255,0.7)", lineHeight: 0, display: "flex", alignItems: "center" }}>
              <BellIcon />
            </button>
            <button onClick={() => setMobileOpen(o => !o)} aria-label="Menu"
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "7px 11px", minHeight: 44, cursor: "pointer", color: "rgba(255,255,255,0.7)", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {HamburgerIcon}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1000, overflowY: "auto", background: "var(--nav-bg)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1.25rem", height: 56, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              {Logo}
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "7px 11px", minHeight: 44, cursor: "pointer", color: "rgba(255,255,255,0.7)", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ paddingBottom: "1rem" }}>
              <button onClick={() => go("home")} style={mobileItemStyle(page === "home")}>Dashboard</button>
              <div style={{ padding: "10px 1.25rem 4px" }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2 }}>Discover</div>
                {primaryNavItems.map(({ label, target, tourId }) => (
                  <button key={target} id={tourId} onClick={() => go(target)} style={mobileItemStyle(page === target)}>
                    {label}
                  </button>
                ))}
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2, marginTop: 8 }}>More</div>
                {moreNavItems.map(({ label, target, community }) => (
                  <button key={target} onClick={() => go(target)} style={{ ...mobileItemStyle(page === target), display: "flex", alignItems: "center", gap: 6 }}>
                    {label}
                    {community && onlineCount > 0 && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22C55E" }} />
                        <span style={{ fontSize: 11, color: "#22C55E", fontWeight: 700 }}>{onlineCount}</span>
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 4, padding: "10px 0 0" }}>
                <div style={{ padding: "0 1.25rem 4px" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2 }}>Account</div>
                </div>
                {profileItems.filter(i => !i.divider).map(({ label, target }) => (
                  <button key={target} onClick={() => go(target)} style={mobileItemStyle(page === target)}>{label}</button>
                ))}
                <button onClick={() => go("help")} style={mobileItemStyle(page === "help")}>Help Centre</button>
                <button onClick={onLogout} style={{ ...mobileItemStyle(false), color: "#D85A30" }}>Log out</button>
              </div>
            </div>
          </div>
        )}
      </nav>
    );
  }

  // ── Desktop logged-in ──────────────────────────────────────────────────────
  return (
    <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2rem", height: 60, background: "var(--nav-bg)", borderBottom: "1px solid rgba(255,255,255,0.08)", position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.35)" : "none", transition: "box-shadow 0.25s ease" }}>
      {Logo}

      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {/* Dashboard */}
        <button onClick={() => go("home")} style={navLinkStyle(page === "home")}>
          <NavDashboardIcon /> Dashboard
        </button>

        {/* Primary nav links — role-specific */}
        {primaryNavItems.map(({ label, target, tourId }) => (
          <button key={target} id={tourId} onClick={() => go(target)} style={navLinkStyle(page === target)}>
            {label}
          </button>
        ))}

        {/* Market — always visible on desktop */}
        <button onClick={() => go("market")} style={navLinkStyle(page === "market")}><Icon name="trending-up" size={13} /> Market</button>

        {/* More dropdown */}
        <div ref={moreRef} style={{ position: "relative" }}>
          <button onClick={() => setMoreOpen(o => !o)} style={{ ...navLinkStyle(moreNavItems.some(i => i.target === page && i.target !== "market")), gap: 4 }}>
            More
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ transition: "transform 0.18s", transform: moreOpen ? "rotate(180deg)" : "none" }}><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          {moreOpen && (
            <div style={{ ...NAV_DROPDOWN_STYLE, left: "50%", transform: "translateX(-50%)" }}>
              {moreNavItems.map(({ label, target, community }) => (
                <button key={target} onClick={() => { go(target); setMoreOpen(false); }} style={{ ...ddItemStyle(page === target), display: "flex", alignItems: "center", gap: 8 }}>
                  {label}
                  {community && onlineCount > 0 && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: "auto" }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22C55E" }} />
                      <span style={{ fontSize: 11, color: "#22C55E", fontWeight: 700 }}>{onlineCount}</span>
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, overflow: "visible" }}>
        <NotificationBell user={user} onReplayTour={onReplayTour} />

        {/* Profile dropdown */}
        <div ref={profileRef} style={{ position: "relative" }}>
          <button id="tour-profile-pill" onClick={() => setProfileOpen(o => !o)}
            style={{ display: "flex", alignItems: "center", gap: 7, background: profileOpen ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 24, padding: "4px 10px 4px 4px", cursor: "pointer" }}>
            {avatarUrl
              ? <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}><img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>
              : <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#3B82F6", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{nameInitials(displayName)}</div>
            }
            <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.85)" }}>{displayName.split(" ")[0]}</span>
            <NavChevron open={profileOpen} />
          </button>
          {(profileOpen || tourForceProfileOpen) && (() => {
            const otherAccounts = navAccounts.filter(a => a.user_id !== user?.id);

            async function doNavSwitch(acct) {
              if (navSwitchLoading) return;
              const acctFirstName = acct.name?.split(" ")[0] || acct.email.split("@")[0];

              // Store in sessionStorage so the overlay persists through the page reload
              sessionStorage.setItem("lb_switching", JSON.stringify({ name: acctFirstName }));

              // Inject overlay immediately for visual feedback during the async setSession call
              const overlay = document.createElement("div");
              overlay.style.cssText = "position:fixed;inset:0;z-index:9999;background:#1E3A5F;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;opacity:0;transition:opacity 150ms ease;";
              overlay.innerHTML = `<div style="font-family:'Playfair Display',Georgia,serif;font-size:28px;font-weight:700;letter-spacing:-0.5px;"><span style="color:#fff">Lender</span><span style="color:#3B82F6">Build</span></div><div style="display:flex;align-items:center;gap:10px;color:rgba(255,255,255,0.75);font-size:15px;"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation:lb-spin 0.8s linear infinite"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>Switching to ${acctFirstName}…</div><style>@keyframes lb-spin{to{transform:rotate(360deg)}}</style>`;
              document.body.appendChild(overlay);
              requestAnimationFrame(() => requestAnimationFrame(() => { overlay.style.opacity = "1"; }));
              setNavSwitchLoading(acct.user_id);

              const { data: { session: curSess } } = await supabase.auth.getSession();
              if (curSess && user) {
                upsertSavedAccount({
                  user_id: user.id, email: user.email, name: displayName,
                  avatar_url: avatarUrl || null, role: user.user_metadata?.role || null,
                  member_id: userProfile?.sequential_id || null,
                  access_token: curSess.access_token, refresh_token: curSess.refresh_token,
                });
              }
              const { error } = await supabase.auth.setSession({ access_token: acct.access_token, refresh_token: acct.refresh_token });
              if (error) {
                // Clear sessionStorage so we don't show a stale overlay on the next page load
                sessionStorage.removeItem("lb_switching");
                // Restore the current session so the user is NOT logged out
                if (curSess) await supabase.auth.setSession({ access_token: curSess.access_token, refresh_token: curSess.refresh_token });
                overlay.style.opacity = "0";
                setTimeout(() => { try { document.body.removeChild(overlay); } catch(e) {} }, 150);
                setNavSwitchLoading(null);
                setNavExpiredAcct(acct);
                setNavExpiredPw("");
                setNavExpiredErr("");
                return;
              }
              window.location.href = "/";
            }

            async function handleNavSwitchWithPassword(e) {
              e.preventDefault();
              setNavSwitchLoading(navExpiredAcct.user_id);
              setNavExpiredErr("");
              const { data, error } = await supabase.auth.signInWithPassword({ email: navExpiredAcct.email, password: navExpiredPw });
              if (error) { setNavSwitchLoading(null); setNavExpiredErr(error.message); return; }
              upsertSavedAccount({ ...navExpiredAcct, access_token: data.session.access_token, refresh_token: data.session.refresh_token });
              const firstName = navExpiredAcct.name?.split(" ")[0] || navExpiredAcct.email.split("@")[0];
              sessionStorage.setItem("lb_switching", JSON.stringify({ name: firstName }));
              window.location.href = "/";
            }

            return (
              <div style={{ ...NAV_DROPDOWN_STYLE, right: 0, left: "auto", minWidth: 320, maxHeight: "80vh", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
                <div style={{ padding: "10px 14px 9px", borderBottom: "0.5px solid #f0f0f0" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{displayName}</div>
                  {userProfile?.sequential_id && <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>{fmtId(userProfile.sequential_id)}</div>}
                  <div style={{ marginTop: 5 }}>
                    <RoleBadge userRole={userProfile?.user_role} authRole={role} />
                  </div>
                </div>

                {/* Saved accounts for switching */}
                {otherAccounts.length > 0 && (
                  <div style={{ borderBottom: "0.5px solid #f0f0f0" }}>
                    <div style={{ padding: "6px 14px 4px", fontSize: 10, fontWeight: 600, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.07em" }}>Switch account</div>
                    {otherAccounts.map(acct => (
                      <div key={acct.user_id}>
                        <button onClick={() => doNavSwitch(acct)} disabled={navSwitchLoading !== null}
                          style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "8px 14px", fontSize: 12, background: "transparent", border: "none", cursor: navSwitchLoading ? "default" : "pointer", textAlign: "left" }}
                          onMouseEnter={e => { if (!navSwitchLoading) e.currentTarget.style.background = "#f9f9f7"; }}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          {acct.avatar_url
                            ? <div style={{ width: 26, height: 26, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}><img src={acct.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>
                            : <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#EBF2FF", color: "#1E3A5F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{nameInitials(acct.name)}</div>
                          }
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "nowrap" }}>
                              <span style={{ fontWeight: 500, color: "#222", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acct.name || acct.email.split("@")[0]}</span>
                              {acct.role && (
                                <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: acct.role === "lender" ? "#EFF6FF" : "#F0FDF4", color: acct.role === "lender" ? "#2563EB" : "#16A34A", border: `1px solid ${acct.role === "lender" ? "#BFDBFE" : "#BBF7D0"}`, flexShrink: 0, whiteSpace: "nowrap" }}>
                                  {acct.role === "lender" ? "Lender" : "Builder"}
                                </span>
                              )}
                              {acct.member_id && (
                                <span style={{ fontSize: 9, color: "#aaa", fontWeight: 500, flexShrink: 0, whiteSpace: "nowrap" }}>{fmtId(acct.member_id)}</span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: "#aaa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acct.email}</div>
                          </div>
                        </button>
                        {navExpiredAcct?.user_id === acct.user_id && (
                          <form onSubmit={handleNavSwitchWithPassword} style={{ padding: "8px 14px 12px 14px", paddingRight: 8, boxSizing: "border-box", background: "#FFFBEB", borderTop: "0.5px solid #f0f0f0" }}>
                            <div style={{ fontSize: 12, color: "#92400E", marginBottom: 8 }}>Session expired — enter password for {acct.email}</div>
                            {navExpiredErr && <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 6 }}>{navExpiredErr}</div>}
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              <input type="password" placeholder="Password" value={navExpiredPw} onChange={e => setNavExpiredPw(e.target.value)} required autoFocus
                                style={{ flex: "1 1 140px", minWidth: 0, height: 34, border: "1px solid #E2E8F0", borderRadius: 7, padding: "0 10px", fontSize: 13, outline: "none" }} />
                              <button type="submit" disabled={navSwitchLoading !== null}
                                style={{ flexShrink: 0, padding: "0 12px", height: 34, background: "var(--accent)", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: navSwitchLoading ? "default" : "pointer" }}>
                                {navSwitchLoading === acct.user_id ? "…" : "Go"}
                              </button>
                              <button type="button" onClick={() => setNavExpiredAcct(null)}
                                style={{ flexShrink: 0, padding: "0 10px", height: 34, background: "transparent", color: "#64748B", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 13, cursor: "pointer" }}>
                                Cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {profileItems.map((item, i) =>
                  item.divider
                    ? <div key={i} style={{ borderTop: "0.5px solid #f0f0f0", margin: "4px 0" }} />
                    : <button key={item.target} id={item.tourId} onClick={() => go(item.target)} style={ddItemStyle(page === item.target)}>
                        <Icon name={item.icon} size={15} className="lb-icon" />{item.label}
                      </button>
                )}
                <div style={{ borderTop: "0.5px solid #f0f0f0", margin: "4px 0" }} />
                { /* Saved searches */ }
                <button onClick={() => go("saved-searches")} style={ddItemStyle(page === "saved-searches")}>
                  <Icon name="search" size={14} className="lb-icon" />Saved searches
                </button>
                {navAccounts.length < 3 && !navAddingAcct && (
                  <button onClick={() => setNavAddingAcct(true)} style={{ ...ddItemStyle(false), color: "#3B82F6" }}>
                    <span style={{ fontSize: 15 }}>＋</span> Add account
                  </button>
                )}
                {navAddingAcct && (
                  <form onSubmit={handleNavAddAccount} style={{ padding: "10px 14px 14px", borderTop: "0.5px solid #f0f0f0" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", marginBottom: 8 }}>Log in to another account</div>
                    {navAddError && <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 6 }}>{navAddError}</div>}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <input type="email" placeholder="Email" value={navAddEmail} onChange={e => setNavAddEmail(e.target.value)} required autoFocus
                        style={{ height: 36, border: "1px solid #E2E8F0", borderRadius: 7, padding: "0 10px", fontSize: 13, outline: "none" }} />
                      <input type="password" placeholder="Password" value={navAddPassword} onChange={e => setNavAddPassword(e.target.value)} required
                        style={{ height: 36, border: "1px solid #E2E8F0", borderRadius: 7, padding: "0 10px", fontSize: 13, outline: "none" }} />
                      <div style={{ display: "flex", gap: 6 }}>
                        <button type="submit" disabled={navAddLoading}
                          style={{ flex: 1, height: 36, background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: navAddLoading ? "default" : "pointer" }}>
                          {navAddLoading ? "Signing in…" : "Log in"}
                        </button>
                        <button type="button" onClick={() => { setNavAddingAcct(false); setNavAddEmail(""); setNavAddPassword(""); setNavAddError(""); }}
                          style={{ padding: "0 10px", height: 36, background: "transparent", color: "#64748B", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 13, cursor: "pointer" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                )}
                <button onClick={onLogout} style={{ ...ddItemStyle(false), color: "#D85A30" }}>
                  <span style={{ fontSize: 15 }}>→</span> Log out
                </button>
              </div>
            );
          })()}
        </div>
      </div>
    </nav>
  );
}

// ─── AUTH PAGE ───────────────────────────────────────────────────────────────

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "#eee" };
  let score = 0;
  if (pw.length >= 8)              score++;
  if (pw.length >= 12)             score++;
  if (/[A-Z]/.test(pw))           score++;
  if (/[0-9]/.test(pw))           score++;
  if (/[^A-Za-z0-9]/.test(pw))   score++;
  if (score <= 1) return { score, label: "Weak",   color: "#D85A30" };
  if (score <= 3) return { score, label: "Fair",   color: "#2E5FA3" };
  return               { score, label: "Strong", color: "#1D9E75" };
}

function AuthPage({ setPage, onLoginSuccess, initialTab = "login", initialEmail = "", waitlistMode = false }) {
  const [tab, setTab] = useState(waitlistMode ? "login" : initialTab);
  const [loginStep, setLoginStep] = useState("credentials");
  useEffect(() => { setTab(waitlistMode ? "login" : initialTab); }, [initialTab, waitlistMode]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  useEffect(() => { if (initialEmail) setEmail(initialEmail); }, [initialEmail]);
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("builder");
  const [mfaCode, setMfaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loginAttempts, setLoginAttempts] = useState([]);

  async function handleLogin(e) {
    e.preventDefault();
    // Client-side rate limit: 5 attempts per 60 seconds
    const now = Date.now();
    const recent = loginAttempts.filter(t => now - t < 60_000);
    if (recent.length >= 5) {
      const waitSec = Math.ceil((60_000 - (now - recent[0])) / 1000);
      setError(`Too many login attempts. Please wait ${waitSec} seconds and try again.`);
      return;
    }
    setLoginAttempts([...recent, now]);
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError(error.message); setLoading(false); return; }
    // Check whether MFA verification is required
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setLoading(false);
    if (aal?.nextLevel === "aal2" && aal.nextLevel !== aal.currentLevel) {
      setLoginStep("mfa");
      return;
    }
    // Redirect to profile setup if the user hasn't completed it yet
    const { data: { user: freshUser } } = await supabase.auth.getUser();
    const { data: { session: freshSess } } = await supabase.auth.getSession();
    if (freshUser && freshSess) {
      let acctRole = freshUser.user_metadata?.role || null;
      let acctMemberId = null;
      const { data: prof } = await supabase.from("profiles").select("role, sequential_id").eq("id", freshUser.id).maybeSingle();
      if (!acctRole) acctRole = prof?.role || null;
      acctMemberId = prof?.sequential_id || null;
      upsertSavedAccount({
        user_id: freshUser.id, email: freshUser.email,
        name: freshUser.user_metadata?.name || freshUser.email.split("@")[0],
        avatar_url: freshUser.user_metadata?.avatar_url || null, role: acctRole,
        member_id: acctMemberId,
        access_token: freshSess.access_token, refresh_token: freshSess.refresh_token,
      });
    }
    if (freshUser?.user_metadata?.profile_complete) {
      onLoginSuccess ? onLoginSuccess(freshUser) : setPage("home");
    } else {
      setPage("profile-setup");
    }
  }

  async function handleMfaVerify(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const totp = factors?.totp?.[0];
    if (!totp) { setError("No authenticator app found."); setLoading(false); return; }
    const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId: totp.id });
    if (cErr) { setError(cErr.message); setLoading(false); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({ factorId: totp.id, challengeId: challenge.id, code: mfaCode });
    setLoading(false);
    if (vErr) { setError(vErr.message); return; }
    const { data: { user: freshUser } } = await supabase.auth.getUser();
    const { data: { session: freshSess } } = await supabase.auth.getSession();
    if (freshUser && freshSess) {
      let acctRole = freshUser.user_metadata?.role || null;
      let acctMemberId = null;
      const { data: prof } = await supabase.from("profiles").select("role, sequential_id").eq("id", freshUser.id).maybeSingle();
      if (!acctRole) acctRole = prof?.role || null;
      acctMemberId = prof?.sequential_id || null;
      upsertSavedAccount({
        user_id: freshUser.id, email: freshUser.email,
        name: freshUser.user_metadata?.name || freshUser.email.split("@")[0],
        avatar_url: freshUser.user_metadata?.avatar_url || null, role: acctRole,
        member_id: acctMemberId,
        access_token: freshSess.access_token, refresh_token: freshSess.refresh_token,
      });
    }
    if (freshUser?.user_metadata?.profile_complete) {
      onLoginSuccess ? onLoginSuccess(freshUser) : setPage("home");
    } else {
      setPage("profile-setup");
    }
  }

  async function handleSignup(e) {
    e.preventDefault();
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (password.length < 8)              { setError("Password must be at least 8 characters."); return; }
    if (!/[0-9]/.test(password))         { setError("Password must include at least one number."); return; }
    if (!/[^A-Za-z0-9]/.test(password))  { setError("Password must include at least one special character (e.g. !@#$%)."); return; }
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name: name.trim(), role },
        emailRedirectTo: "https://y-pi-blush.vercel.app",
      },
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setSuccess("Account created — check your email to confirm, then log in and start matching.");
  }

  const inputStyle = {
    width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8,
    padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box",
  };

  const labelStyle = { fontSize: 13, color: "#555", marginBottom: 4, display: "block" };

  return (
    <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem 1.25rem" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{
            display: "inline-block", background: "#EBF2FF", color: "#1E3A5F",
            fontSize: 12, fontWeight: 500, padding: "4px 14px", borderRadius: 20, marginBottom: "1rem",
          }}>
            LenderBuild
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 500, margin: 0, fontFamily: "'Playfair Display', Georgia, serif", color: "#1E3A5F" }}>
            {tab === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p style={{ fontSize: 14, color: "#64748B", marginTop: 8 }}>
            {tab === "login" ? "Sign in to connect with lenders and builders" : "Connect builders with the right lenders across the UK"}
          </p>
        </div>

        {/* Tab switcher */}
        <div style={{ display: "flex", borderBottom: "0.5px solid #e0e0e0", marginBottom: "1.75rem" }}>
          {(waitlistMode ? ["login"] : ["login", "signup"]).map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setError(""); setSuccess(""); }}
              style={{
                flex: 1, padding: "10px 0", fontSize: 14, fontWeight: 500, cursor: "pointer",
                border: "none", background: "transparent", minHeight: 44,
                color: tab === t ? "#3B82F6" : "#64748B",
                borderBottom: tab === t ? "2px solid #1D9E75" : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {t === "login" ? "Log in" : "Sign up"}
            </button>
          ))}
        </div>

        {error && (
          <div style={{
            background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB",
            borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem",
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            background: "#EBF2FF", color: "#1E3A5F", border: "0.5px solid #A8DFC9",
            borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem",
          }}>
            {success}
          </div>
        )}

        {tab === "login" && loginStep === "mfa" ? (
          <form onSubmit={handleMfaVerify} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={{ textAlign: "center", padding: "1rem 0 0.5rem" }}>
              <div style={{ marginBottom: 8, color: "var(--accent)" }}><Icon name="lock" size={32} /></div>
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Two-factor authentication</div>
              <div style={{ fontSize: 13, color: "#64748B" }}>Enter the 6-digit code from your authenticator app.</div>
            </div>
            <div>
              <label style={labelStyle}>Authentication code</label>
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000" style={{ ...inputStyle, letterSpacing: "0.3em", textAlign: "center", fontSize: 20 }}
                autoFocus
              />
            </div>
            <button
              type="submit" disabled={loading || mfaCode.length < 6}
              style={{
                height: 44, background: loading ? "#aaa" : "#3B82F6", color: loading ? "#fff" : "#1E3A5F",
                border: "none", borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: loading ? "default" : "pointer",
              }}
            >
              {loading ? "Verifying..." : "Verify"}
            </button>
            <p style={{ textAlign: "center", fontSize: 12, color: "#aaa", margin: 0 }}>
              <span onClick={() => { setLoginStep("credentials"); setError(""); setMfaCode(""); }} style={{ color: "#3B82F6", cursor: "pointer" }}>
                Back to login
              </span>
            </p>
          </form>
        ) : tab === "login" ? (
          <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" autoComplete="username" name="email" style={inputStyle}
              />
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
                <span
                  onClick={() => setPage("forgot-password")}
                  style={{ fontSize: 12, color: "#3B82F6", cursor: "pointer" }}
                >
                  Forgot password?
                </span>
              </div>
              <input
                type="password" required value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Your password" autoComplete="current-password" name="password" style={inputStyle}
              />
            </div>
            <button
              type="submit" disabled={loading}
              style={{
                height: 44, background: loading ? "#aaa" : "#3B82F6", color: loading ? "#fff" : "#1E3A5F",
                border: "none", borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: loading ? "default" : "pointer",
                marginTop: 4,
              }}
            >
              {loading ? "Signing in..." : "Log in"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSignup} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>Full name</label>
              <input
                type="text" required value={name} onChange={e => setName(e.target.value)}
                placeholder="Your name" style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" autoComplete="username" name="email" style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input
                type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="8+ chars, 1 number, 1 special character" autoComplete="new-password" name="new-password" style={inputStyle}
              />
              {password.length > 0 && (() => {
                const s = getPasswordStrength(password);
                return (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} style={{
                          flex: 1, height: 3, borderRadius: 2,
                          background: i <= s.score ? s.color : "#eee",
                          transition: "background 0.2s",
                        }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: s.color, marginTop: 3 }}>{s.label}</div>
                  </div>
                );
              })()}
            </div>
            <div>
              <label style={labelStyle}>I am a…</label>
              <div style={{ display: "flex", gap: 10 }}>
                {["builder", "lender"].map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    style={{
                      flex: 1, height: 42, borderRadius: 8, fontSize: 14, fontWeight: 500,
                      cursor: "pointer", textTransform: "capitalize",
                      border: role === r ? "none" : "0.5px solid #ccc",
                      background: role === r ? "#3B82F6" : "#fff",
                      color: role === r ? "#fff" : "#555",
                    }}
                  >
                    {r === "builder" ? "Builder" : "Lender"}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit" disabled={loading}
              style={{
                height: 44, background: loading ? "#aaa" : "#3B82F6", color: loading ? "#fff" : "#1E3A5F",
                border: "none", borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: loading ? "default" : "pointer",
                marginTop: 4,
              }}
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>
        )}

        <p style={{ textAlign: "center", fontSize: 12, color: "#aaa", marginTop: "1.5rem" }}>
          {tab === "login" ? (
            !waitlistMode && <>Don't have an account?{" "}
              <span onClick={() => { setTab("signup"); setError(""); setSuccess(""); }} style={{ color: "#3B82F6", cursor: "pointer" }}>Sign up</span>
            </>
          ) : (
            <>Already have an account?{" "}
              <span onClick={() => { setTab("login"); setError(""); setSuccess(""); }} style={{ color: "#3B82F6", cursor: "pointer" }}>Log in</span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

// ─── FORGOT PASSWORD PAGE ────────────────────────────────────────────────────

function ForgotPasswordPage({ setPage }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setSent(true);
  }

  const inputStyle = {
    width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8,
    padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box",
  };

  return (
    <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem 1.25rem" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ marginBottom: "0.75rem", color: "var(--accent)" }}><Icon name="key" size={36} /></div>
          <h1 style={{ fontSize: 24, fontWeight: 500, margin: "0 0 8px", fontFamily: "'Georgia', serif" }}>Reset your password</h1>
          <p style={{ fontSize: 14, color: "#64748B", margin: 0 }}>
            {sent ? "Check your inbox for the reset link." : "Enter your email and we'll send you a reset link."}
          </p>
        </div>

        {error && (
          <div style={{ background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem" }}>
            {error}
          </div>
        )}

        {sent ? (
          <div style={{ background: "#EBF2FF", border: "0.5px solid #E0C87A", borderRadius: 12, padding: "1.5rem", textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "#1E3A5F", marginBottom: "1rem" }}>
              We've sent a password reset link to <strong>{email}</strong>. Click the link in the email to choose a new password.
            </div>
            <button
              onClick={() => setPage("auth")}
              style={{ background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "8px 20px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Back to log in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={{ fontSize: 13, color: "#555", marginBottom: 4, display: "block" }}>Email address</label>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" style={inputStyle}
              />
            </div>
            <button
              type="submit" disabled={loading}
              style={{
                height: 44, background: loading ? "#aaa" : "#3B82F6", color: loading ? "#fff" : "#1E3A5F",
                border: "none", borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: loading ? "default" : "pointer",
              }}
            >
              {loading ? "Sending..." : "Send reset link"}
            </button>
            <p style={{ textAlign: "center", fontSize: 12, color: "#aaa", margin: 0 }}>
              <span onClick={() => setPage("auth")} style={{ color: "#3B82F6", cursor: "pointer" }}>Back to log in</span>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── RESET PASSWORD PAGE ──────────────────────────────────────────────────────

function ResetPasswordPage({ setPage }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setDone(true);
  }

  const inputStyle = {
    width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8,
    padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box",
  };

  return (
    <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem 1.25rem" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ marginBottom: "0.75rem", color: "var(--accent)" }}><Icon name="lock" size={36} /></div>
          <h1 style={{ fontSize: 24, fontWeight: 500, margin: "0 0 8px", fontFamily: "'Georgia', serif" }}>
            {done ? "Password updated" : "Choose a new password"}
          </h1>
          <p style={{ fontSize: 14, color: "#64748B", margin: 0 }}>
            {done ? "You can now log in with your new password." : "Must be at least 6 characters."}
          </p>
        </div>

        {error && (
          <div style={{ background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem" }}>
            {error}
          </div>
        )}

        {done ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#E1F5EE", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem", color: "#0F6E56" }}><Icon name="check-circle" size={28} /></div>
            <button
              onClick={() => setPage("auth")}
              style={{ background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "10px 24px", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer" }}
            >
              Log in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={{ fontSize: 13, color: "#555", marginBottom: 4, display: "block" }}>New password</label>
              <input
                type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 6 characters" style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 13, color: "#555", marginBottom: 4, display: "block" }}>Confirm password</label>
              <input
                type="password" required minLength={6} value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Same password again" style={inputStyle}
              />
            </div>
            <button
              type="submit" disabled={loading}
              style={{
                height: 44, background: loading ? "#aaa" : "#3B82F6", color: loading ? "#fff" : "#1E3A5F",
                border: "none", borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: loading ? "default" : "pointer",
              }}
            >
              {loading ? "Updating..." : "Update password"}
            </button>
          </form>
        )}
      </div>

    </div>
  );
}

// ─── PROFILE SETUP PAGE ───────────────────────────────────────────────────────

function ProfileSetupPage({ user, setPage, setCelebration }) {
  const role = user?.user_metadata?.role;

  // Profile photo
  const [avatarUrl,      setAvatarUrl]      = useState(user?.user_metadata?.avatar_url || "");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError,     setPhotoError]     = useState("");

  // Shared
  const [bio,      setBio]      = useState("");
  const [location, setLocation] = useState("");

  // Lender-specific
  const [budgetMax,         setBudgetMax]         = useState("");
  const [returnType,        setReturnType]        = useState("Fixed interest");
  const [interestRate,      setInterestRate]      = useState("");
  const [builderSplit,      setBuilderSplit]      = useState("60");
  const [lenderSplit,       setLenderSplit]       = useState("40");
  const [equityStake,       setEquityStake]       = useState("");
  const [preferredProjects, setPreferredProjects] = useState([]);

  // Builder-specific
  const [specialization,    setSpecialization]    = useState("Developer");
  const [projectsCompleted, setProjectsCompleted] = useState("");
  const [totalValue,        setTotalValue]        = useState("");
  const [completionRate,    setCompletionRate]    = useState("100");

  // Companies House verification
  const [companyNumber,      setCompanyNumber]      = useState("");
  const [verifyingCompany,   setVerifyingCompany]   = useState(false);
  const [companyVerified,    setCompanyVerified]    = useState(false);
  const [companyPending,     setCompanyPending]     = useState(false);
  const [companyName,        setCompanyName]        = useState("");
  const [companyStatus,      setCompanyStatus]      = useState("");
  const [companyIncorp,      setCompanyIncorp]      = useState("");
  const [companyError,       setCompanyError]       = useState("");
  const [companyDirectors,   setCompanyDirectors]   = useState([]);
  const [companySelectedDirector, setCompanySelectedDirector] = useState("");
  const [companyDirectorMatch,    setCompanyDirectorMatch]    = useState(true);

  // Builder document uploads
  const [docUploads,      setDocUploads]      = useState({});  // { key: "uploading"|"done"|"error" }
  const [docSubmissions,  setDocSubmissions]  = useState({});  // { key: { status, file_path } }

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Pre-populate if the user already has profile data
  useEffect(() => {
    async function load() {
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle();
      if (profile?.bio)      setBio(profile.bio);
      if (profile?.location) setLocation(profile.location);
      // Load existing document submissions from database
      const { data: subs } = await supabase.from("document_submissions").select("document_type, status, file_path").eq("user_id", user.id);
      if (subs?.length) {
        const map = {};
        subs.forEach(s => { map[s.document_type] = { status: s.status, file_path: s.file_path }; });
        setDocSubmissions(map);
      }

      if (role === "lender") {
        const { data: lp } = await supabase.from("lender_profiles").select("*").eq("user_id", user.id).maybeSingle();
        if (lp) {
          if (lp.budget_max)        setBudgetMax(String(lp.budget_max));
          if (lp.return_type)       setReturnType(lp.return_type);
          if (lp.interest_rate)     setInterestRate(lp.interest_rate);
          if (lp.builder_split)     setBuilderSplit(lp.builder_split.replace("%", ""));
          if (lp.lender_split)      setLenderSplit(lp.lender_split.replace("%", ""));
          if (lp.equity_stake)      setEquityStake(lp.equity_stake);
          if (lp.preferred_projects?.length) setPreferredProjects(lp.preferred_projects);
          if (lp.company_number)    setCompanyNumber(lp.company_number);
          if (lp.verification_status === "verified")            { setCompanyVerified(true); setCompanyPending(false); }
          else if (lp.verification_status === "pending_review") { setCompanyVerified(false); setCompanyPending(true); }
          if (lp.company_name)      setCompanyName(lp.company_name);
          if (lp.company_status)    setCompanyStatus(lp.company_status);
          if (lp.company_incorporated) setCompanyIncorp(lp.company_incorporated);
        }
      } else if (role === "builder") {
        const { data: bp } = await supabase.from("builder_profiles").select("*").eq("user_id", user.id).maybeSingle();
        if (bp) {
          if (bp.specialization)              setSpecialization(bp.specialization);
          if (bp.projects_completed != null)  setProjectsCompleted(String(bp.projects_completed));
          if (bp.total_value != null)         setTotalValue(String(bp.total_value));
          if (bp.completion_rate != null)     setCompletionRate(String(bp.completion_rate));
          if (bp.company_number)              setCompanyNumber(bp.company_number);
          if (bp.verification_status === "verified")            { setCompanyVerified(true); setCompanyPending(false); }
          else if (bp.verification_status === "pending_review") { setCompanyVerified(false); setCompanyPending(true); }
          if (bp.company_name)                setCompanyName(bp.company_name);
          if (bp.company_status)              setCompanyStatus(bp.company_status);
          if (bp.company_incorporated)        setCompanyIncorp(bp.company_incorporated);
        }
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleProject(p) {
    setPreferredProjects(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setPhotoError("Photo must be under 5 MB"); return; }
    setPhotoError("");
    setUploadingPhoto(true);
    const path = `${user.id}/avatar`;
    // Delete any existing file first so we only need INSERT permission (not UPDATE)
    await supabase.storage.from("profile-photos").remove([path]);
    const { error: uploadErr } = await supabase.storage
      .from("profile-photos")
      .upload(path, file, { contentType: file.type });
    if (uploadErr) {
      setUploadingPhoto(false);
      setPhotoError(`Upload failed: ${uploadErr.message}`);
      e.target.value = "";
      return;
    }
    // Cache-bust so the browser fetches the new image even when the URL path is identical
    const { data: { publicUrl } } = supabase.storage.from("profile-photos").getPublicUrl(path);
    const bustedUrl = `${publicUrl}?t=${Date.now()}`;
    setAvatarUrl(bustedUrl);
    await supabase.auth.updateUser({ data: { avatar_url: bustedUrl } });
    setUploadingPhoto(false);
    e.target.value = "";
  }

  async function handleRemovePhoto() {
    setAvatarUrl("");
    // Delete the file from storage
    await supabase.storage.from("profile-photos").remove([`${user.id}/avatar`]);
    // Clear from auth metadata so the navbar/account avatar reverts immediately
    const { error: metaErr } = await supabase.auth.updateUser({ data: { avatar_url: null } });
    if (metaErr) {
      setPhotoError("Failed to remove photo. Please try again.");
      return;
    }
  }

  async function save(skipMode) {
    setSaving(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();

    let body;
    if (skipMode) {
      body = { location: location || "", bio: bio || "", avatar_url: avatarUrl || null };
    } else if (role === "lender") {
      body = {
        location, bio,
        budget_max:         budgetMax ? Number(budgetMax) : null,
        return_type:        returnType,
        interest_rate:      returnType === "Fixed interest" ? (interestRate || null) : null,
        builder_split:      returnType === "Rental split"   ? builderSplit + "%" : null,
        lender_split:       returnType === "Rental split"   ? lenderSplit  + "%" : null,
        equity_stake:       returnType === "Equity stake"   ? (equityStake || null) : null,
        preferred_projects: preferredProjects,
        avatar_url: avatarUrl || null,
      };
    } else {
      body = {
        location, bio,
        specialization,
        projects_completed: projectsCompleted !== "" ? Number(projectsCompleted) : 0,
        total_value:        totalValue        !== "" ? Number(totalValue)        : 0,
        completion_rate:    completionRate    !== "" ? Number(completionRate)    : 100,
        company_number:     companyNumber || undefined,
        avatar_url:         avatarUrl || null,
      };
    }

    const res = await fetch("/api/save-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(body),
    });
    setSaving(false);

    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || "We couldn't save that — please try again.");
      return;
    }

    await supabase.auth.refreshSession();

    // Celebration: profile 100% complete
    if (!skipMode && setCelebration) {
      const celKey = "lb_cel_profile_done";
      if (!localStorage.getItem(celKey)) {
        const avatarUrl2 = avatarUrl || null;
        const dataForScore = role === "lender"
          ? { avatar_url: avatarUrl2, bio, location, budget_max: budgetMax, return_type: returnType, preferred_projects: preferredProjects }
          : { avatar_url: avatarUrl2, bio, location, specialization, projects_completed: projectsCompleted };
        const { score } = computeProfileCompleteness(dataForScore, role);
        if (score >= 100) {
          localStorage.setItem(celKey, "1");
          setCelebration({ message: "Profile 100% complete!", subtitle: "You are now visible to all matches." });
        }
      }
    }

    setPage(skipMode ? "home" : role === "lender" ? "search" : "find-builder");
  }

  const inp = {
    width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8,
    padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box",
    fontFamily: "inherit",
  };
  const label = { fontSize: 13, color: "#555", marginBottom: 5, display: "block", fontWeight: 500 };
  const PROJECT_TYPES = ["Residential", "Commercial", "Mixed use", "Renovation"];
  const SPEC_TYPES    = ["Developer", "Contractor", "Renovator"];

  return (
    <div style={{ padding: "2rem 1.25rem", maxWidth: 560, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
          {role === "lender" ? "Lender" : "Builder"} profile
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 500, margin: "0 0 6px", fontFamily: "'Georgia', serif" }}>
          Set up your profile
        </h1>
        <p style={{ fontSize: 14, color: "#64748B", margin: 0 }}>
          {role === "lender"
            ? "Tell builders what you're looking to fund so they can reach out."
            : "Tell lenders about your work so they can find you."}
        </p>
      </div>

      {(() => {
        const data = role === "lender"
          ? { avatar_url: avatarUrl, bio, location, budget_max: budgetMax, return_type: returnType, preferred_projects: preferredProjects }
          : { avatar_url: avatarUrl, bio, location, specialization, projects_completed: projectsCompleted, verified_documents: user?.user_metadata?.verified_documents };
        const { score, missing } = computeProfileCompleteness(data, role);
        const barColor = score >= 80 ? "#1D9E75" : score >= 50 ? "var(--accent)" : "#D85A30";
        return (
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "14px 16px", marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: "#1E3A5F" }}>Profile completeness</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: barColor }}>{score}%</span>
            </div>
            <div style={{ height: 6, background: "#f0f0f0", borderRadius: 3, marginBottom: score < 100 && missing.length > 0 ? 8 : 0 }}>
              <div style={{ height: 6, borderRadius: 3, background: barColor, width: `${score}%`, transition: "width 0.4s" }} />
            </div>
            {score < 100 && missing.length > 0 && (
              <div style={{ fontSize: 12, color: "#64748B" }}>Still needed: {missing.map((m, i) => <span key={m.label}><span style={{ fontWeight: 500, color: "#555" }}>{m.label}</span>{i < missing.length - 1 ? " · " : ""}</span>)}</div>
            )}
          </div>
        );
      })()}

      {error && (
        <div style={{ background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      <form onSubmit={e => { e.preventDefault(); save(false); }} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

        {/* ── PROFILE PHOTO ──────────────────────────────────────────────── */}
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Profile photo</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Avatar initials={nameInitials(user?.user_metadata?.name)} color={pickColor(user?.id || "")} size={64} url={avatarUrl || undefined} />
            <div>
              <label style={{
                display: "inline-flex", alignItems: "center", padding: "0 16px",
                height: 44, minHeight: 44,
                background: uploadingPhoto ? "#f0f0f0" : "#fff",
                border: "0.5px solid #ccc", borderRadius: 8,
                fontSize: 13, cursor: uploadingPhoto ? "default" : "pointer", color: "#555",
              }}>
                {uploadingPhoto ? "Uploading…" : avatarUrl ? "Replace photo" : "Upload photo"}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhotoUpload} disabled={uploadingPhoto} />
              </label>
              {avatarUrl && !uploadingPhoto && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  style={{ marginLeft: 8, fontSize: 12, color: "#D85A30", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  Remove
                </button>
              )}
              {photoError && <div style={{ fontSize: 12, color: "#993C1D", marginTop: 6 }}>{photoError}</div>}
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>JPG, PNG or WebP · Max 5 MB</div>
            </div>
          </div>
        </div>

        {/* ── LENDER FIELDS ───────────────────────────────────────────────── */}
        {role === "lender" && (<>

          {/* Budget */}
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Investment budget</div>
            <div>
              <label style={label}>Maximum amount you can lend (£)</label>
              <input
                type="text" inputMode="numeric"
                value={fmtInput(budgetMax)} onChange={e => setBudgetMax(parseInput(e.target.value))}
                placeholder="e.g. 200,000" style={inp}
              />
            </div>
          </div>

          {/* Return type */}
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Return structure</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
              {["Fixed interest", "Rental split", "Equity stake"].map(rt => (
                <button
                  key={rt} type="button"
                  onClick={() => setReturnType(rt)}
                  style={{
                    flex: 1, minWidth: 110, padding: "8px 12px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer",
                    border: returnType === rt ? "none" : "0.5px solid #ccc",
                    background: returnType === rt ? "#3B82F6" : "#fff",
                    color:      returnType === rt ? "#fff"     : "#555",
                  }}
                >{rt}</button>
              ))}
            </div>

            <ReturnTypeExplainer returnType={returnType} onApply={rt => setReturnType(rt)}>
              {returnType === "Fixed interest" && (
                <div style={{ marginTop: 4 }}>
                  <label style={label}>Interest rate (e.g. "8% p.a.")</label>
                  <input type="text" value={interestRate} onChange={e => setInterestRate(e.target.value)} placeholder="8% p.a." style={inp} />
                </div>
              )}
              {returnType === "Rental split" && (
                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  <div style={{ flex: 1 }}>
                    <label style={label}>Builder share (%)</label>
                    <input type="number" min="0" max="100" value={builderSplit} onChange={e => setBuilderSplit(e.target.value)} placeholder="60" style={inp} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={label}>Lender share (%)</label>
                    <input type="number" min="0" max="100" value={lenderSplit} onChange={e => setLenderSplit(e.target.value)} placeholder="40" style={inp} />
                  </div>
                </div>
              )}
              {returnType === "Equity stake" && (
                <div style={{ marginTop: 4 }}>
                  <label style={label}>Equity stake (e.g. "15–25%")</label>
                  <input type="text" value={equityStake} onChange={e => setEquityStake(e.target.value)} placeholder="15–25%" style={inp} />
                </div>
              )}
            </ReturnTypeExplainer>
          </div>

          {/* Preferred projects */}
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Preferred project types</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {PROJECT_TYPES.map(p => {
                const on = preferredProjects.includes(p);
                return (
                  <button
                    key={p} type="button"
                    onClick={() => toggleProject(p)}
                    style={{
                      padding: "7px 14px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer",
                      border: on ? "none" : "0.5px solid #ccc",
                      background: on ? "#E1F5EE" : "#fff",
                      color:      on ? "#0F6E56" : "#555",
                    }}
                  >{p}</button>
                );
              })}
            </div>
          </div>

          <div style={{ background: "#EBF2FF", border: "0.5px solid #C7D7F5", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="shield" size={18} style={{ color: "#1E3A5F", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F" }}>Companies House verification</div>
              <div style={{ fontSize: 12, color: "#2E5FA3", marginTop: 2 }}>Go to <strong>Settings → Verification</strong> to verify your company.</div>
            </div>
          </div>

        </>)}

        {/* ── BUILDER FIELDS ──────────────────────────────────────────────── */}
        {role === "builder" && (<>

          {/* Specialization */}
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>What type of work do you do?</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {SPEC_TYPES.map(s => (
                <button
                  key={s} type="button"
                  onClick={() => setSpecialization(s)}
                  style={{
                    flex: 1, minWidth: 100, padding: "9px 12px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer",
                    border:      specialization === s ? "none" : "0.5px solid #ccc",
                    background:  specialization === s ? "#3B82F6" : "#fff",
                    color:       specialization === s ? "#fff"    : "#555",
                  }}
                >{s}</button>
              ))}
            </div>
          </div>

          {/* Track record */}
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Track record</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={label}>Projects completed</label>
                <input type="number" min="0" value={projectsCompleted} onChange={e => setProjectsCompleted(e.target.value)} placeholder="e.g. 5" style={inp} />
              </div>
              <div>
                <label style={label}>Total value of projects built (£)</label>
                <input type="text" inputMode="numeric" value={fmtInput(totalValue)} onChange={e => setTotalValue(parseInput(e.target.value))} placeholder="e.g. 500,000" style={inp} />
              </div>
              {Number(projectsCompleted) > 0 && (
                <div>
                  <label style={label}>On-time completion rate (%)</label>
                  <input type="number" min="0" max="100" value={completionRate} onChange={e => setCompletionRate(e.target.value)} placeholder="e.g. 95" style={inp} />
                </div>
              )}
            </div>
          </div>

          {/* Advanced section toggle */}
          {!showAdvanced && (
            <button type="button" onClick={() => setShowAdvanced(true)} style={{ width: "100%", padding: "12px 16px", minHeight: 44, background: "#F8FAFC", border: "0.5px dashed #94A3B8", borderRadius: 12, fontSize: 13, color: "#64748B", cursor: "pointer", fontWeight: 500, textAlign: "center" }}>
              + Complete full profile — add verification &amp; documents (optional)
            </button>
          )}

          {showAdvanced && <>

          </>}  {/* end showAdvanced */}

        </>)}

        {/* ── BUILDER DOCUMENT UPLOADS ──────────────────────────────────── */}
        {role === "builder" && showAdvanced && (() => {
          const DOC_TYPES = [
            { key: "photo_id",    label: "Photo ID",                   desc: "Passport or driving licence",            accept: "image/*,.pdf" },
            { key: "selfie",      label: "Selfie with ID",             desc: "A photo of you holding your ID",         accept: "image/*" },
            { key: "site_photos", label: "Site photos",                desc: "Photos of your previous work or sites",  accept: "image/*" },
            { key: "planning",    label: "Planning permission docs",   desc: "Planning consents or approvals",         accept: "image/*,.pdf" },
          ];

          async function handleDocUpload(key, file) {
            if (!file) return;
            setDocUploads(prev => ({ ...prev, [key]: "uploading" }));
            const ext = file.name.split(".").pop();
            const path = `${user.id}/${key}.${ext}`;
            const { error: uploadErr } = await supabase.storage
              .from("builder-documents")
              .upload(path, file, { upsert: true });
            if (uploadErr) {
              setDocUploads(prev => ({ ...prev, [key]: "error" }));
              return;
            }
            setDocUploads(prev => ({ ...prev, [key]: "done" }));
            await supabase.from("document_submissions").upsert(
              { user_id: user.id, document_type: key, file_path: path, status: "pending", submitted_at: new Date().toISOString(), reviewed_at: null },
              { onConflict: "user_id,document_type" }
            );
            setDocSubmissions(prev => ({ ...prev, [key]: { status: "pending", file_path: path } }));
          }

          return (
            <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Verification documents</div>
              <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 14px", lineHeight: 1.55 }}>
                Upload these for admin review. You'll receive an email once approved. Documents are stored securely.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {DOC_TYPES.map(({ key, label, desc, accept }) => {
                  const st        = docUploads[key];
                  const sub       = docSubmissions[key];
                  const subStatus = sub?.status; // "pending" | "approved" | "rejected" | undefined
                  const bg     = subStatus === "approved" ? "#f0faf6" : subStatus === "pending" ? "#fffbeb" : subStatus === "rejected" ? "#fef2f2" : "#fafaf8";
                  const border = subStatus === "approved" ? "#A8DFC9"  : subStatus === "pending" ? "#FCD34D"  : subStatus === "rejected" ? "#FCA5A5"  : "#e8e8e5";
                  return (
                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: bg, borderRadius: 8, border: `0.5px solid ${border}` }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {label}
                          {subStatus === "approved" && <span style={{ fontSize: 11, background: "#EBF2FF", color: "#1E3A5F", padding: "2px 7px", borderRadius: 20, fontWeight: 500 }}>✓ Verified</span>}
                          {subStatus === "pending"  && <span style={{ fontSize: 11, background: "#FEF3C7", color: "#B45309", padding: "2px 7px", borderRadius: 20, fontWeight: 500 }}>Document submitted — under review</span>}
                          {subStatus === "rejected" && <span style={{ fontSize: 11, background: "#FEE2E2", color: "#991B1B", padding: "2px 7px", borderRadius: 20, fontWeight: 500 }}>Rejected — please re-upload</span>}
                        </div>
                        <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{desc}</div>
                      </div>
                      <label style={{
                        display: "inline-flex", alignItems: "center",
                        padding: "0 14px", minHeight: 44,
                        background: st === "uploading" ? "#f0f0f0" : "#fff",
                        border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12,
                        cursor: st === "uploading" ? "default" : "pointer", color: "#555",
                        flexShrink: 0, whiteSpace: "nowrap",
                      }}>
                        {st === "uploading" ? "Uploading…" : st === "error" ? "Retry" : subStatus ? "Replace" : "Upload"}
                        <input
                          type="file" accept={accept} style={{ display: "none" }}
                          onChange={e => handleDocUpload(key, e.target.files[0])}
                          disabled={st === "uploading"}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
              {Object.values(docUploads).some(s => s === "error") && (
                <p style={{ fontSize: 12, color: "#993C1D", marginTop: 10 }}>
                  Some uploads failed. Please ensure the "builder-documents" storage bucket has been created in your Supabase project.
                </p>
              )}
            </div>
          );
        })()}

        {/* ── SHARED FIELDS (bio + location) ──────────────────────────────── */}
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>About you</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={label}>Location</label>
              <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Manchester, UK" style={inp} />
            </div>
            <div>
              <label style={label}>Bio</label>
              <textarea
                value={bio} onChange={e => setBio(e.target.value)}
                placeholder={role === "lender"
                  ? "Tell builders what you look for in a project, your experience, and how quickly you make decisions…"
                  : "Describe your speciality, the types of projects you take on, and what makes you a reliable partner…"}
                rows={4}
                style={{ ...inp, height: "auto", padding: "10px 12px", resize: "vertical", lineHeight: 1.55 }}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <button
          type="submit"
          disabled={saving}
          style={{
            width: "100%", height: 44, background: saving ? "#aaa" : "#3B82F6", color: "#fff",
            border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500,
            cursor: saving ? "default" : "pointer",
          }}
        >
          {saving ? "Saving…" : "Save and find matches"}
        </button>

      </form>
    </div>
  );
}

// ─── ACCOUNT PAGE ─────────────────────────────────────────────────────────────

// Pending sent requests list for AccountPage (Feature 2)
function PendingSentRequests({ requests, user }) {
  const [cancelStatus, setCancelStatus] = useState({});

  async function handleWithdraw(lenderName) {
    setCancelStatus(s => ({ ...s, [lenderName]: "loading" }));
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/connect-request", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "cancel-request", lender_name: lenderName }),
    });
    if (res.ok) {
      setCancelStatus(s => ({ ...s, [lenderName]: "done" }));
      await supabase.auth.refreshSession();
    } else {
      setCancelStatus(s => ({ ...s, [lenderName]: "error" }));
      setTimeout(() => setCancelStatus(s => ({ ...s, [lenderName]: undefined })), 3000);
    }
  }

  const visible = requests.filter(r => cancelStatus[r.lender_name] !== "done");
  if (visible.length === 0) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#D97706", marginBottom: 10 }}>
        Pending sent requests ({visible.length})
      </div>
      {visible.map((req, i) => {
        const st = cancelStatus[req.lender_name];
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "0.5px solid #f8f8f8" }}>
            <Avatar initials={nameInitials(req.lender_name)} color={pickColor(req.lender_name || "")} size={32} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{req.lender_name}</div>
              <div style={{ fontSize: 11, color: "#64748B" }}>{req.lender_type || "Lender"} · Awaiting response</div>
            </div>
            {st === "error" && <span style={{ fontSize: 11, color: "#DC2626" }}>Failed</span>}
            {st !== "error" && (
              <button onClick={() => handleWithdraw(req.lender_name)} disabled={st === "loading"}
                style={{ padding: "5px 12px", minHeight: 34, background: "#FEE2E2", color: "#DC2626", border: "0.5px solid #FCA5A5", borderRadius: 6, fontSize: 12, cursor: st === "loading" ? "default" : "pointer", fontWeight: 500 }}>
                {st === "loading" ? "…" : "Withdraw"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ConnectionsTab({ user, role, setPage }) {
  const connections    = user?.user_metadata?.connections || [];
  const builderConns   = user?.user_metadata?.builder_connections || [];

  const connected = role === "lender"
    ? builderConns.filter(c => c.status === "accepted" || c.status === "sent" || c.status === "pending")
    : connections.filter(c => c.status === "accepted" || c.status === "pending");

  const [msgStatus, setMsgStatus] = useState({});

  async function handleMessage(name, msgRole) {
    setMsgStatus(s => ({ ...s, [name]: "loading" }));
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/notify-message", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ other_name: name, other_role: msgRole }),
    });
    if (res.ok) {
      setMsgStatus(s => ({ ...s, [name]: "idle" }));
      setPage("messages");
    } else {
      setMsgStatus(s => ({ ...s, [name]: "error" }));
      setTimeout(() => setMsgStatus(s => ({ ...s, [name]: "idle" })), 3000);
    }
  }

  const secLabel = (text, count, color) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: color || "var(--accent,#3B82F6)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
      {text}{count !== undefined ? ` (${count})` : ""}
    </div>
  );

  const cardStyle = { background: "var(--bg-card,#fff)", borderRadius: 14, padding: "20px", marginBottom: 12, border: "0.5px solid var(--border,#e8e8e8)" };
  const isEmpty = connected.length === 0;

  return (
    <div>
      {/* ── Connected ── */}
      {connected.length > 0 && (
        <div style={cardStyle}>
          {secLabel("Connected", connected.length, "#166534")}
          {connected.map((c, i) => {
            const name    = role === "lender" ? c.builder_name : c.lender_name;
            const sub     = role === "lender" ? (c.builder_type || "Builder") : (c.lender_type || "Lender");
            const msgRole = role === "lender" ? "builder" : "lender";
            const st      = msgStatus[name];
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < connected.length - 1 ? "0.5px solid #f5f5f5" : "none" }}>
                <Avatar initials={nameInitials(name)} color={pickColor(name || "")} size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary,#1E3A5F)" }}>{name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)" }}>{sub}</div>
                </div>
                <button onClick={() => handleMessage(name, msgRole)} disabled={st === "loading"}
                  style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: "var(--accent,#3B82F6)", color: "#fff", fontSize: 12, fontWeight: 500, cursor: st === "loading" ? "default" : "pointer" }}>
                  {st === "loading" ? "…" : st === "error" ? "Failed" : "Message"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {isEmpty && (
        <div style={{ ...cardStyle, textAlign: "center", color: "var(--text-muted,#94A3B8)", padding: "2rem" }}>
          No conversations yet. Message someone to get started.
        </div>
      )}
    </div>
  );
}

function AccountPage({ user, setPage, userProfile, viewerRoleProfile, onReplayTour, darkMode, setDarkMode, accentColor = "#3B82F6", setAccentColor, sidebarWidth = 0 }) {
  const displayName = user?.user_metadata?.name || user?.email?.split("@")[0] || "";
  const role = user?.user_metadata?.role;

  const [factors, setFactors] = useState([]);
  const [enrollStep, setEnrollStep] = useState("idle"); // "idle" | "scan" | "verify"
  const [enrollData, setEnrollData] = useState(null);   // { id, qr_code, secret }
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [idVerified] = useState(!!userProfile?.identity_verified);
  const [idLoading, setIdLoading] = useState(false);
  const [idError, setIdError] = useState("");

  // Companies House (builders only)
  const [chNumber,    setChNumber]    = useState(viewerRoleProfile?.company_number || "");
  const [chVerified,  setChVerified]  = useState(viewerRoleProfile?.verification_status === "verified");
  const [chPending,   setChPending]   = useState(viewerRoleProfile?.verification_status === "pending_review");
  const [chName,      setChName]      = useState(viewerRoleProfile?.company_name || "");
  const [chIncorp,    setChIncorp]    = useState(viewerRoleProfile?.company_incorporated || "");
  const [chVerifying, setChVerifying] = useState(false);
  const [chError,     setChError]     = useState("");
  const [chDirectors,         setChDirectors]         = useState([]);
  const [chSelectedDirector,  setChSelectedDirector]  = useState("");
  const [chDirectorMatch,     setChDirectorMatch]     = useState(true);

  // Bank details (builders only)
  const [bdAccountName,   setBdAccountName]   = useState(viewerRoleProfile?.bank_account_name || "");
  const [bdSortCode,      setBdSortCode]      = useState(() => {
    const sc = viewerRoleProfile?.bank_sort_code || "";
    const d = sc.replace(/\D/g, "");
    return d.length === 6 ? `${d.slice(0,2)}-${d.slice(2,4)}-${d.slice(4)}` : sc;
  });
  const [bdAccountNumber, setBdAccountNumber] = useState(viewerRoleProfile?.bank_account_number || "");
  const [bdSaving,        setBdSaving]        = useState(false);
  const [bdError,         setBdError]         = useState("");
  const [bdSaved,         setBdSaved]         = useState(false);

  useEffect(() => { loadFactors(); }, []);

  async function loadFactors() {
    const { data } = await supabase.auth.mfa.listFactors();
    setFactors(data?.totp || []);
  }

  async function saveBankDetails(e) {
    e.preventDefault();
    setBdError("");
    const sc = bdSortCode.replace(/\D/g, "");
    const an = bdAccountNumber.replace(/\D/g, "");
    if (!bdAccountName.trim()) { setBdError("Please enter the account holder name."); return; }
    if (sc.length !== 6) { setBdError("Sort code must be 6 digits."); return; }
    if (an.length !== 8) { setBdError("Account number must be 8 digits."); return; }
    setBdSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/save-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "save-bank-details", bank_account_name: bdAccountName.trim(), bank_sort_code: sc, bank_account_number: an }),
    });
    const d = await res.json().catch(() => ({}));
    setBdSaving(false);
    if (!res.ok) { setBdError(d.error || "Failed to save bank details."); return; }
    setBdSaved(true);
    setTimeout(() => setBdSaved(false), 3000);
  }

  async function startEnroll() {
    setLoading(true);
    setError("");
    setSuccess("");
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: "LenderBuild",
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setEnrollData({ id: data.id, qr_code: data.totp.qr_code, secret: data.totp.secret });
    setEnrollStep("scan");
  }

  async function verifyEnroll(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId: enrollData.id });
    if (cErr) { setError(cErr.message); setLoading(false); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({
      factorId: enrollData.id,
      challengeId: challenge.id,
      code: verifyCode,
    });
    setLoading(false);
    if (vErr) { setError(vErr.message); return; }
    setEnrollStep("idle");
    setEnrollData(null);
    setVerifyCode("");
    setSuccess("Two-factor authentication enabled successfully.");
    loadFactors();
  }

  async function cancelEnroll() {
    if (enrollData?.id) {
      await supabase.auth.mfa.unenroll({ factorId: enrollData.id });
    }
    setEnrollStep("idle");
    setEnrollData(null);
    setVerifyCode("");
    setError("");
  }

  async function unenroll(factorId) {
    setLoading(true);
    setError("");
    setSuccess("");
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setSuccess("Two-factor authentication has been disabled.");
    loadFactors();
  }

  const activeFactor = factors.find(f => f.status === "verified");
  const inputStyle = {
    width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8,
    padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box",
  };

  const [openSection, setOpenSection] = useState(null);
  const [acctWidth, setAcctWidth] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setAcctWidth(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobileAcct = acctWidth < 768;
  const [notifPrefs, setNotifPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`lb_notif_prefs_${user?.id}`) || "{}"); } catch { return {}; }
  });
  const [privacyPrefs, setPrivacyPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`lb_privacy_prefs_${user?.id}`) || "{}"); } catch { return {}; }
  });
  const [appearanceSaved, setAppearanceSaved] = useState(false);
  const settingsPageRef = useRef(null);

  function toggleNotif(key) {
    setNotifPrefs(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(`lb_notif_prefs_${user?.id}`, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  function togglePrivacy(key) {
    setPrivacyPrefs(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(`lb_privacy_prefs_${user?.id}`, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  async function saveAppearance() {
    setAppearanceSaved(true);
    // Save to Supabase in the background — don't block UI
    supabase.auth.updateUser({
      data: {
        appearance_dark_mode: darkMode,
        appearance_accent_colour: accentColor,
      },
    });
    setTimeout(() => {
      setAppearanceSaved(false);
      setOpenSection(null);
      window.scrollTo({ top: 0, behavior: "instant" });
    }, 1000);
  }

  const avatarUrl = user?.user_metadata?.avatar_url;

  // Reusable sub-page nav header
  function SubNav({ title }) {
    return (
      <div style={{ background: "var(--nav-bg,#1E3A5F)", position: "sticky", top: 0, zIndex: 100, height: 52, display: "flex", alignItems: "center", flexShrink: 0 }}>
        <button onClick={() => { setOpenSection(null); window.scrollTo({ top: 0, behavior: "instant" }); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", padding: "8px 14px", lineHeight: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ flex: 1, textAlign: "center", color: "#fff", fontWeight: 600, fontSize: 16, marginRight: 44 }}>{title}</div>
      </div>
    );
  }

  const subPageWrap = { minHeight: "100vh", background: "var(--bg-page,#F4F5F7)", paddingBottom: "calc(80px + env(safe-area-inset-bottom,0px))" };
  const card = { background: "var(--bg-card,#fff)", borderRadius: 14, padding: "20px", marginBottom: 12 };

  return (
    <div>

      {/* ─── SUB: VERIFICATION ───────────────────────── */}
      {openSection === "verification" && (
        <div style={subPageWrap}>
          <SubNav title="Verification" />
          <div style={{ padding: "16px", maxWidth: 600, margin: "0 auto" }}>
            <div style={card}>
          <div style={{ paddingTop: 14 }}>
            <label style={{ fontSize: 14, fontWeight: 700, color: "#1E3A5F", display: "block", marginBottom: 4 }}>
              Companies House number
            </label>
            <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 12px", lineHeight: 1.55 }}>
              Enter your 8 digit company registration number to get verified. A verified badge increases lender trust and improves your match score.
            </p>

            {chVerified && chName && (
              <div style={{ background: "#DCFCE7", border: "0.5px solid #86EFAC", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#166534", display: "flex", alignItems: "center", gap: 4 }}><Icon name="building" size={13} /> {chName}</div>
                <div style={{ fontSize: 12, color: "#166534", marginTop: 2 }}>
                  Status: <strong>Active</strong>{chIncorp && ` · Incorporated ${chIncorp}`}{chNumber && ` · No. ${chNumber}`}
                </div>
              </div>
            )}
            {chPending && !chVerified && (
              <div style={{ background: "#FEF3C7", border: "0.5px solid #FDE68A", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: "#92400E" }}>
                  {chName ? <><strong>{chName}</strong> — </> : null}Your name couldn't be automatically matched to a Companies House officer. Your account is pending manual review.
                </div>
              </div>
            )}
            {chError && (
              <div style={{ background: "#FEE2E2", color: "#991B1B", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 10 }}>{chError}</div>
            )}

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={chNumber}
                  onChange={e => { setChNumber(e.target.value.toUpperCase()); setChError(""); setChPending(false); setChVerified(false); setChDirectors([]); setChSelectedDirector(""); setChDirectorMatch(true); }}
                  placeholder="e.g. 12345678"
                  maxLength={10}
                  style={{ ...inputStyle, fontFamily: "monospace", letterSpacing: "0.05em" }}
                />
              </div>
              <button
                type="button"
                disabled={chVerifying || !chNumber.trim()}
                onClick={async () => {
                  setChVerifying(true); setChError("");
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const r = await fetch("/api/save-profile", {
                      method: "POST",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                      body: JSON.stringify({ action: "verify-company", company_number: chNumber, full_name: user.user_metadata?.name }),
                    });
                    const d = await r.json();
                    if (!r.ok) { setChError(d.error || "Verification failed."); }
                    else if (d.verified) {
                      setChVerified(true); setChPending(false); setChError("");
                      setChName(d.company_name || ""); setChIncorp(d.company_incorporated || ""); setChDirectors(d.directors || []);
                    } else {
                      const isManualReview = d.reason?.includes("manual review");
                      const isInactive = d.reason === "Company is not active";
                      if (isManualReview) {
                        setChVerified(false); setChPending(true); setChError(""); setChName(d.company_name || "");
                      } else {
                        setChVerified(false); setChPending(false);
                        setChError(d.reason || "Verification failed. Check the registration number.");
                        if (isInactive) setChNumber("");
                      }
                    }
                  } catch { setChError("Could not reach Companies House. Please try again."); }
                  setChVerifying(false);
                }}
                style={{ height: 44, padding: "0 18px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, flexShrink: 0, cursor: chVerifying || !chNumber.trim() ? "default" : "pointer", opacity: !chNumber.trim() ? 0.5 : 1, background: chVerified ? "#166534" : "#1E3A5F", color: "#fff" }}
              >
                {chVerifying ? "Checking…" : chVerified ? "✓ Verified" : "Verify"}
              </button>
            </div>

            {chVerified && chDirectors.length > 0 && (
              <div style={{ marginTop: 12, padding: "10px 14px", background: "#f0faf6", border: "0.5px solid #A8DFC9", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#166534", marginBottom: 6 }}>Active directors on Companies House:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                  {chDirectors.map((dir, i) => (
                    <span key={i} style={{ background: "#DCFCE7", border: "0.5px solid #86EFAC", borderRadius: 12, padding: "2px 8px", fontSize: 11, color: "#166534" }}>{dir.name}</span>
                  ))}
                </div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#166534", display: "block", marginBottom: 4 }}>Which director are you?</label>
                <select
                  value={chSelectedDirector}
                  onChange={e => { setChSelectedDirector(e.target.value); if (e.target.value) setChDirectorMatch(true); }}
                  style={{ width: "100%", height: 36, border: "1px solid #86EFAC", borderRadius: 6, fontSize: 12, padding: "0 8px", background: "#fff", color: "#166534" }}
                >
                  <option value="">Select your name…</option>
                  {chDirectors.map((dir, i) => <option key={i} value={dir.name}>{dir.name}</option>)}
                </select>
                {chDirectorMatch === false && !chSelectedDirector && (
                  <div style={{ background: "#FEF3C7", border: "0.5px solid #FDE68A", borderRadius: 6, padding: "8px 10px", marginTop: 8, fontSize: 12, color: "#92400E" }}>
                    Your profile name does not closely match any registered director — please select your name above or verify manually if you are a director
                  </div>
                )}
                {chSelectedDirector && (
                  <div style={{ background: "#DCFCE7", border: "0.5px solid #86EFAC", borderRadius: 6, padding: "6px 10px", marginTop: 8, fontSize: 12, color: "#166534" }}>
                    ✓ Confirmed as director: {chSelectedDirector}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              {chVerified && <span style={{ fontSize: 11, background: "#166534", color: "#fff", padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>✓ CH Verified</span>}
              {chPending && !chVerified && <span title="We couldn't auto-verify — your account is being reviewed" style={{ fontSize: 11, background: "#92400E", color: "#fff", padding: "2px 8px", borderRadius: 20, fontWeight: 600, cursor: "help" }}>⏳ Pending review</span>}
              <span style={{ fontSize: 11, color: "#94a3b8" }}>Registration number is on your incorporation certificate</span>
            </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ─── SUB: BANK DETAILS ────────────────────────────── */}
      {openSection === "bank-details" && role === "builder" && (
        <div style={subPageWrap}>
          <SubNav title="Bank Details" />
          <div style={{ padding: "16px", maxWidth: 600, margin: "0 auto" }}>
            <div style={card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1E3A5F", marginBottom: 4 }}>
                Bank details for receiving payments
              </div>
              <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 16px", lineHeight: 1.55 }}>
                Enter the account you want lenders to transfer milestone payments into.
              </p>

              {/* Privacy note */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "#F0F4FF", border: "0.5px solid #C7D7F5", borderRadius: 10, padding: "10px 12px", marginBottom: 18 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span style={{ fontSize: 12, color: "#1E3A5F", lineHeight: 1.5 }}>
                  Your bank details are only shared with lenders after a deal is confirmed and a milestone is approved. They are never publicly visible.
                </span>
              </div>

              <form onSubmit={saveBankDetails} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Account holder name</label>
                  <input
                    type="text"
                    value={bdAccountName}
                    onChange={e => setBdAccountName(e.target.value)}
                    placeholder="e.g. John Smith"
                    style={{ width: "100%", height: 44, border: "1.5px solid #d1d5db", borderRadius: 8, padding: "0 12px", fontSize: 14, boxSizing: "border-box" }}
                  />
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Sort code</label>
                    <input
                      type="text"
                      value={bdSortCode}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                        const fmt = digits.length > 4 ? `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4)}` : digits.length > 2 ? `${digits.slice(0,2)}-${digits.slice(2)}` : digits;
                        setBdSortCode(fmt);
                      }}
                      placeholder="XX-XX-XX"
                      maxLength={8}
                      style={{ width: "100%", height: 44, border: "1.5px solid #d1d5db", borderRadius: 8, padding: "0 12px", fontSize: 14, fontFamily: "monospace", letterSpacing: "0.05em", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Account number</label>
                    <input
                      type="text"
                      value={bdAccountNumber}
                      onChange={e => setBdAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 8))}
                      placeholder="12345678"
                      maxLength={8}
                      style={{ width: "100%", height: 44, border: "1.5px solid #d1d5db", borderRadius: 8, padding: "0 12px", fontSize: 14, fontFamily: "monospace", letterSpacing: "0.05em", boxSizing: "border-box" }}
                    />
                  </div>
                </div>

                {bdError && (
                  <div style={{ background: "#FEE2E2", color: "#991B1B", borderRadius: 8, padding: "8px 12px", fontSize: 13 }}>{bdError}</div>
                )}

                <button
                  type="submit"
                  disabled={bdSaving}
                  style={{ height: 46, background: bdSaved ? "#166534" : bdSaving ? "#aaa" : "#1E3A5F", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: bdSaving ? "default" : "pointer" }}
                >
                  {bdSaved ? "✓ Saved" : bdSaving ? "Saving…" : "Save bank details"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ─── SUB: APPEARANCE ──────────────────────────────── */}
      {openSection === "appearance" && (
        <div style={{ ...subPageWrap, paddingBottom: "calc(120px + env(safe-area-inset-bottom,0px))" }}>
          <SubNav title="Appearance" />
          <div style={{ padding: "16px 20px", maxWidth: 600, margin: "0 auto" }}>

            {/* Dark mode */}
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 10 }}>Theme</div>
              <button onClick={() => setDarkMode && setDarkMode(d => !d)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "12px 14px", borderRadius: 12, border: "1.5px solid #e0e0e0", background: "#fff", cursor: "pointer", minHeight: 52, gap: 10 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 500, color: "#374151" }}>
                  <Icon name={darkMode ? "moon" : "sun"} size={20} className="lb-icon" />
                  {darkMode ? "Dark mode" : "Light mode"}
                </span>
                <div style={{ position: "relative", width: 56, height: 30, borderRadius: 15, background: darkMode ? "var(--accent,#3B82F6)" : "#CBD5E1", flexShrink: 0, transition: "background 0.2s" }}>
                  <div style={{ position: "absolute", top: 5, left: darkMode ? 29 : 5, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.25)", transition: "left 0.2s" }} />
                </div>
              </button>
            </div>

            {/* Colour scheme */}
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Colour scheme</div>
              <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 12 }}>{ACCENT_PRESETS.find(p => p.value === accentColor)?.label ?? "Custom"} — applies instantly</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {ACCENT_PRESETS.map(p => {
                  const isSelected = accentColor === p.value;
                  return (
                    <button key={p.value} data-color-swatch="true" onClick={() => setAccentColor && setAccentColor(p.value)} title={p.label}
                      style={{ width: 56, height: 56, borderRadius: "50%", padding: 0, cursor: "pointer", background: p.value, justifySelf: "center", border: "none", outline: "none", boxShadow: isSelected ? `0 0 0 3px #fff, 0 0 0 5px ${p.value}` : "none", transition: "box-shadow 0.15s", position: "relative" }}>
                      {isSelected && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20, fontWeight: 700, pointerEvents: "none" }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>


          </div>
          {/* Sticky save button */}
          <div style={{ position: "fixed", bottom: 0, left: sidebarWidth, right: 0, padding: "12px 20px", background: "#fff", borderTop: "1px solid #f0f0f0", zIndex: 90 }}>
            <button onClick={saveAppearance} style={{ width: "100%", height: 46, background: "var(--accent)", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
              {appearanceSaved ? "✓ Saved!" : "Save preferences"}
            </button>
          </div>
        </div>
      )}

      {/* ─── SUB: NOTIFICATIONS ───────────────────────────── */}
      {openSection === "notifications" && (
        <div style={subPageWrap}>
          <SubNav title="Notifications" />
          <div style={{ padding: "16px", maxWidth: 600, margin: "0 auto" }}>
            <div style={card}>
              {[
                { key: "new_connections", label: "New connection requests" },
                { key: "new_messages",    label: "New messages" },
                { key: "deal_updates",    label: "Deal updates" },
                { key: "milestones",      label: "Milestone approvals" },
              ].map(({ key, label }) => (
                <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "0.5px solid #f5f5f5" }}>
                  <span style={{ fontSize: 15, color: "#1E3A5F", fontWeight: 500 }}>{label}</span>
                  <button onClick={() => toggleNotif(key)}
                    style={{ width: 48, height: 26, borderRadius: 13, border: "none", background: notifPrefs[key] !== false ? "#3B82F6" : "#CBD5E1", cursor: "pointer", position: "relative", transition: "background 0.2s", padding: 0, flexShrink: 0 }}>
                    <div style={{ position: "absolute", top: 3, left: notifPrefs[key] !== false ? 24 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── SUB: PRIVACY ─────────────────────────────────── */}
      {openSection === "privacy" && (
        <div style={subPageWrap}>
          <SubNav title="Privacy" />
          <div style={{ padding: "16px", maxWidth: 600, margin: "0 auto" }}>
            <div style={card}>
              {[
                { key: "hide_from_search",   label: "Hide profile from search",    desc: "Won't appear in lender/builder search results" },
                { key: "hide_email",         label: "Hide email from connections",  desc: "Contact only through in-app messages" },
                { key: "hide_online_status", label: "Hide online status",           desc: "Others won't see when you were last active" },
                { key: "hide_deal_count",    label: "Hide deal count",              desc: "Your completed deal count won't be public" },
              ].map(({ key, label, desc }) => (
                <div key={key} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "12px 0", borderBottom: "0.5px solid #f5f5f5", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 15, color: "#1E3A5F", fontWeight: 500 }}>{label}</div>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 3, lineHeight: 1.4 }}>{desc}</div>
                  </div>
                  <button onClick={() => togglePrivacy(key)}
                    style={{ width: 48, height: 26, borderRadius: 13, border: "none", background: privacyPrefs[key] ? "#3B82F6" : "#CBD5E1", cursor: "pointer", position: "relative", transition: "background 0.2s", padding: 0, flexShrink: 0, marginTop: 2 }}>
                    <div style={{ position: "absolute", top: 3, left: privacyPrefs[key] ? 24 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                  </button>
                </div>
              ))}
            </div>
            <div style={{ ...card, marginTop: 0 }}>
              <button onClick={() => setPage("privacy")} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "4px 0", background: "transparent", border: "none", cursor: "pointer", fontSize: 15, color: "#1E3A5F", fontWeight: 500 }}>
                <Icon name="file" size={15} className="lb-icon" /><span style={{ flex: 1 }}>Privacy Policy</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C4C9D4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
              <div style={{ height: 1, background: "#f5f5f5", margin: "12px 0" }} />
              <button onClick={() => { if (window.confirm("Are you sure you want to request account deletion? This cannot be undone.")) { setPage("help"); } }}
                style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "4px 0", background: "transparent", border: "none", cursor: "pointer", fontSize: 15, color: "#DC2626", fontWeight: 500 }}>
                <Icon name="trash" size={15} className="lb-icon" />Request account deletion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── SUB: SECURITY ────────────────────────────────── */}
      {openSection === "security" && (
        <div style={subPageWrap}>
          <SubNav title="Security" />
          <div style={{ padding: "16px", maxWidth: 600, margin: "0 auto" }}>
            {error   && <div style={{ background: "#FAECE7", color: "#993C1D", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {success && <div style={{ background: "#EBF2FF", color: "#1E3A5F", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 12 }}>{success}</div>}
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1E3A5F", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                Identity verification
                {idVerified && <span style={{ fontSize: 11, background: "#D97706", color: "#fff", padding: "2px 8px", borderRadius: 20, fontWeight: 600, display:"inline-flex", alignItems:"center", gap:3 }}><Icon name="id-card" size={11} /> ID Verified</span>}
              </div>
              {idVerified ? (
                <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, margin: 0 }}>Your identity is verified. A gold ID Verified badge appears on your profile.</p>
              ) : (
                <>
                  <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, margin: "0 0 12px" }}>Verify with a government ID and selfie (Stripe Identity). Adds a gold badge. ~£1.50 charge.</p>
                  {idError && <div style={{ background: "#FEE2E2", color: "#991B1B", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 10 }}>{idError}</div>}
                  <button disabled={idLoading} onClick={async () => { setIdLoading(true); setIdError(""); try { const { data: { session } } = await supabase.auth.getSession(); const r = await fetch("/api/save-profile", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ action: "create-identity-session" }) }); const d = await r.json(); if (!r.ok) setIdError(d.error || "Could not start verification."); else if (d.url) window.location.href = d.url; } catch { setIdError("Something went wrong."); } setIdLoading(false); }}
                    style={{ padding: "8px 20px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 600, border: "none", background: idLoading ? "#aaa" : "#D97706", color: "#fff", cursor: idLoading ? "default" : "pointer" }}>
                    {idLoading ? "Starting…" : "Verify your identity →"}
                  </button>
                </>
              )}
            </div>
            <div style={card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1E3A5F", marginBottom: 12 }}>Two-factor authentication</div>
              {enrollStep === "idle" && (activeFactor ? (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#3B82F6", flexShrink: 0 }} />
                    <div><div style={{ fontSize: 14, fontWeight: 500 }}>Authenticator app enabled</div><div style={{ fontSize: 12, color: "#64748B" }}>Your account is protected with 2FA.</div></div>
                  </div>
                  <button onClick={() => unenroll(activeFactor.id)} disabled={loading} style={{ padding: "8px 16px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, border: "0.5px solid #F5C9BB", background: "#FAECE7", color: "#993C1D", cursor: loading ? "default" : "pointer" }}>{loading ? "Disabling..." : "Disable 2FA"}</button>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, margin: "0 0 1rem" }}>Add an extra layer of security. You'll need a code from your authenticator app each time you log in.</p>
                  <button onClick={startEnroll} disabled={loading} style={{ padding: "8px 20px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", background: "#3B82F6", color: "#fff", cursor: loading ? "default" : "pointer" }}>{loading ? "Setting up..." : "Set up authenticator app"}</button>
                </div>
              ))}
              {enrollStep === "scan" && enrollData && (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Step 1 — Scan QR code</div>
                  <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, margin: "0 0 1.25rem" }}>Open your authenticator app and scan this QR code.</p>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.25rem" }}>
                    <div style={{ padding: 12, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, display: "inline-block" }}>
                      <img src={enrollData.qr_code} alt="QR code" style={{ width: 180, height: 180, display: "block" }} />
                    </div>
                  </div>
                  <details style={{ marginBottom: "1.25rem" }}>
                    <summary style={{ fontSize: 12, color: "#64748B", cursor: "pointer" }}>Can't scan? Enter secret manually</summary>
                    <div style={{ marginTop: 8, padding: "8px 12px", background: "#f5f5f3", borderRadius: 8, fontFamily: "monospace", fontSize: 13, letterSpacing: "0.1em", wordBreak: "break-all" }}>{enrollData.secret}</div>
                  </details>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setEnrollStep("verify")} style={{ flex: 1, height: 44, background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Next — Enter code</button>
                    <button onClick={cancelEnroll} style={{ padding: "0 16px", height: 44, background: "transparent", color: "#555", border: "0.5px solid #ccc", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              )}
              {enrollStep === "verify" && (
                <form onSubmit={verifyEnroll}>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Step 2 — Verify code</div>
                  <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, margin: "0 0 1.25rem" }}>Enter the 6-digit code from your authenticator app.</p>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
                    style={{ width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 20, background: "#fff", boxSizing: "border-box", letterSpacing: "0.3em", textAlign: "center", marginBottom: "1rem" }} autoFocus />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="submit" disabled={loading || verifyCode.length < 6} style={{ flex: 1, height: 44, background: loading ? "#aaa" : "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? "default" : "pointer" }}>{loading ? "Verifying..." : "Enable 2FA"}</button>
                    <button type="button" onClick={() => setEnrollStep("scan")} style={{ padding: "0 16px", height: 44, background: "transparent", color: "#555", border: "0.5px solid #ccc", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Back</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── SUB: CONNECTIONS ─────────────────────────────── */}
      {openSection === "connections" && (
        <div style={subPageWrap}>
          <SubNav title="My Connections" />
          <div style={{ padding: "16px", maxWidth: 600, margin: "0 auto" }}>
            <ConnectionsTab user={user} role={role} setPage={setPage} />
          </div>
        </div>
      )}

      {/* ─── SUB: HELP ────────────────────────────────────── */}
      {openSection === "help" && (
        <div style={subPageWrap}>
          <SubNav title="Help & Support" />
          <div style={{ padding: "16px", maxWidth: 600, margin: "0 auto" }}>
            <div style={card}>
              {[
                { label: "Help Centre",            action: () => setPage("help"),         icon: "book" },
                { label: "How it works",          action: () => setPage("how-it-works"), icon: "circle-help" },
                { label: "Trust & Safety",         action: () => setPage("safety"),       icon: "shield" },
                { label: "Contact support",        action: () => setPage("help"),         icon: "mail" },
                { label: "Replay onboarding tour", action: onReplayTour,                  icon: "film" },
                { label: "System status",          action: () => setPage("status"),       icon: "circle-green" },
              ].map(({ label, action, icon }, i, arr) => (
                <button key={label} onClick={action}
                  style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "0 0", fontSize: 15, background: "transparent", border: "none", borderBottom: i < arr.length - 1 ? "0.5px solid #f5f5f5" : "none", cursor: "pointer", minHeight: 52, color: "var(--accent)", textAlign: "left", fontWeight: 500 }}
                  onTouchStart={e => e.currentTarget.style.background = "#f5f5f5"} onTouchEnd={e => e.currentTarget.style.background = "transparent"}
                  onMouseEnter={e => e.currentTarget.style.background = "#f9f9f7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <Icon name={icon} size={18} style={{ color: "var(--accent)" }} />
                  <span style={{ flex: 1 }}>{label}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ stroke: "var(--accent)", opacity: 0.45 }} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── MAIN SETTINGS MENU ───────────────────────────── */}
      {!openSection && (
        <div style={{ padding: "1.5rem 1.25rem", maxWidth: 600, margin: "0 auto", paddingBottom: "calc(80px + env(safe-area-inset-bottom,0px))" }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 1.5rem", color: "var(--accent)" }}>Settings</h2>
          <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "0.5px solid #e8e8e8" }}>
            {[
              { id: "account",       icon: "user",        title: "Account",        action: () => setPage("profile-setup") },
              { id: "verification",  icon: "shield",      title: "Verification" },
              ...(role === "builder" ? [{ id: "bank-details",    icon: "bank",         title: "Bank Details" }] : []),
              { id: "appearance",    icon: "palette",     title: "Appearance" },
              { id: "notifications", icon: "bell",        title: "Notifications" },
              { id: "privacy",       icon: "lock-open",   title: "Privacy" },
              { id: "security",      icon: "lock",        title: "Security" },
              { id: "connections",   icon: "handshake",   title: "My Connections" },
              { id: "help",          icon: "circle-help", title: "Help & Support" },
            ].map((s, i, arr) => (
              <button key={s.id} onClick={() => { if (s.action) { s.action(); } else { setOpenSection(s.id); window.scrollTo({ top: 0, behavior: "instant" }); } }}
                style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "0 20px", fontSize: 15, background: "transparent", border: "none", borderBottom: i < arr.length - 1 ? "1px solid #f5f5f5" : "none", cursor: "pointer", minHeight: 52, color: "var(--accent)", textAlign: "left" }}
                onTouchStart={e => e.currentTarget.style.background = "#f5f5f5"} onTouchEnd={e => e.currentTarget.style.background = "transparent"}
                onMouseEnter={e => e.currentTarget.style.background = "#f9f9f7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <Icon name={s.icon} size={20} style={{ color: "var(--accent)" }} />
                <span style={{ fontWeight: 500, flex: 1 }}>{s.title}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ stroke: "var(--accent)", opacity: 0.45 }} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GETTING STARTED (NEW USER ONBOARDING) ───────────────────────────────────

function GettingStartedPage({ user, setPage, connections, conversations }) {
  const name = user?.user_metadata?.name?.split(" ")[0] || "there";
  const role = user?.user_metadata?.role;

  const profileComplete = !!user?.user_metadata?.profile_complete;
  const hasMatch = connections > 0;
  const hasMessage = conversations > 0;

  const steps = [
    {
      label: "Complete your profile",
      desc: "Tell us about your investment criteria or track record so you can be matched.",
      done: profileComplete,
      action: () => setPage("profile-setup"),
      cta: "Set up profile",
    },
    {
      label: role === "lender" ? "Browse your first builder" : "Browse your first lender",
      desc: role === "lender" ? "Find a builder that matches your investment goals." : "Find a lender that fits your project.",
      done: hasMatch,
      action: () => setPage(role === "lender" ? "find-builder" : "search"),
      cta: role === "lender" ? "Find a builder" : "Find a lender",
    },
    {
      label: "Send your first connection request",
      desc: "Introduce yourself and start building a relationship.",
      done: hasMessage,
      action: () => setPage(role === "lender" ? "find-builder" : "search"),
      cta: "Browse profiles",
    },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const progressPct = Math.round((completedCount / steps.length) * 100);

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "2rem 1.25rem" }}>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 700, color: "#1E3A5F", margin: "0 0 0.5rem", lineHeight: 1.2 }}>
          Welcome to LenderBuild, {name}
        </h1>
        <p style={{ fontSize: 16, color: "#64748B", margin: 0, lineHeight: 1.7 }}>
          You're just a few steps away from finding your perfect match.
        </p>
      </div>

      {/* Progress bar */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "20px 24px", marginBottom: "1.5rem", border: "0.5px solid #e0e0e0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F" }}>Getting started</span>
          <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>{completedCount} of {steps.length} complete</span>
        </div>
        <div style={{ height: 8, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ height: "100%", borderRadius: 4, background: completedCount === steps.length ? "#16A34A" : "#2E5FA3", width: `${progressPct}%`, transition: "width 0.5s ease" }} />
        </div>
      </div>

      {/* Steps */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: "2rem" }}>
        {steps.map((step, i) => (
          <div key={i} style={{
            background: "#fff", borderRadius: 12, padding: "20px 24px",
            border: "0.5px solid #e0e0e0",
            borderLeft: step.done ? "3px solid #16A34A" : "3px solid #2E5FA3",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            display: "flex", alignItems: "flex-start", gap: 16,
          }}>
            {/* Checkbox */}
            <div style={{
              width: 24, height: 24, borderRadius: "50%", flexShrink: 0, marginTop: 2,
              background: step.done ? "#16A34A" : "#EBF2FF",
              border: step.done ? "none" : "2px solid #2E5FA3",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {step.done && (
                <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                  <path d="M1.5 5l3 3 6-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {!step.done && <span style={{ fontSize: 11, fontWeight: 700, color: "#2E5FA3" }}>{i + 1}</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: step.done ? "#64748B" : "#1E3A5F", marginBottom: 4, textDecoration: step.done ? "line-through" : "none" }}>
                {step.label}
              </div>
              <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.55, marginBottom: step.done ? 0 : 12 }}>{step.desc}</div>
              {!step.done && (
                <button
                  onClick={step.action}
                  style={{ background: "#2E5FA3", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                >
                  {step.cta} →
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          onClick={() => setPage("home-dashboard")}
          style={{ background: "none", border: "none", fontSize: 13, color: "#64748B", cursor: "pointer", textDecoration: "underline" }}
        >
          Skip getting started
        </button>
      </div>
    </div>
  );
}

// ─── DASHBOARD PAGE ──────────────────────────────────────────────────────────

// Pending sent connection requests card for builders (Feature 2)
function PendingBuilderRequestsCard({ requests, user, onCancelled }) {
  const [cancelStatus, setCancelStatus] = useState({});

  async function handleCancel(lenderName) {
    setCancelStatus(s => ({ ...s, [lenderName]: "loading" }));
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/connect-request", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "cancel-request", lender_name: lenderName }),
    });
    if (res.ok) {
      setCancelStatus(s => ({ ...s, [lenderName]: "done" }));
      await supabase.auth.refreshSession();
      onCancelled && onCancelled(lenderName);
    } else {
      setCancelStatus(s => ({ ...s, [lenderName]: "error" }));
      setTimeout(() => setCancelStatus(s => ({ ...s, [lenderName]: undefined })), 3000);
    }
  }

  const visible = requests.filter(r => cancelStatus[r.lender_name] !== "done");
  if (visible.length === 0) return null;

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.5rem", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#D97706", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
        Pending sent requests <span style={{ background: "#D97706", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: 10, marginLeft: 4 }}>{visible.length}</span>
      </div>
      {visible.map(req => {
        const st = cancelStatus[req.lender_name];
        return (
          <div key={req.lender_name} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Avatar initials={nameInitials(req.lender_name)} color={pickColor(req.lender_name || "")} size={34} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{req.lender_name}</div>
              <div style={{ fontSize: 11, color: "#64748B" }}>{req.lender_type || "Lender"} · Pending</div>
            </div>
            {st === "error" ? (
              <span style={{ fontSize: 11, color: "#DC2626" }}>Failed</span>
            ) : (
              <button onClick={() => handleCancel(req.lender_name)} disabled={st === "loading"}
                style={{ padding: "5px 12px", minHeight: 34, background: "#FEE2E2", color: "#DC2626", border: "0.5px solid #FCA5A5", borderRadius: 6, fontSize: 12, cursor: st === "loading" ? "default" : "pointer", fontWeight: 500 }}>
                {st === "loading" ? "…" : "Cancel request"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DashboardPage({ user, setPage, onViewProfile, onViewBuilderProfile, onMessage, viewerRoleProfile, userProfile, page }) {
  const name     = user?.user_metadata?.name || user?.email?.split("@")[0] || "there";
  const role     = user?.user_metadata?.role;
  const avatarUrl = user?.user_metadata?.avatar_url;

  const [conversations,   setConversations]   = useState([]);
  const [deals,           setDeals]           = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [recoLenders,     setRecoLenders]     = useState([]);
  const [recoBuilders,    setRecoBuilders]    = useState([]);
  const [respondStatus,   setRespondStatus]   = useState({});
  const [loading,         setLoading]         = useState(true);
  const [smartMatches,    setSmartMatches]    = useState([]);
  const [latestFundingPosts, setLatestFundingPosts] = useState([]);

  const uid = user?.id || "";
  const [dismissFunding,     setDismissFunding]     = useState(() => { try { return !!localStorage.getItem(`lb_dismiss_funding_${uid}`); } catch { return false; } });
  const [dismissBuilderTools,setDismissBuilderTools] = useState(() => { try { return !!localStorage.getItem(`lb_dismiss_btools_${uid}`); } catch { return false; } });
  const [dismissLenderTools, setDismissLenderTools]  = useState(() => { try { return !!localStorage.getItem(`lb_dismiss_ltools_${uid}`); } catch { return false; } });

  function dismiss(key, setter) {
    setter(true);
    try { localStorage.setItem(key, "1"); } catch {}
  }
  function show(key, setter) {
    setter(false);
    try { localStorage.removeItem(key); } catch {}
  }

  const myConnections = role === "builder"
    ? (user?.user_metadata?.connections || []).filter(c => c.status === "accepted")
    : (user?.user_metadata?.builder_connections || []).filter(c => c.status === "sent" || c.status === "accepted");

  useEffect(() => {
    async function load() {
      try {
        const [convRes] = await Promise.all([
          supabase.from("conversations").select("*")
            .or(`lender_id.eq.${user.id},builder_id.eq.${user.id}`)
            .order("last_message_at", { ascending: false, nullsFirst: false })
            .limit(5),
        ]);
        setConversations(convRes.data || []);

        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const [dr, rr] = await Promise.all([
            fetch("/api/deals", { headers: { Authorization: `Bearer ${session.access_token}` } }),
            role === "lender" ? fetch("/api/lender-requests", { headers: { Authorization: `Bearer ${session.access_token}` } }) : Promise.resolve(null),
          ]);
          if (dr.ok) { const { deals: ds } = await dr.json(); setDeals(ds || []); }
          if (rr?.ok) { const { requests } = await rr.json(); setPendingRequests((requests || []).filter(r => r.status === "pending")); }
        }

        const [lr, br] = await Promise.all([
          fetch("/api/get-profiles?type=lenders").then(r => r.json()).catch(() => ({ lenders: [] })),
          fetch("/api/get-profiles?type=builders").then(r => r.json()).catch(() => ({ builders: [] })),
        ]);
        setRecoLenders(lr.lenders || []);
        setRecoBuilders(br.builders || []);
        if (role === "lender") {
          fetch("/api/posts")
            .then(r => r.json())
            .then(d => setLatestFundingPosts((d.posts || []).filter(p => p.category === "looking-for-lender").slice(0, 3)))
            .catch(() => {});
        }
      } catch (e) {}
      setLoading(false);
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    supabase.from("matches_cache").select("*").eq("user_id", user.id).order("score", { ascending: false }).limit(3)
      .then(({ data }) => setSmartMatches(data || []));
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRespond(builderId, action) {
    setRespondStatus(prev => ({ ...prev, [builderId]: "loading" }));
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/respond-request", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ builder_id: builderId, action }),
    });
    if (res.ok) {
      setRespondStatus(prev => ({ ...prev, [builderId]: action }));
      setPendingRequests(prev => prev.filter(r => r.builder_id !== builderId));
      await supabase.auth.refreshSession();
    } else {
      setRespondStatus(prev => ({ ...prev, [builderId]: "error" }));
    }
  }

  const [projectListings, setProjectListings] = useState([]);
  useEffect(() => {
    if (role === "lender") {
      fetch("/api/project-listings").then(r => r.json()).then(d => setProjectListings((d.listings || []).slice(0, 3))).catch(() => {});
    }
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeDeals = deals.filter(d => d.status !== "closed" && d.finder_fee_status === "paid");
  const pendingPaymentDeals = deals.filter(d => d.status !== "closed" && d.finder_fee_status !== "paid");
  const pendingBuilderRequests = role === "builder"
    ? (user?.user_metadata?.connections || []).filter(c => c.status === "pending")
    : [];

  const recoLenderCards = recoLenders.slice(0, 3).map(l => ({
    id: `real-${l.user_id}`, _real: true,
    initials: nameInitials(l.profiles?.full_name), color: pickColor(l.user_id || ""),
    name: l.profiles?.full_name || "Anonymous", type: "Private lender",
    location: l.profiles?.location || "", bio: l.profiles?.bio || "",
    verified: false, budget: l.budget_max ? fmt(l.budget_max) : "Flexible",
    budget_max_num: l.budget_max || 0, returnType: l.return_type || "Flexible",
    builderShare: l.builder_split || null, lenderShare: l.lender_split || null,
    rate: l.interest_rate || null, equity: l.equity_stake || null,
    term: null, exit: null,
    project: (l.preferred_projects || []).join(", ") || "Any",
    preferred_projects: l.preferred_projects || [], avatar_url: l.avatar_url || null,
    user_id: l.user_id, user_role: l.profiles?.user_role || null, sequential_id: l.profiles?.sequential_id || null, is_founding_member: l.profiles?.is_founding_member || false,
    updated_at: l.updated_at || null,
    verification_status: l.verification_status || null,
    company_name: l.company_name || null,
  }));

  const recoBuilderCards = recoBuilders.slice(0, 3).map(b => ({
    id: `real-${b.user_id}`, _real: true,
    initials: nameInitials(b.profiles?.full_name), color: pickColor(b.user_id || ""),
    name: b.profiles?.full_name || "Anonymous", type: b.specialization || "Builder",
    location: b.profiles?.location || "", bio: b.profiles?.bio || "",
    props: b.projects_completed || 0, value: b.total_value || 0,
    completion: b.completion_rate ?? 100, verified_documents: b.verified_documents || [],
    avatar_url: b.avatar_url || null,
    user_id: b.user_id, user_role: b.profiles?.user_role || null, sequential_id: b.profiles?.sequential_id || null,
    updated_at: b.updated_at || null,
  }));

  const recoForBuilder = recoLenderCards;
  const recoForLender  = recoBuilderCards;

  if (loading) return <SkeletonDashboard />;

  return (
    <div style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column", padding: "1rem 1.25rem", maxWidth: 900, margin: "0 auto", boxSizing: "border-box", width: "100%" }}>

      {/* ── TOP BAR ── */}
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <Avatar initials={nameInitials(name)} color={pickColor(user?.id || "")} size={40} url={avatarUrl} />
        <div>
          <h1 id="tour-dashboard-heading" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(16px, 2.5vw, 22px)", fontWeight: 700, margin: 0, color: "#1E3A5F", lineHeight: 1.2 }}>
            Welcome back, {name.split(" ")[0]}
          </h1>
          <div style={{ fontSize: 12, color: "#64748B", marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ background: role === "lender" ? "#EBF2FF" : "#DCFCE7", color: role === "lender" ? "#1E3A5F" : "#166534", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 500, textTransform: "capitalize" }}>{role}</span>
            {!user?.user_metadata?.profile_complete && (
              <button onClick={() => setPage("profile-setup")} style={{ fontSize: 11, color: "#1E3A5F", background: "#EBF2FF", border: "none", borderRadius: 20, padding: "2px 9px", cursor: "pointer", fontWeight: 500 }}>Complete your profile →</button>
            )}
          </div>
        </div>
        <button onClick={() => setPage(role === "builder" ? "search" : "browse-projects")} style={{ marginLeft: "auto", flexShrink: 0, padding: "8px 20px", background: "#3B82F6", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", minHeight: 38 }}>
          {role === "builder" ? "Find a lender →" : "Browse projects →"}
        </button>
      </div>

      {/* ── BUILDER COLUMN ── */}
      {role === "builder" && (() => {
        const profileData = { avatar_url: avatarUrl, bio: userProfile?.bio, location: userProfile?.location, specialization: viewerRoleProfile?.specialization, projects_completed: viewerRoleProfile?.projects_completed, verified_documents: user?.user_metadata?.verified_documents };
        const { missing } = computeProfileCompleteness(profileData, "builder");
        const card = { background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" };
        return (
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingBottom: 12 }}>
            {/* 1. Top Lender Matches */}
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#2E5FA3", letterSpacing: "0.1em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>Top lender matches <HelpTooltip text="Ranked by compatibility score based on your profile, location, and project type." /></div>
                <button onClick={() => setPage("search")} style={{ fontSize: 12, color: "#2E5FA3", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Browse all →</button>
              </div>
              {recoForBuilder.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {recoForBuilder.map(lc => <LenderCard key={lc.id || lc.name} lc={lc} user={user} setPage={setPage} onViewProfile={onViewProfile} viewerProfile={viewerRoleProfile} />)}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: "#64748B", margin: 0 }}>Complete your profile to see lenders matched to your project type and location.</p>
              )}
            </div>

            {/* 1b. Saved profiles */}
            <div onClick={() => setPage("saved-profiles")} style={{ padding: "14px 20px", background: "var(--accent-bg, #EBF2FF)", border: "0.5px solid color-mix(in srgb, var(--accent, #3B82F6) 30%, transparent)", borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon name="heart" size={18} style={{ color: "var(--accent, #2E5FA3)", flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--accent, #2E5FA3)", marginBottom: 2 }}>Saved profiles</div>
                  <div style={{ fontSize: 12, color: "var(--accent, #2E5FA3)", opacity: 0.8 }}>View lenders you've saved with the heart icon</div>
                </div>
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #2E5FA3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </div>

            {/* 2. Your deals — builder */}
            {(activeDeals.length > 0 || pendingPaymentDeals.length > 0) && (() => {
              const allDeals = [...pendingPaymentDeals, ...activeDeals].slice(0, 3);
              return (
                <div style={card}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#0F6E56", letterSpacing: "0.1em", textTransform: "uppercase" }}>Your deals</div>
                    {(activeDeals.length + pendingPaymentDeals.length) > 3 && <button onClick={() => setPage("my-deals")} style={{ fontSize: 12, color: "#0F6E56", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>View all →</button>}
                  </div>
                  {allDeals.map(deal => {
                    const total = deal.agreed_amount || 0;
                    const counterparty = user.id === deal.lender_id ? deal.builder_name : deal.lender_name;
                    const isPending = deal.finder_fee_status !== "paid";
                    return (
                      <div key={deal.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, background: "var(--bg-subtle, #F8FAFC)", border: "0.5px solid var(--border, #e0e0e0)", marginBottom: 6, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{counterparty}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted, #64748B)", marginTop: 1 }}>{total > 0 ? fmt(total) : "—"} · <span style={{ color: isPending ? "var(--accent, #3B82F6)" : "#0F6E56", fontWeight: 500 }}>{isPending ? "Finder's fee pending" : "Active"}</span></div>
                        </div>
                        <button onClick={() => setPage("deal-detail", deal)} style={{ padding: "5px 12px", minHeight: 32, background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>Open Deal Room →</button>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* 3. Active Projects */}
            {activeDeals.length > 0 && (
              <div style={card}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#534AB7", letterSpacing: "0.1em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>Active projects <HelpTooltip text="Projects where you and a lender have agreed to work together. Track project progress here." /></div>
                  <button onClick={() => setPage("my-deals")} style={{ fontSize: 12, color: "#534AB7", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>View all →</button>
                </div>
                {activeDeals.slice(0, 3).map(deal => {
                  const isComplete = !!deal.tracker_completed_at;
                  const statusColor = isComplete ? "#16A34A" : "#534AB7";
                  return (
                    <div key={deal.id} onClick={() => setPage("deal-detail", deal)} style={{ padding: "12px 14px", background: "#F8FAFC", borderRadius: 10, border: "0.5px solid #e0e0e0", marginBottom: 8, cursor: "pointer" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                        <div><div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F" }}>{deal.title}</div><div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>with {deal.lender_name}</div></div>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: isComplete ? "#E1F5EE" : "#EEF2FF", color: statusColor, flexShrink: 0 }}>{isComplete ? "Complete" : "In progress"}</span>
                      </div>
                      <div style={{ height: 5, background: "#e0e0e0", borderRadius: 3 }}><div style={{ height: 5, borderRadius: 3, background: statusColor, width: isComplete ? "100%" : "40%" }} /></div>
                      <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{isComplete ? "Project complete" : `${(deal.deal_updates || []).length} update${(deal.deal_updates || []).length !== 1 ? "s" : ""} posted`}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 3. Recent Messages */}
            <div style={card}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#2E5FA3", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Recent messages</div>
              {conversations.length > 0 ? (
                <>
                  {conversations.slice(0, 3).map(convo => {
                    const otherName = user.id === convo.lender_id ? convo.builder_name : convo.lender_name;
                    return (
                      <div key={convo.id} onClick={() => onMessage(convo.id)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px", borderRadius: 8, marginBottom: 4 }} onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <Avatar initials={nameInitials(otherName)} color={pickColor(otherName || "")} size={32} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1E3A5F" }}>{otherName}</div>
                          <div style={{ fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{convo.last_message || "Start a conversation"}</div>
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={() => setPage("messages")} style={{ marginTop: 4, width: "100%", background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "8px", minHeight: 40, fontSize: 12, color: "#555", cursor: "pointer" }}>Open all messages →</button>
                </>
              ) : (
                <p style={{ fontSize: 13, color: "#64748B", margin: 0 }}>No conversations yet — connect with a lender to start messaging.</p>
              )}
            </div>

            {/* 4. Profile completion checklist */}
            {missing.length > 0 && (
              <div style={card}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--accent)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>To attract lenders — complete these steps</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {missing.map(item => (
                    <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", border: "1.5px solid var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)" }} /></div>
                      <span style={{ fontSize: 13, color: "#555" }}>{item.label}</span>
                      <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--accent)", fontWeight: 500 }}>+{item.weight}%</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setPage("profile-setup")} style={{ marginTop: 12, padding: "8px 16px", minHeight: 36, background: "var(--accent)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Complete profile →</button>
              </div>
            )}

            {/* 5. Join the community */}
            <div onClick={() => setPage("community-groups")} style={{ padding: "14px 20px", background: "var(--accent-bg, #EBF2FF)", border: "0.5px solid color-mix(in srgb, var(--accent, #3B82F6) 30%, transparent)", borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--accent, #2E5FA3)", marginBottom: 2 }}>Join the community</div>
                <div style={{ fontSize: 12, color: "var(--accent, #2E5FA3)", opacity: 0.8 }}>Network with builders and lenders in your area</div>
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #2E5FA3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </div>
          </div>
        );
      })()}

      {/* ── LENDER COLUMN ── */}
      {role === "lender" && (() => {
        const totalInvested = activeDeals.reduce((s, d) => s + (d.agreed_amount || 0), 0);
        const card = { background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" };
        return (
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingBottom: 12 }}>
            {/* 1. Investment Overview */}
            <div style={{ background: "linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)", borderRadius: 14, padding: "20px 24px", color: "#fff" }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.7, marginBottom: 12 }}>Your investment overview</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                <div><div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Total invested</div><div style={{ fontSize: 22, fontWeight: 700 }}>{totalInvested > 0 ? fmt(totalInvested) : "—"}</div></div>
                <div>
                  <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Active deals</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{activeDeals.length}</div>
                  {pendingPaymentDeals.length > 0 && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{pendingPaymentDeals.length} pending payment</div>}
                </div>
                <div><div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Connections</div><div style={{ fontSize: 22, fontWeight: 700 }}>{myConnections.length}</div></div>
              </div>
            </div>

            {/* Your deals card — lender */}
            {(activeDeals.length > 0 || pendingPaymentDeals.length > 0) && (() => {
              const allDeals = [...pendingPaymentDeals, ...activeDeals].slice(0, 3);
              return (
                <div style={card}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#0F6E56", letterSpacing: "0.1em", textTransform: "uppercase" }}>Your deals</div>
                    {(activeDeals.length + pendingPaymentDeals.length) > 3 && <button onClick={() => setPage("my-deals")} style={{ fontSize: 12, color: "#0F6E56", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>View all →</button>}
                  </div>
                  {allDeals.map(deal => {
                    const total = deal.agreed_amount || 0;
                    const counterparty = user.id === deal.lender_id ? deal.builder_name : deal.lender_name;
                    const isPending = deal.finder_fee_status !== "paid";
                    const feeAmt = (total * 0.01).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    return (
                      <div key={deal.id} style={{ padding: "9px 10px", borderRadius: 8, background: isPending ? "var(--accent-bg, #EBF2FF)" : "var(--bg-subtle, #F8FAFC)", border: `0.5px solid ${isPending ? "var(--accent, #3B82F6)" : "var(--border, #e0e0e0)"}`, marginBottom: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{counterparty}</div>
                            <div style={{ fontSize: 11, color: "var(--text-muted, #64748B)", marginTop: 1 }}>{total > 0 ? fmt(total) : "—"} · <span style={{ color: isPending ? "var(--accent, #3B82F6)" : "#0F6E56", fontWeight: 500 }}>{isPending ? "Finder's fee due" : "Active"}</span></div>
                          </div>
                          {isPending
                            ? <button onClick={() => setPage("deal-detail", deal)} style={{ padding: "5px 12px", minHeight: 32, background: "var(--accent, #3B82F6)", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>Pay finder's fee — £{feeAmt}</button>
                            : <button onClick={() => setPage("deal-detail", deal)} style={{ padding: "5px 12px", minHeight: 32, background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>Open Deal Room →</button>
                          }
                        </div>
                        {isPending && (
                          <div style={{ fontSize: 11, color: "var(--text-muted, #64748B)", marginTop: 6 }}>Pay this to LenderBuild to complete the introduction. The lender and builder arrange fund transfers directly between themselves.</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* 2. Top Builder Matches */}
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#2E5FA3", letterSpacing: "0.1em", textTransform: "uppercase" }}>Top builder matches</div>
                <button onClick={() => setPage("find-builder")} style={{ fontSize: 12, color: "#2E5FA3", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Browse all →</button>
              </div>
              {recoForLender.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {recoForLender.map(b => <BuilderCard key={b.id} builder={b} user={user} setPage={setPage} onMessage={onMessage} onViewProfile={onViewBuilderProfile} viewerProfile={viewerRoleProfile} />)}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: "#64748B", margin: 0 }}>No builder matches yet — browse projects to find builders that suit your investment criteria.</p>
              )}
            </div>

            {/* 2a. Saved profiles */}
            <div onClick={() => setPage("saved-profiles")} style={{ padding: "14px 20px", background: "var(--accent-bg, #EBF2FF)", border: "0.5px solid color-mix(in srgb, var(--accent, #3B82F6) 30%, transparent)", borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon name="heart" size={18} style={{ color: "var(--accent, #2E5FA3)", flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--accent, #2E5FA3)", marginBottom: 2 }}>Saved profiles</div>
                  <div style={{ fontSize: 12, color: "var(--accent, #2E5FA3)", opacity: 0.8 }}>View builders you've saved with the heart icon</div>
                </div>
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #2E5FA3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </div>

            {/* 2b. Latest Posts */}
            {latestFundingPosts.length > 0 && (
              <div style={card}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#2E5FA3", letterSpacing: "0.1em", textTransform: "uppercase" }}>Latest posts</div>
                  <button onClick={() => setPage("posts")} style={{ fontSize: 12, color: "#2E5FA3", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Browse all posts →</button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {latestFundingPosts.map(post => (
                    <div key={post.id} onClick={() => setPage("post-detail", post)} style={{ cursor: "pointer", padding: "10px 12px", borderRadius: 8, background: "#F8FAFC", border: "0.5px solid #e8e8e8" }} onMouseEnter={e => e.currentTarget.style.background = "#F1F5F9"} onMouseLeave={e => e.currentTarget.style.background = "#F8FAFC"}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{post.title}</div>
                      <div style={{ fontSize: 11, color: "#64748B" }}>{post.author_name || "Builder"}{post.funding_needed ? ` · £${Number(post.funding_needed).toLocaleString("en-GB")}` : " · Looking for funding"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. Recent Messages */}
            <div style={card}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#2E5FA3", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Recent messages</div>
              {conversations.length > 0 ? (
                <>
                  {conversations.slice(0, 3).map(convo => {
                    const otherName = user.id === convo.lender_id ? convo.builder_name : convo.lender_name;
                    return (
                      <div key={convo.id} onClick={() => onMessage(convo.id)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px", borderRadius: 8, marginBottom: 4 }} onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <Avatar initials={nameInitials(otherName)} color={pickColor(otherName || "")} size={32} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1E3A5F" }}>{otherName}</div>
                          <div style={{ fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{convo.last_message || "Start a conversation"}</div>
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={() => setPage("messages")} style={{ marginTop: 4, width: "100%", background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "8px", minHeight: 40, fontSize: 12, color: "#555", cursor: "pointer" }}>Open all messages →</button>
                </>
              ) : (
                <p style={{ fontSize: 13, color: "#64748B", margin: 0 }}>No conversations yet — connect with a builder to start messaging.</p>
              )}
            </div>

            {/* 4. Investment Portfolio */}
            <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", borderRadius: 12, padding: "16px 20px", color: "#fff", cursor: "pointer" }} onClick={() => setPage("lender-wallet")}>
              <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>Investment portfolio</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>View your wallet →</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Track returns, download statements, see portfolio breakdown</div>
            </div>

            {/* 5. Join the community */}
            <div onClick={() => setPage("community-groups")} style={{ padding: "14px 20px", background: "var(--accent-bg, #EBF2FF)", border: "0.5px solid color-mix(in srgb, var(--accent, #3B82F6) 30%, transparent)", borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--accent, #2E5FA3)", marginBottom: 2 }}>Join the community</div>
                <div style={{ fontSize: 12, color: "var(--accent, #2E5FA3)", opacity: 0.8 }}>Network with builders and lenders in your area</div>
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #2E5FA3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── HOME PAGE ───────────────────────────────────────────────────────────────


function TrustBadges({ style = {} }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", justifyContent: "center", ...style }}>
      {/* Stripe */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, opacity: 0.7 }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect width="14" height="14" rx="3" fill="#635BFF"/>
          <path d="M5.6 5.25c0-.49.4-.7.97-.7.87 0 1.96.26 2.83.73V3.24A7.5 7.5 0 006.57 2.8C4.72 2.8 3.5 3.76 3.5 5.35c0 2.44 3.36 2.05 3.36 3.1 0 .58-.5.77-1.1.77-.95 0-2.16-.39-3.12-.92v2.06c1.06.46 2.13.65 3.12.65 1.89 0 3.18-.94 3.18-2.54-.01-2.63-3.34-2.17-3.34-3.27z" fill="#fff"/>
        </svg>
        <span style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.65)" }}>Secured by Stripe</span>
      </div>
      {/* SSL */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, opacity: 0.7 }}>
        <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
          <rect x="1" y="6" width="10" height="7" rx="1.5" stroke="rgba(255,255,255,0.6)" strokeWidth="1.3"/>
          <path d="M3.5 6V4.5a2.5 2.5 0 015 0V6" stroke="rgba(255,255,255,0.6)" strokeWidth="1.3" strokeLinecap="round"/>
          <circle cx="6" cy="9.5" r="1" fill="rgba(255,255,255,0.6)"/>
        </svg>
        <span style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.65)" }}>256-bit SSL</span>
      </div>
      {/* UK registered */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, opacity: 0.7 }}>
        <Icon name="globe" size={13} className="lb-icon" />
        <span style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.65)" }}>UK registered business</span>
      </div>
    </div>
  );
}

function SuccessStories() {
  return (
    <div style={{ background: "#F4F5F7", padding: "4rem 2rem", textAlign: "center" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Success stories</div>
      <div style={{ fontSize: 32, marginBottom: 16 }}>⭐</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1E3A5F", fontFamily: "'Playfair Display', Georgia, serif", margin: "0 0 10px" }}>
        Success stories coming soon
      </h2>
      <p style={{ fontSize: 15, color: "#64748B", maxWidth: 440, margin: "0 auto" }}>
        As our community grows and deals complete, we'll share real stories from builders and lenders who found their match on LenderBuild.
      </p>
      </div>
    </div>
  );
}

function HomePage({ setPage, user, onViewProfile }) {
  const heroImage = useMemo(() => PROPERTY_IMAGES[Math.floor(Math.random() * PROPERTY_IMAGES.length)], []);

  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 560);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 560);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Show accepted connection notifications for builders
  const acceptedConnections = user?.user_metadata?.role === "builder"
    ? (user?.user_metadata?.connections || []).filter(c => c.status === "accepted")
    : [];

  const homeUid = user?.id || "";
  const [dismissProfileBanner, setDismissProfileBanner] = useState(() => { try { return !!localStorage.getItem(`lb_dismiss_profile_banner_${homeUid}`); } catch { return false; } });


  return (
    <div>
      {/* Builder notifications */}
      {acceptedConnections.length > 0 && (
        <div style={{ background: "#EBF2FF", borderBottom: "1px solid #E0C87A", padding: "12px 1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Icon name="party" size={16} />
            <div style={{ fontSize: 13, color: "#1E3A5F" }}>
              <strong>
                {acceptedConnections.length === 1
                  ? `${acceptedConnections[0].lender_name} accepted your request!`
                  : `${acceptedConnections.length} lenders accepted your requests!`}
              </strong>
              {" "}Check your account for details.
            </div>
            <button
              onClick={() => setPage("profile-setup")}
              style={{ marginLeft: "auto", background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "5px 14px", minHeight: 44, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              View profile
            </button>
          </div>
        </div>
      )}
      {/* Profile completion prompt */}
      {user && !user.user_metadata?.profile_complete && !dismissProfileBanner && (
        <div style={{ background: "#FFFBEB", borderBottom: "0.5px solid #F6D860", padding: "12px 1.25rem", position: "relative" }}>
          <button onClick={() => { setDismissProfileBanner(true); try { localStorage.setItem(`lb_dismiss_profile_banner_${homeUid}`, "1"); } catch {} }} aria-label="Dismiss" style={{ position: "absolute", top: 8, right: 8, width: 20, height: 20, borderRadius: "50%", background: "#E5E7EB", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#374151", lineHeight: 1, padding: 0 }}>×</button>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", paddingRight: 28 }}>
            <div style={{ fontSize: 13, color: "#1E3A5F", flex: 1 }}>
              Complete your profile to appear in search results
            </div>
            <button
              onClick={() => setPage("profile-setup")}
              style={{ background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "6px 16px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Set up profile
            </button>
          </div>
        </div>
      )}
      {/* Hero */}
      <div style={{
        background: "#1E3A5F", textAlign: "center",
        padding: "4rem 1.25rem 3rem", position: "relative", overflow: "hidden",
        animation: "slideUp 0.5s ease",
      }}>
        {/* Random property background image */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: `url(${heroImage})`,
          backgroundSize: "cover", backgroundPosition: "center",
          opacity: 0.28,
        }} />
        {/* Subtle grid overlay */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: [
            "repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(59,130,246,0.03) 40px, rgba(59,130,246,0.03) 41px)",
            "repeating-linear-gradient(-45deg, transparent, transparent 40px, rgba(59,130,246,0.03) 40px, rgba(59,130,246,0.03) 41px)",
          ].join(", "),
        }} />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto" }}>
          <div style={{
            display: "inline-block", background: "rgba(59,130,246,0.15)", color: "#3B82F6",
            fontSize: 11, fontWeight: 600, padding: "5px 16px", borderRadius: 20, marginBottom: "1.5rem",
            letterSpacing: "0.1em", textTransform: "uppercase",
          }}>
            UK Property Investment Platform
          </div>
          <h1 style={{
            fontSize: "clamp(28px, 9vw, 68px)", fontWeight: 700, lineHeight: 1.1,
            margin: "0 0 1.25rem", fontFamily: "'Playfair Display', Georgia, serif", color: "#fff",
          }}>
            Where <span style={{ color: "#3B82F6", fontSize: "inherit", fontWeight: "inherit" }}>builders</span> meet<br />
            the right <span style={{ color: "#3B82F6", fontSize: "inherit", fontWeight: "inherit" }}>lenders</span>
          </h1>
          <div style={{ width: 64, height: 3, background: "#3B82F6", margin: "0 auto 1.75rem", borderRadius: 2 }} />
          <p style={{ fontSize: 17, color: "rgba(255,255,255,0.7)", maxWidth: 520, margin: "0 auto 2.5rem", lineHeight: 1.75 }}>
            Search lenders by budget, agree on returns, connect instantly. We match you — you build together.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", position: "relative", zIndex: 2 }}>
            <button
              onClick={() => setPage("for-builders")}
              style={{ position: "relative", zIndex: 10, background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "15px 28px", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
              I'm a builder — find funding
            </button>
            <button
              onClick={() => setPage("for-lenders")}
              style={{ position: "relative", zIndex: 10, background: "transparent", color: "#fff", border: "1.5px solid rgba(255,255,255,0.4)", padding: "15px 28px", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
              I'm a lender — earn returns
            </button>
          </div>

          {/* Trust badges in hero */}
          <div style={{ marginTop: "2.5rem" }}>
            <TrustBadges />
          </div>
        </div>
      </div>

      {/* How it works — 3 steps */}
      <div style={{ padding: "3rem 1.25rem", background: "#F4F5F7", borderBottom: "0.5px solid #eee" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>How it works</div>
          <h2 style={{ fontSize: 26, fontWeight: 700, margin: "0 0 0.5rem", fontFamily: "'Playfair Display', Georgia, serif", color: "#1E3A5F" }}>From search to funded in three steps</h2>
          <p style={{ fontSize: 14, color: "#64748B", marginBottom: "3rem" }}>No hidden fees. No middlemen. Just straightforward property finance.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "2rem", textAlign: "left" }}>
            {[
              {
                n: "1",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="10" r="5" stroke="#3B82F6" strokeWidth="1.8"/>
                    <path d="M4 24c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                ),
                title: "Create your profile",
                desc: "Tell us who you are — builder or lender. Add your project scope, budget, and return expectations.",
              },
              {
                n: "2",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="11" cy="11" r="6.5" stroke="#3B82F6" strokeWidth="1.8"/>
                    <path d="M16 16l7 7" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                ),
                title: "Browse & connect",
                desc: "Search lenders by budget, return type, and project preference. Send a connect request in one click.",
              },
              {
                n: "3",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="4" y="8" width="20" height="14" rx="3" stroke="#3B82F6" strokeWidth="1.8"/>
                    <path d="M4 13h20" stroke="#3B82F6" strokeWidth="1.8"/>
                    <path d="M9 18h4" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                ),
                title: "Funds released",
                desc: "Lender approves and releases funds. A simple 1% finder's fee is collected at completion — that's it.",
              },
            ].map(({ n, icon, title, desc }) => (
              <div key={n} style={{ background: "#fff", borderRadius: 14, padding: "1.75rem", border: "0.5px solid #e8e4d9", position: "relative" }}>
                <div style={{ position: "absolute", top: -14, left: 20, width: 28, height: 28, borderRadius: "50%", background: "#3B82F6", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800 }}>{n}</div>
                <div style={{ marginBottom: 14, marginTop: 4 }}>{icon}</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: "#1E3A5F" }}>{title}</div>
                <div style={{ fontSize: 13, color: "#777", lineHeight: 1.65 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Success Stories */}
      <SuccessStories />

    </div>
  );
}

// ─── LENDER CARD ─────────────────────────────────────────────────────────────

function LenderCard({ lc, user, setPage, settings, onViewProfile, viewerProfile, onMessage }) {
  const isListed = settings?.listing_active !== false;
  const [msgStatus, setMsgStatus] = useState("idle");
  const [hovered,   setHovered]   = useState(false);

  // Hidden entirely when lender has closed their listing
  if (!isListed && settings) return null;

  async function handleMessage() {
    if (!user) { setPage("auth"); return; }
    setMsgStatus("loading");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/notify-message", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ other_name: lc.name, other_role: "lender" }),
    });
    if (res.ok) {
      const { conversation } = await res.json();
      setMsgStatus("idle");
      if (onMessage) onMessage(conversation.id);
      else setPage("messages");
    } else {
      setMsgStatus("error");
      setTimeout(() => setMsgStatus("idle"), 3000);
    }
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "var(--bg-card, #fff)",
        border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
        borderRadius: 12, padding: "24px",
        boxShadow: hovered ? "0 8px 24px rgba(0,0,0,0.12)" : "0 2px 8px rgba(0,0,0,0.08)",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        transition: "box-shadow 0.2s, transform 0.2s ease",
        animation: "cardIn 0.4s ease",
        display: "flex", flexDirection: "column",
      }}>
      {lc.featured && (
        <div style={{ marginBottom: 10 }}>
          <span style={{ background: "#EBF2FF", color: "#1E3A5F", fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 600, letterSpacing: "0.04em" }}>Most active</span>
        </div>
      )}
      {(() => {
        if (!viewerProfile || user?.user_metadata?.role !== "builder") return null;
        const m = computeMatch(viewerProfile, lc, "builder");
        if (!m) return null;
        return <div style={{ marginBottom: 10 }}><MatchBadge score={m.score} breakdown={m.components} /></div>;
      })()}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
        <Avatar initials={lc.initials} color={lc.color} size={40} url={lc.avatar_url} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F" }}>{lc.name}</div>
            <RoleBadge userRole={lc.user_role} authRole="lender" />
            {lc.verification_status === "verified" && (
              <span title={lc.company_name ? `Registered business: ${lc.company_name}` : "Verified with Companies House"} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#166534", color: "#fff", flexShrink: 0, cursor: "help", display:"inline-flex", alignItems:"center", gap:3 }}>
                ✓ Verified{lc.company_name ? ` — ${lc.company_name}` : ""}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#64748B" }}>{lc.type}</div>
          {lc.location && (
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2, display:"flex", alignItems:"center", gap:3 }}><Icon name="map-pin" size={12} style={{flexShrink:0}} />{lc.location}</div>
          )}
        </div>
        <HeartButton userId={user?.id} targetUserId={lc.user_id} profileSnap={{ user_id: lc.user_id, role: "lender", name: lc.name, type: lc.type, avatar_url: lc.avatar_url, location: lc.location }} />
      </div>
      <div style={{ display: "inline-block", background: "#EBF2FF", color: "#1E3A5F", fontSize: 13, fontWeight: 600, padding: "4px 12px", borderRadius: 8, marginBottom: 12 }}>
        Up to {lc.budget}
      </div>
      <div style={{ fontSize: 13, flex: 1 }}>
        {[
          ["Return type", lc.returnType],
          lc.builderShare ? ["Builder share", lc.builderShare] : null,
          lc.lenderShare  ? ["Lender share",  lc.lenderShare]  : null,
          lc.rate         ? ["Rate",           lc.rate]         : null,
          lc.term         ? ["Term",           lc.term]         : null,
          lc.equity       ? ["Equity ask",     lc.equity]       : null,
          lc.exit         ? ["Exit timeline",  lc.exit]         : null,
          ["Project pref.", lc.project],
        ].filter(Boolean).map(([label, val]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", color: "#64748B" }}>
            <span>{label}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 500, color: "#2C3E50" }}>
              {val}
              {label === "Return type" && RETURN_TYPE_TOOLTIPS[val] && <HelpTooltip text={RETURN_TYPE_TOOLTIPS[val]} />}
            </span>
          </div>
        ))}
      </div>
      {(() => {
        const cp = computeProfileCompleteness({
          avatar_url: lc.avatar_url, bio: lc.bio, location: lc.location,
          budget_max: lc.budget_max_num,
          return_type: lc.returnType !== "Flexible" ? lc.returnType : null,
          preferred_projects: lc.preferred_projects,
        }, "lender");
        if (cp.score >= 50) return null;
        return (
          <div style={{ marginTop: 8, fontSize: 10, color: "#DC2626", fontWeight: 500 }}>
            Profile incomplete
          </div>
        );
      })()}
      <div style={{ marginTop: 16, borderTop: "0.5px solid #eee", paddingTop: 14 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {user && (
            <button
              onClick={handleMessage}
              disabled={msgStatus === "loading"}
              style={{
                flex: 1, background: "var(--accent)", color: "#fff", border: "none",
                padding: "9px 8px", borderRadius: 8, fontSize: 13, fontWeight: 600, minHeight: 44,
                cursor: msgStatus === "loading" ? "default" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {msgStatus === "loading" ? "Opening…" : msgStatus === "error" ? "Couldn't open chat — retry" : "Message"}
            </button>
          )}
          <button
            onClick={() => onViewProfile && onViewProfile(lc)}
            style={{ flex: 1, background: "transparent", color: "var(--accent)", border: "1.5px solid var(--accent)", padding: "9px 8px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", minHeight: 44 }}
          >
            View profile
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SEARCH PAGE ─────────────────────────────────────────────────────────────

function SearchPage({ user, setPage, onViewProfile, viewerRoleProfile }) {
  const [dismissCalcNudge, setDismissCalcNudge] = useState(() => { try { return !!localStorage.getItem("lb_dismiss_calc_nudge"); } catch { return false; } });
  const [budget,        setBudget]        = useState("any");
  const [returnType,    setReturnType]    = useState("any");
  const [project,       setProject]       = useState("any");
  const [verifiedOnly,  setVerifiedOnly]  = useState(false);
  const [activeOnly,    setActiveOnly]    = useState(false);
  const [sortBy,        setSortBy]        = useState("newest");
  const [budgetMin,     setBudgetMin]     = useState(0);
  const [budgetMax_,    setBudgetMax_]    = useState(1000000);
  const [lenderSettings, setLenderSettings] = useState({});
  const [saveSearchModal, setSaveSearchModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");
  const [saveSearchFreq, setSaveSearchFreq] = useState("instant");
  const [saveSearchMsg, setSaveSearchMsg] = useState("");
  const [realLenders,   setRealLenders]   = useState([]);
  const [loadingReal,   setLoadingReal]   = useState(true);
  const [filterOpen,    setFilterOpen]    = useState(false);
  const [matchFilter,   setMatchFilter]   = useState("all");
  const [regionFilter,  setRegionFilter]  = useState("any");
  const [returnRateFilter, setReturnRateFilter] = useState("any");
  const [presetName,    setPresetName]    = useState("");
  const [presets,       setPresets]       = useState(() => user?.id ? getFilterPresets(user.id, "lender") : []);
  const [width,         setWidth]         = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = width < 640;

  function applyPreset(p) {
    const f = p.filters;
    if (f.budget)      setBudget(f.budget);
    if (f.returnType)  setReturnType(f.returnType);
    if (f.project)     setProject(f.project);
    if (f.verifiedOnly !== undefined) setVerifiedOnly(f.verifiedOnly);
    if (f.activeOnly   !== undefined) setActiveOnly(f.activeOnly);
    if (f.sortBy)      setSortBy(f.sortBy);
    if (f.budgetMin    !== undefined) setBudgetMin(f.budgetMin);
    if (f.budgetMax_   !== undefined) setBudgetMax_(f.budgetMax_);
  }

  function handleSavePreset() {
    if (!user?.id || !presetName.trim()) return;
    const filters = { budget, returnType, project, verifiedOnly, activeOnly, sortBy, budgetMin, budgetMax_ };
    saveFilterPreset(user.id, "lender", presetName.trim(), filters);
    setPresets(getFilterPresets(user.id, "lender"));
    setPresetName("");
  }

  function handleDeletePreset(name) {
    deleteFilterPreset(user.id, "lender", name);
    setPresets(getFilterPresets(user.id, "lender"));
  }

  async function handleSaveSearch(e) {
    e.preventDefault();
    if (!user || !saveSearchName.trim()) return;
    const { data: { session } } = await supabase.auth.getSession();
    const filters = { budget, returnType, project, verifiedOnly, activeOnly, sortBy, budgetMin, budgetMax_ };
    const res = await fetch("/api/save-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "save-search", name: saveSearchName.trim(), role_type: "lender", filters, frequency: saveSearchFreq }),
    });
    if (res.ok) {
      setSaveSearchMsg("Search saved!");
      setSaveSearchModal(false);
      setSaveSearchName("");
      setTimeout(() => setSaveSearchMsg(""), 3000);
    }
  }

  useEffect(() => {
    fetch("/api/lender-settings")
      .then(r => r.json())
      .then(d => setLenderSettings(d.settings || {}))
      .catch(() => {});

    fetch("/api/get-profiles?type=lenders")
      .then(r => r.json())
      .then(d => { setRealLenders(d.lenders || []); setLoadingReal(false); })
      .catch(() => setLoadingReal(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Transform real DB lenders into the same shape as LENDER_CARDS
  const realCards = realLenders.map(l => ({
    id:          `real-${l.user_id}`,
    _real:       true,
    initials:    nameInitials(l.profiles?.full_name),
    color:       pickColor(l.user_id || ""),
    name:        l.profiles?.full_name || "Anonymous",
    type:        "Private lender",
    location:    l.profiles?.location || "",
    bio:         l.profiles?.bio || "",
    verified:    false,
    budget:      l.budget_max ? fmt(l.budget_max) : "Flexible",
    budget_max_num: l.budget_max || 0,
    returnType:  l.return_type || "Flexible",
    builderShare: l.builder_split || null,
    lenderShare:  l.lender_split  || null,
    rate:         l.interest_rate  || null,
    equity:       l.equity_stake   || null,
    term:         null,
    exit:         null,
    project:     (l.preferred_projects || []).join(", ") || "Any",
    preferred_projects: l.preferred_projects || [],
    avatar_url:        l.avatar_url || null,
    user_id:           l.user_id,
    user_role:         l.profiles?.user_role || null,
    sequential_id:     l.profiles?.sequential_id || null,
    updated_at:        l.updated_at || null,
    verification_status: l.verification_status || null,
    company_name:      l.company_name || null,
  }));

  // Filter logic
  function cardMatches(card) {
    if (budget !== "any") {
      const n = card.budget_max_num;
      if (budget === "under-50k"  && !(n > 0 && n < 50000))           return false;
      if (budget === "50k-150k"   && !(n >= 50000 && n <= 150000))    return false;
      if (budget === "150k-500k"  && !(n > 150000 && n <= 500000))    return false;
      if (budget === "500k-plus"  && !(n > 500000))                   return false;
    }
    if (card.budget_max_num > 0) {
      if (card.budget_max_num < budgetMin || card.budget_max_num > budgetMax_) return false;
    }
    if (returnType !== "any" && card.returnType !== returnType) return false;
    if (project !== "any" && card.project !== "Any" && !card.preferred_projects?.includes(project)) return false;
    if (regionFilter !== "any" && !inRegionBucket(card.location, regionFilter)) return false;
    if (returnRateFilter !== "any") {
      const r = card.rate;
      if (r === null || r === undefined) return false;
      if (returnRateFilter === "under-8" && !(r < 8))           return false;
      if (returnRateFilter === "8-12"    && !(r >= 8 && r <= 12)) return false;
      if (returnRateFilter === "12-plus" && !(r > 12))           return false;
    }
    if (verifiedOnly && card.user_role !== "verified_pro" && card.user_role !== "founder") return false;
    if (activeOnly) {
      const days = card.updated_at ? (Date.now() - new Date(card.updated_at).getTime()) / 86400000 : 999;
      if (days > 30) return false;
    }
    if (matchFilter !== "all" && viewerRoleProfile) {
      const m = computeMatch(viewerRoleProfile, card, "builder");
      const s = m?.score || 0;
      if (matchFilter === "great" && s < 80) return false;
      if (matchFilter === "good"  && s < 60) return false;
    }
    return true;
  }

  function sortCards(list) {
    if (sortBy === "budget")  return [...list].sort((a, b) => (b.budget_max_num || 0) - (a.budget_max_num || 0));
    if (sortBy === "active")  return [...list].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
    if (sortBy === "compatibility" && viewerRoleProfile) {
      return [...list].sort((a, b) => {
        const sa = computeMatch(viewerRoleProfile, a, "builder")?.score || 0;
        const sb = computeMatch(viewerRoleProfile, b, "builder")?.score || 0;
        return sb - sa;
      });
    }
    return [...list].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  }

  const filteredReal = sortCards(realCards.filter(cardMatches));
  const allCards     = filteredReal;

  const activeFiltersCountFull = [
    budget !== "any", returnType !== "any", project !== "any",
    verifiedOnly, activeOnly, budgetMin > 0, budgetMax_ < 1000000,
    matchFilter !== "all", regionFilter !== "any", returnRateFilter !== "any",
  ].filter(Boolean).length;

  return (
    <div style={{ padding: "1.5rem 1.25rem" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#2E5FA3", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Find a lender</div>
      <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px, 4vw, 32px)", fontWeight: 700, marginBottom: 4, color: "#1E3A5F" }}>Search available lenders</h2>
      <p style={{ fontSize: 14, color: "#64748B", margin: "0 0 1rem" }}>Browse lenders actively looking to fund UK property projects — filter by budget, return type, and project preference.</p>

      {/* Build Calculator nudge */}
      {dismissCalcNudge ? (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 14px", background: "#F8FAFC", border: "0.5px solid #E2E8F0", borderRadius: 8, marginBottom: "1rem" }}>
        <span style={{ fontSize: 12, color: "#64748B", display:"inline-flex", alignItems:"center", gap:4 }}><Icon name="calculator" size={12} /> Build Calculator</span>
        <button onClick={() => { setDismissCalcNudge(false); try { localStorage.removeItem("lb_dismiss_calc_nudge"); } catch {} }} style={{ fontSize: 12, color: "#2E5FA3", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, padding: "2px 6px", fontWeight: 500 }}>
          Show <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
        </button>
      </div>
      ) : (
      <div style={{ background: "#EBF2FF", border: "0.5px solid #C3D9FF", borderRadius: 12, padding: "10px 14px", marginBottom: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
          <button onClick={() => { setDismissCalcNudge(true); try { localStorage.setItem("lb_dismiss_calc_nudge", "1"); } catch {} }} style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 8px", background: "rgba(255,255,255,0.6)", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, color: "#64748B", fontWeight: 500 }}>
            Hide <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#1E3A5F" }}>Not sure how much to ask for? Use our build calculator</span>
          <button onClick={() => setPage("build-calculator")} style={{ padding: "7px 16px", background: "var(--accent,#3B82F6)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>Build Calculator</button>
        </div>
      </div>
      )}

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <button
          onClick={() => setFilterOpen(o => !o)}
          style={{ display: "flex", alignItems: "center", gap: 8, background: filterOpen ? "#1E3A5F" : "#fff", border: `0.5px solid ${filterOpen ? "#1E3A5F" : "#ccc"}`, borderRadius: 8, padding: "9px 16px", minHeight: 44, fontSize: 14, cursor: "pointer", fontWeight: 500, color: filterOpen ? "#fff" : "#1E3A5F" }}
        >
          <svg width="16" height="12" viewBox="0 0 16 12" fill="none"><path d="M0 1h16M3 6h10M6 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          Filters
          {activeFiltersCountFull > 0 && <span style={{ background: filterOpen ? "rgba(255,255,255,0.3)" : "#2E5FA3", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{activeFiltersCountFull}</span>}
        </button>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ height: 44, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 13, background: "#fff", cursor: "pointer" }}>
          <option value="newest">Sort: Newest first</option>
          <option value="active">Sort: Most active</option>
          <option value="budget">Sort: Highest budget</option>
          <option value="compatibility">Sort: Best match</option>
        </select>
        {user && viewerRoleProfile && (
          <select value={matchFilter} onChange={e => setMatchFilter(e.target.value)} style={{ height: 44, border: matchFilter !== "all" ? "1.5px solid #3B82F6" : "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 13, background: matchFilter !== "all" ? "#EBF2FF" : "#fff", color: matchFilter !== "all" ? "#1E3A5F" : undefined, cursor: "pointer", fontWeight: matchFilter !== "all" ? 600 : 400 }}>
            <option value="all">All matches</option>
            <option value="great">Great matches only (80%+)</option>
            <option value="good">Good matches &amp; above (60%+)</option>
          </select>
        )}
        {activeFiltersCountFull > 0 && (
          <button onClick={() => { setBudget("any"); setReturnType("any"); setProject("any"); setVerifiedOnly(false); setActiveOnly(false); setBudgetMin(0); setBudgetMax_(1000000); setMatchFilter("all"); setRegionFilter("any"); setReturnRateFilter("any"); }}
            style={{ background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: "pointer", fontWeight: 500 }}>
            Clear all filters
          </button>
        )}
        {/* Saved presets */}
        {presets.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {presets.map(p => (
              <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button onClick={() => applyPreset(p)} style={{ background: "#EBF2FF", color: "#1E3A5F", border: "none", borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                  ⭐ {p.name}
                </button>
                <button onClick={() => handleDeletePreset(p.name)} style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", padding: 2, fontSize: 12 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Advanced filter panel */}
      {filterOpen && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "20px 24px", marginBottom: "1.25rem", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Funding range</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { v: "any",       l: "Any" },
                  { v: "under-50k", l: "Under £50k" },
                  { v: "50k-150k",  l: "£50k–£150k" },
                  { v: "150k-500k", l: "£150k–£500k" },
                  { v: "500k-plus", l: "£500k+" },
                ].map(o => (
                  <button key={o.v} onClick={() => setBudget(o.v)} style={{ padding: "5px 11px", minHeight: 32, borderRadius: 20, border: budget === o.v ? "none" : "0.5px solid #e0e0e0", background: budget === o.v ? "var(--accent,#2E5FA3)" : "#fff", color: budget === o.v ? "#fff" : "#555", fontSize: 12, fontWeight: budget === o.v ? 600 : 400, cursor: "pointer" }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Project type</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["any", "New build", "Renovation", "Commercial", "Mixed use"].map(o => (
                  <button key={o} onClick={() => setProject(o)} style={{ padding: "5px 11px", minHeight: 32, borderRadius: 20, border: project === o ? "none" : "0.5px solid #e0e0e0", background: project === o ? "var(--accent,#2E5FA3)" : "#fff", color: project === o ? "#fff" : "#555", fontSize: 12, fontWeight: project === o ? 600 : 400, cursor: "pointer" }}>
                    {o === "any" ? "Any" : o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Region</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["any", "London", "North", "Midlands", "South", "Scotland", "Wales"].map(o => (
                  <button key={o} onClick={() => setRegionFilter(o)} style={{ padding: "5px 11px", minHeight: 32, borderRadius: 20, border: regionFilter === o ? "none" : "0.5px solid #e0e0e0", background: regionFilter === o ? "var(--accent,#2E5FA3)" : "#fff", color: regionFilter === o ? "#fff" : "#555", fontSize: 12, fontWeight: regionFilter === o ? 600 : 400, cursor: "pointer" }}>
                    {o === "any" ? "Any" : o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Expected return rate</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { v: "any",      l: "Any" },
                  { v: "under-8",  l: "Under 8%" },
                  { v: "8-12",     l: "8–12%" },
                  { v: "12-plus",  l: "12%+" },
                ].map(o => (
                  <button key={o.v} onClick={() => setReturnRateFilter(o.v)} style={{ padding: "5px 11px", minHeight: 32, borderRadius: 20, border: returnRateFilter === o.v ? "none" : "0.5px solid #e0e0e0", background: returnRateFilter === o.v ? "var(--accent,#2E5FA3)" : "#fff", color: returnRateFilter === o.v ? "#fff" : "#555", fontSize: 12, fontWeight: returnRateFilter === o.v ? 600 : 400, cursor: "pointer" }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", borderTop: "0.5px solid #f0f0f0", paddingTop: 12, marginBottom: 4 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={verifiedOnly} onChange={e => setVerifiedOnly(e.target.checked)} /> Verified profiles only
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} /> Active in last 30 days
            </label>
          </div>
          {/* Save preset */}
          {user && (
            <div style={{ borderTop: "0.5px solid #f0f0f0", paddingTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "#64748B" }}>Save these filters:</span>
              <input value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="Filter preset name" style={{ flex: 1, minWidth: 140, height: 36, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 10px", fontSize: 13 }} />
              <button onClick={handleSavePreset} disabled={!presetName.trim()} style={{ background: "#2E5FA3", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: presetName.trim() ? "pointer" : "default", opacity: presetName.trim() ? 1 : 0.5 }}>Save preset</button>
            </div>
          )}
        </div>
      )}

      {!user && (
        <div style={{
          background: "#EBF2FF", border: "0.5px solid #E0C87A", borderRadius: 10,
          padding: "12px 16px", fontSize: 13, color: "#0F6E56", marginBottom: "1.25rem",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <span>Log in or sign up to connect with lenders and send messages.</span>
          <button
            onClick={() => setPage("auth")}
            style={{ background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "6px 14px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}
          >
            Log in
          </button>
        </div>
      )}

      {/* Results count + Save Search */}
      {!loadingReal && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 13, color: "#64748B" }}>
            {allCards.length === 0 ? (activeFiltersCountFull > 0 ? "No lenders match your filters — try adjusting them" : "No lenders yet") : `Showing ${allCards.length} lender${allCards.length !== 1 ? "s" : ""} matching your criteria`}
          </div>
          {user && allCards.length > 0 && (
            <button onClick={() => setSaveSearchModal(true)} style={{ padding: "6px 12px", background: "#EBF2FF", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#1E3A5F", cursor: "pointer" }}>
              Save this search
            </button>
          )}
          {saveSearchMsg && <span style={{ fontSize: 12, color: "#16A34A" }}>{saveSearchMsg}</span>}
        </div>
      )}
      {loadingReal && [0,1,2,3].map(i => <SkeletonProfileCard key={i} />)}

      {!loadingReal && allCards.length === 0 && realCards.length === 0 ? (
        <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 16, color: "var(--accent)" }}><Icon name="briefcase" size={32} /></div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1E3A5F", marginBottom: 8 }}>No lenders in your area yet</div>
          <div style={{ fontSize: 14 }}>Be the first lender in your area — set up your profile</div>
          {user && <button onClick={() => setPage("profile-setup")} style={{ marginTop: 20, background: "#2E5FA3", color: "#fff", border: "none", padding: "10px 22px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Set up your lender profile</button>}
        </div>
      ) : !loadingReal && allCards.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 12, color: "var(--text-muted,#64748B)" }}><Icon name="search" size={24} /></div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No lenders match these filters</div>
          <div style={{ fontSize: 13 }}>Try adjusting your filters above.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
          {allCards.map(lc => (
            <LenderCard key={lc.id || lc.name} lc={lc} user={user} setPage={setPage} settings={lenderSettings[lc.name]} onViewProfile={onViewProfile} viewerProfile={viewerRoleProfile} />
          ))}
        </div>
      )}

      {/* Save Search Modal */}
      {saveSearchModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "1.5rem", maxWidth: 380, width: "100%" }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Save this search</div>
            <form onSubmit={handleSaveSearch} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input value={saveSearchName} onChange={e => setSaveSearchName(e.target.value)} placeholder="Search name e.g. London lenders" required
                style={{ height: 44, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 14, boxSizing: "border-box" }} />
              <select value={saveSearchFreq} onChange={e => setSaveSearchFreq(e.target.value)}
                style={{ height: 44, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 14, boxSizing: "border-box" }}>
                <option value="instant">Instant alerts</option>
                <option value="daily">Daily digest</option>
                <option value="weekly">Weekly digest</option>
              </select>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => setSaveSearchModal(false)} style={{ flex: 1, height: 44, background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Cancel</button>
                <button type="submit" style={{ flex: 2, height: 44, background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BUILDER CARD ────────────────────────────────────────────────────────────

function BuilderCard({ builder, user, setPage, onMessage, onViewProfile, viewerProfile }) {
  const [msgStatus, setMsgStatus] = useState("idle");
  const [hovered,   setHovered]   = useState(false);

  async function handleMessage() {
    if (!user) { setPage("auth"); return; }
    setMsgStatus("loading");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/notify-message", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ other_name: builder.name, other_role: "builder" }),
    });
    if (res.ok) {
      const { conversation } = await res.json();
      setMsgStatus("idle");
      onMessage(conversation.id);
    } else {
      setMsgStatus("error");
      setTimeout(() => setMsgStatus("idle"), 3000);
    }
  }

  const builderMatchScore = (() => {
    if (!viewerProfile || user?.user_metadata?.role !== "lender") return null;
    const m = computeMatch(viewerProfile, builder, "lender");
    return m?.score || null;
  })();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "var(--bg-card, #fff)",
        border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
        borderRadius: 12, padding: "24px",
        boxShadow: hovered ? "0 8px 24px rgba(0,0,0,0.12)" : "0 2px 8px rgba(0,0,0,0.08)",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        display: "flex", flexDirection: "column",
      }}>
      {(() => {
        if (!viewerProfile || user?.user_metadata?.role !== "lender") return null;
        const m = computeMatch(viewerProfile, builder, "lender");
        if (!m) return null;
        return <div style={{ marginBottom: 10 }}><MatchBadge score={m.score} breakdown={m.components} /></div>;
      })()}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
        <CredibilityRing score={computeCredibilityScore(builder)} size={40}>
          <Avatar initials={builder.initials} color={builder.color} size={40} url={builder.avatar_url} />
        </CredibilityRing>
        <div style={{ flex: 1, minWidth: 0, paddingLeft: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <div style={{ fontSize: 15, fontWeight: 500 }}>{builder.name}</div>
            <RoleBadge userRole={builder.user_role} authRole="builder" />
            {builder.verified_documents?.length > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                <span style={{ fontSize: 10, fontWeight: 500, padding: "1px 7px", borderRadius: 20, background: "#EBF2FF", color: "#1E3A5F", flexShrink: 0 }}>
                  ✓ Verified
                </span>
                <HelpTooltip text="This user has submitted identity documents that have been reviewed and approved by LenderBuild." />
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#64748B" }}>{builder.type}</div>
        </div>
        <HeartButton userId={user?.id} targetUserId={builder.user_id} profileSnap={{ user_id: builder.user_id, role: "builder", name: builder.name, type: builder.type, avatar_url: builder.avatar_url, location: builder.location }} />
      </div>
      {builder.location && (
        <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}><Icon name="map-pin" size={12} style={{marginRight:3,flexShrink:0}} />{builder.location}</div>
      )}
      <div style={{ fontSize: 13 }}>
        {[
          ["Projects completed", builder.props],
          ["Total value built",  fmt(builder.value)],
        ].map(([label, val]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", color: "#666" }}>
            <span>{label}</span>
            <span style={{ fontWeight: 500, color: "#222" }}>{val}</span>
          </div>
        ))}
      </div>
      {/* Completion rate bar */}
      {builder.props > 0 && (
        <div style={{ marginTop: 10, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#666", marginBottom: 4 }}>
            <span>Completion rate</span>
            <span style={{ fontWeight: 500, color: "var(--accent)" }}>{builder.completion}%</span>
          </div>
          <div style={{ height: 5, background: "#eee", borderRadius: 3 }}>
            <div style={{ height: 5, borderRadius: 3, background: "var(--accent)", width: `${builder.completion}%`, transition: "width 0.3s" }} />
          </div>
        </div>
      )}
      {(() => {
        const cp = computeProfileCompleteness({
          avatar_url: builder.avatar_url, bio: builder.bio, location: builder.location,
          specialization: builder.type, projects_completed: builder.props,
          verified_documents: builder.verified_documents,
        }, "builder");
        if (cp.score >= 50) return null;
        return (
          <div style={{ marginBottom: 8, fontSize: 10, color: "#DC2626", fontWeight: 500 }}>
            Profile incomplete
          </div>
        );
      })()}
      <div style={{ marginTop: "auto", borderTop: "0.5px solid #eee", paddingTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {user && (
          <button
            onClick={handleMessage}
            disabled={msgStatus === "loading"}
            style={{
              flex: 1, background: "var(--accent)", color: "#fff", border: "none",
              padding: 8, borderRadius: 8, fontSize: 13, fontWeight: 600, minHeight: 44,
              cursor: msgStatus === "loading" ? "default" : "pointer",
              transition: "background 0.2s",
            }}
          >
            {msgStatus === "loading" ? "Opening…" : msgStatus === "error" ? "Couldn't open chat — retry" : "Message"}
          </button>
        )}
        <button
          onClick={() => onViewProfile && onViewProfile(builder)}
          style={{ flex: 1, background: "transparent", color: "var(--accent)", border: "1.5px solid var(--accent)", padding: 8, borderRadius: 8, fontSize: 13, cursor: "pointer", minHeight: 44 }}
        >
          View profile
        </button>
      </div>
    </div>
  );
}

// ─── FIND A BUILDER PAGE ──────────────────────────────────────────────────────

function FindBuilderPage({ user, setPage, onMessage, onViewProfile, viewerRoleProfile }) {
  const [dismissReturnsNudge, setDismissReturnsNudge] = useState(() => { try { return !!localStorage.getItem("lb_dismiss_returns_nudge"); } catch { return false; } });
  const [specFilter,        setSpecFilter]       = useState("any");
  const [locationFilter,    setLocationFilter]   = useState("any");
  const [completionFilter,  setCompletionFilter] = useState("any");
  const [verifiedOnlyB,     setVerifiedOnlyB]    = useState(false);
  const [minProjectsFilter, setMinProjectsFilter] = useState("any");
  const [sortByB,           setSortByB]          = useState("newest");
  const [matchFilterB,      setMatchFilterB]      = useState("all");
  const [fundingFilterB,    setFundingFilterB]   = useState("any");
  const [projectTypeFilterB,setProjectTypeFilterB] = useState("any");
  const [regionFilterB,     setRegionFilterB]    = useState("any");
  const [trackRecordFilter, setTrackRecordFilter] = useState("any");
  const [realBuilders,      setRealBuilders]      = useState([]);
  const [loadingReal,       setLoadingReal]       = useState(true);
  const [filterOpen,        setFilterOpen]        = useState(false);
  const [presetNameB,       setPresetNameB]       = useState("");
  const [presetsB,          setPresetsB]          = useState(() => user?.id ? getFilterPresets(user.id, "builder") : []);
  const [width,             setWidth]             = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = width < 640;

  function applyPresetB(p) {
    const f = p.filters;
    if (f.specFilter)       setSpecFilter(f.specFilter);
    if (f.locationFilter)   setLocationFilter(f.locationFilter);
    if (f.completionFilter) setCompletionFilter(f.completionFilter);
    if (f.verifiedOnlyB !== undefined) setVerifiedOnlyB(f.verifiedOnlyB);
    if (f.minProjectsFilter) setMinProjectsFilter(f.minProjectsFilter);
    if (f.sortByB)          setSortByB(f.sortByB);
  }

  function handleSavePresetB() {
    if (!user?.id || !presetNameB.trim()) return;
    const filters = { specFilter, locationFilter, completionFilter, verifiedOnlyB, minProjectsFilter, sortByB };
    saveFilterPreset(user.id, "builder", presetNameB.trim(), filters);
    setPresetsB(getFilterPresets(user.id, "builder"));
    setPresetNameB("");
  }

  function handleDeletePresetB(name) {
    deleteFilterPreset(user.id, "builder", name);
    setPresetsB(getFilterPresets(user.id, "builder"));
  }

  useEffect(() => {
    fetch("/api/get-profiles?type=builders")
      .then(r => r.json())
      .then(d => { setRealBuilders(d.builders || []); setLoadingReal(false); })
      .catch(() => setLoadingReal(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Transform real DB builders into the same shape as BUILDERS
  const realCards = realBuilders.map(b => ({
    id:                 `real-${b.user_id}`,
    _real:              true,
    initials:           nameInitials(b.profiles?.full_name),
    color:              pickColor(b.user_id || ""),
    name:               b.profiles?.full_name || "Anonymous",
    type:               b.specialization || "Builder",
    location:           b.profiles?.location || "",
    bio:                b.profiles?.bio || "",
    props:              b.projects_completed || 0,
    value:              b.total_value || 0,
    completion:         b.completion_rate ?? 100,
    verified_documents: b.verified_documents || [],
    avatar_url:         b.avatar_url || null,
    user_id:            b.user_id,
    user_role:          b.profiles?.user_role || null,
    sequential_id:      b.profiles?.sequential_id || null,
    is_founding_member: b.profiles?.is_founding_member || false,
    updated_at:         b.updated_at || null,
  }));

  // Collect filter options from real builders only
  const locations = [...new Set(realCards.map(b => b.location).filter(Boolean))].sort();
  const specs     = [...new Set(realCards.map(b => b.type).filter(Boolean))].sort();

  function matchesFilters(b) {
    if (specFilter       !== "any" && b.type     !== specFilter)     return false;
    if (locationFilter   !== "any" && b.location !== locationFilter) return false;
    if (completionFilter === "80+"  && b.completion < 80)            return false;
    if (completionFilter === "95+"  && b.completion < 95)            return false;
    if (completionFilter === "100"  && b.completion !== 100)         return false;
    if (verifiedOnlyB && !b.verified_documents?.length)              return false;
    if (minProjectsFilter === "1-5"  && !(b.props >= 1  && b.props <= 5))  return false;
    if (minProjectsFilter === "5-10" && !(b.props >= 5  && b.props <= 10)) return false;
    if (minProjectsFilter === "10+"  && b.props < 10)                return false;
    if (fundingFilterB !== "any") {
      const v = Number(b.value) || 0;
      if (fundingFilterB === "under-50k"  && !(v > 0 && v < 50000))        return false;
      if (fundingFilterB === "50k-150k"   && !(v >= 50000 && v <= 150000)) return false;
      if (fundingFilterB === "150k-500k"  && !(v > 150000 && v <= 500000)) return false;
      if (fundingFilterB === "500k-plus"  && !(v > 500000))                return false;
    }
    if (projectTypeFilterB !== "any") {
      const spec = (b.type || "").toLowerCase();
      const typeMap = { "New build": ["new build","newbuild","residential"], "Renovation": ["renov","refurb"], "Commercial": ["commercial"], "Mixed use": ["mixed"] };
      const keys = typeMap[projectTypeFilterB] || [];
      if (keys.length > 0 && !keys.some(k => spec.includes(k))) return false;
    }
    if (regionFilterB !== "any" && !inRegionBucket(b.location, regionFilterB)) return false;
    if (trackRecordFilter === "1-plus" && (b.props || 0) < 1) return false;
    if (trackRecordFilter === "3-plus" && (b.props || 0) < 3) return false;
    if (matchFilterB !== "all" && viewerRoleProfile) {
      const m = computeMatch(viewerRoleProfile, b, "lender");
      const s = m?.score || 0;
      if (matchFilterB === "great" && s < 80) return false;
      if (matchFilterB === "good"  && s < 60) return false;
    }
    return true;
  }

  function sortBuilders(list) {
    if (sortByB === "projects")      return [...list].sort((a, b) => (b.props || 0) - (a.props || 0));
    if (sortByB === "completion")    return [...list].sort((a, b) => (b.completion || 0) - (a.completion || 0));
    if (sortByB === "compatibility" && viewerRoleProfile) {
      return [...list].sort((a, b) => {
        const sa = computeMatch(viewerRoleProfile, a, "lender")?.score || 0;
        const sb = computeMatch(viewerRoleProfile, b, "lender")?.score || 0;
        return sb - sa;
      });
    }
    return [...list].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  }

  const filteredReal = sortBuilders(realCards.filter(matchesFilters));
  const allFiltered  = filteredReal;
  const activeBuilderFiltersCount = [specFilter !== "any", locationFilter !== "any", completionFilter !== "any", verifiedOnlyB, minProjectsFilter !== "any", matchFilterB !== "all", fundingFilterB !== "any", projectTypeFilterB !== "any", regionFilterB !== "any", trackRecordFilter !== "any"].filter(Boolean).length;

  return (
    <div style={{ padding: "1.5rem 1.25rem" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#2E5FA3", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Find a builder</div>
      <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px, 4vw, 32px)", fontWeight: 700, marginBottom: 4, color: "#1E3A5F" }}>Browse builder profiles</h2>
      <p style={{ fontSize: 14, color: "#64748B", margin: "0 0 1rem" }}>Find verified builders looking for funding — filter by specialisation, location, and track record.</p>

      {/* Returns Calculator nudge */}
      {dismissReturnsNudge ? (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 14px", background: "#F8FAFC", border: "0.5px solid #E2E8F0", borderRadius: 8, marginBottom: "1rem" }}>
        <span style={{ fontSize: 12, color: "#64748B", display:"inline-flex", alignItems:"center", gap:4 }}><Icon name="calculator" size={12} /> Returns Calculator</span>
        <button onClick={() => { setDismissReturnsNudge(false); try { localStorage.removeItem("lb_dismiss_returns_nudge"); } catch {} }} style={{ fontSize: 12, color: "#2E5FA3", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, padding: "2px 6px", fontWeight: 500 }}>
          Show <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
        </button>
      </div>
      ) : (
      <div style={{ background: "#EBF2FF", border: "0.5px solid #C3D9FF", borderRadius: 12, padding: "10px 14px", marginBottom: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
          <button onClick={() => { setDismissReturnsNudge(true); try { localStorage.setItem("lb_dismiss_returns_nudge", "1"); } catch {} }} style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 8px", background: "rgba(255,255,255,0.6)", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, color: "#64748B", fontWeight: 500 }}>
            Hide <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#1E3A5F" }}>Calculate your potential returns before you invest</span>
          <button onClick={() => setPage("build-calculator")} style={{ padding: "7px 16px", background: "var(--accent,#3B82F6)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>Returns Calculator</button>
        </div>
      </div>
      )}

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <button
          onClick={() => setFilterOpen(o => !o)}
          style={{ display: "flex", alignItems: "center", gap: 8, background: filterOpen ? "#1E3A5F" : "#fff", border: `0.5px solid ${filterOpen ? "#1E3A5F" : "#ccc"}`, borderRadius: 8, padding: "9px 16px", minHeight: 44, fontSize: 14, cursor: "pointer", fontWeight: 500, color: filterOpen ? "#fff" : "#1E3A5F" }}
        >
          <svg width="16" height="12" viewBox="0 0 16 12" fill="none"><path d="M0 1h16M3 6h10M6 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          Filters
          {activeBuilderFiltersCount > 0 && <span style={{ background: filterOpen ? "rgba(255,255,255,0.3)" : "#2E5FA3", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{activeBuilderFiltersCount}</span>}
        </button>
        <select value={sortByB} onChange={e => setSortByB(e.target.value)} style={{ height: 44, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 13, background: "#fff", cursor: "pointer" }}>
          <option value="newest">Sort: Newest first</option>
          <option value="projects">Sort: Most projects</option>
          <option value="completion">Sort: Highest completion</option>
          <option value="compatibility">Sort: Best match</option>
        </select>
        {user && viewerRoleProfile && (
          <select value={matchFilterB} onChange={e => setMatchFilterB(e.target.value)} style={{ height: 44, border: matchFilterB !== "all" ? "1.5px solid #3B82F6" : "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 13, background: matchFilterB !== "all" ? "#EBF2FF" : "#fff", color: matchFilterB !== "all" ? "#1E3A5F" : undefined, cursor: "pointer", fontWeight: matchFilterB !== "all" ? 600 : 400 }}>
            <option value="all">All matches</option>
            <option value="great">Great matches only (80%+)</option>
            <option value="good">Good matches &amp; above (60%+)</option>
          </select>
        )}
        {activeBuilderFiltersCount > 0 && (
          <button onClick={() => { setSpecFilter("any"); setLocationFilter("any"); setCompletionFilter("any"); setVerifiedOnlyB(false); setMinProjectsFilter("any"); setMatchFilterB("all"); setFundingFilterB("any"); setProjectTypeFilterB("any"); setRegionFilterB("any"); setTrackRecordFilter("any"); }}
            style={{ background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: "pointer", fontWeight: 500 }}>
            Clear all
          </button>
        )}
        {presetsB.length > 0 && presetsB.map(p => (
          <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button onClick={() => applyPresetB(p)} style={{ background: "#EBF2FF", color: "#1E3A5F", border: "none", borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>⭐ {p.name}</button>
            <button onClick={() => handleDeletePresetB(p.name)} style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", padding: 2, fontSize: 12 }}>×</button>
          </div>
        ))}
      </div>

      {/* Advanced filter panel */}
      {filterOpen && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "20px 24px", marginBottom: "1.25rem", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Funding amount requested</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { v: "any",       l: "Any" },
                  { v: "under-50k", l: "Under £50k" },
                  { v: "50k-150k",  l: "£50k–£150k" },
                  { v: "150k-500k", l: "£150k–£500k" },
                  { v: "500k-plus", l: "£500k+" },
                ].map(o => (
                  <button key={o.v} onClick={() => setFundingFilterB(o.v)} style={{ padding: "5px 10px", minHeight: 30, borderRadius: 20, border: fundingFilterB === o.v ? "none" : "0.5px solid #e0e0e0", background: fundingFilterB === o.v ? "var(--accent,#2E5FA3)" : "#fff", color: fundingFilterB === o.v ? "#fff" : "#555", fontSize: 12, fontWeight: fundingFilterB === o.v ? 600 : 400, cursor: "pointer" }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Project type</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["any", "New build", "Renovation", "Commercial", "Mixed use"].map(o => (
                  <button key={o} onClick={() => setProjectTypeFilterB(o)} style={{ padding: "5px 10px", minHeight: 30, borderRadius: 20, border: projectTypeFilterB === o ? "none" : "0.5px solid #e0e0e0", background: projectTypeFilterB === o ? "var(--accent,#2E5FA3)" : "#fff", color: projectTypeFilterB === o ? "#fff" : "#555", fontSize: 12, fontWeight: projectTypeFilterB === o ? 600 : 400, cursor: "pointer" }}>
                    {o === "any" ? "Any" : o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Project location / region</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["any", "London", "North", "Midlands", "South", "Scotland", "Wales"].map(o => (
                  <button key={o} onClick={() => setRegionFilterB(o)} style={{ padding: "5px 10px", minHeight: 30, borderRadius: 20, border: regionFilterB === o ? "none" : "0.5px solid #e0e0e0", background: regionFilterB === o ? "var(--accent,#2E5FA3)" : "#fff", color: regionFilterB === o ? "#fff" : "#555", fontSize: 12, fontWeight: regionFilterB === o ? 600 : 400, cursor: "pointer" }}>
                    {o === "any" ? "Any" : o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Builder track record</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { v: "any",    l: "Any" },
                  { v: "1-plus", l: "1+ completed" },
                  { v: "3-plus", l: "3+ completed" },
                ].map(o => (
                  <button key={o.v} onClick={() => setTrackRecordFilter(o.v)} style={{ padding: "5px 10px", minHeight: 30, borderRadius: 20, border: trackRecordFilter === o.v ? "none" : "0.5px solid #e0e0e0", background: trackRecordFilter === o.v ? "var(--accent,#2E5FA3)" : "#fff", color: trackRecordFilter === o.v ? "#fff" : "#555", fontSize: 12, fontWeight: trackRecordFilter === o.v ? 600 : 400, cursor: "pointer" }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", borderTop: "0.5px solid #f0f0f0", paddingTop: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={verifiedOnlyB} onChange={e => setVerifiedOnlyB(e.target.checked)} /> Verified builders only
            </label>
          </div>
          {user && (
            <div style={{ borderTop: "0.5px solid #f0f0f0", paddingTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "#64748B" }}>Save these filters:</span>
              <input value={presetNameB} onChange={e => setPresetNameB(e.target.value)} placeholder="Preset name" style={{ flex: 1, minWidth: 120, height: 36, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 10px", fontSize: 13 }} />
              <button onClick={handleSavePresetB} disabled={!presetNameB.trim()} style={{ background: "#16A34A", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: presetNameB.trim() ? "pointer" : "default", opacity: presetNameB.trim() ? 1 : 0.5 }}>Save preset</button>
            </div>
          )}
        </div>
      )}

      {!user && (
        <div style={{
          background: "#EBF2FF", border: "0.5px solid #E0C87A", borderRadius: 10,
          padding: "12px 16px", fontSize: 13, color: "#0F6E56", marginBottom: "1.25rem",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <span>Log in or sign up to connect with builders and send messages.</span>
          <button
            onClick={() => setPage("auth")}
            style={{ background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "6px 14px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}
          >
            Log in
          </button>
        </div>
      )}

      {!loadingReal && (
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: "1rem" }}>
          {allFiltered.length === 0 ? (activeBuilderFiltersCount > 0 ? "No builders match your filters — try adjusting them" : "No builders yet") : `Showing ${allFiltered.length} builder${allFiltered.length !== 1 ? "s" : ""} matching your criteria`}
        </div>
      )}
      {loadingReal && [0,1,2,3].map(i => <SkeletonProfileCard key={i} />)}

      {!loadingReal && realCards.length === 0 ? (
        <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 16, color: "var(--accent)" }}><Icon name="hammer" size={32} /></div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1E3A5F", marginBottom: 8 }}>No builders here yet</div>
          <div style={{ fontSize: 14 }}>Post your project and get matched today</div>
          {user && <button onClick={() => setPage("profile-setup")} style={{ marginTop: 20, background: "#3B82F6", color: "#fff", border: "none", padding: "10px 22px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Set up your builder profile</button>}
        </div>
      ) : !loadingReal && allFiltered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 12, color: "var(--text-muted,#64748B)" }}><Icon name="search" size={24} /></div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No builders match these filters</div>
          <div style={{ fontSize: 13 }}>Try adjusting your filters above.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
          {allFiltered.map(b => (
            <BuilderCard key={b.id} builder={b} user={user} setPage={setPage} onMessage={onMessage} onViewProfile={onViewProfile} viewerProfile={viewerRoleProfile} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LEADERBOARD PAGE ────────────────────────────────────────────────────────

function LeaderboardPage({ onViewProfile, onViewBuilderProfile }) {
  const [tab, setTab] = useState("lenders");
  const [builderSort, setBuilderSort] = useState("properties");
  const [lenders, setLenders] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [{ data: ls }, { data: bs }] = await Promise.all([
          supabase.from("lender_profiles").select("user_id, budget_max, return_type, profiles(full_name, location)").eq("is_active", true),
          supabase.from("builder_profiles").select("user_id, projects_completed, total_value, completion_rate, specialization, profiles(full_name, location)").eq("is_active", true),
        ]);
        setLenders((ls || []).map(l => ({
          id:       l.user_id,
          initials: nameInitials(l.profiles?.full_name),
          color:    pickColor(l.user_id),
          name:     l.profiles?.full_name || "Anonymous",
          location: l.profiles?.location || "",
          type:     "Private lender",
          budget:   l.budget_max || 0,
          returnType: l.return_type || "—",
        })));
        setBuilders((bs || []).map(b => ({
          id:         b.user_id,
          initials:   nameInitials(b.profiles?.full_name),
          color:      pickColor(b.user_id),
          name:       b.profiles?.full_name || "Anonymous",
          location:   b.profiles?.location || "",
          type:       b.specialization || "Builder",
          props:      b.projects_completed || 0,
          value:      b.total_value || 0,
          completion: b.completion_rate ?? 100,
        })));
      } catch (_) {}
      setLoading(false);
    }
    load();
  }, []);

  const sortedLenders = [...lenders].sort((a, b) => b.budget - a.budget);

  const sortedBuilders = [...builders].sort((a, b) => {
    if (builderSort === "properties") return b.props - a.props;
    if (builderSort === "value")      return b.value - a.value;
    return b.completion - a.completion;
  });

  const maxProps = Math.max(1, ...builders.map(b => b.props));

  const tabs = ["lenders", "builders"];

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 900, margin: "0 auto" }}>
      {/* Tab row */}
      <div style={{ display: "flex", gap: 0, borderBottom: "0.5px solid #e0e0e0", marginBottom: "1.75rem" }}>
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "10px 20px", fontSize: 14, fontWeight: 500, cursor: "pointer",
              border: "none", background: "transparent", minHeight: 44,
              color: tab === t ? "#3B82F6" : "#64748B",
              borderBottom: tab === t ? "2px solid #1D9E75" : "2px solid transparent",
              marginBottom: -1, textTransform: "capitalize",
            }}
          >
            {t === "lenders" ? "Top lenders" : "Top builders"}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: "center", padding: "3rem", color: "#aaa" }}>Loading…</div>}

      {/* LENDERS tab */}
      {!loading && tab === "lenders" && (
        <div>
          <div style={{ marginBottom: "1.25rem" }}>
            <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 3 }}>Active lenders</h2>
            <p style={{ fontSize: 13, color: "#64748B" }}>Lenders with active profiles on the platform</p>
          </div>
          {sortedLenders.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
              <div style={{ marginBottom: 16, color: "var(--accent)" }}><Icon name="trophy" size={32} /></div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#1E3A5F", marginBottom: 8 }}>Leaderboard will populate as deals complete</div>
              <div style={{ fontSize: 14 }}>Be the first lender to complete a deal and claim the top spot.</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
              <thead>
                <tr>
                  {["#", "Lender", "Budget", "Return type", "Location"].map((h, i) => (
                    <th key={h} style={{ fontSize: 12, fontWeight: 500, color: "#64748B", textAlign: i >= 2 ? "right" : "left", padding: "0 12px 10px", borderBottom: "0.5px solid #eee" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedLenders.map((l, i) => {
                  const rankColor = i === 0 ? "#2E5FA3" : i === 1 ? "#64748B" : i === 2 ? "#993C1D" : "#aaa";
                  return (
                    <tr key={l.id} style={{ borderBottom: "0.5px solid #f0f0f0" }}>
                      <td style={{ padding: "13px 12px", fontSize: 14, fontWeight: 500, color: rankColor, width: 36 }}>{i + 1}</td>
                      <td style={{ padding: "13px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div onClick={() => onViewProfile && onViewProfile({ id: `real-${l.id}`, _real: true, user_id: l.id, name: l.name, initials: l.initials, color: l.color, type: l.type, location: l.location, bio: "", budget: l.budget ? fmt(l.budget) : "Flexible", returnType: l.returnType, preferred_projects: [], project: "Any", verified: false })} style={{ cursor: onViewProfile ? "pointer" : "default" }}>
                            <Avatar initials={l.initials} color={l.color} size={34} />
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{l.name}</div>
                        </div>
                      </td>
                      <td style={{ padding: "13px 12px", textAlign: "right", fontSize: 14, fontWeight: 500 }}>{l.budget ? fmt(l.budget) : "—"}</td>
                      <td style={{ padding: "13px 12px", textAlign: "right", fontSize: 14 }}>{l.returnType}</td>
                      <td style={{ padding: "13px 12px", textAlign: "right", fontSize: 13, color: "#64748B" }}>{l.location || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* BUILDERS tab */}
      {!loading && tab === "builders" && (
        <div>
          <div style={{ marginBottom: "1.25rem" }}>
            <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 3 }}>Active builders</h2>
            <p style={{ fontSize: 13, color: "#64748B" }}>Builders ranked by projects completed</p>
          </div>
          {sortedBuilders.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
              <div style={{ marginBottom: 16, color: "var(--accent)" }}><Icon name="trophy" size={32} /></div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#1E3A5F", marginBottom: 8 }}>Leaderboard will populate as deals complete</div>
              <div style={{ fontSize: 14 }}>Be the first builder to complete a project and claim the top spot.</div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: "1.25rem", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#64748B" }}>Sort by</span>
                <select value={builderSort} onChange={e => setBuilderSort(e.target.value)} style={{ height: 44, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 10px", fontSize: 13, background: "#fff" }}>
                  <option value="properties">Projects completed</option>
                  <option value="value">Total value</option>
                  <option value="rate">Completion rate</option>
                </select>
              </div>
              <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
                <thead>
                  <tr>
                    {["#", "Builder", "Projects", "Value", "Completion", "Activity"].map((h, i) => (
                      <th key={h} style={{ fontSize: 12, fontWeight: 500, color: "#64748B", textAlign: i >= 2 && i <= 4 ? "right" : "left", padding: "0 12px 10px", borderBottom: "0.5px solid #eee" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedBuilders.map((b, i) => {
                    const pct = Math.round((b.props / maxProps) * 100);
                    const rankColor = i === 0 ? "#2E5FA3" : i === 1 ? "#64748B" : i === 2 ? "#993C1D" : "#aaa";
                    return (
                      <tr key={b.id} style={{ borderBottom: "0.5px solid #f0f0f0" }}>
                        <td style={{ padding: "13px 12px", fontSize: 14, fontWeight: 500, color: rankColor, width: 36 }}>{i + 1}</td>
                        <td style={{ padding: "13px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Avatar initials={b.initials} color={b.color} size={34} />
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 500 }}>{b.name}</div>
                              <div style={{ fontSize: 12, color: "#64748B" }}>{b.type}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "13px 12px", textAlign: "right", fontSize: 14, fontWeight: 500 }}>{b.props}</td>
                        <td style={{ padding: "13px 12px", textAlign: "right", fontSize: 14, fontWeight: 500 }}>{fmt(b.value)}</td>
                        <td style={{ padding: "13px 12px", textAlign: "right", fontSize: 14, fontWeight: 500 }}>{b.completion}%</td>
                        <td style={{ padding: "13px 12px", minWidth: 120 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, height: 6, background: "#eee", borderRadius: 3 }}>
                              <div style={{ height: 6, borderRadius: 3, background: "#2E5FA3", width: `${pct}%` }} />
                            </div>
                            <span style={{ fontSize: 12, color: "#64748B", minWidth: 20 }}>{b.props}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── LENDER DASHBOARD ────────────────────────────────────────────────────────

function LenderDashboard({ user, setPage }) {
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [responding, setResponding] = useState({});
  const [blocking,   setBlocking]   = useState({});
  const [reporting,  setReporting]  = useState({});
  const [reportReasons, setReportReasons] = useState({});
  const [syndicateInvestments, setSyndicateInvestments] = useState([]);
  const [syndicateListings,    setSyndicateListings]    = useState([]);
  const [fundingRooms, setFundingRooms] = useState([]);

  const [accepting,     setAccepting]     = useState(user?.user_metadata?.accepting_requests !== false);
  const [listingActive, setListingActive] = useState(user?.user_metadata?.listing_active     !== false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchSyndicateInvestments();
    fetchFundingRooms();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchFundingRooms() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-list" }),
    });
    if (res.ok) {
      const d = await res.json();
      setFundingRooms(d.rooms || []);
    }
  }

  async function fetchSyndicateInvestments() {
    const { data: cmts } = await supabase
      .from("syndicate_commitments")
      .select("*")
      .eq("lender_user_id", user.id)
      .order("committed_at", { ascending: false });
    setSyndicateInvestments(cmts || []);

    if ((cmts || []).length > 0) {
      const listingIds = [...new Set(cmts.map(c => c.listing_id))];
      const res = await fetch("/api/project-listings").then(r => r.json()).catch(() => ({}));
      const allListings = res.listings || [];
      const synTotals = res.syndicate_totals || {};
      const matched = allListings
        .filter(l => listingIds.includes(l.id))
        .map(l => ({ ...l, synTotal: synTotals[l.id]?.total || 0, synCount: synTotals[l.id]?.count || 0 }));
      setSyndicateListings(matched);
    }
  }

  async function getToken() {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  }

  async function fetchRequests() {
    setLoading(true);
    setError("");
    const token = await getToken();
    if (!token) { setPage("auth"); return; }

    const res  = await fetch("/api/lender-requests", { headers: { Authorization: `Bearer ${token}` } });
    const json = await res.json();
    if (!res.ok) { setError(json.error || "Failed to load requests"); setLoading(false); return; }
    setRequests(json.requests);
    setLoading(false);
  }

  async function handleRespond(builderId, action) {
    setResponding(r => ({ ...r, [builderId]: "loading" }));
    const token = await getToken();
    const res = await fetch("/api/respond-request", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ builder_id: builderId, action }),
    });
    if (!res.ok) {
      setResponding(r => ({ ...r, [builderId]: "error" }));
      setTimeout(() => setResponding(r => ({ ...r, [builderId]: undefined })), 3000);
    } else {
      setResponding(r => ({ ...r, [builderId]: undefined }));
      setRequests(prev => prev.map(req =>
        req.builder_id === builderId ? { ...req, status: action, responded_at: new Date().toISOString() } : req
      ));
    }
  }

  async function handleBlock(builderId) {
    setBlocking(b => ({ ...b, [builderId]: "loading" }));
    const token = await getToken();
    const res = await fetch("/api/block-builder", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: "block", builder_id: builderId }),
    });
    if (!res.ok) {
      setBlocking(b => ({ ...b, [builderId]: "error" }));
      setTimeout(() => setBlocking(b => ({ ...b, [builderId]: undefined })), 3000);
    } else {
      setBlocking(b => ({ ...b, [builderId]: "done" }));
      setRequests(prev => prev.map(req =>
        req.builder_id === builderId
          ? { ...req, status: "declined", responded_at: new Date().toISOString(), blocked: true }
          : req
      ));
    }
  }

  async function handleReport(builderId, builderName) {
    const reason = reportReasons[builderId]?.trim();
    if (!reason) return;
    setReporting(r => ({ ...r, [builderId]: "loading" }));
    const token = await getToken();
    const res = await fetch("/api/block-builder", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: "report", builder_id: builderId, builder_name: builderName, reason }),
    });
    if (!res.ok) {
      setReporting(r => ({ ...r, [builderId]: "error" }));
    } else {
      setReporting(r => ({ ...r, [builderId]: "done" }));
    }
  }

  async function updateSettings(patch) {
    setSettingsLoading(true);
    const token = await getToken();
    await fetch("/api/lender-settings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(patch),
    });
    setSettingsLoading(false);
    await supabase.auth.refreshSession();
  }

  async function toggleAccepting() {
    const next = !accepting;
    setAccepting(next);
    await updateSettings({ accepting_requests: next });
  }

  async function toggleListing() {
    const next = !listingActive;
    setListingActive(next);
    await updateSettings({ listing_active: next });
  }

  const pending  = requests.filter(r => r.status === "pending");
  const actioned = requests.filter(r => r.status !== "pending");

  const statusBadge = (req) => {
    const map = {
      pending:  { bg: "#DBEAFE", text: "#1D4ED8", label: "Pending"  },
      accepted: { bg: "#E1F5EE", text: "#0F6E56", label: "Accepted" },
      declined: { bg: "#FAECE7", text: "#993C1D", label: req.blocked ? "Blocked" : "Declined" },
    };
    const s = map[req.status] || map.pending;
    return (
      <span style={{ background: s.bg, color: s.text, fontSize: 11, padding: "3px 9px", borderRadius: 20, fontWeight: 500 }}>
        {s.label}
      </span>
    );
  };

  function RequestCard({ req }) {
    const respondState = responding[req.builder_id];
    const blockState   = blocking[req.builder_id];
    const reportState  = reporting[req.builder_id];
    const isPending    = req.status === "pending";
    const isActioning  = respondState === "loading" || blockState === "loading";

    return (
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: 12 }}>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: "50%", background: "#EBF2FF", color: "#1E3A5F",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, flexShrink: 0,
            }}>
              {(req.builder_name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500 }}>{req.builder_name}</div>
              <div style={{ fontSize: 12, color: "#64748B" }}>Builder · {req.builder_email}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {statusBadge(req)}
            {/* Report button */}
            {reportState !== "done" && (
              <button
                onClick={() => setReporting(r => ({ ...r, [req.builder_id]: reportState === "open" ? "idle" : "open" }))}
                title="Report user"
                style={{
                  background: "transparent", border: "0.5px solid #ddd", color: "#64748B",
                  padding: "3px 8px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                }}
              >
                Report
              </button>
            )}
            {reportState === "done" && (
              <span style={{ fontSize: 11, color: "#64748B" }}>Reported</span>
            )}
          </div>
        </div>

        {/* Meta */}
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
          {[
            ["Lender type", req.lender_type],
            ["Requested",   new Date(req.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })],
            req.responded_at && ["Responded", new Date(req.responded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })],
          ].filter(Boolean).map(([label, val]) => (
            <div key={label} style={{ fontSize: 13 }}>
              <span style={{ color: "#64748B" }}>{label}: </span>
              <span style={{ fontWeight: 500 }}>{val}</span>
            </div>
          ))}
        </div>

        {/* Report form */}
        {reportState === "open" && (
          <div style={{ marginTop: 12, background: "#f9f9f7", borderRadius: 8, padding: "12px" }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Why are you reporting this user?</div>
            <textarea
              value={reportReasons[req.builder_id] || ""}
              onChange={e => setReportReasons(r => ({ ...r, [req.builder_id]: e.target.value }))}
              placeholder="Describe the issue (e.g. spam, suspicious behaviour)"
              rows={3}
              style={{ width: "100%", border: "0.5px solid #ccc", borderRadius: 8, padding: "8px 10px", fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
            />
            {reportState === "error" && (
              <div style={{ fontSize: 12, color: "#993C1D", marginTop: 4 }}>Failed to submit — please try again.</div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                onClick={() => handleReport(req.builder_id, req.builder_name)}
                disabled={!reportReasons[req.builder_id]?.trim() || reportState === "loading"}
                style={{
                  background: reportState === "loading" ? "#64748B" : "#D85A30", color: "#fff",
                  border: "none", padding: "7px 16px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500,
                  cursor: reportState === "loading" ? "default" : "pointer",
                }}
              >
                {reportState === "loading" ? "Submitting…" : "Submit report"}
              </button>
              <button
                onClick={() => setReporting(r => ({ ...r, [req.builder_id]: "idle" }))}
                style={{ background: "transparent", border: "0.5px solid #ccc", color: "#555", padding: "7px 14px", minHeight: 44, borderRadius: 8, fontSize: 13, cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {isPending && (
          <div style={{ marginTop: 14, borderTop: "0.5px solid #eee", paddingTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => handleBlock(req.builder_id)}
              disabled={blockState === "loading" || blockState === "done"}
              title="Block this builder from messaging you"
              style={{
                flex: 1, minWidth: 80, background: "transparent", color: "#5F5E5A",
                border: "0.5px solid #ddd", padding: "8px 0", borderRadius: 8, minHeight: 44,
                fontSize: 13, fontWeight: 500, cursor: blockState === "loading" ? "default" : "pointer",
              }}
            >
              {blockState === "loading" ? "Blocking…" : "Block user"}
            </button>
          </div>
        )}
        {!isPending && !req.blocked && (
          <div style={{ marginTop: 14, borderTop: "0.5px solid #eee", paddingTop: 12 }}>
            <button
              onClick={() => handleBlock(req.builder_id)}
              disabled={blockState === "loading" || blockState === "done"}
              style={{
                background: "transparent", color: "#5F5E5A", border: "0.5px solid #ddd",
                padding: "6px 14px", minHeight: 44, borderRadius: 8, fontSize: 12, cursor: blockState === "loading" ? "default" : "pointer",
              }}
            >
              {blockState === "done" ? "Blocked" : blockState === "loading" ? "Blocking…" : "Block user"}
            </button>
          </div>
        )}
        {(respondState === "error" || blockState === "error") && (
          <div style={{ fontSize: 12, color: "#993C1D", marginTop: 8 }}>Something went wrong — please try again.</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 700, margin: "0 auto" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Lender dashboard</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: "1.5rem" }}>
        <h2 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Connection requests</h2>
        <button onClick={fetchRequests} style={{ background: "transparent", border: "0.5px solid #ccc", color: "#555", padding: "6px 14px", minHeight: 44, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>
          Refresh
        </button>
      </div>

      {/* ── Profile settings panel ─────────────────────────── */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "1rem" }}>Profile settings</div>

        {/* Accepting requests toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Accepting new requests</div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
              {accepting ? "Builders can currently send you connection requests." : "New requests are paused — builders see a disabled Connect button."}
            </div>
          </div>
          <button
            onClick={toggleAccepting}
            disabled={settingsLoading}
            style={{
              width: 44, height: 24, borderRadius: 12, border: "none",
              background: accepting ? "#3B82F6" : "#ccc",
              cursor: "pointer", position: "relative", flexShrink: 0, transition: "background 0.2s",
            }}
          >
            <span style={{
              position: "absolute", top: 3, left: accepting ? 23 : 3,
              width: 18, height: 18, borderRadius: "50%", background: "#fff",
              transition: "left 0.15s",
            }} />
          </button>
        </div>

        {/* Close listing */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 14, borderTop: "0.5px solid #f0f0f0" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Listing {listingActive ? "active" : "closed"}</div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
              {listingActive
                ? "Your profile appears in search results."
                : "Your profile is hidden from search. You can reactivate at any time."}
            </div>
          </div>
          <button
            onClick={toggleListing}
            disabled={settingsLoading}
            style={{
              padding: "7px 14px", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500,
              border: listingActive ? "0.5px solid #F5C9BB" : "none",
              background: listingActive ? "transparent" : "#3B82F6",
              color: listingActive ? "#993C1D" : "#fff",
              cursor: "pointer",
            }}
          >
            {settingsLoading ? "Saving…" : listingActive ? "Close listing" : "Reactivate listing"}
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "3rem", color: "#64748B", fontSize: 14 }}>Loading requests…</div>}

      {error && (
        <div style={{ background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB", borderRadius: 8, padding: "12px 16px", fontSize: 13, marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {pending.length === 0 && actioned.length === 0 && (
            <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
              <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="inbox" size={32} /></div>
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No requests yet</div>
              <div style={{ fontSize: 13 }}>When builders send you a connection request it will appear here.</div>
            </div>
          )}
          {pending.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#555", marginBottom: 12 }}>Pending ({pending.length})</div>
              {pending.map(req => <RequestCard key={req.builder_id + req.created_at} req={req} />)}
            </div>
          )}
          {actioned.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#555", marginBottom: 12 }}>Previous ({actioned.length})</div>
              {actioned.map(req => <RequestCard key={req.builder_id + req.created_at} req={req} />)}
            </div>
          )}
        </>
      )}

      {/* ── My Syndicate Investments ───────────────────────────── */}
      {syndicateInvestments.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "#1D4ED8", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
            Syndicated Lending
            <HelpTooltip text="Multiple lenders fund one project together. Each lender contributes a portion and receives proportional returns." />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 1rem" }}>My Syndicate Investments</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {syndicateInvestments.map(inv => {
              const listing = syndicateListings.find(l => l.id === inv.listing_id);
              const fundingNeeded = listing?.funding_needed || 0;
              const synTotal = listing?.synTotal || 0;
              const synPct = fundingNeeded > 0 ? Math.min(100, Math.round((synTotal / fundingNeeded) * 100)) : 0;
              const synFull = synPct >= 100;
              const mySharePct = fundingNeeded > 0 ? ((Number(inv.amount) / fundingNeeded) * 100).toFixed(1) : null;
              return (
                <div key={inv.id} style={{ background: "#fff", border: "0.5px solid #BFDBFE", borderRadius: 12, padding: "1rem 1.25rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 500 }}>{listing?.title || `Listing ${inv.listing_id.slice(0, 8)}`}</div>
                      {listing?.builder_name && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{listing.builder_name}{listing?.location ? ` · ${listing.location}` : ""}</div>}
                    </div>
                    <span style={{ background: synFull ? "#ECFDF5" : "#EFF6FF", color: synFull ? "#059669" : "#1D4ED8", fontSize: 11, padding: "3px 9px", borderRadius: 20, fontWeight: 600, flexShrink: 0 }}>
                      {synFull ? "Fully Funded" : `${synPct}% funded`}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "6px 16px", marginBottom: 10 }}>
                    <div style={{ fontSize: 13 }}>
                      <span style={{ color: "#64748B" }}>My commitment: </span>
                      <span style={{ fontWeight: 600, color: "#1D4ED8" }}>{fmt(inv.amount)}</span>
                    </div>
                    {mySharePct && (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: "#64748B" }}>My share: </span>
                        <span style={{ fontWeight: 600 }}>{mySharePct}%</span>
                      </div>
                    )}
                    {fundingNeeded > 0 && (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: "#64748B" }}>Target: </span>
                        <span style={{ fontWeight: 500 }}>{fmt(fundingNeeded)}</span>
                      </div>
                    )}
                    {listing?.expected_return && (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: "#64748B" }}>Return: </span>
                        <span style={{ fontWeight: 500 }}>{listing.expected_return}</span>
                      </div>
                    )}
                    <div style={{ fontSize: 13 }}>
                      <span style={{ color: "#64748B" }}>Committed: </span>
                      <span style={{ fontWeight: 500 }}>{new Date(inv.committed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    </div>
                  </div>

                  {fundingNeeded > 0 && (
                    <div>
                      <div style={{ height: 5, background: "#BFDBFE", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 3, background: synFull ? "#059669" : "#2563EB", width: `${synPct}%`, transition: "width 0.4s ease" }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>{fmt(synTotal)} of {fmt(fundingNeeded)} committed total</div>
                    </div>
                  )}

                  <button
                    onClick={() => setPage("browse-projects")}
                    style={{ marginTop: 10, background: "transparent", border: "0.5px solid #BFDBFE", color: "#1D4ED8", padding: "5px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer" }}
                  >
                    View project →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── My Funding Rooms ──────────────────────────────────── */}
      {fundingRooms.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#7C3AED", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Group Funding</div>
          <h3 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 1rem" }}>My Funding Rooms</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {fundingRooms.map(room => {
              const pct = room.target_amount > 0 ? Math.min(100, Math.round((room.committed_amount / room.target_amount) * 100)) : 0;
              const fullyFunded = room.status === "fully_funded";
              return (
                <div key={room.id} style={{ background: "var(--bg-card, #fff)", border: "1px solid color-mix(in srgb, #7C3AED 20%, transparent)", borderRadius: 12, padding: "1rem 1.25rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 500 }}>{room.listing_title || "Group Funding Project"}</div>
                      {room.builder_name && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{room.builder_name}</div>}
                    </div>
                    <span style={{ background: fullyFunded ? "#DCFCE7" : "#F3F0FF", color: fullyFunded ? "#166534" : "#7C3AED", fontSize: 11, padding: "3px 9px", borderRadius: 20, fontWeight: 600, flexShrink: 0 }}>
                      {fullyFunded ? "Fully Funded" : `${pct}% funded`}
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "6px 16px", marginBottom: 10 }}>
                    {room.my_amount > 0 && (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: "#64748B" }}>My commitment: </span>
                        <span style={{ fontWeight: 600, color: "#7C3AED" }}>{fmt(room.my_amount)}</span>
                      </div>
                    )}
                    {room.target_amount > 0 && (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: "#64748B" }}>Target: </span>
                        <span style={{ fontWeight: 500 }}>{fmt(room.target_amount)}</span>
                      </div>
                    )}
                    <div style={{ fontSize: 13 }}>
                      <span style={{ color: "#64748B" }}>Committed: </span>
                      <span style={{ fontWeight: 500 }}>{fmt(room.committed_amount)}</span>
                    </div>
                  </div>
                  {room.target_amount > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ height: 5, background: "color-mix(in srgb, #7C3AED 12%, transparent)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 3, background: fullyFunded ? "#16A34A" : "#7C3AED", width: `${pct}%`, transition: "width 0.4s ease" }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>{fmt(room.committed_amount)} of {fmt(room.target_amount)} committed</div>
                    </div>
                  )}
                  <button
                    onClick={() => setPage("funding-room", room.id)}
                    style={{ background: "#7C3AED", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" }}
                  >
                    Enter Room →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ADMIN PAGE ──────────────────────────────────────────────────────────────

const ADMIN_DOC_LABELS = { photo_id: "Photo ID", selfie: "Selfie with ID", site_photos: "Site photos", planning: "Planning permission docs" };

function AdminPage({ user }) {
  const [reports,        setReports]        = useState([]);
  const [flaggedUsers,   setFlaggedUsers]   = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState("");
  const [allUsers,       setAllUsers]       = useState([]);
  const [usersLoading,   setUsersLoading]   = useState(true);
  const [roleMsg,        setRoleMsg]        = useState("");
  const [docSubmissions, setDocSubmissions] = useState([]);
  const [docMsg,         setDocMsg]         = useState("");
  const [rejectingId,    setRejectingId]    = useState(null);
  const [rejectReasons,  setRejectReasons]  = useState({});
  const [disputes,       setDisputes]       = useState([]);
  const [disputeMsg,     setDisputeMsg]     = useState("");
  const [dealDocs,       setDealDocs]       = useState([]);
  const [dealDocMsg,     setDealDocMsg]     = useState("");
  const [rejectingDocId, setRejectingDocId] = useState(null);
  const [docRejectReasons, setDocRejectReasons] = useState({});
  // Community moderation state
  const [commMod,        setCommMod]        = useState(null); // { reports, messages, bans, volume }
  const [commMsg,        setCommMsg]        = useState("");
  const [banForm,        setBanForm]        = useState({}); // { [userId]: { open, reason, type } }
  const [kycChecks,      setKycChecks]      = useState([]);
  const [dealsRevenue,   setDealsRevenue]   = useState(null);
  const [chaseMsg,       setChaseMsg]       = useState({});

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const [reportsRes, usersRes, docsRes, disputesRes, dealDocsRes, commModRes, dealsRes] = await Promise.all([
        fetch("/api/admin",                       { headers: { Authorization: `Bearer ${session.access_token}` } }),
        fetch("/api/admin?type=users",            { headers: { Authorization: `Bearer ${session.access_token}` } }),
        fetch("/api/admin?type=documents",        { headers: { Authorization: `Bearer ${session.access_token}` } }),
        fetch("/api/admin?type=disputes",         { headers: { Authorization: `Bearer ${session.access_token}` } }),
        fetch("/api/admin?type=deal-docs",        { headers: { Authorization: `Bearer ${session.access_token}` } }),
        fetch("/api/admin?type=community-mod",    { headers: { Authorization: `Bearer ${session.access_token}` } }),
        fetch("/api/admin?type=deals",            { headers: { Authorization: `Bearer ${session.access_token}` } }),
      ]);
      const reportsJson = await reportsRes.json();
      if (!reportsRes.ok) { setError(reportsJson.error || "Failed to load reports"); }
      else { setReports(reportsJson.reports || []); setFlaggedUsers(reportsJson.flagged_users || []); }
      setLoading(false);

      const usersJson = await usersRes.json();
      setAllUsers(usersJson.users || []);
      setUsersLoading(false);

      const docsJson = await docsRes.json();
      setDocSubmissions(docsJson.submissions || []);

      const disputesJson = await disputesRes.json();
      setDisputes(disputesJson.disputes || []);

      const dealDocsJson = await dealDocsRes.json();
      setDealDocs(dealDocsJson.deal_docs || []);

      const commModJson = await commModRes.json();
      setCommMod(commModJson.reports ? commModJson : null);

      const dealsJson = await dealsRes.json();
      if (!dealsRes.ok) {} else setDealsRevenue(dealsJson);

      // KYC checks — direct Supabase query (admin only)
      const { data: kyc } = await supabase.from("kyc_checks").select("*").order("checked_at", { ascending: false }).limit(100);
      setKycChecks(kyc || []);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSetRole(targetId, newRole) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const res = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "set-user-role", target_user_id: targetId, user_role: newRole }),
    });
    if (res.ok) {
      setAllUsers(prev => prev.map(u => u.id === targetId ? { ...u, user_role: newRole || null } : u));
      setRoleMsg("Role updated.");
      setTimeout(() => setRoleMsg(""), 3000);
    } else {
      setRoleMsg("Failed to update role.");
    }
  }

  async function handleReviewDoc(submissionId, decision, reason = "") {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const res = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "review-document", submission_id: submissionId, decision, rejection_reason: reason }),
    });
    if (res.ok) {
      setDocSubmissions(prev => prev.filter(s => s.id !== submissionId));
      setRejectingId(null);
      setRejectReasons(prev => { const n = { ...prev }; delete n[submissionId]; return n; });
      setDocMsg(`Document ${decision}.`);
      setTimeout(() => setDocMsg(""), 3000);
    } else {
      setDocMsg("Action failed — please try again.");
      setTimeout(() => setDocMsg(""), 3000);
    }
  }

  async function handleDisputeAction(disputeId, action, notes = "") {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const res = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action, dispute_id: disputeId, resolution_notes: notes }),
    });
    if (res.ok) {
      const newStatus = action === "resolve-dispute" ? "resolved" : "escalated";
      setDisputes(prev => prev.map(d => d.id === disputeId ? { ...d, status: newStatus, resolution_notes: notes } : d));
      setDisputeMsg(`Dispute ${newStatus}.`);
      setTimeout(() => setDisputeMsg(""), 3000);
    } else {
      setDisputeMsg("Action failed.");
      setTimeout(() => setDisputeMsg(""), 3000);
    }
  }

  async function handleDealDocAction(docId, action, reason = "") {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const res = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action, deal_doc_id: docId, rejection_reason: reason }),
    });
    if (res.ok) {
      setDealDocs(prev => prev.filter(d => d.id !== docId));
      setRejectingDocId(null);
      setDocRejectReasons(prev => { const n = { ...prev }; delete n[docId]; return n; });
      setDealDocMsg(`Document ${action === "approve-deal-doc" ? "approved" : "rejected"}.`);
      setTimeout(() => setDealDocMsg(""), 3000);
    } else {
      setDealDocMsg("Action failed.");
      setTimeout(() => setDealDocMsg(""), 3000);
    }
  }

  async function handleModerateMessage(messageId, decision, table = "community_messages") {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const res = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "moderate-message", message_id: messageId, decision, message_table: table }),
    });
    if (res.ok) {
      const listKey = table === "group_messages" ? "groupMessages" : "messages";
      setCommMod(prev => prev ? {
        ...prev,
        [listKey]: prev[listKey].map(m => m.id === messageId
          ? { ...m, hidden: decision === "remove", flagged: false }
          : m),
      } : prev);
      setCommMsg(decision === "remove" ? "Message removed." : "Message approved.");
      setTimeout(() => setCommMsg(""), 3000);
    }
  }

  async function handleBanUser(uid, uname, reason, banType) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const bannedUntil = banType === "temporary" ? new Date(Date.now() + 86400000).toISOString() : null;
    const res = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "ban-user", target_user_id: uid, ban_user_name: uname, ban_reason: reason, ban_type: banType, banned_until: bannedUntil }),
    });
    if (res.ok) {
      const newBan = { id: Date.now().toString(), user_id: uid, user_name: uname, reason, ban_type: banType, banned_until: bannedUntil, banned_at: new Date().toISOString() };
      setCommMod(prev => prev ? { ...prev, bans: [newBan, ...(prev.bans || [])] } : prev);
      setBanForm({});
      setCommMsg(`${uname} banned from community.`);
      setTimeout(() => setCommMsg(""), 3000);
    }
  }

  async function handleUnbanUser(banId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const res = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "unban-user", ban_id: banId }),
    });
    if (res.ok) {
      setCommMod(prev => prev ? { ...prev, bans: prev.bans.filter(b => b.id !== banId) } : prev);
      setCommMsg("User unbanned.");
      setTimeout(() => setCommMsg(""), 3000);
    }
  }

  async function handleChaseFee(dealId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    setChaseMsg(prev => ({ ...prev, [dealId]: "Sending…" }));
    const res = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "chase-finder-fee", deal_id: dealId }),
    });
    if (res.ok) {
      setChaseMsg(prev => ({ ...prev, [dealId]: "Chased!" }));
      setTimeout(() => setChaseMsg(prev => { const n = { ...prev }; delete n[dealId]; return n; }), 4000);
    } else {
      const j = await res.json();
      setChaseMsg(prev => ({ ...prev, [dealId]: j.error || "Failed" }));
      setTimeout(() => setChaseMsg(prev => { const n = { ...prev }; delete n[dealId]; return n; }), 4000);
    }
  }

  // Group by builder for a summary
  const byBuilder = {};
  for (const r of reports) {
    if (!byBuilder[r.builder_id]) {
      byBuilder[r.builder_id] = { builder_name: r.builder_name, builder_id: r.builder_id, count: 0, reports: [] };
    }
    byBuilder[r.builder_id].count++;
    byBuilder[r.builder_id].reports.push(r);
  }
  const grouped = Object.values(byBuilder).sort((a, b) => b.count - a.count);

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 800, margin: "0 auto" }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: "#D85A30", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>Admin</div>
      <h2 style={{ fontSize: 22, fontWeight: 500, marginBottom: "1.5rem" }}>Admin panel</h2>

      {loading && <div style={{ textAlign: "center", padding: "3rem", color: "#64748B", fontSize: 14 }}>Loading…</div>}
      {error   && <div style={{ background: "#FAECE7", color: "#993C1D", borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>{error}</div>}

      {/* Flagged accounts */}
      {!loading && !error && (
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            Flagged accounts
            {flaggedUsers.length > 0 && (
              <span style={{ background: "#FAECE7", color: "#993C1D", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
                {flaggedUsers.length}
              </span>
            )}
          </div>
          {flaggedUsers.length === 0 ? (
            <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>No flagged accounts.</div>
          ) : flaggedUsers.map(u => (
            <div key={u.user_id} style={{ background: "#fff", border: "0.5px solid #F5C9BB", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{u.name}</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>{u.email} · {u.role}</div>
                {u.flag_reason && <div style={{ fontSize: 12, color: "#993C1D", marginTop: 2 }}>{u.flag_reason}</div>}
              </div>
              {u.flagged_at && (
                <span style={{ fontSize: 11, color: "#64748B", flexShrink: 0 }}>
                  {new Date(u.flagged_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reported users */}
      {!loading && !error && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Reported users</div>
          {grouped.length === 0 ? (
            <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>No reports yet.</div>
          ) : grouped.map(g => (
            <div key={g.builder_id} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 500 }}>{g.builder_name || g.builder_id}</div>
                <span style={{
                  background: g.count >= 3 ? "#FAECE7" : "#DBEAFE",
                  color:      g.count >= 3 ? "#993C1D" : "#1D4ED8",
                  fontSize: 12, padding: "3px 10px", borderRadius: 20, fontWeight: 500,
                }}>
                  {g.count} {g.count === 1 ? "report" : "reports"}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {g.reports.map((r, i) => (
                  <div key={i} style={{ background: "#f9f9f7", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 500 }}>Reported by {r.lender_name || "unknown lender"}</span>
                      <span style={{ fontSize: 11, color: "#64748B" }}>
                        {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "#444", lineHeight: 1.5 }}>{r.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Document review */}
      <div style={{ marginTop: "2rem", marginBottom: "2rem" }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          Document review
          {docMsg && <span style={{ fontSize: 12, color: "#1D9E75", fontWeight: 400 }}>{docMsg}</span>}
          {docSubmissions.length > 0 && (
            <span style={{ background: "#FEF3C7", color: "#B45309", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
              {docSubmissions.length} pending
            </span>
          )}
        </div>
        {docSubmissions.length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>No pending documents.</div>
        ) : docSubmissions.map(s => (
          <div key={s.id} style={{ background: "#fff", border: "0.5px solid #FCD34D", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{s.user_name}</div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{s.user_email}</div>
              <div style={{ fontSize: 12, color: "#555", marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontWeight: 500 }}>{ADMIN_DOC_LABELS[s.document_type] || s.document_type}</span>
                <span style={{ color: "#ccc" }}>·</span>
                <span style={{ color: "#aaa" }}>{new Date(s.submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                {s.preview_url && (
                  <a href={s.preview_url} target="_blank" rel="noopener noreferrer" style={{ color: "#3B82F6", textDecoration: "none", fontWeight: 500 }}>View document ↗</a>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
              {rejectingId === s.id ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <textarea
                    value={rejectReasons[s.id] || ""}
                    onChange={e => setRejectReasons(prev => ({ ...prev, [s.id]: e.target.value }))}
                    placeholder="Reason for rejection (sent to user)"
                    style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "0.5px solid #FCA5A5", resize: "vertical", minHeight: 60, width: 220, boxSizing: "border-box" }}
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => handleReviewDoc(s.id, "rejected", rejectReasons[s.id] || "")}
                      style={{ flex: 1, fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "0.5px solid #FCA5A5", background: "#FEE2E2", color: "#991B1B", cursor: "pointer", minHeight: 36 }}
                    >Confirm reject</button>
                    <button
                      onClick={() => { setRejectingId(null); setRejectReasons(prev => { const n = { ...prev }; delete n[s.id]; return n; }); }}
                      style={{ fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "0.5px solid #e0e0e0", background: "#fff", color: "#555", cursor: "pointer", minHeight: 36 }}
                    >Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => handleReviewDoc(s.id, "approved")}
                    style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #A8DFC9", background: "#E1F5EE", color: "#0F6E56", cursor: "pointer", minHeight: 36 }}
                  >Approve</button>
                  <button
                    onClick={() => setRejectingId(s.id)}
                    style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #FCA5A5", background: "#FEE2E2", color: "#991B1B", cursor: "pointer", minHeight: 36 }}
                  >Reject</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Role management */}
      <div style={{ marginTop: "2rem" }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          Role management
          {roleMsg && <span style={{ fontSize: 12, color: "#1D9E75", fontWeight: 400 }}>{roleMsg}</span>}
        </div>
        {usersLoading ? (
          <div style={{ fontSize: 13, color: "#64748B" }}>Loading users…</div>
        ) : allUsers.length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>No profiles found.</div>
        ) : allUsers.map(u => (
          <div key={u.id} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 10, padding: "0.9rem 1.25rem", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{u.full_name || "Anonymous"}</span>
                {u.sequential_id && <span style={{ fontSize: 11, color: "#aaa" }}>{fmtId(u.sequential_id)}</span>}
                <RoleBadge userRole={u.user_role} authRole={u.auth_role} />
              </div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{u.email}</div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              {u.user_role === "founder" ? (
                <span style={{ fontSize: 12, color: "#aaa", fontStyle: "italic" }}>Founder — protected</span>
              ) : u.user_role === "verified_pro" ? (
                <button
                  onClick={() => handleSetRole(u.id, null)}
                  style={{ fontSize: 12, padding: "5px 12px", borderRadius: 8, border: "0.5px solid #e0e0e0", background: "#fff", color: "#993C1D", cursor: "pointer", minHeight: 36 }}
                >
                  Remove Verified Pro
                </button>
              ) : (
                <button
                  onClick={() => handleSetRole(u.id, "verified_pro")}
                  style={{ fontSize: 12, padding: "5px 12px", borderRadius: 8, border: "0.5px solid #B45309", background: "#FEF3C7", color: "#B45309", cursor: "pointer", minHeight: 36 }}
                >
                  Grant Verified Pro
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Disputes */}
      <div style={{ marginTop: "2rem" }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          Dispute resolution
          {disputeMsg && <span style={{ fontSize: 12, color: "#1D9E75", fontWeight: 400 }}>{disputeMsg}</span>}
          {disputes.filter(d => d.status === "open").length > 0 && (
            <span style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
              {disputes.filter(d => d.status === "open").length} open
            </span>
          )}
        </div>
        {disputes.length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>No disputes.</div>
        ) : disputes.map(dp => {
          const statusCfg = { open: ["#FEE2E2","#991B1B"], resolved: ["#E1F5EE","#0F6E56"], escalated: ["#EEEDFE","#534AB7"] };
          const [sbg, scol] = statusCfg[dp.status] || ["#f5f5f3","#64748B"];
          return (
            <div key={dp.id} style={{ background: "#fff", border: `0.5px solid ${dp.status === "open" ? "#FCA5A5" : "#e0e0e0"}`, borderRadius: 10, padding: "1rem 1.25rem", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{dp.deals?.title || "Deal"}</div>
                  <div style={{ fontSize: 12, color: "#64748B" }}>Raised by {dp.raised_by_name || dp.raised_by_role} · {new Date(dp.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: sbg, color: scol, flexShrink: 0 }}>
                  {dp.status.charAt(0).toUpperCase() + dp.status.slice(1)}
                </span>
              </div>
              <div style={{ fontSize: 13, color: "#444", lineHeight: 1.6, marginBottom: dp.status === "open" ? 10 : 0 }}>{dp.reason}</div>
              {dp.status === "open" && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={() => handleDisputeAction(dp.id, "resolve-dispute", "Resolved by admin.")}
                    style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #A8DFC9", background: "#E1F5EE", color: "#0F6E56", cursor: "pointer", minHeight: 36 }}
                  >Resolve</button>
                  <button
                    onClick={() => handleDisputeAction(dp.id, "escalate-dispute")}
                    style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #C4B5FD", background: "#EEEDFE", color: "#534AB7", cursor: "pointer", minHeight: 36 }}
                  >Escalate</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Project document review (deal-specific required docs) */}
      <div style={{ marginTop: "2rem" }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          Project document review
          {dealDocMsg && <span style={{ fontSize: 12, color: "#1D9E75", fontWeight: 400 }}>{dealDocMsg}</span>}
          {dealDocs.length > 0 && (
            <span style={{ background: "#FEF3C7", color: "#B45309", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
              {dealDocs.length} pending
            </span>
          )}
        </div>
        {dealDocs.length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>No pending project documents.</div>
        ) : dealDocs.map(doc => (
          <div key={doc.id} style={{ background: "#fff", border: "0.5px solid #FCD34D", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{doc.deals?.title || "Deal"}</div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Builder: {doc.deals?.builder_name}</div>
              <div style={{ fontSize: 12, color: "#555", marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontWeight: 500 }}>{ADMIN_DOC_LABELS[doc.doc_type] || doc.doc_type}</span>
                <span style={{ color: "#ccc" }}>·</span>
                <span style={{ color: "#aaa" }}>{new Date(doc.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                {doc.preview_url && (
                  <a href={doc.preview_url} target="_blank" rel="noopener noreferrer" style={{ color: "#3B82F6", textDecoration: "none", fontWeight: 500 }}>View document ↗</a>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
              {rejectingDocId === doc.id ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <textarea
                    value={docRejectReasons[doc.id] || ""}
                    onChange={e => setDocRejectReasons(prev => ({ ...prev, [doc.id]: e.target.value }))}
                    placeholder="Reason for rejection"
                    style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "0.5px solid #FCA5A5", resize: "vertical", minHeight: 60, width: 220, boxSizing: "border-box" }}
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => handleDealDocAction(doc.id, "reject-deal-doc", docRejectReasons[doc.id] || "")}
                      style={{ flex: 1, fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "0.5px solid #FCA5A5", background: "#FEE2E2", color: "#991B1B", cursor: "pointer", minHeight: 36 }}
                    >Confirm reject</button>
                    <button
                      onClick={() => { setRejectingDocId(null); setDocRejectReasons(prev => { const n = { ...prev }; delete n[doc.id]; return n; }); }}
                      style={{ fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "0.5px solid #e0e0e0", background: "#fff", color: "#555", cursor: "pointer", minHeight: 36 }}
                    >Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => handleDealDocAction(doc.id, "approve-deal-doc")}
                    style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #A8DFC9", background: "#E1F5EE", color: "#0F6E56", cursor: "pointer", minHeight: 36 }}
                  >Approve</button>
                  <button
                    onClick={() => setRejectingDocId(doc.id)}
                    style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #FCA5A5", background: "#FEE2E2", color: "#991B1B", cursor: "pointer", minHeight: 36 }}
                  >Reject</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Default warning system — flagged deals */}
      {/* ── Deals & Revenue ─────────────────────────────────────────────────── */}
      <div style={{ marginTop: "2.5rem" }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          Deals & Revenue
          {dealsRevenue && dealsRevenue.unpaid_count > 0 && (
            <span style={{ background: "#FEF3C7", color: "#B45309", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
              {dealsRevenue.unpaid_count} fee{dealsRevenue.unpaid_count !== 1 ? "s" : ""} unpaid
            </span>
          )}
        </div>
        {dealsRevenue && (
          <div style={{ display: "flex", gap: 12, marginBottom: "1.25rem", flexWrap: "wrap" }}>
            {[
              { label: "Total revenue (all time)", value: `£${Number(dealsRevenue.total_revenue || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: "#E1F5EE", color: "#0F6E56" },
              { label: "Revenue this month", value: `£${Number(dealsRevenue.month_revenue || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: "#EBF2FF", color: "#1E3A5F" },
              { label: "Confirmed deals", value: (dealsRevenue.deals || []).filter(d => d.deal_confirmed_at).length, bg: "#F5F3FF", color: "#6D28D9" },
              { label: "Fees unpaid", value: dealsRevenue.unpaid_count || 0, bg: "#FEF3C7", color: "#B45309" },
            ].map(s => (
              <div key={s.label} style={{ flex: "1 1 140px", background: s.bg, borderRadius: 12, padding: "14px 18px", minWidth: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: s.color, opacity: 0.75, marginTop: 3 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
        {dealsRevenue && (() => {
          // Build monthly revenue chart data from confirmed deals
          const confirmedDeals = (dealsRevenue.deals || []).filter(d => d.deal_confirmed_at && d.finder_fee_status === "paid");
          const monthMap = {};
          for (const d of confirmedDeals) {
            const dt = new Date(d.deal_confirmed_at);
            const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
            const label = dt.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
            if (!monthMap[key]) monthMap[key] = { key, label, revenue: 0 };
            monthMap[key].revenue += Number(d.agreed_amount) * 0.01;
          }
          const chartData = Object.keys(monthMap).sort().map(k => ({
            label: monthMap[k].label,
            revenue: Math.round(monthMap[k].revenue * 100) / 100,
          }));
          if (chartData.length === 0) return null;
          return (
            <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1rem", marginBottom: "1.25rem" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 10 }}>Monthly revenue — finder's fees collected</div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={chartData} margin={{ top: 2, right: 4, left: -20, bottom: 2 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#aaa" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#aaa" }} tickFormatter={v => `£${v}`} />
                  <Tooltip formatter={v => [`£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`, "Revenue"]} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" fill="#0F6E56" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })()}
        {!dealsRevenue ? (
          <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>Loading deals…</div>
        ) : (dealsRevenue.deals || []).filter(d => d.deal_confirmed_at).length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>No confirmed deals yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(dealsRevenue.deals || []).filter(d => d.deal_confirmed_at).map(deal => {
              const fee = (Number(deal.agreed_amount) * 0.01).toFixed(2);
              const paid = deal.finder_fee_status === "paid";
              const msg = chaseMsg[deal.id];
              return (
                <div key={deal.id} style={{ background: "#fff", border: `0.5px solid ${paid ? "#A8DFC9" : "#FCD34D"}`, borderRadius: 10, padding: "0.9rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{deal.builder_name} ↔ {deal.lender_name}</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>
                      £{Number(deal.agreed_amount || 0).toLocaleString()} deal · finder's fee: £{Number(fee).toLocaleString()}
                      {deal.deal_confirmed_at && ` · ${new Date(deal.deal_confirmed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {!paid && (
                      msg ? (
                        <span style={{ fontSize: 11, color: msg === "Chased!" ? "#0F6E56" : "#DC2626" }}>{msg}</span>
                      ) : (
                        <button onClick={() => handleChaseFee(deal.id)}
                          style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 20, background: "#FEF3C7", color: "#B45309", border: "1px solid #FCD34D", cursor: "pointer" }}>
                          Chase payment
                        </button>
                      )
                    )}
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: paid ? "#E1F5EE" : "#FEF2F2", color: paid ? "#0F6E56" : "#DC2626" }}>
                      {paid ? "Fee paid" : "Fee unpaid"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Community Moderation ─────────────────────────────────────────────── */}
      <div style={{ marginTop: "2.5rem" }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          Community Moderation
          {commMsg && <span style={{ fontSize: 12, color: "#1D9E75", fontWeight: 400 }}>{commMsg}</span>}
          {commMod && ((commMod.reports?.length || 0) + (commMod.groupReports?.length || 0) + (commMod.messages?.filter(m => m.flagged && !m.hidden).length || 0) > 0) && (
            <span style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
              {(commMod.reports?.length || 0) + (commMod.groupReports?.length || 0) + (commMod.messages?.filter(m => m.flagged && !m.hidden).length || 0)} pending
            </span>
          )}
        </div>

        {!commMod ? (
          <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>Loading community data…</div>
        ) : (
          <>
            {/* Message volume chart */}
            {commMod.volume && commMod.volume.length > 0 && (
              <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1rem", marginBottom: "1.25rem" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 10 }}>Message volume — last 24h</div>
                <ResponsiveContainer width="100%" height={90}>
                  <BarChart data={commMod.volume} margin={{ top: 2, right: 4, left: -28, bottom: 2 }}>
                    <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#aaa" }} interval={3} />
                    <YAxis tick={{ fontSize: 10, fill: "#aaa" }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="count" fill="#3B82F6" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Reported messages */}
            <div style={{ fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 8, marginTop: 4 }}>Reported messages</div>
            {(commMod.reports || []).length === 0 ? (
              <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "0.875rem", marginBottom: "1rem" }}>No reported messages.</div>
            ) : (() => {
              // Group reports by message_id
              const byMsg = {};
              for (const r of commMod.reports) {
                if (!byMsg[r.message_id]) byMsg[r.message_id] = { reports: [], content: r.message_content, uid: r.message_user_id, uname: r.message_user_name };
                byMsg[r.message_id].reports.push(r);
              }
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "1.25rem" }}>
                  {Object.entries(byMsg).map(([msgId, g]) => {
                    const modMsg = commMod.messages?.find(m => m.id === msgId);
                    return (
                      <div key={msgId} style={{ background: "#fff", border: "0.5px solid #FCA5A5", borderRadius: 10, padding: "1rem 1.25rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: "#991B1B" }}>{g.uname || "Unknown user"}</div>
                            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                              {g.reports.length} report{g.reports.length !== 1 ? "s" : ""} ·{" "}
                              {g.reports.map(r => r.reason).join(", ")}
                            </div>
                            {g.reports.map((r, i) => (
                              <div key={i} style={{ fontSize: 11, color: "#64748B" }}>by {r.reporter_name} — {fmtTimeAgo(r.created_at)}</div>
                            ))}
                          </div>
                          {modMsg && (
                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: modMsg.hidden ? "#F1F5F9" : "#FEE2E2", color: modMsg.hidden ? "#64748B" : "#991B1B", flexShrink: 0 }}>
                              {modMsg.hidden ? "Hidden" : "Visible"}
                            </span>
                          )}
                        </div>
                        {g.content && (
                          <div style={{ fontSize: 13, color: "#555", background: "#f9f9f7", borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontStyle: "italic", wordBreak: "break-word" }}>
                            "{g.content.slice(0, 300)}"
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          {!modMsg?.hidden && (
                            <button onClick={() => handleModerateMessage(msgId, "remove")}
                              style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #FCA5A5", background: "#FEE2E2", color: "#991B1B", cursor: "pointer", minHeight: 36 }}>
                              Remove
                            </button>
                          )}
                          {modMsg?.hidden && (
                            <button onClick={() => handleModerateMessage(msgId, "approve")}
                              style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #A8DFC9", background: "#E1F5EE", color: "#0F6E56", cursor: "pointer", minHeight: 36 }}>
                              Restore
                            </button>
                          )}
                          {/* Ban user from community */}
                          {g.uid && !banForm[g.uid]?.open && !commMod.bans?.some(b => b.user_id === g.uid) && (
                            <button onClick={() => setBanForm(prev => ({ ...prev, [g.uid]: { open: true, reason: "", type: "temporary" } }))}
                              style={{ fontSize: 12, padding: "5px 12px", borderRadius: 8, border: "0.5px solid #e0e0e0", background: "#fff", color: "#555", cursor: "pointer", minHeight: 36 }}>
                              Ban from chat
                            </button>
                          )}
                          {banForm[g.uid]?.open && (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                              <select value={banForm[g.uid].type}
                                onChange={e => setBanForm(prev => ({ ...prev, [g.uid]: { ...prev[g.uid], type: e.target.value } }))}
                                style={{ height: 36, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 8px", fontSize: 12 }}>
                                <option value="temporary">24h ban</option>
                                <option value="permanent">Permanent ban</option>
                              </select>
                              <input placeholder="Reason (optional)"
                                value={banForm[g.uid].reason || ""}
                                onChange={e => setBanForm(prev => ({ ...prev, [g.uid]: { ...prev[g.uid], reason: e.target.value } }))}
                                style={{ height: 36, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 10px", fontSize: 12, width: 160 }} />
                              <button onClick={() => handleBanUser(g.uid, g.uname, banForm[g.uid].reason, banForm[g.uid].type)}
                                style={{ height: 36, padding: "0 14px", background: "#991B1B", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
                                Confirm ban
                              </button>
                              <button onClick={() => setBanForm(prev => { const n = { ...prev }; delete n[g.uid]; return n; })}
                                style={{ height: 36, padding: "0 10px", background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 12, color: "#555", cursor: "pointer" }}>
                                Cancel
                              </button>
                            </div>
                          )}
                          {commMod.bans?.some(b => b.user_id === g.uid) && (
                            <span style={{ fontSize: 11, color: "#991B1B", fontWeight: 600 }}>User banned</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Group chat reports */}
            <div style={{ fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 8, marginTop: 4 }}>Group chat reports</div>
            {(commMod.groupReports || []).length === 0 ? (
              <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "0.875rem", marginBottom: "1rem" }}>No group chat reports.</div>
            ) : (() => {
              const byMsg = {};
              for (const r of commMod.groupReports) {
                if (!byMsg[r.message_id]) byMsg[r.message_id] = { reports: [], content: r.message_content, uid: r.message_user_id, uname: r.message_user_name };
                byMsg[r.message_id].reports.push(r);
              }
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "1.25rem" }}>
                  {Object.entries(byMsg).map(([msgId, g]) => {
                    const modMsg = commMod.groupMessages?.find(m => m.id === msgId);
                    return (
                      <div key={msgId} style={{ background: "#fff", border: "0.5px solid #FCA5A5", borderRadius: 10, padding: "1rem 1.25rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: "#991B1B" }}>{g.uname || "Unknown user"}</div>
                            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                              {g.reports.length} report{g.reports.length !== 1 ? "s" : ""} · {g.reports.map(r => r.reason).join(", ")}
                            </div>
                            {g.reports.map((r, i) => (
                              <div key={i} style={{ fontSize: 11, color: "#64748B" }}>by {r.reporter_name} — {fmtTimeAgo(r.created_at)}</div>
                            ))}
                          </div>
                          {modMsg && (
                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: modMsg.hidden ? "#F1F5F9" : "#FEE2E2", color: modMsg.hidden ? "#64748B" : "#991B1B", flexShrink: 0 }}>
                              {modMsg.hidden ? "Hidden" : "Visible"}
                            </span>
                          )}
                        </div>
                        {g.content && (
                          <div style={{ fontSize: 13, color: "#555", background: "#f9f9f7", borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontStyle: "italic", wordBreak: "break-word" }}>
                            "{g.content.slice(0, 300)}"
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          {!modMsg?.hidden && (
                            <button onClick={() => handleModerateMessage(msgId, "remove", "group_messages")}
                              style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #FCA5A5", background: "#FEE2E2", color: "#991B1B", cursor: "pointer", minHeight: 36 }}>
                              Remove
                            </button>
                          )}
                          {modMsg?.hidden && (
                            <button onClick={() => handleModerateMessage(msgId, "approve", "group_messages")}
                              style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #A8DFC9", background: "#E1F5EE", color: "#0F6E56", cursor: "pointer", minHeight: 36 }}>
                              Restore
                            </button>
                          )}
                          {g.uid && !banForm[g.uid]?.open && !commMod.bans?.some(b => b.user_id === g.uid) && (
                            <button onClick={() => setBanForm(prev => ({ ...prev, [g.uid]: { open: true, reason: "", type: "temporary" } }))}
                              style={{ fontSize: 12, padding: "5px 12px", borderRadius: 8, border: "0.5px solid #e0e0e0", background: "#fff", color: "#555", cursor: "pointer", minHeight: 36 }}>
                              Ban from chat
                            </button>
                          )}
                          {banForm[g.uid]?.open && (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                              <select value={banForm[g.uid].type}
                                onChange={e => setBanForm(prev => ({ ...prev, [g.uid]: { ...prev[g.uid], type: e.target.value } }))}
                                style={{ height: 36, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 8px", fontSize: 12 }}>
                                <option value="temporary">24h ban</option>
                                <option value="permanent">Permanent ban</option>
                              </select>
                              <input value={banForm[g.uid]?.reason || ""} onChange={e => setBanForm(prev => ({ ...prev, [g.uid]: { ...prev[g.uid], reason: e.target.value } }))}
                                placeholder="Reason (optional)" maxLength={200}
                                style={{ height: 36, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 10px", fontSize: 12, width: 160 }} />
                              <button onClick={() => handleBanUser(g.uid, g.uname, banForm[g.uid].reason, banForm[g.uid].type)}
                                style={{ height: 36, padding: "0 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, background: "#DC2626", color: "#fff", cursor: "pointer" }}>
                                Confirm ban
                              </button>
                              <button onClick={() => setBanForm(prev => { const n = { ...prev }; delete n[g.uid]; return n; })}
                                style={{ height: 36, padding: "0 10px", background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 12, color: "#555", cursor: "pointer" }}>
                                Cancel
                              </button>
                            </div>
                          )}
                          {commMod.bans?.some(b => b.user_id === g.uid) && (
                            <span style={{ fontSize: 11, color: "#991B1B", fontWeight: 600 }}>User banned</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Auto-flagged messages */}
            <div style={{ fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 8 }}>Auto-flagged messages</div>
            {(commMod.messages || []).filter(m => m.flagged).length === 0 ? (
              <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "0.875rem", marginBottom: "1rem" }}>No auto-flagged messages.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "1.25rem" }}>
                {(commMod.messages || []).filter(m => m.flagged).map(m => (
                  <div key={m.id} style={{ background: "#fff", border: "0.5px solid #FCD34D", borderRadius: 10, padding: "1rem 1.25rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{m.user_name || "Unknown"}</div>
                        <div style={{ fontSize: 12, color: "#64748B" }}>
                          #{m.channel} · flagged: <em>{m.flag_reason}</em> · {fmtTimeAgo(m.created_at)}
                        </div>
                      </div>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: m.hidden ? "#F1F5F9" : "#FFFBEB", color: m.hidden ? "#64748B" : "#92400E", flexShrink: 0 }}>
                        {m.hidden ? "Hidden" : "Auto-hidden"}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "#555", background: "#FFFBEB", borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontStyle: "italic", wordBreak: "break-word" }}>
                      "{(m.content || "").slice(0, 300)}"
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => handleModerateMessage(m.id, "approve")}
                        style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #A8DFC9", background: "#E1F5EE", color: "#0F6E56", cursor: "pointer", minHeight: 36 }}>
                        Approve
                      </button>
                      <button onClick={() => handleModerateMessage(m.id, "remove")}
                        style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #FCA5A5", background: "#FEE2E2", color: "#991B1B", cursor: "pointer", minHeight: 36 }}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bans list */}
            <div style={{ fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 8 }}>
              Banned users ({(commMod.bans || []).length})
            </div>
            {(commMod.bans || []).length === 0 ? (
              <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "0.875rem" }}>No banned users.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(commMod.bans || []).map(b => (
                  <div key={b.id} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 10, padding: "0.875rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{b.user_name || b.user_id}</div>
                      <div style={{ fontSize: 12, color: "#64748B" }}>
                        {b.ban_type === "permanent" ? "Permanent ban" : `Until ${b.banned_until ? new Date(b.banned_until).toLocaleString("en-GB") : "—"}`}
                        {b.reason ? ` · ${b.reason}` : ""}
                      </div>
                    </div>
                    <button onClick={() => handleUnbanUser(b.id)}
                      style={{ fontSize: 12, padding: "5px 14px", borderRadius: 8, border: "0.5px solid #A8DFC9", background: "#E1F5EE", color: "#0F6E56", cursor: "pointer", minHeight: 36, flexShrink: 0 }}>
                      Unban
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── KYC Checks ─────────────────────────────────────────────────────────── */}
      <div style={{ marginTop: "2.5rem" }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          KYC Checks
          {kycChecks.filter(k => k.result !== "pass").length > 0 && (
            <span style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
              {kycChecks.filter(k => k.result !== "pass").length} flagged
            </span>
          )}
        </div>
        {kycChecks.length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748B", background: "#f9f9f7", borderRadius: 8, padding: "1rem" }}>No KYC checks yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {kycChecks.filter(k => k.result !== "pass").concat(kycChecks.filter(k => k.result === "pass").slice(0, 5)).map(k => (
              <div key={k.id} style={{ background: k.result === "flagged_sanctions" ? "#FEE2E2" : k.result === "warn_disposable" ? "#FEF3C7" : "#f9f9f7", border: `0.5px solid ${k.result === "flagged_sanctions" ? "#FCA5A5" : k.result === "warn_disposable" ? "#FCD34D" : "#e0e0e0"}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {k.result === "flagged_sanctions" ? "Sanctions match" : k.result === "warn_disposable" ? "Disposable email" : "✓ Pass"}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                      User: {k.user_id?.slice(0, 8)}…
                      {k.ip_country && <span> · IP: {k.ip_country}{k.ip_outside_uk ? " (outside UK)" : ""}</span>}
                      {k.details?.email_domain && <span> · Domain: {k.details.email_domain}</span>}
                      <span> · {new Date(k.checked_at).toLocaleDateString("en-GB")}</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 20, fontWeight: 600, background: k.result === "flagged_sanctions" ? "#991B1B" : k.result === "warn_disposable" ? "#92400E" : "#166534", color: "#fff", flexShrink: 0 }}>
                    {k.result.replace(/_/g, " ")}
                  </span>
                </div>
              </div>
            ))}
            {kycChecks.filter(k => k.result === "pass").length > 5 && (
              <div style={{ fontSize: 12, color: "#64748B", textAlign: "center", padding: "8px 0" }}>
                +{kycChecks.filter(k => k.result === "pass").length - 5} more passing checks (not shown)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DEAL CONFIRM WIDGET ─────────────────────────────────────────────────────
// Shown on accepted connections so both parties can confirm the agreed deal amount.

// convo is the selectedConvo object from MessagesPage — when provided, its
// builder_id/lender_id are sent directly so the server can skip the name lookup.
function useDealConfirm(user, otherName, convo = null) {
  const [status,      setStatus]      = useState(null);
  const [myAmount,    setMyAmount]    = useState(null);
  const [theirAmount, setTheirAmount] = useState(null);
  const [dealId,      setDealId]      = useState(null);
  const [feeAmount,   setFeeAmount]   = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [feePaid,     setFeePaid]     = useState(false);
  const [inputAmt,    setInputAmt]    = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error,       setError]       = useState("");

  useEffect(() => {
    if (!otherName) { setStatus(null); return; }
    setStatus(null);
    setMyAmount(null);
    setTheirAmount(null);
    setCheckoutUrl(null);
    setInputAmt("");
    setError("");
    // When convo is provided the parent (MessagesPage) drives loading so it can
    // start deal status and messages in the same effect, truly in parallel.
    if (!convo) loadStatus();
  }, [otherName]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadStatus() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const body = { action: "get-deal-confirmation", other_name: otherName };
    if (convo?.builder_id && convo?.lender_id) {
      body.builder_id = convo.builder_id;
      body.lender_id  = convo.lender_id;
    }
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(body),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.status === "no_connection") { setStatus("none"); return; }
    setStatus(d.status || "none");
    setMyAmount(d.my_amount);
    setTheirAmount(d.their_amount);
    setDealId(d.deal_id);
    setFeePaid(d.finder_fee_paid || false);
    setFeeAmount(d.my_amount ? Math.round(Number(d.my_amount) * 0.01 * 100) / 100 : null);
    setCheckoutUrl(d.checkout_url || null);
  }

  async function handleGetCheckout(currentDealId) {
    const id = currentDealId || dealId;
    if (!id) return;
    setCheckoutLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setCheckoutLoading(false); return; }
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "get-checkout-url", deal_id: id }),
    });
    const d = await res.json().catch(() => ({}));
    setCheckoutLoading(false);
    if (d.paid) { setFeePaid(true); return; }
    if (d.checkout_url) {
      setCheckoutUrl(d.checkout_url);
      window.location.href = d.checkout_url;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const amt = parseFloat(inputAmt.replace(/,/g, ""));
    if (!amt || amt < 100) { setError("Please enter a valid amount (minimum £100)"); return; }
    setSubmitting(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "confirm-deal-amount", other_name: otherName, amount: amt }),
    });
    const d = await res.json().catch(() => ({}));
    setSubmitting(false);
    if (!res.ok) { setError(d.error || "Failed to submit. Please try again."); return; }
    setStatus(d.status);
    setMyAmount(d.my_amount || amt);
    setTheirAmount(d.their_amount || null);
    setDealId(d.deal_id || null);
    setFeePaid(d.finder_fee_paid || false);
    if (d.agreed_amount) setFeeAmount(Math.round(Number(d.agreed_amount) * 0.01 * 100) / 100);
    if (d.checkout_url) setCheckoutUrl(d.checkout_url);
  }

  return { status, myAmount, theirAmount, dealId, feeAmount, checkoutUrl, feePaid,
           checkoutLoading, inputAmt, setInputAmt, submitting, error,
           loadStatus, handleGetCheckout, handleSubmit };
}

// Mismatch banner — rendered as a separate flexShrink:0 element above the message list in DM chat.
function DealMismatchBanner({ dc, otherName }) {
  const { status, myAmount, theirAmount, inputAmt, setInputAmt, submitting, error, handleSubmit } = dc;
  if (status !== "mismatch") return null;
  return (
    <div style={{ padding: "12px 14px", background: "#FEF2F2", border: "0.5px solid #FCA5A5", borderRadius: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#991B1B", marginBottom: 4 }}>Amounts don't match</div>
      <div style={{ fontSize: 12, color: "#7F1D1D", marginBottom: 10 }}>
        Your amount: £{Number(myAmount || 0).toLocaleString()} · Their amount: £{Number(theirAmount || 0).toLocaleString()}
        <br/>Please discuss with {otherName} and re-submit.
      </div>
      <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          type="text" inputMode="numeric" value={fmtInput(inputAmt)} onChange={e => setInputAmt(parseInput(e.target.value))}
          placeholder="New amount (£)" required
          style={{ height: 36, border: "0.5px solid #FCA5A5", borderRadius: 8, padding: "0 10px", fontSize: 13, width: 160 }}
        />
        <button type="submit" disabled={submitting}
          style={{ padding: "0 16px", height: 36, background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: submitting ? "default" : "pointer" }}>
          {submitting ? "Saving…" : "Re-submit"}
        </button>
      </form>
      {error && <div style={{ fontSize: 12, color: "#991B1B", marginTop: 6 }}>{error}</div>}
    </div>
  );
}

function DealConfirmWidget({ user, otherName, otherRole, dc: externalDc, hideMismatch }) {
  const internalDc = useDealConfirm(externalDc ? null : user, externalDc ? null : otherName);
  const dc = externalDc || internalDc;
  const { status, myAmount, theirAmount, feeAmount, checkoutUrl, feePaid,
          checkoutLoading, inputAmt, setInputAmt, submitting, error,
          handleGetCheckout, handleSubmit, dealId: dcDealId } = dc;

  if (status === null) return null;
  if (hideMismatch && status === "mismatch") return null;

  const role = user?.user_metadata?.role;

  // Already paid — project tracker unlocked
  if (feePaid || status === "paid") {
    return (
      <div style={{ marginTop: 10, padding: "10px 14px", background: "#E1F5EE", borderRadius: 10, fontSize: 12, color: "#0F6E56", fontWeight: 500 }}>
        ✓ Finder's fee paid — Project Tracker & Deal Room unlocked
      </div>
    );
  }

  // Confirmed + builder can pay
  if (status === "confirmed" && role === "builder" && (checkoutUrl || feeAmount)) {
    return (
      <div style={{ marginTop: 10, padding: "12px 14px", background: "#FFF7ED", border: "0.5px solid #FCD34D", borderRadius: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#92400E", marginBottom: 6 }}>
          Deal confirmed — finder's fee due
        </div>
        <div style={{ fontSize: 12, color: "#78350F", marginBottom: 10 }}>
          LenderBuild finder's fee: 1% of £{Number(myAmount || 0).toLocaleString()} = <strong>£{Number(feeAmount || 0).toLocaleString()}</strong>
        </div>
        {checkoutUrl ? (
          <button
            onClick={() => { window.location.href = checkoutUrl; }}
            style={{ padding: "8px 18px", background: "#F59E0B", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", minHeight: 36 }}
          >
            Pay finder's fee →
          </button>
        ) : (
          <button
            onClick={() => handleGetCheckout(dcDealId)}
            disabled={checkoutLoading}
            style={{ padding: "8px 18px", background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: checkoutLoading ? "default" : "pointer", minHeight: 36 }}
          >
            {checkoutLoading ? "Loading…" : "Pay finder's fee"}
          </button>
        )}
      </div>
    );
  }

  // Confirmed — lender view (waiting for builder to pay)
  if (status === "confirmed" && role === "lender") {
    return (
      <div style={{ marginTop: 10, padding: "10px 14px", background: "#EBF2FF", borderRadius: 10, fontSize: 12, color: "#1E3A5F" }}>
        ✓ Deal confirmed for £{Number(myAmount || 0).toLocaleString()} — waiting for lender to pay the finder's fee
      </div>
    );
  }

  // Mismatch
  if (status === "mismatch") {
    return (
      <div style={{ marginTop: 10, padding: "12px 14px", background: "#FEF2F2", border: "0.5px solid #FCA5A5", borderRadius: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#991B1B", marginBottom: 4 }}>Amounts don't match</div>
        <div style={{ fontSize: 12, color: "#7F1D1D", marginBottom: 10 }}>
          Your amount: £{Number(myAmount || 0).toLocaleString()} · Their amount: £{Number(theirAmount || 0).toLocaleString()}
          <br/>Please discuss with {otherName} and re-submit.
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            type="text" inputMode="numeric" value={fmtInput(inputAmt)} onChange={e => setInputAmt(parseInput(e.target.value))}
            placeholder="New amount (£)" required
            style={{ height: 36, border: "0.5px solid #FCA5A5", borderRadius: 8, padding: "0 10px", fontSize: 13, width: 160 }}
          />
          <button type="submit" disabled={submitting}
            style={{ padding: "0 16px", height: 36, background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: submitting ? "default" : "pointer" }}>
            {submitting ? "Saving…" : "Re-submit"}
          </button>
        </form>
        {error && <div style={{ fontSize: 12, color: "#991B1B", marginTop: 6 }}>{error}</div>}
      </div>
    );
  }

  // Waiting for other party
  if (status === "waiting") {
    return (
      <div style={{ marginTop: 10, padding: "10px 14px", background: "#F5F3FF", border: "0.5px solid #C4B5FD", borderRadius: 10, fontSize: 12, color: "#4C1D95" }}>
        ✓ Your amount (£{Number(myAmount || 0).toLocaleString()}) submitted — waiting for {otherName} to confirm theirs
      </div>
    );
  }

  // None — show the confirmation form (large, prominent)
  return (
    <div style={{ marginTop: 10, padding: "16px 18px", background: "#fff", border: "2px solid #1E3A5F", borderRadius: 12, boxShadow: "0 2px 12px rgba(30,58,95,0.08)" }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#1E3A5F", marginBottom: 4 }}>
        Confirm your deal amount with {otherName}
      </div>
      <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14, lineHeight: 1.5 }}>
        Have you verbally agreed on a deal? Enter the total amount — both of you must confirm the same figure independently. Once matched, your Project Tracker unlocks automatically.
      </div>
      <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "stretch" }}>
        <div style={{ position: "relative", flex: "1 1 180px" }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 15, color: "#64748B", fontWeight: 500 }}>£</span>
          <input
            type="text" inputMode="numeric" value={fmtInput(inputAmt)} onChange={e => setInputAmt(parseInput(e.target.value))}
            placeholder="e.g. 250,000" required
            style={{ width: "100%", height: 48, border: "1.5px solid #1E3A5F", borderRadius: 8, padding: "0 12px 0 28px", fontSize: 16, fontWeight: 500, boxSizing: "border-box" }}
          />
        </div>
        <button type="submit" disabled={submitting}
          style={{ flex: "0 0 auto", padding: "0 22px", height: 48, background: submitting ? "#aaa" : "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer" }}>
          {submitting ? "Confirming…" : "Confirm deal amount"}
        </button>
      </form>
      {error && <div style={{ fontSize: 12, color: "#DC2626", marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ─── POSTS FEED PAGE ─────────────────────────────────────────────────────────

const CATEGORY_META = {
  "looking-for-lender":  { label: "Looking for funding",       bg: "#EBF2FF", text: "#1E3A5F" },
  "looking-for-builder": { label: "Available to lend",         bg: "#E1F5EE", text: "#0F6E56" },
  "project-update":      { label: "Project update",            bg: "#FFF7ED", text: "#C2410C" },
  "question":            { label: "Question",                  bg: "#F5F3FF", text: "#6D28D9" },
  "general":             { label: "General",                   bg: "#F1EFE8", text: "#5F5E5A" },
  "group-funding":       { label: "Group Funding Opportunity", bg: "#FDF4FF", text: "#7E22CE" },
};

function PostCard({ post, isOwn, onView, onEdit, onDelete, user, onConnect, onMessage, onFundPost }) {
  const cat = CATEGORY_META[post.category] || CATEGORY_META.general;
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [removing,         setRemoving]         = useState(false);
  const preview = post.body.length > 180 ? post.body.slice(0, 180).trimEnd() + "…" : post.body;

  const viewerRole   = user?.user_metadata?.role;
  const showMessage  = user && !isOwn && viewerRole && post.author_role && viewerRole !== post.author_role;

  async function handleDelete(e) {
    e.stopPropagation();
    if (!confirmingDelete) { setConfirmingDelete(true); return; }
    setRemoving(true);
    setTimeout(() => onDelete(post.id), 300);
  }

  async function handleMessage(e) {
    e.stopPropagation();
    onMessage && onMessage(post);
  }

  return (
    <div
      onClick={removing ? undefined : onView}
      style={{
        background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem",
        marginBottom: removing ? 0 : 12, cursor: removing ? "default" : "pointer",
        opacity: removing ? 0 : 1,
        maxHeight: removing ? 0 : 1000,
        overflow: "hidden",
        transition: "opacity 0.3s ease, max-height 0.3s ease, margin-bottom 0.3s ease",
      }}
      onMouseEnter={e => { if (!removing) e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; }}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: "50%", background: "#EBF2FF", color: "#1E3A5F",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0,
          }}>
            {(post.author_name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{post.author_name}</div>
            <div style={{ fontSize: 11, color: "#64748B", textTransform: "capitalize" }}>{post.author_role}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ background: cat.bg, color: cat.text, fontSize: 11, padding: "3px 9px", borderRadius: 20, fontWeight: 500 }}>
            {cat.label}
          </span>
          <span style={{ fontSize: 11, color: "#aaa" }}>
            {new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {(post.location_town || post.property_type) && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 5 }}>
              {post.location_town && <span style={{ fontSize: 11, background: "#EBF2FF", color: "#1E3A5F", borderRadius: 20, padding: "2px 8px", fontWeight: 500 }}>{post.location_town}</span>}
              {post.property_type && <span style={{ fontSize: 11, background: "#F0F4FF", color: "#1E3A5F", borderRadius: 20, padding: "2px 8px", fontWeight: 500, textTransform: "capitalize" }}>{post.property_type.replace(/_/g, " ")}</span>}
            </div>
          )}
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>{post.title}</div>
          {(post.funding_amount || post.funding_needed) && (
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--accent,#1E3A5F)", marginBottom: 5 }}>
              £{Number(post.funding_amount || post.funding_needed).toLocaleString("en-GB")}
            </div>
          )}
          <p style={{ fontSize: 13, color: "#555", lineHeight: 1.65, margin: 0 }}>{preview}</p>
        </div>
        {post.photo_urls?.[0] && (
          <img
            src={post.photo_urls[0]} alt=""
            style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, flexShrink: 0, border: "0.5px solid #e0e0e0" }}
          />
        )}
      </div>

      {isOwn ? (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "0.5px solid #f0f0f0", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={e => { e.stopPropagation(); onEdit(post); }}
            style={{ background: "transparent", color: "#333", border: "0.5px solid #ccc", padding: "5px 12px", minHeight: 44, borderRadius: 8, fontSize: 12, cursor: "pointer" }}
          >
            Edit
          </button>
          {confirmingDelete ? (
            <>
              <span style={{ fontSize: 12, color: "#64748B", alignSelf: "center" }}>Are you sure you want to delete this post?</span>
              <button
                onClick={handleDelete}
                style={{ background: "#DC2626", color: "#fff", border: "none", padding: "5px 12px", minHeight: 44, borderRadius: 8, fontSize: 12, cursor: "pointer" }}
              >
                Yes, delete
              </button>
              <button
                onClick={e => { e.stopPropagation(); setConfirmingDelete(false); }}
                style={{ background: "transparent", color: "#555", border: "0.5px solid #ccc", padding: "5px 10px", minHeight: 44, borderRadius: 8, fontSize: 12, cursor: "pointer" }}
              >
                Keep
              </button>
            </>
          ) : (
            <button
              onClick={handleDelete}
              style={{ background: "transparent", color: "#993C1D", border: "0.5px solid #F5C9BB", padding: "5px 12px", minHeight: 44, borderRadius: 8, fontSize: 12, cursor: "pointer" }}
            >
              Delete
            </button>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#3B82F6", fontWeight: 500 }}>Read more →</span>
          {post.category === "group-funding" && onFundPost && user?.user_metadata?.role === "lender" && (
            <button
              onClick={e => { e.stopPropagation(); onFundPost(post); }}
              style={{ marginLeft: "auto", padding: "6px 16px", minHeight: 36, borderRadius: 8, border: "none", background: "#7E22CE", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              Fund this project
            </button>
          )}
          {post.category !== "group-funding" && showMessage && (
            <button
              onClick={handleMessage}
              style={{ marginLeft: "auto", padding: "6px 14px", minHeight: 36, borderRadius: 8, border: "none", background: "#1E3A5F", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              Message
            </button>
          )}
          {!user && post.author_role && post.category !== "group-funding" && (
            <button
              onClick={e => { e.stopPropagation(); onConnect && onConnect(post); }}
              style={{ marginLeft: "auto", padding: "6px 14px", minHeight: 36, borderRadius: 8, border: "none", background: "#3B82F6", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              {post.author_role === "builder" ? "Message this builder" : "Message this lender"}
            </button>
          )}
          {!user && post.category === "group-funding" && (
            <button
              onClick={e => { e.stopPropagation(); onConnect && onConnect(post); }}
              style={{ marginLeft: "auto", padding: "6px 16px", minHeight: 36, borderRadius: 8, border: "none", background: "#7E22CE", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              Fund this project
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PostDetailPage({ post: initialPost, user, setPage, onMessage, onBack, onEdit, onDelete }) {
  const [post,           setPost]          = useState(initialPost);
  const cat                                = CATEGORY_META[post.category] || CATEGORY_META.general;
  const isOwn                              = user && post.author_id === user.id;
  const [msgStatus,      setMsgStatus]     = useState("idle");
  const [existingConvId, setExistingConvId] = useState(null);
  const [confirmDel,     setConfirmDel]    = useState(false);
  const [deleting,       setDeleting]      = useState(false);
  const [liked,          setLiked]         = useState(!!post.user_has_interest);
  const [likeCount,      setLikeCount]     = useState(post.interest_count || 0);
  const [comments,       setComments]      = useState(post.comments || []);
  const [commentText,    setCommentText]   = useState("");
  const [commentLoading, setCommentLoading]= useState(false);
  const [copied,         setCopied]        = useState(false);
  const [relatedPosts,   setRelatedPosts]  = useState([]);
  const [fundAmount,     setFundAmount]    = useState("");
  const [fundLoading,    setFundLoading]   = useState(false);
  const [fundError,      setFundError]     = useState("");
  const [showFundForm,   setShowFundForm]  = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [lightboxIndex,  setLightboxIndex] = useState(null);

  const viewerRole = user?.user_metadata?.role;
  const showMessage = user && !isOwn && viewerRole && post.author_role && viewerRole !== post.author_role;

  // Scroll to top whenever this post detail mounts or the post changes
  useEffect(() => { scrollMainToTop(); }, [post.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const headers = {};
      if (session) headers.Authorization = `Bearer ${session.access_token}`;
      fetch("/api/posts", { headers })
        .then(r => r.json())
        .then(json => {
          const all = (json.posts || []).filter(p => p.id !== post.id);
          const sameCat = all.filter(p => p.category === post.category);
          setRelatedPosts((sameCat.length >= 2 ? sameCat : all).slice(0, 3));
        })
        .catch(() => {});
    });
  }, [post.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for an existing conversation with this post's author
  useEffect(() => {
    if (!user || !post.author_id || isOwn) return;
    supabase
      .from("conversations")
      .select("id")
      .or(
        `and(lender_id.eq.${user.id},builder_id.eq.${post.author_id}),and(lender_id.eq.${post.author_id},builder_id.eq.${user.id})`
      )
      .maybeSingle()
      .then(({ data }) => { if (data?.id) setExistingConvId(data.id); });
  }, [user?.id, post.author_id, isOwn]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleLike() {
    if (!user) { setPage("auth"); return; }
    const newLiked = !liked;
    setLiked(newLiked);
    setLikeCount(c => c + (newLiked ? 1 : -1));
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    fetch("/api/posts", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "interest", post_id: post.id, author_id: post.author_id }),
    }).catch(() => {});
  }

  async function handleComment(e) {
    e.preventDefault();
    if (!user) { setPage("auth"); return; }
    if (!commentText.trim()) return;
    setCommentLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/posts", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "comment", post_id: post.id, author_id: post.author_id, text: commentText.trim() }),
    });
    const json = await res.json();
    setCommentLoading(false);
    if (res.ok && json.comment) {
      setComments(prev => [...prev, json.comment]);
      setCommentText("");
    }
  }

  function handleCopyLink() {
    const url = `${window.location.origin}/?post=${post.id}`;
    const copyFn = () => { setCopied(true); setTimeout(() => setCopied(false), 2500); };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(copyFn).catch(copyFn);
    } else {
      copyFn();
    }
  }

  async function handleMessage() {
    if (!user) { setPage("auth"); return; }
    if (existingConvId) { onMessage(existingConvId); return; }
    setMsgStatus("loading");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/notify-message", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ other_name: post.author_name, other_role: post.author_role }),
    });
    if (res.ok) {
      const { conversation } = await res.json();
      setMsgStatus("idle");
      setExistingConvId(conversation.id);
      onMessage(conversation.id);
    } else {
      setMsgStatus("error");
      setTimeout(() => setMsgStatus("idle"), 3000);
    }
  }

  async function handleFundSubmit(e) {
    e.preventDefault();
    if (!user) { setPage("auth"); return; }
    const amount = Number(fundAmount);
    if (!amount || amount <= 0) { setFundError("Please enter a valid amount"); return; }
    setFundLoading(true);
    setFundError("");
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 30000);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) { setFundError("Please log in to continue"); return; }
      const res = await fetch("/api/project-listings", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: "funding-room-join-post", post_id: post.id, post_author_id: post.author_id, amount }),
        signal: abort.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setFundError(json.error || "Failed to join. Please try again."); return; }
      setShowFundForm(false);
      if (json.room_id) setPage("funding-room", json.room_id);
    } catch (err) {
      if (err.name === "AbortError") {
        setFundError("Request timed out. Please try again.");
      } else {
        setFundError("Something went wrong. Please try again.");
      }
    } finally {
      clearTimeout(timer);
      setFundLoading(false);
    }
  }

  const btnBase = { border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", minHeight: 38 };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const cardStyle = {
    background: "var(--bg-card,#fff)",
    border: "1px solid var(--border,#e0e0e0)",
    borderRadius: 12,
    padding: isMobile ? 20 : 32,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "1.5rem 1.25rem", minHeight: "calc(100vh - 56px)" }}>
      <button
        onClick={() => onBack ? onBack() : setPage("posts")}
        style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 13, color: "#3B82F6", padding: 0, marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: 4 }}
      >
        ← Back to posts
      </button>

      <div style={cardStyle}>

      {/* Category + date */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem", flexWrap: "wrap" }}>
        <span style={{ background: cat.bg, color: cat.text, fontSize: 12, padding: "4px 10px", borderRadius: 20, fontWeight: 500 }}>
          {cat.label}
        </span>
        <span style={{ fontSize: 12, color: "#aaa" }}>
          {new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
        </span>
      </div>

      {/* Title + own-post actions */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: "1.25rem" }}>
        <h1 style={{ fontSize: 24, fontWeight: 500, margin: 0, fontFamily: "'Georgia', serif", lineHeight: 1.3, flex: 1 }}>
          {post.title}
        </h1>
        {isOwn && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0, paddingTop: 4 }}>
            <button
              onClick={() => onEdit && onEdit(post)}
              style={{ padding: "7px 14px", minHeight: 36, borderRadius: 8, border: "0.5px solid #ccc", background: "#fff", color: "#333", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
            >Edit</button>
            {confirmDel ? (
              <>
                <button
                  onClick={async () => {
                    setDeleting(true);
                    if (onDelete) await onDelete(post.id);
                    setDeleting(false);
                  }}
                  disabled={deleting}
                  style={{ padding: "7px 14px", minHeight: 36, borderRadius: 8, border: "none", background: "#DC2626", color: "#fff", fontSize: 13, fontWeight: 600, cursor: deleting ? "default" : "pointer" }}
                >{deleting ? "Deleting…" : "Confirm delete"}</button>
                <button
                  onClick={() => setConfirmDel(false)}
                  style={{ padding: "7px 12px", minHeight: 36, borderRadius: 8, border: "0.5px solid #ccc", background: "#fff", color: "#555", fontSize: 13, cursor: "pointer" }}
                >Cancel</button>
              </>
            ) : (
              <button
                onClick={() => setConfirmDel(true)}
                style={{ padding: "7px 14px", minHeight: 36, borderRadius: 8, border: "0.5px solid #F5C9BB", background: "#fff", color: "#993C1D", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >Delete</button>
            )}
          </div>
        )}
      </div>

      {/* Author */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1.5rem", paddingBottom: "1.5rem", borderBottom: "0.5px solid #f0f0f0" }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%", background: "#EBF2FF", color: "#1E3A5F",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 600, flexShrink: 0,
        }}>
          {(post.author_name || "?").charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{post.author_name}</div>
          <div style={{ fontSize: 12, color: "#64748B", textTransform: "capitalize", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {post.author_role}
            {post.author_verification_status === "verified" ? (
              <span title={post.author_company_name ? `Registered business: ${post.author_company_name}` : "Verified with Companies House"} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#166534", color: "#fff", cursor: "help", display: "inline-flex", alignItems: "center", gap: 3 }}>
                ✓ Verified{post.author_company_name ? ` — ${post.author_company_name}` : ""}
              </span>
            ) : post.author_verified ? (
              <span style={{ color: "#16A34A", fontWeight: 600 }}>✓ Verified</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ fontSize: 15, color: "#333", lineHeight: 1.8, whiteSpace: "pre-wrap", marginBottom: "1.25rem" }}>
        {post.body}
      </div>

      {/* Project details grid */}
      {(post.funding_amount || post.location_postcode || post.location_town || post.property_type || post.project_type || post.term_months || post.expected_gdv || post.exit_strategy || post.planning_status) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10, marginBottom: "1.25rem" }}>
          {post.funding_amount && (
            <div style={{ background: "var(--accent-bg,#EBF2FF)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Funding sought</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--accent,#1E3A5F)" }}>£{Number(post.funding_amount).toLocaleString("en-GB")}</div>
            </div>
          )}
          {post.location_postcode && (
            <div style={{ background: "var(--bg-card,#F8FAFC)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Postcode</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{post.location_postcode}</div>
            </div>
          )}
          {post.location_town && (
            <div style={{ background: "var(--bg-card,#F8FAFC)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Town / City</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{post.location_town}</div>
            </div>
          )}
          {post.property_type && (
            <div style={{ background: "var(--bg-card,#F8FAFC)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Property type</div>
              <div style={{ fontSize: 14, fontWeight: 600, textTransform: "capitalize" }}>{post.property_type.replace(/_/g, " ")}</div>
            </div>
          )}
          {post.project_type && (
            <div style={{ background: "var(--bg-card,#F8FAFC)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Project type</div>
              <div style={{ fontSize: 14, fontWeight: 600, textTransform: "capitalize" }}>{post.project_type.replace(/_/g, " ")}</div>
            </div>
          )}
          {post.term_months && (
            <div style={{ background: "var(--bg-card,#F8FAFC)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Loan term</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{post.term_months} months</div>
            </div>
          )}
          {post.expected_gdv && (
            <div style={{ background: "var(--bg-card,#F8FAFC)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Expected GDV</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>£{Number(post.expected_gdv).toLocaleString("en-GB")}</div>
            </div>
          )}
          {post.exit_strategy && (
            <div style={{ background: "var(--bg-card,#F8FAFC)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Exit strategy</div>
              <div style={{ fontSize: 14, fontWeight: 600, textTransform: "capitalize" }}>{post.exit_strategy.replace(/_/g, " ")}</div>
            </div>
          )}
          {post.planning_status && (
            <div style={{ background: "var(--bg-card,#F8FAFC)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted,#64748B)", marginBottom: 2 }}>Planning</div>
              <div style={{ fontSize: 14, fontWeight: 600, textTransform: "capitalize" }}>{post.planning_status.replace(/_/g, " ")}</div>
            </div>
          )}
        </div>
      )}

      {/* Photo strip */}
      {post.photo_urls?.length > 0 && (
        <div style={{ overflowX: "auto", display: "flex", gap: 10, paddingBottom: 4, marginBottom: "1.25rem" }}>
          {post.photo_urls.map((url, i) => (
            <img
              key={i} src={url} alt=""
              onClick={() => setLightboxIndex(i)}
              style={{ height: 160, minWidth: 160, maxWidth: 240, objectFit: "cover", borderRadius: 8, cursor: "zoom-in", flexShrink: 0, border: "0.5px solid var(--border,#e0e0e0)" }}
            />
          ))}
        </div>
      )}

      {/* Photo lightbox */}
      {lightboxIndex !== null && post.photo_urls?.[lightboxIndex] && (
        <div
          onClick={() => setLightboxIndex(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <button onClick={() => setLightboxIndex(null)} aria-label="Close" style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          {lightboxIndex > 0 && (
            <button onClick={e => { e.stopPropagation(); setLightboxIndex(i => i - 1); }} aria-label="Previous" style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
          )}
          {lightboxIndex < post.photo_urls.length - 1 && (
            <button onClick={e => { e.stopPropagation(); setLightboxIndex(i => i + 1); }} aria-label="Next" style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
          )}
          <img
            src={post.photo_urls[lightboxIndex]} alt=""
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 12, boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}
          />
          {post.photo_urls.length > 1 && (
            <div style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.7)", fontSize: 13 }}>{lightboxIndex + 1} / {post.photo_urls.length}</div>
          )}
        </div>
      )}

      {/* Group Funding Info Panel */}
      {post.category === "group-funding" && (
        <div style={{ background: "#FDF4FF", border: "1.5px solid #D8B4FE", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#7E22CE", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Group Funding Opportunity</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: post.funding_needed ? 14 : 0 }}>
            {post.funding_needed && (
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 2 }}>Funding target</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#7E22CE" }}>£{Number(post.funding_needed).toLocaleString("en-GB")}</div>
              </div>
            )}
            {post.return_type && (
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 2 }}>Return</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#1E3A5F" }}>
                  {post.return_value ? `${post.return_value}%` : "—"}{" "}
                  <span style={{ fontSize: 12, fontWeight: 400, color: "#64748B" }}>
                    {post.return_type === "fixed_interest" ? "fixed interest" : post.return_type === "revenue_share" ? "revenue share" : "equity stake"}
                  </span>
                </div>
              </div>
            )}
            {post.timeline && (
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 2 }}>Timeline</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F" }}>{post.timeline}</div>
              </div>
            )}
            {post.location && (
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 2 }}>Location</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F" }}>{post.location}</div>
              </div>
            )}
          </div>
          {!isOwn && user?.user_metadata?.role === "lender" && (
            !showFundForm ? (
              <button
                onClick={() => setShowFundForm(true)}
                style={{ width: "100%", height: 46, background: "#7E22CE", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 4 }}
              >
                I want to fund this project
              </button>
            ) : (
              <form onSubmit={handleFundSubmit} style={{ marginTop: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 6 }}>How much would you like to commit? (£)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text" inputMode="numeric" required
                    value={fmtInput(fundAmount)} onChange={e => setFundAmount(parseInput(e.target.value))}
                    placeholder="e.g. 25,000"
                    style={{ flex: 1, height: 46, border: "1.5px solid #7E22CE", borderRadius: 8, padding: "0 14px", fontSize: 15, fontWeight: 500, boxSizing: "border-box", outline: "none" }}
                  />
                  <button type="submit" disabled={fundLoading} style={{ padding: "0 20px", height: 46, background: "#7E22CE", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: fundLoading ? "default" : "pointer", whiteSpace: "nowrap" }}>
                    {fundLoading ? "Joining…" : "Commit & Enter Room"}
                  </button>
                  <button type="button" onClick={() => setShowFundForm(false)} style={{ padding: "0 14px", height: 46, background: "transparent", color: "#555", border: "1px solid #ccc", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
                {fundError && <div style={{ color: "#DC2626", fontSize: 13, marginTop: 8 }}>{fundError}</div>}
              </form>
            )
          )}
          {!user && (
            <button
              onClick={() => setPage("auth")}
              style={{ width: "100%", height: 46, background: "#7E22CE", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 4 }}
            >
              Log in to fund this project
            </button>
          )}
        </div>
      )}

      {/* Stats line */}
      <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: "1.25rem" }}>
        {post.view_count || 0} view{post.view_count !== 1 ? "s" : ""} · {likeCount} like{likeCount !== 1 ? "s" : ""} · {comments.length} comment{comments.length !== 1 ? "s" : ""}
      </div>

      {/* Action bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: "0.75rem", paddingBottom: "1.5rem", borderTop: "0.5px solid #f0f0f0", borderBottom: "0.5px solid #f0f0f0", flexWrap: "wrap" }}>
        {/* Like */}
        <button
          onClick={handleLike}
          style={{ ...btnBase, background: liked ? "#FEF2F2" : "#F8FAFC", color: liked ? "#DC2626" : "#64748B", border: liked ? "0.5px solid #FECACA" : "0.5px solid #e0e0e0", fontWeight: liked ? 600 : 500 }}
        >
          {liked ? <><Icon name="heart-fill" size={14} style={{color:"#DC2626",marginRight:3}} /></> : <><Icon name="heart" size={14} style={{marginRight:3}} /></>}{likeCount > 0 ? likeCount : "Like"}
        </button>

        {/* Share to group */}
        {user && (
          <button
            onClick={() => setShowShareModal(true)}
            style={{ ...btnBase, background: "#F8FAFC", color: "#64748B", border: "0.5px solid #e0e0e0" }}
          >Share to group</button>
        )}

        {/* Copy link */}
        <button
          onClick={handleCopyLink}
          style={{ ...btnBase, background: copied ? "#F0FDF4" : "#F8FAFC", color: copied ? "#16A34A" : "#64748B", border: copied ? "0.5px solid #BBF7D0" : "0.5px solid #e0e0e0" }}
        >
          {copied ? "✓ Copied" : "Copy link"}
        </button>

        {showShareModal && user && (
          <ShareToGroupModal post={post} user={user} onClose={() => setShowShareModal(false)} setPage={setPage} />
        )}

        {/* Message / Open chat */}
        {!isOwn && showMessage && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={handleMessage}
              disabled={msgStatus === "loading"}
              style={{ ...btnBase, background: msgStatus === "loading" ? "var(--text-muted,#aaa)" : "var(--accent,#3B82F6)", color: "#fff", cursor: msgStatus === "loading" ? "default" : "pointer" }}
            >
              {msgStatus === "loading"
                ? "Opening…"
                : existingConvId
                ? `Open chat with ${post.author_name?.split(" ")[0] || post.author_name}`
                : `Message ${post.author_name?.split(" ")[0] || post.author_name}`}
            </button>
          </div>
        )}
        {msgStatus === "error" && (
          <span style={{ fontSize: 12, color: "#993C1D", width: "100%" }}>Couldn't start a conversation — please try again.</span>
        )}
        {!user && !isOwn && (
          <span style={{ fontSize: 12, color: "#64748B", marginLeft: "auto" }}>
            <button onClick={() => setPage("auth")} style={{ background: "none", border: "none", color: "#3B82F6", cursor: "pointer", fontSize: 12, fontWeight: 500, padding: 0 }}>Log in</button> to like, comment or message.
          </span>
        )}
      </div>

      </div>{/* end post card */}

      {/* Comments */}
      <div style={{ ...cardStyle, marginTop: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F", marginBottom: "1rem" }}>
          Comments {comments.length > 0 && <span style={{ color: "#64748B", fontWeight: 400, fontSize: 13 }}>({comments.length})</span>}
        </div>

        {user ? (
          <form onSubmit={handleComment} style={{ display: "flex", gap: 10, marginBottom: "1.5rem", alignItems: "flex-start" }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%", background: "#EBF2FF", color: "#1E3A5F",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0, marginTop: 2,
            }}>
              {(user.user_metadata?.name || user.email || "?").charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Write a comment…"
                rows={2}
                style={{ width: "100%", border: "0.5px solid #d0d0d0", borderRadius: 8, padding: "9px 12px", fontSize: 14, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                onFocus={e => e.target.style.borderColor = "#3B82F6"}
                onBlur={e => e.target.style.borderColor = "#d0d0d0"}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  disabled={commentLoading || !commentText.trim()}
                  style={{ padding: "7px 18px", minHeight: 36, borderRadius: 8, border: "none", background: commentText.trim() ? "#3B82F6" : "#e0e0e0", color: commentText.trim() ? "#fff" : "#aaa", fontSize: 13, fontWeight: 600, cursor: commentText.trim() && !commentLoading ? "pointer" : "default" }}
                >
                  {commentLoading ? "Posting…" : "Post"}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div style={{ background: "#F8FAFC", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "14px 16px", fontSize: 13, color: "#64748B", marginBottom: "1.5rem" }}>
            <button onClick={() => setPage("auth")} style={{ background: "none", border: "none", color: "#3B82F6", cursor: "pointer", fontSize: 13, fontWeight: 500, padding: 0 }}>Log in</button> to leave a comment.
          </div>
        )}

        {comments.length === 0 ? (
          <div style={{ fontSize: 13, color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0" }}>No comments yet. Be the first to reply.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {comments.map(c => (
              <div key={c.id} style={{ display: "flex", gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%", background: "#EBF2FF", color: "#1E3A5F",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0, marginTop: 2,
                }}>
                  {(c.author_name || "?").charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, background: "#F8FAFC", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{c.author_name}</span>
                    <span style={{ fontSize: 11, color: "#9CA3AF", textTransform: "capitalize" }}>{c.author_role}</span>
                    <span style={{ fontSize: 11, color: "#C0C8D2", marginLeft: "auto" }}>
                      {new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{c.text}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>{/* end comments card */}

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <div style={{ marginTop: "2.5rem", paddingTop: "1.5rem", borderTop: "0.5px solid #f0f0f0" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F", marginBottom: "1rem" }}>More posts</div>
          {relatedPosts.map(p => {
            const rc = CATEGORY_META[p.category] || CATEGORY_META.general;
            return (
              <div
                key={p.id}
                onClick={() => setPost(p)}
                style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: 10, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ background: rc.bg, color: rc.text, fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>{rc.label}</span>
                  <span style={{ fontSize: 11, color: "#aaa" }}>{p.author_name}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
                  {p.body.length > 100 ? p.body.slice(0, 100).trimEnd() + "…" : p.body}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const POST_CATEGORY_FILTERS = [
  { key: "all",                 label: "All" },
  { key: "looking-for-lender",  label: "Looking for funding" },
  { key: "looking-for-builder", label: "Available to lend" },
  { key: "group-funding",       label: "Group Funding" },
];

function PostsFeedPage({ user, setPage }) {
  const [posts,        setPosts]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [myLocation,   setMyLocation]   = useState("");
  const [toast,        setToast]        = useState("");
  const [fundModal,    setFundModal]    = useState(null); // post being funded
  const [fundAmount,   setFundAmount]   = useState("");
  const [fundLoading,  setFundLoading]  = useState(false);
  const [fundError,    setFundError]    = useState("");

  // Filters
  const [catFilter,    setCatFilter]    = useState("all");
  const [expOnly,      setExpOnly]      = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [myRegion,     setMyRegion]     = useState(false);
  const [showMine,     setShowMine]     = useState(false);
  const [sortBy,       setSortBy]       = useState("recent");

  useEffect(() => { loadPosts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("location").eq("id", user.id).maybeSingle()
      .then(({ data }) => setMyLocation(data?.location || ""));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPosts() {
    setLoading(true);
    try {
      const headers = {};
      const { data: { session } } = await supabase.auth.getSession();
      if (session) headers.Authorization = `Bearer ${session.access_token}`;
      const res  = await fetch("/api/posts", { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to load posts");
      const raw = json.posts || [];
      if (raw.length > 0) {
        const authorIds = [...new Set(raw.map(p => p.author_id))];
        const [{ data: profRows }, { data: bpRows }] = await Promise.all([
          supabase.from("profiles").select("id, location").in("id", authorIds),
          supabase.from("builder_profiles").select("user_id, projects_completed").in("user_id", authorIds),
        ]);
        const profMap = Object.fromEntries((profRows || []).map(r => [r.id, r]));
        const bpMap   = Object.fromEntries((bpRows  || []).map(r => [r.user_id, r]));
        setPosts(raw.map(p => ({
          ...p,
          author_location:           profMap[p.author_id]?.location || "",
          author_projects_completed: bpMap[p.author_id]?.projects_completed || 0,
        })));
      } else {
        setPosts([]);
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  async function handleView(post) {
    if (user && post.author_id !== user.id) {
      const key = `lb_pv_${post.id}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          fetch("/api/posts", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ action: "view", post_id: post.id, author_id: post.author_id }),
          }).catch(() => {});
        }
      }
    }
    setPage("post-detail", post);
  }

  async function handleFundPost(post) {
    if (!user) { setPage("auth"); return; }
    setFundModal(post);
    setFundAmount("");
    setFundError("");
  }

  async function submitFundPost(e) {
    e.preventDefault();
    if (!fundModal) return;
    const amount = Number(fundAmount);
    if (!amount || amount <= 0) { setFundError("Please enter a valid amount"); return; }
    setFundLoading(true);
    setFundError("");
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 30000);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) { setFundError("Please log in to continue"); return; }
      const res = await fetch("/api/project-listings", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          action: "funding-room-join-post",
          post_id: fundModal.id,
          post_author_id: fundModal.author_id,
          amount,
        }),
        signal: abort.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setFundError(json.error || "Failed to join. Please try again."); return; }
      setFundModal(null);
      setToast("You've committed to this project!");
      setTimeout(() => setToast(""), 4000);
      if (json.room_id) setPage("funding-room", json.room_id);
    } catch (err) {
      if (err.name === "AbortError") {
        setFundError("Request timed out. Please try again.");
      } else {
        setFundError("Something went wrong. Please try again.");
      }
    } finally {
      clearTimeout(timer);
      setFundLoading(false);
    }
  }

  const myReg = ukRegion(myLocation);

  const filtered = posts.filter(p => {
    if (catFilter !== "all" && p.category !== catFilter)                          return false;
    if (showMine   && !(user && p.author_id === user.id))                        return false;
    if (expOnly    && (p.author_projects_completed || 0) < 5)                    return false;
    if (verifiedOnly && !p.author_verified)                                       return false;
    if (myRegion   && myReg && ukRegion(p.author_location) !== myReg)            return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "viewed")    return (b.view_count || 0) - (a.view_count || 0);
    if (sortBy === "responses") return (b.interest_count || 0) - (a.interest_count || 0);
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const hasActiveFilters = catFilter !== "all" || expOnly || verifiedOnly || myRegion || showMine;

  function clearFilters() {
    setCatFilter("all"); setExpOnly(false); setVerifiedOnly(false); setMyRegion(false); setShowMine(false);
  }

  const qualifiers = [
    { key: "exp",      label: "Experienced only",   state: expOnly,      set: setExpOnly,      title: "5+ completed projects" },
    { key: "verified", label: "✓ Verified only",    state: verifiedOnly, set: setVerifiedOnly, title: "ID-verified users" },
    ...(user ? [
      { key: "region", label: "My region",          state: myRegion,     set: setMyRegion,     title: myReg ? `Posts from ${myReg}` : "Set your location in profile" },
      { key: "mine",   label: "My posts",           state: showMine,     set: setShowMine,     title: "Only your posts" },
    ] : []),
  ];

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 780, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Posts</h2>
        </div>
        {user ? (
          <button onClick={() => setPage("create-post")}
            style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "9px 18px", minHeight: 44, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            + New post
          </button>
        ) : (
          <button onClick={() => setPage("auth")}
            style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "9px 18px", minHeight: 44, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Log in to post
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "0.75rem 1rem", marginBottom: "0.875rem" }}>
        {/* Single horizontally scrollable row of all filter pills */}
        <div style={{ display: "flex", gap: 6, overflowX: "auto", whiteSpace: "nowrap", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingBottom: 2, marginBottom: 8 }}>
          {POST_CATEGORY_FILTERS.map(f => (
            <button key={f.key} onClick={() => setCatFilter(f.key)}
              style={{
                padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500,
                cursor: "pointer", border: "none", minHeight: 36, flexShrink: 0,
                background: catFilter === f.key ? "#1E3A5F" : "#F1F5F9",
                color:      catFilter === f.key ? "#fff"    : "#475569",
                transition: "background 0.15s",
              }}>
              {f.label}
              {f.key !== "all" && (
                <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.65 }}>
                  ({posts.filter(p => p.category === f.key).length})
                </span>
              )}
            </button>
          ))}
          {user && (
            <button
              onClick={() => setShowMine(v => !v)}
              style={{
                padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600,
                cursor: "pointer", minHeight: 36, flexShrink: 0,
                background: showMine ? "#3B82F6" : "#F1F5F9",
                color:      showMine ? "#fff"    : "#475569",
                border:     "none",
                transition: "background 0.15s",
              }}>
              My posts
              {showMine && posts.filter(p => p.author_id === user.id).length > 0 && (
                <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.8 }}>
                  ({posts.filter(p => p.author_id === user.id).length})
                </span>
              )}
            </button>
          )}
          {qualifiers.map(q => (
            <button key={q.key} onClick={() => q.set(v => !v)} title={q.title}
              style={{
                padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                cursor: "pointer", minHeight: 32, flexShrink: 0,
                background: q.state ? "#EBF2FF" : "transparent",
                color:      q.state ? "#1E3A5F" : "#64748B",
                border:     q.state ? "1.5px solid #3B82F6" : "1px solid #e0e0e0",
                transition: "all 0.15s",
              }}>
              {q.label}
            </button>
          ))}
        </div>
        {/* Sort control */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#64748B", whiteSpace: "nowrap" }}>Sort:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ height: 32, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 8px", fontSize: 12, background: "#fff", cursor: "pointer" }}>
            <option value="recent">Most recent</option>
            <option value="viewed">Most viewed</option>
            <option value="responses">Most responses</option>
          </select>
        </div>
      </div>

      {/* Results count / active filter badge */}
      {!loading && !error && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.75rem", minHeight: 24 }}>
          <span style={{
            fontSize: 13, fontWeight: hasActiveFilters ? 600 : 400,
            color: hasActiveFilters ? "#1E3A5F" : "#64748B",
            background: hasActiveFilters ? "#EBF2FF" : "transparent",
            padding: hasActiveFilters ? "3px 10px" : "0",
            borderRadius: 20,
          }}>
            {hasActiveFilters
              ? `Showing ${sorted.length} post${sorted.length !== 1 ? "s" : ""} matching your filters`
              : `${sorted.length} post${sorted.length !== 1 ? "s" : ""}`}
          </span>
          {hasActiveFilters && (
            <button onClick={clearFilters}
              style={{ fontSize: 12, color: "#64748B", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
              Clear filters
            </button>
          )}
        </div>
      )}

      <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 380px)" }}>
      {loading && [0,1,2].map(i => <SkeletonPostCard key={i} />)}
      {error   && <div style={{ background: "#FAECE7", color: "#993C1D", borderRadius: 8, padding: "12px 16px", fontSize: 13, marginBottom: "1rem" }}>{error}</div>}

      {!loading && !error && sorted.length === 0 && (
        <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="edit" size={32} /></div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>
            {hasActiveFilters ? "No posts match your filters" : "No posts yet"}
          </div>
          {hasActiveFilters
            ? <button onClick={clearFilters} style={{ background: "none", border: "none", color: "#3B82F6", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>Clear filters →</button>
            : <span style={{ fontSize: 13 }}>Be the first to share what you're looking for.</span>}
        </div>
      )}

      {/* Fund-this-project modal */}
      {fundModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "2rem", maxWidth: 440, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#7E22CE", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Group Funding Room</div>
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 6px", color: "#1E3A5F" }}>{fundModal.title}</h3>
            {fundModal.funding_needed && (
              <div style={{ fontSize: 13, color: "#64748B", marginBottom: 6 }}>
                Target: <strong>£{Number(fundModal.funding_needed).toLocaleString("en-GB")}</strong>
                {fundModal.return_type && ` · Return: ${fundModal.return_type === "fixed_interest" ? `${fundModal.return_value}% fixed interest` : fundModal.return_type === "revenue_share" ? `${fundModal.return_value}% revenue share` : `${fundModal.return_value}% equity`}`}
              </div>
            )}
            {fundModal.timeline && <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 16 }}>Timeline: {fundModal.timeline}</div>}
            <form onSubmit={submitFundPost}>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 6 }}>How much would you like to commit? (£)</label>
              <input
                type="text" inputMode="numeric" required
                value={fmtInput(fundAmount)} onChange={e => setFundAmount(parseInput(e.target.value))}
                placeholder="e.g. 25,000"
                style={{ width: "100%", height: 48, border: "1.5px solid #7E22CE", borderRadius: 8, padding: "0 14px", fontSize: 16, fontWeight: 500, boxSizing: "border-box", marginBottom: 12, outline: "none" }}
              />
              {fundError && <div style={{ color: "#DC2626", fontSize: 13, marginBottom: 10 }}>{fundError}</div>}
              <div style={{ display: "flex", gap: 10 }}>
                <button type="submit" disabled={fundLoading} style={{ flex: 1, height: 46, background: fundLoading ? "#9333EA" : "#7E22CE", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: fundLoading ? "default" : "pointer" }}>
                  {fundLoading ? "Joining…" : "Commit & Enter Room"}
                </button>
                <button type="button" onClick={() => setFundModal(null)} style={{ padding: "0 18px", height: 46, background: "transparent", color: "#555", border: "1px solid #ccc", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {sorted.map(p => (
        <PostCard
          key={p.id}
          post={p}
          isOwn={!!(user && p.author_id === user.id)}
          onView={() => handleView(p)}
          onEdit={post => setPage("edit-post", post)}
          user={user}
          onFundPost={handleFundPost}
          onConnect={async (post) => {
            if (!user) { setPage("auth"); return false; }
            const { data: { session } } = await supabase.auth.getSession();
            const viewerRole = user.user_metadata?.role;
            const body = viewerRole === "lender" && post.author_role === "builder"
              ? { builder_name: post.author_name }
              : { lender_name: post.author_name };
            const res = await fetch("/api/connect-request", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify(body),
            });
            if (res.ok) {
              await supabase.auth.refreshSession();
              return true;
            }
            return false;
          }}
          onMessage={async (post) => {
            if (!user) { setPage("auth"); return; }
            const { data: { session } } = await supabase.auth.getSession();
            const res = await fetch("/api/notify-message", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify({ other_name: post.author_name, other_role: post.author_role }),
            });
            if (res.ok) {
              const { conversation } = await res.json();
              setPage("messages", conversation?.id);
            }
          }}
          onDelete={async (id) => {
            const { data: { session } } = await supabase.auth.getSession();
            await fetch("/api/posts", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify({ action: "delete", post_id: id }),
            });
            setPosts(prev => prev.filter(p => p.id !== id));
            setToast("Post deleted");
            setTimeout(() => setToast(""), 3000);
          }}
        />
      ))}
      </div>{/* end scrollable card list */}

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
          background: "#16A34A", color: "#fff", padding: "11px 22px", borderRadius: 10,
          fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
          zIndex: 9999, pointerEvents: "none",
          animation: "fadeInUp 0.2s ease",
        }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
}

// ─── CREATE / EDIT POST PAGE ──────────────────────────────────────────────────

function CreateEditPostPage({ user, setPage, editPost = null, onSaved, onCancelBack }) {
  const isEdit = !!editPost;
  const [title,          setTitle]          = useState(editPost?.title          || "");
  const [body,           setBody]           = useState(editPost?.body           || "");
  const [category,       setCategory]       = useState(editPost?.category       || "looking-for-lender");
  const [fundingNeeded,    setFundingNeeded]    = useState(editPost?.funding_needed ? String(editPost.funding_needed) : "");
  const [location,         setPostLocation]     = useState(editPost?.location       || "");
  const [returnType,       setReturnType]       = useState(editPost?.return_type    || "fixed_interest");
  const [returnValue,      setReturnValue]      = useState(editPost?.return_value   ? String(editPost.return_value) : "");
  const [timeline,         setTimeline]         = useState(editPost?.timeline       || "");
  // Project detail fields
  const [locationPostcode, setLocationPostcode] = useState(editPost?.location_postcode || "");
  const [locationTown,     setLocationTown]     = useState(editPost?.location_town     || "");
  const [postcodeStatus,   setPostcodeStatus]   = useState(editPost?.location_postcode ? "valid" : "idle");
  const [postcodeError,    setPostcodeError]    = useState("");
  const postcodeTimerRef = useRef(null);
  const townAutoFilledRef = useRef(false);
  const [propertyType,     setPropertyType]     = useState(editPost?.property_type     || "residential");
  const [projectType,      setProjectType]      = useState(editPost?.project_type      || "new_build");
  const [fundingAmount,    setFundingAmount]    = useState(editPost?.funding_amount     ? String(editPost.funding_amount) : "");
  const [termMonths,       setTermMonths]       = useState(editPost?.term_months        ? String(editPost.term_months) : "");
  const [expectedGdv,      setExpectedGdv]      = useState(editPost?.expected_gdv       ? String(editPost.expected_gdv) : "");
  const [exitStrategy,     setExitStrategy]     = useState(editPost?.exit_strategy      || "sale");
  const [planningStatus,   setPlanningStatus]   = useState(editPost?.planning_status    || "not_applied");
  // Photo upload
  const [photoFiles,       setPhotoFiles]       = useState([]);
  const [photoFileUrls,    setPhotoFileUrls]    = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState(editPost?.photo_urls || []);
  const [photoUploading,   setPhotoUploading]   = useState(false);
  const [photoError,       setPhotoError]       = useState("");

  useEffect(() => {
    const urls = photoFiles.map(f => URL.createObjectURL(f));
    setPhotoFileUrls(urls);
    return () => { urls.forEach(u => URL.revokeObjectURL(u)); };
  }, [photoFiles]);
  const [showCalc,         setShowCalc]         = useState(false);
  const [calcType,       setCalcType]       = useState("New build residential");
  const [calcRegion,     setCalcRegion]     = useState("South East");
  const [calcSize,       setCalcSize]       = useState("");
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState("");
  const [toast,          setToast]          = useState(null);
  const [visible,        setVisible]        = useState(false);

  const isGroupFunding    = category === "group-funding";
  const showProjectFields = category === "looking-for-lender" || category === "group-funding";

  // Inline build calculator result
  const calcSqm = parseFloat(calcSize) || 0;
  const calcTypeData = BUILD_COST_DATA?.types?.[calcType];
  const calcMult = BUILD_COST_DATA?.regionMultipliers?.[calcRegion] || 1.0;
  const calcCostLow  = calcTypeData ? Math.round(calcTypeData.low  * calcMult * calcSqm) : 0;
  const calcCostHigh = calcTypeData ? Math.round(calcTypeData.high * calcMult * calcSqm) : 0;
  const calcFunding  = Math.round(((calcCostLow + calcCostHigh) / 2) * 0.8);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  function normalisePostcode(raw) {
    const s = raw.trim().toUpperCase().replace(/\s+/g, "");
    if (s.length >= 5) return s.slice(0, s.length - 3) + " " + s.slice(s.length - 3);
    return s;
  }

  async function validatePostcode(raw) {
    console.log("[postcode] validatePostcode called with:", raw);
    const normalised = normalisePostcode(raw);
    console.log("[postcode] normalised:", normalised);
    if (normalised.length < 5) { setPostcodeStatus("idle"); return; }
    setPostcodeStatus("checking");
    setLocationPostcode(normalised);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      console.log("[postcode] fetching postcodes.io for:", normalised);
      const res = await fetch(`https://api.postcodes.io/postcodes/${normalised.replace(/\s+/g, "")}`, { signal: controller.signal });
      clearTimeout(timeout);
      console.log("[postcode] response status:", res.status);
      if (res.ok) {
        const json = await res.json();
        const town = json.result?.admin_district || json.result?.parliamentary_constituency || "";
        console.log("[postcode] valid — town:", town);
        if (town && (locationTown === "" || townAutoFilledRef.current)) {
          setLocationTown(town);
          townAutoFilledRef.current = true;
        }
        setPostcodeStatus("valid");
        setPostcodeError("");
      } else if (res.status === 404) {
        setPostcodeStatus("invalid");
        setPostcodeError("Not a valid UK postcode");
      } else {
        setPostcodeStatus("error");
        setPostcodeError("Couldn't verify postcode — please check your connection");
      }
    } catch (err) {
      clearTimeout(timeout);
      console.log("[postcode] fetch error:", err);
      setPostcodeStatus("error");
      setPostcodeError("Couldn't verify postcode — please check your connection");
    }
  }

  function handleBack() {
    const hasContent = title.trim() || (body.trim() && body !== (editPost?.body || ""));
    if (hasContent && !window.confirm("Discard changes?")) return;
    onCancelBack ? onCancelBack() : setPage("posts");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) { setError("Title and body are required."); return; }
    if (isGroupFunding && !fundingNeeded) { setError("Funding needed is required for group funding posts."); return; }
    if (category === "looking-for-lender" && !fundingAmount) { setError("Funding amount is required."); return; }
    if (showProjectFields && !locationPostcode.trim()) { setError("Location postcode is required."); return; }
    if (showProjectFields && locationPostcode.trim() && postcodeStatus === "idle") {
      validatePostcode(locationPostcode);
      setError("Please wait — verifying postcode.");
      return;
    }
    if (showProjectFields && postcodeStatus === "invalid") { setError("Please enter a valid UK postcode."); return; }
    setLoading(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const basePayload = isEdit
      ? { action: "update", post_id: editPost.id, title, body, category }
      : { action: "create", title, body, category };

    const projectPayload = showProjectFields ? {
      location_postcode: normalisePostcode(locationPostcode),
      location_town:     locationTown.trim() || null,
      property_type:     propertyType || null,
      project_type:      projectType  || null,
      funding_amount:    fundingAmount  ? Number(fundingAmount)  : null,
      term_months:       termMonths     ? Number(termMonths)     : null,
      expected_gdv:      expectedGdv    ? Number(expectedGdv)    : null,
      exit_strategy:     exitStrategy  || null,
      planning_status:   planningStatus || null,
      photo_urls:        photoPreviewUrls,
    } : {};

    const groupPayload = isGroupFunding
      ? { funding_needed: Number(fundingNeeded), location, return_type: returnType, return_value: returnValue ? Number(returnValue) : null, timeline }
      : (category === "looking-for-lender" ? { timeline } : {});

    const payload = { ...basePayload, ...projectPayload, ...groupPayload };

    const res = await fetch("/api/posts", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(payload),
    });
    const json = await res.json();

    if (!res.ok) {
      setLoading(false);
      if (onSaved) {
        setToast({ type: "error", msg: json.error || "Failed to save. Please try again." });
        setTimeout(() => setToast(null), 4000);
      } else {
        setError(json.error || "Failed to save post.");
      }
      return;
    }

    const savedPostId = isEdit ? editPost.id : json.post?.id;

    // Upload any newly selected photos
    if (photoFiles.length > 0 && savedPostId && user) {
      setPhotoUploading(true);
      const newUrls = [];
      for (let i = 0; i < photoFiles.length; i++) {
        const file = photoFiles[i];
        const ext  = file.name.split(".").pop().toLowerCase();
        const path = `post-photos/${user.id}/${savedPostId}/${i}-${Date.now()}.${ext}`;
        await supabase.storage.from("profile-photos").remove([path]);
        const { error: upErr } = await supabase.storage.from("profile-photos").upload(path, file, { contentType: file.type });
        if (!upErr) {
          const { data: { publicUrl } } = supabase.storage.from("profile-photos").getPublicUrl(path);
          newUrls.push(publicUrl);
        }
      }
      const allUrls = [...photoPreviewUrls, ...newUrls].slice(0, 5);
      if (newUrls.length > 0) {
        await fetch("/api/posts", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: "update-photos", post_id: savedPostId, photo_urls: allUrls }),
        });
      }
      setPhotoUploading(false);
    }

    setLoading(false);
    if (onSaved) {
      onSaved({ ...(isEdit ? editPost : json.post), title, body, category, updated_at: new Date().toISOString() });
    } else {
      await supabase.auth.refreshSession();
      setPage("posts");
    }
  }

  const inputStyle = {
    width: "100%", border: "0.5px solid #ccc", borderRadius: 8,
    padding: "10px 12px", fontSize: 14, background: "#fff", boxSizing: "border-box",
  };

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 640, margin: "0 auto", opacity: visible ? 1 : 0, transition: "opacity 0.25s ease" }}>
      <button
        onClick={handleBack}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "#555", fontSize: 14, cursor: "pointer", padding: 0, marginBottom: "1.5rem" }}
      >
        ← Back
      </button>

      <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
        {isEdit ? "Edit post" : "New post"}
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 1.5rem" }}>
        {isEdit ? "Edit your post" : "Create a post"}
      </h2>

      {error && (
        <div style={{ background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        <div>
          <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Category</label>
          <select
            value={category} onChange={e => setCategory(e.target.value)}
            style={{ ...inputStyle, height: 44 }}
          >
            <option value="looking-for-lender">Looking for funding</option>
            <option value="looking-for-builder">Available to lend</option>
            <option value="group-funding">Group Funding Opportunity</option>
          </select>
        </div>

        {/* Group funding extra fields */}
        {isGroupFunding && (
          <div style={{ background: "#FDF4FF", border: "1px solid #D8B4FE", borderRadius: 10, padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#7E22CE", marginBottom: 2 }}>Group Funding Details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Funding needed (£) *</label>
                <input
                  type="text" inputMode="numeric" required={isGroupFunding}
                  value={fmtInput(fundingNeeded)} onChange={e => setFundingNeeded(parseInput(e.target.value))}
                  placeholder="e.g. 150,000"
                  style={{ ...inputStyle, height: 44 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Location</label>
                <input
                  type="text" maxLength={80}
                  value={location} onChange={e => setPostLocation(e.target.value)}
                  placeholder="e.g. Manchester"
                  style={{ ...inputStyle, height: 44 }}
                />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Return type</label>
                <select value={returnType} onChange={e => setReturnType(e.target.value)} style={{ ...inputStyle, height: 44 }}>
                  <option value="fixed_interest">Fixed interest</option>
                  <option value="revenue_share">Revenue share</option>
                  <option value="equity">Equity stake</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>
                  {returnType === "fixed_interest" ? "Interest rate (%)" : returnType === "revenue_share" ? "Revenue share (%)" : "Equity stake (%)"}
                </label>
                <input
                  type="number" min="0" max="100" step="0.1"
                  value={returnValue} onChange={e => setReturnValue(e.target.value)}
                  placeholder="e.g. 8"
                  style={{ ...inputStyle, height: 44 }}
                />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Timeline</label>
              <input
                type="text" maxLength={80}
                value={timeline} onChange={e => setTimeline(e.target.value)}
                placeholder="e.g. 18 months"
                style={{ ...inputStyle, height: 44 }}
              />
            </div>

            {/* Inline Build Calculator */}
            <div>
              <button
                type="button"
                onClick={() => setShowCalc(v => !v)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#EDE9FE", color: "#7E22CE", border: "1px solid #C4B5FD", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >
                <Icon name="calculator" size={13} /> {showCalc ? "Hide" : "Use"} Build Calculator
              </button>
              {showCalc && (
                <div style={{ marginTop: 12, background: "#fff", border: "0.5px solid #D8B4FE", borderRadius: 8, padding: "1rem" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#7E22CE", marginBottom: 10 }}>Estimate your funding need</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <div>
                      <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>Project type</label>
                      <select value={calcType} onChange={e => setCalcType(e.target.value)} style={{ ...inputStyle, height: 38, fontSize: 12 }}>
                        {BUILD_COST_DATA && Object.keys(BUILD_COST_DATA.types).map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>Region</label>
                      <select value={calcRegion} onChange={e => setCalcRegion(e.target.value)} style={{ ...inputStyle, height: 38, fontSize: 12 }}>
                        {BUILD_COST_DATA && Object.keys(BUILD_COST_DATA.regionMultipliers).map(r => <option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>Floor area (m²)</label>
                      <input
                        type="text" inputMode="numeric"
                        value={fmtInput(calcSize)} onChange={e => setCalcSize(parseInput(e.target.value))}
                        placeholder="e.g. 120"
                        style={{ ...inputStyle, height: 38, fontSize: 12 }}
                      />
                    </div>
                  </div>
                  {calcSqm > 0 && (
                    <div style={{ background: "#FDF4FF", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#7E22CE", fontWeight: 600, marginBottom: 2 }}>Recommended funding (80% of estimate)</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#7E22CE" }}>£{calcFunding.toLocaleString("en-GB")}</div>
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>Build cost range: £{calcCostLow.toLocaleString()} – £{calcCostHigh.toLocaleString()}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFundingNeeded(String(calcFunding))}
                        style={{ padding: "8px 16px", background: "#7E22CE", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", minHeight: 38 }}
                      >
                        Use this amount
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Title</label>
          <input
            type="text" required maxLength={120}
            value={title} onChange={e => setTitle(e.target.value)}
            placeholder={isGroupFunding ? "e.g. Group investment for 4-bed new build in Manchester" : "What are you looking for?"}
            style={{ ...inputStyle, height: 44 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Details</label>
          <textarea
            required rows={6}
            value={body} onChange={e => setBody(e.target.value)}
            placeholder={isGroupFunding ? "Describe the project, investment terms, risk profile, how funds will be used…" : "Describe your project, funding needs, budget, location, timeline…"}
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
          />
        </div>

        {/* ── Project details ── */}
        {showProjectFields && (
          <div style={{ border: "1px solid var(--border,#e0e0e0)", borderRadius: 10, padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem", background: "var(--bg-card,#fff)" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--accent,#1E3A5F)", marginBottom: 2 }}>Project details</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  Postcode *
                  {postcodeStatus === "valid" && <span style={{ color: "#16A34A", fontWeight: 600, fontSize: 12 }}>✓ Verified</span>}
                </label>
                <input
                  type="text" maxLength={8} required={showProjectFields}
                  value={locationPostcode}
                  onChange={e => {
                    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, "").replace(/ {2,}/g, " ").slice(0, 8);
                    setLocationPostcode(val);
                    setPostcodeStatus("idle");
                    setPostcodeError("");
                    clearTimeout(postcodeTimerRef.current);
                    if (val.trim().length >= 5) {
                      postcodeTimerRef.current = setTimeout(() => validatePostcode(val), 500);
                    }
                  }}
                  onBlur={e => {
                    clearTimeout(postcodeTimerRef.current);
                    const current = e.target.value; // read DOM value directly — avoids stale closure
                    console.log("[postcode] onBlur fired, value:", current);
                    if (current.trim().length >= 5) validatePostcode(current);
                    else if (!current.trim()) { setPostcodeStatus("idle"); setPostcodeError(""); }
                  }}
                  placeholder="e.g. SW1A 1AA"
                  style={{ ...inputStyle, height: 44, borderColor: postcodeStatus === "invalid" ? "#DC2626" : postcodeStatus === "valid" ? "#16A34A" : undefined }}
                />
                {postcodeStatus === "checking" && <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>Checking…</div>}
                {postcodeStatus === "invalid" && <div style={{ fontSize: 12, color: "#DC2626", marginTop: 4 }}>{postcodeError}</div>}
                {postcodeStatus === "error"   && <div style={{ fontSize: 12, color: "#B45309", marginTop: 4 }}>{postcodeError}</div>}
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Town / City</label>
                <input
                  type="text" maxLength={80}
                  value={locationTown} onChange={e => { townAutoFilledRef.current = false; setLocationTown(e.target.value); }}
                  placeholder="e.g. London"
                  style={{ ...inputStyle, height: 44 }}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Property type</label>
                <select value={propertyType} onChange={e => setPropertyType(e.target.value)} style={{ ...inputStyle, height: 44 }}>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="mixed_use">Mixed use</option>
                  <option value="land">Land</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Project type</label>
                <select value={projectType} onChange={e => setProjectType(e.target.value)} style={{ ...inputStyle, height: 44 }}>
                  <option value="new_build">New build</option>
                  <option value="refurb">Refurbishment</option>
                  <option value="btl">Buy-to-let</option>
                  <option value="flip">Flip</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {category === "looking-for-lender" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div>
                  <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Funding amount (£) *</label>
                  <input
                    type="text" inputMode="numeric" required={category === "looking-for-lender"}
                    value={fmtInput(fundingAmount)} onChange={e => setFundingAmount(parseInput(e.target.value))}
                    placeholder="e.g. 250,000"
                    style={{ ...inputStyle, height: 44 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Timeline</label>
                  <input
                    type="text" maxLength={80}
                    value={timeline} onChange={e => setTimeline(e.target.value)}
                    placeholder="e.g. 18 months"
                    style={{ ...inputStyle, height: 44 }}
                  />
                </div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Loan term (months)</label>
                <input
                  type="number" min={1} max={360}
                  value={termMonths} onChange={e => setTermMonths(e.target.value)}
                  placeholder="e.g. 12"
                  style={{ ...inputStyle, height: 44 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Expected GDV (£)</label>
                <input
                  type="text" inputMode="numeric"
                  value={fmtInput(expectedGdv)} onChange={e => setExpectedGdv(parseInput(e.target.value))}
                  placeholder="e.g. 400,000"
                  style={{ ...inputStyle, height: 44 }}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Exit strategy</label>
                <select value={exitStrategy} onChange={e => setExitStrategy(e.target.value)} style={{ ...inputStyle, height: 44 }}>
                  <option value="sale">Sale</option>
                  <option value="refinance">Refinance</option>
                  <option value="rental">Rental</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Planning status</label>
                <select value={planningStatus} onChange={e => setPlanningStatus(e.target.value)} style={{ ...inputStyle, height: 44 }}>
                  <option value="granted">Granted</option>
                  <option value="in_progress">In progress</option>
                  <option value="not_applied">Not applied</option>
                  <option value="not_required">Not required</option>
                </select>
              </div>
            </div>

            {/* Photo upload */}
            <div>
              <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>
                Project photos <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(optional — max 5, JPG/PNG/WebP, 5 MB each)</span>
              </label>
              {photoPreviewUrls.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {photoPreviewUrls.map((url, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      <img src={url} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: "0.5px solid #e0e0e0" }} />
                      <button
                        type="button"
                        onClick={() => setPhotoPreviewUrls(urls => urls.filter((_, j) => j !== i))}
                        style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "#DC2626", color: "#fff", border: "none", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {photoPreviewUrls.length < 5 && (
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={e => {
                    const files = Array.from(e.target.files || []);
                    const remaining = 5 - photoPreviewUrls.length;
                    const valid = files.filter(f => ["image/jpeg","image/png","image/webp"].includes(f.type) && f.size <= 5 * 1024 * 1024);
                    const invalid = files.filter(f => !["image/jpeg","image/png","image/webp"].includes(f.type) || f.size > 5 * 1024 * 1024);
                    if (invalid.length > 0) setPhotoError(`${invalid.length} file(s) skipped — must be JPG/PNG/WebP under 5 MB.`);
                    else setPhotoError("");
                    setPhotoFiles(valid.slice(0, remaining));
                    e.target.value = "";
                  }}
                  style={{ fontSize: 13, color: "#555" }}
                />
              )}
              {photoFiles.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {photoFiles.map((f, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      {photoFileUrls[i] && (
                        <img src={photoFileUrls[i]} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: "0.5px solid #e0e0e0" }} />
                      )}
                      <button
                        type="button"
                        onClick={() => setPhotoFiles(files => files.filter((_, j) => j !== i))}
                        style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "#DC2626", color: "#fff", border: "none", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {photoError && <div style={{ fontSize: 12, color: "#DC2626", marginTop: 6 }}>{photoError}</div>}
              {photoUploading && <div style={{ fontSize: 12, color: "#64748B", marginTop: 6 }}>Uploading photos…</div>}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="submit" disabled={loading || (showProjectFields && (postcodeStatus === "invalid" || postcodeStatus === "checking"))}
            style={{
              flex: 1, height: 44, background: (loading || (showProjectFields && (postcodeStatus === "invalid" || postcodeStatus === "checking"))) ? "#5B9BF8" : "#3B82F6", color: "#fff",
              border: "none", borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: (loading || (showProjectFields && (postcodeStatus === "invalid" || postcodeStatus === "checking"))) ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "background 0.2s",
            }}
          >
            {loading && (
              <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "lbSpin 0.7s linear infinite", flexShrink: 0 }} />
            )}
            {photoUploading ? "Uploading photos…" : loading ? "Saving…" : isEdit ? "Save changes" : isGroupFunding ? "Publish & Create Funding Room" : "Publish post"}
          </button>
          <button
            type="button" onClick={handleBack} disabled={loading}
            style={{ padding: "0 20px", height: 44, background: "transparent", color: "#555", border: "0.5px solid #ccc", borderRadius: 8, fontSize: 14, cursor: loading ? "default" : "pointer", opacity: loading ? 0.5 : 1 }}
          >
            Cancel
          </button>
        </div>
      </form>

      {toast && (
        <div style={{
          position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "error" ? "#DC2626" : "#16A34A",
          color: "#fff", padding: "11px 22px", borderRadius: 10,
          fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
          zIndex: 9999, pointerEvents: "none", animation: "fadeInUp 0.2s ease",
        }}>
          {toast.type === "error" ? "✕" : "✓"} {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── MY POSTS PAGE ────────────────────────────────────────────────────────────

function MyPostsPage({ user, setPage }) {
  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [toast,   setToast]   = useState("");

  useEffect(() => {
    loadPosts();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPosts() {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res  = await fetch("/api/posts", {
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to load posts");
      setPosts((json.posts || []).filter(p => p.author_id === user.id));
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  async function handleDelete(postId) {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/posts", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "delete", post_id: postId }),
    });
    if (res.ok) {
      setPosts(prev => prev.filter(p => p.id !== postId));
      setToast("Post deleted");
      setTimeout(() => setToast(""), 3000);
    }
  }

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>My posts</div>
          <h2 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Your posts</h2>
          <p style={{ fontSize: 14, color: "#64748B", margin: "4px 0 0" }}>Manage the posts you've published</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setPage("posts")}
            style={{ background: "transparent", color: "#555", border: "0.5px solid #ccc", padding: "9px 18px", borderRadius: 8, fontSize: 14, cursor: "pointer" }}
          >
            Browse all posts
          </button>
          <button
            onClick={() => setPage("create-post")}
            style={{ background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "9px 18px", minHeight: 44, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            + New post
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "4rem", color: "#64748B", fontSize: 14 }}>Loading…</div>}
      {error   && <div style={{ background: "#FAECE7", color: "#993C1D", borderRadius: 8, padding: "12px 16px", fontSize: 13, marginBottom: "1rem" }}>{error}</div>}

      {!loading && !error && posts.length === 0 && (
        <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="edit" size={32} /></div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No posts yet</div>
          <div style={{ fontSize: 13, marginBottom: "1.5rem" }}>Share what you're looking for with the community.</div>
          <button
            onClick={() => setPage("create-post")}
            style={{ background: "#3B82F6", color: "#FFFFFF", border: "none", padding: "10px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            Create your first post
          </button>
        </div>
      )}

      {posts.map(p => (
        <PostCard
          key={p.id}
          post={p}
          isOwn={true}
          onView={() => setPage("post-detail", p)}
          onEdit={post => setPage("edit-post", post)}
          onDelete={handleDelete}
          user={user}
        />
      ))}

      {toast && (
        <div style={{
          position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
          background: "#16A34A", color: "#fff", padding: "11px 22px", borderRadius: 10,
          fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
          zIndex: 9999, pointerEvents: "none",
          animation: "fadeInUp 0.2s ease",
        }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
}

// ─── LENDER PROFILE PAGE ─────────────────────────────────────────────────────

function LenderProfilePage({ lc, user, setPage, settings, onBack, onMessage, viewerRoleProfile }) {
  const isListed = settings?.listing_active !== false;
  const [msgStatus, setMsgStatus] = useState("idle");

  async function handleMessage() {
    if (!user) { setPage("auth"); return; }
    setMsgStatus("loading");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/notify-message", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ other_name: lc.name, other_role: "lender" }),
    });
    if (res.ok) {
      const { conversation } = await res.json();
      setMsgStatus("idle");
      onMessage(conversation.id);
    } else {
      setMsgStatus("error");
      setTimeout(() => setMsgStatus("idle"), 3000);
    }
  }

  const displayName = (!lc.name || lc.name === "New User") ? "Anonymous Lender" : lc.name;

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 680, margin: "0 auto" }}>

      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "transparent", border: "none",
          color: "#555", fontSize: 14, cursor: "pointer", padding: 0,
          marginBottom: "1.5rem",
        }}
      >
        ← Back to search
      </button>

      {/* Header */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.75rem", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
          <Avatar initials={lc.initials} color={lc.color} size={64} url={lc.avatar_url} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 22, fontWeight: 500 }}>{displayName}</div>
              <RoleBadge userRole={lc.user_role} authRole="lender" />
              {lc.verification_status === "verified" && (
                <span title={lc.company_name ? `Registered business: ${lc.company_name}` : "Verified with Companies House"} style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: "#166534", color: "#fff", flexShrink: 0, cursor: "help", display:"inline-flex", alignItems:"center", gap:3 }}>
                  ✓ Verified{lc.company_name ? ` — ${lc.company_name}` : ""}
                </span>
              )}
              {lc.verified && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <span style={{ background: "#EBF2FF", color: "#1E3A5F", fontSize: 11, padding: "3px 9px", borderRadius: 20, fontWeight: 500 }}>
                    ✓ Verified
                  </span>
                  <HelpTooltip text="This user has submitted identity documents that have been reviewed and approved by LenderBuild." />
                </span>
              )}
              {lc.featured && (
                <span style={{ background: "#EBF2FF", color: "#1E3A5F", fontSize: 11, padding: "3px 9px", borderRadius: 20, fontWeight: 500 }}>
                  Most active
                </span>
              )}
              {lc.identity_verified && (
                <span title="Identity verified via Stripe Identity" style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: "#D97706", color: "#fff", flexShrink: 0, display:"inline-flex", alignItems:"center", gap:3 }}>
                  <Icon name="id-card" size={11} /> ID Verified
                </span>
              )}
            </div>
            <div style={{ fontSize: 14, color: "#64748B", marginTop: 3 }}>{lc.type}</div>
            {lc.location && (
              <div style={{ fontSize: 13, color: "#666", marginTop: 5, display:"flex", alignItems:"center", gap:3 }}>
                <Icon name="map-pin" size={12} style={{flexShrink:0}} />{lc.location}
              </div>
            )}
            {(() => {
              if (!viewerRoleProfile || user?.user_metadata?.role !== "builder") return null;
              const m = computeMatch(viewerRoleProfile, lc, "builder");
              if (!m) return null;
              return <div style={{ marginTop: 8 }}><MatchBadge score={m.score} breakdown={m.components} /></div>;
            })()}
          </div>
        </div>
        {lc.bio && (
          <div style={{ paddingTop: 14, borderTop: "0.5px solid #f0f0f0" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
              About
            </div>
            <p style={{ fontSize: 14, color: "#555", lineHeight: 1.65, margin: 0 }}>
              {lc.bio}
            </p>
          </div>
        )}
      </div>

      {/* Investment details */}
      {(() => {
        const hasBudget = lc.budget && lc.budget !== "Flexible";
        const hasReturnType = lc.returnType && lc.returnType !== "Flexible";
        const hasProject = lc.project && lc.project !== "Any";
        const rows = [
          hasReturnType   ? ["Return type",   lc.returnType]   : null,
          lc.builderShare ? ["Builder share",  lc.builderShare] : null,
          lc.lenderShare  ? ["Lender share",   lc.lenderShare]  : null,
          lc.rate         ? ["Rate",           lc.rate]         : null,
          lc.term         ? ["Term",           lc.term]         : null,
          lc.equity       ? ["Equity ask",     lc.equity]       : null,
          lc.exit         ? ["Exit timeline",  lc.exit]         : null,
          hasProject      ? ["Project pref.",  lc.project]      : null,
        ].filter(Boolean);
        if (!hasBudget && rows.length === 0) return null;
        return (
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>
              Investment details
            </div>
            {hasBudget && (
              <div style={{ display: "inline-block", background: "#EBF2FF", color: "#1E3A5F", fontSize: 14, fontWeight: 500, padding: "5px 12px", borderRadius: 8, marginBottom: rows.length > 0 ? 14 : 0 }}>
                Up to {lc.budget}
              </div>
            )}
            {rows.length > 0 && (
              <div>
                {rows.map(([label, val], i, arr) => (
                  <div key={label} style={{
                    display: "flex", justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: i < arr.length - 1 ? "0.5px solid #f5f5f3" : "none",
                    fontSize: 14, color: "#666",
                  }}>
                    <span>{label}</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 500, color: "#222" }}>
                      {val}
                      {label === "Return type" && RETURN_TYPE_TOOLTIPS[val] && <HelpTooltip text={RETURN_TYPE_TOOLTIPS[val]} />}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}


      {/* Actions CTA */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem" }}>
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>
          Interested in working with {displayName}?
        </div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>
          Send a message to start a conversation directly.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {user && (
            <button
              onClick={handleMessage}
              disabled={msgStatus === "loading"}
              style={{
                flex: 1, background: "var(--accent)", color: "#fff", border: "none",
                padding: "11px 0", borderRadius: 8, fontSize: 14, fontWeight: 600, minHeight: 44,
                cursor: msgStatus === "loading" ? "default" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {msgStatus === "loading" ? "Opening…" : msgStatus === "error" ? "Couldn't open chat — try again" : "Send message"}
            </button>
          )}
          {!user && (
            <button
              onClick={() => setPage("auth")}
              style={{
                flex: 1, background: "#2E5FA3", color: "#FFFFFF", border: "none",
                padding: "11px 0", borderRadius: 8, fontSize: 14, fontWeight: 600, minHeight: 44,
                cursor: "pointer",
              }}
            >
              Log in to message
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── BUILDER PROFILE PAGE ────────────────────────────────────────────────────

function BuilderProfilePage({ builder, user, setPage, onBack, onMessage, viewerRoleProfile }) {
  const [msgStatus, setMsgStatus] = useState("idle");

  async function handleMessage() {
    if (!user) { setPage("auth"); return; }
    setMsgStatus("loading");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/notify-message", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ other_name: builder.name, other_role: "builder" }),
    });
    if (res.ok) {
      const { conversation } = await res.json();
      setMsgStatus("idle");
      onMessage(conversation.id);
    } else {
      setMsgStatus("error");
      setTimeout(() => setMsgStatus("idle"), 3000);
    }
  }

  const completionColor = builder.completion >= 95 ? "#1D9E75" : builder.completion >= 80 ? "#2E5FA3" : "#D85A30";

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 680, margin: "0 auto" }}>

      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "transparent", border: "none",
          color: "#555", fontSize: 14, cursor: "pointer", padding: 0,
          marginBottom: "1.5rem",
        }}
      >
        ← Back to builders
      </button>

      {/* Header */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.75rem", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: builder.bio ? 16 : 0 }}>
          <CredibilityRing score={computeCredibilityScore(builder)} size={64}>
            <Avatar initials={builder.initials} color={builder.color} size={64} url={builder.avatar_url} />
          </CredibilityRing>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 22, fontWeight: 500 }}>{builder.name}</div>
              <RoleBadge userRole={builder.user_role} authRole="builder" />
              {builder.verified_documents?.length > 0 && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 9px", borderRadius: 20, background: "#EBF2FF", color: "#1E3A5F", flexShrink: 0 }}>
                    ✓ Verified
                  </span>
                  <HelpTooltip text="This user has submitted identity documents that have been reviewed and approved by LenderBuild." />
                </span>
              )}
              {builder.verification_status === "verified" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <span title={builder.company_name ? `Registered business: ${builder.company_name}` : "Verified with Companies House"} style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: "#166534", color: "#fff", flexShrink: 0, cursor: "help", display:"inline-flex", alignItems:"center", gap:3 }}>
                    ✓ Verified{builder.company_name ? ` — ${builder.company_name}` : ""}
                  </span>
                  <HelpTooltip text="This builder's company has been confirmed as active on the UK Companies House register." />
                </span>
              )}
              {builder.identity_verified && (
                <span title="Identity verified via Stripe Identity" style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: "#D97706", color: "#fff", flexShrink: 0, display:"inline-flex", alignItems:"center", gap:3 }}>
                  <Icon name="id-card" size={11} /> ID Verified
                </span>
              )}
            </div>
            {builder.sequential_id && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: "#aaa" }}>{fmtId(builder.sequential_id)}</span>
                {builder.is_founding_member && <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 20, background: "#FEF3C7", color: "#D97706" }}>Founding Member</span>}
              </div>
            )}
            <div style={{ fontSize: 14, color: "#64748B", marginTop: 3 }}>{builder.type}</div>
            {builder.location && (
              <div style={{ fontSize: 13, color: "#666", marginTop: 5 }}><Icon name="map-pin" size={12} style={{marginRight:3,flexShrink:0}} />{builder.location}</div>
            )}
            {(() => {
              if (!viewerRoleProfile || user?.user_metadata?.role !== "lender") return null;
              const m = computeMatch(viewerRoleProfile, builder, "lender");
              if (!m) return null;
              return <div style={{ marginTop: 8 }}><MatchBadge score={m.score} breakdown={m.components} /></div>;
            })()}
          </div>
        </div>
        {builder.verified_documents?.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            {[
              { key: "photo_id",    label: "ID verified" },
              { key: "selfie",      label: "Selfie verified" },
              { key: "site_photos", label: "Site photos" },
              { key: "planning",    label: "Planning docs" },
            ].filter(d => builder.verified_documents.includes(d.key)).map(d => (
              <span key={d.key} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#EBF2FF", color: "#1E3A5F" }}>
                {d.label}
              </span>
            ))}
          </div>
        )}
        {builder.bio && (
          <p style={{ fontSize: 14, color: "#555", lineHeight: 1.65, margin: 0, paddingTop: 14, borderTop: "0.5px solid #f0f0f0" }}>
            {builder.bio}
          </p>
        )}
      </div>

      {/* Track record */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem", marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>
          Track record
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <MetricCard label="Projects completed" value={builder.props} />
          <MetricCard label="Total value built" value={fmt(builder.value)} />
        </div>
        {builder.props > 0 && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#666", marginBottom: 6 }}>
              <span>Completion rate</span>
              <span style={{ fontWeight: 500, color: completionColor }}>{builder.completion}%</span>
            </div>
            <div style={{ height: 6, background: "#eee", borderRadius: 3 }}>
              <div style={{ height: 6, borderRadius: 3, background: completionColor, width: `${builder.completion}%`, transition: "width 0.3s" }} />
            </div>
          </div>
        )}
      </div>

      {/* Financial health indicators */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem", marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#0F6E56", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>
          Financial health
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Companies House status */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Companies House filing</div>
              <div style={{ fontSize: 12, color: "#64748B" }}>Company accounts status</div>
            </div>
            {builder.company_number ? (
              <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: builder.company_status === "active" || builder.company_status === "Active" ? "#E1F5EE" : "#FEF3C7", color: builder.company_status === "active" || builder.company_status === "Active" ? "#0F6E56" : "#B45309" }}>
                {builder.company_status === "active" || builder.company_status === "Active" ? "Active — up to date" : builder.company_status || "Status unknown"}
              </span>
            ) : (
              <span style={{ fontSize: 12, color: "#94A3B8" }}>Not registered</span>
            )}
          </div>
          {/* Time on platform */}
          {builder.member_since && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Member since</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>Time on LenderBuild</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
                {(() => {
                  const ms = new Date(builder.member_since);
                  const now = new Date();
                  const months = (now.getFullYear() - ms.getFullYear()) * 12 + (now.getMonth() - ms.getMonth());
                  if (months < 1) return "New member";
                  if (months < 12) return `${months} month${months !== 1 ? "s" : ""}`;
                  const years = Math.floor(months / 12);
                  return `${years} year${years !== 1 ? "s" : ""}`;
                })()}
              </span>
            </div>
          )}
          {/* Repayment history */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Repayment history</div>
              <div style={{ fontSize: 12, color: "#64748B" }}>
                {builder.repayment_stats && builder.repayment_stats.total > 0
                  ? `${builder.repayment_stats.total} total · ${builder.repayment_stats.onTime} on time · ${builder.repayment_stats.late} late · ${builder.repayment_stats.missed} missed`
                  : "No repayment history yet"}
              </div>
            </div>
          </div>
          {/* Repayment score */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Repayment score</div>
              <div style={{ fontSize: 12, color: "#64748B" }}>Based on deal history</div>
            </div>
            {builder.repayment_score !== null && builder.repayment_score !== undefined ? (
              <div style={{ display: "flex", gap: 3 }}>
                {[1,2,3,4,5].map(s => (
                  <span key={s} style={{ fontSize: 18, color: s <= builder.repayment_score ? "#D97706" : "#e0e0e0" }}>★</span>
                ))}
              </div>
            ) : (
              <span style={{ fontSize: 12, color: "#94A3B8" }}>No data yet</span>
            )}
          </div>
        </div>
      </div>

      {/* Actions CTA */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem" }}>
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>
          Interested in working with {builder.name}?
        </div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>
          Send a message to start a conversation directly.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {user && (
            <button
              onClick={handleMessage}
              disabled={msgStatus === "loading"}
              style={{
                flex: 1, background: "var(--accent)", color: "#fff", border: "none",
                padding: "11px 0", borderRadius: 8, fontSize: 14, fontWeight: 600, minHeight: 44,
                cursor: msgStatus === "loading" ? "default" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {msgStatus === "loading" ? "Opening…" : msgStatus === "error" ? "Couldn't open chat — retry" : "Message"}
            </button>
          )}
          {!user && (
            <button
              onClick={() => setPage("auth")}
              style={{
                flex: 1, background: "#3B82F6", color: "#FFFFFF", border: "none",
                padding: "11px 0", borderRadius: 8, fontSize: 14, fontWeight: 600, minHeight: 44,
                cursor: "pointer",
              }}
            >
              Log in to message
            </button>
          )}
        </div>
        {builder.sequential_id && (
          <div style={{ marginTop: 10 }}>
            <button
              onClick={() => setPage("builder-passport", builder.sequential_id)}
              style={{ width: "100%", background: "transparent", border: "1px solid #CBD5E1", color: "#475569", borderRadius: 8, padding: "9px 0", fontSize: 13, fontWeight: 500, cursor: "pointer", minHeight: 40 }}
            >
              View full builder passport
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MESSAGES PAGE ───────────────────────────────────────────────────────────

function MessagesPage({ user, initialConversationId, setPage, onViewProfile, onViewBuilderProfile }) {
  const [conversations,   setConversations]   = useState([]);
  const [selectedConvo,   setSelectedConvo]   = useState(null);
  const [messages,        setMessages]        = useState([]);
  const [newMessage,      setNewMessage]      = useState("");
  const [loading,         setLoading]         = useState(true);
  const [sending,         setSending]         = useState(false);
  const [avatarMap,       setAvatarMap]       = useState({});
  const [clearModal,      setClearModal]      = useState(false);
  const [clearing,        setClearing]        = useState(false);
  const [editingId,       setEditingId]       = useState(null);
  const [editContent,     setEditContent]     = useState("");
  const [msgMenuId,       setMsgMenuId]       = useState(null);
  const [msgMenuUp,       setMsgMenuUp]       = useState(false);
  const [deleteMsgId,     setDeleteMsgId]     = useState(null);
  const [deleteConvoModal, setDeleteConvoModal] = useState(false);
  const [deletingConvo,   setDeletingConvo]   = useState(false);
  const [headerMenuOpen,  setHeaderMenuOpen]  = useState(false);
  const [listCollapsed,   setListCollapsed]   = useState(false);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const [scheduledAt,     setScheduledAt]     = useState("");
  const [dismissedNotices, setDismissedNotices] = useState(() => { try { return JSON.parse(localStorage.getItem("lb_pm_dismissed_notices") || "[]"); } catch { return []; } });
  const msgsScrollRef   = useRef(null);
  const inputRef        = useRef(null);
  const [chatReady,     setChatReady]     = useState(false);
  const [width, setWidth] = useState(window.innerWidth);

  const myId = user?.id;
  const role = user?.user_metadata?.role;

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = width < 700;

  const dmOtherName = selectedConvo ? (myId === selectedConvo.lender_id ? selectedConvo.builder_name : selectedConvo.lender_name) : null;
  const dmDc = useDealConfirm(user, dmOtherName, selectedConvo);

  useEffect(() => { loadConversations(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialConversationId || !conversations.length || selectedConvo) return;
    const convo = conversations.find(c => c.id === initialConversationId);
    if (convo) setSelectedConvo(convo);
  }, [initialConversationId, conversations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time: INSERT + UPDATE + DELETE
  useEffect(() => {
    if (!selectedConvo) return;
    setMessages([]);
    setChatReady(false);
    let active = true;
    Promise.all([loadMessages(selectedConvo.id, { apply: false }), dmDc.loadStatus()])
      .then(([msgs]) => { if (active) { setMessages(msgs); setChatReady(true); } });

    const channel = supabase
      .channel(`messages-${selectedConvo.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${selectedConvo.id}` }, payload => {
        const msg = payload.new;
        if (msg.status === "scheduled" && msg.sender_id !== myId) return; // hide others' scheduled
        setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
        if (msg.status !== "scheduled") {
          setConversations(prev => prev.map(c =>
            c.id === msg.conversation_id ? { ...c, last_message: msg.content, last_message_at: msg.created_at } : c
          ));
        }
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "messages", filter: `conversation_id=eq.${selectedConvo.id}` }, payload => {
        const msg = payload.new;
        setMessages(prev => prev.map(m => m.id === msg.id ? msg : m));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "messages", filter: `conversation_id=eq.${selectedConvo.id}` }, () => {
        loadMessages(selectedConvo.id);
      })
      .subscribe();

    return () => { active = false; supabase.removeChannel(channel); };
  }, [selectedConvo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Process scheduled messages every 60 seconds
  useEffect(() => {
    if (!selectedConvo || !myId) return;
    const tick = async () => {
      const now = new Date().toISOString();
      const due = messages.filter(m => m.status === "scheduled" && m.sender_id === myId && m.scheduled_at && m.scheduled_at <= now);
      if (due.length === 0) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch("/api/notify-message", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: "process-scheduled", conversation_id: selectedConvo.id }),
      }).catch(() => {});
      loadMessages(selectedConvo.id);
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, [selectedConvo?.id, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // While the mobile chat overlay is open, disable overflow on #main-scroll so
  // that iOS Safari's overflow-containment bug doesn't make position:fixed children
  // position relative to that element instead of the viewport.
  useEffect(() => {
    const el = document.getElementById("main-scroll");
    if (!el) return;
    if (isMobile && selectedConvo) {
      const prev = el.style.overflowY;
      el.style.overflowY = "hidden";
      return () => { el.style.overflowY = prev; };
    }
  }, [isMobile, selectedConvo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close message menu on outside click
  useEffect(() => {
    if (!msgMenuId) return;
    const handler = () => setMsgMenuId(null);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [msgMenuId]);

  // Close header ⋯ menu on outside click
  useEffect(() => {
    if (!headerMenuOpen) return;
    const handler = () => setHeaderMenuOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [headerMenuOpen]);

  async function loadConversations() {
    setLoading(true);
    const { data } = await supabase.from("conversations").select("*")
      .or(`lender_id.eq.${myId},builder_id.eq.${myId}`)
      .order("last_message_at", { ascending: false, nullsFirst: false });
    // Filter out conversations the current user has soft-deleted (deleted_for column).
    const convos = (data || []).filter(c => !(c.deleted_for || []).includes(myId));
    setConversations(convos);
    setLoading(false);
    const otherIds = [...new Set(convos.map(c => myId === c.lender_id ? c.builder_id : c.lender_id).filter(Boolean))];
    if (otherIds.length > 0) {
      fetch(`/api/get-profiles?type=avatars&ids=${otherIds.join(",")}`)
        .then(r => r.json()).then(d => setAvatarMap(d.avatars || {})).catch(() => {});
    }
  }

  async function loadMessages(conversationId, { apply = true } = {}) {
    const { data } = await supabase.from("messages").select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });
    const filtered = (data || []).filter(m => m.status !== "scheduled" || m.sender_id === myId);
    if (apply) setMessages(filtered);
    return filtered;
  }

  async function handleSend(e) {
    e.preventDefault();
    const raw = newMessage.trim();
    if (!raw || !selectedConvo || sending) return;

    const content = raw;
    setSending(true);
    setNewMessage("");
    setSchedulePickerOpen(false);

    const row = { conversation_id: selectedConvo.id, sender_id: myId, content, status: "sent" };
    if (scheduledAt) {
      row.scheduled_at = new Date(scheduledAt).toISOString();
      row.status = "scheduled";
      setScheduledAt("");
    }

    const { data: msg } = await supabase.from("messages").insert(row).select().single();

    if (msg && msg.status !== "scheduled") {
      setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
      const ts = msg.created_at;
      setConversations(prev => prev.map(c =>
        c.id === selectedConvo.id ? { ...c, last_message: content, last_message_at: ts } : c
      ));
      await supabase.from("conversations").update({ last_message: content, last_message_at: ts }).eq("id", selectedConvo.id);
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s) {
        fetch("/api/notify-message", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.access_token}` },
          body: JSON.stringify({ conversation_id: selectedConvo.id }),
        }).catch(() => {});
      }
    } else if (msg) {
      // Show the scheduled message to sender immediately
      setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
    }

    setSending(false);
    inputRef.current?.focus();
  }

  async function handleClearConvo() {
    if (!selectedConvo) return;
    setClearing(true);
    const { data: { session } } = await supabase.auth.getSession();
    await fetch("/api/notify-message", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "clear-conversation", conversation_id: selectedConvo.id }),
    }).catch(() => {});
    setMessages([]);
    setConversations(prev => prev.map(c =>
      c.id === selectedConvo.id ? { ...c, last_message: null, last_message_at: null } : c
    ));
    setClearModal(false);
    setClearing(false);
  }

  async function handleEditSave(msgId) {
    const newContent = editContent.trim();
    if (!newContent) return;
    await supabase.from("messages")
      .update({ content: newContent, edited_at: new Date().toISOString() })
      .eq("id", msgId).eq("sender_id", myId);
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: newContent, edited_at: new Date().toISOString() } : m));
    setEditingId(null);
    setEditContent("");
  }

  async function handleCancelScheduled(msgId) {
    await supabase.from("messages").delete().eq("id", msgId).eq("sender_id", myId);
    setMessages(prev => prev.filter(m => m.id !== msgId));
  }

  async function handleDeleteMessage() {
    if (!deleteMsgId) return;
    await supabase.from("messages").delete().eq("id", deleteMsgId).eq("sender_id", myId);
    // Real-time DELETE subscription picks this up for both parties automatically.
    setMessages(prev => prev.filter(m => m.id !== deleteMsgId));
    setDeleteMsgId(null);
  }

  async function handleDeleteConvo() {
    if (!selectedConvo) return;
    setDeletingConvo(true);
    const { data: fresh } = await supabase.from("conversations").select("deleted_for").eq("id", selectedConvo.id).single();
    const existing = fresh?.deleted_for || [];
    if (!existing.includes(myId)) {
      await supabase.from("conversations").update({ deleted_for: [...existing, myId] }).eq("id", selectedConvo.id);
    }
    setConversations(prev => prev.filter(c => c.id !== selectedConvo.id));
    setSelectedConvo(null);
    setDeleteConvoModal(false);
    setDeletingConvo(false);
  }

  async function handleAvatarClick(convo) {
    const otherId   = myId === convo.lender_id ? convo.builder_id : convo.lender_id;
    const otherRole = myId === convo.lender_id ? "builder" : "lender";
    const otherName = myId === convo.lender_id ? convo.builder_name : convo.lender_name;
    if (!otherId || !setPage) return;

    const { data: profile } = await supabase.from("profiles").select("*").eq("id", otherId).maybeSingle();
    const snap = { id: `real-${otherId}`, _real: true, user_id: otherId, name: otherName,
      initials: nameInitials(otherName), color: pickColor(otherId),
      avatar_url: avatarMap[otherId] || null, location: profile?.location || "",
      bio: profile?.bio || "", user_role: profile?.user_role, sequential_id: profile?.sequential_id };

    if (otherRole === "lender" && onViewProfile) onViewProfile({ ...snap, type: "Private lender", budget: "Flexible", project: "Any", preferred_projects: [], returnType: "Flexible", verified: false });
    else if (otherRole === "builder" && onViewBuilderProfile) onViewBuilderProfile({ ...snap, type: "Builder", props: 0, value: 0, completion: 100, verified_documents: [] });
  }

  function getOtherName(convo) { return myId === convo.lender_id ? convo.builder_name : convo.lender_name; }
  function getOtherRole(convo) { return myId === convo.lender_id ? "Builder" : "Lender"; }

  function fmtTime(ts) {
    if (!ts) return "";
    const d   = new Date(ts);
    const now = new Date();
    const min = Math.floor((now - d) / 60000);
    if (min < 1)  return "just now";
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    if (h < 24)   return `${h}h`;
    const day = Math.floor(h / 24);
    if (day < 7)  return `${day}d`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  }

  function fmtScheduled(ts) {
    const d = new Date(ts);
    return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  }

  const showList = isMobile ? !selectedConvo : !listCollapsed;
  const showChat = !isMobile || !!selectedConvo;

  // Min datetime for scheduler: 1 minute from now
  const minDateTime = new Date(Date.now() + 60000).toISOString().slice(0, 16);

  return (
    <div style={{
      padding: isMobile ? "0" : "1.5rem 1.25rem",
      flex: isMobile ? undefined : 1,
      minHeight: isMobile ? undefined : 0,
      boxSizing: "border-box",
      overflow: isMobile ? undefined : "hidden",
      overflowX: isMobile ? "hidden" : undefined,
      display: isMobile ? undefined : "flex",
      flexDirection: isMobile ? undefined : "column",
    }}>

      {/* Delete message confirmation modal */}
      {deleteMsgId && createPortal(
        <>
          <div onClick={() => setDeleteMsgId(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9010 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(380px, 90vw)", background: "#fff", borderRadius: 14, padding: "24px", zIndex: 9011, boxShadow: "0 16px 56px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Delete this message?</div>
            <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6, marginBottom: 20 }}>
              This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleDeleteMessage}
                style={{ flex: 1, padding: "10px 0", background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Delete
              </button>
              <button onClick={() => setDeleteMsgId(null)}
                style={{ flex: 1, padding: "10px 0", background: "transparent", border: "0.5px solid #ccc", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Delete conversation confirmation modal */}
      {deleteConvoModal && createPortal(
        <>
          <div onClick={() => !deletingConvo && setDeleteConvoModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9010 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(420px, 90vw)", background: "#fff", borderRadius: 14, padding: "24px", zIndex: 9011, boxShadow: "0 16px 56px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Delete this conversation?</div>
            <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6, marginBottom: 20 }}>
              All messages will be removed for you. The other person will still see the conversation on their side.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleDeleteConvo} disabled={deletingConvo}
                style={{ flex: 1, padding: "10px 0", background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                {deletingConvo ? "Deleting…" : "Delete"}
              </button>
              <button onClick={() => setDeleteConvoModal(false)}
                style={{ flex: 1, padding: "10px 0", background: "transparent", border: "0.5px solid #ccc", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Clear conversation confirmation modal */}
      {clearModal && createPortal(
        <>
          <div onClick={() => !clearing && setClearModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9010 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(400px, 90vw)", background: "#fff", borderRadius: 14, padding: "24px", zIndex: 9011, boxShadow: "0 16px 56px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Clear conversation?</div>
            <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6, marginBottom: 20 }}>
              Are you sure you want to clear this conversation? This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleClearConvo} disabled={clearing}
                style={{ flex: 1, padding: "10px 0", background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                {clearing ? "Clearing…" : "Clear conversation"}
              </button>
              <button onClick={() => setClearModal(false)}
                style={{ flex: 1, padding: "10px 0", background: "transparent", border: "0.5px solid #ccc", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {showList && (
        <div style={{ marginBottom: "1.25rem", padding: isMobile ? "1.5rem 1.25rem 0" : 0 }}>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0, fontFamily: "'Georgia', serif" }}>Messages</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
            Your conversations with connected {role === "lender" ? "builders" : "lenders"}
          </p>
        </div>
      )}

      <div style={{
        display: "flex", gap: 16,
        height: isMobile ? (selectedConvo ? "100%" : "auto") : undefined,
        flex: isMobile ? undefined : 1,
        minHeight: isMobile ? undefined : 0,
      }}>

        {/* ── Conversations list ──────────────────────────────────────────── */}
        {showList && (
          <div style={{
            width: isMobile ? "100%" : 300, flexShrink: 0, background: "#fff",
            border: isMobile ? "none" : "0.5px solid #e0e0e0", borderRadius: isMobile ? 0 : 12,
            overflow: "hidden", display: "flex", flexDirection: "column", marginTop: 0,
          }}>
            <div style={{ padding: "10px 12px 10px 16px", borderBottom: "0.5px solid #f0f0f0", fontSize: 13, fontWeight: 500, color: "#555", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Conversations</span>
              {!isMobile && (
                <button onClick={() => setListCollapsed(true)} title="Collapse sidebar"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "3px 6px", color: "#aaa", display: "flex", alignItems: "center", borderRadius: 5, lineHeight: 1 }}
                  onMouseEnter={e => e.currentTarget.style.color = "#555"}
                  onMouseLeave={e => e.currentTarget.style.color = "#aaa"}>
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M9.5 3L5.5 7.5L9.5 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><line x1="3" y1="2" x2="3" y2="13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                </button>
              )}
            </div>
            {loading ? (
              <div>{[0,1,2,3].map(i => <SkeletonConversationRow key={i} />)}</div>
            ) : conversations.length === 0 ? (
              <div style={{ padding: "2.5rem 1.5rem", textAlign: "center" }}>
                <div style={{ marginBottom: 10, color: "var(--accent)" }}><Icon name="message" size={36} /></div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>No conversations yet</div>
                <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.55, marginBottom: 16 }}>
                  Find someone to connect with and start a conversation.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                  <button onClick={() => setPage("search")} style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", width: "100%", maxWidth: 200 }}>
                    Find lenders →
                  </button>
                  <button onClick={() => setPage("find-builder")} style={{ background: "transparent", color: "#1E3A5F", border: "1px solid #1E3A5F", padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", width: "100%", maxWidth: 200 }}>
                    Find builders →
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ overflowY: "auto", flex: 1 }}>
                {conversations.map((convo, i) => {
                  const isSelected = selectedConvo?.id === convo.id;
                  const otherName  = getOtherName(convo);
                  const otherId    = myId === convo.lender_id ? convo.builder_id : convo.lender_id;
                  return (
                    <div key={convo.id} onClick={() => setSelectedConvo(convo)}
                      style={{ padding: "12px 16px", borderBottom: i < conversations.length - 1 ? "0.5px solid #f5f5f3" : "none",
                        background: isSelected ? "#f0faf6" : "transparent", cursor: "pointer", transition: "background 0.1s" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div onClick={e => { e.stopPropagation(); handleAvatarClick(convo); }} style={{ cursor: "pointer", flexShrink: 0 }}>
                          <Avatar initials={nameInitials(otherName)} color={pickColor(otherName || "")} size={38} url={avatarMap[otherId]} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 4 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{otherName || "Unknown"}</div>
                            <div style={{ fontSize: 11, color: "#aaa", flexShrink: 0 }}>{fmtTime(convo.last_message_at || convo.created_at)}</div>
                          </div>
                          <div style={{ fontSize: 12, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>
                            {convo.last_message || "No messages yet — say hello!"}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Chat panel ─────────────────────────────────────────────────── */}
        {showChat && (
          <div style={{ flex: 1, background: "#fff", border: isMobile ? "none" : "0.5px solid #e0e0e0", borderRadius: isMobile ? 0 : 12, display: "flex", flexDirection: "column", overflowX: "hidden", ...(isMobile && selectedConvo ? { position: "fixed", top: 113, left: 0, right: 0, bottom: "calc(60px + env(safe-area-inset-bottom, 0px))", zIndex: 50 } : { overflow: "hidden" }) }}>
            {!selectedConvo ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "#bbb", position: "relative" }}>
                {listCollapsed && (
                  <button onClick={() => setListCollapsed(false)} title="Show conversations"
                    style={{ position: "absolute", top: 12, left: 12, background: "none", border: "none", cursor: "pointer", padding: "3px 6px", color: "#aaa", display: "flex", alignItems: "center", borderRadius: 5, lineHeight: 1 }}
                    onMouseEnter={e => e.currentTarget.style.color = "#555"}
                    onMouseLeave={e => e.currentTarget.style.color = "#aaa"}>
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M5.5 3L9.5 7.5L5.5 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><line x1="12" y1="2" x2="12" y2="13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  </button>
                )}
                <div style={{ color: "var(--accent)" }}><Icon name="message" size={38} /></div>
                <div style={{ fontSize: 14 }}>Select a conversation</div>
              </div>
            ) : (
              <>
                {/* Chat header — portaled to <body> on mobile so position:fixed is always
                    relative to the viewport, never to an overflow scroll container */}
                {isMobile ? createPortal(
                  <div style={{ position: "fixed", top: 57, left: 0, right: 0, zIndex: 100, background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "11px 16px", display: "flex", alignItems: "center", gap: 10, transform: "translateZ(0)", WebkitTransform: "translateZ(0)", willChange: "transform" }}>
                    <button onClick={() => setSelectedConvo(null)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 10px 4px 0", color: "#555", display: "flex", alignItems: "center", flexShrink: 0 }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <div onClick={() => handleAvatarClick(selectedConvo)} style={{ cursor: "pointer", flexShrink: 0 }}>
                      <Avatar initials={nameInitials(getOtherName(selectedConvo))} color={pickColor(getOtherName(selectedConvo) || "")} size={34}
                        url={avatarMap[myId === selectedConvo.lender_id ? selectedConvo.builder_id : selectedConvo.lender_id]} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{getOtherName(selectedConvo)}</div>
                      <div style={{ fontSize: 11, color: "#64748B" }}>{getOtherRole(selectedConvo)}</div>
                    </div>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <button onClick={e => { e.stopPropagation(); setHeaderMenuOpen(o => !o); }}
                        style={{ background: "none", border: "0.5px solid #e0e0e0", borderRadius: 7, padding: "5px 10px", fontSize: 18, color: "#64748B", cursor: "pointer", lineHeight: 1, display: "flex", alignItems: "center" }}>
                        ⋯
                      </button>
                      {headerMenuOpen && (
                        <>
                          <div onClick={() => setHeaderMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
                          <div onClick={e => e.stopPropagation()} style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: 180, overflow: "hidden" }}>
                            <button onClick={() => { setHeaderMenuOpen(false); setClearModal(true); }}
                              style={{ width: "100%", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                              Clear for everyone
                            </button>
                            <button onClick={() => { setHeaderMenuOpen(false); setDeleteConvoModal(true); }}
                              style={{ width: "100%", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#DC2626", display: "flex", alignItems: "center", gap: 8 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>
                              Delete for me
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>,
                  document.body
                ) : (
                  <div style={{ padding: "11px 16px", borderBottom: "0.5px solid #f0f0f0", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, background: "#fff" }}>
                    {listCollapsed && (
                      <button onClick={() => setListCollapsed(false)} title="Show conversations"
                        style={{ background: "none", border: "none", cursor: "pointer", padding: "3px 6px", color: "#aaa", display: "flex", alignItems: "center", borderRadius: 5, lineHeight: 1, flexShrink: 0 }}
                        onMouseEnter={e => e.currentTarget.style.color = "#555"}
                        onMouseLeave={e => e.currentTarget.style.color = "#aaa"}>
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M5.5 3L9.5 7.5L5.5 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><line x1="12" y1="2" x2="12" y2="13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                      </button>
                    )}
                    <div onClick={() => handleAvatarClick(selectedConvo)} style={{ cursor: "pointer", flexShrink: 0 }}>
                      <Avatar initials={nameInitials(getOtherName(selectedConvo))} color={pickColor(getOtherName(selectedConvo) || "")} size={34}
                        url={avatarMap[myId === selectedConvo.lender_id ? selectedConvo.builder_id : selectedConvo.lender_id]} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{getOtherName(selectedConvo)}</div>
                      <div style={{ fontSize: 11, color: "#64748B" }}>{getOtherRole(selectedConvo)}</div>
                    </div>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <button onClick={e => { e.stopPropagation(); setHeaderMenuOpen(o => !o); }}
                        style={{ background: "none", border: "0.5px solid #e0e0e0", borderRadius: 7, padding: "5px 10px", fontSize: 18, color: "#64748B", cursor: "pointer", lineHeight: 1, display: "flex", alignItems: "center" }}>
                        ⋯
                      </button>
                      {headerMenuOpen && (
                        <>
                          <div onClick={() => setHeaderMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
                          <div onClick={e => e.stopPropagation()} style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: 180, overflow: "hidden" }}>
                            <button onClick={() => { setHeaderMenuOpen(false); setClearModal(true); }}
                              style={{ width: "100%", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                              Clear for everyone
                            </button>
                            <button onClick={() => { setHeaderMenuOpen(false); setDeleteConvoModal(true); }}
                              style={{ width: "100%", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#DC2626", display: "flex", alignItems: "center", gap: 8 }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>
                              Delete for me
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {!chatReady ? (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 24, height: 24, border: "2.5px solid #e0e0e0", borderTopColor: "var(--accent, #3B82F6)", borderRadius: "50%", animation: "lb-spin 0.7s linear infinite" }} />
                  </div>
                ) : (
                <>

                {/* Mismatch banner — pinned above messages, separate from the deal widget */}
                {dmDc.status === "mismatch" && (
                  <div style={{ padding: "8px 16px", flexShrink: 0, background: "#fff", borderBottom: "0.5px solid #f0f0f0" }}>
                    <DealMismatchBanner dc={dmDc} otherName={dmOtherName} />
                  </div>
                )}

                {/* Pinned deal card + security notice — above messages scroll, always visible */}
                <div style={{ flexShrink: 0, background: "#fff", borderBottom: "0.5px solid #f0f0f0", padding: "0 16px 8px" }}>
                  {selectedConvo && !dismissedNotices.includes(selectedConvo.id) && (
                    <div style={{ background: "#EFF6FF", border: "0.5px solid #BFDBFE", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#1E40AF", lineHeight: 1.55, display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 4, marginTop: 8 }}>
                      <Icon name="lock" size={13} className="lb-icon" style={{opacity:0.6}} />
                      <span style={{ flex: 1 }}>You are now connected. You can share contact details privately but we recommend keeping deal agreements and payments on the platform for your protection.</span>
                      <button onClick={() => { const next = [...dismissedNotices, selectedConvo.id]; localStorage.setItem("lb_pm_dismissed_notices", JSON.stringify(next)); setDismissedNotices(next); }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#93C5FD", fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}>✕</button>
                    </div>
                  )}
                  {(dmDc.status === "confirmed" || dmDc.status === "paid" || dmDc.feePaid) && dmDc.dealId ? (
                    <button
                      onClick={async () => {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (!session) return;
                        const res = await fetch("/api/deals", { headers: { Authorization: `Bearer ${session.access_token}` } });
                        const d = await res.json().catch(() => ({}));
                        const deal = (d.deals || []).find(dl => dl.id === dmDc.dealId);
                        if (deal) setPage("deal-detail", deal);
                      }}
                      style={{ marginTop: 8, width: "100%", padding: "10px 16px", background: "var(--accent, #3B82F6)", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "center", display: "block" }}
                    >
                      Open Deal Room →
                    </button>
                  ) : (
                    <DealConfirmWidget
                      user={user}
                      otherName={getOtherName(selectedConvo)}
                      otherRole={getOtherRole(selectedConvo).toLowerCase()}
                      dc={dmDc}
                      hideMismatch
                    />
                  )}
                </div>

                {/* Messages list — column-reverse keeps scroll anchored at bottom natively */}
                <div ref={msgsScrollRef} style={{ position: "relative", flex: 1, minHeight: 0, overflowY: "scroll", overflowX: "hidden", paddingTop: 20, paddingRight: 12, paddingBottom: "1rem", paddingLeft: 12, display: "flex", flexDirection: "column-reverse", gap: 8 }}>
                  {messages.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#bbb", fontSize: 13, marginBottom: "2rem" }}>No messages yet — say hello!</div>
                  ) : (
                    messages.slice().reverse().map(msg => {
                      const isMine = msg.sender_id === myId;
                      const isScheduled = msg.status === "scheduled";
                      const isEditing = editingId === msg.id;

                      return (
                        <div key={msg.id} style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", position: "relative" }}>
                          <div style={{ maxWidth: isMobile ? "85%" : "72%", position: "relative" }}>
                            {/* Scheduled badge */}
                            {isScheduled && (
                              <div style={{ fontSize: 10, color: "#92400E", background: "#FEF3C7", border: "0.5px solid #FCD34D", borderRadius: 6, padding: "2px 7px", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                                <Icon name="clock" size={12} style={{marginRight:3,verticalAlign:"middle"}} />Scheduled for {fmtScheduled(msg.scheduled_at)}
                                <button onClick={() => handleCancelScheduled(msg.id)}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: "#92400E", fontSize: 10, padding: "0 2px", marginLeft: 4 }}>✕ Cancel</button>
                              </div>
                            )}

                            {/* Bubble */}
                            {isEditing ? (
                              <div style={{ background: "#fff", border: "1.5px solid #2E5FA3", borderRadius: 12, padding: "8px 10px" }}>
                                <textarea
                                  value={editContent}
                                  onChange={e => setEditContent(e.target.value)}
                                  autoFocus
                                  rows={3}
                                  style={{ width: "100%", border: "none", outline: "none", resize: "vertical", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }}
                                />
                                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                                  <button onClick={() => handleEditSave(msg.id)}
                                    style={{ padding: "4px 12px", background: "#2E5FA3", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>Save</button>
                                  <button onClick={() => { setEditingId(null); setEditContent(""); }}
                                    style={{ padding: "4px 10px", background: "transparent", border: "0.5px solid #ccc", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>Cancel</button>
                                </div>
                              </div>
                            ) : (() => {
                              // Detect deal proposal JSON
                              let proposal = null;
                              if (msg.content && msg.content.trimStart().startsWith("{")) {
                                try {
                                  const parsed = JSON.parse(msg.content);
                                  if (parsed.proposed_by) proposal = parsed;
                                } catch (_) {}
                              }
                              if (proposal) {
                                const statusColour = { pending: "#D97706", accepted: "#16A34A", rejected: "#DC2626" }[proposal.status] || "#64748B";
                                const statusLabel  = { pending: "Pending", accepted: "Accepted", rejected: "Rejected" }[proposal.status] || proposal.status;
                                return (
                                  <div style={{ background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 14, padding: "14px 16px", minWidth: 220, maxWidth: 300, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
                                    <div style={{ fontSize: 10, fontWeight: 600, color: "#2E5FA3", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Deal proposal</div>
                                    <div style={{ fontSize: 22, fontWeight: 700, color: "#1E3A5F", marginBottom: 4 }}>£{Number(proposal.amount).toLocaleString("en-GB")}</div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginBottom: 8 }}>
                                      {proposal.return_type && <span style={{ fontSize: 12, color: "#64748B" }}>{proposal.return_type === "fixed_interest" ? "Fixed interest" : proposal.return_type === "equity" ? "Equity share" : proposal.return_type}</span>}
                                      {proposal.rate != null && <span style={{ fontSize: 12, color: "#64748B" }}>{proposal.rate}%</span>}
                                      {proposal.timeline != null && <span style={{ fontSize: 12, color: "#64748B" }}>{proposal.timeline} months</span>}
                                    </div>
                                    {proposal.notes && <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5, marginBottom: 8, borderTop: "0.5px solid #f0f0f0", paddingTop: 8 }}>{proposal.notes}</div>}
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                      <span style={{ fontSize: 11, color: statusColour, fontWeight: 600, background: `${statusColour}18`, padding: "2px 8px", borderRadius: 20 }}>{statusLabel}</span>
                                      <span style={{ fontSize: 10, color: "#aaa" }}>{isMine ? "You proposed" : "They proposed"}</span>
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <div style={{
                                  background: isScheduled ? "#FEF9C3" : isMine ? "#1E3A5F" : "#f5f5f3",
                                  color: isScheduled ? "#713F12" : isMine ? "#fff" : "#222",
                                  borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                                  padding: "8px 12px", fontSize: 14, lineHeight: 1.5,
                                }}>
                                  <div style={{ whiteSpace: "pre-wrap" }}>{msg.content}</div>
                                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 3, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
                                    {msg.edited_at && <span style={{ fontStyle: "italic" }}>edited</span>}
                                    {new Date(msg.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Three-dots menu for own messages */}
                            {isMine && !isEditing && (
                              <div style={{ position: "relative", zIndex: 10 }}>
                                <button onClick={e => { e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); setMsgMenuUp(rect.bottom > window.innerHeight * 0.6); setMsgMenuId(msgMenuId === msg.id ? null : msg.id); }}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 14, padding: "2px 4px", lineHeight: 1 }}>⋯</button>
                                {msgMenuId === msg.id && (
                                  <>
                                    <div onClick={() => setMsgMenuId(null)} style={{ position: "fixed", inset: 0, zIndex: 999, cursor: "pointer" }} />
                                    <div onClick={e => e.stopPropagation()} style={{ position: "absolute", right: "calc(100% + 4px)", ...(msgMenuUp ? { bottom: 0 } : { top: 0 }), background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: 100, overflow: "hidden" }}>
                                      {!isScheduled && (
                                        <button onClick={() => { setEditingId(msg.id); setEditContent(msg.content); setMsgMenuId(null); }}
                                          style={{ width: "100%", padding: "8px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#374151" }}>
                                          Edit
                                        </button>
                                      )}
                                      {isScheduled && (
                                        <button onClick={() => { handleCancelScheduled(msg.id); setMsgMenuId(null); }}
                                          style={{ width: "100%", padding: "8px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#DC2626" }}>
                                          Cancel
                                        </button>
                                      )}
                                      <button onClick={() => { setDeleteMsgId(msg.id); setMsgMenuId(null); }}
                                        style={{ width: "100%", padding: "8px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#DC2626" }}>
                                        Delete
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                </>
                )}

                {/* Schedule picker */}
                {schedulePickerOpen && (
                  <div style={{ padding: "8px 14px", borderTop: "0.5px solid #f0f0f0", background: "#FEFCE8", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: "#92400E", fontWeight: 500 }}><Icon name="clock" size={12} style={{marginRight:3,verticalAlign:"middle"}} />Schedule for:</span>
                    <input type="datetime-local" min={minDateTime} value={scheduledAt}
                      onChange={e => setScheduledAt(e.target.value)}
                      style={{ flex: 1, minWidth: 180, height: 34, border: "0.5px solid #FCD34D", borderRadius: 7, padding: "0 8px", fontSize: 12, background: "#fff" }} />
                    <button onClick={() => { setSchedulePickerOpen(false); setScheduledAt(""); }}
                      style={{ background: "none", border: "none", color: "#92400E", cursor: "pointer", fontSize: 13 }}>✕</button>
                  </div>
                )}

                {/* Input */}
                <form onSubmit={handleSend} style={{ padding: isMobile ? "6px 14px" : "10px 14px", margin: 0, borderTop: "1px solid #e5e7eb", display: "flex", gap: 8, alignItems: "flex-end", flexShrink: 0 }}>
                  <textarea ref={inputRef} value={newMessage} onChange={e => setNewMessage(e.target.value)}
                    rows={1}
                    placeholder={scheduledAt ? "Type message to schedule…" : "Type a message…"}
                    style={{ flex: 1, minHeight: 44, maxHeight: 120, border: `0.5px solid ${scheduledAt ? "#FCD34D" : "#ddd"}`, borderRadius: 20, padding: "11px 14px", fontSize: isMobile ? 16 : 14, background: scheduledAt ? "#FEFCE8" : "#fafafa", outline: "none", boxSizing: "border-box", resize: "none", overflowY: "auto", fontFamily: "inherit", lineHeight: "1.4" }}
                    autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !isMobile) { e.preventDefault(); handleSend(e); } }} />

                  {/* Clock / schedule button */}
                  <button type="button" onClick={() => setSchedulePickerOpen(o => !o)} title="Schedule message"
                    style={{ width: 36, height: 36, borderRadius: "50%", border: "none", flexShrink: 0, background: scheduledAt ? "#FCD34D" : "#f0f0f0", color: scheduledAt ? "#92400E" : "#888", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  </button>

                  <button type="submit" disabled={!newMessage.trim() || sending} aria-label="Send message"
                    style={{ width: 44, height: 44, borderRadius: "50%", flexShrink: 0, background: newMessage.trim() && !sending ? (scheduledAt ? "#D97706" : "#2E5FA3") : "#e0e0e0", border: "none", cursor: newMessage.trim() && !sending ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M14 8H2M14 8L9 3M14 8L9 13" stroke={newMessage.trim() && !sending ? "#fff" : "#aaa"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── COMMUNITY PAGE ──────────────────────────────────────────────────────────

const COMMUNITY_CHANNELS = [
  { id: "general", label: "General",            desc: "Open discussion" },
  { id: "funding", label: "Looking for funding", desc: "Builders post what they need" },
  { id: "lending", label: "Available to lend",   desc: "Lenders post what they're offering" },
];

// Link / phone / off-platform blocking
const _LINK_RE   = /(?:https?:\/\/|www\.)[\w\-./?=#&%+:@!~]+|[\w-]+\.(?:com|co\.uk|org\.uk|net|io|app|ai|tech|online|info|biz|co|uk)(?:[/\s?#][^\s]*)?/gi;
const _PHONE_RE  = /(?:\+44[\s-]?|0044[\s-]?|0(?:7\d{3}|800|808|870|845|844|1\d{3}|2\d{3}|3\d{3}))[\s-]?\d{3}[\s-]?\d{3,4}/g;
const _OFFSITE_RE= /\b(?:whatsapp|wha?ts[\s-]?app|telegram|t\.me\/|signal\.me|snapchat|wechat|instagram|imessage|facetime)\b/gi;
const _ALLOWED_  = /lenderbuild\.co\.uk/i;

function sanitizeChatMessage(text) {
  let s = text; let blocked = false;
  s = s.replace(_LINK_RE,    m => { if (_ALLOWED_.test(m)) return m; blocked = true; return "[link removed - connect through the platform]"; });
  s = s.replace(_PHONE_RE,   () => { blocked = true; return "[link removed - connect through the platform]"; });
  s = s.replace(_OFFSITE_RE, () => { blocked = true; return "[link removed - connect through the platform]"; });
  return { sanitized: s.trim(), blocked };
}

// Auto-moderation keyword list
const AUTOMOD_TERMS = [
  "investment guaranteed","guaranteed returns","100% returns","100% guaranteed",
  "guaranteed profit","send money","bank transfer","wire transfer",
  "western union","moneygram","bitcoin","cryptocurrency","crypto ",
  " btc "," eth "," usdt ","cash only","no questions asked",
  "untraceable","offshore account",
];
function checkAutomod(text) {
  const l = " " + text.toLowerCase() + " ";
  for (const t of AUTOMOD_TERMS) if (l.includes(t)) return t.trim();
  return null;
}

// Rate limiting via localStorage (5 msg / 60s)
function chatRateLimitAllowed(userId) {
  const k = `lb_rl_${userId}`, now = Date.now();
  const ts = JSON.parse(localStorage.getItem(k) || "[]").filter(t => now - t < 60000);
  if (ts.length >= 5) return false;
  localStorage.setItem(k, JSON.stringify([...ts, now].slice(-20)));
  return true;
}
function chatCooldownSecs(userId) {
  const k = `lb_rl_${userId}`, now = Date.now();
  const ts = JSON.parse(localStorage.getItem(k) || "[]").filter(t => now - t < 60000);
  if (ts.length < 5) return 0;
  return Math.max(1, Math.ceil((Math.min(...ts) + 60000 - now) / 1000));
}

const COMMUNITY_WARN_KEY = "lb_comm_warned_v1";

// ── Report reasons ────────────────────────────────────────────────────────────
const REPORT_REASONS = ["Spam", "Scam or fraud", "Inappropriate content", "Off-platform solicitation"];

function MessageBubble({ msg, myId, onReport, reported }) {
  const isOwn    = msg.user_id === myId;
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [subOpen,   setSubOpen]   = useState(false);
  const [reportSent, setReportSent] = useState(reported || false);
  const menuRef = useRef(null);

  useEffect(() => {
    function outside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) { setMenuOpen(false); setSubOpen(false); }
    }
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  if (msg.hidden) {
    return (
      <div style={{ display: "flex", gap: 10, marginBottom: "0.875rem", alignItems: "center" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#f0f0f0", flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: "#aaa", fontStyle: "italic", background: "#f9f9f7", padding: "5px 12px", borderRadius: 8, border: "0.5px solid #ececec" }}>
          [message under review]
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: "0.875rem", alignItems: "flex-start" }}>
      <Avatar initials={nameInitials(msg.user_name || "?")} color={pickColor(msg.user_id || "")} size={34} url={msg.user_avatar_url || null} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: isOwn ? "#2E5FA3" : "#1E3A5F" }}>{msg.user_name || "User"}</span>
          <RoleBadge authRole={msg.user_role} />
          {msg.sequential_id && <span style={{ fontSize: 11, color: "#94A3B8", fontFamily: "monospace" }}>{fmtId(msg.sequential_id)}</span>}
          <span style={{ fontSize: 11, color: "#aaa", marginLeft: "auto", flexShrink: 0 }}>{fmtTimeAgo(msg.created_at)}</span>
          {!isOwn && (
            <div ref={menuRef} style={{ position: "relative", flexShrink: 0 }}>
              <button onClick={() => { setMenuOpen(o => !o); setSubOpen(false); }}
                style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 5px", color: "#bbb", borderRadius: 4, fontSize: 17, lineHeight: 1 }}>
                ···
              </button>
              {menuOpen && (
                <div style={{ position: "absolute", right: 0, top: "calc(100% + 2px)", background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.10)", zIndex: 200, minWidth: 180, overflow: "hidden" }}>
                  {!subOpen ? (
                    <button onClick={() => setSubOpen(true)} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 13, color: "#D85A30", background: "none", border: "none", cursor: "pointer" }}>
                      Report message
                    </button>
                  ) : (
                    <>
                      <div style={{ padding: "7px 14px 5px", fontSize: 11, fontWeight: 600, color: "#777", borderBottom: "0.5px solid #f0f0f0" }}>Select reason:</div>
                      {REPORT_REASONS.map(r => (
                        <button key={r}
                          onClick={async () => {
                            setMenuOpen(false); setSubOpen(false);
                            if (!reportSent) { setReportSent(true); await onReport(msg, r); }
                          }}
                          disabled={reportSent}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 14px", fontSize: 13, color: reportSent ? "#aaa" : "#333", background: "none", border: "none", cursor: reportSent ? "default" : "pointer" }}>
                          {r}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ fontSize: 14, color: "#333", lineHeight: 1.55, wordBreak: "break-word" }}>{msg.content}</div>
        {reportSent && !msg.hidden && <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>Reported — thank you</div>}
      </div>
    </div>
  );
}

function ScamWarningModal({ onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div style={{ background: "#fff", borderRadius: 16, maxWidth: 440, width: "100%", padding: "1.75rem", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
          <Icon name="shield" size={28} className="lb-icon" />
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: "#1E3A5F" }}>Stay safe in community chat</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: "1.5rem" }}>
          {[
            "LenderBuild will never ask you to transfer money outside the platform.",
            "Always verify a user's ID badge before agreeing to any deal.",
            "Report anyone asking for personal bank details in chat.",
            "All legitimate deals happen through the Project Tracker only.",
          ].map((line, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ color: "#2E5FA3", fontWeight: 700, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 14, color: "#333", lineHeight: 1.5 }}>{line}</span>
            </div>
          ))}
        </div>
        <button onClick={onClose}
          style={{ width: "100%", background: "#2E5FA3", color: "#fff", border: "none", padding: "12px", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
          I understand — enter community
        </button>
      </div>
    </div>
  );
}

// ─── SHARE TO GROUP MODAL ────────────────────────────────────────────────────

function ShareToGroupModal({ post, user, onClose, setPage }) {
  const [groups,       setGroups]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState(new Set());
  const [sharing,      setSharing]      = useState(false);
  const [sharedGroups, setSharedGroups] = useState(null);

  useEffect(() => {
    async function load() {
      const { data: memberRows } = await supabase
        .from("community_group_members").select("group_id").eq("user_id", user.id);
      if (!memberRows?.length) { setGroups([]); setLoading(false); return; }
      const { data: groupRows } = await supabase
        .from("community_groups").select("id, name, region")
        .in("id", memberRows.map(r => r.group_id));
      setGroups(groupRows || []);
      setLoading(false);
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleGroup(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleShare() {
    if (!selected.size || sharing) return;
    setSharing(true);
    const name = user.user_metadata?.name || user.email?.split("@")[0] || "User";
    const preview = post.body.length > 120 ? post.body.slice(0, 120).trimEnd() + "…" : post.body;
    const content = `📌 Shared a post: "${post.title}"\n${preview}`;
    const { data: profile } = await supabase.from("profiles").select("sequential_id").eq("id", user.id).maybeSingle();
    const targets = groups.filter(g => selected.has(g.id));
    await Promise.all(targets.map(async g => {
      const { data: chan } = await supabase
        .from("group_channels").select("id").eq("group_id", g.id).eq("is_default", true).maybeSingle();
      await supabase.from("group_messages").insert({
        group_id: g.id, channel_id: chan?.id || null, user_id: user.id,
        content, user_name: name, user_role: user.user_metadata?.role || "member",
        sequential_id: profile?.sequential_id || null,
        shared_post_id: post.id,
      });
      await supabase.from("community_groups").update({ last_activity: new Date().toISOString() }).eq("id", g.id);
    }));
    setSharedGroups(targets);
    setSharing(false);
  }

  const count = selected.size;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: "1.5rem", width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#1E3A5F" }}>Share to groups</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#94A3B8", padding: "0 4px" }}>✕</button>
        </div>

        {sharedGroups !== null ? (
          <>
            {/* Checkmark + heading */}
            <div style={{ textAlign: "center", padding: "0.5rem 0 1.25rem" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#1E3A5F", marginBottom: 4 }}>Shared successfully!</div>
              <div style={{ fontSize: 13, color: "#64748B" }}>
                Your post was shared to {sharedGroups.length} {sharedGroups.length === 1 ? "group" : "groups"}.
              </div>
            </div>

            {/* Group list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "1.25rem" }}>
              {sharedGroups.map(g => (
                <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#F8FAFC", border: "0.5px solid #e5e7eb", borderRadius: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: "var(--accent-bg, #EBF2FF)", color: "var(--accent, #3B82F6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                    {g.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</div>
                    {g.region && <div style={{ fontSize: 12, color: "#94A3B8" }}>{g.region}</div>}
                  </div>
                  <button
                    onClick={() => { sessionStorage.setItem("lb_open_group", g.id); onClose(); setPage("community-groups"); }}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--accent, #3B82F6)", whiteSpace: "nowrap", padding: "4px 6px", borderRadius: 6, flexShrink: 0 }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                    onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
                  >
                    View group →
                  </button>
                </div>
              ))}
            </div>

            {/* Done button */}
            <button
              onClick={onClose}
              style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", fontSize: 14, fontWeight: 700, background: "var(--accent, #3B82F6)", color: "#fff", cursor: "pointer" }}
            >
              Done
            </button>
          </>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "1.5rem", color: "#aaa", fontSize: 14 }}>Loading your groups…</div>
        ) : groups.length === 0 ? (
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <p style={{ color: "#64748B", margin: "0 0 0.75rem", fontSize: 14 }}>You haven't joined any communities yet.</p>
            <button onClick={() => { onClose(); setPage("community-groups"); }}
              style={{ color: "var(--accent, #3B82F6)", background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
              Browse communities →
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "1rem" }}>
              {groups.map(g => {
                const checked = selected.has(g.id);
                return (
                  <div key={g.id}
                    onClick={() => toggleGroup(g.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                      background: checked ? "var(--accent-bg, #EBF2FF)" : "#F8FAFC",
                      border: `0.5px solid ${checked ? "var(--accent, #3B82F6)" : "#e5e7eb"}`,
                      borderRadius: 10, cursor: "pointer", userSelect: "none",
                    }}>
                    {/* Custom checkbox */}
                    <div style={{
                      width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                      border: `2px solid ${checked ? "var(--accent, #3B82F6)" : "#cbd5e1"}`,
                      background: checked ? "var(--accent, #3B82F6)" : "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.12s, border-color 0.12s",
                    }}>
                      {checked && (
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    {/* Avatar */}
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: "var(--accent-bg, #EBF2FF)", color: "var(--accent, #3B82F6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
                      {g.name.charAt(0).toUpperCase()}
                    </div>
                    {/* Name + region */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</div>
                      {g.region && <div style={{ fontSize: 12, color: "#94A3B8" }}>{g.region}</div>}
                    </div>
                    {/* Navigate arrow — stops propagation so it doesn't toggle checkbox */}
                    <button
                      onClick={e => { e.stopPropagation(); onClose(); setPage("community-groups"); }}
                      title={`Go to ${g.name}`}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", color: "#94A3B8", display: "flex", alignItems: "center", flexShrink: 0, borderRadius: 6, marginRight: -4 }}
                      onMouseEnter={e => { e.currentTarget.style.color = "var(--accent, #3B82F6)"; }}
                      onMouseLeave={e => { e.currentTarget.style.color = "#94A3B8"; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
            <button
              onClick={handleShare}
              disabled={!count || sharing}
              style={{
                width: "100%", padding: "12px", borderRadius: 10, border: "none", fontSize: 14, fontWeight: 700,
                background: count && !sharing ? "var(--accent, #3B82F6)" : "#e0e0e0",
                color: count && !sharing ? "#fff" : "#aaa",
                cursor: count && !sharing ? "pointer" : "default",
                transition: "background 0.15s",
              }}
            >
              {sharing ? "Sharing…" : count ? `Share to ${count} ${count === 1 ? "group" : "groups"}` : "Select groups above"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── COMMUNITY POST CARD (social-style) ──────────────────────────────────────

function CommunityPostCard({ post, user, onView, onLike, isOwn, onEdit, onDelete, setPage }) {
  const cat = CATEGORY_META[post.category] || CATEGORY_META.general;
  const [liked,          setLiked]          = useState(post.user_has_interest || false);
  const [likeCount,      setLikeCount]      = useState(post.interest_count || 0);
  const [animating,      setAnimating]      = useState(false);
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [removing,       setRemoving]       = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handler(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  async function handleLike(e) {
    e.stopPropagation();
    if (!user) { onView(); return; }
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount(c => wasLiked ? c - 1 : c + 1);
    setAnimating(true);
    setTimeout(() => setAnimating(false), 500);
    onLike(post, !wasLiked);
  }

  function handleDeleteClick(e) {
    e.stopPropagation();
    setMenuOpen(false);
    setRemoving(true);
    setTimeout(() => onDelete && onDelete(post.id), 300);
  }

  return (
    <div
      onClick={removing ? undefined : onView}
      style={{
        position: "relative",
        background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 16, padding: "1.25rem",
        marginBottom: removing ? 0 : 12, cursor: removing ? "default" : "pointer",
        opacity: removing ? 0 : 1,
        maxHeight: removing ? 0 : 2000,
        overflow: "hidden",
        transition: "opacity 0.3s ease, max-height 0.3s ease, margin-bottom 0.3s ease",
      }}
      onMouseEnter={e => { if (!removing) e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,0,0,0.08)"; }}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <span style={{ background: cat.bg, color: cat.text, fontSize: 12, padding: "4px 10px", borderRadius: 20, fontWeight: 600 }}>{cat.label}</span>
        {(isOwn || !!user) && (
          <div ref={menuRef} style={{ marginLeft: "auto", position: "relative" }} onClick={e => e.stopPropagation()}>
            <button
              onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 8, color: "#94A3B8", fontSize: 20, lineHeight: 1, display: "flex", alignItems: "center" }}
              title="Post options"
            >⋯</button>
            {menuOpen && (
              <div style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 150, overflow: "hidden" }}>
                {isOwn && (
                  <>
                    <button
                      onClick={e => { e.stopPropagation(); setMenuOpen(false); onEdit && onEdit(post); }}
                      style={{ display: "block", width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 14, color: "#1E293B" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}
                    >Edit</button>
                    <button
                      onClick={handleDeleteClick}
                      style={{ display: "block", width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 14, color: "#DC2626", borderBottom: "0.5px solid #f5f5f5" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#FEF2F2"}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}
                    >Delete</button>
                  </>
                )}
                <button
                  onClick={e => { e.stopPropagation(); setMenuOpen(false); setShowShareModal(true); }}
                  style={{ display: "block", width: "100%", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: 14, color: "#1E293B" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >Share to group</button>
              </div>
            )}
          </div>
        )}
        {showShareModal && user && (
          <ShareToGroupModal post={post} user={user} onClose={() => setShowShareModal(false)} setPage={setPage || (() => {})} />
        )}
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: "#1E3A5F", marginBottom: 8, lineHeight: 1.3 }}>{post.title}</div>
      <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.6, margin: "0 0 14px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{post.body}</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: cat.bg, color: cat.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {(post.author_name || "?").charAt(0).toUpperCase()}
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{post.author_name}</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", flexShrink: 0 }}>·</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", flexShrink: 0, whiteSpace: "nowrap" }}>{fmtTimeAgo(post.created_at)}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {(post.view_count || 0) > 0 && (
            <span style={{ fontSize: 12, color: "#9CA3AF", display: "flex", alignItems: "center", gap: 3 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              {post.view_count}
            </span>
          )}
          <button
            onClick={handleLike}
            style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", padding: "5px 8px", borderRadius: 10, transition: "background 0.15s", color: liked ? "#E53E3E" : "#9CA3AF" }}
            onMouseEnter={e => e.currentTarget.style.background = "#FFF5F5"}
            onMouseLeave={e => e.currentTarget.style.background = "none"}
          >
            <span style={{ fontSize: 17, display: "inline-block", transform: animating ? "scale(1.5)" : "scale(1)", transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1)" }}>
              {liked ? <Icon name="heart-fill" size={14} style={{color:"#DC2626"}} /> : <Icon name="heart" size={14} />}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{likeCount}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── POSTS SECTION (embedded in Community Hub) ────────────────────────────────

function PostsSection({ user, setPage, onMessage, openPostId, onPostOpened }) {
  const [posts,            setPosts]            = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState("");
  const [catFilter,        setCatFilter]        = useState("all");
  const [showMine,         setShowMine]         = useState(false);
  const [viewingPost,      setViewingPost]      = useState(null);
  const [editingPost,      setEditingPost]      = useState(null);
  const [toast,            setToast]            = useState("");
  const [contentKey,       setContentKey]       = useState(0);
  const [postsFilterOpen,  setPostsFilterOpen]  = useState(false);
  const [fundingFilterP,   setFundingFilterP]   = useState("any");
  const [projectTypeFilterP, setProjectTypeFilterP] = useState("any");
  const [regionFilterP,    setRegionFilterP]    = useState("any");
  const [trackRecordFilterP, setTrackRecordFilterP] = useState("any");
  const [isMobile,         setIsMobile]         = useState(() => window.innerWidth <= 768);
  const handledPostIdRef = useRef(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => { loadPosts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Open a specific post from a deep link once posts have finished loading
  useEffect(() => {
    if (!openPostId || loading || handledPostIdRef.current === openPostId) return;
    handledPostIdRef.current = openPostId;
    const found = posts.find(p => String(p.id) === String(openPostId));
    if (found) {
      setViewingPost(found);
      scrollMainToTop();
    } else {
      setToast("That post is no longer available");
      setTimeout(() => setToast(""), 4000);
    }
    onPostOpened?.();
  }, [loading, openPostId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPosts() {
    setLoading(true);
    try {
      const headers = {};
      const { data: { session } } = await supabase.auth.getSession();
      if (session) headers.Authorization = `Bearer ${session.access_token}`;
      const res  = await fetch("/api/posts", { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to load posts");
      const raw = json.posts || [];
      if (raw.length > 0) {
        const authorIds = [...new Set(raw.map(p => p.author_id))];
        const [{ data: profRows }, { data: bpRows }] = await Promise.all([
          supabase.from("profiles").select("id, location").in("id", authorIds),
          supabase.from("builder_profiles").select("user_id, projects_completed").in("user_id", authorIds),
        ]);
        const profMap = Object.fromEntries((profRows || []).map(r => [r.id, r]));
        const bpMap   = Object.fromEntries((bpRows  || []).map(r => [r.user_id, r]));
        setPosts(raw.map(p => ({
          ...p,
          author_location:           profMap[p.author_id]?.location || "",
          author_projects_completed: bpMap[p.author_id]?.projects_completed || 0,
        })));
      } else {
        setPosts([]);
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  async function handleLike(post) {
    if (!user) { setPage("auth"); return; }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    fetch("/api/posts", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "interest", post_id: post.id, author_id: post.author_id }),
    }).catch(() => {});
  }

  async function handleView(post) {
    if (user && post.author_id !== user.id) {
      const key = `lb_pv_${post.id}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          fetch("/api/posts", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ action: "view", post_id: post.id, author_id: post.author_id }),
          }).catch(() => {});
        }
      }
    }
    setViewingPost(post);
    scrollMainToTop();
  }

  async function handleDelete(postId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      fetch("/api/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: "delete", post_id: postId }),
      }).catch(() => {});
    }
    setPosts(prev => prev.filter(p => p.id !== postId));
    setToast("Post deleted");
    setTimeout(() => setToast(""), 3000);
  }

  function handleSaved(updatedPost) {
    setPosts(prev => prev.map(p => p.id === updatedPost.id ? { ...p, ...updatedPost } : p));
    if (viewingPost && viewingPost.id === updatedPost.id) {
      setViewingPost(prev => ({ ...prev, ...updatedPost }));
      setContentKey(k => k + 1);
    }
    setEditingPost(null);
    setToast("Post updated successfully");
    setTimeout(() => setToast(""), 3500);
    scrollMainToTop();
  }

  if (editingPost) {
    return (
      <>
        <CreateEditPostPage
          user={user}
          setPage={setPage}
          editPost={editingPost}
          onSaved={handleSaved}
          onCancelBack={() => { setEditingPost(null); scrollMainToTop(); }}
        />
        {toast && (
          <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "#DC2626", color: "#fff", padding: "11px 22px", borderRadius: 10, fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.18)", zIndex: 9999, pointerEvents: "none" }}>
            ✕ {toast}
          </div>
        )}
      </>
    );
  }

  if (viewingPost) {
    return (
      <>
        <div key={contentKey} style={{ animation: contentKey > 0 ? "lbFadeIn 0.3s ease forwards" : "none" }}>
        <PostDetailPage
          post={viewingPost}
          user={user}
          setPage={setPage}
          onMessage={onMessage}
          onEdit={post => { setEditingPost(post); scrollMainToTop(); }}
          onDelete={async (postId) => {
            await handleDelete(postId);
            setViewingPost(null);
            scrollMainToTop();
          }}
          onBack={() => { setViewingPost(null); scrollMainToTop(); }}
        />
        </div>
        {toast && (
          <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "#16A34A", color: "#fff", padding: "11px 22px", borderRadius: 10, fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.18)", zIndex: 9999, pointerEvents: "none" }}>
            ✓ {toast}
          </div>
        )}
      </>
    );
  }

  const postsActiveFilters = [fundingFilterP !== "any", projectTypeFilterP !== "any", regionFilterP !== "any", trackRecordFilterP !== "any"].filter(Boolean).length;

  const filtered = posts.filter(p => {
    if (catFilter !== "all" && p.category !== catFilter) return false;
    if (showMine && !(user && p.author_id === user.id)) return false;
    if (fundingFilterP !== "any") {
      const fn = Number(p.funding_needed) || 0;
      if (fn === 0) return false;
      if (fundingFilterP === "under-50k"  && !(fn < 50000))            return false;
      if (fundingFilterP === "50k-150k"   && !(fn >= 50000 && fn <= 150000)) return false;
      if (fundingFilterP === "150k-500k"  && !(fn > 150000 && fn <= 500000)) return false;
      if (fundingFilterP === "500k-plus"  && !(fn > 500000))           return false;
    }
    if (projectTypeFilterP !== "any") {
      const text = ((p.title || "") + " " + (p.body || "")).toLowerCase();
      const typeMap = { "New build": ["new build","newbuild","new-build"], "Renovation": ["renov","refurb"], "Commercial": ["commercial"], "Mixed use": ["mixed use","mixed-use"] };
      const keys = typeMap[projectTypeFilterP] || [];
      if (keys.length > 0 && !keys.some(k => text.includes(k))) return false;
    }
    if (regionFilterP !== "any" && !inRegionBucket(p.author_location || p.location || "", regionFilterP)) return false;
    if (trackRecordFilterP === "1-plus" && (p.author_projects_completed || 0) < 1) return false;
    if (trackRecordFilterP === "3-plus" && (p.author_projects_completed || 0) < 3) return false;
    return true;
  });
  const sorted   = [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", gap: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => setPostsFilterOpen(o => !o)}
            style={{ display: "flex", alignItems: "center", gap: 7, background: postsFilterOpen ? "#1E3A5F" : "#fff", border: `0.5px solid ${postsFilterOpen ? "#1E3A5F" : "#ccc"}`, borderRadius: 8, padding: "8px 14px", minHeight: 40, fontSize: 13, cursor: "pointer", fontWeight: 500, color: postsFilterOpen ? "#fff" : "#1E3A5F" }}
          >
            <svg width="15" height="11" viewBox="0 0 16 12" fill="none"><path d="M0 1h16M3 6h10M6 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Filters
            {postsActiveFilters > 0 && <span style={{ background: postsFilterOpen ? "rgba(255,255,255,0.3)" : "var(--accent,#2E5FA3)", color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>{postsActiveFilters}</span>}
          </button>
          {postsActiveFilters > 0 && (
            <button onClick={() => { setFundingFilterP("any"); setProjectTypeFilterP("any"); setRegionFilterP("any"); setTrackRecordFilterP("any"); }}
              style={{ background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
              Clear
            </button>
          )}
        </div>
        <button
          onClick={() => user ? setPage("create-post") : setPage("auth")}
          style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "9px 18px", minHeight: 44, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
        >
          + Create post
        </button>
      </div>

      {postsFilterOpen && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "16px 20px", marginBottom: "1rem", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Funding amount</div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {[
                  { v: "any",       l: "Any" },
                  { v: "under-50k", l: "Under £50k" },
                  { v: "50k-150k",  l: "£50k–£150k" },
                  { v: "150k-500k", l: "£150k–£500k" },
                  { v: "500k-plus", l: "£500k+" },
                ].map(o => (
                  <button key={o.v} onClick={() => setFundingFilterP(o.v)} style={{ padding: "4px 10px", minHeight: 28, borderRadius: 20, border: fundingFilterP === o.v ? "none" : "0.5px solid #e0e0e0", background: fundingFilterP === o.v ? "var(--accent,#2E5FA3)" : "#fff", color: fundingFilterP === o.v ? "#fff" : "#555", fontSize: 12, fontWeight: fundingFilterP === o.v ? 600 : 400, cursor: "pointer" }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Project type</div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {["any", "New build", "Renovation", "Commercial", "Mixed use"].map(o => (
                  <button key={o} onClick={() => setProjectTypeFilterP(o)} style={{ padding: "4px 10px", minHeight: 28, borderRadius: 20, border: projectTypeFilterP === o ? "none" : "0.5px solid #e0e0e0", background: projectTypeFilterP === o ? "var(--accent,#2E5FA3)" : "#fff", color: projectTypeFilterP === o ? "#fff" : "#555", fontSize: 12, fontWeight: projectTypeFilterP === o ? 600 : 400, cursor: "pointer" }}>
                    {o === "any" ? "Any" : o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Location / region</div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {["any", "London", "North", "Midlands", "South", "Scotland", "Wales"].map(o => (
                  <button key={o} onClick={() => setRegionFilterP(o)} style={{ padding: "4px 10px", minHeight: 28, borderRadius: 20, border: regionFilterP === o ? "none" : "0.5px solid #e0e0e0", background: regionFilterP === o ? "var(--accent,#2E5FA3)" : "#fff", color: regionFilterP === o ? "#fff" : "#555", fontSize: 12, fontWeight: regionFilterP === o ? 600 : 400, cursor: "pointer" }}>
                    {o === "any" ? "Any" : o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Builder track record</div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {[
                  { v: "any",    l: "Any" },
                  { v: "1-plus", l: "1+ completed" },
                  { v: "3-plus", l: "3+ completed" },
                ].map(o => (
                  <button key={o.v} onClick={() => setTrackRecordFilterP(o.v)} style={{ padding: "4px 10px", minHeight: 28, borderRadius: 20, border: trackRecordFilterP === o.v ? "none" : "0.5px solid #e0e0e0", background: trackRecordFilterP === o.v ? "var(--accent,#2E5FA3)" : "#fff", color: trackRecordFilterP === o.v ? "#fff" : "#555", fontSize: 12, fontWeight: trackRecordFilterP === o.v ? 600 : 400, cursor: "pointer" }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "#16A34A", color: "#fff", padding: "11px 22px", borderRadius: 10, fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.18)", zIndex: 9999, pointerEvents: "none" }}>
          ✓ {toast}
        </div>
      )}

      <div style={{ overflowX: "auto", whiteSpace: "nowrap", marginBottom: "1.25rem", scrollbarWidth: "none" }}>
        {POST_CATEGORY_FILTERS.map(f => (
          <button key={f.key} onClick={() => setCatFilter(f.key)}
            style={{ display: "inline-block", padding: "7px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: "pointer", border: "none", minHeight: 36, background: catFilter === f.key ? "#1E3A5F" : "#F1F5F9", color: catFilter === f.key ? "#fff" : "#475569", transition: "background 0.15s", marginRight: 6 }}>
            {f.label}
            {f.key !== "all" && posts.filter(p => p.category === f.key).length > 0 && (
              <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.65 }}>({posts.filter(p => p.category === f.key).length})</span>
            )}
          </button>
        ))}
        {user && (
          <button
            onClick={() => setShowMine(v => !v)}
            style={{ display: "inline-block", padding: "7px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none", minHeight: 36, background: showMine ? "#3B82F6" : "#F1F5F9", color: showMine ? "#fff" : "#475569", transition: "background 0.15s" }}>
            My posts
            {showMine && posts.filter(p => p.author_id === user.id).length > 0 && (
              <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.8 }}>({posts.filter(p => p.author_id === user.id).length})</span>
            )}
          </button>
        )}
      </div>

      {loading && <div style={{ textAlign: "center", padding: "3rem", color: "#64748B", fontSize: 14 }}>Loading posts…</div>}
      {error && <div style={{ background: "#FAECE7", color: "#993C1D", borderRadius: 8, padding: "12px 16px", fontSize: 13, marginBottom: "1rem" }}>{error}</div>}

      {!loading && !error && sorted.length === 0 && (
        <div style={{ textAlign: "center", padding: "3rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="edit" size={36} /></div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>
            {showMine ? "You haven't posted yet" : catFilter !== "all" ? "No posts in this category yet" : "No posts yet"}
          </div>
          <span style={{ fontSize: 13 }}>{showMine ? "Tap + Create post to share something." : "Be the first to share something with the community."}</span>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
        {sorted.map(p => (
          <CommunityPostCard
            key={p.id}
            post={p}
            user={user}
            isOwn={!!(user && p.author_id === user.id)}
            onView={() => handleView(p)}
            onLike={(post) => handleLike(post)}
            onEdit={post => { setEditingPost(post); scrollMainToTop(); }}
            onDelete={handleDelete}
            setPage={setPage}
          />
        ))}
      </div>
    </div>
  );
}

// ─── FULL-SCREEN COMMUNITY SUB-PAGE WRAPPER ──────────────────────────────────
function FullScreenCommunityPage({ title, onBack, children }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  if (!isMobile) return <>{children}</>;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 1000, display: "flex", flexDirection: "column", overflowY: "hidden" }}>
      <div style={{ background: "#1E3A5F", height: 56, display: "flex", alignItems: "center", padding: "0 16px", flexShrink: 0, position: "relative" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "8px 4px", display: "flex", alignItems: "center", gap: 6, fontSize: 14, zIndex: 1 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", fontSize: 17, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>{title}</span>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {children}
      </div>
    </div>
  );
}

function CommunityPostsPage({ user, setPage, onMessage, showBack = false, backTo = "community-groups", backLabel = "Back to Groups", openPostId, onPostOpened }) {
  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "1.5rem 1.25rem" }}>
      {showBack && (
        <button
          onClick={() => setPage(backTo)}
          style={{ background: "none", border: "none", color: "#3B82F6", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}
        >
          ← {backLabel}
        </button>
      )}
      <div style={{ marginBottom: "1.25rem" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Posts</h2>
      </div>
      <PostsSection user={user} setPage={setPage} onMessage={onMessage} openPostId={openPostId} onPostOpened={onPostOpened} />
    </div>
  );
}

function CommunityGroupsPage({ user, setPage }) {
  return (
    <FullScreenCommunityPage title="Groups" onBack={() => setPage("home")}>
      <div style={{ padding: "1rem 1.25rem" }}>
        <GroupsTab user={user} setPage={setPage} fullScreen />
      </div>
    </FullScreenCommunityPage>
  );
}

function CommunityChatPage({ user, setPage }) {
  return (
    <FullScreenCommunityPage title="Chat" onBack={() => setPage("home")}>
      <CommunityChatSection user={user} setPage={setPage} />
    </FullScreenCommunityPage>
  );
}

function CommunityPage({ user, setPage, onMessage }) {
  const [showWarn, setShowWarn] = useState(!localStorage.getItem(COMMUNITY_WARN_KEY));

  function handleWarnClose() {
    localStorage.setItem(COMMUNITY_WARN_KEY, "1");
    setShowWarn(false);
  }

  return (
    <>
      {showWarn && <ScamWarningModal onClose={handleWarnClose} />}
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "1.5rem 1.25rem" }}>
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>LenderBuild</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>Community Hub</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: 0 }}>Groups and live chat for builders and lenders</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {[
            { page: "community-groups", icon: "users", title: "Groups", desc: "Join local area groups and connect with others" },
            { page: "community-chat", icon: "message", title: "Chat", desc: "Live chat with builders and lenders" },
          ].map(item => (
            <button key={item.page} onClick={() => setPage(item.page)}
              style={{ display: "flex", alignItems: "center", gap: 16, background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 14, padding: "18px 20px", cursor: "pointer", textAlign: "left", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", transition: "box-shadow 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.1)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)"}>
              <div style={{ width: 50, height: 50, borderRadius: 14, background: "var(--accent-bg,#EBF2FF)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "var(--accent,#3B82F6)" }}><Icon name={item.icon} size={24} /></div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1E3A5F", marginBottom: 3 }}>{item.title}</div>
                <div style={{ fontSize: 13, color: "#64748B" }}>{item.desc}</div>
              </div>
              <svg style={{ marginLeft: "auto", flexShrink: 0 }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── COMMUNITY CHAT SECTION (extracted from old CommunityPage) ────────────────
function CommunityChatSection({ user, setPage }) {
  const [channel,   setChannel]   = useState("general");
  const [messages,  setMessages]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [text,      setText]      = useState("");
  const [sending,   setSending]   = useState(false);
  const [cooldown,  setCooldown]  = useState(0);
  const [coolMsg,   setCoolMsg]   = useState("");
  const [banned,    setBanned]    = useState(null);
  const [reported,  setReported]  = useState({});
  const [dismissSafety, setDismissSafety] = useState(() => { try { return !!localStorage.getItem("lb_dismiss_community_safety"); } catch { return false; } });
  const messagesEndRef  = useRef(null);
  const chatScrollRef   = useRef(null);
  const realtimeChanRef = useRef(null);
  const cooldownRef     = useRef(null);
  const myId = user?.id;
  const isMobile = window.innerWidth < 768;

  // Check ban status on mount
  useEffect(() => {
    if (!myId) return;
    supabase.from("community_bans").select("*").eq("user_id", myId).maybeSingle()
      .then(({ data }) => {
        if (!data) { setBanned(null); return; }
        if (data.ban_type === "permanent") { setBanned(data); return; }
        if (data.banned_until && new Date(data.banned_until) > new Date()) { setBanned(data); return; }
        setBanned(null);
      });
  }, [myId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load messages + realtime subscription, switching with channel
  useEffect(() => {
    loadMessages();
    if (realtimeChanRef.current) supabase.removeChannel(realtimeChanRef.current);
    realtimeChanRef.current = supabase.channel(`comm-${channel}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "community_messages", filter: `channel=eq.${channel}` },
        payload => {
          setMessages(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]);
          setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, 50);
        })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "community_messages", filter: `channel=eq.${channel}` },
        payload => setMessages(prev => prev.map(m => m.id === payload.new.id ? { ...m, ...payload.new } : m)))
      .subscribe();
    return () => { if (realtimeChanRef.current) supabase.removeChannel(realtimeChanRef.current); };
  }, [channel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(s => {
        if (s <= 1) { clearInterval(cooldownRef.current); setCoolMsg(""); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  async function loadMessages() {
    setLoading(true);
    const { data } = await supabase.from("community_messages").select("*")
      .eq("channel", channel).order("created_at", { ascending: true }).limit(50);
    setMessages(data || []);
    setLoading(false);
    setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, 80);
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!text.trim() || sending) return;
    if (!user) { setPage("auth"); return; }
    if (banned) return;

    // Rate limit
    if (!chatRateLimitAllowed(myId)) {
      const secs = chatCooldownSecs(myId);
      setCooldown(secs);
      setCoolMsg(`Please wait ${secs}s before sending another message`);
      return;
    }

    setSending(true);
    const name = user.user_metadata?.name || user.email?.split("@")[0] || "User";

    // Link blocking
    const { sanitized, blocked } = sanitizeChatMessage(text.trim());
    if (blocked) {
      // Notify the user inline then continue with sanitized content
      setCoolMsg("Links and contact details are not allowed in community chat.");
      setTimeout(() => setCoolMsg(""), 5000);
      // Send in-app notification to the sender
      supabase.from("notifications").insert({
        user_id: myId,
        type:    "message",
        message: "Your message contained a link or contact detail that was removed. Please connect through the platform.",
      }).then(() => {}).catch(() => {});
    }

    if (!sanitized) { setSending(false); return; }

    // Auto-moderation
    const flagTerm = checkAutomod(sanitized);
    const shouldFlag = !!flagTerm;

    const { data: profile } = await supabase.from("profiles").select("sequential_id").eq("id", myId).maybeSingle();
    const payload = {
      user_id:         myId,
      channel,
      content:         sanitized.slice(0, 1000),
      user_name:       name,
      user_avatar_url: user.user_metadata?.avatar_url || null,
      user_role:       user.user_metadata?.role || "member",
      sequential_id:   profile?.sequential_id || null,
      flagged:         shouldFlag,
      hidden:          shouldFlag,
      flag_reason:     flagTerm || null,
    };

    const { data: inserted, error } = await supabase.from("community_messages").insert(payload).select().single();
    if (!error) {
      setText("");
      // Alert admin of auto-flagged message
      if (shouldFlag && inserted) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          fetch("/api/notify-message", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ action: "community-flag-alert", message_id: inserted.id, flag_reason: flagTerm, message_content: sanitized, user_name: name }),
          }).catch(() => {});
        }
      }
    }
    setSending(false);
  }

  async function handleReport(msg, reason) {
    if (!user) return;
    const name = user.user_metadata?.name || user.email?.split("@")[0] || "User";
    setReported(prev => ({ ...prev, [msg.id]: true }));
    await supabase.rpc("report_community_message", {
      p_message_id:        msg.id,
      p_reporter_id:       myId,
      p_reporter_name:     name,
      p_reason:            reason,
      p_message_content:   msg.content,
      p_message_user_id:   msg.user_id,
      p_message_user_name: msg.user_name,
    });
  }

  const channelMeta  = COMMUNITY_CHANNELS.find(c => c.id === channel);
  const channelLabel = channel === "general" ? "#general" : channel === "funding" ? "#looking-for-funding" : "#available-to-lend";
  const canSend      = !banned && cooldown === 0 && !!user;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Safety banner */}
      {!dismissSafety && (
        <div style={{ background: "#EBF5FF", border: "0.5px solid #BFDBFE", borderRadius: 10, padding: "10px 14px", margin: "12px 16px 0", display: "flex", gap: 10, alignItems: "flex-start", position: "relative" }}>
          <button onClick={() => { setDismissSafety(true); try { localStorage.setItem("lb_dismiss_community_safety", "1"); } catch {} }} aria-label="Dismiss" style={{ position: "absolute", top: 6, right: 6, width: 20, height: 20, borderRadius: "50%", background: "#E5E7EB", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#374151", lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
          <Icon name="shield" size={16} className="lb-icon" />
          <p style={{ margin: 0, fontSize: 13, color: "#1E3A5F", lineHeight: 1.55, paddingRight: 20 }}>
            <strong>For your protection:</strong> never share personal financial details in public chat. Always verify identities before connecting.
          </p>
        </div>
      )}

      {/* Ban notice */}
      {banned && (
        <div style={{ background: "#FEE2E2", border: "0.5px solid #FCA5A5", borderRadius: 10, padding: "12px 16px", margin: "12px 16px 0" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#991B1B" }}>Community chat access suspended</div>
          <div style={{ fontSize: 13, color: "#991B1B", marginTop: 4 }}>
            {banned.ban_type === "permanent"
              ? "Your access has been permanently suspended."
              : `Your access is suspended until ${new Date(banned.banned_until).toLocaleString("en-GB")}.`}
            {banned.reason && ` Reason: ${banned.reason}`}
          </div>
        </div>
      )}

      {/* Channel tabs */}
      <div style={{ display: "flex", gap: 8, padding: "12px 16px", flexWrap: "wrap" }}>
        {COMMUNITY_CHANNELS.map(ch => (
          <button key={ch.id} onClick={() => setChannel(ch.id)} title={ch.desc}
            style={{ padding: "6px 14px", borderRadius: 20, border: `0.5px solid ${channel === ch.id ? "#3B82F6" : "#e0e0e0"}`, fontSize: 13, fontWeight: 500, cursor: "pointer", background: channel === ch.id ? "#EBF2FF" : "#fff", color: channel === ch.id ? "#2E5FA3" : "#555", transition: "all 0.15s" }}>
            {ch.label}
          </button>
        ))}
      </div>

      {/* Chat container */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, margin: "0 16px 16px", display: "flex", flexDirection: "column", flex: 1, minHeight: 300 }}>
        <div style={{ padding: "9px 1rem", borderBottom: "0.5px solid #f0f0f0", flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: "#64748B" }}>{channelMeta?.desc} · Last 50 messages</span>
        </div>

        <div ref={chatScrollRef} style={{ flex: 1, overflowY: "auto", padding: "1rem", minHeight: 200 }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "#aaa", padding: "2rem", fontSize: 14 }}>Loading…</div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem" }}>
              <div style={{ marginBottom: 8, color: "var(--accent)" }}><Icon name="message" size={32} /></div>
              <div style={{ fontSize: 14, color: "#64748B" }}>No messages yet — start the conversation!</div>
            </div>
          ) : (
            messages.map(msg => (
              <MessageBubble key={msg.id} msg={msg} myId={myId}
                onReport={handleReport} reported={!!reported[msg.id]} />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {coolMsg && (
          <div style={{ padding: "7px 1rem", background: "#FFFBEB", borderTop: "0.5px solid #FDE68A", fontSize: 13, color: "#92400E", flexShrink: 0 }}>
            ⏳ {coolMsg}{cooldown > 0 ? ` (${cooldown}s)` : ""}
          </div>
        )}

        {user && !banned ? (
          <form onSubmit={handleSend} style={{ borderTop: "0.5px solid #f0f0f0", padding: "0.75rem 1rem", display: "flex", gap: 8, alignItems: "flex-end", flexShrink: 0 }}>
            <textarea value={text} onChange={e => setText(e.target.value)}
              placeholder={cooldown > 0 ? `Wait ${cooldown}s…` : `Message ${channelLabel}…`}
              maxLength={1000} disabled={cooldown > 0} rows={1}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !isMobile) { e.preventDefault(); handleSend(e); } }}
              onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
              style={{ flex: 1, minHeight: 40, maxHeight: 120, border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 14, background: cooldown > 0 ? "#f5f5f5" : "#f9f9f9", outline: "none", resize: "none", overflowY: "auto", fontFamily: "inherit", lineHeight: "1.4", boxSizing: "border-box" }}
            />
            <button type="submit" disabled={!text.trim() || sending || cooldown > 0}
              style={{ width: 40, height: 40, borderRadius: 8, border: "none", flexShrink: 0, background: canSend && text.trim() && !sending ? "#3B82F6" : "#e0e0e0", cursor: canSend && text.trim() && !sending ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 8H2M14 8L9 3M14 8L9 13" stroke={canSend && text.trim() && !sending ? "#fff" : "#bbb"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </form>
        ) : !user ? (
          <div style={{ padding: "1rem", borderTop: "0.5px solid #f0f0f0", textAlign: "center", flexShrink: 0 }}>
            <button onClick={() => setPage("auth")} style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "9px 24px", minHeight: 44, borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
              Log in to chat
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── COMMUNITY GROUPS ────────────────────────────────────────────────────────

const GROUP_REGIONS = [
  "London", "South East", "South West", "Midlands",
  "North West", "North East", "Yorkshire", "Scotland",
  "Wales", "Northern Ireland", "Online/National",
];


const GROUP_REGION_FILTERS = [
  "All UK", "London", "North", "Midlands", "South", "Scotland", "Wales",
];

function GroupsTab({ user, setPage }) {
  const [view,          setView]          = useState("browse"); // "browse" | "create"
  const [groups,        setGroups]        = useState([]);
  const [myMemberships, setMyMemberships] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [creating,      setCreating]      = useState(false);
  const [createForm,    setCreateForm]    = useState({ name: "", description: "", region: "", is_private: false, rules: "" });
  const [createError,   setCreateError]   = useState("");
  const [myJoinRequests, setMyJoinRequests] = useState([]); // group_ids I have pending requests for
  const [myFavourites,   setMyFavourites]   = useState([]); // group_ids I have favourited
  const [avatarFile,     setAvatarFile]     = useState(null);
  const [avatarPreview,  setAvatarPreview]  = useState(null);
  const [regionFilter,  setRegionFilter]  = useState("All UK");
  const [myLocation,    setMyLocation]    = useState("");
  const [windowWidth,   setWindowWidth]   = useState(() => window.innerWidth);
  // Rules modal
  const [rulesModalGroup, setRulesModalGroup] = useState(null);
  const [rulesAccepted,   setRulesAccepted]   = useState(false);
  const myId   = user?.id;
  const myRole = user?.user_metadata?.role || "";

  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (!myId) return;
    supabase.from("profiles").select("location").eq("id", myId).maybeSingle()
      .then(({ data }) => setMyLocation(data?.location || ""));
  }, [myId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadGroups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!groups.length) return;
    const pendingId = sessionStorage.getItem("lb_open_group");
    if (!pendingId) return;
    sessionStorage.removeItem("lb_open_group");
    const grp = groups.find(g => g.id === pendingId);
    if (grp) setSelectedGroup(grp);
  }, [groups]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadGroups() {
    setLoading(true);
    const [{ data: allGroups }, membRes, reqRes, favRes] = await Promise.all([
      supabase.from("community_groups").select("*").order("last_activity", { ascending: false, nullsFirst: false }),
      myId ? supabase.from("community_group_members").select("group_id").eq("user_id", myId) : Promise.resolve({ data: [] }),
      myId ? supabase.from("group_join_requests").select("group_id").eq("user_id", myId).eq("status", "pending") : Promise.resolve({ data: [] }),
      myId ? supabase.from("group_favourites").select("group_id").eq("user_id", myId) : Promise.resolve({ data: [] }),
    ]);
    setGroups(allGroups || []);
    setMyMemberships((membRes.data || []).map(m => m.group_id));
    setMyJoinRequests((reqRes.data || []).map(r => r.group_id));
    setMyFavourites((favRes.data || []).map(f => f.group_id));
    setLoading(false);
  }

  async function doJoin(groupId) {
    await supabase.from("community_group_members").insert({ group_id: groupId, user_id: myId });
    loadGroups();
  }

  async function handleRequestJoin(groupId) {
    if (!user) { setPage("auth"); return; }
    await supabase.from("group_join_requests").upsert(
      { group_id: groupId, user_id: myId, status: "pending" },
      { onConflict: "group_id,user_id" }
    );
    setMyJoinRequests(prev => prev.includes(groupId) ? prev : [...prev, groupId]);
  }

  async function handleJoin(groupId) {
    if (!user) { setPage("auth"); return; }
    const g = groups.find(x => x.id === groupId);
    const groupRules = Array.isArray(g?.rules) ? g.rules : [];
    if (groupRules.length > 0) {
      setRulesAccepted(false);
      setRulesModalGroup(g);
      return;
    }
    if (g?.is_private) { await handleRequestJoin(groupId); return; }
    await doJoin(groupId);
  }

  async function handleLeave(groupId) {
    if (!user) return;
    await supabase.from("community_group_members").delete().eq("group_id", groupId).eq("user_id", myId);
    loadGroups();
  }

  async function toggleFavourite(groupId) {
    if (!user) return;
    const isFav = myFavourites.includes(groupId);
    if (isFav) {
      setMyFavourites(prev => prev.filter(id => id !== groupId));
      await supabase.from("group_favourites").delete().eq("user_id", myId).eq("group_id", groupId);
    } else {
      setMyFavourites(prev => [...prev, groupId]);
      await supabase.from("group_favourites").insert({ user_id: myId, group_id: groupId });
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!user) { setPage("auth"); return; }
    setCreateError("");
    if (!createForm.name.trim() || !createForm.region) {
      setCreateError("Please enter a group name and select a region.");
      return;
    }
    const { data: myCreated } = await supabase.from("community_groups").select("id").eq("created_by", myId);
    if ((myCreated || []).length >= 3) {
      setCreateError("You can only create up to 3 groups.");
      return;
    }
    setCreating(true);
    const name = user.user_metadata?.name || user.email?.split("@")[0] || "User";
    const rulesArray = createForm.rules.split("\n").map(r => r.trim()).filter(Boolean).slice(0, 20);
    const { data: newGroup, error } = await supabase.from("community_groups").insert({
      name:            createForm.name.trim().slice(0, 60),
      description:     createForm.description.trim().slice(0, 300),
      region:          createForm.region,
      created_by:      myId,
      created_by_name: name,
      is_private:      createForm.is_private,
      rules:           rulesArray,
    }).select().single();
    if (error) { setCreateError("Failed to create group. Please try again."); setCreating(false); return; }
    await supabase.from("community_group_members").insert({ group_id: newGroup.id, user_id: myId });
    if (avatarFile) {
      const ext = avatarFile.name.split(".").pop();
      const path = `group-avatars/${newGroup.id}.${ext}`;
      const { error: upErr } = await supabase.storage.from("profile-photos").upload(path, avatarFile, { upsert: true });
      if (!upErr) {
        const { data: urlData } = supabase.storage.from("profile-photos").getPublicUrl(path);
        await supabase.from("community_groups").update({ avatar_url: urlData.publicUrl }).eq("id", newGroup.id);
      }
    }
    setCreateForm({ name: "", description: "", region: "", is_private: false, rules: "" });
    setAvatarFile(null);
    setAvatarPreview(null);
    setCreating(false);
    setView("browse");
    loadGroups();
  }


  // Rules modal
  if (rulesModalGroup) {
    const rules = Array.isArray(rulesModalGroup.rules) ? rulesModalGroup.rules : [];
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
        <div style={{ background: "#fff", borderRadius: 16, maxWidth: 480, width: "100%", padding: "1.75rem", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1E3A5F", margin: "0 0 4px" }}>Group rules</h3>
          <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 1rem" }}>Please read and accept the rules for <strong>{rulesModalGroup.name}</strong> before joining.</p>
          <div style={{ flex: 1, overflowY: "auto", marginBottom: "1rem" }}>
            {rules.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: "0.5px solid #f0f0f0" }}>
                <span style={{ fontWeight: 700, color: "#1E3A5F", minWidth: 22, flexShrink: 0 }}>{i + 1}.</span>
                <span style={{ fontSize: 14, color: "#333", lineHeight: 1.5 }}>{r}</span>
              </div>
            ))}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1.25rem", cursor: "pointer" }}>
            <input type="checkbox" checked={rulesAccepted} onChange={e => setRulesAccepted(e.target.checked)}
              style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#16A34A" }} />
            <span style={{ fontSize: 14, color: "#333" }}>I have read and agree to follow these rules</span>
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setRulesModalGroup(null)}
              style={{ flex: 1, padding: "11px", borderRadius: 10, border: "0.5px solid #e0e0e0", background: "#fff", color: "#64748B", fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
              Cancel
            </button>
            <button disabled={!rulesAccepted} onClick={async () => {
              const g = rulesModalGroup;
              setRulesModalGroup(null);
              if (g.is_private) { await handleRequestJoin(g.id); } else { await doJoin(g.id); }
            }}
              style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", background: rulesAccepted ? "#16A34A" : "#e0e0e0", color: rulesAccepted ? "#fff" : "#aaa", fontSize: 14, fontWeight: 700, cursor: rulesAccepted ? "pointer" : "default" }}>
              {rulesModalGroup?.is_private ? "Request to join" : "Join group"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedGroup) {
    const isMobile = windowWidth <= 768;
    return (
      <div style={isMobile
        ? { position: "fixed", inset: 0, background: "#fff", zIndex: 10000, display: "flex", flexDirection: "column" }
        : { display: "flex", flexDirection: "column", height: "calc(100vh - 60px)", minHeight: 500, overflow: "hidden", background: "#fff", borderRadius: 0, borderLeft: "var(--border-light)", margin: "-1rem -1.25rem -1rem", maxWidth: "none" }}>
        <div style={{ background: "#1E3A5F", height: 56, display: "flex", alignItems: "center", padding: "0 16px", flexShrink: 0, position: "relative", borderRadius: isMobile ? 0 : "12px 12px 0 0" }}>
          <button onClick={() => { setSelectedGroup(null); loadGroups(); }} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "8px 4px", display: "flex", alignItems: "center", zIndex: 1 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", fontSize: 16, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "60%" }}>{selectedGroup.name}</span>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <GroupChatRoom
            group={selectedGroup} user={user} setPage={setPage}
            myMemberships={myMemberships} myJoinRequests={myJoinRequests}
            onBack={() => { setSelectedGroup(null); loadGroups(); }}
            onJoin={() => handleJoin(selectedGroup.id)}
            onLeave={() => handleLeave(selectedGroup.id)}
          />
        </div>
      </div>
    );
  }

  if (view === "create") {
    return (
      <div>
        <button onClick={() => { setView("browse"); setCreateError(""); }}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#3B82F6", fontSize: 14, padding: "0 0 1rem", display: "flex", alignItems: "center", gap: 6 }}>
          ← Back to groups
        </button>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 1rem", color: "#1E3A5F" }}>Create a group</h3>
        <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 1.25rem" }}>You can create up to 3 groups. Groups are for local areas or specific topics.</p>
        <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", display: "block", marginBottom: 4 }}>Group name *</label>
            <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Manchester Property Investors" maxLength={60}
              style={{ width: "100%", height: 40, border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "0 12px", fontSize: 14, outline: "none" }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", display: "block", marginBottom: 4 }}>Description</label>
            <textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What is this group about?" maxLength={300} rows={3}
              style={{ width: "100%", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, resize: "vertical", outline: "none" }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", display: "block", marginBottom: 4 }}>Group rules <span style={{ fontWeight: 400, color: "#94A3B8" }}>(optional)</span></label>
            <textarea value={createForm.rules} onChange={e => setCreateForm(f => ({ ...f, rules: e.target.value }))}
              placeholder={"Enter one rule per line, e.g.:\nBe respectful to all members\nNo spam or self-promotion"} rows={4}
              style={{ width: "100%", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
            <p style={{ fontSize: 12, color: "#94A3B8", margin: "4px 0 0" }}>One rule per line. Shown to members before they join.</p>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", display: "block", marginBottom: 6 }}>Group photo <span style={{ fontWeight: 400, color: "#94A3B8" }}>(optional)</span></label>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="" style={{ width: 56, height: 56, borderRadius: 12, objectFit: "cover", border: "0.5px solid #e0e0e0", flexShrink: 0 }} />
              ) : (
                <div style={{ width: 56, height: 56, borderRadius: 12, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", border: "0.5px dashed #cbd5e1", flexShrink: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                </div>
              )}
              <label style={{ cursor: "pointer", fontSize: 13, color: "var(--accent)", fontWeight: 600, padding: "7px 14px", borderRadius: 8, border: "1.5px solid var(--accent)", background: "var(--accent-bg)" }}>
                {avatarFile ? "Change photo" : "Upload photo"}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  setAvatarFile(f);
                  const reader = new FileReader();
                  reader.onload = ev => setAvatarPreview(ev.target.result);
                  reader.readAsDataURL(f);
                }} />
              </label>
              {avatarFile && <button type="button" onClick={() => { setAvatarFile(null); setAvatarPreview(null); }} style={{ fontSize: 12, color: "#94A3B8", background: "none", border: "none", cursor: "pointer", padding: "4px" }}>Remove</button>}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", display: "block", marginBottom: 4 }}>Region *</label>
            <select value={createForm.region} onChange={e => setCreateForm(f => ({ ...f, region: e.target.value }))}
              style={{ width: "100%", height: 40, border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "0 12px", fontSize: 14, outline: "none", background: "#fff" }}>
              <option value="">Select a region…</option>
              {GROUP_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={createForm.is_private}
                onChange={e => setCreateForm(f => ({ ...f, is_private: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#3B82F6" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F" }}>Private group</span>
            </label>
            {createForm.is_private && (
              <p style={{ fontSize: 12, color: "#64748B", margin: "6px 0 0 26px" }}>
                Private groups require join requests that you approve or decline.
              </p>
            )}
          </div>
          {createError && (
            <div style={{ fontSize: 13, color: "#DC2626", background: "#FEE2E2", borderRadius: 8, padding: "8px 12px" }}>{createError}</div>
          )}
          <button type="submit" disabled={creating}
            style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "11px", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: creating ? "default" : "pointer" }}>
            {creating ? "Creating…" : "Create group"}
          </button>
        </form>
      </div>
    );
  }

  const myGroups = groups
    .filter(g => myMemberships.includes(g.id))
    .sort((a, b) => {
      const af = myFavourites.includes(a.id);
      const bf = myFavourites.includes(b.id);
      if (af && !bf) return -1;
      if (!af && bf) return 1;
      return 0;
    });

  // Region filter logic
  const REGION_MATCH = {
    "London":   ["London"],
    "North":    ["North West", "North East", "Yorkshire"],
    "Midlands": ["Midlands"],
    "South":    ["South East", "South West"],
    "Scotland": ["Scotland"],
    "Wales":    ["Wales", "Northern Ireland"],
  };
  const visibleGroups = regionFilter === "All UK"
    ? groups
    : groups.filter(g => (REGION_MATCH[regionFilter] || []).includes(g.region));

  // "You might like" suggestions
  const userRegion = ukRegion(myLocation);
  const suggestions = groups
    .filter(g => !myMemberships.includes(g.id))
    .filter(g => {
      const desc = (g.name + " " + (g.description || "")).toLowerCase();
      if (userRegion && g.region && (g.region.toLowerCase().includes(userRegion.toLowerCase()) || userRegion.toLowerCase().includes(g.region.toLowerCase()))) return true;
      if (myRole === "lender" && (desc.includes("lend") || desc.includes("invest") || desc.includes("fund"))) return true;
      if (myRole === "builder" && (desc.includes("build") || desc.includes("develop") || desc.includes("construct"))) return true;
      return false;
    })
    .slice(0, 3);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13, color: "#64748B" }}>Local area groups for builders and lenders</p>
        <button onClick={() => user ? setView("create") : setPage("auth")}
          style={{ background: "#16A34A", color: "#fff", border: "none", padding: "9px 18px", minHeight: 44, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
          + Create group
        </button>
      </div>

      {/* Region filter */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1.25rem" }}>
        {GROUP_REGION_FILTERS.map(r => (
          <button key={r} onClick={() => setRegionFilter(r)}
            style={{ padding: "7px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: "pointer", border: "none", minHeight: 36, background: regionFilter === r ? "#1E3A5F" : "#F1F5F9", color: regionFilter === r ? "#fff" : "#475569", transition: "background 0.15s" }}>
            {r}
          </button>
        ))}
      </div>


      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: "#F0F2F5", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ height: 15, width: "52%", background: "#F0F2F5", borderRadius: 6, marginBottom: 8 }} />
                <div style={{ height: 11, width: "38%", background: "#F0F2F5", borderRadius: 6, marginBottom: 8 }} />
                <div style={{ height: 13, width: "72%", background: "#F0F2F5", borderRadius: 6 }} />
              </div>
              <div style={{ width: 64, height: 38, background: "#F0F2F5", borderRadius: 8, flexShrink: 0 }} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* My groups */}
          {myGroups.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1E3A5F", letterSpacing: "0.05em", marginBottom: 10 }}>MY GROUPS</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {myGroups.map(g => (
                  <GroupCard key={g.id} group={g} isMember={true} isOwner={g.created_by === myId}
                    isFavourited={myFavourites.includes(g.id)}
                    onOpen={() => setSelectedGroup(g)} onLeave={() => handleLeave(g.id)} onJoin={() => {}}
                    onToggleFavourite={() => toggleFavourite(g.id)} />
                ))}
              </div>
            </div>
          )}

          {/* You might like */}
          {user && suggestions.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6D28D9", letterSpacing: "0.05em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                YOU MIGHT LIKE THESE GROUPS
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {suggestions.map(g => (
                  <GroupCard key={g.id} group={g} isMember={false} isOwner={false}
                    hasPendingRequest={myJoinRequests.includes(g.id)}
                    onOpen={() => setSelectedGroup(g)} onLeave={() => {}} onJoin={() => handleJoin(g.id)} />
                ))}
              </div>
            </div>
          )}

          {/* All groups */}
          {visibleGroups.length > 0 ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748B", letterSpacing: "0.05em", marginBottom: 10 }}>
                {regionFilter === "All UK" ? "ALL GROUPS" : `${regionFilter.toUpperCase()} GROUPS`}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {visibleGroups.map(g => (
                  <GroupCard key={g.id} group={g} isMember={myMemberships.includes(g.id)} isOwner={g.created_by === myId}
                    hasPendingRequest={myJoinRequests.includes(g.id)}
                    isFavourited={myFavourites.includes(g.id)}
                    onOpen={() => setSelectedGroup(g)} onLeave={() => handleLeave(g.id)} onJoin={() => handleJoin(g.id)}
                    onToggleFavourite={() => toggleFavourite(g.id)} />
                ))}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "3rem 1rem", background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12 }}>
              <div style={{ marginBottom: 8, color: "var(--accent)" }}><Icon name="users" size={36} /></div>
              <div style={{ fontSize: 15, fontWeight: 500, color: "#1E3A5F", marginBottom: 6 }}>
                {regionFilter === "All UK" ? "No groups yet" : `No groups in ${regionFilter} yet`}
              </div>
              <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>Be the first to create one!</div>
              <button onClick={() => user ? setView("create") : setPage("auth")}
                style={{ background: "#16A34A", color: "#fff", border: "none", padding: "10px 22px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Create group
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GroupCard({ group, isMember, isOwner, hasPendingRequest, isFavourited, onOpen, onJoin, onLeave, onToggleFavourite }) {
  const regionColors = {
    "London": { bg: "#EBF2FF", text: "#1E3A5F" },
    "South East": { bg: "#E1F5EE", text: "#0F6E56" },
    "South West": { bg: "#E1F5EE", text: "#0F6E56" },
    "Midlands": { bg: "#FFF7ED", text: "#C2410C" },
    "North West": { bg: "#F5F3FF", text: "#6D28D9" },
    "North East": { bg: "#F5F3FF", text: "#6D28D9" },
    "Yorkshire": { bg: "#FFF7ED", text: "#C2410C" },
    "Scotland": { bg: "#EFF6FF", text: "#1D4ED8" },
    "Wales": { bg: "#F0FDF4", text: "#166534" },
    "Northern Ireland": { bg: "#FDF4FF", text: "#86198F" },
    "Online/National": { bg: "#F1F5F9", text: "#475569" },
  };
  const rc = regionColors[group.region] || { bg: "#F1F5F9", text: "#475569" };

  return (
    <div
      style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", transition: "box-shadow 0.15s" }}
      onClick={onOpen}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.07)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      {/* Group avatar */}
      {group.avatar_url ? (
        <img src={group.avatar_url} alt="" style={{ width: 44, height: 44, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 44, height: 44, borderRadius: 12, background: rc.bg, color: rc.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, fontWeight: 700, flexShrink: 0 }}>
          {group.name.charAt(0).toUpperCase()}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#1E3A5F" }}>{group.name}</span>
          {isOwner && <Icon name="crown" size={12} style={{color:"#D97706"}} />}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, background: rc.bg, color: rc.text, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>{group.region}</span>
          <span style={{ fontSize: 12, color: "#64748B" }}>·</span>
          <span style={{ fontSize: 12, color: "#64748B", display: "flex", alignItems: "center", gap: 3 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            {group.member_count || 0} members
          </span>
          {isMember && <span style={{ fontSize: 11, background: "#DCFCE7", color: "#166534", padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>✓ Joined</span>}
        </div>
        {group.description && (
          <div style={{ fontSize: 13, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.description}</div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0, alignItems: "flex-end" }} onClick={e => e.stopPropagation()}>
        {/* Favourite star — only for joined groups */}
        {isMember && onToggleFavourite && (
          <button onClick={onToggleFavourite}
            title={isFavourited ? "Remove from favourites" : "Add to favourites"}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 0", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavourited ? "var(--accent)" : "none"} stroke={isFavourited ? "var(--accent)" : "#CBD5E1"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </button>
        )}
        {!isMember ? (
          group.is_private ? (
            hasPendingRequest ? (
              <div style={{ padding: "8px 14px", borderRadius: 8, background: "#FEF9C3", color: "#854D0E", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>Requested</div>
            ) : (
              <button onClick={onJoin}
                style={{ padding: "8px 14px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 700, background: "#3B82F6", color: "#fff", cursor: "pointer", minHeight: 38, whiteSpace: "nowrap" }}>
                Request to join
              </button>
            )
          ) : (
            <button onClick={onJoin}
              style={{ padding: "8px 16px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 700, background: "#16A34A", color: "#fff", cursor: "pointer", minHeight: 38 }}>
              Join
            </button>
          )
        ) : (
          <button onClick={onOpen}
            style={{ padding: "8px 14px", borderRadius: 8, border: "0.5px solid #3B82F6", fontSize: 13, fontWeight: 600, background: "#EBF2FF", color: "#2E5FA3", cursor: "pointer", minHeight: 38, whiteSpace: "nowrap" }}>
            Open chat
          </button>
        )}
        {isMember && !isOwner && (
          <button onClick={onLeave}
            style={{ padding: "4px 10px", borderRadius: 6, border: "0.5px solid #e0e0e0", fontSize: 11, background: "transparent", color: "#94A3B8", cursor: "pointer" }}>
            Leave
          </button>
        )}
      </div>
    </div>
  );
}

function GroupChatRoom({ group: initialGroup, user, setPage, myMemberships, myJoinRequests, onBack, onJoin, onLeave }) {
  const [group,          setGroup]          = useState(initialGroup);
  const [messages,       setMessages]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [text,           setText]           = useState("");
  const [sending,        setSending]        = useState(false);
  const [cooldown,       setCooldown]       = useState(0);
  const [coolMsg,        setCoolMsg]        = useState("");
  const [banned,         setBanned]         = useState(null);
  const [reported,       setReported]       = useState({});
  const [onlineCount,    setOnlineCount]    = useState(1);
  // Channels
  const [channels,       setChannels]       = useState([]);
  const [activeChannel,  setActiveChannel]  = useState(null); // null = no channels yet
  const [addingChannel,  setAddingChannel]  = useState(false);
  const [newChanName,    setNewChanName]    = useState("");
  // Settings panel
  const [showSettings,   setShowSettings]   = useState(false);
  const [settingsTab,    setSettingsTab]    = useState("members"); // "members" | "rules"
  const [members,        setMembers]        = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [rules,          setRules]          = useState([]);
  const [newRule,        setNewRule]        = useState("");
  const [editRuleIdx,    setEditRuleIdx]    = useState(null);
  const [editRuleText,   setEditRuleText]   = useState("");
  const [isModerator,    setIsModerator]    = useState(false);
  // Shared-post preview map: postId → post object
  const [postMap,        setPostMap]        = useState({});
  // Mute state
  const [muted,          setMuted]          = useState(false);
  // Join requests (owner view) + profile editing
  const [joinRequests,       setJoinRequests]       = useState([]);
  const [joinReqLoading,     setJoinReqLoading]     = useState(false);
  const [activeTab,          setActiveTab]          = useState("chat"); // "chat" | "members"
  const [groupAvatarFile,    setGroupAvatarFile]    = useState(null);
  const [groupAvatarPreview, setGroupAvatarPreview] = useState(null);
  const [editDescription,    setEditDescription]    = useState(initialGroup.description || "");
  const [editRulesText,      setEditRulesText]      = useState((Array.isArray(initialGroup.rules) ? initialGroup.rules : []).join("\n"));
  const [savingProfile,      setSavingProfile]      = useState(false);
  const [settingsToast,      setSettingsToast]      = useState(null);
  const [avatarLightboxOpen, setAvatarLightboxOpen] = useState(false);
  const [windowWidth,        setWindowWidth]        = useState(() => window.innerWidth);
  const chatScrollRef   = useRef(null);
  const realtimeChanRef = useRef(null);
  const presenceChanRef = useRef(null);
  const cooldownRef     = useRef(null);
  const myId    = user?.id;
  const isMobile  = windowWidth < 768;
  const isMember  = myMemberships.includes(group.id);
  const isCreator = group.created_by === myId;
  const hasPendingRequest = (myJoinRequests || []).includes(group.id);

  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (!avatarLightboxOpen) return;
    const handler = e => { if (e.key === "Escape") setAvatarLightboxOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [avatarLightboxOpen]);

  useEffect(() => {
    if (!myId) return;
    supabase.from("community_bans").select("*").eq("user_id", myId).maybeSingle()
      .then(({ data }) => {
        if (!data) { setBanned(null); return; }
        if (data.ban_type === "permanent") { setBanned(data); return; }
        if (data.banned_until && new Date(data.banned_until) > new Date()) { setBanned(data); return; }
        setBanned(null);
      });
    // Check moderator/muted status
    supabase.from("community_group_members")
      .select("is_moderator, muted, muted_until")
      .eq("group_id", group.id).eq("user_id", myId).maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        setIsModerator(!!data.is_moderator);
        const mutedNow = data.muted && (!data.muted_until || new Date(data.muted_until) > new Date());
        setMuted(mutedNow);
      });
  }, [myId, group.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load channels
  useEffect(() => {
    supabase.from("group_channels").select("*").eq("group_id", group.id).order("position", { ascending: true })
      .then(({ data }) => {
        const chs = data || [];
        setChannels(chs);
        if (chs.length > 0) setActiveChannel(chs.find(c => c.is_default) || chs[0]);
        setRules(Array.isArray(group.rules) ? group.rules : []);
      });
  }, [group.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Presence channel for online count
  useEffect(() => {
    if (!myId) return;
    const pChan = supabase.channel(`presence-grp-${group.id}`, { config: { presence: { key: myId } } })
      .on("presence", { event: "sync" }, () => {
        const state = pChan.presenceState();
        setOnlineCount(Math.max(1, Object.keys(state).length));
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await pChan.track({ user_id: myId, online_at: new Date().toISOString() });
        }
      });
    presenceChanRef.current = pChan;
    return () => { supabase.removeChannel(pChan); };
  }, [group.id, myId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadMessages();
    if (realtimeChanRef.current) supabase.removeChannel(realtimeChanRef.current);
    realtimeChanRef.current = supabase.channel(`grp-${group.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "group_messages", filter: `group_id=eq.${group.id}` },
        payload => {
          setMessages(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]);
          setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, 50);
        })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "group_messages", filter: `group_id=eq.${group.id}` },
        payload => setMessages(prev => prev.map(m => m.id === payload.new.id ? { ...m, ...payload.new } : m)))
      .subscribe();
    return () => { if (realtimeChanRef.current) supabase.removeChannel(realtimeChanRef.current); };
  }, [group.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cooldown <= 0) return;
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(s => {
        if (s <= 1) { clearInterval(cooldownRef.current); setCoolMsg(""); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  async function loadMessages() {
    setLoading(true);
    let q = supabase.from("group_messages").select("*").eq("group_id", group.id).order("created_at", { ascending: true }).limit(100);
    if (activeChannel) q = q.eq("channel_id", activeChannel.id);
    else q = q.is("channel_id", null);
    const { data } = await q;
    setMessages(data || []);
    setLoading(false);
    setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, 80);
    // Fetch post data for any shared-post messages
    const sharedIds = [...new Set((data || []).map(m => m.shared_post_id).filter(Boolean))];
    if (sharedIds.length > 0) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers = session ? { Authorization: `Bearer ${session.access_token}` } : {};
        const res = await fetch("/api/posts", { headers });
        const json = await res.json();
        const map = {};
        (json.posts || []).forEach(p => { if (sharedIds.includes(p.id)) map[p.id] = p; });
        setPostMap(map);
      } catch { /* non-fatal — falls back to plain text */ }
    }
  }

  // Reload messages when channel changes
  useEffect(() => { if (!loading || activeChannel !== null) loadMessages(); }, [activeChannel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDeleteMessage(msgId) {
    await supabase.from("group_messages").update({
      deleted: true, deleted_by: myId, deleted_at: new Date().toISOString(),
    }).eq("id", msgId);
  }

  async function handlePinMessage(msg) {
    const { data: updated } = await supabase.from("community_groups").update({
      pinned_message_id:      msg.id,
      pinned_message_content: msg.content,
      pinned_message_author:  msg.user_name,
    }).eq("id", group.id).select().single();
    if (updated) setGroup(updated);
  }

  async function handleUnpinMessage() {
    const { data: updated } = await supabase.from("community_groups").update({
      pinned_message_id: null, pinned_message_content: null, pinned_message_author: null,
    }).eq("id", group.id).select().single();
    if (updated) setGroup(updated);
  }

  async function handleAddChannel(e) {
    e.preventDefault();
    if (!newChanName.trim()) return;
    const pos = channels.length;
    const { data: ch } = await supabase.from("group_channels").insert({
      group_id: group.id, name: newChanName.trim().slice(0, 40), description: "", position: pos, is_default: pos === 0,
    }).select().single();
    if (ch) {
      const updated = [...channels, ch];
      setChannels(updated);
      if (pos === 0) setActiveChannel(ch);
    }
    setNewChanName("");
    setAddingChannel(false);
  }

  async function loadMembers() {
    setMembersLoading(true);
    const { data: mems } = await supabase.from("community_group_members")
      .select("user_id, is_moderator, muted, muted_until, removed").eq("group_id", group.id);
    if (!mems || mems.length === 0) { setMembers([]); setMembersLoading(false); return; }
    const ids = mems.map(m => m.user_id);
    const { data: profiles } = await supabase.from("profiles").select("id, name, role").in("id", ids);
    const profileMap = {};
    for (const p of profiles || []) profileMap[p.id] = p;
    setMembers(mems.map(m => ({ ...m, name: profileMap[m.user_id]?.name || "Member", role: profileMap[m.user_id]?.role || "" })));
    setMembersLoading(false);
  }

  async function handleMemberAction(memberId, action) {
    if (action === "mute") {
      const muteUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      await supabase.from("community_group_members").update({ muted: true, muted_until: muteUntil }).eq("group_id", group.id).eq("user_id", memberId);
    } else if (action === "unmute") {
      await supabase.from("community_group_members").update({ muted: false, muted_until: null }).eq("group_id", group.id).eq("user_id", memberId);
    } else if (action === "remove") {
      await supabase.from("community_group_members").update({ removed: true }).eq("group_id", group.id).eq("user_id", memberId);
    } else if (action === "make_mod") {
      await supabase.from("community_group_members").update({ is_moderator: true }).eq("group_id", group.id).eq("user_id", memberId);
    } else if (action === "remove_mod") {
      await supabase.from("community_group_members").update({ is_moderator: false }).eq("group_id", group.id).eq("user_id", memberId);
    }
    loadMembers();
  }

  async function handleSaveRules() {
    const { data: updated } = await supabase.from("community_groups").update({ rules }).eq("id", group.id).select().single();
    if (updated) setGroup(updated);
  }

  async function loadJoinRequests() {
    setJoinReqLoading(true);
    const { data } = await supabase.from("group_join_requests")
      .select("id, user_id, created_at, profiles(name, role)")
      .eq("group_id", group.id).eq("status", "pending")
      .order("created_at", { ascending: true });
    setJoinRequests(data || []);
    setJoinReqLoading(false);
  }

  async function handleAcceptRequest(userId, requestId) {
    await supabase.from("community_group_members").insert({ group_id: group.id, user_id: userId });
    await supabase.from("group_join_requests").update({ status: "accepted" }).eq("id", requestId);
    setJoinRequests(prev => prev.filter(r => r.id !== requestId));
    setGroup(prev => ({ ...prev, member_count: (prev.member_count || 0) + 1 }));
  }

  async function handleDeclineRequest(requestId) {
    await supabase.from("group_join_requests").update({ status: "declined" }).eq("id", requestId);
    setJoinRequests(prev => prev.filter(r => r.id !== requestId));
  }

  async function handleSaveGroupProfile() {
    setSavingProfile(true);
    let newAvatarUrl = group.avatar_url;
    if (groupAvatarFile) {
      const ext = groupAvatarFile.name.split(".").pop();
      const path = `group-avatars/${group.id}.${ext}`;
      const { error: upErr } = await supabase.storage.from("profile-photos").upload(path, groupAvatarFile, { upsert: true });
      if (upErr) {
        console.error("group avatar upload failed:", upErr, JSON.stringify(upErr));
        setSettingsToast({ type: "error", msg: `Photo upload failed: ${upErr.message}` });
        setTimeout(() => setSettingsToast(null), 6000);
        setSavingProfile(false);
        return;
      }
      const { data: urlData } = supabase.storage.from("profile-photos").getPublicUrl(path);
      newAvatarUrl = urlData.publicUrl;
    }
    const newRules = editRulesText.split("\n").map(r => r.trim()).filter(Boolean).slice(0, 20);
    const payload = { description: editDescription, rules: newRules };
    if (groupAvatarFile) payload.avatar_url = newAvatarUrl;
    const { data: updated, error: updateErr } = await supabase.from("community_groups")
      .update(payload).eq("id", group.id).select().single();
    if (updateErr) {
      setSettingsToast({ type: "error", msg: updateErr.message });
      setTimeout(() => setSettingsToast(null), 6000);
    } else if (!updated) {
      setSettingsToast({ type: "warning", msg: "Saved, but no rows were updated — likely a permissions issue" });
      setTimeout(() => setSettingsToast(null), 6000);
    } else {
      setGroup(updated);
      setRules(newRules);
      setSettingsToast({ type: "success", msg: "Group settings saved" });
      setTimeout(() => setSettingsToast(null), 3000);
    }
    setGroupAvatarFile(null);
    setGroupAvatarPreview(null);
    setSavingProfile(false);
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!text.trim() || sending || !user || banned || muted || !isMember) return;

    if (!chatRateLimitAllowed(myId)) {
      const secs = chatCooldownSecs(myId);
      setCooldown(secs);
      setCoolMsg(`Please wait ${secs}s before sending another message`);
      return;
    }

    setSending(true);
    const name = user.user_metadata?.name || user.email?.split("@")[0] || "User";
    const { sanitized, blocked } = sanitizeChatMessage(text.trim());
    if (blocked) {
      setCoolMsg("Links and contact details are not allowed in group chat.");
      setTimeout(() => setCoolMsg(""), 5000);
      supabase.from("notifications").insert({ user_id: myId, type: "message", message: "Your message contained a link or contact detail that was removed." }).then(() => {}).catch(() => {});
    }
    if (!sanitized) { setSending(false); return; }

    const flagTerm = checkAutomod(sanitized);
    const { data: profile } = await supabase.from("profiles").select("sequential_id").eq("id", myId).maybeSingle();
    const payload = {
      group_id:        group.id,
      channel_id:      activeChannel ? activeChannel.id : null,
      user_id:         myId,
      content:         sanitized.slice(0, 1000),
      user_name:       name,
      user_avatar_url: user.user_metadata?.avatar_url || null,
      user_role:       user.user_metadata?.role || "member",
      sequential_id:   profile?.sequential_id || null,
      flagged:         !!flagTerm,
      hidden:          !!flagTerm,
      flag_reason:     flagTerm || null,
    };

    const { error } = await supabase.from("group_messages").insert(payload);
    if (!error) {
      setText("");
      supabase.from("community_groups").update({ last_activity: new Date().toISOString() }).eq("id", group.id).then(() => {}).catch(() => {});
    }
    setSending(false);
  }

  async function handleReport(msg, reason) {
    if (!user) return;
    const name = user.user_metadata?.name || user.email?.split("@")[0] || "User";
    setReported(prev => ({ ...prev, [msg.id]: true }));
    await supabase.rpc("report_group_message", {
      p_message_id:        msg.id,
      p_reporter_id:       myId,
      p_reporter_name:     name,
      p_reason:            reason,
      p_message_content:   msg.content,
      p_message_user_id:   msg.user_id,
      p_message_user_name: msg.user_name,
    });
  }

  const canSend = !banned && !muted && cooldown === 0 && !!user && isMember;

  // ── Settings panel ───────────────────────────────────────────────────────
  if (showSettings) {
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflowY: "auto", padding: "1rem 1.25rem" }}>
        <button onClick={() => setShowSettings(false)}
          style={{ background: "none", border: "none", color: "#3B82F6", fontSize: 14, cursor: "pointer", padding: "0 0 1rem", display: "flex", alignItems: "center", gap: 6 }}>
          ← Back to chat
        </button>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1E3A5F", margin: "0 0 1rem" }}>Group settings</h3>

        {/* Tab switcher */}
        <div style={{ display: "flex", gap: 8, marginBottom: "1.25rem", flexWrap: "wrap" }}>
          {[
            { key: "members", label: "Members" },
            { key: "rules",   label: "Rules" },
            ...(isCreator ? [
              { key: "requests", label: joinRequests.length > 0 ? `Requests (${joinRequests.length})` : "Requests" },
              { key: "profile",  label: "Profile" },
            ] : []),
          ].map(t => (
            <button key={t.key} onClick={() => {
              setSettingsTab(t.key);
              if (t.key === "members")  loadMembers();
              if (t.key === "requests") loadJoinRequests();
            }}
              style={{ padding: "8px 18px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", background: settingsTab === t.key ? "#1E3A5F" : "#F1F5F9", color: settingsTab === t.key ? "#fff" : "#475569" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Members tab */}
        {settingsTab === "members" && (
          <div>
            {membersLoading ? (
              <div style={{ textAlign: "center", color: "#aaa", padding: "2rem" }}>Loading…</div>
            ) : members.length === 0 ? (
              <div style={{ textAlign: "center", color: "#aaa", padding: "2rem" }}>No members found</div>
            ) : members.map(m => {
              const isOwn = m.user_id === myId;
              const isOwnerMember = m.user_id === group.created_by;
              return (
                <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "0.5px solid #f0f0f0" }}>
                  <Avatar initials={nameInitials(m.name)} color={pickColor(m.user_id)} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F" }}>{m.name} {isOwnerMember ? <Icon name="crown" size={12} style={{color:"#D97706",marginLeft:3}} /> : null} {m.is_moderator ? <span style={{ fontSize: 11, background: "#DBEAFE", color: "#1D4ED8", padding: "1px 6px", borderRadius: 20 }}>Mod</span> : ""}</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>{m.role || "member"}{m.muted ? " · Muted" : ""}{m.removed ? " · Removed" : ""}</div>
                  </div>
                  {/* Owner controls: mute/mod/remove for all non-owner members */}
                  {isCreator && !isOwn && !isOwnerMember && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button onClick={() => handleMemberAction(m.user_id, m.muted ? "unmute" : "mute")}
                        style={{ padding: "5px 10px", fontSize: 12, borderRadius: 6, border: "0.5px solid #e0e0e0", background: "#fff", color: "#475569", cursor: "pointer" }}>
                        {m.muted ? "Unmute" : "Mute 24h"}
                      </button>
                      {!m.is_moderator ? (
                        <button onClick={() => handleMemberAction(m.user_id, "make_mod")}
                          style={{ padding: "5px 10px", fontSize: 12, borderRadius: 6, border: "0.5px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8", cursor: "pointer" }}>
                          Assign Mod
                        </button>
                      ) : (
                        <button onClick={() => handleMemberAction(m.user_id, "remove_mod")}
                          style={{ padding: "5px 10px", fontSize: 12, borderRadius: 6, border: "0.5px solid #e0e0e0", background: "#fff", color: "#64748B", cursor: "pointer" }}>
                          Remove Mod
                        </button>
                      )}
                      {!m.removed && (
                        <button onClick={() => { if (window.confirm(`Remove ${m.name} from this group?`)) handleMemberAction(m.user_id, "remove"); }}
                          style={{ padding: "5px 10px", fontSize: 12, borderRadius: 6, border: "0.5px solid #FCA5A5", background: "#FEF2F2", color: "#DC2626", cursor: "pointer" }}>
                          Kick
                        </button>
                      )}
                    </div>
                  )}
                  {/* Mod controls: mute and kick regular members only */}
                  {isModerator && !isCreator && !isOwn && !isOwnerMember && !m.is_moderator && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => handleMemberAction(m.user_id, m.muted ? "unmute" : "mute")}
                        style={{ padding: "5px 10px", fontSize: 12, borderRadius: 6, border: "0.5px solid #e0e0e0", background: "#fff", color: "#475569", cursor: "pointer" }}>
                        {m.muted ? "Unmute" : "Mute 24h"}
                      </button>
                      {!m.removed && (
                        <button onClick={() => { if (window.confirm(`Kick ${m.name} from this group?`)) handleMemberAction(m.user_id, "remove"); }}
                          style={{ padding: "5px 10px", fontSize: 12, borderRadius: 6, border: "0.5px solid #FCA5A5", background: "#FEF2F2", color: "#DC2626", cursor: "pointer" }}>
                          Kick
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Rules tab */}
        {settingsTab === "rules" && (
          <div>
            <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 1rem" }}>
              Group rules are shown to new members when they join.
            </p>
            {rules.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "1rem" }}>
                {rules.map((rule, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#F8FAFC", border: "0.5px solid #e5e7eb", borderRadius: 8, padding: "10px 12px" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1E3A5F", flexShrink: 0, minWidth: 20 }}>{i + 1}.</span>
                    {editRuleIdx === i ? (
                      <div style={{ flex: 1, display: "flex", gap: 6 }}>
                        <input value={editRuleText} onChange={e => setEditRuleText(e.target.value)} autoFocus
                          style={{ flex: 1, height: 36, border: "0.5px solid #BFDBFE", borderRadius: 6, padding: "0 10px", fontSize: 13 }} />
                        <button onClick={() => { const r = [...rules]; r[i] = editRuleText; setRules(r); setEditRuleIdx(null); handleSaveRules(); }}
                          style={{ padding: "0 12px", height: 36, background: "#16A34A", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>Save</button>
                        <button onClick={() => setEditRuleIdx(null)}
                          style={{ padding: "0 10px", height: 36, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>✕</button>
                      </div>
                    ) : (
                      <>
                        <span style={{ flex: 1, fontSize: 13, color: "#333", lineHeight: 1.5 }}>{rule}</span>
                        {isCreator && (
                          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                            <button onClick={() => { setEditRuleIdx(i); setEditRuleText(rule); }}
                              style={{ padding: "3px 8px", fontSize: 12, borderRadius: 5, border: "0.5px solid #e0e0e0", background: "#fff", color: "#475569", cursor: "pointer" }}>Edit</button>
                            <button onClick={() => { const r = rules.filter((_, j) => j !== i); setRules(r); setTimeout(handleSaveRules, 0); }}
                              style={{ padding: "3px 8px", fontSize: 12, borderRadius: 5, border: "0.5px solid #FCA5A5", background: "#FEF2F2", color: "#DC2626", cursor: "pointer" }}>✕</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: "1rem" }}>No rules set yet.</div>
            )}
            {isCreator && (
              <form onSubmit={e => { e.preventDefault(); if (newRule.trim()) { const r = [...rules, newRule.trim()]; setRules(r); setNewRule(""); supabase.from("community_groups").update({ rules: r }).eq("id", group.id).then(() => {}); } }}
                style={{ display: "flex", gap: 8 }}>
                <input value={newRule} onChange={e => setNewRule(e.target.value)} placeholder="Add a new rule…"
                  style={{ flex: 1, height: 40, border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "0 12px", fontSize: 14, outline: "none" }} />
                <button type="submit" disabled={!newRule.trim()}
                  style={{ padding: "0 16px", height: 40, borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600, background: newRule.trim() ? "#16A34A" : "#e0e0e0", color: newRule.trim() ? "#fff" : "#aaa", cursor: newRule.trim() ? "pointer" : "default" }}>
                  Add rule
                </button>
              </form>
            )}
          </div>
        )}

        {/* Requests tab */}
        {settingsTab === "requests" && isCreator && (
          <div>
            <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 1rem" }}>People who have requested to join this group.</p>
            {joinReqLoading ? (
              <div style={{ textAlign: "center", color: "#aaa", padding: "2rem" }}>Loading…</div>
            ) : joinRequests.length === 0 ? (
              <div style={{ textAlign: "center", color: "#94A3B8", padding: "2rem", fontSize: 14 }}>No pending requests</div>
            ) : joinRequests.map(req => (
              <div key={req.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "0.5px solid #f0f0f0" }}>
                <Avatar initials={nameInitials(req.profiles?.name || "?")} color={pickColor(req.user_id)} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F" }}>{req.profiles?.name || "Member"}</div>
                  <div style={{ fontSize: 12, color: "#64748B", textTransform: "capitalize" }}>{req.profiles?.role || "member"}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => handleAcceptRequest(req.user_id, req.id)}
                    style={{ padding: "6px 14px", fontSize: 13, borderRadius: 8, border: "none", background: "#16A34A", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                    Accept
                  </button>
                  <button onClick={() => handleDeclineRequest(req.id)}
                    style={{ padding: "6px 12px", fontSize: 13, borderRadius: 8, border: "0.5px solid #e0e0e0", background: "#fff", color: "#64748B", cursor: "pointer" }}>
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Profile tab */}
        {settingsTab === "profile" && isCreator && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", display: "block", marginBottom: 8 }}>Group photo</label>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  {groupAvatarPreview || group.avatar_url ? (
                    <img src={groupAvatarPreview || group.avatar_url} alt="" style={{ width: 64, height: 64, borderRadius: 14, objectFit: "cover", border: "0.5px solid #e0e0e0", flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 64, height: 64, borderRadius: 14, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", border: "0.5px dashed #cbd5e1", flexShrink: 0 }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    </div>
                  )}
                  <label style={{ cursor: "pointer", fontSize: 13, color: "var(--accent)", fontWeight: 600, padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--accent)", background: "var(--accent-bg)" }}>
                    {groupAvatarFile ? "Change photo" : "Upload photo"}
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                      const f = e.target.files?.[0]; if (!f) return;
                      setGroupAvatarFile(f);
                      const reader = new FileReader();
                      reader.onload = ev => setGroupAvatarPreview(ev.target.result);
                      reader.readAsDataURL(f);
                    }} />
                  </label>
                  {groupAvatarFile && <button type="button" onClick={() => { setGroupAvatarFile(null); setGroupAvatarPreview(null); }} style={{ fontSize: 12, color: "#94A3B8", background: "none", border: "none", cursor: "pointer" }}>Remove</button>}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", display: "block", marginBottom: 6 }}>Description</label>
                <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} maxLength={300} rows={3}
                  placeholder="What is this group about?"
                  style={{ width: "100%", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", display: "block", marginBottom: 4 }}>Group rules</label>
                <textarea value={editRulesText} onChange={e => setEditRulesText(e.target.value)} rows={5}
                  placeholder={"One rule per line, e.g.:\nBe respectful\nNo spam"}
                  style={{ width: "100%", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
                <p style={{ fontSize: 12, color: "#94A3B8", margin: "4px 0 0" }}>Shown to members before they join.</p>
              </div>
            </div>
            <button onClick={handleSaveGroupProfile} disabled={savingProfile}
              style={{ marginTop: "auto", background: "var(--accent)", color: "#fff", border: "none", padding: "11px", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: savingProfile ? "default" : "pointer" }}>
              {savingProfile ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
        {settingsToast && (
          <div style={{
            position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
            background: settingsToast.type === "error" ? "#DC2626" : settingsToast.type === "warning" ? "#D97706" : "#16A34A",
            color: "#fff", padding: "11px 22px", borderRadius: 10,
            fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
            zIndex: 9999, pointerEvents: "none", animation: "fadeInUp 0.2s ease",
            maxWidth: "90vw", textAlign: "center",
          }}>
            {settingsToast.type === "error" ? "✕" : settingsToast.type === "warning" ? "⚠" : "✓"} {settingsToast.msg}
          </div>
        )}
      </div>
    );
  }

  // ── Main chat view ───────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
    <div style={{ padding: "1rem 1.25rem 0.5rem", flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "0.75rem", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0, flex: 1 }}>
          {group.avatar_url ? (
            <div onClick={() => setAvatarLightboxOpen(true)} style={{ cursor: "pointer", flexShrink: 0, borderRadius: 14, overflow: "hidden", width: 52, height: 52, border: "0.5px solid #e0e0e0" }}>
              <img src={group.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ) : (
            <div style={{ width: 52, height: 52, borderRadius: 14, background: "#EBF2FF", color: "#1E3A5F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, flexShrink: 0 }}>
              {group.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            {group.description && <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 4px" }}>{group.description}</p>}
            <div style={{ fontSize: 12, color: "#94A3B8", display: "flex", flexWrap: "wrap", gap: "3px 10px", alignItems: "center" }}>
              <span><Icon name="map-pin" size={12} style={{marginRight:3,flexShrink:0}} />{group.region}</span>
              <span>{group.member_count || 0} {group.member_count === 1 ? "member" : "members"}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22C55E", display: "inline-block", flexShrink: 0 }} />
                <span style={{ color: "#22C55E", fontWeight: 600 }}>{onlineCount} online</span>
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
          {(isCreator || isModerator) && isMember && (
            <button onClick={() => { setShowSettings(true); setSettingsTab("members"); loadMembers(); }}
              style={{ padding: "7px 12px", borderRadius: 8, border: "0.5px solid #e0e0e0", fontSize: 13, fontWeight: 500, background: "#fff", color: "#64748B", cursor: "pointer" }}>
              Settings
            </button>
          )}
          {user && !isCreator && (
            isMember ? (
              <button onClick={onLeave}
                style={{ padding: "7px 14px", borderRadius: 8, border: "0.5px solid #e0e0e0", fontSize: 13, fontWeight: 500, background: "#fff", color: "#64748B", cursor: "pointer" }}>
                Leave
              </button>
            ) : group.is_private ? (
              hasPendingRequest ? (
                <div style={{ padding: "7px 14px", borderRadius: 8, background: "#FEF9C3", color: "#854D0E", fontSize: 13, fontWeight: 600 }}>Requested</div>
              ) : (
                <button onClick={onJoin}
                  style={{ padding: "7px 14px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 500, background: "#3B82F6", color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>
                  Request to join
                </button>
              )
            ) : (
              <button onClick={onJoin}
                style={{ padding: "7px 14px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 500, background: "#3B82F6", color: "#fff", cursor: "pointer" }}>
                Join group
              </button>
            )
          )}
        </div>
      </div>

      {banned && (
        <div style={{ background: "#FEE2E2", border: "0.5px solid #FCA5A5", borderRadius: 10, padding: "12px 16px", marginBottom: "0.75rem" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#991B1B" }}>Community chat access suspended</div>
        </div>
      )}
      {muted && (
        <div style={{ background: "#FFFBEB", border: "0.5px solid #FDE68A", borderRadius: 10, padding: "12px 16px", marginBottom: "0.75rem" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#92400E" }}>You are muted in this group</div>
        </div>
      )}

      {!isMember && user && !banned && (
        <div style={{ background: "#EFF6FF", border: "1.5px solid #BFDBFE", borderRadius: 12, padding: "16px 18px", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1E3A5F", marginBottom: 3 }}>
              {group.is_private ? "Private group" : "Join to participate"}
            </div>
            <div style={{ fontSize: 13, color: "#3B82F6" }}>
              {group.is_private
                ? hasPendingRequest
                  ? "Your join request is pending approval by the group owner."
                  : "This is a private group. Send a join request to participate."
                : "Join this group to send messages and see the full conversation."}
            </div>
          </div>
          {group.is_private ? (
            hasPendingRequest ? (
              <div style={{ padding: "10px 18px", borderRadius: 10, background: "#FEF9C3", color: "#854D0E", fontSize: 14, fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}>
                Request sent ✓
              </div>
            ) : (
              <button onClick={onJoin}
                style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "10px 22px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", minHeight: 44, flexShrink: 0, whiteSpace: "nowrap" }}>
                Request to join
              </button>
            )
          ) : (
            <button onClick={onJoin}
              style={{ background: "#16A34A", color: "#fff", border: "none", padding: "10px 22px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", minHeight: 44, flexShrink: 0, whiteSpace: "nowrap" }}>
              Join group
            </button>
          )}
        </div>
      )}
      {!user && (
        <div style={{ background: "#EFF6FF", border: "1.5px solid #BFDBFE", borderRadius: 12, padding: "14px 18px", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, color: "#1E3A5F", fontWeight: 500 }}>Log in to join and chat</div>
          <button onClick={() => setPage("auth")}
            style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", minHeight: 40, flexShrink: 0 }}>
            Log in
          </button>
        </div>
      )}

      {/* Pinned message banner */}
      {group.pinned_message_content && (
        <div style={{ background: "#EFF6FF", border: "0.5px solid #BFDBFE", borderRadius: 10, padding: "10px 14px", marginBottom: "0.75rem", display: "flex", alignItems: "flex-start", gap: 8 }}>
          <Icon name="map-pin" size={14} style={{color:"#1D4ED8"}} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#64748B", marginBottom: 2 }}>Pinned by {group.pinned_message_author || "moderator"}</div>
            <div style={{ fontSize: 13, color: "#1E3A5F", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.pinned_message_content}</div>
          </div>
          {(isCreator || isModerator) && (
            <button onClick={handleUnpinMessage}
              style={{ background: "none", border: "none", color: "#94A3B8", fontSize: 16, cursor: "pointer", flexShrink: 0, padding: "0 2px" }}>✕</button>
          )}
        </div>
      )}

      {/* Channel switcher */}
      {channels.length > 0 && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap" }}>
          {channels.map(ch => (
            <button key={ch.id} onClick={() => setActiveChannel(ch)}
              style={{ padding: "5px 14px", borderRadius: 20, border: "none", fontSize: 13, fontWeight: activeChannel?.id === ch.id ? 700 : 400, background: activeChannel?.id === ch.id ? "#1E3A5F" : "#F1F5F9", color: activeChannel?.id === ch.id ? "#fff" : "#475569", cursor: "pointer", transition: "background 0.15s" }}>
              # {ch.name}
            </button>
          ))}
          {isCreator && windowWidth > 768 && (
            addingChannel ? (
              <form onSubmit={handleAddChannel} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input value={newChanName} onChange={e => setNewChanName(e.target.value)} autoFocus placeholder="Channel name"
                  style={{ height: 32, border: "0.5px solid #BFDBFE", borderRadius: 20, padding: "0 12px", fontSize: 13, outline: "none" }} />
                <button type="submit" style={{ height: 32, padding: "0 12px", borderRadius: 20, border: "none", background: "#3B82F6", color: "#fff", fontSize: 13, cursor: "pointer" }}>Add</button>
                <button type="button" onClick={() => setAddingChannel(false)} style={{ height: 32, padding: "0 10px", borderRadius: 20, border: "0.5px solid #e0e0e0", background: "#fff", color: "#555", fontSize: 13, cursor: "pointer" }}>✕</button>
              </form>
            ) : (
              <button onClick={() => setAddingChannel(true)}
                style={{ width: 28, height: 28, borderRadius: "50%", border: "0.5px dashed #94A3B8", background: "transparent", color: "#94A3B8", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                +
              </button>
            )
          )}
        </div>
      )}
      {channels.length === 0 && isCreator && windowWidth > 768 && (
        <div style={{ marginBottom: "0.75rem" }}>
          {addingChannel ? (
            <form onSubmit={handleAddChannel} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input value={newChanName} onChange={e => setNewChanName(e.target.value)} autoFocus placeholder="First channel name (e.g. General)"
                style={{ flex: 1, height: 36, border: "0.5px solid #BFDBFE", borderRadius: 8, padding: "0 12px", fontSize: 13, outline: "none" }} />
              <button type="submit" style={{ height: 36, padding: "0 14px", border: "none", borderRadius: 8, background: "#3B82F6", color: "#fff", fontSize: 13, cursor: "pointer" }}>Create</button>
              <button type="button" onClick={() => setAddingChannel(false)} style={{ height: 36, padding: "0 10px", border: "0.5px solid #e0e0e0", borderRadius: 8, background: "#fff", color: "#555", fontSize: 13, cursor: "pointer" }}>✕</button>
            </form>
          ) : (
            <button onClick={() => setAddingChannel(true)}
              style={{ fontSize: 13, color: "#3B82F6", background: "none", border: "0.5px dashed #BFDBFE", borderRadius: 8, padding: "6px 14px", cursor: "pointer" }}>
              + Add channels
            </button>
          )}
        </div>
      )}

      {/* Chat / Members tab switcher */}
      {isMember && (
        <div style={{ display: "flex", gap: 4, marginBottom: "0.5rem" }}>
          {[{ k: "chat", l: "Chat" }, { k: "members", l: `Members (${group.member_count || 0})` }].map(t => (
            <button key={t.k} onClick={() => { setActiveTab(t.k); if (t.k === "members" && members.length === 0) loadMembers(); }}
              style={{ padding: "7px 18px", borderRadius: 20, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", background: activeTab === t.k ? "var(--accent)" : "#F1F5F9", color: activeTab === t.k ? "#fff" : "#64748B", transition: "background 0.15s" }}>
              {t.l}
            </button>
          ))}
        </div>
      )}
    </div>

      {/* Members tab content — fills remaining height */}
      {activeTab === "members" && isMember && (
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 1.25rem 1rem", background: "#fff" }}>
          {membersLoading ? (
            <div style={{ textAlign: "center", color: "#aaa", padding: "2rem" }}>Loading…</div>
          ) : members.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94A3B8", padding: "2rem", fontSize: 14 }}>No members found</div>
          ) : members.map(m => {
            const isOwnerMem = m.user_id === group.created_by;
            return (
              <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "0.5px solid #f0f0f0" }}>
                <Avatar initials={nameInitials(m.name)} color={pickColor(m.user_id)} size={38} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {m.name}
                    {isOwnerMem && (
                      <span style={{ fontSize: 11, background: "#FEF3C7", color: "#92400E", padding: "2px 7px", borderRadius: 20, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
                        <Icon name="crown" size={10} />Owner
                      </span>
                    )}
                    {!isOwnerMem && m.is_moderator && (
                      <span style={{ fontSize: 11, background: "#DBEAFE", color: "#1D4ED8", padding: "2px 7px", borderRadius: 20, fontWeight: 600 }}>Mod</span>
                    )}
                    {!isOwnerMem && !m.is_moderator && (
                      <span style={{ fontSize: 11, background: "#F1F5F9", color: "#475569", padding: "2px 7px", borderRadius: 20, fontWeight: 500 }}>Member</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#94A3B8", textTransform: "capitalize" }}>{m.role || "member"}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "chat" && <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", borderTop: "0.5px solid #e0e0e0", background: "#fff" }}>
        <div style={{ padding: "9px 1rem", borderBottom: "0.5px solid #f0f0f0", flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: "#64748B" }}>
            {activeChannel ? `# ${activeChannel.name}` : "Group chat"} · Last 100 messages
          </span>
        </div>

        <div ref={chatScrollRef} style={{ flex: 1, overflowY: "auto", padding: isMobile ? "1rem" : "1rem 0.75rem" }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "#aaa", padding: "2rem", fontSize: 14 }}>Loading…</div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem" }}>
              <div style={{ marginBottom: 8, color: "var(--accent)" }}><Icon name="message" size={32} /></div>
              <div style={{ fontSize: 14, color: "#64748B" }}>No messages yet — start the conversation!</div>
            </div>
          ) : (
            messages.map(msg => (
              <GroupMessageBubble key={msg.id} msg={msg} myId={myId} groupCreatorId={group.created_by}
                isOwnerOrMod={isCreator || isModerator}
                onReport={handleReport} reported={!!reported[msg.id]}
                onDelete={handleDeleteMessage} onPin={handlePinMessage}
                postMap={postMap} />
            ))
          )}
        </div>

        {coolMsg && (
          <div style={{ padding: "7px 1rem", background: "#FFFBEB", borderTop: "0.5px solid #FDE68A", fontSize: 13, color: "#92400E", flexShrink: 0 }}>
            ⏳ {coolMsg}{cooldown > 0 ? ` (${cooldown}s)` : ""}
          </div>
        )}

        {user && !banned && !muted && isMember ? (
          <form onSubmit={handleSend} style={{ borderTop: "0.5px solid #f0f0f0", padding: "0.75rem 1rem", display: "flex", gap: 8, alignItems: "flex-end", flexShrink: 0 }}>
            <textarea value={text} onChange={e => setText(e.target.value)}
              placeholder={cooldown > 0 ? `Wait ${cooldown}s…` : activeChannel ? `Message #${activeChannel.name}…` : `Message ${group.name}…`}
              maxLength={1000} disabled={cooldown > 0} rows={1}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !isMobile) { e.preventDefault(); handleSend(e); } }}
              onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
              style={{ flex: 1, minHeight: 40, maxHeight: 120, border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 14, background: cooldown > 0 ? "#f5f5f5" : "#f9f9f9", outline: "none", resize: "none", overflowY: "auto", fontFamily: "inherit", lineHeight: "1.4", boxSizing: "border-box" }}
            />
            <button type="submit" disabled={!text.trim() || sending || cooldown > 0}
              style={{ width: 40, height: 40, borderRadius: 8, border: "none", flexShrink: 0, background: canSend && text.trim() && !sending ? "#3B82F6" : "#e0e0e0", cursor: canSend && text.trim() && !sending ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 8H2M14 8L9 3M14 8L9 13" stroke={canSend && text.trim() && !sending ? "#fff" : "#bbb"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </form>
        ) : !isMember ? (
          <div style={{ padding: "10px 1rem", borderTop: "0.5px solid #f0f0f0", textAlign: "center", fontSize: 13, color: "#64748B", flexShrink: 0 }}>
            Join this group above to start chatting.
          </div>
        ) : null}
      </div>}

      {avatarLightboxOpen && group.avatar_url && (
        <div
          onClick={() => setAvatarLightboxOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <button
            onClick={() => setAvatarLightboxOpen(false)}
            aria-label="Close"
            style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 22, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >×</button>
          <img
            src={group.avatar_url}
            alt={group.name}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 12, boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}
          />
        </div>
      )}
    </div>
  );
}

function GroupMessageBubble({ msg, myId, groupCreatorId, onReport, reported, isOwnerOrMod, onDelete, onPin, postMap = {} }) {
  const isOwn        = msg.user_id === myId;
  const isMsgCreator = msg.user_id === groupCreatorId;
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [subOpen,    setSubOpen]    = useState(false);
  const [reportSent, setReportSent] = useState(reported || false);
  const menuRef = useRef(null);
  const longPressRef = useRef(null);

  useEffect(() => {
    function outside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) { setMenuOpen(false); setSubOpen(false); }
    }
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  function startLongPress() {
    longPressRef.current = setTimeout(() => { setMenuOpen(true); setSubOpen(false); }, 600);
  }
  function cancelLongPress() { clearTimeout(longPressRef.current); }

  if (msg.deleted) {
    return (
      <div style={{ display: "flex", gap: 10, marginBottom: "0.875rem", alignItems: "center" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#f0f0f0", flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: "#bbb", fontStyle: "italic", background: "#fafafa", padding: "5px 12px", borderRadius: 8, border: "0.5px solid #ececec" }}>
          [message deleted]
        </span>
      </div>
    );
  }

  if (msg.hidden) {
    return (
      <div style={{ display: "flex", gap: 10, marginBottom: "0.875rem", alignItems: "center" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#f0f0f0", flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: "#aaa", fontStyle: "italic", background: "#f9f9f7", padding: "5px 12px", borderRadius: 8, border: "0.5px solid #ececec" }}>
          [message under review]
        </span>
      </div>
    );
  }

  const showDots = !isOwn || isOwnerOrMod;

  return (
    <div
      style={{ display: "flex", gap: 10, marginBottom: "0.875rem", alignItems: "flex-start", background: "var(--bg-card,#fff)", border: "0.5px solid var(--border,#e8e8e8)", borderRadius: 8, padding: "12px 16px" }}
      onTouchStart={startLongPress} onTouchEnd={cancelLongPress} onTouchMove={cancelLongPress}
    >
      <Avatar initials={nameInitials(msg.user_name || "?")} color={pickColor(msg.user_id || "")} size={34} url={msg.user_avatar_url || null} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: isOwn ? "#2E5FA3" : "#1E3A5F" }}>{msg.user_name || "User"}</span>
          {isMsgCreator && <Icon name="crown" size={12} style={{color:"#D97706"}} />}
          <RoleBadge authRole={msg.user_role} />
          {msg.pinned && <span style={{ fontSize: 11, color: "#1D4ED8", background: "#DBEAFE", padding: "1px 6px", borderRadius: 10, display:"inline-flex", alignItems:"center", gap:3 }}><Icon name="map-pin" size={10} />pinned</span>}
          <span style={{ fontSize: 11, color: "#aaa", marginLeft: "auto", flexShrink: 0 }}>{fmtTimeAgo(msg.created_at)}</span>
          {showDots && (
            <div ref={menuRef} style={{ position: "relative", flexShrink: 0 }}>
              <button onClick={() => { setMenuOpen(o => !o); setSubOpen(false); }}
                style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 5px", color: "#bbb", borderRadius: 4, fontSize: 17, lineHeight: 1 }}>
                ···
              </button>
              {menuOpen && (
                <div style={{ position: "absolute", right: 0, top: "calc(100% + 2px)", background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.10)", zIndex: 200, minWidth: 190, overflow: "hidden" }}>
                  {!subOpen ? (
                    <>
                      {isOwnerOrMod && (
                        <>
                          <button onClick={() => { setMenuOpen(false); onDelete && onDelete(msg.id); }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 13, color: "#DC2626", background: "none", border: "none", cursor: "pointer", borderBottom: "0.5px solid #f0f0f0" }}>
                            Delete message
                          </button>
                          <button onClick={() => { setMenuOpen(false); onPin && onPin(msg); }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 13, color: "#1D4ED8", background: "none", border: "none", cursor: "pointer", borderBottom: "0.5px solid #f0f0f0" }}>
                            Pin message
                          </button>
                        </>
                      )}
                      {!isOwn && (
                        <button onClick={() => setSubOpen(true)} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 13, color: "#D85A30", background: "none", border: "none", cursor: "pointer" }}>
                          Report message
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <div style={{ padding: "7px 14px 5px", fontSize: 11, fontWeight: 600, color: "#777", borderBottom: "0.5px solid #f0f0f0" }}>Select reason:</div>
                      {REPORT_REASONS.map(r => (
                        <button key={r}
                          onClick={async () => {
                            setMenuOpen(false); setSubOpen(false);
                            if (!reportSent) { setReportSent(true); await onReport(msg, r); }
                          }}
                          disabled={reportSent}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 14px", fontSize: 13, color: reportSent ? "#aaa" : "#333", background: "none", border: "none", cursor: reportSent ? "default" : "pointer" }}>
                          {r}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {msg.shared_post_id ? (() => {
          const openPost = () => window.dispatchEvent(new CustomEvent("lenderbuild:open-post", { detail: { postId: msg.shared_post_id } }));
          const sharedPost = postMap[msg.shared_post_id];
          if (sharedPost) {
            const cat = CATEGORY_META[sharedPost.category] || CATEGORY_META.general;
            const excerpt = (sharedPost.body || "").length > 140 ? sharedPost.body.slice(0, 140).trimEnd() + "…" : (sharedPost.body || "");
            return (
              <div
                onClick={openPost}
                style={{ cursor: "pointer", background: "var(--accent-bg,#EBF2FF)", border: "0.5px solid var(--accent,#3B82F6)", borderRadius: 8, padding: "10px 12px", marginTop: 2 }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
              >
                <div style={{ marginBottom: 6 }}>
                  <span style={{ background: cat.bg, color: cat.text, fontSize: 11, padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>{cat.label}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1E3A5F", textDecoration: "underline", lineHeight: 1.4, marginBottom: 4 }}>{sharedPost.title}</div>
                {excerpt && <div style={{ fontSize: 13, color: "var(--text-muted,#64748B)", lineHeight: 1.5, marginBottom: 8 }}>{excerpt}</div>}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: cat.bg, color: cat.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                    {(sharedPost.author_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>{sharedPost.author_name}</span>
                  <span style={{ fontSize: 12, color: "#9CA3AF" }}>·</span>
                  <span style={{ fontSize: 12, color: "#9CA3AF" }}>{fmtTimeAgo(sharedPost.created_at)}</span>
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                    {(sharedPost.view_count || 0) > 0 && (
                      <span style={{ fontSize: 12, color: "#9CA3AF", display: "flex", alignItems: "center", gap: 3 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        {sharedPost.view_count}
                      </span>
                    )}
                    {(sharedPost.interest_count || 0) > 0 && (
                      <span style={{ fontSize: 12, color: "#9CA3AF", display: "flex", alignItems: "center", gap: 3 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                        {sharedPost.interest_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          // Fallback: post not loaded or deleted
          const lines = (msg.content || "").split("\n");
          const titleLine = lines[0] || "";
          const preview   = lines.slice(1).join("\n").trim();
          const titleText = titleLine.replace(/^📌\s*Shared a post:\s*/i, "").replace(/^[""]|[""]$/g, "");
          return (
            <div
              onClick={openPost}
              style={{ cursor: "pointer", background: "var(--accent-bg,#EBF2FF)", border: "0.5px solid var(--accent,#3B82F6)", borderRadius: 8, padding: "8px 12px", marginTop: 2 }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--accent,#3B82F6)", marginBottom: 3 }}>📌 Shared post</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", textDecoration: "underline", lineHeight: 1.4 }}>{titleText}</div>
              {preview && <div style={{ fontSize: 13, color: "var(--text-muted,#64748B)", marginTop: 4, lineHeight: 1.5 }}>{preview}</div>}
            </div>
          );
        })() : (
          <div style={{ fontSize: 14, color: "#333", lineHeight: 1.55, wordBreak: "break-word" }}>{msg.content}</div>
        )}
        {reportSent && !msg.hidden && <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>Reported — thank you</div>}
      </div>
    </div>
  );
}

// ─── BROWSE PROJECTS PAGE ────────────────────────────────────────────────────

function BrowseProjectsPage({ user, setPage }) {
  const [listings,        setListings]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [interestStatus,  setInterestStatus]  = useState({});
  const [commitments,     setCommitments]     = useState([]); // group_project_commitments (legacy)
  const [syndicateTotals, setSyndicateTotals] = useState({}); // { [listing_id]: { total, count } }
  const [myCommitments,   setMyCommitments]   = useState({}); // { [listing_id]: amount } for syndicate
  const [myRoomsMap,      setMyRoomsMap]      = useState({}); // { [listing_id]: room } for group funding
  const role = user?.user_metadata?.role;

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setLoading(true);
    const [listRes, { data: cmts }] = await Promise.all([
      fetch("/api/project-listings").then(r => r.json()).catch(() => ({})),
      supabase.from("group_project_commitments").select("*"),
    ]);
    setListings(listRes.listings || []);
    setSyndicateTotals(listRes.syndicate_totals || {});
    setCommitments(cmts || []);

    if (user) {
      const { data: { session } } = await supabase.auth.getSession();
      const [synRes, roomRes] = await Promise.all([
        supabase.from("syndicate_commitments").select("listing_id, amount").eq("lender_user_id", user.id),
        session ? fetch("/api/project-listings", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: "funding-room-list" }),
        }).then(r => r.ok ? r.json() : { rooms: [] }).catch(() => ({ rooms: [] })) : Promise.resolve({ rooms: [] }),
      ]);
      const mineMap = {};
      for (const c of synRes.data || []) mineMap[c.listing_id] = Number(c.amount);
      setMyCommitments(mineMap);
      const roomMap = {};
      for (const r of roomRes.rooms || []) roomMap[r.listing_id] = r;
      setMyRoomsMap(roomMap);
    }
    setLoading(false);
  }

  async function handleExpressInterest(listing) {
    if (!user) { setPage("auth"); return; }
    const key = listing.id;
    setInterestStatus(prev => ({ ...prev, [key]: "loading" }));
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/connect-request", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "express-interest", builder_user_id: listing.user_id, builder_name: listing.builder_name, listing_title: listing.title }),
    });
    if (res.ok) {
      setInterestStatus(prev => ({ ...prev, [key]: "done" }));
      await supabase.auth.refreshSession();
    } else {
      const d = await res.json().catch(() => ({}));
      if (d.error === "Already expressed interest") {
        setInterestStatus(prev => ({ ...prev, [key]: "done" }));
      } else {
        setInterestStatus(prev => ({ ...prev, [key]: "error" }));
        setTimeout(() => setInterestStatus(prev => ({ ...prev, [key]: "idle" })), 3000);
      }
    }
  }

  async function handleCommit(listing, amount) {
    if (!user) { setPage("auth"); return; }
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-join", listing_id: listing.id, builder_user_id: listing.user_id, amount: Number(amount) }),
    });
    if (res.ok) {
      const d = await res.json();
      const room = d.room;
      setMyRoomsMap(prev => ({ ...prev, [listing.id]: { ...room, my_amount: Number(amount), listing_title: listing.title } }));
      setPage("funding-room", room.id);
    }
  }

  async function handleSyndicateCommit(listing, amount) {
    if (!user) { setPage("auth"); return; }
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "commit-syndicate", listing_id: listing.id, builder_user_id: listing.user_id, amount: Number(amount) }),
    });
    if (res.ok) {
      const d = await res.json();
      const newTotal = (d.commitments || []).reduce((s, c) => s + Number(c.amount), 0);
      const newCount = (d.commitments || []).length;
      setSyndicateTotals(prev => ({ ...prev, [listing.id]: { total: newTotal, count: newCount } }));
      setMyCommitments(prev => ({ ...prev, [listing.id]: Number(amount) }));
    }
  }

  const myListings        = role === "builder" ? listings.filter(l => l.user_id === user?.id) : [];
  const otherListings     = role === "builder" ? listings.filter(l => l.user_id !== user?.id) : listings;
  const syndicatedListings = otherListings.filter(l => l.syndicate_open);
  const groupListings     = otherListings.filter(l => l.group_funding && !l.syndicate_open);
  const regularListings   = otherListings.filter(l => !l.group_funding && !l.syndicate_open);

  function cmtsFor(listingId) {
    return commitments.filter(c => c.listing_id === listingId);
  }

  return (
    <div style={{ padding: "1.5rem 1.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0, fontFamily: "'Georgia', serif" }}>Browse Projects</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>Property projects seeking funding from lenders</p>
        </div>
        {role === "builder" && (
          <button
            onClick={() => setPage("create-project-listing")}
            style={{ padding: "9px 18px", minHeight: 44, background: "#3B82F6", color: "#FFFFFF", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            + Post a project
          </button>
        )}
        {!user && (
          <button
            onClick={() => setPage("auth")}
            style={{ padding: "9px 18px", background: "#3B82F6", color: "#FFFFFF", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            Log in to express interest
          </button>
        )}
      </div>

      {/* Builder's own listings */}
      {role === "builder" && myListings.length > 0 && (
        <div style={{ marginBottom: "1.75rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Your listings</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {myListings.map(l => (
              <ProjectListingCard key={l.id} listing={l} user={user} isOwn
                interestStatus={interestStatus[l.id]} onExpressInterest={handleExpressInterest}
                listingCommitments={cmtsFor(l.id)} onCommit={handleCommit}
                syndicateTotal={syndicateTotals[l.id]?.total || 0}
                syndicateCount={syndicateTotals[l.id]?.count || 0}
                myCommitmentAmount={myCommitments[l.id] || 0}
                onSyndicateCommit={handleSyndicateCommit}
                myRoom={myRoomsMap[l.id]} onEnterRoom={roomId => setPage("funding-room", roomId)}
              />
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", color: "#aaa", padding: "3rem", fontSize: 14 }}>Loading projects…</div>
      ) : otherListings.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <div style={{ marginBottom: 10, color: "var(--accent)" }}><Icon name="hard-hat" size={36} /></div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No projects yet</div>
          <div style={{ fontSize: 13, color: "#64748B" }}>
            {role === "builder"
              ? "Be the first to post a project listing."
              : "Check back soon — builders are posting projects."}
          </div>
        </div>
      ) : (
        <>
          {/* Syndicated lending projects */}
          {syndicatedListings.length > 0 && (
            <div style={{ marginBottom: "1.75rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#1D4ED8", letterSpacing: "0.1em", textTransform: "uppercase" }}>Syndicated Lending</div>
                <HelpTooltip text="Multiple lenders fund one project together. Each lender contributes a portion and receives proportional returns." />
                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "#EFF6FF", color: "#1D4ED8", fontWeight: 600 }}>Multiple lenders · tracked commitments</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
                {syndicatedListings.map(l => (
                  <ProjectListingCard key={l.id} listing={l} user={user} isOwn={false}
                    interestStatus={interestStatus[l.id]} onExpressInterest={handleExpressInterest}
                    listingCommitments={cmtsFor(l.id)} onCommit={handleCommit}
                    syndicateTotal={syndicateTotals[l.id]?.total || 0}
                    syndicateCount={syndicateTotals[l.id]?.count || 0}
                    myCommitmentAmount={myCommitments[l.id] || 0}
                    onSyndicateCommit={handleSyndicateCommit}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Group funding projects */}
          {groupListings.length > 0 && (
            <div style={{ marginBottom: "1.75rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#7C3AED", letterSpacing: "0.1em", textTransform: "uppercase" }}>Group Funding Projects</div>
                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "#F3F0FF", color: "#7C3AED", fontWeight: 600 }}>Multiple lenders welcome</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
                {groupListings.map(l => (
                  <ProjectListingCard key={l.id} listing={l} user={user} isOwn={false}
                    interestStatus={interestStatus[l.id]} onExpressInterest={handleExpressInterest}
                    listingCommitments={cmtsFor(l.id)} onCommit={handleCommit}
                    syndicateTotal={0} syndicateCount={0} myCommitmentAmount={0} onSyndicateCommit={handleSyndicateCommit}
                    myRoom={myRoomsMap[l.id]} onEnterRoom={roomId => setPage("funding-room", roomId)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Regular projects */}
          {regularListings.length > 0 && (
            <>
              {(syndicatedListings.length > 0 || groupListings.length > 0) && (
                <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 12 }}>
                  {role === "builder" && myListings.length > 0 ? "Other projects" : "All projects"}
                </div>
              )}
              {role === "builder" && myListings.length > 0 && syndicatedListings.length === 0 && groupListings.length === 0 && (
                <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 12 }}>Other projects</div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                {regularListings.map(l => (
                  <ProjectListingCard key={l.id} listing={l} user={user} isOwn={false}
                    interestStatus={interestStatus[l.id]} onExpressInterest={handleExpressInterest}
                    listingCommitments={[]} onCommit={handleCommit}
                    syndicateTotal={0} syndicateCount={0} myCommitmentAmount={0} onSyndicateCommit={handleSyndicateCommit}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function ProjectListingCard({ listing, user, isOwn, interestStatus, onExpressInterest, listingCommitments = [], onCommit,
  syndicateTotal = 0, syndicateCount = 0, myCommitmentAmount = 0, onSyndicateCommit, myRoom, onEnterRoom }) {
  const [photoIdx,    setPhotoIdx]    = useState(0);
  const [commitInput, setCommitInput] = useState("");
  const [commitOpen,  setCommitOpen]  = useState(false);
  const [committing,  setCommitting]  = useState(false);
  const [synCommitOpen,  setSynCommitOpen]  = useState(false);
  const [synCommitInput, setSynCommitInput] = useState("");
  const [synCommitting,  setSynCommitting]  = useState(false);
  const [synError,       setSynError]       = useState("");
  const photos = listing.photos || [];
  const role   = user?.user_metadata?.role;
  const st     = interestStatus || "idle";

  const alreadySent = user?.user_metadata?.builder_connections?.some(
    c => c.builder_name === listing.builder_name && c.project_title === listing.title
  );
  const isDone = alreadySent || st === "done";

  const btnLabel = isDone ? "Interest sent!" : st === "loading" ? "Sending…" : st === "error" ? "Error — retry" : "Express Interest";
  const btnBg    = isDone ? "#5aaf90" : st === "error" ? "#D85A30" : "#1D9E75";

  // Group funding progress — prefer live room data if available
  const isGroup        = !!listing.group_funding;
  const totalNeeded    = listing.funding_needed || 0;
  const roomCommitted  = myRoom ? Number(myRoom.committed_amount) : null;
  const totalCommitted = roomCommitted !== null ? roomCommitted : listingCommitments.reduce((s, c) => s + Number(c.amount), 0);
  const pct            = totalNeeded > 0 ? Math.min(100, Math.round((totalCommitted / totalNeeded) * 100)) : 0;
  const fullyFunded    = myRoom ? myRoom.status === "fully_funded" : pct >= 100;
  const myCommitment   = listingCommitments.find(c => c.lender_id === user?.id);

  // Syndicated lending
  const isSyndicate      = !!listing.syndicate_open;
  const synPct           = totalNeeded > 0 ? Math.min(100, Math.round((syndicateTotal / totalNeeded) * 100)) : 0;
  const synFullyFunded   = synPct >= 100;
  const minCommitment    = listing.min_commitment || 0;
  const mySharePct       = totalNeeded > 0 && myCommitmentAmount > 0
    ? ((myCommitmentAmount / totalNeeded) * 100).toFixed(1)
    : null;

  async function handleCommitSubmit(e) {
    e.preventDefault();
    if (!commitInput || isNaN(Number(commitInput)) || Number(commitInput) <= 0) return;
    setCommitting(true);
    await onCommit(listing, commitInput);
    setCommitting(false);
    setCommitOpen(false);
    setCommitInput("");
  }

  async function handleSynCommitSubmit(e) {
    e.preventDefault();
    setSynError("");
    const val = Number(synCommitInput);
    if (!synCommitInput || isNaN(val) || val <= 0) return;
    if (minCommitment > 0 && val < minCommitment) {
      setSynError(`Minimum commitment is ${fmt(minCommitment)}`);
      return;
    }
    setSynCommitting(true);
    await onSyndicateCommit(listing, synCommitInput);
    setSynCommitting(false);
    setSynCommitOpen(false);
    setSynCommitInput("");
  }

  const cardBorder = isSyndicate ? "#BFDBFE" : isGroup ? "#DDD6FE" : "#e0e0e0";
  const cardHeaderBg = isSyndicate ? "#EFF6FF" : isGroup ? "#F3F0FF" : "#f0faf6";

  return (
    <div style={{ background: "#fff", border: `0.5px solid ${cardBorder}`, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      {photos.length > 0 ? (
        <div style={{ position: "relative", height: 180, background: "#f0f0f0", overflow: "hidden", flexShrink: 0 }}>
          <img src={photos[photoIdx]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          {photos.length > 1 && (
            <div style={{ position: "absolute", bottom: 8, right: 8, display: "flex", gap: 4 }}>
              {photos.map((_, i) => (
                <button key={i} onClick={() => setPhotoIdx(i)} style={{
                  width: 7, height: 7, borderRadius: "50%", border: "none",
                  cursor: "pointer", padding: 0,
                  background: i === photoIdx ? "#fff" : "rgba(255,255,255,0.5)",
                }} />
              ))}
            </div>
          )}
          <div style={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 4 }}>
            {isSyndicate && (
              <span style={{ background: "#1D4ED8", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
                Syndicated
              </span>
            )}
            {isGroup && (
              <span style={{ background: "#7C3AED", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
                Group Funding
              </span>
            )}
          </div>
          {isSyndicate && synFullyFunded && (
            <div style={{ position: "absolute", top: 8, right: 8, background: "#059669", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
              Fully Funded ✓
            </div>
          )}
        </div>
      ) : (
        <div style={{ height: 90, background: cardHeaderBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
          <Icon name="hard-hat" size={32} style={{color:"rgba(255,255,255,0.5)"}} />
          <div style={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 4 }}>
            {isSyndicate && (
              <span style={{ background: "#1D4ED8", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
                Syndicated
              </span>
            )}
            {isGroup && (
              <span style={{ background: "#7C3AED", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
                Group Funding
              </span>
            )}
          </div>
          {isSyndicate && synFullyFunded && (
            <div style={{ position: "absolute", top: 8, right: 8, background: "#059669", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
              Fully Funded ✓
            </div>
          )}
        </div>
      )}

      <div style={{ padding: "1rem", flex: 1, display: "flex", flexDirection: "column" }}>
        {(() => {
          const ai = computeAIRiskAnalysis(listing);
          return (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{listing.title}</div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: ai.riskBg, color: ai.riskColor, flexShrink: 0, marginLeft: 6 }}>
                {ai.riskLabel}
              </span>
            </div>
          );
        })()}

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <Avatar initials={nameInitials(listing.builder_name)} color={pickColor(listing.user_id || "")} size={22} url={listing.builder_avatar_url} />
          <span style={{ fontSize: 12, color: "#64748B" }}>{listing.builder_name}</span>
          {listing.location && <span style={{ fontSize: 12, color: "#aaa" }}>· <Icon name="map-pin" size={12} style={{marginRight:3,flexShrink:0}} />{listing.location}</span>}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
          {listing.project_type    && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#EBF2FF", color: "#1E3A5F" }}>{listing.project_type}</span>}
          {listing.funding_needed  && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#EBF2FF", color: "#1E3A5F" }}>{fmt(listing.funding_needed)}</span>}
          {listing.expected_return && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#DBEAFE", color: "#1D4ED8" }}>{listing.expected_return} return</span>}
        </div>

        {listing.description && (
          <p style={{
            fontSize: 13, color: "#666", lineHeight: 1.5, margin: "0 0 10px", flex: 1,
            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            {listing.description}
          </p>
        )}

        {listing.timeline && (
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>⏱ {listing.timeline}</div>
        )}

        {/* Syndicated lending progress bar */}
        {isSyndicate && totalNeeded > 0 && (
          <div style={{ marginBottom: 12, background: "#EFF6FF", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: synFullyFunded ? "#059669" : "#1D4ED8", fontWeight: 700 }}>
                {synFullyFunded ? "Fully Funded!" : `${synPct}% funded`}
              </span>
              <span style={{ fontSize: 12, color: "#64748B" }}>
                {fmt(syndicateTotal)} of {fmt(totalNeeded)}
              </span>
            </div>
            <div style={{ height: 7, background: "#BFDBFE", borderRadius: 4, overflow: "hidden", marginBottom: 6 }}>
              <div style={{
                height: "100%", borderRadius: 4,
                background: synFullyFunded ? "#059669" : "#2563EB",
                width: `${synPct}%`, transition: "width 0.4s ease",
              }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#64748B" }}>
                {syndicateCount} lender{syndicateCount !== 1 ? "s" : ""} committed
              </span>
              {minCommitment > 0 && (
                <span style={{ fontSize: 11, color: "#1D4ED8", fontWeight: 500 }}>
                  Min: {fmt(minCommitment)}
                </span>
              )}
            </div>
            {/* Return terms */}
            {(() => {
              const rt = listing.syndicate_return_type;
              if (!rt) return null;
              let termLine = null;
              if (rt === "fixed_interest" && listing.syndicate_interest_rate) {
                termLine = `${listing.syndicate_interest_rate}% p.a. fixed interest${listing.syndicate_loan_term_months ? ` · ${listing.syndicate_loan_term_months}mo term` : ""}`;
              } else if (rt === "rental_split" && listing.syndicate_rental_share_pct) {
                termLine = `${listing.syndicate_rental_share_pct}% rental split${listing.syndicate_monthly_rental ? ` · est. ${fmt(listing.syndicate_monthly_rental)}/mo` : ""}`;
              } else if (rt === "equity_stake" && listing.syndicate_equity_pct) {
                termLine = `${listing.syndicate_equity_pct}% equity stake`;
              }
              if (!termLine) return null;
              return (
                <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, background: "#DBEAFE", color: "#1D4ED8", padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>
                    {rt === "fixed_interest" ? "Fixed interest" : rt === "rental_split" ? "Rental split" : "Equity stake"}
                  </span>
                  <span style={{ fontSize: 11, color: "#64748B" }}>{termLine}</span>
                </div>
              );
            })()}
            {listing.syndicate_max_lenders && (
              <div style={{ marginTop: 4, fontSize: 11, color: "#64748B" }}>
                Max {listing.syndicate_max_lenders} lender{listing.syndicate_max_lenders !== 1 ? "s" : ""}
                {syndicateCount > 0 ? ` · ${syndicateCount} committed` : ""}
              </div>
            )}
            {myCommitmentAmount > 0 && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: "0.5px solid #BFDBFE" }}>
                <div style={{ fontSize: 12, color: "#1D4ED8", fontWeight: 600 }}>
                  Your commitment: {fmt(myCommitmentAmount)} · {mySharePct}% share
                </div>
              </div>
            )}
          </div>
        )}

        {/* Group funding progress bar */}
        {isGroup && !isSyndicate && totalNeeded > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: "#7C3AED", fontWeight: 600 }}>
                {fullyFunded ? "Fully funded!" : `${pct}% committed`}
              </span>
              <span style={{ fontSize: 12, color: "#64748B" }}>
                {fmt(totalCommitted)} / {fmt(totalNeeded)}
              </span>
            </div>
            <div style={{ height: 6, background: "#EDE9FE", borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 3,
                background: fullyFunded ? "#059669" : "#7C3AED",
                width: `${pct}%`, transition: "width 0.4s ease",
              }} />
            </div>
            {listingCommitments.length > 0 && (
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                {listingCommitments.length} lender{listingCommitments.length !== 1 ? "s" : ""} committed
                {myCommitment && <span style={{ color: "#7C3AED", fontWeight: 600 }}> · You: {fmt(myCommitment.amount)}</span>}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        {isOwn ? (
          <div style={{ fontSize: 12, color: "#3B82F6", fontWeight: 500, paddingTop: 4 }}>Your listing · active</div>
        ) : isSyndicate && role === "lender" ? (
          <>
            {!synFullyFunded && !synCommitOpen && (
              <button
                onClick={() => { setSynCommitOpen(true); setSynCommitInput(myCommitmentAmount > 0 ? String(myCommitmentAmount) : ""); }}
                style={{
                  width: "100%",
                  background: myCommitmentAmount > 0 ? "#EFF6FF" : "#1D4ED8",
                  color: myCommitmentAmount > 0 ? "#1D4ED8" : "#fff",
                  border: myCommitmentAmount > 0 ? "0.5px solid #BFDBFE" : "none",
                  padding: "8px 0", minHeight: 44, borderRadius: 8,
                  fontSize: 13, fontWeight: 500, marginTop: "auto", cursor: "pointer",
                  transition: "background 0.2s",
                }}
              >
                {myCommitmentAmount > 0 ? `Update commitment · ${fmt(myCommitmentAmount)}` : "Commit funds"}
              </button>
            )}
            {synFullyFunded && (
              <div style={{ fontSize: 12, color: "#059669", fontWeight: 700, padding: "8px 0", textAlign: "center", background: "#ECFDF5", borderRadius: 8 }}>
                Fully Funded ✓
              </div>
            )}
            {synCommitOpen && (
              <form onSubmit={handleSynCommitSubmit} style={{ marginTop: "auto" }}>
                {minCommitment > 0 && (
                  <div style={{ fontSize: 11, color: "#64748B", marginBottom: 6 }}>
                    Minimum commitment: {fmt(minCommitment)}
                  </div>
                )}
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ flex: 1, position: "relative" }}>
                    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#555" }}>£</span>
                    <input
                      type="text" inputMode="numeric"
                      value={fmtInput(synCommitInput)}
                      onChange={e => { setSynCommitInput(parseInput(e.target.value)); setSynError(""); }}
                      placeholder={minCommitment > 0 ? fmtInput(String(minCommitment)) : "Amount"}
                      style={{ width: "100%", height: 40, border: "0.5px solid #BFDBFE", borderRadius: 8, padding: "0 10px 0 22px", fontSize: 14, boxSizing: "border-box" }}
                      autoFocus
                    />
                  </div>
                  <button type="submit" disabled={synCommitting}
                    style={{ padding: "0 14px", height: 40, background: "#1D4ED8", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: synCommitting ? "default" : "pointer", flexShrink: 0 }}>
                    {synCommitting ? "…" : "Confirm"}
                  </button>
                  <button type="button" onClick={() => { setSynCommitOpen(false); setSynCommitInput(""); setSynError(""); }}
                    style={{ padding: "0 10px", height: 40, background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 13, color: "#555", cursor: "pointer", flexShrink: 0 }}>
                    ✕
                  </button>
                </div>
                {synError && <div style={{ fontSize: 11, color: "#993C1D", marginTop: 4 }}>{synError}</div>}
              </form>
            )}
          </>
        ) : isSyndicate && !user ? (
          <button
            style={{ width: "100%", background: "#1D4ED8", color: "#fff", border: "none", padding: "8px 0", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, marginTop: "auto", cursor: "pointer" }}
            onClick={() => window.location.hash = "auth"}
          >
            Log in to commit funds
          </button>
        ) : isGroup && !isSyndicate && isOwn && myRoom ? (
          <button
            onClick={() => onEnterRoom && onEnterRoom(myRoom.id)}
            style={{ width: "100%", background: "#7C3AED", color: "#fff", border: "none", padding: "8px 0", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, marginTop: "auto", cursor: "pointer" }}
          >
            View Funding Room →
          </button>
        ) : isGroup && !isSyndicate && role === "lender" ? (
          <>
            {myRoom ? (
              <button
                onClick={() => onEnterRoom && onEnterRoom(myRoom.id)}
                style={{ width: "100%", background: "#7C3AED", color: "#fff", border: "none", padding: "8px 0", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, marginTop: "auto", cursor: "pointer" }}
              >
                {fullyFunded ? "View Room — Fully Funded ✓" : `Enter Room · ${fmt(myRoom.my_amount || 0)} committed`}
              </button>
            ) : (
              <>
                {!fullyFunded && !commitOpen && (
                  <button
                    onClick={() => setCommitOpen(true)}
                    style={{ width: "100%", background: "#7C3AED", color: "#fff", border: "none", padding: "8px 0", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, marginTop: "auto", cursor: "pointer", transition: "background 0.2s" }}
                  >
                    Commit funding — Join Room
                  </button>
                )}
                {fullyFunded && (
                  <div style={{ fontSize: 12, color: "#059669", fontWeight: 600, padding: "8px 0", textAlign: "center" }}>
                    Fully funded ✓
                  </div>
                )}
                {commitOpen && (
                  <form onSubmit={handleCommitSubmit} style={{ display: "flex", gap: 6, marginTop: "auto" }}>
                    <div style={{ flex: 1, position: "relative" }}>
                      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#555" }}>£</span>
                      <input
                        type="text" inputMode="numeric"
                        value={fmtInput(commitInput)}
                        onChange={e => setCommitInput(parseInput(e.target.value))}
                        placeholder="Amount"
                        style={{ width: "100%", height: 40, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 10px 0 22px", fontSize: 14, boxSizing: "border-box" }}
                        autoFocus
                      />
                    </div>
                    <button type="submit" disabled={committing}
                      style={{ padding: "0 14px", height: 40, background: "#7C3AED", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: committing ? "default" : "pointer", flexShrink: 0 }}>
                      {committing ? "…" : "Confirm"}
                    </button>
                    <button type="button" onClick={() => { setCommitOpen(false); setCommitInput(""); }}
                      style={{ padding: "0 10px", height: 40, background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 13, color: "#555", cursor: "pointer", flexShrink: 0 }}>
                      ✕
                    </button>
                  </form>
                )}
              </>
            )}
          </>
        ) : !isSyndicate && !isGroup && role === "lender" ? (
          <button
            onClick={() => onExpressInterest(listing)}
            disabled={isDone || st === "loading"}
            style={{
              width: "100%", background: btnBg, color: "#fff", border: "none",
              padding: "8px 0", minHeight: 44, borderRadius: 8, fontSize: 13, fontWeight: 500, marginTop: "auto",
              cursor: isDone || st === "loading" ? "default" : "pointer",
              transition: "background 0.2s",
            }}
          >
            {btnLabel}
          </button>
        ) : !user ? (
          <div style={{ fontSize: 12, color: "#64748B", marginTop: "auto" }}>Log in to express interest</div>
        ) : null}
      </div>
    </div>
  );
}

// ─── CREATE PROJECT LISTING PAGE ──────────────────────────────────────────────

function CreateProjectListingPage({ user, setPage }) {
  const [title,          setTitle]          = useState("");
  const [location,       setLocation]       = useState("");
  const [projectType,    setProjectType]    = useState("");
  const [fundingNeeded,  setFundingNeeded]  = useState("");
  const [expectedReturn, setExpectedReturn] = useState("");
  const [timeline,       setTimeline]       = useState("");
  const [description,    setDescription]    = useState("");
  const [photos,         setPhotos]         = useState([]);
  const [groupFunding,   setGroupFunding]   = useState(false);
  const [syndicateOpen,       setSyndicateOpen]       = useState(false);
  const [minCommitment,       setMinCommitment]       = useState("");
  const [maxLenders,          setMaxLenders]          = useState("");
  const [synReturnType,       setSynReturnType]       = useState("fixed_interest");
  const [synInterestRate,     setSynInterestRate]     = useState("");
  const [synLoanTermMonths,   setSynLoanTermMonths]   = useState("");
  const [synRentalSharePct,   setSynRentalSharePct]   = useState("");
  const [synMonthlyRental,    setSynMonthlyRental]    = useState("");
  const [synEquityPct,        setSynEquityPct]        = useState("");
  const [uploading,           setUploading]           = useState(false);
  const [saving,              setSaving]              = useState(false);
  const [error,               setError]               = useState("");
  const [aiPreview,           setAiPreview]           = useState(null);

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 5 - photos.length;
    if (remaining <= 0) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files.slice(0, remaining)) {
      if (file.size > 5 * 1024 * 1024) continue;
      const ext  = file.name.split(".").pop().toLowerCase();
      const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from("project-photos").upload(path, file);
      if (!uploadErr) {
        const { data: { publicUrl } } = supabase.storage.from("project-photos").getPublicUrl(path);
        newUrls.push(publicUrl);
      }
    }
    setPhotos(prev => [...prev, ...newUrls].slice(0, 5));
    setUploading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) { setError("Title is required"); return; }
    setSaving(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({
        action:          "create",
        title:           title.trim(),
        location:        location.trim(),
        project_type:    projectType,
        funding_needed:  fundingNeeded ? Number(fundingNeeded) : null,
        expected_return: expectedReturn.trim(),
        timeline:        timeline.trim(),
        description:     description.trim(),
        photos,
        group_funding:                groupFunding,
        syndicate_open:               syndicateOpen,
        min_commitment:               syndicateOpen && minCommitment ? Number(minCommitment) : null,
        syndicate_max_lenders:        syndicateOpen && maxLenders ? Number(maxLenders) : null,
        syndicate_return_type:        syndicateOpen ? synReturnType : null,
        syndicate_interest_rate:      syndicateOpen && synReturnType === "fixed_interest" && synInterestRate ? Number(synInterestRate) : null,
        syndicate_loan_term_months:   syndicateOpen && synReturnType === "fixed_interest" && synLoanTermMonths ? Number(synLoanTermMonths) : null,
        syndicate_rental_share_pct:   syndicateOpen && synReturnType === "rental_split" && synRentalSharePct ? Number(synRentalSharePct) : null,
        syndicate_monthly_rental:     syndicateOpen && synReturnType === "rental_split" && synMonthlyRental ? Number(synMonthlyRental) : null,
        syndicate_equity_pct:         syndicateOpen && synReturnType === "equity_stake" && synEquityPct ? Number(synEquityPct) : null,
      }),
    });
    setSaving(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || "We couldn't save that — please try again.");
      return;
    }
    // Show AI analysis preview before navigating
    const listingForAI = { title, location, project_type: projectType, funding_needed: fundingNeeded ? Number(fundingNeeded) : null, description, photos, timeline };
    setAiPreview(computeAIRiskAnalysis(listingForAI));
  }

  const inp = {
    width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8,
    padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box",
  };
  const lbl = { fontSize: 13, color: "#555", marginBottom: 5, display: "block", fontWeight: 500 };
  const PROJECT_TYPES = ["Residential", "Commercial", "Mixed use", "Renovation", "Industrial", "Other"];

  if (aiPreview) {
    return (
      <div style={{ padding: "2rem 1.25rem", maxWidth: 560, margin: "0 auto" }}>
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 16, padding: "1.75rem", textAlign: "center" }}>
          <div style={{ marginBottom: 12, color: "#16A34A" }}><Icon name="check-circle" size={36} /></div>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1E3A5F", marginBottom: 6 }}>Project listed!</h2>
          <p style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}>Here's your AI risk preview:</p>
          <div style={{ background: aiPreview.riskBg, border: `1px solid ${aiPreview.riskColor}30`, borderRadius: 12, padding: "1rem", marginBottom: 16, textAlign: "left" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: aiPreview.riskColor }}>{aiPreview.riskLabel}</span>
              <span style={{ fontSize: 12, color: aiPreview.riskColor }}>Score: {aiPreview.score}/100</span>
            </div>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.6, margin: "0 0 8px" }}>{aiPreview.summary}</p>
            <div style={{ fontSize: 12, color: "#555", fontStyle: "italic" }}>{aiPreview.structure}</div>
          </div>
          <button onClick={() => setPage("browse-projects")} style={{ padding: "11px 28px", background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            View your listing →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem 1.25rem", maxWidth: 560, margin: "0 auto" }}>
      <button
        onClick={() => setPage("browse-projects")}
        style={{ background: "none", border: "none", color: "#555", fontSize: 14, cursor: "pointer", padding: 0, marginBottom: "1.5rem" }}
      >
        ← Browse projects
      </button>
      <h1 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 6px", fontFamily: "'Georgia', serif" }}>Post a project</h1>
      <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 1.75rem" }}>List your project to attract lenders</p>

      {error && (
        <div style={{ background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Project details</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={lbl}>Project title *</label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. 3-bed new build, Manchester" required style={inp} />
            </div>
            <div>
              <label style={lbl}>Location</label>
              <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Manchester, UK" style={inp} />
            </div>
            <div>
              <label style={lbl}>Project type</label>
              <select value={projectType} onChange={e => setProjectType(e.target.value)} style={inp}>
                <option value="">Select type…</option>
                {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Funding needed (£)</label>
              <input type="text" inputMode="numeric" value={fmtInput(fundingNeeded)} onChange={e => setFundingNeeded(parseInput(e.target.value))} placeholder="e.g. 150,000" style={inp} />
            </div>
            <div>
              <label style={lbl}>Expected return for lender</label>
              <input value={expectedReturn} onChange={e => setExpectedReturn(e.target.value)} placeholder="e.g. 8% p.a., 60/40 split" style={inp} />
            </div>
            <div>
              <label style={lbl}>Timeline</label>
              <input value={timeline} onChange={e => setTimeline(e.target.value)} placeholder="e.g. 12 months" style={inp} />
            </div>
            <div>
              <label style={lbl}>Description</label>
              <textarea
                value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Describe the project, planning status, expected profits…"
                style={{ ...inp, height: 100, padding: "10px 12px", resize: "vertical", lineHeight: 1.5 }}
              />
            </div>

            {/* Group funding toggle */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", paddingTop: 4 }}>
              <input
                type="checkbox"
                checked={groupFunding}
                onChange={e => setGroupFunding(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>Open to group funding</div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Multiple lenders can each commit a portion of the total amount. Each lender only funds their committed portion via milestones.</div>
              </div>
            </label>

            {/* Syndicated lending toggle */}
            <div style={{ borderTop: "0.5px solid #f0f0f0", paddingTop: 14 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={syndicateOpen}
                  onChange={e => { setSyndicateOpen(e.target.checked); if (!e.target.checked) { setMinCommitment(""); setMaxLenders(""); } }}
                  style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>Open to syndicated lending</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Lenders commit specific amounts tracked on a live progress bar. You're notified each time someone commits and again when fully funded.</div>
                </div>
              </label>
              {syndicateOpen && (
                <div style={{ marginTop: 14, paddingLeft: 26, display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={lbl}>Return type for lenders</label>
                    <select value={synReturnType} onChange={e => setSynReturnType(e.target.value)} style={inp}>
                      <option value="fixed_interest">Fixed interest (monthly repayments)</option>
                      <option value="rental_split">Rental split</option>
                      <option value="equity_stake">Equity stake</option>
                    </select>
                  </div>
                  {synReturnType === "fixed_interest" && (
                    <div style={{ display: "flex", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={lbl}>Interest rate (% p.a.)</label>
                        <input type="number" min="0" max="100" step="0.1" value={synInterestRate} onChange={e => setSynInterestRate(e.target.value)} placeholder="e.g. 8" style={inp} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={lbl}>Loan term (months)</label>
                        <input type="number" min="1" max="360" step="1" value={synLoanTermMonths} onChange={e => setSynLoanTermMonths(e.target.value)} placeholder="e.g. 18" style={inp} />
                      </div>
                    </div>
                  )}
                  {synReturnType === "rental_split" && (
                    <div style={{ display: "flex", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={lbl}>Lender's share (%)</label>
                        <input type="number" min="0" max="100" step="1" value={synRentalSharePct} onChange={e => setSynRentalSharePct(e.target.value)} placeholder="e.g. 40" style={inp} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={lbl}>Est. monthly rental (£)</label>
                        <input type="text" inputMode="numeric" value={fmtInput(synMonthlyRental)} onChange={e => setSynMonthlyRental(parseInput(e.target.value))} placeholder="e.g. 2,500" style={inp} />
                      </div>
                    </div>
                  )}
                  {synReturnType === "equity_stake" && (
                    <div>
                      <label style={lbl}>Equity percentage offered (%)</label>
                      <input type="number" min="0" max="100" step="0.5" value={synEquityPct} onChange={e => setSynEquityPct(e.target.value)} placeholder="e.g. 25" style={{ ...inp, maxWidth: 220 }} />
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={lbl}>Min. contribution per lender (£) <span style={{ color: "#aaa", fontWeight: 400 }}>optional</span></label>
                      <input type="text" inputMode="numeric" value={fmtInput(minCommitment)} onChange={e => setMinCommitment(parseInput(e.target.value))} placeholder="e.g. 10,000" style={inp} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={lbl}>Max. lenders <span style={{ color: "#aaa", fontWeight: 400 }}>optional</span></label>
                      <input type="number" min="1" step="1" value={maxLenders} onChange={e => setMaxLenders(e.target.value)} placeholder="e.g. 5" style={inp} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>
            Photos (up to 5)
          </div>
          {photos.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              {photos.map((url, i) => (
                <div key={i} style={{ position: "relative", width: 80, height: 80 }}>
                  <img src={url} alt="" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, border: "0.5px solid #e0e0e0" }} />
                  <button
                    type="button"
                    onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
                    style={{
                      position: "absolute", top: -6, right: -6,
                      width: 20, height: 20, borderRadius: "50%",
                      background: "#D85A30", color: "#fff", border: "none",
                      cursor: "pointer", fontSize: 13, lineHeight: 1,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
          {photos.length < 5 && (
            <label style={{
              display: "inline-flex", alignItems: "center", padding: "0 16px",
              height: 44, minHeight: 44,
              background: uploading ? "#f0f0f0" : "#fff",
              border: "0.5px solid #ccc", borderRadius: 8,
              fontSize: 13, cursor: uploading ? "default" : "pointer", color: "#555",
            }}>
              {uploading ? "Uploading…" : "Add photos"}
              <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhotoUpload} disabled={uploading} />
            </label>
          )}
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>Max 5 photos · 5 MB each · Requires a "project-photos" public bucket in Supabase Storage</div>
        </div>

        <button
          type="submit"
          disabled={saving || uploading}
          style={{
            background: saving || uploading ? "#aaa" : "#3B82F6", color: "#fff",
            border: "none", padding: "12px", borderRadius: 8,
            fontSize: 15, fontWeight: 500,
            cursor: saving || uploading ? "default" : "pointer",
          }}
        >
          {saving ? "Posting…" : "Post project"}
        </button>
      </form>
    </div>
  );
}

// ─── ABOUT PAGE ──────────────────────────────────────────────────────────────

const EMAILJS_SERVICE_ID  = "service_y64xe7w";
const EMAILJS_TEMPLATE_ID = "template_oh952z8";
const EMAILJS_PUBLIC_KEY  = "foqJQiW92yDGHnI7i";

function ContactForm({ submitBtnColor = "#3B82F6" }) {
  const [status, setStatus]   = useState("idle"); // idle | sending | success | error
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [subject, setSubject] = useState("General enquiry");
  const [message, setMessage] = useState("");
  const inputStyle = { width: "100%", height: 44, border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "0 12px", fontSize: 14, outline: "none", boxSizing: "border-box" };

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("sending");
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        { from_name: name, from_email: email, subject, message },
        EMAILJS_PUBLIC_KEY
      );
      setStatus("success");
      setName(""); setEmail(""); setSubject("General enquiry"); setMessage("");
    } catch {
      setStatus("error");
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {status === "success" && (
        <div style={{ background: "#DCFCE7", border: "0.5px solid #86EFAC", borderRadius: 10, padding: "14px 16px", fontSize: 14, color: "#166534", fontWeight: 500 }}>
          Thank you! We will get back to you within 24 hours.
        </div>
      )}
      {status === "error" && (
        <div style={{ background: "#FEE2E2", border: "0.5px solid #FCA5A5", borderRadius: 10, padding: "14px 16px", fontSize: 14, color: "#991B1B", fontWeight: 500 }}>
          Something went wrong. Please try again.
        </div>
      )}
      <input type="text" required placeholder="Your name" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
      <input type="email" required placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
      <select required value={subject} onChange={e => setSubject(e.target.value)}
        style={{ ...inputStyle, background: "#fff", color: "#374151", appearance: "auto" }}>
        {["General enquiry", "Partnership", "Technical issue", "Press", "Other"].map(s => <option key={s}>{s}</option>)}
      </select>
      <textarea required placeholder="Your message…" rows={4} value={message} onChange={e => setMessage(e.target.value)}
        style={{ width: "100%", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, resize: "vertical", outline: "none", lineHeight: 1.5, boxSizing: "border-box" }} />
      <button type="submit" disabled={status === "sending"}
        style={{ padding: "11px", background: submitBtnColor, color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: status === "sending" ? "not-allowed" : "pointer", opacity: status === "sending" ? 0.8 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        {status === "sending" ? (
          <>
            <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
            Sending…
          </>
        ) : "Submit"}
      </button>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </form>
  );
}

function AboutPage({ setPage }) {
  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 760, margin: "0 auto" }}>
      <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 700, color: "#1E3A5F", margin: "0 0 0.5rem" }}>About LenderBuild</h1>
      <p style={{ fontSize: 16, color: "#64748B", margin: "0 0 2.5rem", lineHeight: 1.6 }}>Making UK property investment accessible to builders and lenders alike.</p>

      {/* Founder section */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 14, padding: "2rem", marginBottom: "1.5rem", display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ width: 90, height: 90, borderRadius: "50%", background: "linear-gradient(135deg, #1E3A5F 0%, #3B82F6 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 36, fontWeight: 700, color: "#fff" }}>L</span>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#1E3A5F", marginBottom: 4 }}>Louis Graham</div>
          <div style={{ fontSize: 14, color: "#3B82F6", fontWeight: 500, marginBottom: 12 }}>Founder & CEO, LenderBuild</div>
          <p style={{ fontSize: 14, color: "#555", lineHeight: 1.7, margin: 0 }}>
            I built LenderBuild after watching talented builders struggle to find the right funding, while motivated lenders had no reliable way to vet and connect with credible projects. The UK property market is full of opportunity — but the matchmaking problem was getting in the way. LenderBuild fixes that.
          </p>
        </div>
      </div>

      {/* Mission */}
      <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", borderRadius: 14, padding: "2rem", marginBottom: "1.5rem" }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.75)", marginBottom: 10 }}>Our mission</div>
        <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, margin: "0 0 14px", color: "#ffffff" }}>Making property investment accessible</h2>
        <p style={{ fontSize: 14, lineHeight: 1.75, color: "#ffffff", margin: 0 }}>
          We believe every builder with a solid plan deserves access to capital, and every lender deserves a transparent, trustworthy platform to put their money to work. LenderBuild uses smart matching, verified profiles, and milestone-based payments to create fair, protected deals for both sides.
        </p>
      </div>

      {/* Team */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 14, padding: "1.75rem", marginBottom: "1.5rem" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>The team</div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 50, height: 50, borderRadius: "50%", background: "#EBF2FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: "#1E3A5F" }}>L</span>
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F" }}>Louis Graham</div>
            <div style={{ fontSize: 13, color: "#64748B" }}>Founder — product, engineering & growth</div>
          </div>
        </div>
        <p style={{ fontSize: 13, color: "#64748B", margin: "16px 0 0", lineHeight: 1.6 }}>We're a small team with big ambitions. If you share our vision for transforming UK property finance, <button onClick={() => document.getElementById("contact-form").scrollIntoView({ behavior: "smooth" })} style={{ background: "none", border: "none", color: "#3B82F6", cursor: "pointer", fontSize: 13, padding: 0, fontWeight: 500 }}>get in touch</button>.</p>
      </div>

      {/* Contact */}
      <div id="contact-form" style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 14, padding: "1.75rem" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Contact us</div>
        <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 16px" }}>Have a question or want to get involved? Fill in the form and we'll get back to you.</p>
        <ContactForm submitBtnColor="#3B82F6" />
      </div>
    </div>
  );
}

// ─── FUNDING ROOM PAGE ────────────────────────────────────────────────────────

function FundingRoomPage({ user, setPage, roomId }) {
  const [room,          setRoom]          = useState(null);
  const [members,       setMembers]       = useState([]);
  const [messages,      setMessages]      = useState([]);
  const [listing,       setListing]       = useState(null);
  const [builderName,   setBuilderName]   = useState("");
  const [builderAvatar, setBuilderAvatar] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [chatInput,     setChatInput]     = useState("");
  const [sending,       setSending]       = useState(false);
  const [leaving,       setLeaving]       = useState(false);
  const [leaveConfirm,  setLeaveConfirm]  = useState(false);
  // Terms
  const [termsReturn,   setTermsReturn]   = useState("");
  const [termsValue,    setTermsValue]    = useState("");
  const [settingTerms,  setSettingTerms]  = useState(false);
  const [agreeingTerms, setAgreeingTerms] = useState(false);
  const [termsError,    setTermsError]    = useState("");
  // Finder fee
  const [feeLoading,    setFeeLoading]    = useState(false);
  // Remove member
  const [removingMember, setRemovingMember] = useState(null);
  const [removeError,    setRemoveError]    = useState("");
  // Schedule meeting
  const [showMeetingForm,  setShowMeetingForm]  = useState(false);
  const [meetingAt,        setMeetingAt]        = useState("");
  const [meetingNotes,     setMeetingNotes]     = useState("");
  const [schedulingMeeting,setSchedulingMeeting]= useState(false);
  const [meetingError,     setMeetingError]     = useState("");
  const chatEndRef    = useRef(null);
  const realtimeChanRef = useRef(null);

  const isMobile  = window.innerWidth < 768;
  const role     = user?.user_metadata?.role;
  const isBuilder = room?.builder_id === user?.id;
  const myMembership = members.find(m => m.lender_id === user?.id);

  async function loadRoom() {
    setLoading(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setError("Please log in"); setLoading(false); return; }
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-get", room_id: roomId }),
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.error || "Failed to load room"); setLoading(false); return; }
    const d = await res.json();
    setRoom(d.room);
    setMembers(d.members || []);
    setMessages(d.messages || []);
    setListing(d.listing || null);
    setBuilderName(d.builder_name || "Builder");
    setBuilderAvatar(d.builder_avatar_url || null);
    setLoading(false);
  }

  useEffect(() => {
    loadRoom();
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime: messages + members + room status
  useEffect(() => {
    if (!roomId) return;
    if (realtimeChanRef.current) supabase.removeChannel(realtimeChanRef.current);
    realtimeChanRef.current = supabase.channel(`fr-${roomId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "funding_room_messages", filter: `room_id=eq.${roomId}` },
        payload => setMessages(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]))
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "funding_room_members", filter: `room_id=eq.${roomId}` },
        payload => setMembers(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]))
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "funding_room_members", filter: `room_id=eq.${roomId}` },
        payload => setMembers(prev => prev.filter(m => m.id !== payload.old.id)))
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "funding_room_members", filter: `room_id=eq.${roomId}` },
        payload => setMembers(prev => prev.map(m => m.id === payload.new.id ? payload.new : m)))
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "funding_rooms", filter: `id=eq.${roomId}` },
        payload => setRoom(payload.new))
      .subscribe();
    return () => { if (realtimeChanRef.current) supabase.removeChannel(realtimeChanRef.current); };
  }, [roomId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSendMessage(e) {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text) return;
    setSending(true);
    const name = user?.user_metadata?.name || user?.email?.split("@")[0] || "Member";
    await supabase.from("funding_room_messages").insert({
      room_id: roomId, sender_id: user.id, sender_name: name, message: text, is_system: false,
    });
    setChatInput("");
    setSending(false);
  }

  async function handleLeaveRoom() {
    setLeaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-leave", room_id: roomId }),
    });
    setLeaving(false);
    if (res.ok) setPage("browse-projects");
  }

  async function handleSetTerms(e) {
    e.preventDefault();
    if (!termsReturn) { setTermsError("Please select a return type"); return; }
    setSettingTerms(true);
    setTermsError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-set-terms", room_id: roomId, return_type: termsReturn, return_value: termsValue }),
    });
    const json = await res.json();
    setSettingTerms(false);
    if (!res.ok) { setTermsError(json.error || "Failed to set terms"); return; }
    setRoom(prev => ({ ...prev, return_type: termsReturn, return_value: termsValue, terms_set_at: new Date().toISOString() }));
  }

  async function handleAgreeTerms() {
    setAgreeingTerms(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-agree-terms", room_id: roomId }),
    });
    setAgreeingTerms(false);
    if (res.ok) {
      setMembers(prev => prev.map(m => m.lender_id === user.id ? { ...m, status: "terms_agreed", terms_agreed_at: new Date().toISOString() } : m));
    }
  }

  async function handlePayFinderFee() {
    setFeeLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-finder-fee", room_id: roomId }),
    });
    const json = await res.json();
    setFeeLoading(false);
    if (res.ok && json.checkout_url) {
      window.location.href = json.checkout_url;
    }
  }

  async function handleRemoveMember(lenderId) {
    setRemovingMember(lenderId);
    setRemoveError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-remove-member", room_id: roomId, lender_id: lenderId }),
    });
    const json = await res.json().catch(() => ({}));
    setRemovingMember(null);
    if (!res.ok) setRemoveError(json.error || "Could not remove member");
  }

  async function handleScheduleMeeting(e) {
    e.preventDefault();
    if (!meetingAt) { setMeetingError("Please select a date and time"); return; }
    setSchedulingMeeting(true);
    setMeetingError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/project-listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "funding-room-schedule-meeting", room_id: roomId, meeting_at: meetingAt, meeting_notes: meetingNotes }),
    });
    const json = await res.json().catch(() => ({}));
    setSchedulingMeeting(false);
    if (!res.ok) { setMeetingError(json.error || "Failed to schedule meeting"); return; }
    setShowMeetingForm(false);
    setRoom(prev => ({ ...prev, meeting_at: meetingAt, meeting_notes: meetingNotes }));
  }

  const targetAmount    = room?.target_amount || 0;
  const committedAmount = room?.committed_amount || 0;
  const pct             = targetAmount > 0 ? Math.min(100, Math.round((committedAmount / targetAmount) * 100)) : 0;
  const fullyFunded     = room?.status === "fully_funded";

  if (loading) return (
    <div style={{ padding: "3rem", textAlign: "center", color: "#64748B" }}>
      <div style={{ fontSize: 14 }}>Loading funding room…</div>
    </div>
  );

  if (error) return (
    <div style={{ padding: "2rem 1.25rem" }}>
      <button onClick={() => setPage("browse-projects")} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 5 }}>
        ← Back to projects
      </button>
      <div style={{ color: "#DC2626", fontSize: 14 }}>{error}</div>
    </div>
  );

  return (
    <div style={{ padding: "1.25rem", maxWidth: 720, margin: "0 auto" }}>
      {/* Back + header */}
      <button onClick={() => setPage("browse-projects")} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 5, padding: 0 }}>
        ← Back to projects
      </button>

      <div style={{ background: "var(--bg-card)", border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)", borderRadius: 12, padding: "1.25rem", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#F3F0FF", color: "#7C3AED" }}>Group Funding Room</span>
              {fullyFunded && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#DCFCE7", color: "#166534" }}>Fully Funded ✓</span>}
            </div>
            <h2 style={{ fontSize: 19, fontWeight: 600, margin: "0 0 4px", lineHeight: 1.3 }}>{listing?.title || "Group Funding Project"}</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Avatar initials={nameInitials(builderName)} color={pickColor(room?.builder_id || "")} size={22} url={builderAvatar} />
              <span style={{ fontSize: 13, color: "#64748B" }}>{builderName}</span>
              {listing?.location && <span style={{ fontSize: 12, color: "#94A3B8" }}>· {listing.location}</span>}
            </div>
          </div>
          {myMembership && (
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 11, color: "#64748B", marginBottom: 2 }}>Your commitment</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--accent)" }}>{fmt(myMembership.amount)}</div>
            </div>
          )}
        </div>

        {/* Fully funded banner */}
        {fullyFunded && (
          <div style={{ background: "#DCFCE7", border: "1px solid #86EFAC", borderRadius: 8, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#166534" }}>Fully funded — deal confirmation in progress</span>
          </div>
        )}

        {/* Progress bar */}
        {targetAmount > 0 && (
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: fullyFunded ? "#166534" : "var(--accent)" }}>
                {fullyFunded ? "Target reached!" : `${pct}% committed`}
              </span>
              <span style={{ fontSize: 13, color: "#64748B" }}>
                {fmt(committedAmount)} of {fmt(targetAmount)}
              </span>
            </div>
            <div style={{ height: 10, background: "var(--bg-page, #f1f5f9)", borderRadius: 5, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 5,
                background: fullyFunded ? "#16A34A" : "var(--accent)",
                width: `${pct}%`, transition: "width 0.5s ease",
              }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 11, color: "#94A3B8" }}>
              <span>{members.length} lender{members.length !== 1 ? "s" : ""} committed</span>
              <span>{fmt(Math.max(0, targetAmount - committedAmount))} remaining</span>
            </div>
          </div>
        )}
      </div>

      {/* Members list */}
      <div style={{ background: "var(--bg-card)", border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)", borderRadius: 12, padding: "1rem 1.25rem", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: "var(--text-primary, #1E3A5F)" }}>
          Committed lenders · {members.length}
        </div>
        {members.length === 0 ? (
          <div style={{ fontSize: 13, color: "#94A3B8", padding: "8px 0" }}>No lenders have committed yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {members.map((m) => {
              const sharePct = targetAmount > 0 ? ((Number(m.amount) / targetAmount) * 100).toFixed(1) : null;
              const isMe = m.lender_id === user?.id;
              const canRemove = isBuilder && m.status !== "fee_paid";
              return (
                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: isMe ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent" }}>
                  <Avatar initials={nameInitials(m.lender_name)} color={pickColor(m.lender_id)} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{m.lender_name}{isMe ? " (you)" : ""}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>
                      Joined {new Date(m.joined_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      {m.status === "fee_paid" && <span style={{ color: "#16A34A", marginLeft: 6, fontWeight: 600 }}>· Fee paid</span>}
                      {m.status === "terms_agreed" && <span style={{ color: "#7E22CE", marginLeft: 6 }}>· Terms agreed</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--accent)" }}>{fmt(m.amount)}</div>
                      {sharePct && <div style={{ fontSize: 11, color: "#94A3B8" }}>{sharePct}% share</div>}
                    </div>
                    {canRemove && (
                      <button
                        onClick={() => handleRemoveMember(m.lender_id)}
                        disabled={removingMember === m.lender_id}
                        title="Remove from room"
                        style={{ padding: "4px 10px", fontSize: 11, color: "#DC2626", background: "transparent", border: "1px solid #FCA5A5", borderRadius: 6, cursor: removingMember === m.lender_id ? "default" : "pointer", whiteSpace: "nowrap", opacity: removingMember === m.lender_id ? 0.6 : 1 }}
                      >
                        {removingMember === m.lender_id ? "Removing…" : "Remove"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {removeError && <div style={{ color: "#DC2626", fontSize: 12, marginTop: 8 }}>{removeError}</div>}
      </div>

      {/* Commitment protection notice (lenders only) */}
      {!isBuilder && myMembership && myMembership.status !== "fee_paid" && (
        <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 12, padding: "12px 16px", marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C2410C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <div style={{ fontSize: 12, color: "#9A3412", lineHeight: 1.6 }}>
            <strong>Your commitment is not binding.</strong> You are under no obligation until you have agreed the investment terms and paid the finder's fee. You can withdraw at any time before that point using the "Leave Room" button below.
          </div>
        </div>
      )}

      {/* Scheduled meeting banner */}
      {room?.meeting_at && (
        <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1D4ED8" }}>Meeting Scheduled</span>
          </div>
          <div style={{ fontSize: 13, color: "#1E40AF" }}>
            {new Date(room.meeting_at).toLocaleString("en-GB", { dateStyle: "full", timeStyle: "short" })}
          </div>
          {room.meeting_notes && <div style={{ fontSize: 12, color: "#1E40AF", marginTop: 4 }}>{room.meeting_notes}</div>}
          {!isBuilder && <div style={{ fontSize: 11, color: "#3B82F6", marginTop: 6 }}>Reply in the room chat below to confirm your attendance.</div>}
          {isBuilder && (
            <button onClick={() => { setShowMeetingForm(true); setMeetingAt(room.meeting_at?.slice(0, 16) || ""); setMeetingNotes(room.meeting_notes || ""); }}
              style={{ marginTop: 8, fontSize: 11, color: "#1D4ED8", background: "transparent", border: "1px solid #BFDBFE", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
              Update meeting
            </button>
          )}
        </div>
      )}

      {/* Schedule meeting (builder, no meeting yet or update form open) */}
      {isBuilder && ((!room?.meeting_at && !showMeetingForm) || showMeetingForm) && (
        <div style={{ background: "var(--bg-card)", border: "1px solid #BFDBFE", borderRadius: 12, padding: "1.25rem", marginBottom: 16 }}>
          {!showMeetingForm ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1D4ED8" }}>Schedule a Meeting</div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Propose a time for all lenders to discuss before committing funds.</div>
              </div>
              <button onClick={() => setShowMeetingForm(true)} style={{ padding: "8px 16px", background: "#1D4ED8", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                Schedule Meeting
              </button>
            </div>
          ) : (
            <form onSubmit={handleScheduleMeeting} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1D4ED8", marginBottom: 2 }}>Propose a meeting time</div>
              <div>
                <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>Date &amp; time</label>
                <input type="datetime-local" value={meetingAt} onChange={e => setMeetingAt(e.target.value)} required
                  style={{ width: "100%", height: 40, border: "1px solid #BFDBFE", borderRadius: 8, padding: "0 10px", fontSize: 13, boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>Agenda / notes (optional)</label>
                <textarea value={meetingNotes} onChange={e => setMeetingNotes(e.target.value)} maxLength={400} rows={2}
                  placeholder="e.g. We'll discuss project timeline and return structure"
                  style={{ width: "100%", border: "1px solid #BFDBFE", borderRadius: 8, padding: "8px 10px", fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
              </div>
              {meetingError && <div style={{ color: "#DC2626", fontSize: 12 }}>{meetingError}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" disabled={schedulingMeeting} style={{ flex: 1, height: 40, background: "#1D4ED8", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: schedulingMeeting ? "default" : "pointer" }}>
                  {schedulingMeeting ? "Scheduling…" : "Notify All Lenders"}
                </button>
                <button type="button" onClick={() => { setShowMeetingForm(false); setMeetingError(""); }} style={{ height: 40, padding: "0 16px", background: "transparent", border: "1px solid #e0e0e0", borderRadius: 8, fontSize: 13, color: "#64748B", cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Chat */}
      <div style={{ background: "var(--bg-card)", border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ padding: "10px 16px", borderBottom: "1px solid color-mix(in srgb, var(--accent) 10%, transparent)", fontSize: 13, fontWeight: 600, color: "var(--text-primary, #1E3A5F)" }}>
          Group Chat · {listing?.title ? `"${listing.title}"` : "Funding Room"}
        </div>
        <div style={{ height: 320, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", color: "#94A3B8", fontSize: 13, marginTop: 40 }}>No messages yet. Start the conversation!</div>
          )}
          {messages.map((msg) => {
            const isMe = msg.sender_id === user?.id;
            if (msg.is_system) {
              return (
                <div key={msg.id} style={{ textAlign: "center", fontSize: 11, color: "#94A3B8", padding: "2px 0" }}>
                  {msg.message}
                </div>
              );
            }
            return (
              <div key={msg.id} style={{ display: "flex", flexDirection: isMe ? "row-reverse" : "row", gap: 8, alignItems: "flex-end" }}>
                {!isMe && <Avatar initials={nameInitials(msg.sender_name)} color={pickColor(msg.sender_id)} size={26} />}
                <div style={{ maxWidth: "75%" }}>
                  {!isMe && <div style={{ fontSize: 10, color: "#94A3B8", marginBottom: 2, paddingLeft: 4 }}>{msg.sender_name}</div>}
                  <div style={{
                    background: isMe ? "var(--accent)" : "var(--bg-page, #f1f5f9)",
                    color: isMe ? "#fff" : "var(--text-primary, #1E3A5F)",
                    borderRadius: isMe ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                    padding: "8px 12px", fontSize: 13, lineHeight: 1.5,
                    wordBreak: "break-word",
                  }}>
                    {msg.message}
                  </div>
                  <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2, textAlign: isMe ? "right" : "left", paddingRight: isMe ? 4 : 0, paddingLeft: isMe ? 0 : 4 }}>
                    {new Date(msg.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>
        <form onSubmit={handleSendMessage} style={{ padding: "10px 12px", borderTop: "1px solid color-mix(in srgb, var(--accent) 10%, transparent)", display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            placeholder="Type a message…"
            disabled={sending} rows={1}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !isMobile) { e.preventDefault(); handleSendMessage(e); } }}
            onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
            style={{ flex: 1, minHeight: 40, maxHeight: 120, border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)", borderRadius: 8, padding: "8px 12px", fontSize: 13, background: "var(--bg-page, #f8fafc)", color: "var(--text-primary, #1E3A5F)", outline: "none", resize: "none", overflowY: "auto", fontFamily: "inherit", lineHeight: "1.4", boxSizing: "border-box" }}
          />
          <button type="submit" disabled={sending || !chatInput.trim()} style={{ height: 40, padding: "0 16px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: sending || !chatInput.trim() ? "default" : "pointer", opacity: !chatInput.trim() ? 0.5 : 1 }}>
            Send
          </button>
        </form>
      </div>

      {/* Terms section */}
      {isBuilder && !room?.terms_set_at && (
        <div style={{ background: "var(--bg-card)", border: "1px solid #D8B4FE", borderRadius: 12, padding: "1.25rem", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#7E22CE", marginBottom: 4 }}>Set Investment Terms</div>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>Set the return terms for this funding room. Lenders must agree before paying their finder's fee.</div>
          <form onSubmit={handleSetTerms} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>Return type</label>
                <select value={termsReturn} onChange={e => setTermsReturn(e.target.value)} required
                  style={{ width: "100%", height: 40, border: "1px solid #D8B4FE", borderRadius: 8, padding: "0 10px", fontSize: 13 }}>
                  <option value="">Select…</option>
                  <option value="fixed_interest">Fixed interest</option>
                  <option value="revenue_share">Revenue share</option>
                  <option value="equity">Equity stake</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>
                  {termsReturn === "fixed_interest" ? "Interest rate (%)" : termsReturn === "revenue_share" ? "Revenue share (%)" : "Equity (%)"}
                </label>
                <input
                  type="number" min="0" max="100" step="0.1"
                  value={termsValue} onChange={e => setTermsValue(e.target.value)}
                  placeholder="e.g. 8"
                  style={{ width: "100%", height: 40, border: "1px solid #D8B4FE", borderRadius: 8, padding: "0 10px", fontSize: 13, boxSizing: "border-box" }}
                />
              </div>
            </div>
            {termsError && <div style={{ color: "#DC2626", fontSize: 12 }}>{termsError}</div>}
            <button type="submit" disabled={settingTerms} style={{ height: 40, background: "#7E22CE", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: settingTerms ? "default" : "pointer" }}>
              {settingTerms ? "Saving…" : "Set Terms for All Lenders"}
            </button>
          </form>
        </div>
      )}

      {/* Terms display + agree (lender) */}
      {!isBuilder && myMembership && room?.terms_set_at && (
        <div style={{ background: "var(--bg-card)", border: "1px solid #D8B4FE", borderRadius: 12, padding: "1.25rem", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#7E22CE", marginBottom: 8 }}>Investment Terms</div>
          <div style={{ background: "#FDF4FF", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F" }}>
              {room.return_type === "fixed_interest" ? "Fixed Interest" : room.return_type === "revenue_share" ? "Revenue Share" : "Equity Stake"}
              {room.return_value ? `: ${room.return_value}%` : ""}
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
              Your commitment: {fmt(myMembership.amount)} ·
              Finder's fee (1%): {fmt(Number(myMembership.amount) * 0.01)}
            </div>
          </div>
          {(!myMembership.status || myMembership.status === "committed") && (
            <button
              onClick={handleAgreeTerms} disabled={agreeingTerms}
              style={{ width: "100%", height: 42, background: "#7E22CE", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: agreeingTerms ? "default" : "pointer" }}
            >
              {agreeingTerms ? "Confirming…" : "I agree to these terms"}
            </button>
          )}
          {(myMembership.status === "terms_agreed") && (
            <div>
              <div style={{ fontSize: 12, color: "#16A34A", fontWeight: 600, marginBottom: 8 }}>Terms agreed ✓</div>
              <button
                onClick={handlePayFinderFee} disabled={feeLoading}
                style={{ width: "100%", height: 42, background: "#16A34A", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: feeLoading ? "default" : "pointer" }}
              >
                {feeLoading ? "Redirecting…" : `Pay finder's fee — ${fmt(Number(myMembership.amount) * 0.01)}`}
              </button>
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6, textAlign: "center" }}>1% of your commitment · Secure payment via Stripe</div>
            </div>
          )}
          {myMembership.status === "fee_paid" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#DCFCE7", borderRadius: 8, padding: "10px 14px" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>Finder's fee paid — you're fully confirmed in this deal</span>
            </div>
          )}
        </div>
      )}

      {/* Terms set confirmation (builder) */}
      {isBuilder && room?.terms_set_at && (
        <div style={{ background: "var(--bg-card)", border: "1px solid #D8B4FE", borderRadius: 12, padding: "1.25rem", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#7E22CE", marginBottom: 4 }}>Investment Terms Set</div>
          <div style={{ fontSize: 13, color: "#1E3A5F" }}>
            {room.return_type === "fixed_interest" ? "Fixed Interest" : room.return_type === "revenue_share" ? "Revenue Share" : "Equity Stake"}
            {room.return_value ? `: ${room.return_value}%` : ""}
          </div>
          <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
            {members.filter(m => m.status === "terms_agreed" || m.status === "fee_paid").length} of {members.length} lender{members.length !== 1 ? "s" : ""} agreed · {members.filter(m => m.status === "fee_paid").length} paid finder's fee
          </div>
        </div>
      )}

      {/* Leave room (lenders only, only if not fully funded) */}
      {!isBuilder && myMembership && !fullyFunded && (
        <div style={{ background: "var(--bg-card)", border: "1px solid #FCA5A5", borderRadius: 12, padding: "1rem 1.25rem" }}>
          {!leaveConfirm ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#DC2626" }}>Withdraw commitment</div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Remove your commitment of {fmt(myMembership.amount)} from this room.</div>
              </div>
              <button onClick={() => setLeaveConfirm(true)} style={{ padding: "8px 16px", background: "transparent", color: "#DC2626", border: "1.5px solid #FCA5A5", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", minHeight: 40 }}>
                Leave Room
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#DC2626", marginBottom: 6 }}>Are you sure you want to leave?</div>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>Your commitment of {fmt(myMembership.amount)} will be withdrawn. This cannot be undone.</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleLeaveRoom} disabled={leaving} style={{ padding: "8px 16px", background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: leaving ? "default" : "pointer", minHeight: 40 }}>
                  {leaving ? "Leaving…" : "Confirm — Leave Room"}
                </button>
                <button onClick={() => setLeaveConfirm(false)} style={{ padding: "8px 16px", background: "transparent", color: "#64748B", border: "1px solid #e0e0e0", borderRadius: 8, fontSize: 13, cursor: "pointer", minHeight: 40 }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── DEAL ROOM PAGE ───────────────────────────────────────────────────────────

function DealRoomPage({ user, setPage, deal }) {
  const [activeTab, setActiveTab] = useState("summary");
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [callModal, setCallModal] = useState(false);
  const [callTime, setCallTime] = useState("");
  const [callSent, setCallSent] = useState(false);

  const totalAmount = deal.agreed_amount || 0;
  const role = user?.user_metadata?.role;
  const docs = deal.deal_documents || [];

  useEffect(() => {
    if (activeTab === "notes") loadNotes();
  }, [activeTab]); // eslint-disable-line

  async function loadNotes() {
    const { data } = await supabase.from("deal_notes").select("*").eq("deal_id", deal.id).order("created_at", { ascending: true });
    setNotes(data || []);
  }

  async function handleAddNote(e) {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSavingNote(true);
    const { data: { user: authUser } } = await supabase.auth.getUser();
    const authorName = authUser?.user_metadata?.name || authUser?.email || "Unknown";
    const { error } = await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      user_id: user.id,
      author_name: authorName,
      content: noteText.trim(),
    });
    setSavingNote(false);
    if (!error) {
      setNoteText("");
      loadNotes();
    }
  }

  async function handleSendCallProposal(e) {
    e.preventDefault();
    const { data: { session } } = await supabase.auth.getSession();
    await fetch("/api/notify-message", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({
        action: "contact-form",
        name: user?.user_metadata?.name || "User",
        email: user?.email,
        message: `Meeting proposal for deal "${deal.title}": ${callTime}`,
      }),
    });
    setCallSent(true);
    setCallModal(false);
  }

  // Build timeline events
  const timelineEvents = [
    { date: deal.created_at, label: "Deal created", color: "#3B82F6" },
    ...docs.filter(d => d.uploaded_at).map(d => ({ date: d.uploaded_at, label: `Document uploaded: ${d.doc_type?.replace(/_/g, " ")}`, color: "#D97706" })),
    ...(deal.deal_updates || []).map(u => ({ date: u.created_at, label: "Progress update posted", color: "#8B5CF6" })),
    ...(deal.tracker_completed_at ? [{ date: deal.tracker_completed_at, label: "Project marked complete", color: "#16A34A" }] : []),
  ].sort((a, b) => new Date(a.date) - new Date(b.date));

  const tabs = ["Summary", "Documents", "Timeline", "Notes", "Progress Log", "Messages"];


  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "1.5rem 1.25rem" }}>
      <button onClick={() => setPage("deal-detail", deal)} style={{ background: "none", border: "none", color: "#3B82F6", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: "1rem" }}>
        ← Back to deal
      </button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 4px", fontFamily: "'Georgia', serif" }}>Deal Room</h1>
          <div style={{ fontSize: 13, color: "#64748B" }}>{deal.title}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: deal.status === "active" ? "#DCFCE7" : "#EBF2FF", color: deal.status === "active" ? "#16A34A" : "#1E3A5F", textTransform: "capitalize" }}>
          {deal.status}
        </span>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "0.5px solid #e0e0e0", marginBottom: "1.5rem", overflowX: "auto" }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t.toLowerCase())}
            style={{ padding: "8px 14px", border: "none", background: "none", fontSize: 13, fontWeight: activeTab === t.toLowerCase() ? 600 : 400, color: activeTab === t.toLowerCase() ? "#3B82F6" : "#64748B", borderBottom: activeTab === t.toLowerCase() ? "2px solid #3B82F6" : "2px solid transparent", cursor: "pointer", whiteSpace: "nowrap" }}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Summary ── */}
      {activeTab === "summary" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Parties</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 140 }}><div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>Lender</div><div style={{ fontSize: 14, fontWeight: 500 }}>{deal.lender_name}</div></div>
              <div style={{ flex: 1, minWidth: 140 }}><div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>Builder</div><div style={{ fontSize: 14, fontWeight: 500 }}>{deal.builder_name}</div></div>
            </div>
          </div>
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Deal overview</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ background: "#f5f5f3", borderRadius: 10, padding: "0.75rem 1rem", flex: 1, minWidth: 100 }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>Deal value</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: "#3B82F6" }}>{fmt(totalAmount)}</div>
              </div>
              {deal.property_value && (
                <div style={{ background: "#f5f5f3", borderRadius: 10, padding: "0.75rem 1rem", flex: 1, minWidth: 100 }}>
                  <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>LTV</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{Math.round((totalAmount / deal.property_value) * 100)}%</div>
                </div>
              )}
              <div style={{ background: "#f5f5f3", borderRadius: 10, padding: "0.75rem 1rem", flex: 1, minWidth: 100 }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>Updates posted</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{(deal.deal_updates || []).length}</div>
              </div>
            </div>
          </div>
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Schedule a call</div>
            {callSent
              ? <div style={{ fontSize: 13, color: "#16A34A" }}>Proposal sent ✓</div>
              : <button onClick={() => setCallModal(true)} style={{ padding: "8px 18px", background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Schedule a call</button>
            }
          </div>
        </div>
      )}

      {/* ── Documents ── */}
      {activeTab === "documents" && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Deal documents</div>
          {docs.length === 0
            ? <div style={{ fontSize: 13, color: "#aaa" }}>No documents uploaded yet.</div>
            : docs.map((doc, i) => {
                const { data: { publicUrl } } = supabase.storage.from("builder-documents").getPublicUrl(doc.file_path || "");
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "0.5px solid #f0f0f0" }}>
                    <Icon name="file" size={16} className="lb-icon" />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{doc.doc_type?.replace(/_/g, " ")}</div>
                      <span style={{ fontSize: 11, padding: "1px 7px", borderRadius: 20, background: doc.status === "approved" ? "#DCFCE7" : "#F1F5F9", color: doc.status === "approved" ? "#16A34A" : "#64748B" }}>{doc.status}</span>
                    </div>
                    {doc.file_path && <a href={publicUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#3B82F6", textDecoration: "none" }}>Download</a>}
                  </div>
                );
              })
          }
        </div>
      )}

      {/* ── Timeline ── */}
      {activeTab === "timeline" && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>Deal timeline</div>
          {timelineEvents.length === 0
            ? <div style={{ fontSize: 13, color: "#aaa" }}>No events yet.</div>
            : timelineEvents.map((ev, i) => (
                <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: ev.color, flexShrink: 0, marginTop: 3 }} />
                    {i < timelineEvents.length - 1 && <div style={{ width: 1.5, flex: 1, background: "#e0e0e0", margin: "3px 0" }} />}
                  </div>
                  <div style={{ paddingBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{ev.label}</div>
                    <div style={{ fontSize: 11, color: "#aaa" }}>{new Date(ev.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                  </div>
                </div>
              ))
          }
        </div>
      )}

      {/* ── Notes ── */}
      {activeTab === "notes" && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Shared notes</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {notes.length === 0 && <div style={{ fontSize: 13, color: "#aaa" }}>No notes yet. Add the first one below.</div>}
            {notes.map(n => (
              <div key={n.id} style={{ background: "#f9f9f7", borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{n.author_name}</span>
                  <span style={{ fontSize: 11, color: "#aaa" }}>{new Date(n.created_at).toLocaleDateString("en-GB")}</span>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>{n.content}</div>
              </div>
            ))}
          </div>
          <form onSubmit={handleAddNote} style={{ display: "flex", gap: 8 }}>
            <textarea
              value={noteText} onChange={e => setNoteText(e.target.value)}
              placeholder="Add a note visible to both parties…"
              rows={3}
              style={{ flex: 1, border: "0.5px solid #ccc", borderRadius: 8, padding: "8px 10px", fontSize: 13, resize: "vertical", outline: "none" }}
            />
            <button type="submit" disabled={savingNote || !noteText.trim()} style={{ padding: "0 14px", alignSelf: "flex-end", height: 40, background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Add
            </button>
          </form>
        </div>
      )}

      {/* ── Progress Log ── */}
      {activeTab === "progress log" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {deal.tracker_completed_at && (
            <div style={{ background: "#E1F5EE", border: "0.5px solid #A8DFC9", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#16A34A"/><path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F6E56" }}>Project complete — both parties have confirmed.</div>
            </div>
          )}
          {(deal.deal_updates || []).length === 0 && (
            <div style={{ background: "#f9f9f9", border: "0.5px solid #e0e0e0", borderRadius: 10, padding: "2rem", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
              No progress updates yet. The builder will post updates here as work progresses.
            </div>
          )}
          {(deal.deal_updates || []).map(u => (
            <div key={u.id} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1rem 1.25rem" }}>
              <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 6 }}>{new Date(u.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
              <div style={{ fontSize: 14, color: "#1E293B", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{u.body}</div>
              {u.photo_urls?.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  {u.photo_urls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt="Progress" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, border: "0.5px solid #e0e0e0" }} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Messages ── */}
      {activeTab === "messages" && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem", textAlign: "center" }}>
          <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="message" size={32} /></div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>Direct messages</div>
          <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>Continue the conversation in the full messages thread.</div>
          <button onClick={() => setPage("messages")} style={{ padding: "10px 24px", background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Go to Messages
          </button>
        </div>
      )}

      {/* Schedule a call modal */}
      {callModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "1.5rem", maxWidth: 400, width: "100%" }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Propose a meeting time</div>
            <form onSubmit={handleSendCallProposal}>
              <input
                value={callTime} onChange={e => setCallTime(e.target.value)}
                placeholder="e.g. Tuesday 3pm or 15 May at 14:00"
                style={{ width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 14, boxSizing: "border-box", marginBottom: 12 }}
                required
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => setCallModal(false)} style={{ flex: 1, height: 44, background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Cancel</button>
                <button type="submit" style={{ flex: 2, height: 44, background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Send proposal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BUILD COST CALCULATOR ────────────────────────────────────────────────────

const BUILD_COST_DATA = {
  types: {
    "New build residential": { low: 1500, high: 2500, label: "New build residential" },
    "Renovation":            { low: 800,  high: 1500, label: "Renovation" },
    "Extension":             { low: 1200, high: 2000, label: "Extension" },
    "Commercial":            { low: 2000, high: 3500, label: "Commercial" },
  },
  regionMultipliers: {
    "London":           1.4,
    "South East":       1.2,
    "South West":       1.1,
    "East of England":  1.1,
    "East Midlands":    1.0,
    "West Midlands":    1.0,
    "Yorkshire":        0.9,
    "North West":       0.9,
    "North East":       0.85,
    "Scotland":         0.9,
    "Wales":            0.88,
    "Northern Ireland": 0.85,
    "Other":            1.0,
  },
  // 2024 average new-build property price per sq m by region
  propertyPricePerSqm: {
    "London":           10500,
    "South East":       5200,
    "South West":       4200,
    "East of England":  4400,
    "East Midlands":    2900,
    "West Midlands":    3000,
    "Yorkshire":        2600,
    "North West":       2700,
    "North East":       2100,
    "Scotland":         2700,
    "Wales":            2400,
    "Northern Ireland": 2100,
    "Other":            3000,
  },
  milestones: [
    { stage: "Site preparation & foundations",  pct: 15 },
    { stage: "Structure & external walls",      pct: 25 },
    { stage: "Roof & weatherproofing",          pct: 15 },
    { stage: "Internal fit-out & mechanical",   pct: 25 },
    { stage: "Finishing & decoration",          pct: 15 },
    { stage: "Final completion & snagging",     pct: 5  },
  ],
};

function buildLtvRateSuggestion(ltv) {
  if (ltv < 50)  return { rate: "5–6% p.a.", risk: "Very low", color: "#16A34A" };
  if (ltv < 60)  return { rate: "6–7% p.a.", risk: "Low",      color: "#16A34A" };
  if (ltv < 70)  return { rate: "7–8.5% p.a.", risk: "Moderate", color: "#D97706" };
  if (ltv < 75)  return { rate: "8.5–10% p.a.", risk: "Medium-high", color: "#D97706" };
  if (ltv < 80)  return { rate: "10–12% p.a.", risk: "High",   color: "#DC2626" };
  return { rate: "Not recommended", risk: "Very high (>80% LTV)", color: "#DC2626" };
}

function BuildCostCalculatorPage({ setPage }) {
  const [projectType, setProjectType] = useState("New build residential");
  const [region, setRegion] = useState("South East");
  const [size, setSize] = useState("");
  const [sizeTick, setSizeTick] = useState(0);
  const [sqmError, setSqmError] = useState("");

  function handleSizeChange(e) {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (!raw) { setSize(""); setSqmError(""); return; }
    const num = parseInt(raw, 10);
    if (num > 100000) {
      // sizeTick always increments so React re-renders even when size is already
      // "100000" (same state = bail-out = controlled value not applied to DOM).
      setSizeTick(t => t + 1);
      setSize("100000");
      setSqmError("Maximum floor area is 100,000 m²");
    } else {
      setSize(raw);
      setSqmError(num < 5 ? "Minimum floor area is 5 m²" : "");
    }
  }

  const rawSqm = parseFloat(size) || 0;
  const sqm = rawSqm > 0 ? Math.max(5, Math.min(100000, rawSqm)) : 0;
  const typeData = BUILD_COST_DATA.types[projectType];
  const mult = BUILD_COST_DATA.regionMultipliers[region] || 1.0;
  const pricePerSqm = BUILD_COST_DATA.propertyPricePerSqm[region] || 3000;

  const costLow  = Math.round(typeData.low  * mult * sqm);
  const costHigh = Math.round(typeData.high * mult * sqm);
  const midCost  = Math.round((costLow + costHigh) / 2);
  const fundingRec = Math.round(midCost * 0.8);

  // Property value on completion estimate
  let gdvLow, gdvHigh;
  if (projectType === "New build residential") {
    gdvLow  = Math.round(sqm * pricePerSqm * 0.9);
    gdvHigh = Math.round(sqm * pricePerSqm * 1.1);
  } else if (projectType === "Renovation") {
    gdvLow  = Math.round(midCost * 1.5);
    gdvHigh = Math.round(midCost * 1.9);
  } else if (projectType === "Extension") {
    gdvLow  = Math.round(midCost * 1.8);
    gdvHigh = Math.round(midCost * 2.4);
  } else {
    // Commercial: simplified yield-based approach (~8% cap rate)
    const annualRent = sqm * 150 * mult; // rough £/sqm/year
    gdvLow  = Math.round(annualRent / 0.09);
    gdvHigh = Math.round(annualRent / 0.07);
  }

  const midGdv = Math.round((gdvLow + gdvHigh) / 2);
  const ltv = midGdv > 0 ? Math.round((fundingRec / midGdv) * 100) : 0;
  const ltvSug = buildLtvRateSuggestion(ltv);
  const hasResult = sqm > 0;

  const inputStyle = {
    width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8,
    padding: "0 12px", fontSize: 14, boxSizing: "border-box", background: "#fff",
  };
  const labelStyle = { fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6, display: "block" };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "0" }}>
      {/* Hero */}
      <div style={{ background: "var(--nav-bg,#1E3A5F)", padding: "2.5rem 1.5rem", textAlign: "center", color: "#fff" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--accent,#3B82F6)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>Tools</div>
        <h1 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, color: "#fff", margin: "0 0 10px" }}>Build Cost Calculator</h1>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", maxWidth: 440, margin: "0 auto" }}>
          UK construction cost estimates based on 2024/25 data with regional multipliers. Includes funding, GDV, and LTV analysis.
        </p>
      </div>

      <div style={{ padding: "2rem 1.25rem" }}>
        {/* Inputs */}
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", marginBottom: "1.25rem" }}>Project details</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>Project type</label>
              <select value={projectType} onChange={e => setProjectType(e.target.value)} style={inputStyle}>
                {Object.keys(BUILD_COST_DATA.types).map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Region</label>
              <select value={region} onChange={e => setRegion(e.target.value)} style={inputStyle}>
                {Object.keys(BUILD_COST_DATA.regionMultipliers).map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Floor area (sq metres)</label>
              <input
                type="text" inputMode="numeric"
                min={5} max={100000}
                value={fmtInput(size)} onChange={handleSizeChange}
                placeholder="e.g. 120"
                style={{ ...inputStyle, borderColor: sqmError ? "#EF4444" : undefined }}
              />
              {sqmError && (
                <div style={{ fontSize: 11, color: "#EF4444", marginTop: 4 }}>{sqmError}</div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: "1rem" }}>
            Region multiplier: <strong>{(BUILD_COST_DATA.regionMultipliers[region] || 1.0).toFixed(2)}×</strong> &nbsp;·&nbsp;
            Base cost range: <strong>£{BUILD_COST_DATA.types[projectType].low.toLocaleString()}–£{BUILD_COST_DATA.types[projectType].high.toLocaleString()}/m²</strong>
          </div>
        </div>

        {hasResult ? (
          <>
            {/* Cost estimate */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: "1.5rem" }}>
              {[
                { label: "Estimated build cost (low)",  value: fmt(costLow),  sub: `£${Math.round(typeData.low * mult).toLocaleString()}/m²` },
                { label: "Estimated build cost (high)", value: fmt(costHigh), sub: `£${Math.round(typeData.high * mult).toLocaleString()}/m²` },
                { label: "Recommended funding (80%)",   value: fmt(fundingRec), sub: "of midpoint estimate", accent: true },
                { label: "Est. value on completion",    value: `${fmt(gdvLow)}–${fmt(gdvHigh)}`, sub: "GDV estimate" },
              ].map(s => (
                <div key={s.label} style={{ background: s.accent ? "var(--accent-bg,#EBF2FF)" : "#fff", border: `0.5px solid ${s.accent ? "var(--accent,#3B82F6)" : "#e0e0e0"}`, borderRadius: 12, padding: "1.25rem" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.accent ? "var(--accent,#3B82F6)" : "#1E3A5F" }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>{s.sub}</div>
                </div>
              ))}
            </div>

            {/* LTV + rate */}
            <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem", marginBottom: "1.5rem" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", marginBottom: "1rem" }}>Loan to Value &amp; Suggested Lender Return</div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div style={{ textAlign: "center", minWidth: 100 }}>
                  <div style={{ fontSize: 36, fontWeight: 700, color: ltvSug.color }}>{ltv}%</div>
                  <div style={{ fontSize: 12, color: "#64748B" }}>LTV ratio</div>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: ltvSug.color }}>{ltvSug.rate}</span>
                    <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: ltvSug.color + "22", color: ltvSug.color }}>{ltvSug.risk}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.5 }}>
                    Suggested lender return based on {ltv}% LTV. Lower LTV = lower risk = lower required rate.
                    {ltv >= 80 && " LTV above 80% is considered high risk by most UK bridging lenders."}
                  </div>
                  <div style={{ marginTop: 10, height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: 6, background: ltvSug.color, borderRadius: 3, width: `${Math.min(ltv, 100)}%`, transition: "width 0.4s" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94A3B8", marginTop: 3 }}>
                    <span>0%</span><span>50%</span><span>75%</span><span>100%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Milestone breakdown */}
            <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", marginBottom: "1rem" }}>Suggested Milestone Breakdown</div>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: "1rem" }}>6-stage release based on {fmt(fundingRec)} recommended funding</div>
              {BUILD_COST_DATA.milestones.map((m, i) => {
                const amount = Math.round(fundingRec * m.pct / 100);
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < BUILD_COST_DATA.milestones.length - 1 ? "0.5px solid #f0f0f0" : "none" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--accent-bg,#EBF2FF)", color: "var(--accent,#3B82F6)", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "#1E3A5F" }}>{m.stage}</div>
                      <div style={{ height: 4, background: "#f0f0f0", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
                        <div style={{ height: 4, background: "var(--accent,#3B82F6)", borderRadius: 2, width: `${m.pct}%` }} />
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F" }}>{fmt(amount)}</div>
                      <div style={{ fontSize: 11, color: "#94A3B8" }}>{m.pct}%</div>
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "0.5px solid #f0f0f0", fontSize: 11, color: "#94A3B8", lineHeight: 1.5 }}>
                * Cost estimates are for guidance only based on 2024/25 BCIS data and regional market data. Actual costs vary by specification, contractor, and site conditions. Always obtain professional quantity surveyor quotes before proceeding.
              </div>
            </div>
          </>
        ) : (
          <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "3rem", textAlign: "center", color: "#94A3B8" }}>
            <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="hard-hat" size={36} /></div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6, color: "#374151" }}>Enter your project details above</div>
            <div style={{ fontSize: 13 }}>Fill in the project type, region, and floor area to see cost estimates, funding recommendations, and milestone breakdown.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MARKET INTELLIGENCE PAGE ─────────────────────────────────────────────────

function MarketPage({ setPage }) {
  const [monthlyData,   setMonthlyData]   = useState([]);
  const [projectTypeData, setProjectTypeData] = useState([]);
  const [regionData,    setRegionData]    = useState([]);
  const [ukHPI,         setUkHPI]         = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [lastUpdated,   setLastUpdated]   = useState(null);
  const refreshTimer = useRef(null);

  const RENTAL_YIELDS = [
    { region: "Liverpool",   yield: 6.8 },
    { region: "Manchester",  yield: 6.2 },
    { region: "Nottingham",  yield: 6.1 },
    { region: "Sheffield",   yield: 5.9 },
    { region: "Birmingham",  yield: 5.7 },
    { region: "Leeds",       yield: 5.4 },
    { region: "Bristol",     yield: 4.9 },
    { region: "London",      yield: 3.8 },
    { region: "UK Average",  yield: 5.1 },
  ];

  const BRIDGING_RATES = [
    { type: "First charge (residential)", rate: "0.45–0.75% /mo" },
    { type: "Second charge",              rate: "0.75–1.20% /mo" },
    { type: "Commercial",                 rate: "0.85–1.50% /mo" },
    { type: "Land / development",         rate: "0.75–1.25% /mo" },
  ];

  const HOUSING_STARTS = [
    { year: "2022", starts: 196200 },
    { year: "2023", starts: 183330 },
    { year: "2024", starts: 162870 },
  ];

  const PIE_COLORS = ["#3B82F6", "#16A34A", "#7C3AED", "#EA580C", "#DB2777"];

  function toRegion(loc) {
    const l = (loc || "").toLowerCase();
    if (!l) return null;
    if (l.includes("london"))                                             return "London";
    if (l.includes("manchester") || l.includes("salford") || l.includes("stockport") || l.includes("liverpool") || l.includes("blackpool") || l.includes("preston")) return "North West";
    if (l.includes("birmingham") || l.includes("coventry") || l.includes("wolverhampton") || l.includes("leicester") || l.includes("nottingham") || l.includes("derby")) return "Midlands";
    if (l.includes("leeds") || l.includes("sheffield") || l.includes("bradford") || l.includes("york") || l.includes("hull"))  return "Yorkshire";
    if (l.includes("bristol") || l.includes("bath") || l.includes("exeter") || l.includes("plymouth") || l.includes("bournemouth")) return "South West";
    if (l.includes("brighton") || l.includes("southampton") || l.includes("portsmouth") || l.includes("guildford") || l.includes("oxford") || l.includes("reading")) return "South East";
    if (l.includes("cambridge") || l.includes("norwich") || l.includes("ipswich") || l.includes("colchester")) return "East";
    if (l.includes("newcastle") || l.includes("sunderland") || l.includes("durham") || l.includes("middlesbrough")) return "North East";
    if (l.includes("edinburgh") || l.includes("glasgow") || l.includes("scotland") || l.includes("aberdeen")) return "Scotland";
    if (l.includes("cardiff") || l.includes("wales") || l.includes("swansea"))  return "Wales";
    return "Other UK";
  }

  async function loadAll() {
    setLoading(true);
    try {
      const [
        { data: deals },
        { data: profiles },
        listRes,
        hpiRes,
      ] = await Promise.all([
        supabase.from("deals").select("created_at, total_amount").order("created_at"),
        supabase.from("profiles").select("user_role, location"),
        fetch("/api/project-listings").then(r => r.json()).catch(() => ({})),
        fetch("https://landregistry.data.gov.uk/data/hpi/regionOrCountry/england/period/2024-12.json")
          .then(r => r.ok ? r.json() : null).catch(() => null),
      ]);

      // ── Monthly deal volume (last 6 months) ──
      if (deals) {
        const now = new Date();
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push({ name: d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }), count: 0, capital: 0 });
        }
        deals.forEach(d => {
          const date = new Date(d.created_at);
          months.forEach(m => {
            const [mon, yr] = m.name.split(" ");
            const md = new Date(`${mon} 20${yr}`);
            if (date.getMonth() === md.getMonth() && date.getFullYear() === md.getFullYear()) {
              m.count++;
              m.capital += Number(d.total_amount) || 0;
            }
          });
        });
        setMonthlyData(months);
      }

      // ── Project types ──
      const listings = listRes.listings || [];
      const typeMap = {};
      listings.forEach(l => { const t = l.project_type || "Other"; typeMap[t] = (typeMap[t] || 0) + 1; });
      setProjectTypeData(
        Object.entries(typeMap).sort((a, b) => b[1] - a[1]).slice(0, 5)
          .map(([name, value]) => ({ name, value }))
      );

      // ── Geographic spread ──
      if (profiles) {
        const regionMap = {};
        profiles.forEach(p => {
          const r = toRegion(p.location);
          if (r) regionMap[r] = (regionMap[r] || 0) + 1;
        });
        setRegionData(
          Object.entries(regionMap).sort((a, b) => b[1] - a[1]).slice(0, 7)
            .map(([name, value]) => ({ name, value }))
        );
      }

      // ── UK HPI ──
      if (hpiRes) {
        try {
          const pt = hpiRes.result?.primaryTopic;
          if (pt?.averagePrice) setUkHPI({ avgPrice: pt.averagePrice, annualChange: pt.percentageChange, period: "December 2024" });
        } catch (_) {}
      }

      setLastUpdated(new Date());
    } catch (_) {}
    setLoading(false);
  }

  useEffect(() => {
    loadAll();
    refreshTimer.current = setInterval(loadAll, 5 * 60 * 1000);
    return () => clearInterval(refreshTimer.current);
  }, []); // eslint-disable-line

  const fmtM = v => {
    if (!v && v !== 0) return "—";
    if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `£${Math.round(v / 1_000)}k`;
    return `£${Math.round(v)}`;
  };

  const MktSectionLabel = ({ children }) => (
    <div style={{ fontSize: 10, fontWeight: 700, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, marginTop: 4 }}>{children}</div>
  );

  const EmptyChartMsg = ({ msg }) => (
    <div style={{ height: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#CBD5E1" }}>
      <div style={{ marginBottom: 8, color: "var(--accent)" }}><Icon name="bar-chart" size={28} /></div>
      <div style={{ fontSize: 12 }}>{msg}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 0 }}>
      {/* Hero */}
      <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", padding: "3rem 1.5rem", textAlign: "center", color: "#fff" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#93C5FD", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>Data-driven insights</div>
        <h1 style={{ fontSize: "clamp(26px, 5vw, 42px)", fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, color: "#fff", margin: "0 0 10px" }}>Market Intelligence</h1>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", maxWidth: 500, margin: "0 auto 1.5rem" }}>Live platform stats and UK property market data — updated every 5 minutes.</p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
          {lastUpdated && (
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
              Last updated {lastUpdated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button onClick={loadAll} disabled={loading}
            style={{ padding: "8px 20px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8, color: "#fff", fontSize: 13, cursor: loading ? "default" : "pointer", opacity: loading ? 0.6 : 1 }}>
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      <div style={{ padding: "2rem 1.25rem" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "4rem", color: "#aaa" }}>Loading market data…</div>
        ) : (
          <>
            {/* ── Three charts ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: "2rem" }}>

              {/* Line chart — monthly volume */}
              <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 2 }}>Monthly deal volume</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 14 }}>Last 6 months · platform data</div>
                {monthlyData.every(m => m.count === 0) ? <EmptyChartMsg msg="No deal data yet" /> : (
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={monthlyData} margin={{ left: -10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip formatter={v => [v, "Deals"]} />
                      <Line type="monotone" dataKey="count" name="Deals" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4, fill: "#3B82F6" }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Pie chart — project types */}
              <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 2 }}>Project types</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 14 }}>Active listings · platform data</div>
                {projectTypeData.length === 0 ? <EmptyChartMsg msg="No listing data yet" /> : (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={projectTypeData} cx="50%" cy="50%" outerRadius={68} dataKey="value" nameKey="name"
                        label={({ name, percent }) => percent > 0.08 ? `${name} ${Math.round(percent * 100)}%` : ""}
                        labelLine={false} fontSize={10}>
                        {projectTypeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Bar chart — geographic spread */}
              <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 2 }}>Geographic spread</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 14 }}>Users by region · platform data</div>
                {regionData.length === 0 ? <EmptyChartMsg msg="No location data yet" /> : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={regionData} layout="vertical" margin={{ left: 0, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={82} />
                      <Tooltip />
                      <Bar dataKey="value" name="Users" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* ── UK Property Market ── */}
            <MktSectionLabel>UK Property Market</MktSectionLabel>

            {/* HPI banner */}
            {ukHPI ? (
              <div style={{ background: "linear-gradient(135deg, #EBF5FF, #F0F9FF)", border: "0.5px solid #BFDBFE", borderRadius: 12, padding: "1.25rem 1.5rem", marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#3B82F6", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>UK Average House Price</div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: "#1E3A5F" }}>{fmtM(ukHPI.avgPrice)}</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>{ukHPI.period} · HM Land Registry</div>
                </div>
                {ukHPI.annualChange != null && (
                  <div>
                    <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }}>Annual change</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: ukHPI.annualChange >= 0 ? "#16A34A" : "#DC2626" }}>
                      {ukHPI.annualChange >= 0 ? "+" : ""}{Number(ukHPI.annualChange).toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ background: "#F8FAFC", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1rem 1.5rem", marginBottom: 14, fontSize: 13, color: "#94A3B8" }}>
                UK house price data unavailable — Land Registry API may be temporarily down.
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: "2rem" }}>
              {/* Rental yields */}
              <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 2 }}>Rental yields by city</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 14 }}>2024/25 estimates</div>
                {RENTAL_YIELDS.map(r => (
                  <div key={r.region} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
                    <div style={{ fontSize: 12, minWidth: 90, color: r.region === "UK Average" ? "#1E3A5F" : "#555", fontWeight: r.region === "UK Average" ? 600 : 400 }}>{r.region}</div>
                    <div style={{ flex: 1, height: 7, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: 7, background: r.region === "UK Average" ? "#3B82F6" : "#16A34A", borderRadius: 4, width: `${(r.yield / 8) * 100}%` }} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: r.region === "UK Average" ? "#3B82F6" : "#16A34A", minWidth: 36, textAlign: "right" }}>{r.yield}%</div>
                  </div>
                ))}
              </div>

              {/* Bridging rates + housing starts stacked */}
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 2 }}>Bridging loan rates</div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 14 }}>Current market — Q1 2025</div>
                  {BRIDGING_RATES.map(b => (
                    <div key={b.type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "0.5px solid #f0f0f0" }}>
                      <div style={{ fontSize: 12, color: "#555" }}>{b.type}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", whiteSpace: "nowrap", marginLeft: 8 }}>{b.rate}</div>
                    </div>
                  ))}
                </div>

                <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 2 }}>UK housing starts</div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 14 }}>Annual new homes · ONS</div>
                  {HOUSING_STARTS.map(h => (
                    <div key={h.year} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
                      <div style={{ fontSize: 12, color: "#555", minWidth: 36 }}>{h.year}</div>
                      <div style={{ flex: 1, height: 7, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ height: 7, background: "#3B82F6", borderRadius: 4, width: `${(h.starts / 210000) * 100}%` }} />
                      </div>
                      <div style={{ fontSize: 12, color: "#555", minWidth: 58, textAlign: "right" }}>{h.starts.toLocaleString()}</div>
                    </div>
                  ))}
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6 }}>Source: ONS House Building Statistics</div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div style={{ background: "#F8FAFC", borderRadius: 10, padding: "0.75rem 1rem", fontSize: 11, color: "#94A3B8", lineHeight: 1.7 }}>
              Platform statistics are live from LenderBuild's database. UK house prices from HM Land Registry HPI (Open Government Licence). Rental yields and bridging rates are market estimates based on publicly available 2024/25 data — not financial advice. Housing starts from ONS House Building Statistics. Data refreshes automatically every 5 minutes.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── WAITLIST PAGE ────────────────────────────────────────────────────────────

function WaitlistPage({ setPage, showLoginLink = false }) {
  const [name,   setName]   = useState("");
  const [email,  setEmail]  = useState("");
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "already" | "error"
  const [errMsg, setErrMsg] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("loading");
    try {
      const res  = await fetch("/api/save-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "join-waitlist", name, email }),
      });
      const data = await res.json();
      if (!res.ok) { setErrMsg(data.error || "Something went wrong"); setStatus("error"); return; }
      setStatus(data.already ? "already" : "success");
    } catch {
      setErrMsg("Network error — please try again.");
      setStatus("error");
    }
  }

  const inputStyle = { width: "100%", height: 44, border: "1px solid #E2E8F0", borderRadius: 8, padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box" };

  return (
    <div style={{ maxWidth: 460, margin: "0 auto", padding: "2.5rem 1.25rem", minHeight: "100dvh", boxSizing: "border-box" }}>
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#FEF3C7", color: "#D97706", fontSize: 12, fontWeight: 600, padding: "4px 14px", borderRadius: 20, marginBottom: "0.75rem" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          Early Access
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 8px", color: "#1E3A5F", lineHeight: 1.25 }}>The marketplace where builders meet private lenders</h1>
        <p style={{ fontSize: 14, color: "#64748B", margin: 0, lineHeight: 1.6 }}>
          LenderBuild is launching soon. Join early to match with lenders who move fast and understand property development — plus a permanent <strong>Founding Member</strong> badge when we go live.
        </p>
      </div>

      {status === "success" && (
        <div style={{ background: "#DCFCE7", color: "#14532D", border: "1px solid #A8DFC9", borderRadius: 14, padding: "20px", textAlign: "center", fontSize: 14 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>You're on the list!</div>
          <div>We'll be in touch when LenderBuild launches.</div>
        </div>
      )}

      {status === "already" && (
        <div style={{ background: "#EBF2FF", color: "#1E3A5F", border: "1px solid #BFDBFE", borderRadius: 14, padding: "20px", textAlign: "center", fontSize: 14 }}>
          You're already on the waitlist — we'll reach out when we launch.
        </div>
      )}

      {status !== "success" && status !== "already" && (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <div>
            <label style={{ fontSize: 13, color: "#374151", display: "block", marginBottom: 4 }}>Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 13, color: "#374151", display: "block", marginBottom: 4 }}>Email <span style={{ color: "#DC2626" }}>*</span></label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} />
          </div>
          {status === "error" && <div style={{ fontSize: 13, color: "#DC2626" }}>{errMsg}</div>}
          <button type="submit" disabled={status === "loading"}
            style={{ height: 46, background: "var(--accent,#3B82F6)", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: status === "loading" ? "default" : "pointer", opacity: status === "loading" ? 0.7 : 1, marginTop: 2 }}>
            {status === "loading" ? "Joining…" : "Join waitlist"}
          </button>
        </form>
      )}

      <div style={{ textAlign: "center", marginTop: "1.25rem", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        {!showLoginLink && <button onClick={() => setPage("home")} style={{ fontSize: 13, color: "#64748B", background: "none", border: "none", cursor: "pointer" }}>← Back to home</button>}
        {showLoginLink && (
          <button onClick={() => setPage("auth")} style={{ fontSize: 12, color: "#94A3B8", background: "none", border: "none", cursor: "pointer" }}>
            Log in (testers only, nice try 👀)
          </button>
        )}
      </div>
    </div>
  );
}

// ─── BUILDER PASSPORT PAGE ────────────────────────────────────────────────────

function BuilderPassportPage({ sequential_id, user, setPage }) {
  const [profile, setProfile] = useState(null);
  const [builderData, setBuilderData] = useState(null);
  const [deals, setDeals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [reviewForm, setReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    if (sequential_id) loadPassport(sequential_id);
  }, [sequential_id]); // eslint-disable-line

  async function loadPassport(seqId) {
    setLoading(true);
    const { data: prof } = await supabase.from("profiles").select("*").eq("sequential_id", Number(seqId)).maybeSingle();
    if (!prof) { setLoading(false); return; }
    setProfile(prof);
    const [{ data: bp }, { data: dealData }, { data: revData }] = await Promise.all([
      supabase.from("builder_profiles").select("*").eq("user_id", prof.id).maybeSingle(),
      supabase.from("deals").select("id,title,created_at,status").eq("builder_id", prof.id),
      supabase.from("builder_reviews").select("*").eq("builder_user_id", prof.id),
    ]);
    setBuilderData(bp || null);
    setDeals(dealData || []);
    setReviews(revData || []);
    setLoading(false);
  }

  async function handleSubmitReview(e) {
    e.preventDefault();
    if (!user) { setPage("auth"); return; }
    setSubmittingReview(true);
    const { data: { session } } = await supabase.auth.getSession();
    await fetch("/api/connect-request", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "save-review", builder_user_id: profile.id, rating: reviewRating, comment: reviewComment }),
    });
    setSubmittingReview(false);
    setReviewForm(false);
    const { data: revData } = await supabase.from("builder_reviews").select("*").eq("builder_user_id", profile.id);
    setReviews(revData || []);
  }

  const shareUrl = `${window.location.origin}/builder/${sequential_id}`;
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;
  const completedDeals = deals.filter(d => d.status === "completed").length;
  const totalFunded = deals.length; // simplified

  if (loading) return <div style={{ textAlign: "center", padding: "4rem", color: "#aaa" }}>Loading builder passport…</div>;
  if (!profile) return <div style={{ textAlign: "center", padding: "4rem", color: "var(--text-muted,#64748B)" }}><div style={{ marginBottom: 12 }}><Icon name="search" size={36} /></div><div style={{ fontSize: 15 }}>Builder not found</div></div>;

  const credScore = computeCredibilityScore({ verified_documents: builderData?.verified_documents || [], props: builderData?.projects_completed || 0, reviews });

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "2rem 1.25rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem", flexWrap: "wrap", gap: 10 }}>
        <button onClick={() => setPage("find-builder")} style={{ background: "none", border: "none", color: "#3B82F6", fontSize: 13, cursor: "pointer", padding: 0 }}>← Find builders</button>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { navigator.clipboard?.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            style={{ padding: "7px 14px", background: "#f0f0f0", border: "none", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
            {copied ? "Copied!" : "Share"}
          </button>
        </div>
      </div>

      {/* Profile header */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 16, padding: "1.5rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          <CredibilityRing score={credScore} size={68}>
            <div style={{ width: 68, height: 68, borderRadius: "50%", background: "#3B82F6", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 700 }}>
              {(profile.full_name || "?").charAt(0).toUpperCase()}
            </div>
          </CredibilityRing>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 4px", color: "#1E3A5F" }}>{profile.full_name}</h1>
            {profile.sequential_id && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: "#aaa" }}>{fmtId(profile.sequential_id)}</span>
                {profile.is_founding_member && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#FEF3C7", color: "#D97706" }}>Founding Member</span>}
              </div>
            )}
            {profile.location && <div style={{ fontSize: 13, color: "#64748B", marginBottom: 6 }}><Icon name="map-pin" size={12} style={{marginRight:3,flexShrink:0}} />{profile.location}</div>}
            {builderData?.specialization && <div style={{ fontSize: 12, padding: "2px 8px", borderRadius: 20, background: "#EBF2FF", color: "#1E3A5F", display: "inline-block", marginBottom: 6 }}>{builderData.specialization}</div>}
            {profile.bio && <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6, margin: 0 }}>{profile.bio}</p>}
          </div>
          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(shareUrl)}`} alt="QR" style={{ borderRadius: 8 }} />
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: "1rem", flexWrap: "wrap" }}>
          {[
            { label: "Projects completed", value: builderData?.projects_completed ?? "—" },
            { label: "Deals on platform", value: totalFunded },
            { label: "Completed deals", value: completedDeals },
            { label: "Avg. rating", value: avgRating ? `${avgRating} ⭐` : "—" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 100, textAlign: "center", background: "#f9f9f7", borderRadius: 10, padding: "0.75rem" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1E3A5F" }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Reviews */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 16, padding: "1.5rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F" }}>Lender reviews ({reviews.length})</div>
          {user && user.user_metadata?.role === "lender" && !reviewForm && (
            <button onClick={() => setReviewForm(true)} style={{ fontSize: 12, color: "#3B82F6", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>+ Add review</button>
          )}
        </div>
        {reviews.length === 0 && !reviewForm && <div style={{ fontSize: 13, color: "#aaa" }}>No reviews yet.</div>}
        {reviews.map(r => (
          <div key={r.id} style={{ padding: "10px 0", borderBottom: "0.5px solid #f0f0f0" }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
              {"⭐".repeat(r.rating)}
              <span style={{ fontSize: 11, color: "#aaa", marginLeft: 4 }}>{new Date(r.created_at).toLocaleDateString("en-GB")}</span>
            </div>
            {r.comment && <div style={{ fontSize: 13, color: "#555", lineHeight: 1.5 }}>{r.comment}</div>}
          </div>
        ))}
        {reviewForm && (
          <form onSubmit={handleSubmitReview} style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[1,2,3,4,5].map(n => (
                <button key={n} type="button" onClick={() => setReviewRating(n)}
                  style={{ fontSize: 22, background: "none", border: "none", cursor: "pointer", opacity: n <= reviewRating ? 1 : 0.3 }}>⭐</button>
              ))}
            </div>
            <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} placeholder="Share your experience…" rows={3}
              style={{ border: "0.5px solid #ccc", borderRadius: 8, padding: "8px 10px", fontSize: 13, resize: "vertical", outline: "none" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={() => setReviewForm(false)} style={{ flex: 1, height: 40, background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button type="submit" disabled={submittingReview} style={{ flex: 2, height: 40, background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{submittingReview ? "Saving…" : "Submit review"}</button>
            </div>
          </form>
        )}
      </div>

      {/* Deals (anonymized) */}
      {deals.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 16, padding: "1.5rem" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 12 }}>Projects on platform</div>
          {deals.map(d => (
            <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid #f0f0f0" }}>
              <div style={{ fontSize: 13, color: "#555" }}>{d.title || "Unnamed project"}</div>
              <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 20, background: d.status === "completed" ? "#DCFCE7" : "#EBF2FF", color: d.status === "completed" ? "#16A34A" : "#1E3A5F" }}>{d.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SAVED SEARCHES PAGE ──────────────────────────────────────────────────────

function SavedSearchesPage({ user, setPage }) {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadSearches(); }, []); // eslint-disable-line

  async function loadSearches() {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setLoading(false); return; }
    const res = await fetch("/api/save-profile", { headers: { Authorization: `Bearer ${session.access_token}` } });
    const d = await res.json().catch(() => ({}));
    setSearches(d.searches || []);
    setLoading(false);
  }

  async function handleDelete(searchId) {
    const { data: { session } } = await supabase.auth.getSession();
    await fetch("/api/save-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "delete-search", search_id: searchId }),
    });
    setSearches(prev => prev.filter(s => s.id !== searchId));
  }

  return (
    <div style={{ maxWidth: 620, margin: "0 auto", padding: "1.5rem 1.25rem" }}>
      <button onClick={() => setPage("account")} style={{ background: "none", border: "none", color: "#3B82F6", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: "1rem" }}>← Settings</button>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 4px", fontFamily: "'Georgia', serif" }}>Saved searches</h1>
      <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 1.5rem" }}>Your saved search filters with alert preferences.</p>

      {loading
        ? <div style={{ textAlign: "center", padding: "2rem", color: "#aaa" }}>Loading…</div>
        : searches.length === 0
          ? (
            <div style={{ textAlign: "center", padding: "3rem" }}>
              <div style={{ marginBottom: 10, color: "var(--text-muted,#64748B)" }}><Icon name="search" size={36} /></div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>No saved searches yet</div>
              <div style={{ fontSize: 13, color: "#64748B" }}>When you search for lenders or builders, save your filters to get alerts.</div>
            </div>
          )
          : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {searches.map(s => (
                <div key={s.id} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1rem 1.25rem", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{s.name}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, padding: "1px 6px", borderRadius: 20, background: "#EBF2FF", color: "#1E3A5F" }}>{s.role_type}</span>
                      <span style={{ fontSize: 11, padding: "1px 6px", borderRadius: 20, background: "#F0FDF4", color: "#16A34A" }}>{s.frequency} alerts</span>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{new Date(s.created_at).toLocaleDateString("en-GB")}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(s.id)}
                    style={{ background: "transparent", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", padding: 4, lineHeight: 1 }}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )
      }
    </div>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────

function MinimalFooter({ setPage }) {
  return (
    <footer style={{ borderTop: "1px solid rgba(0,0,0,0.07)", marginTop: "auto", padding: "1.25rem 1.25rem", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "var(--text-muted,#9ca3af)", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "0 0.5rem" }}>
        <span>© 2025 LenderBuild Ltd</span>
        <span>·</span>
        <button onClick={() => setPage("privacy")} style={{ background: "none", border: "none", padding: 0, fontSize: 11, color: "var(--text-muted,#9ca3af)", cursor: "pointer" }}>Privacy Policy</button>
        <span>·</span>
        <button onClick={() => setPage("terms")} style={{ background: "none", border: "none", padding: 0, fontSize: 11, color: "var(--text-muted,#9ca3af)", cursor: "pointer" }}>Terms and Conditions</button>
        <span>·</span>
        <button onClick={() => setPage("risk-warning")} style={{ background: "none", border: "none", padding: 0, fontSize: 11, color: "var(--text-muted,#9ca3af)", cursor: "pointer" }}>Risk Warning</button>
      </div>
    </footer>
  );
}

function Footer({ setPage }) {
  return (
    <footer style={{ background: "#1E3A5F", position: "relative" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "3rem 1.25rem 2rem" }}>
        {/* Top row */}
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "2.5rem", marginBottom: "2.5rem" }}>
          <div style={{ maxWidth: 260 }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#3B82F6", marginBottom: 12 }}>LenderBuild</div>
            <div style={{ fontSize: 13, lineHeight: 1.75, color: "rgba(255,255,255,0.5)" }}>
              Connecting property builders with the right lenders across the UK.
            </div>
          </div>
          <div style={{ display: "flex", gap: "3.5rem", flexWrap: "wrap" }}>
            {!WAITLIST_MODE && <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#3B82F6", marginBottom: 14 }}>Platform</div>
              {[
                ["search",          "Find a lender"],
                ["find-builder",    "Find a builder"],
                ["browse-projects", "Browse projects"],
                ["posts",           "Posts"],
                ["market",            "Market Intelligence"],
                ["build-calculator",  "Build Calculator"],
              ].map(([p, label]) => (
                <button key={p} onClick={() => setPage(p)} style={{ display: "block", background: "transparent", border: "none", color: "rgba(255,255,255,0.55)", fontSize: 13, padding: "5px 0", cursor: "pointer", textAlign: "left" }}>{label}</button>
              ))}
            </div>}
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#3B82F6", marginBottom: 14 }}>Help</div>
              {[
                ["help",         "Help Centre"],
                ["how-it-works", "How it works"],
                ["safety",       "Trust & Safety"],
                ["about",        "About us"],
                ...(!WAITLIST_MODE ? [["status", "System status"]] : []),
              ].map(([p, label]) => (
                <button key={p} onClick={() => setPage(p)} style={{ display: "block", background: "transparent", border: "none", color: "rgba(255,255,255,0.55)", fontSize: 13, padding: "5px 0", cursor: "pointer", textAlign: "left" }}>{label}</button>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#3B82F6", marginBottom: 14 }}>Legal</div>
              {[
                ["privacy",          "Privacy Policy"],
                ["terms",            "Terms & Conditions"],
                ["risk-warning",     "Risk Warning"],
                ["legal-resources",  "Legal Resources"],
              ].map(([p, label]) => (
                <button key={p} onClick={() => setPage(p)} style={{ display: "block", background: "transparent", border: "none", color: "rgba(255,255,255,0.55)", fontSize: 13, padding: "5px 0", cursor: "pointer", textAlign: "left" }}>{label}</button>
              ))}
            </div>
          </div>
        </div>
        {/* Divider + bottom row */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "1.5rem", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", lineHeight: 1.6 }}>
            LenderBuild is an introducer only. We do not provide financial, legal, or investment advice, do not handle funds between parties, and accept no responsibility for the outcome of any deal arranged through the platform. Users must conduct their own due diligence.
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>
              © {new Date().getFullYear()} LenderBuild. All rights reserved. Registered in England & Wales.
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>
              Not authorised or regulated by the Financial Conduct Authority.
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── MY DEALS PAGE ────────────────────────────────────────────────────────────

function MyDealsPage({ user, setPage }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const myId = user?.id;
  const role = user?.user_metadata?.role;

  useEffect(() => {
    if (!myId) return;
    (async () => {
      const { data } = await supabase
        .from("deals")
        .select("*")
        .or(`lender_id.eq.${myId},builder_id.eq.${myId}`)
        .order("created_at", { ascending: false });
      setDeals(data || []);
      setLoading(false);
    })();
  }, [myId]);

  const sorted = [...deals].sort((a, b) => {
    const rank = d => d.status === "closed" ? 2 : (d.finder_fee_status === "paid" ? 0 : 1);
    return rank(a) - rank(b);
  });

  const statusBadge = deal => {
    if (deal.status === "closed") return { label: "Closed", bg: "#F1F5F9", color: "#64748B" };
    if (deal.finder_fee_status === "paid") return { label: "Active", bg: "#DCFCE7", color: "#0F6E56" };
    return { label: "Pending payment", bg: "#FEF3C7", color: "#D97706" };
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px" }}>
      <h2 style={{ margin: "0 0 24px", fontSize: 20, fontWeight: 700, color: "#1E3A5F" }}>My Deals</h2>

      {loading && <div style={{ textAlign: "center", padding: 48, color: "#64748B", fontSize: 14 }}>Loading…</div>}

      {!loading && sorted.length === 0 && (
        <div style={{ textAlign: "center", padding: 64, color: "#64748B" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No deals yet</div>
          <div style={{ fontSize: 13 }}>
            {role === "lender" ? "Connect with a builder and agree terms to create your first deal." : "A lender will create a deal once you've connected and agreed terms."}
          </div>
        </div>
      )}

      {!loading && sorted.map(deal => {
        const total = deal.agreed_amount || 0;
        const counterparty = myId === deal.lender_id ? deal.builder_name : deal.lender_name;
        const badge = statusBadge(deal);
        return (
          <div key={deal.id} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "14px 18px", marginBottom: 10, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1E3A5F", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{counterparty}</div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>{total > 0 ? fmt(total) : "Amount TBC"}</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: badge.bg, color: badge.color, flexShrink: 0 }}>{badge.label}</span>
            <button onClick={() => setPage("deal-detail", deal)} style={{ padding: "7px 16px", background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>Open Deal Room →</button>
          </div>
        );
      })}
    </div>
  );
}

// ─── DEALS PAGE ───────────────────────────────────────────────────────────────

function DealsPage({ user, setPage, setCelebration }) {
  const role = user?.user_metadata?.role;
  const [deals,      setDeals]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [creating,   setCreating]   = useState(false);
  const [builderName, setBuilderName] = useState("");
  const [dealTitle,   setDealTitle]   = useState("");
  const [agreedAmount,        setAgreedAmount]        = useState("");
  const [saving,              setSaving]              = useState(false);
  const [error,               setError]               = useState("");
  const [propertyValue,       setPropertyValue]       = useState("");
  const [returnType,          setReturnType]          = useState("");
  const [interestRate,        setInterestRate]        = useState("");
  const [loanTermMonths,      setLoanTermMonths]      = useState("");
  const [rentalSplitPct,      setRentalSplitPct]      = useState("");
  const [monthlyRental,       setMonthlyRental]       = useState("");
  const [equityPct,           setEquityPct]           = useState("");
  const [repaymentStartDate,  setRepaymentStartDate]  = useState("");
  const [selectedTemplate,    setSelectedTemplate]    = useState(null);

  useEffect(() => { loadDeals(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for Stripe payment return in query string
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("milestone_paid") || params.get("finder_fee_paid") || params.get("repayment_paid")) {
      loadDeals();
      window.history.replaceState(null, "", "/");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadDeals() {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setLoading(false); return; }
    const res = await fetch("/api/deals", {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    const d = await res.json().catch(() => ({}));
    setDeals(d.deals || []);
    setLoading(false);
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({
        builder_name: builderName, title: dealTitle, agreed_amount: agreedAmount ? Number(agreedAmount) : null,
        property_value: propertyValue ? Number(propertyValue) : null,
        return_type: returnType || null,
        interest_rate: interestRate ? Number(interestRate) : null,
        loan_term_months: loanTermMonths ? Number(loanTermMonths) : null,
        rental_split_pct: rentalSplitPct ? Number(rentalSplitPct) : null,
        estimated_monthly_rental: monthlyRental ? Number(monthlyRental) : null,
        equity_pct: equityPct ? Number(equityPct) : null,
        repayment_start_date: repaymentStartDate || null,
      }),
    });
    const d = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) { setError(d.error || "Failed to create deal."); return; }
    setCreating(false);
    setBuilderName(""); setDealTitle(""); setPropertyValue(""); setAgreedAmount("");
    setReturnType(""); setInterestRate(""); setLoanTermMonths(""); setRentalSplitPct(""); setMonthlyRental(""); setEquityPct(""); setRepaymentStartDate("");
    setSelectedTemplate(null);
    // Celebration: first deal
    if (setCelebration) {
      const celKey = "lb_cel_first_deal";
      if (!localStorage.getItem(celKey)) {
        localStorage.setItem(celKey, "1");
        setCelebration({ message: "Your first project is live!", subtitle: "Your lender will be notified." });
      }
    }
    loadDeals();
  }

  function handleSelectTemplate(tpl) {
    setSelectedTemplate(tpl);
    setReturnType(tpl.fields.returnType || "");
    setInterestRate(tpl.fields.interestRate || "");
    setLoanTermMonths(tpl.fields.loanTermMonths || "");
    if (tpl.fields.rentalSplitPct) setRentalSplitPct(tpl.fields.rentalSplitPct);
    if (tpl.fields.dealTitle) setDealTitle(tpl.fields.dealTitle);
  }

  const inp = {
    width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8,
    padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box",
  };
  const lbl = { fontSize: 13, color: "#555", marginBottom: 4, display: "block", fontWeight: 500 };

  function totalAmount(deal) {
    return deal.agreed_amount || 0;
  }
  function dealProgress(deal) {
    return deal.tracker_completed_at ? 100 : 0;
  }

  return (
    <div style={{ padding: "1.5rem 1.25rem", minHeight: "calc(100vh - 56px)" }}>

      {/* ── How it works banner ─────────────────────────────────────────── */}
      <div style={{ background: "#EBF2FF", border: "0.5px solid #BFDBFE", borderRadius: 12, padding: "1rem 1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1E3A5F", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>How it works</div>
        <div style={{ display: "flex", gap: 0, flexWrap: "wrap" }}>
          {[
            { n: 1, label: "Connect",     desc: "Match with a lender or builder" },
            { n: 2, label: "Agree amount", desc: "Both confirm the same deal figure" },
            { n: 3, label: "Pay 1% fee",  desc: "Lender pays via Stripe for the introduction" },
            { n: 4, label: "Funds transfer", desc: "Lender sends funds to builder's bank" },
            { n: 5, label: "Confirm receipt", desc: "Both parties confirm funds received" },
          ].map((step, i, arr) => (
            <div key={step.n} style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "4px 8px", minWidth: 70 }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#1E3A5F", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0, marginBottom: 4 }}>{step.n}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", textAlign: "center", lineHeight: 1.2 }}>{step.label}</div>
                <div style={{ fontSize: 10, color: "#64748B", textAlign: "center", lineHeight: 1.3, marginTop: 2 }}>{step.desc}</div>
              </div>
              {i < arr.length - 1 && <div style={{ fontSize: 14, color: "#93C5FD", flexShrink: 0, margin: "0 2px", alignSelf: "flex-start", marginTop: 10 }}>→</div>}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 22, fontWeight: 500, margin: 0, fontFamily: "'Georgia', serif" }}>
            My Deals
            <HelpTooltip text="Payments are made by the lender directly to the builder in stages as each phase completes. The platform tracks progress — no money passes through LenderBuild." />
          </h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
            All your active and past deals in one place.
          </p>
        </div>
        {role === "lender" && !creating && (
          <button
            onClick={() => setCreating(true)}
            style={{ padding: "8px 18px", minHeight: 44, background: "#3B82F6", color: "#FFFFFF", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            + New project
          </button>
        )}
      </div>

      {/* ── Create deal form ──────────────────────────────────────────── */}
      {creating && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: "1rem", fontFamily: "'Georgia', serif" }}>Create a project</div>

          {/* ── Deal Templates ── */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Start from a template</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
              {DEAL_TEMPLATES.map(tpl => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => handleSelectTemplate(tpl)}
                  style={{
                    textAlign: "left", background: selectedTemplate?.id === tpl.id ? "#EBF2FF" : "#fafaf8",
                    border: `1.5px solid ${selectedTemplate?.id === tpl.id ? "#3B82F6" : "#e8e8e5"}`,
                    borderRadius: 10, padding: "10px 12px", cursor: "pointer",
                    outline: selectedTemplate?.id === tpl.id ? "2px solid #3B82F6" : "none",
                  }}
                >
                  <div style={{ marginBottom: 4 }}><Icon name={tpl.icon} size={20} className="lb-icon" /></div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", marginBottom: 2, lineHeight: 1.3 }}>{tpl.name}</div>
                  <div style={{ fontSize: 11, color: "#64748B", lineHeight: 1.4 }}>{tpl.typical}</div>
                </button>
              ))}
            </div>
            {selectedTemplate && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#3B82F6" }}>
                Template selected: <strong>{selectedTemplate.name}</strong> — fields pre-filled below
              </div>
            )}
          </div>

          {error && (
            <div style={{ background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem" }}>
              {error}
            </div>
          )}
          <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={lbl}>Builder name</label>
              <input type="text" required value={builderName} onChange={e => setBuilderName(e.target.value)} placeholder="Builder's full name on LenderBuild" style={inp} />
            </div>
            <div>
              <label style={lbl}>Project title</label>
              <input type="text" required value={dealTitle} onChange={e => setDealTitle(e.target.value)} placeholder="e.g. 3-bed residential build, Manchester" style={inp} />
            </div>
            <div>
              <label style={lbl}>Estimated property value (£) — used for LTV calculation</label>
              <input type="text" inputMode="numeric" value={fmtInput(propertyValue)} onChange={e => setPropertyValue(parseInput(e.target.value))} placeholder="e.g. 500,000" style={inp} />
            </div>

            <div>
              <label style={lbl}>Return type</label>
              <select value={returnType} onChange={e => setReturnType(e.target.value)} style={inp}>
                <option value="">— Select return type —</option>
                <option value="fixed_interest">Fixed interest (monthly repayments)</option>
                <option value="rental_split">Rental split (% of rental income)</option>
                <option value="equity_stake">Equity stake (% on sale)</option>
              </select>
              {returnType && (() => {
                const rtLabel = returnType === "fixed_interest" ? "Fixed interest" : returnType === "rental_split" ? "Rental split" : "Equity stake";
                return (
                  <ReturnTypeExplainer returnType={rtLabel} onApply={r => {
                    setReturnType(r === "Fixed interest" ? "fixed_interest" : r === "Rental split" ? "rental_split" : "equity_stake");
                  }}>
                    <div />
                  </ReturnTypeExplainer>
                );
              })()}
            </div>

            {returnType === "fixed_interest" && (
              <div style={{ background: "#f9f9f7", border: "0.5px solid #e8e8e5", borderRadius: 10, padding: "1rem", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.08em" }}>Fixed interest details</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Annual interest rate (%)</label>
                    <input type="number" min="0" max="50" step="0.1" value={interestRate} onChange={e => setInterestRate(e.target.value)} placeholder="e.g. 8" style={{ ...inp, height: 38, fontSize: 13 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Loan term (months)</label>
                    <input type="number" min="1" max="360" step="1" value={loanTermMonths} onChange={e => setLoanTermMonths(e.target.value)} placeholder="e.g. 12" style={{ ...inp, height: 38, fontSize: 13 }} />
                  </div>
                </div>
                <div>
                  <label style={lbl}>First repayment date</label>
                  <input type="date" value={repaymentStartDate} onChange={e => setRepaymentStartDate(e.target.value)} style={{ ...inp, height: 38, fontSize: 13 }} />
                </div>
              </div>
            )}

            {returnType === "rental_split" && (
              <div style={{ background: "#f9f9f7", border: "0.5px solid #e8e8e5", borderRadius: 10, padding: "1rem", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.08em" }}>Rental split details</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Lender's share of rent (%)</label>
                    <input type="number" min="0" max="100" step="1" value={rentalSplitPct} onChange={e => setRentalSplitPct(e.target.value)} placeholder="e.g. 40" style={{ ...inp, height: 38, fontSize: 13 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Estimated monthly rental (£)</label>
                    <input type="text" inputMode="numeric" value={fmtInput(monthlyRental)} onChange={e => setMonthlyRental(parseInput(e.target.value))} placeholder="e.g. 1,500" style={{ ...inp, height: 38, fontSize: 13 }} />
                  </div>
                </div>
                <div>
                  <label style={lbl}>First payment date</label>
                  <input type="date" value={repaymentStartDate} onChange={e => setRepaymentStartDate(e.target.value)} style={{ ...inp, height: 38, fontSize: 13 }} />
                </div>
              </div>
            )}

            {returnType === "equity_stake" && (
              <div style={{ background: "#f9f9f7", border: "0.5px solid #e8e8e5", borderRadius: 10, padding: "1rem", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.08em" }}>Equity stake details</div>
                <div>
                  <label style={lbl}>Lender's equity stake (%)</label>
                  <input type="number" min="0" max="100" step="0.1" value={equityPct} onChange={e => setEquityPct(e.target.value)} placeholder="e.g. 20" style={{ ...inp, height: 38, fontSize: 13 }} />
                </div>
              </div>
            )}

            <div>
              <label style={lbl}>Agreed loan amount (£)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 14, color: "#555", flexShrink: 0 }}>£</span>
                <input
                  type="text" inputMode="numeric" required value={fmtInput(agreedAmount)}
                  onChange={e => setAgreedAmount(parseInput(e.target.value))}
                  placeholder="e.g. 150,000"
                  style={{ ...inp, flex: 1 }}
                />
              </div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>The total loan amount agreed between you and the builder. Used to calculate the 1% finder's fee.</div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button
                type="button" onClick={() => { setCreating(false); setError(""); }}
                style={{ flex: 1, height: 44, background: "transparent", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 14, cursor: "pointer", color: "#555" }}
              >Cancel</button>
              <button
                type="submit" disabled={saving}
                style={{ flex: 2, height: 44, background: saving ? "#aaa" : "#3B82F6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: saving ? "default" : "pointer" }}
              >
                {saving ? "Creating…" : "Create project"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Deals list ────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "#aaa", fontSize: 14 }}>Loading…</div>
      ) : deals.length === 0 ? (
        <div style={{ padding: "1.5rem 0" }}>
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="clipboard" size={40} /></div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No projects yet</div>
            <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6 }}>
              {role === "lender"
                ? "Click \"+ New project\" to set up a project with a builder."
                : "Once you and your lender both confirm the same deal amount below, your project tracker unlocks automatically."}
            </div>
          </div>
          {role === "builder" && (() => {
            const accepted = (user?.user_metadata?.connections || []).filter(c => c.status === "accepted");
            if (!accepted.length) return (
              <div style={{ background: "#F8FAFC", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem 1.5rem", textAlign: "center" }}>
                <div style={{ fontSize: 13, color: "#64748B", marginBottom: 8 }}>You don't have any connected lenders yet.</div>
                <button onClick={() => setPage("search")} style={{ padding: "8px 20px", background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Find a lender →
                </button>
              </div>
            );
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {accepted.map(conn => (
                  <div key={conn.lender_name} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem 1.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <Avatar initials={nameInitials(conn.lender_name)} color={pickColor(conn.lender_name || "")} size={32} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{conn.lender_name}</div>
                        <div style={{ fontSize: 11, color: "#64748B" }}>{conn.lender_type || "Lender"} · Connected</div>
                      </div>
                    </div>
                    <DealConfirmWidget user={user} otherName={conn.lender_name} otherRole="lender" />
                  </div>
                ))}
              </div>
            );
          })()}
          {role === "lender" && (() => {
            const accepted = (user?.user_metadata?.lender_connections || []).filter(c => c.status === "accepted");
            if (!accepted.length) return null;
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {accepted.map(conn => (
                  <div key={conn.builder_name} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem 1.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <Avatar initials={nameInitials(conn.builder_name)} color={pickColor(conn.builder_name || "")} size={32} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{conn.builder_name}</div>
                        <div style={{ fontSize: 11, color: "#64748B" }}>Builder · Connected</div>
                      </div>
                    </div>
                    <DealConfirmWidget user={user} otherName={conn.builder_name} otherRole="builder" />
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {deals.map(deal => {
            const total    = totalAmount(deal);
            const isComplete = !!deal.tracker_completed_at;
            const ltv = deal.property_value ? Math.round((total / deal.property_value) * 100) : null;
            const ltvBg   = ltv === null ? null : ltv < 70 ? "#E1F5EE" : ltv <= 80 ? "#FEF3C7" : "#FEE2E2";
            const ltvText = ltv === null ? null : ltv < 70 ? "#0F6E56" : ltv <= 80 ? "#B45309" : "#991B1B";
            const openDispute = (deal.disputes || []).find(d => d.status === "open");
            const today = new Date().toISOString().split("T")[0];
            const hasMissedRepayment = (deal.repayments || []).some(r => (r.status === "missed") || (r.status === "scheduled" && r.due_date < today));
            const nextRepayment = (deal.repayments || []).filter(r => r.status === "scheduled" || r.status === "missed").sort((a,b) => a.due_date.localeCompare(b.due_date))[0];
            return (
              <div
                key={deal.id}
                onClick={() => setPage("deal-detail", deal)}
                style={{
                  background: "#fff", border: openDispute ? "0.5px solid #FCA5A5" : "0.5px solid #e0e0e0", borderRadius: 12,
                  padding: "1.25rem 1.5rem", cursor: "pointer",
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <div style={{ fontSize: 15, fontWeight: 500 }}>{deal.title}</div>
                      {deal.finder_fee_status === "paid"
                        ? <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: "#E1F5EE", color: "#0F6E56" }}>Active</span>
                        : <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: "#FEF3C7", color: "#B45309" }}>Pending payment</span>
                      }
                      {openDispute && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: "#FEE2E2", color: "#991B1B" }}>Dispute open</span>}
                      {deal.flagged_default && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: "#FEE2E2", color: "#991B1B" }}>In default</span>}
                      {hasMissedRepayment && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: "#FEF3C7", color: "#B45309" }}>Missed repayment</span>}
                      {ltv !== null && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: ltvBg, color: ltvText }}>
                            LTV {ltv}%
                          </span>
                          <HelpTooltip text="Loan to Value ratio. The percentage of the property value being borrowed. Under 70% is considered low risk." />
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748B" }}>
                      {role === "lender" ? `Builder: ${deal.builder_name}` : `Lender: ${deal.lender_name}`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 17, fontWeight: 600, color: "#3B82F6" }}>{fmt(total)}</div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{(deal.deal_updates || []).length} update{(deal.deal_updates || []).length !== 1 ? "s" : ""}</div>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#aaa", marginBottom: 4 }}>
                    <span>{isComplete ? "Complete" : "In progress"}</span>
                    <span>{isComplete ? "Project complete" : "Tracker active"}</span>
                  </div>
                  <div style={{ height: 4, background: "#eee", borderRadius: 2 }}>
                    <div style={{ height: 4, borderRadius: 2, background: isComplete ? "#16A34A" : "#3B82F6", width: isComplete ? "100%" : "40%", transition: "width 0.3s" }} />
                  </div>
                  {nextRepayment && (
                    <div style={{ fontSize: 11, color: hasMissedRepayment ? "#B45309" : "#64748B", marginTop: 6 }}>
                      {hasMissedRepayment ? "Missed repayment — " : "Next repayment: "}
                      {fmt(nextRepayment.amount)} due {nextRepayment.due_date}
                    </div>
                  )}
                  {deal.finder_fee_status !== "paid" && (
                    <div style={{ marginTop: 12, background: role === "builder" ? "#FFF7ED" : "#EBF2FF", border: `0.5px solid ${role === "builder" ? "#FCD34D" : "#BFDBFE"}`, borderRadius: 10, padding: "10px 14px" }}>
                      {role === "builder" ? (
                        <>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#92400E", marginBottom: 6 }}>
                            Finder's fee due — 1% of the deal value, payable to LenderBuild for the introduction
                          </div>
                          <div style={{ fontSize: 12, color: "#78350F", marginBottom: 8 }}>
                            Fee: <strong>£{(totalAmount(deal) * 0.01).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            <span style={{ color: "#aaa", marginLeft: 6 }}>(1% of £{totalAmount(deal).toLocaleString()})</span>
                          </div>
                          <button
                            onClick={ev => { ev.stopPropagation(); setPage("deal-detail", deal); }}
                            style={{ padding: "8px 18px", background: "#F59E0B", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 36 }}
                          >
                            Pay finder's fee — £{(totalAmount(deal) * 0.01).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} →
                          </button>
                        </>
                      ) : (
                        <div style={{ fontSize: 12, color: "#1E3A5F" }}>
                          Finder's fee of £{(totalAmount(deal) * 0.01).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} is payable to LenderBuild for the introduction.
                        </div>
                      )}
                    </div>
                  )}
                  {["active","completed"].includes(deal.status) && (
                    <div style={{ marginTop: 10 }}>
                      <button
                        onClick={ev => { ev.stopPropagation(); setPage("deal-room", deal); }}
                        style={{ padding: "6px 14px", background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                      >
                        Open Deal Room →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── DEAL DETAIL PAGE ─────────────────────────────────────────────────────────

const DEAL_DOC_TYPES = [
  { key: "site_insurance",         label: "Site insurance certificate" },
  { key: "public_liability",       label: "Public liability insurance" },
  { key: "personal_guarantee",     label: "Personal guarantee (signed)" },
  { key: "solicitor_confirmation", label: "Solicitor confirmation of legal charge registration" },
];
const ADMIN_EMAIL_CONTACT = "lenderbuild.support@gmail.com";

// ─── MY REPAYMENTS PAGE ───────────────────────────────────────────────────────
function MyRepaymentsPage({ user, setPage }) {
  const [deals,   setDeals]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [chasing,           setChasing]           = useState(null);
  const [receiving,         setReceiving]         = useState(null);
  const [confirmingReceive, setConfirmingReceive] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch("/api/deals", { headers: { Authorization: `Bearer ${session.access_token}` } });
      if (res.ok) { const d = await res.json(); setDeals(d.deals || []); }
      setLoading(false);
    })();
  }, []);

  const todayStr = new Date().toISOString().split("T")[0];
  const d7ago = new Date(); d7ago.setDate(d7ago.getDate() - 7);
  const sevenDayStr = d7ago.toISOString().split("T")[0];

  const allRepayments = deals.flatMap(d =>
    (d.repayments || []).map(r => ({ ...r, deal_title: d.title, deal_id: d.id }))
  ).sort((a, b) => a.due_date.localeCompare(b.due_date));

  const upcoming  = allRepayments.filter(r => r.status === "scheduled" && r.due_date >= todayStr);
  const overdue   = allRepayments.filter(r => r.status === "missed" || (r.status === "scheduled" && r.due_date < todayStr));
  const paid      = allRepayments.filter(r => r.status === "paid");

  async function chaseRepayment(repaymentId) {
    setChasing(repaymentId);
    const { data: { session } } = await supabase.auth.getSession();
    await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "chase-repayment", repayment_id: repaymentId }),
    });
    setChasing(null);
    alert("Reminder sent to builder.");
  }

  async function markReceived(repaymentId) {
    if (confirmingReceive !== repaymentId) { setConfirmingReceive(repaymentId); return; }
    setConfirmingReceive(null);
    setReceiving(repaymentId);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "mark-repayment-received", repayment_id: repaymentId }),
    });
    if (res.ok) { window.location.reload(); } else { setReceiving(null); }
  }

  const RepRow = ({ r, showActions = true }) => {
    const isOverdue = r.status === "missed" || (r.status === "scheduled" && r.due_date < todayStr);
    const isVeryOverdue = isOverdue && r.due_date <= sevenDayStr;
    const bg = isOverdue ? "#FEF2F2" : "#fff";
    const borderColor = isOverdue ? "#FECACA" : "#e0e0e0";
    return (
      <div style={{ background: bg, border: `0.5px solid ${borderColor}`, borderRadius: 10, padding: "12px 16px", marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: isOverdue ? "#DC2626" : "#1E293B" }}>
              £{Number(r.amount).toLocaleString()}
              {isOverdue && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, background: "#DC2626", color: "#fff", padding: "2px 8px", borderRadius: 20 }}>OVERDUE</span>}
              {isVeryOverdue && <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 700, background: "#7F1D1D", color: "#fff", padding: "2px 8px", borderRadius: 20 }}>7+ DAYS</span>}
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{r.deal_title}</div>
            <div style={{ fontSize: 12, color: isOverdue ? "#DC2626" : "#64748B", marginTop: 1 }}>Due: {r.due_date} · Ref: {r.confirmation_number}</div>
          </div>
          {showActions && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {isOverdue && (
                <button onClick={() => chaseRepayment(r.id)} disabled={chasing === r.id}
                  style={{ padding: "6px 14px", background: "#EEF2FF", color: "#3730A3", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: chasing === r.id ? "default" : "pointer", minHeight: 32 }}>
                  {chasing === r.id ? "Sending…" : "Chase payment"}
                </button>
              )}
              {confirmingReceive === r.id ? (
                <>
                  <span style={{ fontSize: 12, color: "#64748B", alignSelf: "center" }}>Confirm?</span>
                  <button onClick={() => markReceived(r.id)} disabled={receiving === r.id}
                    style={{ padding: "6px 14px", background: "#16A34A", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", minHeight: 32 }}>
                    {receiving === r.id ? "Saving…" : "Yes, received"}
                  </button>
                  <button onClick={() => setConfirmingReceive(null)}
                    style={{ padding: "6px 10px", background: "transparent", color: "#64748B", border: "0.5px solid #ccc", borderRadius: 7, fontSize: 12, cursor: "pointer", minHeight: 32 }}>
                    Cancel
                  </button>
                </>
              ) : (
                <button onClick={() => markReceived(r.id)} disabled={receiving === r.id}
                  style={{ padding: "6px 14px", background: "#E1F5EE", color: "#0F6E56", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: receiving === r.id ? "default" : "pointer", minHeight: 32 }}>
                  {receiving === r.id ? "Saving…" : "Mark as received"}
                </button>
              )}
              {isVeryOverdue && (
                <button onClick={() => { const d = deals.find(dl => dl.id === r.deal_id); if (d) setPage("deal-detail", d); }}
                  style={{ padding: "6px 14px", background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", minHeight: 32 }}>
                  Raise dispute
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 720, margin: "0 auto" }}>
      <button onClick={() => setPage("my-deals")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "#555", fontSize: 14, cursor: "pointer", padding: 0, marginBottom: "1.5rem" }}>
        ← Back to my deals
      </button>
      <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 6 }}>My repayments</h2>
      <p style={{ fontSize: 14, color: "#64748B", marginBottom: "1.5rem" }}>Track all repayments due across your active deals.</p>

      {loading && <div style={{ color: "#64748B" }}>Loading…</div>}

      {!loading && overdue.length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#DC2626", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Overdue ({overdue.length})
          </div>
          {overdue.map(r => <RepRow key={r.id} r={r} />)}
          {overdue.some(r => r.due_date <= sevenDayStr) && (
            <div style={{ padding: "12px 16px", background: "#FEF2F2", border: "0.5px solid #FECACA", borderRadius: 10, fontSize: 13, color: "#7F1D1D", marginTop: 8 }}>
              One or more repayments are 7+ days overdue. If you cannot resolve this directly with the builder, contact your solicitor regarding the legal charge registered against the property.
            </div>
          )}
        </div>
      )}

      {!loading && upcoming.length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Upcoming ({upcoming.length})
          </div>
          {upcoming.map(r => <RepRow key={r.id} r={r} showActions={false} />)}
        </div>
      )}

      {!loading && paid.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0F6E56", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Received ({paid.length})
          </div>
          {paid.slice(0, 10).map(r => (
            <div key={r.id} style={{ background: "#F0FDF4", border: "0.5px solid #A8DFC9", borderRadius: 10, padding: "10px 16px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#0F6E56" }}>£{Number(r.amount).toLocaleString()}</span>
                <span style={{ fontSize: 12, color: "#64748B", marginLeft: 8 }}>{r.deal_title}</span>
              </div>
              <div style={{ fontSize: 12, color: "#64748B" }}>Received {r.paid_at ? r.paid_at.split("T")[0] : r.due_date}</div>
            </div>
          ))}
        </div>
      )}

      {!loading && allRepayments.length === 0 && (
        <div style={{ textAlign: "center", padding: "3rem 0", color: "#94A3B8" }}>No repayments found. Repayment schedules appear here once a deal has been set up.</div>
      )}
    </div>
  );
}

// ─── LEGAL RESOURCES PAGE ─────────────────────────────────────────────────────
function LegalResourcesPage({ setPage, user }) {
  const sections = [
    {
      title: "How to register a legal charge",
      icon: "bank",
      content: [
        "1. Instruct a solicitor who specialises in property finance to prepare the legal charge document.",
        "2. Your solicitor drafts a deed of legal charge, which you and the builder must both sign.",
        "3. Submit the signed charge to HM Land Registry using form CH1 (for registered land) via the Land Registry portal.",
        "4. Pay the registration fee (based on the loan amount). Registration typically takes 2–8 weeks.",
        "5. Once registered, HM Land Registry issues a title register entry confirming your charge. Request a copy for your records.",
        "6. Enter your charge reference number in the LenderBuild deal checklist to complete step 3.",
      ],
      link: { label: "Go to HM Land Registry portal", url: "https://www.gov.uk/government/organisations/hm-land-registry" },
    },
    {
      title: "Template letter of instruction to solicitors",
      icon: "file",
      content: [],
      template: `Dear [Solicitor name],

I am writing to instruct you in connection with a property development finance transaction.

Lender: [Your name]
Builder / Borrower: [Builder name]
Property: [Property address]
Loan amount: £[Amount]
Deal reference: LB-[Deal ID]

I wish to register a first legal charge over the above property as security for the loan. Please advise on the steps required, prepare the necessary deed of legal charge, and register it with HM Land Registry.

Please confirm your fees and anticipated timeline at your earliest convenience.

Yours sincerely,
[Your name]`,
    },
    {
      title: "Find a property solicitor",
      icon: "search",
      content: [
        "The Law Society of England and Wales maintains a directory of regulated solicitors. Use their 'Find a solicitor' tool to search for specialists in property, conveyancing, and secured lending.",
      ],
      link: { label: "Find a solicitor at lawsociety.org.uk", url: "https://solicitors.lawsociety.org.uk/" },
    },
    {
      title: "What happens if a builder defaults?",
      icon: "warning",
      content: [
        "If the builder fails to repay and you hold a registered legal charge, you have legal rights over the property:",
        "1. Issue formal written notice demanding repayment, referencing your legal charge.",
        "2. If unpaid, your solicitor can begin enforcement proceedings — ultimately the right to appoint a receiver or take possession and sell the property.",
        "3. The proceeds from any sale are used to repay your loan first (before unsecured creditors), up to the value of your charge.",
        "4. Keep records of all repayment attempts, communications, and missed payments on LenderBuild as evidence.",
        "Note: enforcement can be costly and time-consuming. Always seek specialist legal advice before taking action.",
      ],
    },
    {
      title: "Raising a dispute",
      icon: "handshake",
      content: [
        "If you cannot resolve a payment issue directly with the builder, you can raise a formal dispute on LenderBuild. This freezes the deal and notifies both parties and our admin team.",
        "For unresolved disputes, consider contacting the Property Ombudsman, which provides an independent dispute resolution service for property transactions.",
      ],
      link: { label: "The Property Ombudsman", url: "https://www.tpos.co.uk/" },
    },
  ];

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 760, margin: "0 auto" }}>
      <button onClick={() => setPage("home")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "#555", fontSize: 14, cursor: "pointer", padding: 0, marginBottom: "1.5rem" }}>
        ← Back
      </button>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>Legal Resources</h2>
      <p style={{ fontSize: 14, color: "#64748B", marginBottom: "2rem", lineHeight: 1.6 }}>
        Guidance on protecting your investment through legal charges, solicitor instructions, and what to do if something goes wrong.
      </p>
      <div style={{ background: "#FEF3C7", border: "0.5px solid #FDE68A", borderRadius: 10, padding: "14px 18px", marginBottom: "2rem", fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
        This page is for general guidance only. LenderBuild is not authorised to provide legal or financial advice. Always consult a qualified solicitor before committing funds.
      </div>

      {sections.map((s, i) => (
        <div key={i} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.5rem", marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 12, display:"flex", alignItems:"center", gap: 8 }}><Icon name={s.icon} size={18} className="lb-icon" />{s.title}</div>
          {s.content.length > 0 && (
            <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.75, marginBottom: s.link || s.template ? 14 : 0 }}>
              {s.content.map((line, j) => <p key={j} style={{ margin: "0 0 6px" }}>{line}</p>)}
            </div>
          )}
          {s.template && (
            <div style={{ background: "#F8FAFC", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "14px 16px", fontFamily: "monospace", fontSize: 13, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.7, marginBottom: s.link ? 14 : 0 }}>
              {s.template}
            </div>
          )}
          {s.link && (
            <a href={s.link.url} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#2E5FA3", fontSize: 14, fontWeight: 500, textDecoration: "none", background: "#EBF2FF", padding: "8px 16px", borderRadius: 8 }}>
              {s.link.label} →
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function DealDetailPage({ user, setPage, deal: initialDeal, setCelebration }) {
  const role = user?.user_metadata?.role;
  const [progressUpdates,   setProgressUpdates]   = useState(initialDeal?.deal_updates || []);
  const [updateBody,        setUpdateBody]        = useState("");
  const [updatePhotos,      setUpdatePhotos]      = useState([]);
  const [postingUpdate,     setPostingUpdate]     = useState(false);
  const [uploadingPhoto,    setUploadingPhoto]    = useState(false);
  const [trackerSelf,       setTrackerSelf]       = useState(role === "lender" ? !!initialDeal?.tracker_complete_lender : !!initialDeal?.tracker_complete_builder);
  const [trackerOther,      setTrackerOther]      = useState(role === "lender" ? !!initialDeal?.tracker_complete_builder : !!initialDeal?.tracker_complete_lender);
  const [trackerDone,       setTrackerDone]       = useState(!!initialDeal?.tracker_completed_at);
  const [completingTracker, setCompletingTracker] = useState(false);
  const [error,           setError]           = useState("");
  const [finderFeePaid,   setFinderFeePaid]   = useState(initialDeal?.finder_fee_status === "paid");
  const [finderFeePaying, setFinderFeePaying] = useState(false);
  const [dealDocs,          setDealDocs]          = useState(initialDeal?.deal_documents || []);
  const [docUploading,      setDocUploading]      = useState({});
  const [disputes,          setDisputes]          = useState(initialDeal?.disputes || []);
  const [disputeReason,     setDisputeReason]     = useState("");
  const [raisingDispute,    setRaisingDispute]    = useState(false);
  const [showDisputeForm,   setShowDisputeForm]   = useState(false);
  const [repayments] = useState((initialDeal?.repayments || []).sort((a,b) => a.payment_index - b.payment_index));
  const [legalSelf,         setLegalSelf]         = useState(role === "lender" ? !!initialDeal?.legal_confirmed_lender : !!initialDeal?.legal_confirmed_builder);
  const legalOther = role === "lender" ? !!initialDeal?.legal_confirmed_builder : !!initialDeal?.legal_confirmed_lender;
  const [solicitorName,     setSolicitorName]     = useState(initialDeal?.legal_solicitor_name || "");
  const [solicitorRef,      setSolicitorRef]      = useState(initialDeal?.legal_solicitor_ref || "");
  const [confirmingLegal,   setConfirmingLegal]   = useState(false);
  const [solicitorInstructed, setSolicitorInstructed] = useState(!!initialDeal?.legal_solicitor_instructed);
  const [solicitorFirm,     setSolicitorFirm]     = useState(initialDeal?.legal_solicitor_firm || "");
  const [chargeRegistered,  setChargeRegistered]  = useState(!!initialDeal?.legal_charge_registered);
  const [chargeRef,         setChargeRef]         = useState(initialDeal?.legal_charge_ref || "");
  const [savingLegal,       setSavingLegal]       = useState(false);
  const [confirmingBank,    setConfirmingBank]    = useState(false);
  const [bankDetailsProvided, setBankDetailsProvided] = useState(!!initialDeal?.builder_bank_details_provided);
  const [bankAccountName,   setBankAccountName]   = useState("");
  const [bankSortCode,      setBankSortCode]      = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [chasingRepayment,      setChasingRepayment]      = useState(null);
  const [markingReceived,       setMarkingReceived]       = useState(null);
  const [confirmingReceiveDD,   setConfirmingReceiveDD]   = useState(null);
  const [makingRepayment,   setMakingRepayment]   = useState(null);
  const [agreementSignedSelf,  setAgreementSignedSelf]  = useState(role === "lender" ? !!initialDeal?.agreement_signed_lender : !!initialDeal?.agreement_signed_builder);
  const agreementSignedOther = role === "lender" ? !!initialDeal?.agreement_signed_builder : !!initialDeal?.agreement_signed_lender;
  const [signingAgreement,  setSigningAgreement]  = useState(false);
  const [agreementError,    setAgreementError]    = useState("");
  const bothSigned = agreementSignedSelf && agreementSignedOther;
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = width < 700;

  const openDispute = disputes.find(d => d.status === "open");
  const allDocsUploaded = DEAL_DOC_TYPES.every(dt => dealDocs.some(d => d.doc_type === dt.key));
  const legalChargeConfirmed = legalSelf && legalOther;
  const todayStr = new Date().toISOString().split("T")[0];
  const nextRepayment = repayments.filter(r => r.status === "scheduled" || r.status === "missed").sort((a,b) => a.due_date.localeCompare(b.due_date))[0] || null;
  const totalToRepay = repayments.reduce((s, r) => s + Number(r.amount), 0);
  const amountRepaid = repayments.filter(r => r.status === "paid").reduce((s, r) => s + Number(r.amount), 0);
  const hasMissedRep = repayments.some(r => r.status === "missed" || (r.status === "scheduled" && r.due_date < todayStr));

  async function handleDealDocUpload(docType, file) {
    if (!file) return;
    setDocUploading(prev => ({ ...prev, [docType]: "uploading" }));
    const ext = file.name.split(".").pop();
    const path = `deal-docs/${initialDeal.id}/${docType}.${ext}`;
    const { error: uploadErr } = await supabase.storage
      .from("builder-documents")
      .upload(path, file, { upsert: true });
    if (uploadErr) {
      setDocUploading(prev => ({ ...prev, [docType]: "error" }));
      return;
    }
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "upload-deal-doc", deal_id: initialDeal.id, doc_type: docType, file_path: path }),
    });
    if (res.ok) {
      setDealDocs(prev => {
        const filtered = prev.filter(d => d.doc_type !== docType);
        return [...filtered, { doc_type: docType, status: "pending", file_path: path }];
      });
      setDocUploading(prev => ({ ...prev, [docType]: "done" }));
    } else {
      setDocUploading(prev => ({ ...prev, [docType]: "error" }));
    }
  }

  async function handleRaiseDispute() {
    if (!disputeReason.trim()) return;
    setRaisingDispute(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "raise-dispute", deal_id: initialDeal.id, reason: disputeReason }),
    });
    const d = await res.json().catch(() => ({}));
    setRaisingDispute(false);
    if (!res.ok) { setError(d.error || "Failed to raise dispute."); return; }
    setDisputes(prev => [...prev, { status: "open", reason: disputeReason, raised_by_role: role, created_at: new Date().toISOString() }]);
    setShowDisputeForm(false);
    setDisputeReason("");
  }

  async function handleConfirmLegal() {
    setConfirmingLegal(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "confirm-legal", deal_id: initialDeal.id, solicitor_name: solicitorName, solicitor_ref: solicitorRef }),
    });
    const d = await res.json().catch(() => ({}));
    setConfirmingLegal(false);
    if (!res.ok) { setError(d.error || "Failed to confirm."); return; }
    setLegalSelf(true);
  }

  async function handleSaveLegalChecklist(updates) {
    setSavingLegal(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "save-legal-checklist", deal_id: initialDeal.id, ...updates }),
    });
    setSavingLegal(false);
  }

  async function handleConfirmBankDetails() {
    if (!bankAccountName.trim() || !bankSortCode.trim() || !bankAccountNumber.trim()) {
      setError("Please enter your account name, sort code, and account number.");
      return;
    }
    setConfirmingBank(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "confirm-bank-details", account_name: bankAccountName.trim(), sort_code: bankSortCode.trim(), account_number: bankAccountNumber.trim() }),
    });
    const d = await res.json().catch(() => ({}));
    setConfirmingBank(false);
    if (!res.ok) { setError(d.error || "Failed to save bank details."); return; }
    setBankDetailsProvided(true);
  }

  async function handlePhotoUploadForUpdate(file) {
    if (!file) return;
    setUploadingPhoto(true);
    const ext = file.name.split(".").pop();
    const path = `updates/${initialDeal.id}/${Date.now()}.${ext}`;
    const { error: uploadErr } = await supabase.storage.from("milestone-photos").upload(path, file, { upsert: false });
    if (uploadErr) { setUploadingPhoto(false); return; }
    const { data: urlData } = supabase.storage.from("milestone-photos").getPublicUrl(path);
    setUpdatePhotos(prev => [...prev, urlData.publicUrl]);
    setUploadingPhoto(false);
  }

  async function handlePostUpdate(e) {
    e.preventDefault();
    if (!updateBody.trim() || postingUpdate) return;
    setPostingUpdate(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "post-update", deal_id: initialDeal.id, body: updateBody, photo_urls: updatePhotos }),
    });
    const d = await res.json().catch(() => ({}));
    setPostingUpdate(false);
    if (!res.ok) { setError(d.error || "Failed to post update."); return; }
    setProgressUpdates(prev => [d.update, ...prev]);
    setUpdateBody("");
    setUpdatePhotos([]);
  }

  async function handleCompleteTracker() {
    if (completingTracker) return;
    setCompletingTracker(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "complete-tracker", deal_id: initialDeal.id }),
    });
    const d = await res.json().catch(() => ({}));
    setCompletingTracker(false);
    if (!res.ok) { setError(d.error || "Failed."); return; }
    setTrackerSelf(true);
    if (d.both_complete) {
      setTrackerOther(true);
      setTrackerDone(true);
      if (setCelebration) setCelebration({ message: "Project complete!", subtitle: "Both parties have confirmed. Added to builder's portfolio." });
    }
  }

  async function handleChaseRepayment(repaymentId) {
    setChasingRepayment(repaymentId);
    const { data: { session } } = await supabase.auth.getSession();
    await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "chase-repayment", repayment_id: repaymentId }),
    });
    setChasingRepayment(null);
    alert("Reminder sent to builder.");
  }

  async function handleMarkReceived(repaymentId) {
    if (confirmingReceiveDD !== repaymentId) { setConfirmingReceiveDD(repaymentId); return; }
    setConfirmingReceiveDD(null);
    setMarkingReceived(repaymentId);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "mark-repayment-received", repayment_id: repaymentId }),
    });
    setMarkingReceived(null);
    if (res.ok) window.location.reload();
  }

  async function handleMakeRepayment(repaymentId) {
    setMakingRepayment(repaymentId);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "make-repayment", repayment_id: repaymentId }),
    });
    const d = await res.json().catch(() => ({}));
    setMakingRepayment(null);
    if (!res.ok) { setError(d.error || "Failed to start payment."); return; }
    if (d.checkout_url) window.location.href = d.checkout_url;
  }

  async function handleSignAgreement() {
    setSigningAgreement(true);
    setAgreementError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "sign-agreement", deal_id: initialDeal.id }),
    });
    const d = await res.json().catch(() => ({}));
    setSigningAgreement(false);
    if (!res.ok) { setAgreementError(d.error || "Failed to sign."); return; }
    setAgreementSignedSelf(true);
  }

  async function handlePayFinderFee() {
    setFinderFeePaying(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "finder-fee", deal_id: initialDeal.id }),
    });
    const d = await res.json().catch(() => ({}));
    setFinderFeePaying(false);
    if (!res.ok) { setError(d.error || "Failed to start payment."); return; }
    if (d.already_paid) { setFinderFeePaid(true); return; }
    if (d.checkout_url) { window.location.href = d.checkout_url; }
  }

  if (!initialDeal) return null;

  const totalAmount = initialDeal.agreed_amount || 0;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "1.5rem 1.25rem", minHeight: "calc(100vh - 56px)" }}>
      <button
        onClick={() => setPage("my-deals")}
        style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 13, color: "#3B82F6", padding: 0, marginBottom: "1rem", display: "flex", alignItems: "center", gap: 4 }}
      >
        ← Back to my deals
      </button>

      <div style={{ marginBottom: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 6px", fontFamily: "'Georgia', serif" }}>{initialDeal.title}</h1>
          <div style={{ fontSize: 13, color: "#64748B" }}>
            {role === "lender" ? `Builder: ${initialDeal.builder_name}` : `Lender: ${initialDeal.lender_name}`}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: "1.5rem", flexWrap: "wrap" }}>
        {[
          { label: "Deal value",      value: fmt(totalAmount),              bg: "#f5f5f3", col: "#2C3E50" },
          { label: "Updates posted",  value: progressUpdates.length,        bg: "#f5f5f3", col: "#2C3E50" },
          { label: "Status",          value: trackerDone ? "Complete" : "Active", bg: trackerDone ? "#E1F5EE" : "#f5f5f3", col: trackerDone ? "#0F6E56" : "#2C3E50" },
        ].map(({ label, value, bg, col }) => (
          <div key={label} style={{ background: bg, borderRadius: 10, padding: "0.9rem 1.1rem", flex: 1, minWidth: 100 }}>
            <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: col }}>{value}</div>
          </div>
        ))}
      </div>

      {/* LTV Badge */}
      {(() => {
        const pv = initialDeal.property_value;
        if (!pv || !totalAmount) return null;
        const ltv = Math.round((totalAmount / pv) * 100);
        const isHigh = ltv > 80;
        const isMid  = ltv >= 70 && ltv <= 80;
        const bg   = isHigh ? "#FEE2E2" : isMid ? "#FEF3C7" : "#E1F5EE";
        const col  = isHigh ? "#991B1B" : isMid ? "#B45309" : "#0F6E56";
        const border = isHigh ? "#FCA5A5" : isMid ? "#FCD34D" : "#A8DFC9";
        return (
          <div style={{ background: bg, border: `0.5px solid ${border}`, borderRadius: 10, padding: "12px 16px", marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: col }}>
                Loan to Value (LTV): {ltv}%
                <HelpTooltip text="Loan to Value ratio. The percentage of the property value being borrowed. Under 70% is considered low risk." />
              </span>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: col, color: "#fff", fontWeight: 600 }}>
                {ltv < 70 ? "Low risk" : ltv <= 80 ? "Medium risk" : "High risk"}
              </span>
            </div>
            {isHigh && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#991B1B", lineHeight: 1.5 }}>
                High risk — we recommend not lending above 75% of property value. The loan amount exceeds 80% of the estimated property value, which significantly increases your exposure if the builder defaults.
              </p>
            )}
            <div style={{ fontSize: 11, color: col, opacity: 0.75, marginTop: 4 }}>
              Loan {fmt(totalAmount)} ÷ Property {fmt(pv)}
            </div>
          </div>
        );
      })()}

      {/* Open dispute banner */}
      {openDispute && (
        <div style={{ background: "#FEE2E2", border: "0.5px solid #FCA5A5", borderRadius: 10, padding: "12px 16px", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#991B1B", marginBottom: 4 }}>
            Active dispute — deal marked as disputed
          </div>
          <div style={{ fontSize: 12, color: "#991B1B", lineHeight: 1.5 }}>
            <strong>Raised by:</strong> {openDispute.raised_by_name || openDispute.raised_by_role}<br />
            <strong>Reason:</strong> {openDispute.reason}
          </div>
          <div style={{ fontSize: 12, color: "#991B1B", marginTop: 6 }}>
            Resolution is between you, the other party, and your respective solicitors or a mediator. LenderBuild does not adjudicate disputes or control funds. For general help, visit the <button onClick={() => setPage("help")} style={{ background: "none", border: "none", cursor: "pointer", color: "#991B1B", fontWeight: 600, padding: 0, fontSize: "inherit", textDecoration: "underline" }}>Help Centre</button>.
          </div>
        </div>
      )}

      {/* Default flag banner */}
      {initialDeal.flagged_default && (
        <div style={{ background: "#FEE2E2", border: "0.5px solid #FCA5A5", borderRadius: 10, padding: "12px 16px", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#991B1B" }}>
            This deal has been flagged as in default
          </div>
          <div style={{ fontSize: 12, color: "#991B1B", marginTop: 4 }}>
            This deal has been flagged as in default. Our admin team is reviewing this deal.
          </div>
        </div>
      )}

      {/* Finder's fee — lenders only */}
      {role === "lender" && (
        finderFeePaid ? (
          <div style={{ background: "#E1F5EE", border: "0.5px solid #A8DFC9", borderRadius: 10, padding: "12px 16px", marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: 10 }}>
            <Icon name="check-circle" size={16} style={{color:"#0F6E56",flexShrink:0}} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F6E56" }}>Finder's fee paid</div>
              <div style={{ fontSize: 12, color: "#0F6E56", opacity: 0.8 }}>Finder's fee settled — thank you.</div>
            </div>
          </div>
        ) : (
          <div style={{ background: "#EBF2FF", border: "0.5px solid #C3D9FF", borderRadius: 10, padding: "14px 16px", marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 2 }}>
                  Finder's fee required — {fmt(Math.round(totalAmount * 0.01))}
                  <HelpTooltip text="A 1% fee charged when a deal is confirmed. Only paid when both parties successfully connect and agree terms." />
                </div>
                <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
                  A 1% finder's fee ({fmt(Math.round(totalAmount * 0.01))}) is payable to LenderBuild for introducing you to this builder. This is how LenderBuild earns its revenue.
                </div>
              </div>
              <button
                onClick={handlePayFinderFee}
                disabled={finderFeePaying}
                style={{
                  padding: "8px 18px", minHeight: 44, flexShrink: 0,
                  background: finderFeePaying ? "#aaa" : "#3B82F6",
                  color: "#fff", border: "none", borderRadius: 8,
                  fontSize: 13, fontWeight: 600,
                  cursor: finderFeePaying ? "default" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {finderFeePaying ? "Redirecting…" : "Pay finder's fee"}
              </button>
            </div>
          </div>
        )
      )}

      {/* Legal warning — lenders only */}
      {role === "lender" && (
        <div style={{ background: "#FFFBEB", border: "1px solid #F59E0B", borderRadius: 10, padding: "14px 16px", marginBottom: "1.25rem", display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Icon name="warning" size={18} className="lb-icon" />
          <p style={{ margin: 0, fontSize: 13, color: "#78350F", lineHeight: 1.6 }}>
            <strong>Important:</strong> Before releasing any funds, we strongly recommend instructing a solicitor to register a legal charge against the property. This protects your investment if the builder defaults. LenderBuild is an introduction platform only and does not provide financial advice.
          </p>
        </div>
      )}

      {/* Required documents checklist — builders upload, lenders see status */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Required documents</div>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12, lineHeight: 1.5 }}>
          Both parties should review these documents and consult their solicitors before releasing any funds. LenderBuild does not approve, hold, or release funds.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {DEAL_DOC_TYPES.map(dt => {
            const doc = dealDocs.find(d => d.doc_type === dt.key);
            const upStatus = docUploading[dt.key];
            const isUploaded = !!doc;
            const statusColor = isUploaded ? "#0F6E56" : "#64748B";
            const statusBg    = isUploaded ? "#E1F5EE" : "#f5f5f3";
            const statusLabel = isUploaded ? "Uploaded ✓" : "Not uploaded";
            return (
              <div key={dt.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", borderRadius: 8, background: statusBg, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: statusColor }}>{dt.label}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: statusColor }}>{statusLabel}</span>
                  {role === "builder" && (
                    <label style={{ display: "inline-flex", alignItems: "center", padding: "5px 12px", minHeight: 36, background: "#3B82F6", color: "#fff", borderRadius: 7, fontSize: 12, cursor: upStatus === "uploading" ? "default" : "pointer", fontWeight: 500 }}>
                      {upStatus === "uploading" ? "Uploading…" : isUploaded ? "Re-upload" : "Upload"}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }}
                        onChange={e => handleDealDocUpload(dt.key, e.target.files[0])}
                        disabled={upStatus === "uploading"}
                      />
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {allDocsUploaded && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#0F6E56", fontWeight: 500 }}>All documents uploaded — both parties should review them before proceeding.</div>
        )}
      </div>

      {/* Legal protection checklist */}
      {(() => {
        const step1Done = bothSigned;
        const step2Done = solicitorInstructed;
        const step3Done = chargeRegistered;
        const step4Done = bankDetailsProvided;
        const stepsComplete = [step1Done, step2Done, step3Done, step4Done].filter(Boolean).length;
        const allDone = stepsComplete === 4;
        const inp = { height: 36, border: "0.5px solid #ccc", borderRadius: 7, padding: "0 10px", fontSize: 13, background: "#fff", width: "100%", boxSizing: "border-box" };
        const yesNoBtn = (active, onClick, label) => (
          <button onClick={onClick} style={{ padding: "5px 14px", borderRadius: 7, border: active ? "none" : "0.5px solid #ccc", background: active ? "#0F6E56" : "#f5f5f3", color: active ? "#fff" : "#555", fontSize: 12, fontWeight: 600, cursor: "pointer", minHeight: 32 }}>{label}</button>
        );
        return (
          <div style={{ background: "#fff", border: allDone ? "0.5px solid #A8DFC9" : "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Legal protection checklist</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: allDone ? "#0F6E56" : "#B45309", background: allDone ? "#E1F5EE" : "#FEF3C7", padding: "3px 12px", borderRadius: 20 }}>
                {stepsComplete} of 4 steps complete
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 14, lineHeight: 1.5 }}>
              Track your legal protection steps. We strongly recommend completing all four before any funds move, but this is for your own records only — LenderBuild does not gate or enforce anything.
            </div>

            {/* Step 1: Agreement self-reported */}
            <div style={{ padding: "12px 0", borderBottom: "0.5px solid #f0f0f0", display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: step1Done ? "#0F6E56" : "#e0e0e0", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{step1Done ? "✓" : "1"}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Both parties have a solicitor-prepared loan agreement</div>
                <div style={{ fontSize: 12, color: step1Done ? "#0F6E56" : "#64748B", marginBottom: agreementSignedSelf ? 0 : 8 }}>
                  {`${agreementSignedSelf ? "✓ You reported signing" : "You: not yet reported"} · ${agreementSignedOther ? `✓ ${role === "lender" ? "Builder" : "Lender"} reported signing` : `${role === "lender" ? "Builder" : "Lender"}: not yet reported`}`}
                </div>
                {!agreementSignedSelf && (
                  <button
                    disabled={signingAgreement}
                    onClick={handleSignAgreement}
                    style={{ padding: "5px 14px", borderRadius: 7, border: "none", background: signingAgreement ? "#aaa" : "#1E3A5F", color: "#fff", fontSize: 12, fontWeight: 600, cursor: signingAgreement ? "default" : "pointer", minHeight: 32 }}
                  >
                    {signingAgreement ? "Saving…" : "I have signed a loan agreement with my solicitor"}
                  </button>
                )}
                {agreementError && <div style={{ fontSize: 12, color: "#991B1B", marginTop: 4 }}>{agreementError}</div>}
              </div>
            </div>

            {/* Step 2: Solicitor instructed */}
            <div style={{ padding: "12px 0", borderBottom: "0.5px solid #f0f0f0" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: step2Done || role === "lender" ? 10 : 0 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: step2Done ? "#0F6E56" : "#e0e0e0", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{step2Done ? "✓" : "2"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Has a solicitor been instructed?</div>
                  {role === "lender" && (
                    <div style={{ display: "flex", gap: 8, marginBottom: step2Done ? 10 : 0 }}>
                      {yesNoBtn(solicitorInstructed, () => { setSolicitorInstructed(true); handleSaveLegalChecklist({ solicitor_instructed: true }); }, "Yes")}
                      {yesNoBtn(!solicitorInstructed, () => { setSolicitorInstructed(false); handleSaveLegalChecklist({ solicitor_instructed: false }); }, "No")}
                    </div>
                  )}
                  {role !== "lender" && <div style={{ fontSize: 12, color: step2Done ? "#0F6E56" : "#64748B" }}>{step2Done ? "Lender confirmed." : "Awaiting lender confirmation."}</div>}
                </div>
              </div>
              {solicitorInstructed && role === "lender" && (
                <div style={{ marginLeft: 34, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <label style={{ fontSize: 11, color: "#555", fontWeight: 500, display: "block", marginBottom: 3 }}>Solicitor name</label>
                    <input value={solicitorName} onChange={e => setSolicitorName(e.target.value)} onBlur={() => handleSaveLegalChecklist({ solicitor_name: solicitorName })} placeholder="e.g. Jane Smith" style={inp} />
                  </div>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <label style={{ fontSize: 11, color: "#555", fontWeight: 500, display: "block", marginBottom: 3 }}>Firm</label>
                    <input value={solicitorFirm} onChange={e => setSolicitorFirm(e.target.value)} onBlur={() => handleSaveLegalChecklist({ solicitor_firm: solicitorFirm })} placeholder="e.g. Smith & Co Solicitors" style={inp} />
                  </div>
                </div>
              )}
              {solicitorInstructed && role !== "lender" && (initialDeal?.legal_solicitor_name || initialDeal?.legal_solicitor_firm) && (
                <div style={{ marginLeft: 34, fontSize: 12, color: "#555" }}>
                  {initialDeal.legal_solicitor_name}{initialDeal.legal_solicitor_firm ? ` · ${initialDeal.legal_solicitor_firm}` : ""}
                </div>
              )}
            </div>

            {/* Step 3: Legal charge registered */}
            <div style={{ padding: "12px 0", borderBottom: "0.5px solid #f0f0f0" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: step3Done || role === "lender" ? 10 : 0 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: step3Done ? "#0F6E56" : "#e0e0e0", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{step3Done ? "✓" : "3"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                    Has a legal charge been registered at the Land Registry?
                    <HelpTooltip text="A legal claim registered against the property at the Land Registry. Protects the lender if the builder defaults." />
                  </div>
                  {role === "lender" && (
                    <>
                      <div style={{ display: "flex", gap: 8, marginBottom: step3Done ? 10 : 0 }}>
                        {yesNoBtn(chargeRegistered, () => { setChargeRegistered(true); handleSaveLegalChecklist({ charge_registered: true }); }, "Yes")}
                        {yesNoBtn(!chargeRegistered, () => { setChargeRegistered(false); handleSaveLegalChecklist({ charge_registered: false }); }, "No")}
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <a href="https://www.gov.uk/search-property-information-land-registry" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "var(--accent)", textDecoration: "underline", cursor: "pointer" }}>
                          Verify this charge — £3 (via HM Land Registry) ↗
                        </a>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3, lineHeight: 1.5 }}>
                          LenderBuild does not verify registered charges. Search HM Land Registry directly to confirm — you'll pay Land Registry's £3 fee, not LenderBuild.
                        </div>
                      </div>
                    </>
                  )}
                  {role !== "lender" && <div style={{ fontSize: 12, color: step3Done ? "#0F6E56" : "#64748B" }}>{step3Done ? "Lender confirmed." : "Awaiting lender confirmation."}</div>}
                </div>
              </div>
              {chargeRegistered && role === "lender" && (
                <div style={{ marginLeft: 34 }}>
                  <label style={{ fontSize: 11, color: "#555", fontWeight: 500, display: "block", marginBottom: 3 }}>Land Registry charge reference number</label>
                  <input value={chargeRef} onChange={e => setChargeRef(e.target.value)} onBlur={() => handleSaveLegalChecklist({ charge_ref: chargeRef })} placeholder="e.g. HM-2024-123456" style={{ ...inp, maxWidth: 260 }} />
                </div>
              )}
              {chargeRegistered && role !== "lender" && initialDeal?.legal_charge_ref && (
                <div style={{ marginLeft: 34, fontSize: 12, color: "#555" }}>Ref: {initialDeal.legal_charge_ref}</div>
              )}
              {role === "lender" && !chargeRegistered && (
                <div style={{ marginLeft: 34, marginTop: 6 }}>
                  <button onClick={() => setPage("legal-resources")} style={{ fontSize: 12, color: "#2E5FA3", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                    How to register a legal charge →
                  </button>
                </div>
              )}
            </div>

            {/* Step 4: Builder bank details */}
            <div style={{ padding: "12px 0" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: step4Done ? "#0F6E56" : "#e0e0e0", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{step4Done ? "✓" : "4"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Builder's bank details for payment</div>
                  {step4Done
                    ? <div style={{ fontSize: 12, color: "#0F6E56" }}>Bank details saved. The lender will see these when transferring payments.</div>
                    : role === "builder"
                    ? (
                      <div>
                        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>Enter the bank details where milestone payments should be sent.</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 340 }}>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 500, color: "#555", display: "block", marginBottom: 3 }}>Account name (full name on account)</label>
                            <input value={bankAccountName} onChange={e => setBankAccountName(e.target.value)} placeholder="e.g. John Smith" style={{ width: "100%", height: 36, border: "0.5px solid #ccc", borderRadius: 7, padding: "0 10px", fontSize: 13, boxSizing: "border-box" }} />
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: 11, fontWeight: 500, color: "#555", display: "block", marginBottom: 3 }}>Sort code</label>
                              <input value={bankSortCode} onChange={e => setBankSortCode(e.target.value)} placeholder="e.g. 20-00-00" maxLength={8} style={{ width: "100%", height: 36, border: "0.5px solid #ccc", borderRadius: 7, padding: "0 10px", fontSize: 13, boxSizing: "border-box" }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: 11, fontWeight: 500, color: "#555", display: "block", marginBottom: 3 }}>Account number</label>
                              <input value={bankAccountNumber} onChange={e => setBankAccountNumber(e.target.value)} placeholder="e.g. 12345678" maxLength={8} style={{ width: "100%", height: 36, border: "0.5px solid #ccc", borderRadius: 7, padding: "0 10px", fontSize: 13, boxSizing: "border-box" }} />
                            </div>
                          </div>
                        </div>
                        <button onClick={handleConfirmBankDetails} disabled={confirmingBank} style={{ marginTop: 10, padding: "6px 16px", background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: confirmingBank ? "default" : "pointer", minHeight: 32 }}>
                          {confirmingBank ? "Saving…" : "Save bank details"}
                        </button>
                      </div>
                    )
                    : <div style={{ fontSize: 12, color: "#64748B" }}>Waiting for builder to add their bank details.</div>
                  }
                </div>
              </div>
            </div>

            {allDone && (
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#E1F5EE", borderRadius: 8, fontSize: 13, color: "#0F6E56", fontWeight: 500 }}>
                All legal protection steps are complete. You are well positioned to proceed with payments directly to the builder.
              </div>
            )}
          </div>
        );
      })()}

      {/* Repayment schedule */}
      {initialDeal.return_type && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
            {initialDeal.return_type === "fixed_interest" ? "Repayment schedule — fixed interest" : initialDeal.return_type === "rental_split" ? "Repayment schedule — rental split" : "Return details — equity stake"}
          </div>

          {initialDeal.return_type === "fixed_interest" && (() => {
            const principal = initialDeal.milestones?.reduce((s, m) => s + Number(m.amount), 0) || 0;
            const r = (initialDeal.interest_rate || 0) / 100 / 12;
            const n = initialDeal.loan_term_months || 1;
            const monthly = r === 0 ? principal / n : principal * r / (1 - Math.pow(1 + r, -n));
            const totalRepay = monthly * n;
            const totalInterest = totalRepay - principal;
            return (
              <div>
                <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                  {[
                    { label: "Monthly payment", value: fmt(Math.round(monthly)) },
                    { label: "Total repayments", value: fmt(Math.round(totalRepay)) },
                    { label: "Total interest", value: fmt(Math.round(totalInterest)) },
                    { label: "Term", value: `${n} months` },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: "#f5f5f3", borderRadius: 8, padding: "10px 14px", flex: 1, minWidth: 100 }}>
                      <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#2C3E50" }}>{value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: "#64748B" }}>Interest rate: {initialDeal.interest_rate}% p.a.</div>
              </div>
            );
          })()}

          {initialDeal.return_type === "rental_split" && (() => {
            const monthly = Math.round((initialDeal.estimated_monthly_rental || 0) * (initialDeal.rental_split_pct || 0) / 100);
            const principal = initialDeal.milestones?.reduce((s, m) => s + Number(m.amount), 0) || 0;
            const months = monthly > 0 ? Math.ceil(principal / monthly) : 0;
            return (
              <div>
                <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                  {[
                    { label: "Monthly income to lender", value: fmt(monthly) },
                    { label: "Lender's share", value: `${initialDeal.rental_split_pct}%` },
                    { label: "Est. monthly rental", value: fmt(initialDeal.estimated_monthly_rental || 0) },
                    { label: "Est. repayment timeline", value: months > 0 ? `${months} months` : "—" },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: "#f5f5f3", borderRadius: 8, padding: "10px 14px", flex: 1, minWidth: 100 }}>
                      <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#2C3E50" }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {initialDeal.return_type === "equity_stake" && (() => {
            const propVal = initialDeal.property_value || 0;
            const projected = propVal * (initialDeal.equity_pct || 0) / 100;
            return (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {[
                  { label: "Equity stake", value: `${initialDeal.equity_pct || 0}%` },
                  { label: "Est. property value", value: propVal ? fmt(propVal) : "Not set" },
                  { label: "Projected payout on sale", value: projected ? fmt(Math.round(projected)) : "—" },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: "#f5f5f3", borderRadius: 8, padding: "10px 14px", flex: 1, minWidth: 100 }}>
                    <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#2C3E50" }}>{value}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Repayment tracker */}
      {repayments.length > 0 && (
        <div style={{ background: "#fff", border: hasMissedRep ? "0.5px solid #FCD34D" : "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              Repayment tracker
              {hasMissedRep && <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#FEF3C7", color: "#B45309" }}>Missed payment</span>}
            </div>
            {role === "builder" && nextRepayment && (
              <button
                onClick={() => handleMakeRepayment(nextRepayment.id)}
                disabled={!!makingRepayment}
                style={{ padding: "8px 18px", minHeight: 40, background: makingRepayment ? "#aaa" : (hasMissedRep ? "#991B1B" : "#3B82F6"), color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: makingRepayment ? "default" : "pointer", flexShrink: 0 }}
              >
                {makingRepayment ? "Redirecting…" : `Make repayment — ${fmt(nextRepayment.amount)}`}
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
            {[
              { label: "Total to repay", value: fmt(Math.round(totalToRepay)), col: "#2C3E50", bg: "#f5f5f3" },
              { label: "Repaid so far", value: fmt(Math.round(amountRepaid)), col: "#0F6E56", bg: "#E1F5EE" },
              { label: "Remaining", value: fmt(Math.round(totalToRepay - amountRepaid)), col: "#2C3E50", bg: "#f5f5f3" },
              ...(nextRepayment ? [{ label: "Next due", value: nextRepayment.due_date, col: hasMissedRep ? "#B45309" : "#2C3E50", bg: hasMissedRep ? "#FEF3C7" : "#f5f5f3" }] : []),
            ].map(({ label, value, col, bg }) => (
              <div key={label} style={{ background: bg, borderRadius: 8, padding: "10px 14px", flex: 1, minWidth: 90 }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: col }}>{value}</div>
              </div>
            ))}
          </div>
          {totalToRepay > 0 && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#aaa", marginBottom: 4 }}>
                <span>{Math.round((amountRepaid / totalToRepay) * 100)}% repaid</span>
                <span>{repayments.filter(r => r.status === "paid").length}/{repayments.length} payments</span>
              </div>
              <div style={{ height: 8, background: "#eee", borderRadius: 4 }}>
                <div style={{ height: 8, borderRadius: 4, background: "#0F6E56", width: `${Math.min(100, Math.round((amountRepaid / totalToRepay) * 100))}%`, transition: "width 0.4s" }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Repayment history table */}
      {repayments.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Repayment history</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "0.5px solid #f0f0f0" }}>
                  {["#", "Due date", "Amount", "Status", "Paid on", "Ref"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, fontWeight: 600, color: "#aaa", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {repayments.map(r => {
                  const isMissed = r.status === "missed" || (r.status === "scheduled" && r.due_date < todayStr);
                  const statusCfg = {
                    paid: ["#E1F5EE","#0F6E56","Paid"],
                    missed: ["#FEE2E2","#991B1B","Missed"],
                    processing: ["#EEEDFE","#534AB7","Processing"],
                    scheduled: ["#f5f5f3","#64748B","Scheduled"],
                  };
                  const [sbg, scol, slabel] = isMissed ? ["#FEF3C7","#B45309","Missed"] : (statusCfg[r.status] || statusCfg.scheduled);
                  return (
                    <tr key={r.id} style={{ borderBottom: "0.5px solid #f9f9f7" }}>
                      <td style={{ padding: "8px 10px", color: "#aaa" }}>{r.payment_index}</td>
                      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{r.due_date}</td>
                      <td style={{ padding: "8px 10px", fontWeight: 500 }}>{fmt(r.amount)}</td>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: sbg, color: scol }}>{slabel}</span>
                      </td>
                      <td style={{ padding: "8px 10px", color: "#64748B", whiteSpace: "nowrap" }}>
                        {r.paid_at ? new Date(r.paid_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#aaa", fontSize: 11, whiteSpace: "nowrap" }}>{r.confirmation_number || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Raise a dispute */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Dispute resolution</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>If there's a problem with this deal, you can mark it as disputed for your own records. Resolution is between you, the other party, and your respective solicitors or a mediator. LenderBuild does not adjudicate disputes or control funds.</div>
          </div>
          {!openDispute && (
            <button
              onClick={() => setShowDisputeForm(v => !v)}
              style={{ padding: "7px 16px", minHeight: 38, background: "transparent", border: "0.5px solid #FCA5A5", color: "#991B1B", borderRadius: 8, fontSize: 13, cursor: "pointer", flexShrink: 0 }}
            >
              Raise a dispute
            </button>
          )}
        </div>
        {showDisputeForm && !openDispute && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "0.5px solid #f0f0f0" }}>
            <textarea
              value={disputeReason}
              onChange={e => setDisputeReason(e.target.value)}
              placeholder="Describe the issue in detail…"
              style={{ width: "100%", minHeight: 80, padding: "10px 12px", fontSize: 13, border: "0.5px solid #ccc", borderRadius: 8, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                onClick={handleRaiseDispute}
                disabled={raisingDispute || !disputeReason.trim()}
                style={{ padding: "7px 18px", minHeight: 38, background: raisingDispute ? "#aaa" : "#991B1B", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: raisingDispute ? "default" : "pointer" }}
              >
                {raisingDispute ? "Submitting…" : "Submit dispute"}
              </button>
              <button onClick={() => { setShowDisputeForm(false); setDisputeReason(""); }}
                style={{ padding: "7px 14px", minHeight: 38, background: "transparent", border: "0.5px solid #e0e0e0", color: "#555", borderRadius: 8, fontSize: 13, cursor: "pointer" }}
              >Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Emergency contact */}
      <div style={{ background: "#EBF2FF", border: "0.5px solid #C3D9FF", borderRadius: 12, padding: "1rem 1.25rem", marginBottom: "1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 2 }}>Need urgent help?</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>Contact our team directly for any urgent issues with this project.</div>
        </div>
        <button
          onClick={() => setPage("help")}
          style={{ padding: "8px 16px", minHeight: 38, background: "#1E3A5F", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer", flexShrink: 0 }}
        >
          Report a problem with this project
        </button>
      </div>

      {error && (
        <div style={{ background: "#FAECE7", color: "#993C1D", border: "0.5px solid #F5C9BB", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem" }}>
          {error}
        </div>
      )}


      {/* Progress Log header */}
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 10 }}>Progress Log</div>

      {/* Progress Log — updates timeline */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: "1.5rem" }}>
        {trackerDone && (
          <div style={{ background: "#E1F5EE", border: "0.5px solid #A8DFC9", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#16A34A"/><path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0F6E56" }}>Project complete — both parties have confirmed.</div>
          </div>
        )}
        {!trackerDone && trackerSelf && (
          <div style={{ background: "#FEF3C7", border: "0.5px solid #FCD34D", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#92400E" }}>
            You've marked this project complete. Waiting for the {role === "builder" ? "lender" : "builder"} to confirm.
          </div>
        )}
        {!trackerDone && !trackerSelf && (
          <button
            onClick={handleCompleteTracker}
            disabled={completingTracker}
            style={{ alignSelf: "flex-start", padding: "8px 20px", minHeight: 40, background: completingTracker ? "#aaa" : "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: completingTracker ? "default" : "pointer" }}
          >
            {completingTracker ? "Saving…" : "Mark project as complete"}
          </button>
        )}

        {role === "builder" && !trackerDone && (
          <form onSubmit={handlePostUpdate} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F" }}>Post a progress update</div>
            <textarea
              value={updateBody}
              onChange={e => setUpdateBody(e.target.value)}
              placeholder="Describe what's been completed, any issues, or next steps…"
              rows={3}
              style={{ width: "100%", border: "0.5px solid #ccc", borderRadius: 8, padding: "10px 12px", fontSize: 13, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, color: "#3B82F6", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => handlePhotoUploadForUpdate(e.target.files[0])} disabled={uploadingPhoto} />
                {uploadingPhoto ? "Uploading…" : "+ Add photo"}
              </label>
              {updatePhotos.map((url, i) => (
                <div key={i} style={{ position: "relative" }}>
                  <img src={url} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: "0.5px solid #e0e0e0" }} />
                  <button type="button" onClick={() => setUpdatePhotos(prev => prev.filter((_, j) => j !== i))} style={{ position: "absolute", top: -6, right: -6, width: 16, height: 16, borderRadius: "50%", background: "#ef4444", color: "#fff", border: "none", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                </div>
              ))}
              <button type="submit" disabled={!updateBody.trim() || postingUpdate} style={{ marginLeft: "auto", padding: "7px 18px", minHeight: 36, background: updateBody.trim() && !postingUpdate ? "#3B82F6" : "#D1D5DB", color: updateBody.trim() && !postingUpdate ? "#fff" : "#9CA3AF", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: updateBody.trim() && !postingUpdate ? "pointer" : "default" }}>
                {postingUpdate ? "Posting…" : "Post update"}
              </button>
            </div>
          </form>
        )}

        {progressUpdates.length === 0 && (
          <div style={{ background: "#f9f9f9", border: "0.5px solid #e0e0e0", borderRadius: 10, padding: "1.5rem", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
            No progress updates yet.{role === "builder" ? " Use the form above to post your first update." : " The builder will post updates here as work progresses."}
          </div>
        )}
        {progressUpdates.map(u => (
          <div key={u.id} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1rem 1.25rem" }}>
            <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 6 }}>{new Date(u.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
            <div style={{ fontSize: 14, color: "#1E293B", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{u.body}</div>
            {u.photo_urls?.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {u.photo_urls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt="Progress" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, border: "0.5px solid #e0e0e0" }} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LEGAL PAGES ──────────────────────────────────────────────────────────────

function LegalPageWrapper({ title, children, setPage }) {
  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "2rem 1.25rem", minHeight: "calc(100vh - 56px)" }}>
      <button
        onClick={() => setPage("home")}
        style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 13, color: "#3B82F6", padding: 0, marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: 4 }}
      >
        ← Back to home
      </button>
      <h1 style={{ fontSize: 26, fontWeight: 500, margin: "0 0 0.25rem", fontFamily: "'Georgia', serif" }}>{title}</h1>
      <p style={{ fontSize: 12, color: "#aaa", margin: "0 0 2rem" }}>Last updated: April 2025</p>
      <div style={{ fontSize: 14, lineHeight: 1.75, color: "#333" }}>
        {children}
      </div>
    </div>
  );
}

function H({ children }) {
  return <h2 style={{ fontSize: 17, fontWeight: 600, margin: "1.75rem 0 0.5rem", fontFamily: "'Georgia', serif" }}>{children}</h2>;
}
function P({ children }) {
  return <p style={{ margin: "0 0 1rem" }}>{children}</p>;
}
function UL({ children }) {
  return <ul style={{ margin: "0 0 1rem", paddingLeft: "1.5rem" }}>{children}</ul>;
}
function LI({ children }) {
  return <li style={{ marginBottom: "0.4rem" }}>{children}</li>;
}

function PrivacyPolicyPage({ setPage }) {
  return (
    <LegalPageWrapper title="Privacy Policy" setPage={setPage}>
      <P>LenderBuild (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is committed to protecting and respecting your privacy. This policy explains how we collect, use, and safeguard your personal data when you use our platform at lenderbuild.co.uk.</P>

      <H>1. Who we are</H>
      <P>LenderBuild is a property investment matching platform based in the United Kingdom. We connect property developers and builders with private lenders and investors. We are the data controller for personal data collected through this platform.</P>

      <H>2. What data we collect</H>
      <P>We may collect the following personal data:</P>
      <UL>
        <LI>Identity data: name, username</LI>
        <LI>Contact data: email address</LI>
        <LI>Profile data: location, professional biography, specialisation, investment preferences</LI>
        <LI>Verification documents: photo ID, selfie with ID, site photos, planning documents (builders only)</LI>
        <LI>Transaction data: information about deals and milestone payments processed through our platform</LI>
        <LI>Technical data: IP address, browser type, device information, usage data</LI>
        <LI>Communications: messages exchanged with other users on the platform</LI>
      </UL>

      <H>3. How we use your data</H>
      <P>We use your personal data to:</P>
      <UL>
        <LI>Provide and maintain the LenderBuild platform</LI>
        <LI>Match lenders with builders and facilitate connections</LI>
        <LI>Verify the identity of builders who upload verification documents</LI>
        <LI>Process milestone payments via Stripe</LI>
        <LI>Send transactional email notifications (connection requests, messages, milestone updates)</LI>
        <LI>Ensure platform security and prevent fraud</LI>
        <LI>Comply with our legal obligations</LI>
      </UL>

      <H>4. Legal basis for processing</H>
      <P>We process your personal data under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018 on the following bases:</P>
      <UL>
        <LI><strong>Contract performance:</strong> to provide our service to you</LI>
        <LI><strong>Legitimate interests:</strong> to improve our platform, prevent fraud, and maintain security</LI>
        <LI><strong>Consent:</strong> for verification document uploads and marketing communications</LI>
        <LI><strong>Legal obligation:</strong> to comply with applicable law</LI>
      </UL>

      <H>5. Data sharing</H>
      <P>We share your data with:</P>
      <UL>
        <LI><strong>Supabase:</strong> our database and authentication provider (data hosted in the EU)</LI>
        <LI><strong>Stripe:</strong> our payment processor for milestone payments</LI>
        <LI><strong>Resend:</strong> our email delivery provider for transactional emails</LI>
        <LI><strong>Vercel:</strong> our hosting provider</LI>
      </UL>
      <P>We do not sell your personal data to third parties. We only share data with our service providers as necessary to operate the platform.</P>

      <H>6. Data retention</H>
      <P>We retain your personal data for as long as your account is active or as needed to provide our services. If you delete your account, we will delete or anonymise your data within 30 days, except where we are required to retain it for legal or regulatory purposes.</P>

      <H>7. Your rights</H>
      <P>Under UK GDPR, you have the right to:</P>
      <UL>
        <LI>Access your personal data</LI>
        <LI>Correct inaccurate personal data</LI>
        <LI>Request erasure of your personal data</LI>
        <LI>Restrict or object to processing</LI>
        <LI>Data portability</LI>
        <LI>Withdraw consent at any time</LI>
      </UL>
      <P>To exercise your rights, please contact us at privacy@lenderbuild.co.uk. You also have the right to lodge a complaint with the Information Commissioner&apos;s Office (ICO) at ico.org.uk.</P>

      <H>8. Cookies</H>
      <P>We use essential cookies to maintain your session. We do not currently use analytics or advertising cookies. You can control cookies through your browser settings.</P>

      <H>9. Security</H>
      <P>We implement appropriate technical and organisational measures to protect your personal data, including encrypted storage, access controls, and rate limiting. However, no method of transmission over the internet is completely secure.</P>

      <H>10. Changes to this policy</H>
      <P>We may update this privacy policy from time to time. We will notify you of significant changes by email or by posting a notice on the platform. Continued use of the platform after any changes constitutes your acceptance of the updated policy.</P>

      <H>11. Contact us</H>
      <P>If you have any questions about this privacy policy or our data practices, please contact us at privacy@lenderbuild.co.uk.</P>
    </LegalPageWrapper>
  );
}

function TermsPage({ setPage }) {
  return (
    <LegalPageWrapper title="Terms &amp; Conditions" setPage={setPage}>
      <P>Please read these Terms and Conditions carefully before using LenderBuild. By registering an account or using our platform, you agree to be bound by these terms. If you do not agree, please do not use our platform.</P>

      <H>1. About LenderBuild</H>
      <P>LenderBuild is a property investment matching platform that connects property developers and builders with private lenders and investors in the United Kingdom. We are not a regulated financial institution and do not provide financial advice, arrange regulated mortgage contracts, or manage investments.</P>

      <H>2. Eligibility</H>
      <P>To use LenderBuild, you must:</P>
      <UL>
        <LI>Be at least 18 years of age</LI>
        <LI>Be based in the United Kingdom</LI>
        <LI>Have the legal capacity to enter into binding contracts</LI>
        <LI>Provide accurate and truthful information</LI>
      </UL>

      <H>3. Account registration</H>
      <P>You are responsible for maintaining the confidentiality of your account credentials. You must notify us immediately of any unauthorised use of your account. You are responsible for all activity that occurs under your account.</P>

      <H>4. Platform use</H>
      <P>LenderBuild provides a marketplace for lenders and builders to find each other and agree terms. We do not:</P>
      <UL>
        <LI>Guarantee the performance or conduct of any user</LI>
        <LI>Verify the accuracy of information provided by users (beyond optional document uploads)</LI>
        <LI>Act as a party to any deal or investment agreement between users</LI>
        <LI>Provide legal, financial, or tax advice</LI>
      </UL>

      <H>5. Milestone payments</H>
      <P>The milestone payment feature allows lenders to set up structured payments for agreed work stages. Payments are processed through Stripe. LenderBuild charges a platform fee on each transaction. Once a milestone payment has been released, it cannot be reversed through our platform. Any disputes between lenders and builders regarding work quality or payment are the responsibility of the parties involved.</P>

      <H>6. Prohibited conduct</H>
      <P>You must not use LenderBuild to:</P>
      <UL>
        <LI>Provide false, misleading, or fraudulent information</LI>
        <LI>Harass, threaten, or abuse other users</LI>
        <LI>Engage in money laundering or other illegal financial activity</LI>
        <LI>Circumvent our platform to avoid fees</LI>
        <LI>Scrape, copy, or redistribute content without our permission</LI>
        <LI>Attempt to gain unauthorised access to our systems</LI>
      </UL>

      <H>7. Finder&apos;s fee</H>
      <P>A 1% finder&apos;s fee is charged on deals facilitated through the platform. This fee is payable by the party initiating the payment. The fee covers platform operation, security, and ongoing maintenance.</P>

      <H>8. Limitation of liability</H>
      <P>To the maximum extent permitted by law, LenderBuild shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the platform, including loss of profits, loss of investment, or loss of data. Our total liability to you shall not exceed the fees you have paid to us in the preceding 12 months.</P>
      <P>Nothing in these terms limits our liability for death or personal injury caused by our negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded under English law.</P>

      <H>9. Intellectual property</H>
      <P>All content on LenderBuild, including text, graphics, logos, and software, is owned by or licensed to LenderBuild and is protected by UK and international intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.</P>

      <H>10. Termination</H>
      <P>We may suspend or terminate your account if you breach these terms, engage in fraudulent activity, or if we decide to discontinue the platform. You may close your account at any time by contacting us.</P>

      <H>11. Governing law</H>
      <P>These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</P>

      <H>12. Changes to these terms</H>
      <P>We may update these terms from time to time. We will notify you of material changes by email. Continued use of the platform after changes constitutes acceptance of the updated terms.</P>

      <H>13. Contact</H>
      <P>For questions about these terms, please contact us at legal@lenderbuild.co.uk.</P>
    </LegalPageWrapper>
  );
}

function RiskWarningPage({ setPage }) {
  return (
    <LegalPageWrapper title="Risk Warning" setPage={setPage}>
      <div style={{ background: "#FAECE7", border: "0.5px solid #F5C9BB", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#993C1D", marginBottom: 6 }}>Important: Please read this risk warning in full before using LenderBuild.</div>
        <div style={{ fontSize: 13, color: "#993C1D", lineHeight: 1.6 }}>Property investment carries significant risks, including loss of capital. Past performance is not a reliable indicator of future results.</div>
      </div>

      <H>1. Not regulated financial advice</H>
      <P>LenderBuild is a matching platform only. We are not authorised or regulated by the Financial Conduct Authority (FCA). Nothing on this platform constitutes financial advice, investment advice, or a recommendation to invest. You should seek independent financial and legal advice before committing any funds to a property investment.</P>

      <H>2. Risk of capital loss</H>
      <P>Property lending and investment involves significant risks:</P>
      <UL>
        <LI><strong>You could lose some or all of your invested capital.</strong></LI>
        <LI>Property values can fall as well as rise. There is no guarantee that a project will complete or generate the expected return.</LI>
        <LI>Builders may fail to complete projects on time, within budget, or to the required standard.</LI>
        <LI>Planning permission may be refused or revoked, causing projects to fail.</LI>
        <LI>Market conditions, interest rates, and economic factors beyond anyone&apos;s control can affect returns.</LI>
        <LI>You may not be able to recover your money if a builder defaults or becomes insolvent.</LI>
      </UL>

      <H>3. Illiquidity</H>
      <P>Property investments are typically illiquid. Once you have committed funds to a project, you may not be able to withdraw them before the project completes. You should only invest money you can afford to have tied up for the duration of the project.</P>

      <H>4. No FSCS protection</H>
      <P>Investments made through LenderBuild are not covered by the Financial Services Compensation Scheme (FSCS). If LenderBuild ceases to operate or if a deal fails, you may have no recourse to compensation from the FSCS.</P>

      <H>5. Builder verification</H>
      <P>While LenderBuild allows builders to upload verification documents (ID, site photos, planning documents), we do not independently verify the authenticity of these documents or the claims made by builders on their profiles. The verified badge indicates that documents have been uploaded, not that they have been independently checked. You should conduct your own due diligence before entering into any agreement.</P>

      <H>6. Platform risk</H>
      <P>LenderBuild is an early-stage platform. There is a risk that the platform could be discontinued, that technical errors could occur, or that our systems could be compromised. We take reasonable precautions to protect user data and funds, but cannot guarantee against all risks.</P>

      <H>7. Tax</H>
      <P>Returns from property lending and investment may be subject to Income Tax, Capital Gains Tax, or other taxes depending on your circumstances. You are responsible for reporting any income or gains to HM Revenue &amp; Customs. LenderBuild does not provide tax advice. Please consult a qualified tax adviser.</P>

      <H>8. Due diligence</H>
      <P>Before entering into any deal through LenderBuild, you should:</P>
      <UL>
        <LI>Conduct thorough due diligence on the builder, project, and site</LI>
        <LI>Review all relevant legal documents, including any loan agreements or security documents</LI>
        <LI>Seek independent legal advice on the terms of any agreement</LI>
        <LI>Verify planning permissions, building regulations approval, and any other relevant consents</LI>
        <LI>Consider taking out appropriate insurance</LI>
        <LI>Only invest what you can afford to lose</LI>
      </UL>

      <H>9. Dispute resolution</H>
      <P>LenderBuild does not mediate or adjudicate disputes between lenders and builders. Any disputes regarding the quality of work, payment, or other matters are the sole responsibility of the parties involved. We strongly recommend that all agreements are documented in a legally binding contract before any funds are transferred.</P>

      <H>10. Contact</H>
      <P>If you have questions about these risks or our platform, please contact us at lenderbuild.support@gmail.com.</P>
    </LegalPageWrapper>
  );
}

// ─── FOR BUILDERS PAGE ───────────────────────────────────────────────────────

function ForBuildersPage({ setPage }) {
  const bullets = [
    { heading: "The banks aren't built for you", body: "High street banks treat property developers as high risk. Long applications, strict criteria, and slow decisions mean missed opportunities. LenderBuild connects you with private lenders who actually understand how development works." },
    { heading: "Keep more of your margin", body: "Traditional brokers charge 1–3% and stay involved throughout the deal. LenderBuild charges a single 1% fee paid only when your deal completes. No monthly fees, no subscriptions, no surprises." },
    { heading: "Move at the speed of the market", body: "Property deals don't wait. With LenderBuild you can go from posting your project to agreeing terms in days, not the weeks or months a bank would take." },
    { heading: "You stay in control", body: "No broker controlling the conversation. You speak directly with your lender, agree your own terms, and build a relationship that could fund your next project too." },
    { heading: "Pay back on your own agreed terms", body: "Repayment terms are agreed directly between you and your lender. No rigid bank schedules, no pressure from a third party. You negotiate a structure that works for your project timeline." },
    { heading: "Built for UK property", body: "LenderBuild is designed specifically for UK residential, commercial, and renovation development. Not a generic lending platform — a tool built for what you actually do." },
  ];
  return (
    <div style={{ background: "#F4F5F7", minHeight: "100vh" }}>
      <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", padding: "72px 1.25rem 80px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "inline-block", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 20, padding: "5px 14px", fontSize: 12, color: "rgba(255,255,255,0.85)", fontWeight: 500, marginBottom: 24, letterSpacing: "0.04em" }}>For Builders</div>
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 700, color: "#fff", margin: "0 0 18px", lineHeight: 1.15 }}>Stop getting rejected.<br />Start getting funded.</h1>
          <p style={{ fontSize: "clamp(15px, 2vw, 19px)", color: "rgba(255,255,255,0.75)", maxWidth: 560, margin: "0 auto 40px", lineHeight: 1.65 }}>Traditional banks say no to most property developers. LenderBuild connects you directly with private lenders who are ready to back your project.</p>
          <button onClick={() => setPage(WAITLIST_MODE ? "waitlist" : "auth")} style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "14px 34px", borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>Post your project free →</button>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "64px 1.25rem" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: "36px 40px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1E3A5F", marginBottom: 24 }}>Why would a builder use LenderBuild?</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {bullets.map(({ heading, body }) => (
              <div key={heading} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#3B82F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5l3.5 3.5 5.5-7" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F", marginBottom: 3 }}>{heading}</div>
                  <div style={{ fontSize: 14, color: "#4B5563", lineHeight: 1.65 }}>{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 40, background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", borderRadius: 16, padding: "40px", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px, 3vw, 30px)", color: "#fff", margin: "0 0 12px" }}>Ready to find your funding?</h2>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.72)", margin: "0 0 28px" }}>Create your builder profile free. No subscription. No commitment.</p>
          <button onClick={() => setPage(WAITLIST_MODE ? "waitlist" : "auth")} style={{ background: "#fff", color: "#1E3A5F", border: "none", padding: "13px 32px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Post your project free →</button>
          <div style={{ marginTop: 18 }}>
            <button onClick={() => setPage("for-lenders")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.65)", fontSize: 13, cursor: "pointer", padding: 0 }}>Are you a lender? View lender information →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FOR LENDERS PAGE ────────────────────────────────────────────────────────

function ForLendersPage({ setPage }) {
  const bullets = [
    "Hand-pick your projects — browse vetted builders and choose exactly who you back",
    "Set your own terms — fixed interest or equity, you decide the rate and structure",
    "Outperform savings accounts — private property lending has historically delivered far stronger returns",
    "Stay protected at every stage — funds release in milestones so your capital is never fully exposed",
  ];
  return (
    <div style={{ background: "#F4F5F7", minHeight: "100vh" }}>
      <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", padding: "72px 1.25rem 80px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "inline-block", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 20, padding: "5px 14px", fontSize: 12, color: "rgba(255,255,255,0.85)", fontWeight: 500, marginBottom: 24, letterSpacing: "0.04em" }}>For Lenders</div>
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 700, color: "#fff", margin: "0 0 18px", lineHeight: 1.15 }}>Your capital deserves better<br />than a savings account.</h1>
          <p style={{ fontSize: "clamp(15px, 2vw, 19px)", color: "rgba(255,255,255,0.75)", maxWidth: 580, margin: "0 auto 40px", lineHeight: 1.65 }}>Put your capital to work in real UK property projects. Earn more than a typical bank or savings account while staying fully in control of every deal.</p>
          <button onClick={() => setPage(WAITLIST_MODE ? "waitlist" : "auth")} style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "14px 34px", borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>Start earning on your terms →</button>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "64px 1.25rem" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: "36px 40px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#2E5FA3", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>Why lenders choose LenderBuild</div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 20 }}>
            {bullets.map(item => (
              <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#1E3A5F", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5l3.5 3.5 5.5-7" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <span style={{ fontSize: 15, color: "#2C3E50", lineHeight: 1.6 }}>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ marginTop: 40, background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", borderRadius: 16, padding: "40px", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px, 3vw, 30px)", color: "#fff", margin: "0 0 12px" }}>Ready to put your capital to work?</h2>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.72)", margin: "0 0 28px" }}>Create your lender profile free. Browse builders and start earning.</p>
          <button onClick={() => setPage(WAITLIST_MODE ? "waitlist" : "auth")} style={{ background: "#fff", color: "#1E3A5F", border: "none", padding: "13px 32px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Start earning on your terms →</button>
          <div style={{ marginTop: 18 }}>
            <button onClick={() => setPage("for-builders")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.65)", fontSize: 13, cursor: "pointer", padding: 0 }}>Are you a builder? View builder information →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HOW IT WORKS PAGE ───────────────────────────────────────────────────────

function HowItWorksPage({ setPage }) {
  const sec = { maxWidth: 860, margin: "0 auto", padding: "0 1.25rem" };
  const card = { background: "#fff", borderRadius: 14, padding: "28px 28px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" };
  const tag = { display: "inline-block", background: "#EBF2FF", color: "#2E5FA3", fontWeight: 700, fontSize: 12, borderRadius: 20, padding: "3px 11px", marginBottom: 10, letterSpacing: "0.04em" };

  const steps = [
    { n: "01", title: "Create your profile", body: "Sign up as a lender or builder. Tell us about your investment appetite, project types, location preferences, and budget range. A complete profile unlocks your full compatibility score and gets three times as many matches." },
    { n: "02", title: "Get matched", body: "Our compatibility algorithm scores every pairing on budget alignment, location, project type, experience level, and risk appetite. Browse your top matches ranked by score — no cold searching required." },
    { n: "03", title: "Connect and agree terms", body: "Send a connection request with your proposal. Once accepted, both parties agree the deal terms inside the platform: loan amount, return type (fixed interest, rental split, or equity stake), and a six-stage milestone schedule." },
    { n: "04", title: "Release funds via milestones", body: "Funds are never released all at once. Each stage is unlocked only when both parties approve the completion evidence. The lender retains a legal charge over the property throughout the project." },
  ];

  const matching = [
    { label: "Budget alignment", desc: "How closely the lender's available capital matches the builder's funding requirement." },
    { label: "Location overlap", desc: "Whether the lender covers the region where the project is based." },
    { label: "Project type fit", desc: "Matching residential, commercial, conversion, and land-development preferences." },
    { label: "Experience level", desc: "Pairing seasoned builders with appropriately sophisticated lenders." },
    { label: "Return preference", desc: "Matching lenders who want fixed interest with builders offering it, and vice versa for equity deals." },
    { label: "Risk appetite", desc: "LTV ratio and security strength weighed against each lender's stated risk tolerance." },
  ];

  const milestones = [
    { n: 1, title: "Site preparation", ex: "Land cleared, planning consent confirmed, contractor engaged." },
    { n: 2, title: "Foundations", ex: "Groundworks complete, structural engineer sign-off obtained." },
    { n: 3, title: "Structure", ex: "Frame or walls at plate level, roof structure in place." },
    { n: 4, title: "Weathered-in", ex: "Roof on, windows and doors fitted, building wind and watertight." },
    { n: 5, title: "First fix complete", ex: "Electrical, plumbing, and HVAC first fix done, plastering complete." },
    { n: 6, title: "Completion", ex: "Second fix complete, snagging resolved, building regs sign-off." },
  ];

  const returns = [
    { icon: "trending-up", label: "Fixed interest", ex: "Example: £200,000 lent at 8% p.a. over 18 months = £24,000 return. Simple, predictable income regardless of how the project performs." },
    { icon: "home", label: "Rental income split", ex: "Example: 40% of net rental income on a £350,000 buy-to-let conversion. The lender shares in ongoing income once the project is tenanted." },
    { icon: "briefcase", label: "Equity stake", ex: "Example: 20% equity in a £500,000 development. The lender participates in the profit when the property is sold or refinanced." },
  ];

  const faqs = [
    { q: "Who can join LenderBuild?", a: "Any UK-based individual or company can register as a builder or lender. Builders must be working on UK property projects. Lenders must be able to demonstrate they are investing personal or business capital (not regulated retail investment)." },
    { q: "Is LenderBuild regulated by the FCA?", a: "LenderBuild is currently not authorised or regulated by the Financial Conduct Authority. We operate as an introduction platform only. We are actively pursuing FCA authorisation. All investments carry risk — please read our Risk Warning before proceeding." },
    { q: "How is my identity verified?", a: "Every user completes document verification before connecting with other members. You will be asked to upload a government-issued photo ID and proof of address. Our admin team reviews each submission within one business day." },
    { q: "What happens if a builder defaults?", a: "If a builder misses a milestone deadline our system triggers a structured escalation: a reminder on day 1, a formal warning to both parties on day 7, and an urgent admin alert on day 14. The lender's legal charge over the property remains in place throughout." },
    { q: "How are funds held?", a: "Funds are processed through Stripe, which is PCI DSS Level 1 compliant. LenderBuild does not hold client funds — all payments move directly between parties via Stripe's secure infrastructure." },
    { q: "Can I back out after agreeing terms?", a: "Deals can be cancelled by mutual consent before any funds are released. Once a milestone payment has been made it cannot be reversed. We strongly recommend both parties take independent legal advice before signing any off-platform agreement." },
    { q: "What is a legal charge and do I need one?", a: "A legal charge (also called a first or second charge mortgage) is a legally registered interest in the property that gives the lender security. We strongly recommend lenders obtain a solicitor-registered legal charge before releasing funds. The platform supports this process but does not arrange it directly." },
    { q: "How much does LenderBuild cost?", a: "Creating a profile and browsing matches is free. When a deal is agreed, LenderBuild charges a finder's fee of 1% of the total deal value, payable by the lender before the first milestone is unlocked. There are no monthly subscription fees." },
  ];

  return (
    <div style={{ background: "#F4F5F7", minHeight: "100vh" }}>
      {/* Hero */}
      <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", padding: "72px 1.25rem 80px" }}>
        <div style={{ ...sec, textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 20, padding: "5px 14px", fontSize: 12, color: "rgba(255,255,255,0.85)", fontWeight: 500, marginBottom: 24, letterSpacing: "0.04em" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9.5" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5"/><path d="M8 12l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Platform guide
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 700, color: "#fff", margin: "0 0 18px", lineHeight: 1.15 }}>How LenderBuild works</h1>
          <p style={{ fontSize: "clamp(15px, 2vw, 19px)", color: "rgba(255,255,255,0.75)", maxWidth: 560, margin: "0 auto 32px", lineHeight: 1.6 }}>From first match to funded project in four simple steps</p>
          <button onClick={() => setPage(WAITLIST_MODE ? "waitlist" : "auth")} style={{ background: "#fff", color: "#1E3A5F", border: "none", padding: "13px 32px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Get started free →</button>
        </div>
      </div>

      {/* Steps */}
      <div style={{ padding: "64px 1.25rem" }}>
        <div style={sec}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={tag}>Step by step</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px, 3vw, 32px)", color: "#1E3A5F", margin: 0 }}>The four stages of every deal</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {steps.map(s => (
              <div key={s.n} style={card}>
                <div style={{ fontSize: 36, fontWeight: 800, color: "#EBF2FF", fontFamily: "'Playfair Display', serif", lineHeight: 1, marginBottom: 12 }}>{s.n}</div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1E3A5F", margin: "0 0 10px" }}>{s.title}</h3>
                <p style={{ fontSize: 13.5, color: "#4B5563", lineHeight: 1.65, margin: 0 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How matching works */}
      <div style={{ background: "#fff", padding: "64px 1.25rem" }}>
        <div style={sec}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "start" }}>
            <div>
              <div style={tag}>Matching algorithm</div>
              <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(20px, 3vw, 28px)", color: "#1E3A5F", margin: "0 0 16px" }}>How your compatibility score is calculated</h2>
              <p style={{ fontSize: 14, color: "#4B5563", lineHeight: 1.7, margin: "0 0 16px" }}>Every lender-builder pairing receives a compatibility score from 0–100. The score is calculated instantly from six weighted factors and updated whenever either party updates their profile.</p>
              <p style={{ fontSize: 14, color: "#4B5563", lineHeight: 1.7, margin: 0 }}>A higher score means both parties are likely to agree terms quickly, reducing the time from introduction to funded project.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {matching.map(m => (
                <div key={m.label} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 16px", background: "#F8FAFF", borderRadius: 10, border: "1px solid #E8EEFA" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#2E5FA3", flexShrink: 0, marginTop: 5 }} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1E3A5F", marginBottom: 3 }}>{m.label}</div>
                    <div style={{ fontSize: 12.5, color: "#6B7280", lineHeight: 1.55 }}>{m.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Milestone payments */}
      <div style={{ padding: "64px 1.25rem" }}>
        <div style={sec}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={tag}>Milestone payments</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(20px, 3vw, 28px)", color: "#1E3A5F", margin: "0 0 12px" }}>Funds released in six controlled stages</h2>
            <p style={{ fontSize: 14, color: "#6B7280", maxWidth: 520, margin: "0 auto" }}>Neither party can release funds unilaterally. Each stage requires photographic evidence, both parties to approve, and a 1% finder's fee to be paid before stage one unlocks.</p>
          </div>
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", top: 20, left: 19, bottom: 20, width: 2, background: "#E8EEFA" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {milestones.map((m, i) => (
                <div key={m.n} style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#2E5FA3", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0, position: "relative", zIndex: 1 }}>{m.n}</div>
                  <div style={{ ...card, flex: 1, padding: "18px 20px 16px", marginTop: 4 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", marginBottom: 5 }}>{m.title}</div>
                    <div style={{ fontSize: 12.5, color: "#6B7280", lineHeight: 1.55 }}>{m.ex}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Repayment types */}
      <div style={{ background: "#fff", padding: "64px 1.25rem" }}>
        <div style={sec}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={tag}>Returns</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(20px, 3vw, 28px)", color: "#1E3A5F", margin: "0 0 12px" }}>Three ways lenders get their return</h2>
            <p style={{ fontSize: 14, color: "#6B7280", maxWidth: 500, margin: "0 auto" }}>Both parties agree the return structure before any funds are committed. It is written into the deal record on the platform and referenced in any off-platform legal agreement.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
            {returns.map(r => (
              <div key={r.label} style={{ ...card, border: "1px solid #E8EEFA" }}>
                <div style={{ fontSize: 28, marginBottom: 14 }}>{r.icon}</div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1E3A5F", margin: "0 0 10px" }}>{r.label}</h3>
                <p style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.65, margin: 0 }}>{r.ex}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div style={{ padding: "64px 1.25rem" }}>
        <div style={sec}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={tag}>FAQ</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(20px, 3vw, 28px)", color: "#1E3A5F", margin: 0 }}>Common questions</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {faqs.map((f, i) => (
              <details key={i} style={{ ...card, padding: 0, overflow: "hidden" }}>
                <summary style={{ padding: "18px 22px", fontSize: 14, fontWeight: 600, color: "#1E3A5F", cursor: "pointer", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {f.q}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M6 9l6 6 6-6" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </summary>
                <div style={{ padding: "0 22px 18px", fontSize: 13.5, color: "#4B5563", lineHeight: 1.7 }}>{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", padding: "72px 1.25rem" }}>
        <div style={{ ...sec, textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(24px, 4vw, 36px)", color: "#fff", margin: "0 0 14px" }}>Ready to get started?</h2>
          <p style={{ fontSize: 16, color: "rgba(255,255,255,0.72)", margin: "0 0 32px" }}>Create your profile in under five minutes. No subscription fees.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => setPage(WAITLIST_MODE ? "waitlist" : "auth")} style={{ background: "#fff", color: "#1E3A5F", border: "none", padding: "13px 32px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Sign up free →</button>
            <button onClick={() => setPage("safety")} style={{ background: "transparent", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.3)", padding: "13px 28px", borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: "pointer" }}>Trust & Safety</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TRUST AND SAFETY PAGE ────────────────────────────────────────────────────

function SafetyPage({ setPage }) {
  const sec = { maxWidth: 860, margin: "0 auto", padding: "0 1.25rem" };
  const card = { background: "#fff", borderRadius: 14, padding: "28px 28px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" };
  const tag = { display: "inline-block", background: "#EBF2FF", color: "#2E5FA3", fontWeight: 700, fontSize: 12, borderRadius: 20, padding: "3px 11px", marginBottom: 10, letterSpacing: "0.04em" };

  const stats = [
    { n: "100%", label: "Users ID-verified" },
    { n: "6", label: "Milestone stages" },
    { n: "PCI DSS", label: "Payment compliance" },
    { n: "GDPR", label: "Data protection" },
  ];

  const protections = [
    {
      icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z" fill="#EBF2FF" stroke="#2E5FA3" strokeWidth="1.5" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="#2E5FA3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      title: "Identity verification",
      body: "Every member must upload a government-issued photo ID (passport or driving licence) and proof of address before they can send or accept connection requests. Our admin team manually reviews each submission. Unverified users cannot access contact details or initiate deals.",
    },
    {
      icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" fill="#EBF2FF" stroke="#2E5FA3" strokeWidth="1.5"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="#2E5FA3" strokeWidth="1.5" strokeLinecap="round"/></svg>,
      title: "Legal charge protection",
      body: "We strongly recommend every lender register a legal charge (first or second charge mortgage) against the property before releasing funds. A legal charge is a formal, Land Registry-recorded interest that gives the lender a legal right over the property in the event of default. This is the same security mechanism used by high-street banks. LenderBuild supports this process through our admin team and provides guidance on solicitor-registered charges.",
    },
    {
      icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><rect x="2" y="6" width="20" height="14" rx="2" fill="#EBF2FF" stroke="#2E5FA3" strokeWidth="1.5"/><path d="M6 10h12M6 14h7" stroke="#2E5FA3" strokeWidth="1.5" strokeLinecap="round"/></svg>,
      title: "Milestone payment protection",
      body: "Funds are never transferred in a single lump sum. Every deal is structured into up to six milestones (site preparation, foundations, structure, weathered-in, first fix, completion). Each milestone payment requires both parties to approve completion evidence inside the platform before Stripe processes the transfer. Neither party can release funds unilaterally.",
    },
    {
      icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><rect x="2" y="5" width="20" height="16" rx="2" fill="#EBF2FF" stroke="#2E5FA3" strokeWidth="1.5"/><path d="M2 10h20M6 15h4" stroke="#2E5FA3" strokeWidth="1.5" strokeLinecap="round"/></svg>,
      title: "Stripe-secured payments",
      body: "All payments are processed through Stripe, one of the world's most trusted payment infrastructure providers. Stripe is certified at PCI DSS Level 1 — the highest level of payment security compliance. All card data is encrypted at rest and in transit using AES-256. LenderBuild never stores card numbers or bank details on our servers.",
    },
    {
      icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><circle cx="12" cy="8" r="4" fill="#EBF2FF" stroke="#2E5FA3" strokeWidth="1.5"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#2E5FA3" strokeWidth="1.5" strokeLinecap="round"/></svg>,
      title: "Admin oversight on every deal",
      body: "Every deal created on LenderBuild is visible to our admin team. We actively monitor for unusual payment patterns, stalled milestones, and unresolved disputes. Admins can freeze deal payments, escalate disputes to third-party mediation, and flag accounts for review at any time.",
    },
    {
      icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" fill="#EBF2FF" stroke="#2E5FA3" strokeWidth="1.5"/><path d="M8 12h8M8 8h5M8 16h3" stroke="#2E5FA3" strokeWidth="1.5" strokeLinecap="round"/></svg>,
      title: "GDPR data protection",
      body: "LenderBuild is committed to full compliance with the UK GDPR and the Data Protection Act 2018. Your personal data is stored securely in Supabase infrastructure hosted within the EU. We do not sell or share your data with third parties for marketing purposes. You can request deletion of your account and all associated data at any time by contacting lenderbuild.support@gmail.com.",
    },
  ];

  const escalation = [
    { step: 1, title: "In-platform dispute raised", desc: "Either party raises a dispute through the Deals page. The deal's milestone payments are immediately frozen." },
    { step: 2, title: "Admin notification", desc: "Our team is alerted instantly. Both parties receive an email confirming the dispute is under review." },
    { step: 3, title: "Evidence review", desc: "Admin reviews milestone evidence, communications, and deal history. Both parties are contacted within 2 business days." },
    { step: 4, title: "Resolution or escalation", desc: "Admin issues a recommendation. If accepted, payments resume. If not, the dispute is referred to an independent mediator." },
    { step: 5, title: "Legal recourse", desc: "For unresolved financial disputes, the lender's legal charge remains enforceable through the courts. We recommend both parties maintain independent legal representation." },
  ];

  return (
    <div style={{ background: "#F4F5F7", minHeight: "100vh" }}>
      {/* Hero */}
      <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", padding: "72px 1.25rem 80px" }}>
        <div style={{ ...sec, textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 20, padding: "5px 14px", fontSize: 12, color: "rgba(255,255,255,0.85)", fontWeight: 500, marginBottom: 24, letterSpacing: "0.04em" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z" fill="white"/></svg>
            Trust & Safety
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 700, color: "#fff", margin: "0 0 18px", lineHeight: 1.15 }}>Your security is our priority</h1>
          <p style={{ fontSize: "clamp(15px, 2vw, 18px)", color: "rgba(255,255,255,0.75)", maxWidth: 540, margin: "0 auto 40px", lineHeight: 1.6 }}>Every protection we have built into LenderBuild — from identity checks to legal charge guidance to admin-monitored disputes.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, maxWidth: 640, margin: "0 auto" }}>
            {stats.map(s => (
              <div key={s.n} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: "14px 8px", textAlign: "center" }}>
                <div style={{ fontSize: "clamp(15px, 2.5vw, 22px)", fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>{s.n}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.62)", marginTop: 5, lineHeight: 1.35 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Protections grid */}
      <div style={{ padding: "64px 1.25rem" }}>
        <div style={sec}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={tag}>Platform protections</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(20px, 3vw, 28px)", color: "#1E3A5F", margin: 0 }}>Six layers of protection on every deal</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {protections.map((p, i) => (
              <div key={i} style={{ ...card, display: "flex", gap: 20, alignItems: "flex-start" }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: "#F0F4FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{p.icon}</div>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1E3A5F", margin: "0 0 8px" }}>{p.title}</h3>
                  <p style={{ fontSize: 13.5, color: "#4B5563", lineHeight: 1.7, margin: 0 }}>{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dispute escalation */}
      <div style={{ background: "#fff", padding: "64px 1.25rem" }}>
        <div style={sec}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={tag}>Dispute resolution</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(20px, 3vw, 28px)", color: "#1E3A5F", margin: "0 0 12px" }}>What happens if something goes wrong</h2>
            <p style={{ fontSize: 14, color: "#6B7280", maxWidth: 500, margin: "0 auto" }}>Our structured escalation process protects both parties and keeps every dispute moving towards resolution.</p>
          </div>
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", top: 20, left: 19, bottom: 20, width: 2, background: "#E8EEFA" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {escalation.map(e => (
                <div key={e.step} style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: e.step <= 3 ? "#2E5FA3" : "#1D9E75", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0, position: "relative", zIndex: 1 }}>{e.step}</div>
                  <div style={{ background: "#F8FAFF", border: "1px solid #E8EEFA", borderRadius: 12, padding: "16px 18px", flex: 1, marginTop: 4 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", marginBottom: 5 }}>{e.title}</div>
                    <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6 }}>{e.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* FCA & regulatory status */}
      <div style={{ padding: "64px 1.25rem" }}>
        <div style={sec}>
          <div style={{ ...card, border: "1.5px solid #E8EEFA" }}>
            <div style={tag}>Regulatory status</div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(18px, 2.5vw, 24px)", color: "#1E3A5F", margin: "0 0 14px" }}>FCA authorisation and current status</h2>
            <p style={{ fontSize: 13.5, color: "#4B5563", lineHeight: 1.75, margin: "0 0 14px" }}>
              <strong>LenderBuild is currently not authorised or regulated by the Financial Conduct Authority (FCA).</strong> We operate as a technology-enabled introduction platform that facilitates connections between property developers and private lenders. We do not provide financial advice, arrange loans, or hold client funds.
            </p>
            <p style={{ fontSize: 13.5, color: "#4B5563", lineHeight: 1.75, margin: "0 0 14px" }}>
              We are actively pursuing FCA authorisation under the credit broking and arranging investments permissions. Until such time as authorisation is granted, we recommend all users:
            </p>
            <ul style={{ margin: "0 0 14px", paddingLeft: 20, fontSize: 13.5, color: "#4B5563", lineHeight: 2 }}>
              <li>Take independent legal advice before committing funds</li>
              <li>Obtain a solicitor-registered legal charge on any property investment</li>
              <li>Read our full Risk Warning before proceeding</li>
              <li>Only invest capital they can afford to lose</li>
            </ul>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button onClick={() => setPage("risk-warning")} style={{ background: "#EBF2FF", color: "#2E5FA3", border: "none", padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Read Risk Warning</button>
              <button onClick={() => setPage("how-it-works")} style={{ background: "transparent", color: "#6B7280", border: "1px solid #D1D5DB", padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>How it works</button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2E5FA3 100%)", padding: "72px 1.25rem" }}>
        <div style={{ ...sec, textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px, 4vw, 34px)", color: "#fff", margin: "0 0 14px" }}>Safe, transparent property finance</h2>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.72)", margin: "0 0 30px" }}>Join thousands of builders and lenders building together on LenderBuild.</p>
          <button onClick={() => setPage(WAITLIST_MODE ? "waitlist" : "auth")} style={{ background: "#fff", color: "#1E3A5F", border: "none", padding: "13px 32px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Create your free account →</button>
        </div>
      </div>
    </div>
  );
}

// ─── DISPUTES PAGE ───────────────────────────────────────────────────────────

function DisputesPage({ user, setPage }) {
  const [disputes,    setDisputes]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [reason,      setReason]      = useState("");
  const [dealId,      setDealId]      = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [deals,       setDeals]       = useState([]);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setLoading(false); return; }
      const res = await fetch("/api/deals", { headers: { Authorization: `Bearer ${session.access_token}` } });
      const d = await res.json().catch(() => ({}));
      const allDeals = d.deals || [];
      setDeals(allDeals);
      const allDisputes = allDeals.flatMap(deal =>
        (deal.disputes || []).map(dp => ({ ...dp, deal_title: deal.title, deal_id: deal.id }))
      );
      allDisputes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setDisputes(allDisputes);
      setLoading(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(e) {
    e.preventDefault();
    if (!dealId || !reason.trim()) return;
    setSubmitting(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/deals", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "raise-dispute", deal_id: dealId, reason }),
    });
    const d = await res.json().catch(() => ({}));
    setSubmitting(false);
    if (!res.ok) { setError(d.error || "Failed to raise dispute."); return; }
    const deal = deals.find(dl => dl.id === dealId);
    setDisputes(prev => [{ status: "open", reason, deal_title: deal?.title || dealId, created_at: new Date().toISOString() }, ...prev]);
    setReason(""); setDealId("");
    setSuccess("Dispute raised. All payments on this deal have been frozen. Our team will be in touch.");
    setTimeout(() => setSuccess(""), 6000);
  }

  const statusBadge = (s) => {
    const cfg = { open: ["#FEE2E2","#991B1B","Open"], resolved: ["#E1F5EE","#0F6E56","Resolved"], escalated: ["#EEEDFE","#534AB7","Escalated"] };
    const [bg, col, label] = cfg[s] || ["#f5f5f3","#64748B",s];
    return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: bg, color: col }}>{label}</span>;
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "1.5rem 1.25rem", minHeight: "calc(100vh - 56px)" }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 4px", fontFamily: "'Georgia', serif" }}>Disputes</h1>
      <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 1.5rem" }}>
        Raise a formal dispute if there is a serious problem with a deal. All milestone payments will be frozen until the dispute is resolved by our admin team.
      </p>

      {success && (
        <div style={{ background: "#E1F5EE", border: "0.5px solid #A8DFC9", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#0F6E56", marginBottom: "1rem" }}>
          {success}
        </div>
      )}

      {/* Raise a dispute form */}
      <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: "1rem" }}>Raise a new dispute</div>
        {error && (
          <div style={{ background: "#FAECE7", color: "#993C1D", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: "1rem" }}>{error}</div>
        )}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <div>
            <label style={{ fontSize: 13, color: "#555", fontWeight: 500, display: "block", marginBottom: 4 }}>Select deal</label>
            <select
              required value={dealId} onChange={e => setDealId(e.target.value)}
              style={{ width: "100%", height: 44, border: "0.5px solid #ccc", borderRadius: 8, padding: "0 12px", fontSize: 14, background: "#fff", boxSizing: "border-box" }}
            >
              <option value="">— Choose a deal —</option>
              {deals.map(d => (
                <option key={d.id} value={d.id}>{d.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, color: "#555", fontWeight: 500, display: "block", marginBottom: 4 }}>Describe the issue</label>
            <textarea
              required value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Please describe the problem in detail…"
              style={{ width: "100%", minHeight: 100, padding: "10px 12px", fontSize: 13, border: "0.5px solid #ccc", borderRadius: 8, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
            />
          </div>
          <button
            type="submit" disabled={submitting}
            style={{ padding: "10px 20px", minHeight: 44, background: submitting ? "#aaa" : "#991B1B", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: submitting ? "default" : "pointer", alignSelf: "flex-start" }}
          >
            {submitting ? "Submitting…" : "Submit dispute"}
          </button>
        </form>
      </div>

      {/* Dispute history */}
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Your disputes</div>
      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#aaa", fontSize: 14 }}>Loading…</div>
      ) : disputes.length === 0 ? (
        <div style={{ background: "#f9f9f7", borderRadius: 8, padding: "1rem", fontSize: 13, color: "#64748B" }}>No disputes raised yet.</div>
      ) : disputes.map((dp, i) => (
        <div key={i} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{dp.deal_title}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {statusBadge(dp.status)}
              <span style={{ fontSize: 11, color: "#aaa" }}>{new Date(dp.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "#444", lineHeight: 1.6 }}>{dp.reason}</div>
          {dp.resolution_notes && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#0F6E56", background: "#E1F5EE", borderRadius: 6, padding: "6px 10px" }}>
              <strong>Resolution:</strong> {dp.resolution_notes}
            </div>
          )}
        </div>
      ))}

      <div style={{ marginTop: "1.5rem", background: "#EBF2FF", border: "0.5px solid #C3D9FF", borderRadius: 10, padding: "1rem 1.25rem" }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "#1E3A5F", marginBottom: 4 }}>Need urgent help?</div>
        <div style={{ fontSize: 12, color: "#64748B" }}>
          For urgent issues, use the contact form above and we'll get back to you within 24 hours.
        </div>
      </div>
    </div>
  );
}

// ─── LENDER WALLET PAGE ───────────────────────────────────────────────────────

function LenderWalletPage({ user, setPage }) {
  const [deals,      setDeals]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  useEffect(() => {
    async function load() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setLoading(false); return; }
      const res = await fetch("/api/deals", { headers: { Authorization: `Bearer ${session.access_token}` } });
      if (!res.ok) { setError("Could not load portfolio."); setLoading(false); return; }
      const { deals: ds } = await res.json();
      setDeals((ds || []).filter(d => d.lender_id === user.id));
      setLoading(false);
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div style={{ padding: "2rem", textAlign: "center", color: "#64748B", fontSize: 14 }}>Loading portfolio…</div>;
  if (error)   return <div style={{ padding: "2rem", textAlign: "center", color: "#991B1B", fontSize: 14 }}>{error}</div>;

  const allRepayments = deals.flatMap(d => (d.repayments || []).map(r => ({ ...r, deal_title: d.title })));
  const totalInvested  = deals.reduce((s, d) => s + (d.milestones || []).reduce((ms, m) => ms + Number(m.amount), 0), 0);
  const totalReturnsRx = allRepayments.filter(r => r.status === "paid").reduce((s, r) => s + Number(r.amount), 0);
  const totalExpected  = allRepayments.reduce((s, r) => s + Number(r.amount), 0);
  const activeDeals    = deals.filter(d => d.status === "active").length;
  const completedDeals = deals.filter(d => d.status === "completed").length;
  const defaultDeals   = deals.filter(d => d.flagged_default).length;

  // Avg return rate from deals with fixed_interest
  const fixedDeals = deals.filter(d => d.return_type === "fixed_interest" && d.interest_rate);
  const avgRate = fixedDeals.length > 0
    ? (fixedDeals.reduce((s, d) => s + parseFloat(d.interest_rate) || 0, 0) / fixedDeals.length).toFixed(1)
    : null;

  // Monthly income chart: group paid repayments by YYYY-MM
  const monthlyMap = {};
  allRepayments.filter(r => r.status === "paid" && r.paid_at).forEach(r => {
    const mo = r.paid_at?.slice(0, 7);
    if (mo) monthlyMap[mo] = (monthlyMap[mo] || 0) + Number(r.amount);
  });
  const chartData = Object.entries(monthlyMap).sort().slice(-12).map(([mo, amt]) => ({
    month: new Date(mo + "-01").toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
    amount: amt,
  }));

  // Portfolio breakdown by return type
  const byReturnType = {};
  deals.forEach(d => {
    const key = d.return_type || "Other";
    const val = (d.milestones || []).reduce((s, m) => s + Number(m.amount), 0);
    byReturnType[key] = (byReturnType[key] || 0) + val;
  });
  const pieData = Object.entries(byReturnType).map(([name, value]) => ({ name, value }));
  const PIE_COLORS = ["#2E5FA3","#1D9E75","#7F77DD","#D97706","#DC2626"];

  function downloadCSV() {
    const rows = [["Deal","Date","Amount","Status","Confirmation"]];
    allRepayments.forEach(r => {
      rows.push([r.deal_title, r.due_date || "", r.amount, r.status, r.confirmation_number || ""]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,"''")}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `lenderbuild-statement-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  }

  const card = { background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem" };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "1.5rem 1.25rem" }}>
      <button onClick={() => setPage("my-deals")} style={{ background: "none", border: "none", color: "#3B82F6", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: "1rem" }}>← Back to my deals</button>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 4px", fontFamily: "'Georgia', serif" }}>Investment Portfolio</h1>
          <div style={{ fontSize: 13, color: "#64748B" }}>{deals.length} deal{deals.length !== 1 ? "s" : ""} · all figures from live data</div>
        </div>
        <button onClick={downloadCSV} style={{ padding: "8px 18px", background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          ↓ Download statement (CSV)
        </button>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: "1.5rem" }}>
        {[
          { label: "Total invested",          value: fmt(totalInvested),  color: "#1E3A5F", bg: "#EBF2FF" },
          { label: "Returns received",         value: fmt(totalReturnsRx), color: "#0F6E56", bg: "#DCFCE7" },
          { label: "Expected total returns",   value: fmt(totalExpected),  color: "#534AB7", bg: "#EEEDFE" },
          { label: "Average return rate",      value: avgRate ? `${avgRate}% p.a.` : "—", color: "#D97706", bg: "#FEF3C7" },
          { label: "Active deals",             value: activeDeals,         color: "#1E3A5F", bg: "#EBF2FF" },
          { label: "Completed deals",          value: completedDeals,      color: "#0F6E56", bg: "#DCFCE7" },
          ...(defaultDeals > 0 ? [{ label: "In default", value: defaultDeals, color: "#991B1B", bg: "#FEE2E2" }] : []),
        ].map(({ label, value, color, bg }) => (
          <div key={label} style={{ background: bg, borderRadius: 10, padding: "14px" }}>
            <div style={{ fontSize: 11, color: color, opacity: 0.7, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: chartData.length > 0 ? "1fr 1fr" : "1fr", gap: 14, marginBottom: "1.5rem" }}>
        {/* Monthly income chart */}
        {chartData.length > 0 && (
          <div style={card}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Monthly income received</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => `£${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => [`£${Number(v).toLocaleString()}`, "Received"]} />
                <Bar dataKey="amount" fill="#2E5FA3" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {/* Portfolio by return type */}
        {pieData.length > 0 && (
          <div style={card}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>Portfolio by return type</div>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent*100)}%`} labelLine={false} style={{ fontSize: 11 }}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => [`£${Number(v).toLocaleString()}`, ""]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Deal list */}
      {deals.length === 0 ? (
        <div style={{ ...card, textAlign: "center", color: "#64748B", fontSize: 14, padding: "2rem" }}>
          No deals found. <button onClick={() => setPage("deals")} style={{ background: "none", border: "none", color: "#3B82F6", cursor: "pointer", fontSize: 14 }}>Create your first deal →</button>
        </div>
      ) : (
        <div style={card}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>All deals</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {deals.map(d => {
              const invested = (d.milestones || []).reduce((s, m) => s + Number(m.amount), 0);
              const returned = (d.repayments || []).filter(r => r.status === "paid").reduce((s, r) => s + Number(r.amount), 0);
              const progress = invested > 0 ? Math.min(Math.round((returned / invested) * 100), 100) : 0;
              return (
                <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "#f9f9f7", borderRadius: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</div>
                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Builder: {d.builder_name} · {d.return_type?.replace(/_/g," ") || "—"}</div>
                    <div style={{ height: 4, background: "#e0e0e0", borderRadius: 2, marginTop: 6 }}>
                      <div style={{ height: 4, background: "#2E5FA3", borderRadius: 2, width: `${progress}%`, transition: "width 0.3s" }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F" }}>{fmt(invested)}</div>
                    <div style={{ fontSize: 11, color: "#0F6E56" }}>{fmt(returned)} back</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── NOTIFICATIONS PAGE ───────────────────────────────────────────────────────

function NotificationsPage({ user }) {
  const [notifs,      setNotifs]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [typeFilter,  setTypeFilter]  = useState("all");

  const myId = user?.id;

  useEffect(() => {
    if (!myId) return;
    supabase.from("notifications").select("*").eq("user_id", myId)
      .order("created_at", { ascending: false }).limit(100)
      .then(({ data }) => { setNotifs(data || []); setLoading(false); });

    const ch = supabase.channel(`notifs-page-${myId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${myId}` },
        p => setNotifs(prev => [p.new, ...prev].slice(0, 100)))
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [myId]);

  async function markRead(id) {
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    await supabase.from("notifications").update({ read: true }).eq("id", id);
  }

  async function markAllRead() {
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    await supabase.from("notifications").update({ read: true }).eq("user_id", myId);
  }

  async function deleteNotif(id) {
    setNotifs(prev => prev.filter(n => n.id !== id));
    await supabase.from("notifications").delete().eq("id", id);
  }

  const typeIcons = {
    connection_request:  { icon: "handshake",   label: "Connection" },
    connection_accepted: { icon: "check-circle", label: "Connected" },
    message:             { icon: "message",      label: "Message" },
    milestone_complete:  { icon: "clipboard",    label: "Milestone" },
    milestone_approved:  { icon: "dollar",       label: "Payment" },
    deal_created:        { icon: "bar-chart",    label: "Deal" },
    document_approved:   { icon: "file",         label: "Document" },
    repayment_due:       { icon: "⏰", label: "Repayment" },
  };

  const allTypes = [...new Set(notifs.map(n => n.type))];
  const filtered = typeFilter === "all" ? notifs : notifs.filter(n => n.type === typeFilter);

  function groupByDate(list) {
    const today     = new Date(); today.setHours(0,0,0,0);
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const weekAgo   = new Date(today); weekAgo.setDate(today.getDate() - 7);
    const groups    = { Today: [], Yesterday: [], "This week": [], Earlier: [] };
    list.forEach(n => {
      const d = new Date(n.created_at); d.setHours(0,0,0,0);
      if (d >= today)     groups.Today.push(n);
      else if (d >= yesterday) groups.Yesterday.push(n);
      else if (d >= weekAgo)   groups["This week"].push(n);
      else                     groups.Earlier.push(n);
    });
    return groups;
  }

  const groups  = groupByDate(filtered);
  const unread  = notifs.filter(n => !n.read).length;

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "1.5rem 1.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700, color: "#1E3A5F", margin: 0 }}>
            Notifications
          </h1>
          {unread > 0 && <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>{unread} unread</div>}
        </div>
        {unread > 0 && (
          <button onClick={markAllRead} style={{ background: "#EBF2FF", color: "#1E3A5F", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            Mark all read
          </button>
        )}
      </div>

      {/* Type filter tabs */}
      {allTypes.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1.25rem" }}>
          {["all", ...allTypes].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
              style={{ padding: "5px 12px", minHeight: 34, borderRadius: 20, border: typeFilter === t ? "none" : "0.5px solid #e0e0e0", background: typeFilter === t ? "#1E3A5F" : "#fff", color: typeFilter === t ? "#fff" : "#555", fontSize: 12, fontWeight: typeFilter === t ? 600 : 400, cursor: "pointer" }}
            >
              {t === "all" ? "All" : (typeIcons[t]?.label || t)}
            </button>
          ))}
        </div>
      )}

      {loading && <div style={{ textAlign: "center", padding: "3rem", color: "#aaa" }}>Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="bell" size={40} /></div>
          <div style={{ fontSize: 16, fontWeight: 500 }}>No notifications yet</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>We'll let you know when something important happens.</div>
        </div>
      )}

      {!loading && Object.entries(groups).map(([group, items]) => {
        if (!items.length) return null;
        return (
          <div key={group} style={{ marginBottom: "1.5rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{group}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {items.map(n => {
                const ti = typeIcons[n.type] || { icon: "bell", label: n.type };
                return (
                  <div key={n.id}
                    onClick={() => !n.read && markRead(n.id)}
                    style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 16px", background: n.read ? "#fff" : "#EBF5FF", borderRadius: 10, border: "0.5px solid #e0e0e0", cursor: n.read ? "default" : "pointer", position: "relative" }}
                  >
                    <Icon name={ti.icon} size={20} className="lb-icon" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: "#1E3A5F", lineHeight: 1.45 }}>{n.message}</div>
                      <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{fmtTimeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#2E5FA3", flexShrink: 0, marginTop: 4 }} />}
                    <button onClick={e => { e.stopPropagation(); deleteNotif(n.id); }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: 2, lineHeight: 1, flexShrink: 0 }} title="Delete">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── SAVED PROFILES PAGE ──────────────────────────────────────────────────────

function SavedProfilesPage({ user, setPage, onViewProfile, onViewBuilderProfile }) {
  const [profiles, setProfiles] = useState([]);
  const [liveData,  setLiveData]  = useState({});

  useEffect(() => {
    if (!user?.id) return;
    const saved = getSavedProfiles(user.id);
    setProfiles(saved);
    // Fetch live data for each saved profile
    const lenderIds  = saved.filter(p => p.role === "lender").map(p => p.user_id);
    const builderIds = saved.filter(p => p.role === "builder").map(p => p.user_id);
    const fetches = [];
    if (lenderIds.length)  fetches.push(supabase.from("lender_profiles").select("user_id, updated_at, is_active").in("user_id", lenderIds));
    if (builderIds.length) fetches.push(supabase.from("builder_profiles").select("user_id, updated_at, is_active").in("user_id", builderIds));
    Promise.all(fetches).then(results => {
      const map = {};
      results.forEach(r => (r.data || []).forEach(row => { map[row.user_id] = row; }));
      setLiveData(map);
    });
  }, [user?.id]);

  function remove(targetId) {
    toggleSavedProfile(user.id, { user_id: targetId });
    setProfiles(getSavedProfiles(user.id));
  }

  if (!profiles.length) {
    return (
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "1.5rem 1.25rem", textAlign: "center" }}>
        <div style={{ marginBottom: 16, color: "#CBD5E1" }}><Icon name="heart" size={48} /></div>
        <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, color: "#1E3A5F", margin: "0 0 12px" }}>No saved profiles yet</h2>
        <p style={{ fontSize: 14, color: "#64748B" }}>Click the heart icon on any lender or builder card to save them here.</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
          <button onClick={() => setPage("search")} style={{ background: "#2E5FA3", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Find a lender</button>
          <button onClick={() => setPage("find-builder")} style={{ background: "#16A34A", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Find a builder</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "1.5rem 1.25rem" }}>
      <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700, color: "#1E3A5F", margin: "0 0 6px" }}>Saved profiles</h1>
      <p style={{ fontSize: 14, color: "#64748B", margin: "0 0 1.5rem" }}>{profiles.length} saved profile{profiles.length !== 1 ? "s" : ""}</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
        {profiles.map(p => {
          const live = liveData[p.user_id];
          const isActive = live?.is_active !== false;
          const recentlyUpdated = live?.updated_at && (Date.now() - new Date(live.updated_at).getTime()) < 30 * 86400000;
          return (
            <div key={p.user_id} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "20px 24px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <Avatar initials={nameInitials(p.name)} color={pickColor(p.user_id)} size={44} url={p.avatar_url} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F" }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: "#64748B" }}>{p.type}</div>
                </div>
                <button onClick={() => remove(p.user_id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", padding: 4 }} title="Remove from saved">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                </button>
              </div>
              {p.location && <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}><Icon name="map-pin" size={12} style={{marginRight:3,flexShrink:0}} />{p.location}</div>}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: p.role === "lender" ? "#EBF2FF" : "#DCFCE7", color: p.role === "lender" ? "#1E3A5F" : "#166534", fontWeight: 500, textTransform: "capitalize" }}>{p.role}</span>
                {isActive ? (
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#DCFCE7", color: "#166534", fontWeight: 500 }}>Active</span>
                ) : (
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#F1F5F9", color: "#64748B", fontWeight: 500 }}>Inactive</span>
                )}
                {recentlyUpdated && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#FEF3C7", color: "#92400E", fontWeight: 500 }}>Recently updated</span>}
              </div>
              <button
                onClick={() => {
                  if (p.role === "lender" && onViewProfile) onViewProfile({ id: `real-${p.user_id}`, _real: true, user_id: p.user_id, name: p.name, initials: nameInitials(p.name), color: pickColor(p.user_id), avatar_url: p.avatar_url, type: p.type, location: p.location || "", bio: "" });
                  else if (p.role === "builder" && onViewBuilderProfile) onViewBuilderProfile({ id: `real-${p.user_id}`, _real: true, user_id: p.user_id, name: p.name, initials: nameInitials(p.name), color: pickColor(p.user_id), avatar_url: p.avatar_url, type: p.type, location: p.location || "", bio: "", verified_documents: [], props: 0, value: 0, completion: 100 });
                }}
                style={{ width: "100%", background: "#2E5FA3", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, fontWeight: 600, cursor: "pointer", minHeight: 40 }}
              >
                View profile
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── STATUS PAGE ──────────────────────────────────────────────────────────────

function StatusPage() {
  const [checks, setChecks] = useState([
    { name: "API",              status: "checking", latency: null, checked: null },
    { name: "Database",         status: "checking", latency: null, checked: null },
    { name: "Stripe",           status: "checking", latency: null, checked: null },
    { name: "Email service",    status: "checking", latency: null, checked: null },
  ]);

  const runChecks = useCallback(async () => {
    setChecks(prev => prev.map(c => ({ ...c, status: "checking" })));
    const results = await Promise.all([
      // API check — ping get-profiles
      (async () => {
        const t = Date.now();
        try {
          const r = await fetch("/api/get-profiles?type=lenders", { signal: AbortSignal.timeout(5000) });
          return { status: r.ok ? "ok" : "degraded", latency: Date.now() - t };
        } catch { return { status: "error", latency: null }; }
      })(),
      // Database check — ping Supabase
      (async () => {
        const t = Date.now();
        try {
          const { error } = await supabase.from("profiles").select("id").limit(1);
          return { status: error ? "degraded" : "ok", latency: Date.now() - t };
        } catch { return { status: "error", latency: null }; }
      })(),
      // Stripe check — ping deals API (requires auth, just check endpoint responds)
      (async () => {
        const t = Date.now();
        try {
          const r = await fetch("/api/lender-settings", { signal: AbortSignal.timeout(5000) });
          return { status: r.ok ? "ok" : "degraded", latency: Date.now() - t };
        } catch { return { status: "error", latency: null }; }
      })(),
      // Email check — ping notify-message endpoint existence (expect 401 not 500)
      (async () => {
        const t = Date.now();
        try {
          const r = await fetch("/api/notify-message", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}", signal: AbortSignal.timeout(5000) });
          return { status: r.status !== 500 ? "ok" : "degraded", latency: Date.now() - t };
        } catch { return { status: "error", latency: null }; }
      })(),
    ]);

    const ts = new Date().toISOString();
    setChecks(prev => prev.map((c, i) => ({ ...c, ...results[i], checked: ts })));
  }, []);

  useEffect(() => { runChecks(); }, [runChecks]);

  const statusConfig = {
    checking: { color: "#94A3B8", bg: "#F1F5F9",  label: "Checking…",    dot: "#94A3B8" },
    ok:       { color: "#166534", bg: "#DCFCE7",  label: "Operational",  dot: "#16A34A" },
    degraded: { color: "#92400E", bg: "#FEF3C7",  label: "Degraded",     dot: "#D97706" },
    error:    { color: "#991B1B", bg: "#FEE2E2",  label: "Down",         dot: "#DC2626" },
  };

  const overallOk = checks.every(c => c.status === "ok");
  const anyError  = checks.some(c => c.status === "error");
  const overall   = anyError ? "error" : overallOk ? "ok" : checks.some(c => c.status === "checking") ? "checking" : "degraded";

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: "2rem 1.25rem" }}>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, fontWeight: 700, color: "#1E3A5F", margin: "0 0 0.5rem" }}>System Status</h1>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: statusConfig[overall].bg, color: statusConfig[overall].color, borderRadius: 20, padding: "6px 16px", fontSize: 14, fontWeight: 600 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: statusConfig[overall].dot, display: "inline-block" }} />
          {overall === "ok" ? "All systems operational" : overall === "checking" ? "Checking systems…" : overall === "degraded" ? "Some systems degraded" : "Service disruption"}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: "1.5rem" }}>
        {checks.map(c => {
          const cfg = statusConfig[c.status] || statusConfig.checking;
          return (
            <div key={c.name} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F" }}>{c.name}</div>
                {c.checked && <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>Last checked {fmtTimeAgo(c.checked)}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: cfg.color, background: cfg.bg, borderRadius: 20, padding: "3px 10px" }}>{cfg.label}</span>
                {c.latency != null && <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>{c.latency}ms</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: "center" }}>
        <button onClick={runChecks} style={{ background: "#2E5FA3", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
          Refresh checks
        </button>
      </div>

      <div style={{ marginTop: "2rem", background: "#F8FAFC", borderRadius: 12, padding: "16px 20px", fontSize: 13, color: "#64748B" }}>
        <strong style={{ color: "#1E3A5F" }}>Incident history:</strong> No incidents in the last 30 days.
      </div>
    </div>
  );
}

// ─── ANALYTICS PAGE (admin only) ─────────────────────────────────────────────

function AnalyticsPage({ user }) {
  const [dealsByMonth,  setDealsByMonth]  = useState([]);
  const [signupsByWeek, setSignupsByWeek] = useState([]);
  const [topBuilders,   setTopBuilders]   = useState([]);
  const [topLenders,    setTopLenders]    = useState([]);
  const [summary,       setSummary]       = useState(null);
  const [loading,       setLoading]       = useState(true);

  useEffect(() => {
    async function load() {
      try {
        // Deals per month + capital
        const { data: deals } = await supabase.from("deals").select("created_at, total_amount, finder_fee_status").order("created_at");
        if (deals) {
          const byMonth = {};
          deals.forEach(d => {
            const key = d.created_at?.slice(0, 7);
            if (!key) return;
            if (!byMonth[key]) byMonth[key] = { month: key, deals: 0, capital: 0, fees: 0 };
            byMonth[key].deals++;
            byMonth[key].capital += Number(d.total_amount) || 0;
            if (d.finder_fee_status === "paid") byMonth[key].fees += (Number(d.total_amount) || 0) * 0.01;
          });
          setDealsByMonth(Object.values(byMonth).slice(-12));

          const totalCapital = deals.reduce((s, d) => s + (Number(d.total_amount) || 0), 0);
          const totalFees    = deals.filter(d => d.finder_fee_status === "paid").reduce((s, d) => s + (Number(d.total_amount) || 0) * 0.01, 0);
          setSummary({ totalDeals: deals.length, totalCapital, totalFees, avgDeal: deals.length ? Math.round(totalCapital / deals.length) : 0 });
        }

        // Signups per week
        const { data: profiles } = await supabase.from("profiles").select("created_at").order("created_at");
        if (profiles) {
          const byWeek = {};
          profiles.forEach(p => {
            const d = new Date(p.created_at);
            d.setHours(0,0,0,0);
            d.setDate(d.getDate() - d.getDay()); // start of week
            const key = d.toISOString().slice(0, 10);
            if (!byWeek[key]) byWeek[key] = { week: key, signups: 0 };
            byWeek[key].signups++;
          });
          setSignupsByWeek(Object.values(byWeek).slice(-12));
        }

        // Top builders
        const { data: builders } = await supabase.from("builder_profiles")
          .select("user_id, projects_completed, total_value, profiles(full_name)").order("projects_completed", { ascending: false }).limit(5);
        setTopBuilders(builders || []);

        // Top lenders
        const { data: lenders } = await supabase.from("lender_profiles")
          .select("user_id, budget_max, return_type, profiles(full_name)").order("budget_max", { ascending: false }).limit(5);
        setTopLenders(lenders || []);

      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: "4rem", color: "#aaa" }}>Loading analytics…</div>;

  const fmtM = v => v >= 1000000 ? `£${(v/1000000).toFixed(1)}M` : v >= 1000 ? `£${Math.round(v/1000)}k` : `£${v}`;

  return (
    <div style={{ padding: "1.5rem 1.25rem", maxWidth: 1000, margin: "0 auto" }}>
      <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700, color: "#1E3A5F", margin: "0 0 1.5rem" }}>Analytics</h1>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: "2rem" }}>
          {[
            { label: "Total deals", val: summary.totalDeals, icon: "bar-chart" },
            { label: "Capital matched", val: fmtM(summary.totalCapital), icon: "dollar" },
            { label: "Finder's fees", val: fmtM(summary.totalFees), icon: "bank" },
            { label: "Avg deal size", val: fmtM(summary.avgDeal), icon: "trending-up" },
          ].map(({ label, val, icon }) => (
            <div key={label} style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "16px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}><Icon name={icon} size={13} className="lb-icon" /> {label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: "#1E3A5F" }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Deals per month bar chart */}
      {dealsByMonth.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "20px 24px", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F", marginBottom: 16 }}>Deals created per month</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dealsByMonth} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v, n) => [n === "capital" ? fmtM(v) : v, n === "capital" ? "Capital" : "Deals"]} />
              <Bar dataKey="deals" fill="#2E5FA3" radius={[4, 4, 0, 0]} name="Deals" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Capital per month line chart */}
      {dealsByMonth.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "20px 24px", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F", marginBottom: 16 }}>Capital matched per month</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={dealsByMonth} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtM(v)} />
              <Tooltip formatter={v => [fmtM(v), "Capital"]} />
              <Line type="monotone" dataKey="capital" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} name="Capital" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Signups per week */}
      {signupsByWeek.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "20px 24px", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F", marginBottom: 16 }}>User signups per week</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={signupsByWeek} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="signups" stroke="#D97706" strokeWidth={2} dot={{ r: 3 }} name="Signups" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Geographic breakdown by region */}
      {(topBuilders.length > 0 || topLenders.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: "1.5rem" }}>
          {/* Top builders */}
          {topBuilders.length > 0 && (
            <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "20px 24px" }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F", marginBottom: 12 }}>Top 5 builders</div>
              {topBuilders.map((b, i) => (
                <div key={b.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < 4 ? "0.5px solid #f0f0f0" : "none" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? "#D97706" : "#94A3B8", width: 20 }}>#{i+1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.profiles?.full_name || "Anonymous"}</div>
                    <div style={{ fontSize: 11, color: "#64748B" }}>{b.projects_completed || 0} projects · {fmtM(b.total_value || 0)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Top lenders */}
          {topLenders.length > 0 && (
            <div style={{ background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "20px 24px" }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A5F", marginBottom: 12 }}>Top 5 lenders by budget</div>
              {topLenders.map((l, i) => (
                <div key={l.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < 4 ? "0.5px solid #f0f0f0" : "none" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? "#D97706" : "#94A3B8", width: 20 }}>#{i+1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.profiles?.full_name || "Anonymous"}</div>
                    <div style={{ fontSize: 11, color: "#64748B" }}>{l.budget_max ? fmt(l.budget_max) : "Flexible"} · {l.return_type}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {dealsByMonth.length === 0 && signupsByWeek.length === 0 && (
        <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748B" }}>
          <div style={{ marginBottom: 12, color: "var(--accent)" }}><Icon name="trending-up" size={32} /></div>
          <div style={{ fontSize: 15, fontWeight: 500 }}>No data yet</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Analytics will populate as deals are created and users sign up.</div>
        </div>
      )}
    </div>
  );
}

// ─── SHARED CONSTANTS ────────────────────────────────────────────────────────

const PROPERTY_IMAGES = [
  "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1920&q=80",
  "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80",
  "https://images.unsplash.com/photo-1582407947304-fd86f28f7d8e?w=1920&q=80",
  "https://images.unsplash.com/photo-1464082354059-27db6ce50048?w=1920&q=80",
  "https://images.unsplash.com/photo-1523217582562-09d0def993a6?w=1920&q=80",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80",
];

// Trust cards shared by the login loader and the onboarding welcome modal
const TRUST_ITEMS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" style={{ display: "block", flexShrink: 0 }}>
        <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z"
          fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: "Bank-level security",
    desc: "All data encrypted and protected",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" style={{ display: "block", flexShrink: 0 }}>
        <circle cx="12" cy="12" r="9.5" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5"/>
        <path d="M8 12l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: "Verified users only",
    desc: "Every member is ID checked before connecting",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" style={{ display: "block", flexShrink: 0 }}>
        <path d="M12 2L4 6v6c0 4.5 3.33 8.74 8 9.93C17.67 20.74 21 16.5 21 12V6l-9-4z"
          fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M12 8v4M12 14.5v.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    title: "Legal charge required",
    desc: "Lender investments secured against property",
  },
  {
    icon: (
      <div style={{ display: "block", width: 80, height: 28, flexShrink: 0 }}>
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg"
          alt="Stripe" width="80"
          style={{ display: "block", filter: "brightness(0) invert(1)", height: 28, objectFit: "contain" }}
        />
      </div>
    ),
    title: "Payments secured by Stripe",
    desc: "The same technology used by Amazon and Google",
  },
];

// ─── LOGIN LOADER (removed — login now goes straight to dashboard) ────────────

function LoginLoader({ onDone }) {
  useEffect(() => { onDone(); }, []); // eslint-disable-line
  return null;
  const [fading, setFading] = useState(false);
  const heroImage = useMemo(() => PROPERTY_IMAGES[Math.floor(Math.random() * PROPERTY_IMAGES.length)], []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10020,
      background: "#1E3A5F",
      display: "flex", alignItems: "center", justifyContent: "center",
      opacity: fading ? 0 : 1,
      transition: "opacity 0.5s ease",
      pointerEvents: fading ? "none" : "all",
    }}>
      {/* Background image */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `url(${heroImage})`,
        backgroundSize: "cover", backgroundPosition: "center", zIndex: 0,
      }} />
      {/* Dark overlay */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.62)", zIndex: 1 }} />

      {/* Content */}
      <div style={{
        position: "relative", zIndex: 2,
        width: "100%", maxWidth: 600,
        padding: "clamp(32px, 5vw, 56px) clamp(20px, 5vw, 48px) clamp(56px, 7vw, 80px)",
        textAlign: "center",
        animation: "tourBounce 0.45s ease forwards",
      }}>
        {/* Shield badge */}
        <div style={{
          width: 64, height: 64, background: "rgba(255,255,255,0.15)",
          border: "1.5px solid rgba(255,255,255,0.3)", borderRadius: 18,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px", backdropFilter: "blur(8px)",
        }}>
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
            <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z" fill="white"/>
          </svg>
        </div>

        <h2 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(28px, 4vw, 40px)",
          fontWeight: 700, color: "#fff",
          margin: "0 0 10px", lineHeight: 1.15,
          textShadow: "0 2px 12px rgba(0,0,0,0.3)",
        }}>
          LenderBuild
        </h2>
        <p style={{
          fontSize: "clamp(14px, 2vw, 17px)", color: "rgba(255,255,255,0.75)",
          margin: "0 0 32px", lineHeight: 1.5,
        }}>
          The UK's trusted property investment platform
        </p>

        {/* Trust cards */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
          marginBottom: 0, textAlign: "left",
        }}>
          {TRUST_ITEMS.map((t, i) => (
            <div key={i} style={{
              background: "rgba(255,255,255,0.13)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 14, padding: "14px 13px 13px",
              display: "flex", flexDirection: "column", gap: 9,
            }}>
              {t.icon}
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", lineHeight: 1.3, marginBottom: 2 }}>{t.title}</div>
                <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.65)", lineHeight: 1.4 }}>{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Loading bar — fills over 2.5s */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: 4,
        background: "rgba(255,255,255,0.12)", zIndex: 3,
      }}>
        <div style={{
          height: "100%",
          background: "linear-gradient(90deg, #3B82F6, #60A5FA)",
          animationName: "loginBarFill",
          animationDuration: "2.5s",
          animationTimingFunction: "cubic-bezier(0.4, 0, 0.6, 1)",
          animationFillMode: "forwards",
        }} />
      </div>
    </div>
  );
}

// ─── ONBOARDING TOUR ─────────────────────────────────────────────────────────
// step 1 = welcome modal (no spotlight, no skip)
// steps 2–8 = TOUR_STEPS[step-2] (spotlight steps 1–7)

const TOUR_STEPS = [
  {
    targetId: null, // resolved dynamically to tour-find-lender or tour-find-builder
    title: "Find your perfect match",
    text: "Browse verified lenders and builders filtered by budget, location, and project type. Your compatibility score shows how well you're matched — the higher the score, the better the fit.",
  },
  {
    targetId: "tour-inbox-btn",
    title: "Connect and send requests",
    text: "Express interest in a profile and send a connection request. Once accepted, your inbox unlocks a direct conversation — everything in one place, no chasing emails.",
  },
  {
    targetId: null,
    title: "Confirm a deal amount",
    text: "When you're ready to move forward, both parties confirm the loan amount, interest rate, and timeline inside the platform. Nothing moves until both sides have agreed.",
  },
  {
    targetId: null,
    title: "Finder's fee",
    text: "A small platform fee is charged once a deal is confirmed. Payment is handled securely through Stripe — you'll see the exact amount before you're charged, with no surprises.",
  },
  {
    targetId: "tour-project-tracker",
    title: "Project Tracker & milestones",
    text: "Every deal is managed through the Project Tracker. Funds are released milestone by milestone — both sides must approve each stage before the next one unlocks. Full accountability, no disputes.",
  },
  {
    targetId: "tour-browse-projects",
    title: "Group funding rooms",
    text: "Multiple lenders can co-fund a single project. Join a funding room, commit your share, and track the total commitment in real time. Chat directly with the builder and fellow investors inside the room.",
  },
  {
    targetId: null,
    title: "Community posts & groups",
    text: "Share market insights, ask questions, and join groups in the LenderBuild community. Build your reputation through upvotes and a verified profile badge that boosts your credibility with matches.",
  },
];

const TOUR_TOTAL = TOUR_STEPS.length; // 7 steps
const SPOTLIGHT_PAD = 10;

function TourConfetti() {
  const pieces = useMemo(() => Array.from({ length: 50 }, (_, i) => {
    const colors = ["#3B82F6","#1D9E75","#F59E0B","#EF4444","#8B5CF6","#EC4899","#06B6D4","#FBBF24"];
    return {
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2.5,
      duration: 3.5 + Math.random() * 2.5,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 7 + Math.random() * 7,
      isRect: Math.random() > 0.45,
      rEnd: `${(Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 360)}deg`,
    };
  }), []);

  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 10010, overflow: "hidden" }}>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: "absolute",
          left: `${p.left}%`,
          top: -30,
          width: p.size,
          height: p.isRect ? p.size * 0.45 : p.size,
          background: p.color,
          borderRadius: p.isRect ? 2 : "50%",
          "--r-end": p.rEnd,
          animationName: "confettiFall",
          animationDuration: `${p.duration}s`,
          animationDelay: `${p.delay}s`,
          animationFillMode: "forwards",
          animationTimingFunction: "ease-in",
        }} />
      ))}
    </div>
  );
}

function TourWelcomeModal({ onStart }) {
  const heroImage = useMemo(() => PROPERTY_IMAGES[Math.floor(Math.random() * PROPERTY_IMAGES.length)], []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9997,
      background: "#1E3A5F",
      display: "flex", alignItems: "center", justifyContent: "center",
      overflowY: "auto",
    }}>
      {/* Background property image */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `url(${heroImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        zIndex: 0,
      }} />
      {/* Dark overlay */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.62)", zIndex: 1 }} />

      {/* Content */}
      <div
        style={{
          position: "relative", zIndex: 2,
          width: "100%", maxWidth: 600,
          padding: "clamp(32px, 5vw, 56px) clamp(20px, 5vw, 48px) clamp(36px, 5vw, 56px)",
          textAlign: "center",
          animation: "tourBounce 0.45s ease forwards",
        }}
      >
        {/* Shield badge */}
        <div style={{
          width: 64, height: 64, background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.3)",
          borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 24px", backdropFilter: "blur(8px)",
        }}>
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
            <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z" fill="white"/>
          </svg>
        </div>

        <h2 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(32px, 5vw, 48px)",
          fontWeight: 700, color: "#fff",
          margin: "0 0 12px", lineHeight: 1.15,
          textShadow: "0 2px 12px rgba(0,0,0,0.3)",
        }}>
          Welcome to LenderBuild
        </h2>
        <p style={{
          fontSize: "clamp(16px, 2.5vw, 20px)", color: "rgba(255,255,255,0.82)",
          margin: "0 0 36px", lineHeight: 1.5, fontWeight: 400,
        }}>
          The UK's trusted property investment matching platform
        </p>

        {/* 2×2 trust grid */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
          marginBottom: 36, textAlign: "left",
        }}>
          {TRUST_ITEMS.map((t, i) => (
            <div key={i} style={{
              background: "rgba(255,255,255,0.13)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 14,
              padding: "16px 14px 14px",
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              {t.icon}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", lineHeight: 1.3, marginBottom: 3 }}>{t.title}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.68)", lineHeight: 1.45 }}>{t.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={onStart}
          style={{
            width: "100%", padding: "16px 24px",
            background: "#fff", color: "#1E3A5F",
            border: "none", borderRadius: 14,
            fontSize: 17, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
            letterSpacing: "-0.01em",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#EBF2FF"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
        >
          Let's get started →
        </button>
      </div>
    </div>
  );
}

function OnboardingTour({ step, userRole, onNext, onBack, onSkip, onFinish, onRedo, setPage }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [spotRect, setSpotRect] = useState(null); // measured rect of highlighted element

  // step 1 = welcome modal; steps 2–(TOUR_TOTAL+1) = TOUR_STEPS[step-2]
  const isWelcome = step === 1;
  const isFinal   = step === TOUR_TOTAL + 1;
  const cfg       = !isWelcome ? ((() => {
    const s = [...TOUR_STEPS];
    s[1] = { ...s[1], targetId: userRole === "lender" ? "tour-find-builder" : "tour-find-lender" };
    return s;
  })()[step - 2] || {}) : {};
  const hasSpotlight = !isWelcome && !isFinal && !!cfg.targetId;

  // Lock page scroll while tour is active
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Measure target element: rAF to wait for paint, then retry once if missing/hidden.
  useEffect(() => {
    if (!hasSpotlight) { setSpotRect(null); return; }
    let cancelled = false;
    let retryTimer = null;
    const measure = () => {
      const el = document.getElementById(cfg.targetId);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return null;
      return { top: r.top, left: r.left, width: r.width, height: r.height };
    };
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      const rect = measure();
      if (rect) { setSpotRect(rect); return; }
      retryTimer = setTimeout(() => {
        if (cancelled) return;
        setSpotRect(measure());
      }, 200);
    });
    return () => { cancelled = true; cancelAnimationFrame(rafId); if (retryTimer) clearTimeout(retryTimer); };
  }, [step]); // eslint-disable-line

  // Re-measure on resize
  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 640);
      if (!hasSpotlight || !cfg.targetId) return;
      const el = document.getElementById(cfg.targetId);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setSpotRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [hasSpotlight, cfg.targetId]); // eslint-disable-line

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e) {
      if (isWelcome) return;
      if (e.key === "ArrowRight") onNext();
      else if (e.key === "ArrowLeft" && step > 2) onBack();
      else if (e.key === "Escape" && !isFinal) onSkip();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isWelcome, isFinal, step, onNext, onBack, onSkip]); // eslint-disable-line

  if (isWelcome) return <TourWelcomeModal onStart={onNext} />;

  const PAD = SPOTLIGHT_PAD;
  const vw = window.innerWidth, vh = window.innerHeight;

  // Pre-compute spotlight bounds for both the overlay frame and the glow ring
  // Clamp to viewport to prevent negative heights / off-screen overlays
  const sTop    = spotRect ? Math.max(0,  spotRect.top    - PAD) : 0;
  const sLeft   = spotRect ? Math.max(0,  spotRect.left   - PAD) : 0;
  const sRight  = spotRect ? Math.min(vw, spotRect.left   + spotRect.width  + PAD) : 0;
  const sBottom = spotRect ? Math.min(vh, spotRect.top    + spotRect.height + PAD) : 0;
  const sW      = Math.max(0, sRight - sLeft);
  const sH      = Math.max(0, sBottom - sTop);

  // Tooltip placement relative to the highlighted element
  function getLayout() {
    const W = 320, M = 14;
    if (isFinal) return {
      outer: { top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: Math.min(440, vw - 28) },
      inner: { borderRadius: 20, padding: "32px 28px 26px", textAlign: "center" },
    };
    if (isMobile) return {
      outer: { bottom: 0, left: 0, right: 0 },
      inner: { borderRadius: "22px 22px 0 0", padding: "22px 20px 38px", maxHeight: "58vh", overflowY: "auto" },
    };
    if (!spotRect) return {
      outer: { top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: Math.min(W, vw - 28) },
      inner: { borderRadius: 16, padding: "20px 20px 16px" },
    };
    const spBot = spotRect.top + spotRect.height + PAD, spTop = spotRect.top - PAD;
    const spRight = spotRect.left + spotRect.width + PAD, spLeft = spotRect.left - PAD;
    let top, left;
    if      (vh - spBot >= 220) { top = spBot + M; left = Math.max(M, Math.min(spotRect.left + spotRect.width / 2 - W / 2, vw - W - M)); }
    else if (spTop      >= 220) { top = spTop - M - 220; left = Math.max(M, Math.min(spotRect.left + spotRect.width / 2 - W / 2, vw - W - M)); }
    else if (vw - spRight >= W + M) { left = spRight + M; top = Math.max(M, spotRect.top + spotRect.height / 2 - 110); }
    else                        { left = Math.max(M, spLeft - M - W); top = Math.max(M, spotRect.top + spotRect.height / 2 - 110); }
    return {
      outer: { top: Math.max(M, Math.min(top, vh - 240)), left: Math.max(M, Math.min(left, vw - W - M)), width: W },
      inner: { borderRadius: 16, padding: "20px 20px 16px" },
    };
  }

  const { outer, inner } = getLayout();
  const dotStep = step - 1;
  const dots = Array.from({ length: TOUR_TOTAL }, (_, i) => i + 1);
  const displayStep = step - 1;

  const arrowBtnBase = {
    position: "fixed",
    bottom: isMobile ? 24 : "50%",
    transform: isMobile ? "none" : "translateY(50%)",
    width: isMobile ? 48 : 56, height: isMobile ? 48 : 56,
    borderRadius: "50%", background: "#1E3A5F", border: "none", color: "#fff",
    cursor: "pointer", zIndex: 10001, display: "flex", alignItems: "center",
    justifyContent: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
    transition: "background 0.2s, opacity 0.2s",
  };

  return (
    <>
      {/* Dark overlay — 4 divs forming a frame around the spotlight cutout.
          Each div fills one side of the screen; together they leave a clean rectangular
          hole exactly where the target element is. No clip-path, no transitions. */}
      {spotRect && hasSpotlight ? (
        <>
          {/* Top band: full width, from screen top to top of spotlight */}
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: sTop, background: "rgba(0,0,0,0.75)", zIndex: 9996, pointerEvents: "none" }} />
          {/* Bottom band: full width, from bottom of spotlight to screen bottom */}
          <div style={{ position: "fixed", top: sBottom, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.75)", zIndex: 9996, pointerEvents: "none" }} />
          {/* Left band: from top of spotlight to bottom of spotlight, left of spotlight */}
          <div style={{ position: "fixed", top: sTop, left: 0, width: sLeft, height: sH, background: "rgba(0,0,0,0.75)", zIndex: 9996, pointerEvents: "none" }} />
          {/* Right band: from top of spotlight to bottom of spotlight, right of spotlight */}
          <div style={{ position: "fixed", top: sTop, left: sRight, right: 0, height: sH, background: "rgba(0,0,0,0.75)", zIndex: 9996, pointerEvents: "none" }} />
        </>
      ) : (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 9996, pointerEvents: "none" }} />
      )}

      {/* Glow ring — snaps instantly to the element; only the box-shadow colour pulses */}
      {spotRect && hasSpotlight && (
        <div style={{
          position: "fixed",
          top: sTop, left: sLeft, width: sW, height: sH,
          borderRadius: 10,
          zIndex: 9997, pointerEvents: "none",
          animation: "tourGlowPulse 2.5s ease-in-out 0.6s infinite",
        }} />
      )}

      {/* Edge navigation arrows */}
      {!isFinal && (
        <>
          <button onClick={step > 2 ? onBack : undefined} disabled={step <= 2}
            aria-label="Previous step"
            style={{ ...arrowBtnBase, left: isMobile ? 10 : 18, opacity: step <= 2 ? 0.35 : 1 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <button onClick={onNext} aria-label="Next step" style={{ ...arrowBtnBase, right: isMobile ? 10 : 18 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </>
      )}

      {/* Confetti on final step */}
      {isFinal && <TourConfetti />}

      {/* Tooltip — fades in on each step change via key={step} remount */}
      <div style={{ position: "fixed", zIndex: 10000, ...outer }} onClick={e => e.stopPropagation()}>
        <div key={step} style={{
          background: "#fff",
          boxShadow: "0 24px 64px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.08)",
          animation: "fadeIn 0.2s ease forwards",
          ...inner,
        }}>
          {!isFinal && (
            <div style={{ fontSize: 11, fontWeight: 700, color: "#3B82F6", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8 }}>
              Step {displayStep} of {TOUR_TOTAL}
            </div>
          )}
          <div style={{ fontSize: isFinal ? 24 : 17, fontWeight: 700, color: "#1E3A5F", marginBottom: 9, fontFamily: "'Playfair Display', Georgia, serif", lineHeight: 1.3 }}>
            {cfg.title}
          </div>
          <p style={{ fontSize: 14, color: "#4B5563", lineHeight: 1.65, margin: "0 0 16px" }}>
            {cfg.text}
          </p>

          {isFinal && (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", justifyContent: "center" }}>
                <button onClick={() => { onFinish(); setPage(userRole === "lender" ? "find-builder" : "search"); }}
                  style={{ flex: 1, minWidth: 130, padding: "13px 16px", background: "#2E5FA3", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Find a match
                </button>
                <button onClick={() => { onFinish(); setPage("browse-projects"); }}
                  style={{ flex: 1, minWidth: 130, padding: "13px 16px", background: "#1D9E75", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Browse projects
                </button>
              </div>
              <div style={{ textAlign: "center", marginBottom: 6 }}>
                <button onClick={onFinish}
                  style={{ background: "transparent", border: "none", fontSize: 13, color: "#94A3B8", cursor: "pointer", padding: "4px 8px" }}>
                  Done
                </button>
              </div>
              <div style={{ marginTop: 6, padding: "10px 14px", background: "#F8FAFC", borderRadius: 10, textAlign: "center" }}>
                <span style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
                  You can replay this tour anytime from Settings, or{" "}
                  <button
                    onClick={onRedo}
                    style={{ background: "none", border: "none", color: "#3B82F6", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" }}
                  >
                    tap here to replay now
                  </button>
                </span>
              </div>
            </>
          )}

          {!isFinal && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={onBack} disabled={step === 2}
                style={{ flex: isMobile ? 1 : "none", padding: isMobile ? "13px 20px" : "10px 18px", minHeight: isMobile ? 50 : 42, background: "transparent", border: "0.5px solid #d0d0d0", borderRadius: 10, fontSize: 14, fontWeight: 500, color: step === 2 ? "#ccc" : "#4B5563", cursor: step === 2 ? "default" : "pointer" }}>
                ← Back
              </button>
              <button onClick={onNext}
                style={{ flex: isMobile ? 1 : "none", padding: isMobile ? "13px 20px" : "10px 22px", minHeight: isMobile ? 50 : 42, background: "#2E5FA3", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Next →
              </button>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 14, alignItems: "center" }}>
            {dots.map(d => (
              <div key={d} style={{
                height: d === dotStep ? 12 : 8, width: d === dotStep ? 12 : 8,
                borderRadius: "50%",
                background: d === dotStep ? "#2E5FA3" : "#CBD5E1",
                transform: d === dotStep ? "scale(1.15)" : "scale(1)",
                transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
              }} />
            ))}
          </div>

          {!isFinal && (
            <div style={{ textAlign: "center", marginTop: 10 }}>
              <button onClick={onSkip}
                style={{ background: "none", border: "none", fontSize: 12, color: "#94A3B8", cursor: "pointer", textDecoration: "underline", padding: "6px 8px", minHeight: 36 }}>
                Skip tour
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── HELP TOOLTIP ─────────────────────────────────────────────────────────────
function HelpTooltip({ text }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, below: false });

  useEffect(() => {
    if (!open) return;
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function calcPos() {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const mobile = window.innerWidth < 640;
    const tipW = mobile ? Math.min(220, window.innerWidth - 24) : 240;
    let left = rect.left + rect.width / 2 - tipW / 2;
    if (left < 8) left = 8;
    if (left + tipW > window.innerWidth - 8) left = window.innerWidth - tipW - 8;
    const below = mobile || rect.top < 100;
    const top = below ? rect.bottom + 6 : rect.top - 6;
    setPos({ top, left, below });
  }

  function handleClick(e) { e.stopPropagation(); calcPos(); setOpen(o => !o); }
  function handleEnter() { if (window.innerWidth >= 640) { calcPos(); setOpen(true); } }
  function handleLeave() { if (window.innerWidth >= 640) setOpen(false); }

  const isMobileTip = typeof window !== "undefined" && window.innerWidth < 640;
  const TIP_W = isMobileTip ? Math.min(220, window.innerWidth - 24) : 240;
  const tooltip = open ? createPortal(
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: "fixed", top: pos.top, left: pos.left,
        transform: pos.below ? "none" : "translateY(-100%)",
        background: "#1E3A5F", color: "#fff", fontSize: 12, lineHeight: 1.55,
        padding: "9px 12px", borderRadius: 8, width: TIP_W,
        zIndex: 9999, boxShadow: "0 4px 16px rgba(0,0,0,0.25)", pointerEvents: "none",
      }}
    >
      {text}
      {pos.below
        ? <div style={{ position: "absolute", top: -5, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderBottom: "5px solid #1E3A5F" }} />
        : <div style={{ position: "absolute", bottom: -5, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid #1E3A5F" }} />
      }
    </div>,
    document.body
  ) : null;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}
      onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={handleClick}
        style={{ width: 18, height: 18, minWidth: 18, minHeight: 18, aspectRatio: "1 / 1", borderRadius: "50%", border: "none", background: "#3B82F6", color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, padding: 0, flexShrink: 0, verticalAlign: "middle", boxSizing: "border-box" }}
        title="Help"
      >?</button>
      {tooltip}
    </span>
  );
}

// ─── COMMAND PALETTE ──────────────────────────────────────────────────────────
// ─── DEVELOPER PANEL (Feature 1) ─────────────────────────────────────────────
function DevPanel({ user, onClose, devRoleOverride, setDevRoleOverride }) {
  const [showMeta,    setShowMeta]    = useState(false);
  const [copied,      setCopied]      = useState(false);
  const [simStatus,   setSimStatus]   = useState("");
  const [clearStatus, setClearStatus] = useState("");
  const role = user?.user_metadata?.role;
  if (role !== "admin" && role !== "founder") return null;

  async function simulateNotification() {
    await supabase.from("notifications").insert({ user_id: user.id, type: "test", message: "Test notification from developer panel" });
    setSimStatus("Notification sent!"); setTimeout(() => setSimStatus(""), 3000);
  }

  async function clearMyData() {
    if (!window.confirm("This will clear your connections and notifications. Continue?")) return;
    setClearStatus("Clearing…");
    await supabase.auth.admin; // no-op guard
    await supabase.auth.updateUser({ data: { connections: [], builder_connections: [] } });
    await supabase.from("notifications").delete().eq("user_id", user.id);
    await supabase.auth.refreshSession();
    setClearStatus("Done!"); setTimeout(() => setClearStatus(""), 3000);
  }

  function copyId() {
    navigator.clipboard?.writeText(user.id).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  }

  const effectiveRole = devRoleOverride || role;
  const ROLES = ["builder", "lender", "admin"];

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 9990 }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(480px, 94vw)", background: "#0f172a", color: "#e2e8f0", borderRadius: 16, boxShadow: "0 24px 80px rgba(0,0,0,0.55)", zIndex: 9991, overflow: "hidden", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "0.5px solid #1e293b", background: "#020617", position: "sticky", top: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Icon name="wrench" size={18} className="lb-icon" />
            <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "monospace", color: "#7dd3fc" }}>Developer Panel</span>
            <span style={{ fontSize: 10, background: "#1e3a5f", color: "#7dd3fc", padding: "1px 7px", borderRadius: 4, fontWeight: 700 }}>{(role || "").toUpperCase()}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 20, padding: "0 4px", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Role switcher */}
          <div style={{ background: "#1e293b", borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>View as role</div>
            <div style={{ display: "flex", gap: 6 }}>
              {ROLES.map(r => (
                <button key={r} onClick={() => setDevRoleOverride(r === role ? null : r)}
                  style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
                    background: effectiveRole === r ? "#2563eb" : "#334155", color: effectiveRole === r ? "#fff" : "#94a3b8" }}>
                  {r}
                </button>
              ))}
            </div>
            {devRoleOverride && (
              <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 8 }}>
                ⚠ Viewing as <strong>{devRoleOverride}</strong> — dashboard reflects this role.
                <button onClick={() => setDevRoleOverride(null)} style={{ background: "none", border: "none", color: "#fbbf24", cursor: "pointer", textDecoration: "underline", fontSize: 11, marginLeft: 6 }}>Reset</button>
              </div>
            )}
          </div>

          {/* User ID */}
          <div style={{ background: "#1e293b", borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>User ID</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <code style={{ flex: 1, fontSize: 11, color: "#7dd3fc", fontFamily: "monospace", wordBreak: "break-all", background: "#0f172a", padding: "6px 10px", borderRadius: 6 }}>{user?.id}</code>
              <button onClick={copyId} style={{ flexShrink: 0, padding: "5px 12px", borderRadius: 6, border: "none", background: copied ? "#16a34a" : "#334155", color: "#fff", fontSize: 11, cursor: "pointer" }}>
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div style={{ background: "#1e293b", borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Actions</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button onClick={simulateNotification}
                style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: "#334155", color: "#e2e8f0", fontSize: 12, cursor: "pointer" }}>
                Simulate notification
              </button>
              <button onClick={clearMyData}
                style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: "#7f1d1d", color: "#fca5a5", fontSize: 12, cursor: "pointer" }}>
                {clearStatus || "Clear my data"}
              </button>
              {simStatus && <span style={{ fontSize: 12, color: "#4ade80" }}>{simStatus}</span>}
            </div>
          </div>

          {/* Raw metadata */}
          <div style={{ background: "#1e293b", borderRadius: 10, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>Raw user metadata</div>
              <button onClick={() => setShowMeta(m => !m)} style={{ background: "none", border: "none", color: "#7dd3fc", fontSize: 11, cursor: "pointer" }}>{showMeta ? "Hide" : "Show"}</button>
            </div>
            {showMeta && (
              <pre style={{ fontSize: 10, color: "#a3e635", background: "#0f172a", padding: "10px 12px", borderRadius: 6, overflowY: "auto", maxHeight: 200, margin: 0, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {JSON.stringify(user?.user_metadata, null, 2)}
              </pre>
            )}
          </div>

        </div>
      </div>
    </>,
    document.body
  );
}

const PALETTE_COMMANDS = [
  { cat: "Navigation", label: "Dashboard",          target: "home",                    icon: "home" },
  { cat: "Navigation", label: "Find lender",         target: "search",                  icon: "search" },
  { cat: "Navigation", label: "Find builder",        target: "find-builder",            icon: "hammer" },
  { cat: "Navigation", label: "Browse projects",     target: "browse-projects",         icon: "hard-hat" },
  { cat: "Navigation", label: "Messages",            target: "messages",                icon: "message" },
  { cat: "Navigation", label: "My deals",            target: "deals",                   icon: "bar-chart" },
  { cat: "Navigation", label: "Leaderboard",         target: "leaderboard",             icon: "trophy" },
  { cat: "Navigation", label: "Community",           target: "community-groups",        icon: "users" },
  { cat: "Navigation", label: "Posts",               target: "posts",                   icon: "pencil" },
  { cat: "Navigation", label: "Market Intelligence", target: "market",                  icon: "trending-up" },
  { cat: "Navigation", label: "Build Calculator",    target: "build-calculator",        icon: "calculator" },
  { cat: "Navigation", label: "Returns Calculator",  target: "build-calculator",        icon: "dollar" },
  { cat: "Navigation", label: "Notifications",       target: "notifications",           icon: "bell" },
  { cat: "Navigation", label: "Disputes",            target: "disputes",                icon: "scale" },
  { cat: "Actions",    label: "Post a project",      target: "create-project-listing",  icon: "plus" },
  { cat: "Actions",    label: "Create a post",       target: "create-post",             icon: "edit" },
  { cat: "Actions",    label: "Saved profiles",      target: "saved-profiles",          icon: "heart" },
  { cat: "Settings",   label: "Settings",            target: "account",                 icon: "settings" },
  { cat: "Settings",   label: "Edit profile",        target: "profile-setup",           icon: "user" },
  { cat: "Settings",   label: "How it works",        target: "how-it-works",            icon: "circle-help" },
  { cat: "Settings",   label: "Trust & Safety",      target: "safety",                  icon: "shield" },
  { cat: "Developer",  label: "/dev — Developer panel", target: "__dev_panel__",           icon: "settings" },
];

function CommandPalette({ open, onClose, setPage, user, onOpenDevPanel, onViewProfile, onViewBuilderProfile }) {
  const [query,      setQuery]      = useState("");
  const [cursor,     setCursor]     = useState(0);
  const [idResult,   setIdResult]   = useState(null);
  const [idSearching, setIdSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setQuery(""); setCursor(0); setIdResult(null); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  // Detect #NNN pattern for sequential ID search (Feature 8)
  const isIdSearch = /^#?\d+$/.test(query.trim()) && query.trim().length > 0;

  useEffect(() => {
    if (!isIdSearch) { setIdResult(null); return; }
    const num = parseInt(query.trim().replace(/^#/, ""), 10);
    if (!num) return;
    setIdSearching(true);
    setIdResult(null);
    const timeout = setTimeout(async () => {
      try {
        const res = await fetch(`/api/get-profiles?type=by-id&id=${num}`);
        if (res.ok) { const { profile } = await res.json(); setIdResult(profile || null); }
        else setIdResult(null);
      } catch { setIdResult(null); }
      setIdSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, isIdSearch]);

  const userRole = user?.user_metadata?.role;
  const filtered = PALETTE_COMMANDS.filter(c => {
    if (c.target === "__dev_panel__" && userRole !== "admin" && userRole !== "founder") return false;
    if (isIdSearch) return false;
    return !query || c.label.toLowerCase().includes(query.toLowerCase()) || c.cat.toLowerCase().includes(query.toLowerCase());
  });

  const categories = [...new Set(filtered.map(c => c.cat))];

  function go(target) {
    if (target === "__dev_panel__") { onOpenDevPanel && onOpenDevPanel(); onClose(); return; }
    setPage(target); onClose();
  }

  function handleIdResultClick(profile) {
    if (!profile) return;
    const snap = { id: `real-${profile.id}`, _real: true, user_id: profile.id, name: profile.full_name || "Anonymous",
      initials: nameInitials(profile.full_name), color: pickColor(profile.id || ""),
      avatar_url: profile.avatar_url || null, location: profile.location || "", bio: profile.bio || "",
      user_role: profile.user_role, sequential_id: profile.sequential_id };
    if (profile.role === "lender" && onViewProfile) { onViewProfile({ ...snap, type: "Private lender", budget: "Flexible", project: "Any", preferred_projects: [], returnType: "Flexible", verified: false }); }
    else if (profile.role === "builder" && onViewBuilderProfile) { onViewBuilderProfile({ ...snap, type: "Builder", props: 0, value: 0, completion: 100, verified_documents: [] }); }
    onClose();
  }

  function handleKey(e) {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor(c => Math.min(c + 1, filtered.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === "Enter" && filtered[cursor]) go(filtered[cursor].target);
    if (e.key === "Enter" && isIdSearch && idResult) handleIdResultClick(idResult);
  }

  if (!open) return null;

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9998, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: "20%", left: "50%", transform: "translateX(-50%)", width: "min(560px, 92vw)", background: "#fff", borderRadius: 16, boxShadow: "0 24px 80px rgba(0,0,0,0.3)", zIndex: 9999, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "0.5px solid #f0f0f0" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setCursor(0); }}
            onKeyDown={handleKey}
            placeholder="Search pages and actions..."
            style={{ flex: 1, border: "none", outline: "none", fontSize: 15, color: "#1E293B", background: "transparent" }}
          />
          <kbd style={{ fontSize: 11, color: "#94A3B8", background: "#F1F5F9", border: "0.5px solid #CBD5E1", borderRadius: 4, padding: "2px 6px" }}>ESC</kbd>
        </div>
        <div style={{ maxHeight: 380, overflowY: "auto", padding: "8px 0" }}>
          {/* Sequential ID search results */}
          {isIdSearch && (
            <div>
              <div style={{ padding: "4px 16px 2px", fontSize: 10, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em" }}>Find by ID</div>
              {idSearching && <div style={{ padding: "12px 16px", fontSize: 13, color: "#94A3B8" }}>Searching…</div>}
              {!idSearching && idResult && (
                <button onClick={() => handleIdResultClick(idResult)}
                  style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 16px", background: "#EBF2FF", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <Avatar initials={nameInitials(idResult.full_name)} color={pickColor(idResult.id || "")} size={36} url={idResult.avatar_url} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F" }}>{idResult.full_name || "Anonymous"}</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>{idResult.role} · {fmtId(idResult.sequential_id)}</div>
                  </div>
                  <span style={{ fontSize: 12, color: "#2E5FA3", fontWeight: 500, flexShrink: 0 }}>View profile ↵</span>
                </button>
              )}
              {!idSearching && !idResult && query.length > 0 && (
                <div style={{ padding: "12px 16px", fontSize: 13, color: "#94A3B8" }}>No user with that ID.</div>
              )}
            </div>
          )}
          {!isIdSearch && (categories.length === 0 ? (
            <div style={{ padding: "24px", textAlign: "center", color: "#94A3B8", fontSize: 14 }}>No results for "{query}"</div>
          ) : categories.map(cat => {
            const items = filtered.filter(c => c.cat === cat);
            return (
              <div key={cat}>
                <div style={{ padding: "4px 16px 2px", fontSize: 10, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em" }}>{cat}</div>
                {items.map((cmd) => {
                  const globalIdx = filtered.indexOf(cmd);
                  const active = globalIdx === cursor;
                  return (
                    <button key={cmd.target + cmd.label} onClick={() => go(cmd.target)} onMouseEnter={() => setCursor(globalIdx)}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 16px", fontSize: 14, background: active ? "#EBF2FF" : "transparent", border: "none", cursor: "pointer", textAlign: "left", color: active ? "#1E3A5F" : "#374151" }}>
                      <Icon name={cmd.icon} size={16} className="lb-icon" />
                      <span style={{ fontWeight: active ? 600 : 400 }}>{cmd.label}</span>
                      {active && <span style={{ marginLeft: "auto", fontSize: 11, color: "#94A3B8" }}>↵ Open</span>}
                    </button>
                  );
                })}
              </div>
            );
          }))}
        </div>
        <div style={{ padding: "8px 16px", borderTop: "0.5px solid #f0f0f0", display: "flex", gap: 12, fontSize: 11, color: "#94A3B8", flexWrap: "wrap" }}>
          <span><kbd style={{ background: "#F1F5F9", border: "0.5px solid #CBD5E1", borderRadius: 3, padding: "1px 4px", fontSize: 10 }}>↑↓</kbd> Navigate</span>
          <span><kbd style={{ background: "#F1F5F9", border: "0.5px solid #CBD5E1", borderRadius: 3, padding: "1px 4px", fontSize: 10 }}>↵</kbd> Open</span>
          <span><kbd style={{ background: "#F1F5F9", border: "0.5px solid #CBD5E1", borderRadius: 3, padding: "1px 4px", fontSize: 10 }}>Ctrl+K</kbd> Toggle</span>
          <span style={{ color: "#3B82F6" }}><kbd style={{ background: "#EBF2FF", border: "0.5px solid #CBD5E1", borderRadius: 3, padding: "1px 4px", fontSize: 10 }}>#42</kbd> Find by ID</span>
        </div>
      </div>
    </>,
    document.body
  );
}

// ─── ONBOARDING CHECKLIST ─────────────────────────────────────────────────────
function OnboardingChecklist({ user, setPage }) {
  const [expanded, setExpanded] = useState(true);
  if (!user) return null;
  const uid = user.id;
  const dismissed = (() => { try { return localStorage.getItem(`lb_checklist_dismissed_${uid}`) === "true"; } catch { return false; } })();

  const meta = user?.user_metadata || {};
  const steps = [
    { label: "Complete profile",         done: !!meta.profile_complete,          target: "profile-setup" },
    { label: "Find your first match",    done: !!localStorage.getItem(`lb_first_match_${uid}`), target: meta.role === "lender" ? "find-builder" : "search" },
    { label: "Send a connection",        done: (meta.connections?.length > 0 || meta.builder_connections?.length > 0), target: meta.role === "lender" ? "find-builder" : "search" },
    { label: "Start a conversation",     done: !!localStorage.getItem(`lb_first_msg_${uid}`),   target: "messages" },
    { label: "Create/browse a project",  done: !!localStorage.getItem(`lb_first_project_${uid}`), target: meta.role === "lender" ? "browse-projects" : "create-project-listing" },
  ];
  const doneCount = steps.filter(s => s.done).length;
  if (dismissed || doneCount === steps.length) return null;

  return (
    <div style={{ position: "fixed", bottom: "calc(88px + env(safe-area-inset-bottom, 0px))", left: 16, zIndex: 1050, width: 240, background: "#fff", borderRadius: 14, boxShadow: "0 4px 24px rgba(0,0,0,0.18)", border: "0.5px solid #e0e0e0", overflow: "hidden" }}>
      <button onClick={() => setExpanded(o => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#1E3A5F", border: "none", cursor: "pointer", color: "#fff" }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Getting started · {doneCount}/{steps.length}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}><path d="M6 9l6 6 6-6"/></svg>
      </button>
      <div style={{ height: 4, background: "#f0f0f0" }}>
        <div style={{ height: 4, background: "#22C55E", width: `${(doneCount / steps.length) * 100}%`, transition: "width 0.4s" }} />
      </div>
      {expanded && (
        <div style={{ padding: "8px 0" }}>
          {steps.map((s, i) => (
            <button key={i} onClick={() => setPage(s.target)}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 14px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
              onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: s.done ? "#22C55E" : "#F1F5F9", border: s.done ? "none" : "1.5px solid #CBD5E1" }}>
                {s.done ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        : <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#CBD5E1" }} />}
              </span>
              <span style={{ fontSize: 12, color: s.done ? "#94A3B8" : "#374151", textDecoration: s.done ? "line-through" : "none", fontWeight: s.done ? 400 : 500 }}>{s.label}</span>
            </button>
          ))}
          <div style={{ borderTop: "0.5px solid #f0f0f0", marginTop: 4, padding: "6px 14px" }}>
            <button onClick={() => { try { localStorage.setItem(`lb_checklist_dismissed_${uid}`, "true"); } catch {} window.location.reload(); }}
              style={{ fontSize: 11, color: "#94A3B8", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SWITCH ACCOUNT PAGE ──────────────────────────────────────────────────────
// ─── PROFILE MENU PAGE ────────────────────────────────────────────────────────
function ProfileMenuPage({ user, setPage, onLogout }) {
  const role = user?.user_metadata?.role;
  const avatarUrl = user?.user_metadata?.avatar_url;
  const displayName = user?.user_metadata?.name || user?.email?.split("@")[0] || "";
  const [menuWidth, setMenuWidth] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setMenuWidth(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobileMenu = menuWidth < 768;
  const [savedAccounts, setSavedAccounts] = useState(() => getSavedAccounts().filter(a => a.user_id !== user?.id));
  const [switchLoadingId, setSwitchLoadingId] = useState(null);
  const [showSwitchOverlay, setShowSwitchOverlay] = useState("");
  const [switchWarning, setSwitchWarning] = useState("");
  const [expiredAcct, setExpiredAcct] = useState(null);
  const [expiredPassword, setExpiredPassword] = useState("");
  const [expiredError, setExpiredError] = useState("");
  const [addingAccount, setAddingAccount] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addPassword, setAddPassword] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");

  useEffect(() => {
    const accts = getSavedAccounts().filter(a => a.user_id !== user?.id);
    const missing = accts.filter(a => !a.member_id && a.user_id);
    if (!missing.length) return;
    (async () => {
      const { data } = await supabase.from("profiles").select("id, sequential_id").in("id", missing.map(a => a.user_id));
      if (!data?.length) return;
      let updated = false;
      data.forEach(p => {
        const acct = accts.find(a => a.user_id === p.id);
        if (acct && p.sequential_id) { acct.member_id = p.sequential_id; upsertSavedAccount(acct); updated = true; }
      });
      if (updated) setSavedAccounts(getSavedAccounts().filter(a => a.user_id !== user?.id));
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveCurrentSession() {
    const { data: { session: curSess } } = await supabase.auth.getSession();
    if (curSess && user) {
      const { data: prof } = await supabase.from("profiles").select("sequential_id").eq("id", user.id).maybeSingle();
      upsertSavedAccount({
        user_id: user.id, email: user.email, name: displayName,
        avatar_url: avatarUrl || null, role: role || null,
        member_id: prof?.sequential_id || null,
        access_token: curSess.access_token, refresh_token: curSess.refresh_token,
      });
    }
  }

  function doSwitchRedirect(acctFirstName) {
    if (acctFirstName) sessionStorage.setItem("lb_switching", JSON.stringify({ name: acctFirstName }));
    setShowSwitchOverlay(acctFirstName || "account");
    setTimeout(() => { window.location.href = "/"; }, 300);
  }

  async function handleSwitch(acct) {
    if (acct.email === user?.email) {
      setSwitchWarning("This is already your active account");
      return;
    }
    setSwitchWarning("");
    setSwitchLoadingId(acct.user_id);
    await supabase.auth.signOut();
    setSwitchLoadingId(null);
    setPage("auth", { authTab: "login", prefillEmail: acct.email });
  }

  async function handleSwitchWithPassword(e) {
    e.preventDefault();
    setSwitchLoadingId(expiredAcct.user_id);
    setExpiredError("");
    const { data, error } = await supabase.auth.signInWithPassword({ email: expiredAcct.email, password: expiredPassword });
    if (error) { setSwitchLoadingId(null); setExpiredError(error.message); return; }
    upsertSavedAccount({ ...expiredAcct, access_token: data.session.access_token, refresh_token: data.session.refresh_token });
    doSwitchRedirect(expiredAcct.name?.split(" ")[0] || expiredAcct.email.split("@")[0]);
  }

  async function handleAddAccount(e) {
    e.preventDefault();
    setAddLoading(true);
    setAddError("");
    const { data, error } = await supabase.auth.signInWithPassword({ email: addEmail, password: addPassword });
    setAddLoading(false);
    if (error) { setAddError(error.message); return; }
    await saveCurrentSession();
    let newAcctRole = data.user.user_metadata?.role || null;
    const { data: newProf } = await supabase.from("profiles").select("role, sequential_id").eq("id", data.user.id).maybeSingle();
    if (!newAcctRole) newAcctRole = newProf?.role || null;
    upsertSavedAccount({
      user_id: data.user.id, email: data.user.email,
      name: data.user.user_metadata?.name || data.user.email.split("@")[0],
      avatar_url: data.user.user_metadata?.avatar_url || null,
      role: newAcctRole, member_id: newProf?.sequential_id || null,
      access_token: data.session.access_token, refresh_token: data.session.refresh_token,
    });
    const addFirstName = (data.user.user_metadata?.name || data.user.email.split("@")[0]).split(" ")[0];
    sessionStorage.setItem("lb_switching", JSON.stringify({ name: addFirstName }));
    window.location.href = "/";
  }

  const navItems = [
    { label: "My profile",    target: "profile-setup",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
    { label: "My deals",      target: "deals",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg> },
    { label: "Messages",      target: "messages",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
    { label: "Notifications", target: "notifications",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg> },
    { label: "Settings",      target: "account",
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg> },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", marginBottom: isMobileMenu ? -44 : 0 }}>
      {/* Switch account overlay */}
      {showSwitchOverlay && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#1E3A5F", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700, letterSpacing: -0.5 }}>
            <span style={{ color: "#fff" }}>Lender</span><span style={{ color: "#3B82F6" }}>Build</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "rgba(255,255,255,0.75)", fontSize: 15 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "lb-spin 0.8s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
            Switching to {showSwitchOverlay}…
          </div>
          <style>{`@keyframes lb-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {/* Navbar */}
      <div style={{ background: "#1E3A5F", position: "sticky", top: 0, zIndex: 100, height: 56, display: "flex", alignItems: "center" }}>
        <button onClick={() => setPage("home")} style={{ background: "none", border: "none", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", padding: "8px 14px", lineHeight: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ flex: 1, textAlign: "center", color: "#fff", fontWeight: 600, fontSize: 17, marginRight: 44 }}>Account</div>
      </div>

      {/* User card */}
      <div style={{ background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", padding: "28px 20px 24px", borderBottom: "1px solid #efefef" }}>
        {avatarUrl
          ? <img src={avatarUrl} alt="" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", marginBottom: 12 }} />
          : <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#1E3A5F", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 700, marginBottom: 12 }}>{displayName.charAt(0).toUpperCase()}</div>
        }
        <div style={{ fontWeight: 700, fontSize: 18, color: "#1E3A5F", marginBottom: 8 }}>{displayName}</div>
        {role && <RoleBadge userRole={role} authRole={role} />}
        {/* Action pill */}
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button onClick={onLogout}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 24, border: "1.5px solid #D1D5DB", background: "transparent", color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Log out
          </button>
        </div>
      </div>

      {/* Saved accounts — one-tap switch */}
      {(savedAccounts.length > 0 || getSavedAccounts().length < 3) && (
        <div style={{ background: "#fff", marginTop: 12 }}>
          <div style={{ padding: "10px 20px 6px", fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.07em" }}>Switch account</div>
          {savedAccounts.map(acct => (
            <div key={acct.user_id}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", borderBottom: "1px solid #F1F5F9" }}>
                {acct.avatar_url
                  ? <img src={acct.avatar_url} alt="" style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                  : <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#1E3A5F", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{nameInitials(acct.name || acct.email)}</div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acct.name || acct.email.split("@")[0]}</span>
                    {acct.role && (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 10, background: acct.role === "lender" ? "#EFF6FF" : "#F0FDF4", color: acct.role === "lender" ? "#2563EB" : "#16A34A", border: `1px solid ${acct.role === "lender" ? "#BFDBFE" : "#BBF7D0"}`, flexShrink: 0, whiteSpace: "nowrap" }}>
                        {acct.role === "lender" ? "Lender" : "Builder"}
                      </span>
                    )}
                    {acct.member_id && (
                      <span style={{ fontSize: 10, color: "#aaa", fontWeight: 500, flexShrink: 0, whiteSpace: "nowrap" }}>{fmtId(acct.member_id)}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acct.email}</div>
                </div>
                <button onClick={() => handleSwitch(acct)} disabled={switchLoadingId !== null || showSwitchOverlay}
                  style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 20, border: "1.5px solid #1E3A5F", background: switchLoadingId === acct.user_id || showSwitchOverlay ? "#1E3A5F" : "transparent", color: switchLoadingId === acct.user_id || showSwitchOverlay ? "#fff" : "#1E3A5F", fontSize: 13, fontWeight: 600, cursor: (switchLoadingId !== null || showSwitchOverlay) ? "default" : "pointer", whiteSpace: "nowrap", transition: "background 0.15s, color 0.15s" }}>
                  {(switchLoadingId === acct.user_id || showSwitchOverlay) && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: "lb-spin 0.8s linear infinite", flexShrink: 0 }}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  )}
                  {switchLoadingId === acct.user_id || showSwitchOverlay ? "Switching…" : `Switch to ${acct.name?.split(" ")[0] || acct.email.split("@")[0]}`}
                </button>
              </div>
              {switchWarning && acct.email === user?.email && (
                <div style={{ padding: "8px 20px", fontSize: 13, color: "#92400E", background: "#FFFBEB", borderBottom: "1px solid #F1F5F9" }}>{switchWarning}</div>
              )}
              {/* Inline password prompt when token is expired */}
              {expiredAcct?.user_id === acct.user_id && (
                <form onSubmit={handleSwitchWithPassword} style={{ padding: "12px 20px 16px", background: "#FFFBEB", borderBottom: "1px solid #F1F5F9" }}>
                  <div style={{ fontSize: 13, color: "#92400E", marginBottom: 10 }}>Session expired — enter password for {acct.email}</div>
                  {expiredError && <div style={{ fontSize: 13, color: "#DC2626", marginBottom: 8 }}>{expiredError}</div>}
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="password" placeholder="Password" value={expiredPassword} onChange={e => setExpiredPassword(e.target.value)} required autoFocus
                      style={{ flex: 1, height: 42, border: "1px solid #E2E8F0", borderRadius: 8, padding: "0 12px", fontSize: 15, outline: "none" }} />
                    <button type="submit" disabled={switchLoadingId !== null}
                      style={{ padding: "0 16px", height: 42, background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                      {switchLoadingId === acct.user_id ? "…" : "Go"}
                    </button>
                    <button type="button" onClick={() => setExpiredAcct(null)}
                      style={{ padding: "0 12px", height: 42, background: "transparent", color: "#64748B", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          ))}
          {/* Add account */}
          {getSavedAccounts().length < 3 && !addingAccount && (
            <button onClick={() => setAddingAccount(true)}
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "0 20px", fontSize: 14, background: "transparent", border: "none", cursor: "pointer", minHeight: 48, color: "#3B82F6", fontWeight: 500 }}
              onTouchStart={e => e.currentTarget.style.background = "#EFF6FF"}
              onTouchEnd={e => e.currentTarget.style.background = "transparent"}
              onMouseEnter={e => e.currentTarget.style.background = "#EFF6FF"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ width: 28, height: 28, borderRadius: "50%", border: "1.5px dashed #3B82F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </div>
              Add account
            </button>
          )}
          {addingAccount && (
            <form onSubmit={handleAddAccount} style={{ padding: "14px 20px 18px" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F", marginBottom: 10 }}>Log in to another account</div>
              {addError && <div style={{ fontSize: 13, color: "#DC2626", marginBottom: 8 }}>{addError}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <input type="email" placeholder="Email" value={addEmail} onChange={e => setAddEmail(e.target.value)} required autoFocus
                  style={{ height: 44, border: "1px solid #E2E8F0", borderRadius: 8, padding: "0 12px", fontSize: 15, outline: "none" }} />
                <input type="password" placeholder="Password" value={addPassword} onChange={e => setAddPassword(e.target.value)} required
                  style={{ height: 44, border: "1px solid #E2E8F0", borderRadius: 8, padding: "0 12px", fontSize: 15, outline: "none" }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="submit" disabled={addLoading}
                    style={{ flex: 1, height: 44, background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: addLoading ? "default" : "pointer" }}>
                    {addLoading ? "Signing in…" : "Log in"}
                  </button>
                  <button type="button" onClick={() => { setAddingAccount(false); setAddEmail(""); setAddPassword(""); setAddError(""); }}
                    style={{ padding: "0 16px", height: 44, background: "transparent", color: "#64748B", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Nav links */}
      <div style={{ background: "#fff", marginTop: 12 }}>
        {navItems.map((item, i) => (
          <button key={item.target}
            onClick={() => { setPage(item.target); window.scrollTo({ top: 0, behavior: "instant" }); }}
            style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", padding: "0 20px", fontSize: 15, background: "transparent", border: "none", borderBottom: i < navItems.length - 1 ? "1px solid #f5f5f5" : "none", cursor: "pointer", minHeight: 52, color: "#1E3A5F", textAlign: "left" }}
            onTouchStart={e => e.currentTarget.style.background = "#f5f5f5"}
            onTouchEnd={e => e.currentTarget.style.background = "transparent"}
            onMouseEnter={e => e.currentTarget.style.background = "#f9f9f7"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <span style={{ color: "#64748B", lineHeight: 0, flexShrink: 0 }}>{item.icon}</span>
            <span style={{ fontWeight: 500, flex: 1 }}>{item.label}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C4C9D4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        ))}
      </div>

      {/* Bottom log out */}
      <div style={{ background: "#fff", marginTop: 12 }}>
        <button onClick={onLogout}
          style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", padding: "0 20px", fontSize: 15, background: "transparent", border: "none", cursor: "pointer", color: "#DC2626", minHeight: 52, fontWeight: 500 }}
          onTouchStart={e => e.currentTarget.style.background = "#FEF2F2"}
          onTouchEnd={e => e.currentTarget.style.background = "transparent"}
          onMouseEnter={e => e.currentTarget.style.background = "#FEF2F2"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Log out
        </button>
      </div>

    </div>
  );
}

// ─── HELP CENTRE PAGE ────────────────────────────────────────────────────────

function HelpContactForm() {
  return (
    <div style={{ marginTop: 16 }}>
      <ContactForm submitBtnColor="#1E3A5F" />
    </div>
  );
}

function HelpCentrePage({ setPage }) {
  const [activeCategory, setActiveCategory] = useState(null);
  const [openQuestion, setOpenQuestion]     = useState(null);
  const [search, setSearch]                 = useState("");
  const [helpful, setHelpful]               = useState({});

  const categories = [
    {
      id: "getting-started",
      title: "Getting started",
      color: "#2E5FA3",
      bg: "#EBF2FF",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2C8.5 5.5 7 9 7 13l5 5c4 0 7.5-1.5 11-5C20 7 17 2 12 2z"/>
          <circle cx="12" cy="12" r="2"/>
          <path d="M7 13c-2 1-3 3-3 4s2 1 3 0l2-2"/>
        </svg>
      ),
      questions: [
        { q: "How do I sign up?", a: "Tap Sign up on the homepage and enter your full name, email address and a strong password. Choose your role — Builder or Lender — then check your inbox and click the verification link we send you. Once verified you'll land straight in your dashboard." },
        { q: "How do I set up my profile?", a: "Go to Settings > Account and tap Edit Profile. Add your bio, location and a profile photo. Builders can also add their specialisation and project history. A complete profile boosts your match score and increases your visibility to other members." },
        { q: "How do I verify my identity?", a: "In Settings > Account, scroll down to the Identity Verification section. Follow the on-screen steps to confirm your personal details. Verified members receive a badge on their profile which increases trust with other members." },
        { q: "What is my member number?", a: "Your member number (e.g. LB-001234) is unique to your account. You'll find it on your profile page and in Settings > Account below your name. You can share it with other members so they can find you directly." },
        { q: "How do I switch between accounts?", a: "Tap your profile picture in the top right corner and look under Switch Account. Tap any saved account to switch instantly. If you don't see an account listed, tap Add Account to sign in with another email address — you can save up to three accounts." },
      ],
    },
    {
      id: "for-builders",
      title: "For builders",
      color: "#92400E",
      bg: "#FEF3C7",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 20h20"/>
          <path d="M4 20V10l8-6 8 6v10"/>
          <path d="M9 20v-5h6v5"/>
        </svg>
      ),
      questions: [
        { q: "How do I make a post?", a: "Tap Posts in the bottom navigation, then tap the + New Post button. Write your post, add a category such as Advice or Project Update, then tap Publish. Your post appears in the community feed and notifies your followers." },
        { q: "How do I find a lender?", a: "Tap Find a Lender in the navigation. Use the filters to narrow by loan type, maximum funding amount, region and preferred project types. Tap any lender card to view their full profile, then tap Express Interest to send a connection request." },
        { q: "How do I create a project?", a: "Go to Browse Projects and tap Create Project Listing. Fill in the project title, location, total development value, funding required and a description of the scheme. Upload supporting documents and tap Publish. Lenders can then discover and express interest in your project." },
        { q: "What documents do I need to upload?", a: "For a complete project listing we recommend uploading: planning permission documents, a site plan or layout drawing, proof of GDV valuation, and your building specification. Strong documentation significantly increases lender confidence and your match score." },
        { q: "How do I verify my company?", a: "Go to Settings > Companies House and enter your company registration number. We check it against Companies House automatically. If your company is active we'll show you the list of directors to confirm your identity, then add a CH Verified badge to your profile." },
      ],
    },
    {
      id: "for-lenders",
      title: "For lenders",
      color: "#166534",
      bg: "#DCFCE7",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="6" width="20" height="14" rx="2"/>
          <path d="M2 10h20"/>
          <circle cx="12" cy="15" r="2"/>
        </svg>
      ),
      questions: [
        { q: "How do I find builders to fund?", a: "Tap Find a Builder in the navigation. Filter by location, project type, loan size required and verification status. Tap a builder card to view their Builder Passport — a detailed profile showing their track record, documents and current projects." },
        { q: "How do I express interest in a project?", a: "Open any builder profile or project listing and tap the Express Interest button. The builder receives your connection request with your profile details attached. Once they accept, you'll both be able to message each other directly." },
        { q: "How do I track my investments?", a: "Go to Deals in the navigation to see all your active and completed deals. Each deal shows the current milestone status, upcoming repayments and the full deal timeline. Tap any deal to open the full Deal Room." },
        { q: "What returns can I expect?", a: "Returns depend on the deal structure agreed with the builder. Development finance typically offers between 8–15% per annum, but this varies by project risk, location and loan term. Always conduct your own due diligence and take independent financial advice before committing funds." },
        { q: "How do I calculate my returns?", a: "Open the Build Calculator from the More menu in the navigation. Enter the loan amount, interest rate, loan term and arrangement fee to see a full projected return breakdown. You can compare multiple scenarios side by side." },
      ],
    },
    {
      id: "payments-and-deals",
      title: "Payments and deals",
      color: "#534AB7",
      bg: "#EEEDFE",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2"/>
          <path d="M2 10h20"/>
          <path d="M6 15h2M10 15h4"/>
        </svg>
      ),
      questions: [
        { q: "How do milestone payments work?", a: "When a deal is created, both parties agree a set of milestones — for example, Foundations, Frame, Weathertight. Each milestone has a value attached. The builder requests release when that stage is complete, and the lender approves before funds are transferred." },
        { q: "How do I release a payment?", a: "Go to Deals and open the relevant deal. Find the milestone marked as Requested and tap the Release Payment button. You'll be shown the amount and asked to confirm. Once confirmed, the payment is processed and the milestone is marked as Complete." },
        { q: "What is the finder's fee?", a: "The finder's fee is a small platform fee charged on deals successfully matched through LenderBuild. The exact amount and who pays it is shown clearly in the deal summary before either party confirms. It covers platform costs and ongoing deal support." },
        { q: "How do repayments work?", a: "Repayments are structured within the Deal Room according to the agreed terms — monthly, quarterly or rolled up to the end of the term. You'll receive a notification when a repayment is due or has been processed." },
        { q: "What happens if a payment is missed?", a: "Both parties are notified immediately if a payment is not received on time. You can open the deal and tap Raise a Query to start a conversation with the other party, or escalate to the Disputes section if the issue cannot be resolved directly." },
      ],
    },
    {
      id: "account-and-settings",
      title: "Account and settings",
      color: "#475569",
      bg: "#F1F5F9",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
        </svg>
      ),
      questions: [
        { q: "How do I change my profile photo?", a: "Go to Settings > Account and tap Edit Profile. Tap your current photo or the avatar placeholder at the top of the form. Your device's image picker opens — select a photo, crop if needed, and tap Save. Your new photo appears across the platform instantly." },
        { q: "How do I enable dark mode?", a: "Go to Settings > Appearance and tap the Theme toggle at the top of the page. The app switches instantly between Light and Dark mode. Your preference is saved to your account so it follows you across all devices you log into." },
        { q: "How do I change my colour scheme?", a: "Go to Settings > Appearance and scroll to the Colour Scheme section. Tap any of the colour circles to change the accent colour — available in Blue, Teal, Purple, Amber, Coral and Grey. The change applies instantly across the whole app." },
        { q: "How do I manage notifications?", a: "Go to Settings > Notifications to see all notification types. Toggle each one individually — including New messages, Match alerts, Deal updates, Milestone reminders and Platform announcements. Changes are saved automatically." },
        { q: "How do I delete my account?", a: "To permanently delete your account, email us at lenderbuild.support@gmail.com from your registered address with the subject 'Account deletion request'. We'll process your request within 30 days in line with our Privacy Policy. Note that active deals must be completed before deletion can proceed." },
      ],
    },
    {
      id: "safety-and-trust",
      title: "Safety and trust",
      color: "#991B1B",
      bg: "#FEE2E2",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z"/>
        </svg>
      ),
      questions: [
        { q: "How are users verified?", a: "All users are verified by email on sign-up. Builders can additionally verify their company through Companies House — once confirmed they receive a CH Verified badge on their profile. Identity verification is also available in Settings > Account for an extra layer of trust. We also manually review accounts flagged by other members." },
        { q: "What is a legal charge?", a: "A legal charge is a formal security interest registered against a property. It gives the lender a legal claim over the asset if the borrower defaults on the loan. First charge lending takes priority over all other claims. Always take independent legal advice before agreeing to any charge arrangement." },
        { q: "What happens if there is a dispute?", a: "Go to Disputes in the navigation (found in your profile menu). Tap New Dispute, select the relevant deal and describe the issue clearly. Our Trust & Safety team reviews all disputes within 48 hours and may request evidence from both parties before working towards a resolution." },
        { q: "How do I report someone?", a: "On any profile page or post, tap the three-dot menu (⋯) and select Report. Choose the reason — such as Fraudulent activity, Misleading information or Abusive behaviour — add any supporting details and submit. Our team reviews all reports within 48 hours." },
        { q: "Is my data safe?", a: "Yes. We use Supabase for secure cloud storage with row-level security policies that ensure users can only access their own data. All connections use TLS encryption in transit. We never sell or share your personal data with third parties. See our Privacy Policy for full details." },
      ],
    },
  ];

  const searchResults = search.trim()
    ? categories.flatMap(cat =>
        cat.questions
          .map((item, idx) => ({ ...item, cat, idx }))
          .filter(item =>
            item.q.toLowerCase().includes(search.toLowerCase()) ||
            item.a.toLowerCase().includes(search.toLowerCase())
          )
      )
    : [];

  if (activeCategory) {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
        <div style={{ background: "#1E3A5F", position: "sticky", top: 60, zIndex: 99, height: 52, display: "flex", alignItems: "center" }}>
          <button onClick={() => { setActiveCategory(null); setOpenQuestion(null); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", padding: "8px 14px", lineHeight: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
          <div style={{ flex: 1, textAlign: "center", color: "#fff", fontWeight: 600, fontSize: 16, marginRight: 44 }}>{activeCategory.title}</div>
        </div>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "16px 16px calc(80px + env(safe-area-inset-bottom,0px))" }}>
          {activeCategory.questions.map((item, idx) => {
            const isOpen = openQuestion === idx;
            const helpKey = `${activeCategory.id}-${idx}`;
            return (
              <div key={idx} style={{ background: "#fff", borderRadius: 12, marginBottom: 8, overflow: "hidden", border: "0.5px solid #e8e8e8", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <button onClick={() => setOpenQuestion(isOpen ? null : idx)}
                  style={{ width: "100%", padding: "16px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", gap: 12 }}>
                  <span style={{ fontSize: 15, fontWeight: 500, color: "#1E3A5F", lineHeight: 1.4 }}>{item.q}</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "none" }}><path d="M6 9l6 6 6-6"/></svg>
                </button>
                {isOpen && (
                  <div>
                    <div style={{ padding: "0 18px 16px", fontSize: 14, color: "#374151", lineHeight: 1.75, borderTop: "0.5px solid #f5f5f5", paddingTop: 14 }}>{item.a}</div>
                    <div style={{ borderTop: "0.5px solid #f0f0f0", padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, color: "#94A3B8" }}>Was this helpful?</span>
                        {helpful[helpKey] === "yes" ? (
                          <span style={{ fontSize: 12, color: "#16A34A", fontWeight: 500, display:"inline-flex", alignItems:"center", gap:4 }}><Icon name="thumbs-up" size={12} /> Thanks for the feedback!</span>
                        ) : helpful[helpKey] === "no" ? (
                          <span style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>We'll work on improving this.</span>
                        ) : (
                          <>
                            <button onClick={() => setHelpful(h => ({ ...h, [helpKey]: "yes" }))} style={{ padding: "4px 14px", borderRadius: 20, border: "0.5px solid #A7F3D0", background: "#F0FDF4", color: "#16A34A", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Yes</button>
                            <button onClick={() => setHelpful(h => ({ ...h, [helpKey]: "no" }))} style={{ padding: "4px 14px", borderRadius: 20, border: "0.5px solid #FECACA", background: "#FFF5F5", color: "#DC2626", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>No</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ background: "#fff", borderRadius: 12, padding: "22px 20px", border: "0.5px solid #e8e8e8", marginTop: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", marginBottom: 6 }}>Still need help?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 4, lineHeight: 1.55 }}>Our support team is here for anything not covered above.</div>
            <HelpContactForm />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <div style={{ background: "#1E3A5F", padding: "2.5rem 1.25rem 2.25rem" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#fff", margin: "0 0 6px", fontFamily: "'Playfair Display', Georgia, serif" }}>How can we help you?</h1>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", margin: "0 0 1.25rem" }}>Search our help articles or browse by category below</p>
          <div style={{ position: "relative" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinecap="round" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search for help…"
              style={{ width: "100%", height: 48, borderRadius: 10, border: "none", paddingLeft: 42, paddingRight: 40, fontSize: 15, background: "rgba(255,255,255,0.12)", color: "#fff", outline: "none", boxSizing: "border-box" }} />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", lineHeight: 0, padding: 4 }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "20px 16px calc(80px + env(safe-area-inset-bottom,0px))" }}>
        {search.trim() ? (
          searchResults.length > 0 ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#64748B", marginBottom: 12 }}>{searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for &ldquo;{search}&rdquo;</div>
              {searchResults.map((item, i) => {
                const isOpen = openQuestion === `s${i}`;
                const helpKey = `search-${item.cat.id}-${item.idx}`;
                return (
                  <div key={i} style={{ background: "#fff", borderRadius: 12, marginBottom: 8, overflow: "hidden", border: "0.5px solid #e8e8e8" }}>
                    <div style={{ padding: "10px 18px 0" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: item.cat.color, background: item.cat.bg, padding: "2px 8px", borderRadius: 20 }}>{item.cat.title}</span>
                    </div>
                    <button onClick={() => setOpenQuestion(isOpen ? null : `s${i}`)}
                      style={{ width: "100%", padding: "10px 18px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", gap: 12 }}>
                      <span style={{ fontSize: 15, fontWeight: 500, color: "#1E3A5F", lineHeight: 1.4 }}>{item.q}</span>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "none" }}><path d="M6 9l6 6 6-6"/></svg>
                    </button>
                    {isOpen && (
                      <div>
                        <div style={{ padding: "0 18px 16px", fontSize: 14, color: "#374151", lineHeight: 1.75, borderTop: "0.5px solid #f5f5f5", paddingTop: 14 }}>{item.a}</div>
                        <div style={{ borderTop: "0.5px solid #f0f0f0", padding: "12px 18px", display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "#94A3B8" }}>Was this helpful?</span>
                          {helpful[helpKey] === "yes" ? (
                            <span style={{ fontSize: 12, color: "#16A34A", fontWeight: 500, display:"inline-flex", alignItems:"center", gap:4 }}><Icon name="thumbs-up" size={12} /> Thanks!</span>
                          ) : helpful[helpKey] === "no" ? (
                            <span style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>We'll improve this.</span>
                          ) : (
                            <>
                              <button onClick={() => setHelpful(h => ({ ...h, [helpKey]: "yes" }))} style={{ padding: "4px 14px", borderRadius: 20, border: "0.5px solid #A7F3D0", background: "#F0FDF4", color: "#16A34A", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Yes</button>
                              <button onClick={() => setHelpful(h => ({ ...h, [helpKey]: "no" }))} style={{ padding: "4px 14px", borderRadius: 20, border: "0.5px solid #FECACA", background: "#FFF5F5", color: "#DC2626", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>No</button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "3rem 0", color: "#94A3B8" }}>
              <div style={{ marginBottom: 10, color: "var(--text-muted,#64748B)" }}><Icon name="search" size={36} /></div>
              <div style={{ fontSize: 15, fontWeight: 500, color: "#374151", marginBottom: 4 }}>No results found</div>
              <div style={{ fontSize: 13 }}>Try different keywords or browse the categories below</div>
              <button onClick={() => setSearch("")} style={{ marginTop: 16, padding: "8px 20px", borderRadius: 8, border: "0.5px solid #d0d0d0", background: "#fff", color: "#1E3A5F", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Clear search</button>
            </div>
          )
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {categories.map(cat => (
                <button key={cat.id} onClick={() => { setActiveCategory(cat); setOpenQuestion(null); }}
                  style={{ background: "#fff", borderRadius: 14, padding: "20px 16px", border: "0.5px solid #e8e8e8", cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", transition: "box-shadow 0.15s, transform 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "none"; }}>
                  <div style={{ width: 50, height: 50, borderRadius: 12, background: cat.bg, color: cat.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {cat.icon}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F", lineHeight: 1.3 }}>{cat.title}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8" }}>{cat.questions.length} articles</div>
                </button>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 14, padding: "24px 20px", border: "0.5px solid #e8e8e8", marginTop: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1E3A5F", marginBottom: 4 }}>Contact support</div>
              <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.55 }}>Can't find the answer? Send us a message and we'll get back to you within 24 hours.</div>
              <HelpContactForm />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────

// Inject global keyframes once
if (typeof document !== "undefined" && !document.getElementById("lb-keyframes")) {
  const s = document.createElement("style");
  s.id = "lb-keyframes";
  s.textContent = `@keyframes fadeInUp{from{opacity:0;transform:translateX(-50%) translateY(12px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}@keyframes lbSpin{to{transform:rotate(360deg)}}@keyframes lbFadeIn{from{opacity:0}to{opacity:1}}`;
  document.head.appendChild(s);
}

export default function App() {
  const [darkMode, setDarkMode]                 = useDarkMode();
  const [accentColor, setAccentColor]           = useAccentColor();
  const [paletteOpen, setPaletteOpen]           = useState(false);
  const [devPanelOpen, setDevPanelOpen]         = useState(false);
  const [devRoleOverride, setDevRoleOverride]   = useState(null);
  const [page, setPage]                         = useState(() => {
    if (typeof window === "undefined") return "home";
    const path = window.location.pathname.replace(/^\/+/, "");
    if (!path) return "home";
    const DEEP_LINK = new Set([
      "help", "how-it-works", "safety", "about", "privacy", "terms",
      "risk-warning", "legal-resources", "status", "search", "find-builder", "waitlist",
      "browse-projects", "leaderboard", "market", "build-calculator",
      "posts", "community", "community-posts", "auth", "forgot-password",
    ]);
    return DEEP_LINK.has(path) ? path : "home";
  });
  const pageRef = useRef("home");
  const isSwitchingAccountRef = useRef(false);
  const [authInitialTab,   setAuthInitialTab]   = useState("login");
  const [authInitialEmail, setAuthInitialEmail] = useState("");
  const [pageRefreshKeys,  setPageRefreshKeys]  = useState({});
  const [user, setUser]                         = useState(null);
  const [selectedLender, setSelectedLender]     = useState(null);
  const [lenderSettings, setLenderSettings]     = useState({});
  const [editingPost, setEditingPost]           = useState(null);
  const [selectedPost,      setSelectedPost]        = useState(null);
  const [pendingOpenPostId, setPendingOpenPostId]   = useState(null);
  const [openConversationId, setOpenConversationId] = useState(null);
  const [selectedBuilder,   setSelectedBuilder]     = useState(null);
  const [selectedDeal,           setSelectedDeal]           = useState(null);
  const [selectedFundingRoom,    setSelectedFundingRoom]    = useState(null);
  const [selectedBuilderPassportId, setSelectedBuilderPassportId] = useState(null);
  const [unreadCount,       setUnreadCount]         = useState(0);
  const [communityBadge,    setCommunityBadge]      = useState(false);
  const [userProfile,       setUserProfile]         = useState(null);
  const [viewerRoleProfile, setViewerRoleProfile]   = useState(null);
  const [tourStep,          setTourStep]            = useState(0); // 0 = inactive
  const [tourForceProfileOpen, setTourForceProfileOpen] = useState(false);
  const [loginLoading,      setLoginLoading]         = useState(false);
  const [initialising,      setInitialising]          = useState(true);
  const [riskWarningAccepting, setRiskWarningAccepting] = useState(false);
  const [riskWarningChecked,   setRiskWarningChecked]   = useState(false);
  const [sessionFcaAccepted,   setSessionFcaAccepted]   = useState(() => {
    try { return sessionStorage.getItem("fca_warning_accepted") === "true"; } catch { return false; }
  });
  useEffect(() => {
    if (user && !sessionFcaAccepted) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [user, sessionFcaAccepted]); // eslint-disable-line react-hooks/exhaustive-deps
  const [windowWidth,       setWindowWidth]           = useState(() => typeof window !== "undefined" ? window.innerWidth : 1200);
  const [sidebarCollapsed,  setSidebarCollapsed]      = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [paymentSuccessDealId,   setPaymentSuccessDealId]   = useState(null);
  const [paymentSuccessDeal,     setPaymentSuccessDeal]     = useState(null);
  const [paymentSuccessConfirmed,setPaymentSuccessConfirmed]= useState(false);
  const [paymentSuccessTimedOut, setPaymentSuccessTimedOut] = useState(false);
  const [showPaymentCancelled,   setShowPaymentCancelled]   = useState(false);
  const sessionTimerRef     = useRef(null);
  const tourShownRef        = useRef(false);
  // Capture ?post=<id> synchronously at init before any routing or effect can strip it
  const deepLinkPostIdRef   = useRef((() => {
    try {
      const id = new URLSearchParams(window.location.search).get("post") || null;
      if (id) console.log("[LenderBuild] deep-link post id captured:", id);
      return id;
    } catch (_) { return null; }
  })());

  // ── Ctrl+K command palette + Ctrl+Shift+D dev panel ─────────────────────
  useEffect(() => {
    function handler(e) {
      if (e.ctrlKey && e.key === "k") { e.preventDefault(); setPaletteOpen(o => !o); }
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        const role = user?.user_metadata?.role;
        if (role === "admin" || role === "founder") { e.preventDefault(); setDevPanelOpen(o => !o); }
      }
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [user?.user_metadata?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track window width for responsive layout ──────────────────────────────
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── DIAGNOSTIC: log elements overflowing viewport width (remove after fix) ──
  useEffect(() => {
    const all = document.querySelectorAll("*");
    all.forEach(el => {
      if (el.getBoundingClientRect().right > window.innerWidth) {
        console.log("OVERFLOW:", el.tagName, el.className, el.getBoundingClientRect().right);
      }
    });
  }, []);

  // ── Session timeout: log out after 30 minutes of inactivity ───────────────
  useEffect(() => {
    if (!user) { clearTimeout(sessionTimerRef.current); return; }

    function resetTimer() {
      clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = setTimeout(async () => {
        await supabase.auth.signOut();
        setPage("home");
      }, 30 * 60 * 1000); // 30 minutes
    }

    const events = ["click", "keydown", "scroll", "mousemove", "touchstart"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(sessionTimerRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load current user's profile + role-specific data for match scoring ──────
  useEffect(() => {
    if (!user) { setUserProfile(null); setViewerRoleProfile(null); return; }
    const role = user.user_metadata?.role;
    supabase.from("profiles")
      .select("sequential_id, user_role, location, bio")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data: p }) => {
        setUserProfile(p || null);
        const loc = p?.location || "";
        if (role === "builder") {
          supabase.from("builder_profiles")
            .select("total_value, projects_completed, specialization")
            .eq("user_id", user.id)
            .maybeSingle()
            .then(({ data: bp }) => setViewerRoleProfile(bp ? { ...bp, location: loc } : null));
        } else if (role === "lender") {
          supabase.from("lender_profiles")
            .select("budget_max, preferred_projects, return_type")
            .eq("user_id", user.id)
            .maybeSingle()
            .then(({ data: lp }) => setViewerRoleProfile(lp ? { ...lp, location: loc } : null));
        }
      });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fade out account-switch overlay once the new user session is ready ───────
  useEffect(() => {
    if (!user) return;
    const overlay = document.getElementById("lb-switch-overlay");
    if (!overlay) return;
    window.__lb_account_switching = false;
    overlay.style.transition = "opacity 300ms ease";
    overlay.style.opacity = "0";
    const t = setTimeout(() => { try { overlay.remove(); } catch(e) {} }, 300);
    return () => clearTimeout(t);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Onboarding tour trigger — brand-new accounts only ────────────────────
  // Only fires when:
  //   1. onboarding_complete is not set (never completed), AND
  //   2. tour_completed is not set (legacy flag for users pre-dating this flag), AND
  //   3. The account was created within the last 5 minutes (true new signup,
  //      not an existing user who simply lacks the flags).
  useEffect(() => {
    if (!user || tourShownRef.current) return;
    const meta = user.user_metadata || {};
    if (meta.onboarding_complete || meta.tour_completed) return;
    const createdAt = user.created_at ? new Date(user.created_at).getTime() : 0;
    const isNewAccount = createdAt > 0 && (Date.now() - createdAt) < 5 * 60 * 1000;
    if (isNewAccount) {
      tourShownRef.current = true;
      const t = setTimeout(() => setTourStep(1), 900);
      return () => clearTimeout(t);
    }
  }, [user?.id]); // eslint-disable-line

  // Open profile dropdown for step 5 so the element exists in DOM
  useEffect(() => {
    setTourForceProfileOpen(tourStep === 6);
  }, [tourStep]);

  // ── Crisp live chat — identify logged-in users ────────────────────────────
  useEffect(() => {
    if (!window.$crisp) return;
    if (user) {
      window.$crisp.push(["set", "user:email", [user.email]]);
      const name = user.user_metadata?.name || user.email?.split("@")[0] || "";
      if (name) window.$crisp.push(["set", "user:nickname", [name]]);
      window.$crisp.push(["set", "session:data", [[["role", user.user_metadata?.role || "unknown"]]]]);
    } else {
      window.$crisp.push(["do", "session:reset"]);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Shared-post click from group chat ────────────────────────────────────
  useEffect(() => {
    function handleOpenPostEvent(e) {
      const postId = e.detail?.postId;
      if (!postId) return;
      setPendingOpenPostId(postId);
      navigateTo("posts");
    }
    window.addEventListener("lenderbuild:open-post", handleOpenPostEvent);
    return () => window.removeEventListener("lenderbuild:open-post", handleOpenPostEvent);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Post deep link: ?post=<id> ────────────────────────────────────────────
  useEffect(() => {
    if (initialising) return;
    const postId = deepLinkPostIdRef.current;
    if (!postId) return;
    deepLinkPostIdRef.current = null; // consume once
    window.history.replaceState(null, "", window.location.pathname);
    if (user) {
      setPendingOpenPostId(postId);
      navigateTo("posts");
    } else {
      try {
        localStorage.setItem("pendingPostId", postId);
        localStorage.setItem("pendingPage", "posts");
      } catch (_) {}
      navigateTo("auth");
    }
  }, [initialising]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auth gate for internal pages ──────────────────────────────────────────
  const GATED_PAGES = new Set(["search", "find-builder", "posts", "browse-projects", "market", "build-calculator"]);
  useEffect(() => {
    if (initialising || user) return;
    if (GATED_PAGES.has(page)) {
      try { localStorage.setItem("pendingPage", page); } catch (_) {}
      navigateTo("auth");
    }
  }, [page, user, initialising]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Unread message badge ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    async function loadUnread() {
      const lastRead = localStorage.getItem(`msgs_read_${user.id}`) || new Date(0).toISOString();
      const { data } = await supabase
        .from("conversations")
        .select("id")
        .or(`lender_id.eq.${user.id},builder_id.eq.${user.id}`)
        .gt("last_message_at", lastRead);
      setUnreadCount((data || []).length);
    }
    loadUnread();
    const channel = supabase.channel("nav-unread")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" },
        payload => { if (payload.new?.sender_id !== user.id) loadUnread(); })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page === "messages" && user) {
      setUnreadCount(0);
      localStorage.setItem(`msgs_read_${user.id}`, new Date().toISOString());
    }
    if (page === "community" || page === "community-groups") {
      setCommunityBadge(false);
      localStorage.setItem("lb_comm_last_visit", Date.now().toString());
    }
  }, [page, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Page title ───────────────────────────────────────────────────────────
  useEffect(() => {
    const titles = {
      "home": "LenderBuild", "home-dashboard": "Dashboard — LenderBuild",
      "search": "Find Lenders — LenderBuild", "find-builder": "Find Builders — LenderBuild",
      "browse-projects": "Browse Projects — LenderBuild",
      "community": "Community — LenderBuild", "posts": "Posts — LenderBuild",
      "post-detail": "Post — LenderBuild", "create-post": "New Post — LenderBuild",
      "edit-post": "Edit Post — LenderBuild", "my-posts": "My Posts — LenderBuild",
      "messages": "Messages — LenderBuild", "my-deals": "My Deals — LenderBuild", "deals": "My Deals — LenderBuild",
      "deal-detail": "Deal — LenderBuild", "disputes": "Disputes — LenderBuild",
      "profile-menu": "Account — LenderBuild",
      "profile-setup": "Profile Setup — LenderBuild", "account": "Settings — LenderBuild",
      "notifications": "Notifications — LenderBuild", "saved-profiles": "Saved Profiles — LenderBuild",
      "auth": "Sign In — LenderBuild", "lender-dashboard": "Lender Dashboard — LenderBuild",
      "legal-resources": "Legal Resources — LenderBuild", "my-repayments": "My Repayments — LenderBuild",
      "leaderboard": "Leaderboard — LenderBuild", "admin": "Admin — LenderBuild",
    };
    document.title = titles[page] || "LenderBuild";
  }, [page]);

  // ── Community activity badge ──────────────────────────────────────────────
  useEffect(() => {
    if (!user) { setCommunityBadge(false); return; }
    const lastVisit = localStorage.getItem("lb_comm_last_visit");
    if (!lastVisit) { setCommunityBadge(true); return; }
    supabase
      .from("community_messages")
      .select("id", { count: "exact", head: true })
      .gt("created_at", new Date(parseInt(lastVisit)).toISOString())
      .then(({ count }) => { setCommunityBadge((count || 0) > 0); });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── After Stripe funding-room finder fee payment ─────────────────────────
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const roomId = params.get("room_id");
    const feePaid = params.get("fee_paid");
    if (!roomId || !feePaid) return;
    window.history.replaceState(null, "", "/");
    // Mark fee as paid on the server
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      fetch("/api/project-listings", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: "funding-room-fee-confirm", room_id: roomId }),
      }).catch(() => {});
    });
    navigateTo("funding-room", roomId);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── After Stripe finder's-fee payment — show polling confirmation modal ──────
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");
    const dealId = params.get("deal_id") || params.get("finder_fee_paid"); // backward compat
    if (!paymentStatus && !params.get("finder_fee_paid")) return;
    window.history.replaceState(null, "", "/");
    if (paymentStatus === "cancelled") {
      setShowPaymentCancelled(true);
      setTimeout(() => setShowPaymentCancelled(false), 6000);
      return;
    }
    if (paymentStatus === "success" || params.get("finder_fee_paid")) {
      if (!dealId) return;
      setPaymentSuccessDealId(dealId);
      setPaymentSuccessConfirmed(false);
      setPaymentSuccessTimedOut(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll for finder_fee_status === 'paid' after Stripe redirect ───────────
  useEffect(() => {
    if (!paymentSuccessDealId) return;
    if (paymentSuccessConfirmed || paymentSuccessTimedOut) return;
    let attempts = 0;
    const maxAttempts = 15; // 2s × 15 = 30s
    const interval = setInterval(async () => {
      attempts++;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { clearInterval(interval); return; }
        const res = await fetch("/api/deals", { headers: { Authorization: `Bearer ${session.access_token}` } });
        const json = await res.json();
        const deal = (json.deals || []).find(d => String(d.id) === String(paymentSuccessDealId));
        if (deal) setPaymentSuccessDeal(deal);
        if (deal?.finder_fee_status === "paid") {
          setPaymentSuccessConfirmed(true);
          clearInterval(interval);
          return;
        }
      } catch (_) {}
      if (attempts >= maxAttempts) {
        setPaymentSuccessTimedOut(true);
        clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [paymentSuccessDealId, paymentSuccessConfirmed, paymentSuccessTimedOut]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { pageRef.current = page; }, [page]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setIsLoggedIn(true);
        const meta = session.user.user_metadata || {};
        if (meta.appearance_dark_mode !== undefined)    setDarkMode(!!meta.appearance_dark_mode);
        if (meta.appearance_accent_colour)              setAccentColor(meta.appearance_accent_colour);
      } else {
        setIsLoggedIn(false);
      }
      setUser(session?.user ?? null);
      setInitialising(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (isSwitchingAccountRef.current) return;

      if (event === "SIGNED_IN" && session?.user) {
        setIsLoggedIn(true);
        const meta = session.user.user_metadata || {};
        if (meta.appearance_dark_mode !== undefined)    setDarkMode(!!meta.appearance_dark_mode);
        if (meta.appearance_accent_colour)              setAccentColor(meta.appearance_accent_colour);
        if (pageRef.current === "auth") setLoginLoading(true);
      } else if (event === "SIGNED_OUT") {
        setIsLoggedIn(false);
        clearPreferenceStorage();
        setDarkMode(false);
      }
      setUser(session?.user ?? null);
      if (event === "PASSWORD_RECOVERY") setPage("reset-password");
      // Only redirect to profile-setup from the auth page — not from token-refresh SIGNED_IN
      // events that fire while already navigated elsewhere (e.g. after account switch).
      if (event === "SIGNED_IN" && session?.user && !session.user.user_metadata?.profile_complete
          && (pageRef.current === "auth" || pageRef.current === "profile-setup")) {
        setPage("profile-setup");
      }
    });

    fetch("/api/lender-settings")
      .then(r => r.json())
      .then(d => setLenderSettings(d.settings || {}))
      .catch(() => {});

    const handlePop = () => {
      setSelectedLender(null);
      setPage("home");
    };
    window.addEventListener("popstate", handlePop);
    return () => {
      subscription.unsubscribe();
      window.removeEventListener("popstate", handlePop);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleViewProfile(lc) {
    setSelectedLender(lc);
    setPage("lender-profile");
    window.history.pushState(null, "", "/lender/" + lc.id);
  }

  function handleBackFromProfile() {
    setSelectedLender(null);
    setPage("search");
    window.history.pushState(null, "", "/");
  }

  function handleOpenConversation(conversationId) {
    setOpenConversationId(conversationId);
    setPage("messages");
    window.history.pushState(null, "", "/");
  }

  function handleViewBuilderProfile(builder) {
    setSelectedBuilder(builder);
    setPage("builder-profile");
    window.history.pushState(null, "", "/");
  }

  function handleBackFromBuilderProfile() {
    setSelectedBuilder(null);
    setPage("find-builder");
    window.history.pushState(null, "", "/");
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    clearPreferenceStorage();
    setAccentColor("#3B82F6");
    setDarkMode(false);
    setPage("home");
    window.history.pushState(null, "", "/");
  }

  async function handleTourFinish() {
    setTourStep(0);
    await supabase.auth.updateUser({ data: { onboarding_complete: true } });
  }

  async function handleTourSkip() {
    setTourStep(0);
    await supabase.auth.updateUser({ data: { onboarding_complete: true } });
  }

  function handleReplayTour() {
    tourShownRef.current = false;
    setTourStep(1);
  }

  async function handleLoginSuccess(freshUser) {
    // Gate the auth listener for the duration of the switch so trailing Supabase
    // SIGNED_IN events (token-refresh, session-init) cannot clobber the page state.
    isSwitchingAccountRef.current = true;
    setIsLoggedIn(true);
    if (freshUser) {
      setUser(freshUser);
      const meta = freshUser.user_metadata || {};
      if (meta.appearance_dark_mode !== undefined)    setDarkMode(!!meta.appearance_dark_mode);
      if (meta.appearance_accent_colour)              setAccentColor(meta.appearance_accent_colour);
    }
    navigateTo("home");
    // Navigate back to the page the user was trying to visit before logging in
    try {
      const pendingPage = localStorage.getItem("pendingPage");
      if (pendingPage) {
        localStorage.removeItem("pendingPage");
        navigateTo(pendingPage);
      }
    } catch (_) {}
    // Open any post the user was trying to view before logging in
    try {
      const pendingPostId = localStorage.getItem("pendingPostId");
      if (pendingPostId) {
        localStorage.removeItem("pendingPostId");
        setPendingOpenPostId(pendingPostId);
      }
    } catch (_) {}
    // Release the gate after Supabase's trailing events settle (token-refresh fires async)
    setTimeout(() => { isSwitchingAccountRef.current = false; }, 1000);
  }

  function handleLoginDone() { setLoginLoading(false); }



  // navigateTo accepts an optional second arg for post/deal editing, or { authTab } for auth page
  function refreshCurrentPage(p) {
    setPageRefreshKeys(k => ({ ...k, [p]: (k[p] || 0) + 1 }));
  }

  async function handleAcceptRiskWarning() {
    if (!riskWarningChecked || riskWarningAccepting) return;
    // Set the session flag immediately — before any awaits — so that auth state
    // changes (TOKEN_REFRESHED) firing during the async save cannot re-show the modal.
    try { sessionStorage.setItem("fca_warning_accepted", "true"); } catch (_) {}
    setSessionFcaAccepted(true);
    setRiskWarningAccepting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetch("/api/save-profile", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: "accept-risk-warning" }),
        });
      }
      // Refresh user so risk_warning_accepted is in local state too
      const { data: { user: freshUser } } = await supabase.auth.getUser();
      if (freshUser) setUser(freshUser);
    } catch (_) {
      setUser(u => u ? { ...u, user_metadata: { ...u.user_metadata, risk_warning_accepted: true } } : u);
    } finally {
      setRiskWarningAccepting(false);
      setRiskWarningChecked(false);
    }
  }

  function navigateTo(p, data) {
    if (p === "auth" && data?.authTab) {
      setAuthInitialTab(data.authTab);
    }
    if (p === "auth" && data?.prefillEmail !== undefined) {
      setAuthInitialEmail(data.prefillEmail);
    }
    if (p === "edit-post") {
      setEditingPost(data || null);
    } else {
      setEditingPost(null);
    }
    if (p === "post-detail") {
      setSelectedPost(data || null);
    } else {
      setSelectedPost(null);
    }
    if (p === "deal-detail" || p === "deal-room") {
      setSelectedDeal(data || null);
    } else {
      setSelectedDeal(null);
    }
    if (p === "funding-room") {
      setSelectedFundingRoom(data || null);
    } else {
      setSelectedFundingRoom(null);
    }
    if (p === "builder-passport") {
      setSelectedBuilderPassportId(data || null);
    } else {
      setSelectedBuilderPassportId(null);
    }
    // Clear the pre-selected conversation when navigating to messages normally
    if (p === "messages") setOpenConversationId(null);
    if (p !== "lender-profile") setSelectedLender(null);
    if (p !== "builder-profile") setSelectedBuilder(null);
    // Pages that rely on separately-held state and can't be deep-linked
    const DATA_PAGES = new Set(["lender-profile", "builder-profile", "deal-detail", "deal-room", "post-detail", "edit-post", "builder-passport", "funding-room"]);
    const urlPath = (p === "home" || p === "home-dashboard" || DATA_PAGES.has(p)) ? "/" : `/${p}`;
    window.history.pushState(null, "", urlPath);
    setPage(p);
    scrollMainToTop();
  }

  const isMobileLayout = windowWidth < 768;
  const showSidebar    = !isMobileLayout && !!user && windowWidth >= 1024;

  if (initialising) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#1E3A5F", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700, letterSpacing: -0.3 }}>
          <span style={{ color: "#FFFFFF" }}>Lender</span><span style={{ color: "#3B82F6" }}>Build</span>
        </div>
      </div>
    );
  }

  const isWaitlistGate = WAITLIST_MODE && !user && !["auth", "forgot-password", "reset-password", "privacy", "terms", "risk-warning"].includes(page);

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", height: "100vh", overflow: "hidden", overflowX: "hidden", maxWidth: "100vw", display: "flex", flexDirection: "column", background: "var(--bg-page)", backgroundColor: "#fff", color: "var(--text-primary)" }}>
      <Navbar page={page} setPage={navigateTo} user={user} onLogout={handleLogout} unreadCount={unreadCount} userProfile={userProfile} tourForceProfileOpen={tourForceProfileOpen} darkMode={darkMode} setDarkMode={setDarkMode} onReplayTour={handleReplayTour} />
      <div style={{ display: "flex", alignItems: "flex-start", flex: 1, overflow: "hidden", paddingTop: windowWidth < 768 ? 57 : 60 }}>
        {showSidebar && (
          <DesktopSidebar
            page={page}
            setPage={navigateTo}
            onRefreshPage={refreshCurrentPage}
            user={user}
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(c => !c)}
            unreadCount={unreadCount}
          />
        )}
        <div id="main-scroll" style={{ flex: 1, minWidth: 0, height: "100%", overflowY: page === "messages" ? "hidden" : "auto", overflowX: "hidden", touchAction: "pan-y", paddingBottom: user && windowWidth <= 768 ? "calc(120px + env(safe-area-inset-bottom, 0px))" : 0, ...(page === "messages" ? { display: "flex", flexDirection: "column" } : {}) }}>
          {user && page !== "home" && page !== "home-dashboard" && page !== "auth" && page !== "forgot-password" && page !== "reset-password" && page !== "account" && page !== "profile-menu" && page !== "messages" && page !== "community-groups" && page !== "deal-detail" && (
            <div style={{ padding: "12px 1.25rem 0" }}>
              <button onClick={() => navigateTo("home")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontSize: 13, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4, padding: 0 }}>
                ← Back to dashboard
              </button>
            </div>
          )}
          {isWaitlistGate ? <WaitlistPage setPage={navigateTo} showLoginLink /> : <div key={page} className="page-transition" style={page === "messages" ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : {}}>
          {(page === "home" || page === "home-dashboard") && (!user ? <HomePage setPage={navigateTo} user={user} onViewProfile={handleViewProfile} /> : <DashboardPage key={`home-${pageRefreshKeys.home || 0}`} user={user} setPage={navigateTo} onViewProfile={handleViewProfile} onViewBuilderProfile={handleViewBuilderProfile} onMessage={handleOpenConversation} viewerRoleProfile={viewerRoleProfile} userProfile={userProfile} page={page} />)}
          {page === "search"           && user && <SearchPage         key={`search-${pageRefreshKeys.search || 0}`} setPage={navigateTo} user={user} onViewProfile={handleViewProfile} viewerRoleProfile={viewerRoleProfile} />}
          {page === "find-builder"     && user && <FindBuilderPage    key={`find-builder-${pageRefreshKeys["find-builder"] || 0}`} setPage={navigateTo} user={user} onMessage={handleOpenConversation} onViewProfile={handleViewBuilderProfile} viewerRoleProfile={viewerRoleProfile} />}
          {page === "community"        && <CommunityPage      user={user} setPage={navigateTo} onMessage={handleOpenConversation} />}
          {page === "posts"            && user && <CommunityPostsPage key={`posts-${pageRefreshKeys.posts || 0}`} user={user} setPage={navigateTo} onMessage={handleOpenConversation} openPostId={pendingOpenPostId} onPostOpened={() => setPendingOpenPostId(null)} />}
          {page === "community-posts"  && <CommunityPostsPage user={user} setPage={navigateTo} onMessage={handleOpenConversation} showBack={true} />}
          {page === "community-groups" && <CommunityGroupsPage key={`community-groups-${pageRefreshKeys["community-groups"] || 0}`} user={user} setPage={navigateTo} />}
          {page === "community-chat"   && <CommunityChatPage user={user} setPage={navigateTo} />}
          {page === "post-detail"      && (selectedPost ? <PostDetailPage post={selectedPost} user={user} setPage={navigateTo} onMessage={handleOpenConversation} onEdit={post => navigateTo("edit-post", post)} onDelete={async (postId) => { const { data: { session } } = await supabase.auth.getSession(); if (session) fetch("/api/posts", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ action: "delete", post_id: postId }) }).catch(() => {}); navigateTo("posts"); }} /> : <CommunityPostsPage user={user} setPage={navigateTo} onMessage={handleOpenConversation} showBack={false} />)}
          {page === "my-posts"         && user && <MyPostsPage        user={user} setPage={navigateTo} />}
          {page === "create-post"      && user && <CreateEditPostPage user={user} setPage={navigateTo} />}
          {page === "edit-post"        && user && <CreateEditPostPage user={user} setPage={navigateTo} editPost={editingPost} />}
          {page === "leaderboard"      && <LeaderboardPage />}
          {page === "auth"             && <AuthPage           setPage={navigateTo} onLoginSuccess={handleLoginSuccess} initialTab={authInitialTab} initialEmail={authInitialEmail} waitlistMode={WAITLIST_MODE} />}
          {page === "forgot-password"  && <ForgotPasswordPage setPage={navigateTo} />}
          {page === "reset-password"   && <ResetPasswordPage  setPage={navigateTo} />}
          {page === "profile-menu"     && user && <ProfileMenuPage   user={user} setPage={navigateTo} onLogout={handleLogout} />}
          {page === "account"          && user && <AccountPage      user={user} setPage={navigateTo} userProfile={userProfile} viewerRoleProfile={viewerRoleProfile} onReplayTour={handleReplayTour} darkMode={darkMode} setDarkMode={setDarkMode} accentColor={accentColor} setAccentColor={setAccentColor} sidebarWidth={showSidebar ? (sidebarCollapsed ? 58 : 224) : 0} />}
          {page === "profile-setup"   && user && <ProfileSetupPage user={user} setPage={navigateTo} setCelebration={setCelebration} />}
          {page === "messages"         && user && <MessagesPage key={`messages-${pageRefreshKeys.messages || 0}`} user={user} initialConversationId={openConversationId} setPage={navigateTo} onViewProfile={handleViewProfile} onViewBuilderProfile={handleViewBuilderProfile} />}
          {page === "lender-dashboard" && user && <LenderDashboard  user={user} setPage={navigateTo} />}
          {page === "lender-wallet"    && user && <LenderWalletPage user={user} setPage={navigateTo} />}
          {page === "admin"            && user && <AdminPage user={user} />}
          {page === "lender-profile"   && (selectedLender ? (
            <LenderProfilePage
              lc={selectedLender}
              user={user}
              setPage={navigateTo}
              settings={lenderSettings[selectedLender.name]}
              onBack={handleBackFromProfile}
              onMessage={handleOpenConversation}
              viewerRoleProfile={viewerRoleProfile}
            />
          ) : <SearchPage setPage={navigateTo} user={user} onViewProfile={handleViewProfile} viewerRoleProfile={viewerRoleProfile} />)}
          {page === "builder-profile"  && (selectedBuilder ? (
            <BuilderProfilePage
              builder={selectedBuilder}
              user={user}
              setPage={navigateTo}
              onBack={handleBackFromBuilderProfile}
              onMessage={handleOpenConversation}
              viewerRoleProfile={viewerRoleProfile}
            />
          ) : <FindBuilderPage setPage={navigateTo} user={user} onMessage={handleOpenConversation} onViewProfile={handleViewBuilderProfile} viewerRoleProfile={viewerRoleProfile} />)}
          {page === "my-deals"                 && user && <MyDealsPage user={user} setPage={navigateTo} />}
          {page === "deals"                   && user && <DealsPage user={user} setPage={navigateTo} setCelebration={setCelebration} />}
          {page === "deal-detail"             && user && (selectedDeal ? <DealDetailPage user={user} setPage={navigateTo} deal={selectedDeal} setCelebration={setCelebration} /> : <DealsPage user={user} setPage={navigateTo} setCelebration={setCelebration} />)}
          {page === "disputes"                && user && <DisputesPage user={user} setPage={navigateTo} />}
          {page === "browse-projects"         && user && <BrowseProjectsPage user={user} setPage={navigateTo} />}
          {page === "funding-room"            && user && (selectedFundingRoom ? <FundingRoomPage user={user} setPage={navigateTo} roomId={selectedFundingRoom} /> : <BrowseProjectsPage user={user} setPage={navigateTo} />)}
          {page === "create-project-listing"  && user && <CreateProjectListingPage user={user} setPage={navigateTo} />}
          {page === "analytics"               && user && user.user_metadata?.role === "admin" && <AnalyticsPage user={user} />}
          {page === "privacy"       && <PrivacyPolicyPage  setPage={navigateTo} />}
          {page === "terms"         && <TermsPage           setPage={navigateTo} />}
          {page === "risk-warning"  && <RiskWarningPage     setPage={navigateTo} />}
          {page === "how-it-works"   && <HowItWorksPage      setPage={navigateTo} />}
          {page === "for-builders"   && <ForBuildersPage     setPage={navigateTo} />}
          {page === "for-lenders"    && <ForLendersPage      setPage={navigateTo} />}
          {page === "safety"         && <SafetyPage           setPage={navigateTo} />}
          {page === "about"         && <AboutPage            setPage={navigateTo} />}
          {page === "notifications"    && user && <NotificationsPage user={user} />}
          {page === "saved-profiles"   && user && <SavedProfilesPage user={user} setPage={navigateTo} onViewProfile={handleViewProfile} onViewBuilderProfile={handleViewBuilderProfile} />}
          {page === "saved-searches"   && user && <SavedSearchesPage user={user} setPage={navigateTo} />}
          {page === "market"           && user && <MarketPage setPage={navigateTo} />}
          {page === "build-calculator" && user && <BuildCostCalculatorPage setPage={navigateTo} />}
          {page === "deal-room"        && user && (selectedDeal ? <DealRoomPage user={user} setPage={navigateTo} deal={selectedDeal} /> : <DealsPage user={user} setPage={navigateTo} setCelebration={setCelebration} />)}
          {page === "my-repayments"    && user && SHOW_MY_REPAYMENTS && <MyRepaymentsPage user={user} setPage={navigateTo} />}
          {page === "legal-resources"  && <LegalResourcesPage setPage={navigateTo} user={user} />}
          {page === "waitlist"         && <WaitlistPage setPage={navigateTo} />}
          {page === "builder-passport" && <BuilderPassportPage sequential_id={selectedBuilderPassportId} user={user} setPage={navigateTo} />}
          {page === "status"           && <StatusPage />}
          {page === "help"             && <HelpCentrePage setPage={navigateTo} />}
          </div>}
          {!isWaitlistGate && !user && (["home","about","how-it-works","help","for-builders","for-lenders"].includes(page)
            ? <Footer setPage={navigateTo} />
            : <MinimalFooter setPage={navigateTo} />)}
        </div>
      </div>
      {tourStep > 0 && (
        <OnboardingTour
          step={tourStep}
          userRole={user?.user_metadata?.role}
          onNext={() => setTourStep(s => Math.min(s + 1, TOUR_TOTAL + 1))}
          onBack={() => setTourStep(s => Math.max(s - 1, 2))}
          onSkip={handleTourSkip}
          onFinish={handleTourFinish}
          onRedo={handleReplayTour}
          setPage={navigateTo}
        />
      )}
      {loginLoading && <LoginLoader onDone={handleLoginDone} />}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} setPage={navigateTo} user={user} onOpenDevPanel={() => setDevPanelOpen(true)} onViewProfile={handleViewProfile} onViewBuilderProfile={handleViewBuilderProfile} />
      {devPanelOpen && user && <DevPanel user={user} onClose={() => setDevPanelOpen(false)} devRoleOverride={devRoleOverride} setDevRoleOverride={setDevRoleOverride} setPage={navigateTo} />}
      {user && windowWidth <= 768 && (
        <BottomNav page={page} setPage={navigateTo} onRefreshPage={refreshCurrentPage} user={user} unreadCount={unreadCount} communityBadge={communityBadge} />
      )}
      {/* Payment cancelled banner */}
      {showPaymentCancelled && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: "#1E293B", color: "#fff", borderRadius: 10, padding: "14px 24px", fontSize: 14, boxShadow: "0 4px 24px rgba(0,0,0,0.18)", display: "flex", alignItems: "center", gap: 12, maxWidth: 480 }}>
          <span>Payment cancelled. You can try again any time from your dashboard.</span>
          <button onClick={() => setShowPaymentCancelled(false)} style={{ background: "none", border: "none", color: "#94A3B8", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      )}

      {/* Post-Stripe payment confirmation modal */}
      {paymentSuccessDealId && !paymentSuccessConfirmed && !paymentSuccessTimedOut && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--bg-card, #fff)", borderRadius: 16, padding: "48px 40px", textAlign: "center", maxWidth: 420, width: "90%", boxShadow: "0 8px 48px rgba(0,0,0,0.22)" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Processing your payment…</div>
            <div style={{ fontSize: 14, color: "var(--text-muted, #64748B)" }}>We're confirming your payment with Stripe. This usually takes a few seconds.</div>
            <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
              <div style={{ width: 28, height: 28, border: "3px solid var(--accent, #3B82F6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            </div>
          </div>
        </div>
      )}
      {paymentSuccessDealId && paymentSuccessConfirmed && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--bg-card, #fff)", borderRadius: 16, padding: "48px 40px", textAlign: "center", maxWidth: 420, width: "90%", boxShadow: "0 8px 48px rgba(0,0,0,0.22)" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>✓</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#065F46" }}>Your deal is now active!</div>
            <div style={{ fontSize: 14, color: "var(--text-muted, #64748B)", marginBottom: 28 }}>The finder's fee has been received. You can now access your Deal Room.</div>
            <button
              onClick={() => { setPaymentSuccessDealId(null); setPaymentSuccessConfirmed(false); if (paymentSuccessDeal) setPage("deal-detail", paymentSuccessDeal); else setPage("my-deals"); }}
              style={{ background: "var(--accent, #3B82F6)", color: "#fff", border: "none", borderRadius: 8, padding: "12px 28px", fontSize: 15, fontWeight: 600, cursor: "pointer" }}
            >Open Deal Room →</button>
          </div>
        </div>
      )}
      {paymentSuccessDealId && paymentSuccessTimedOut && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--bg-card, #fff)", borderRadius: 16, padding: "48px 40px", textAlign: "center", maxWidth: 460, width: "90%", boxShadow: "0 8px 48px rgba(0,0,0,0.22)" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>Payment received</div>
            <div style={{ fontSize: 14, color: "var(--text-muted, #64748B)", marginBottom: 28 }}>It's taking a little longer than usual to update. Try refreshing in a moment, or contact us at <strong>support@lenderbuild.co.uk</strong> if the problem persists.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => window.location.reload()} style={{ background: "var(--accent, #3B82F6)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Refresh page</button>
              <button onClick={() => { setPaymentSuccessDealId(null); setPaymentSuccessTimedOut(false); setPage("my-deals"); }} style={{ background: "none", border: "1px solid var(--border, #e0e0e0)", borderRadius: 8, padding: "10px 22px", fontSize: 14, cursor: "pointer" }}>Go to My Deals</button>
            </div>
          </div>
        </div>
      )}

      {celebration && <CelebrationOverlay message={celebration.message} subtitle={celebration.subtitle} onClose={() => setCelebration(null)} />}
      {user && !sessionFcaAccepted && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "#fff", borderRadius: 16, maxWidth: 520, width: "100%", padding: "2rem", boxShadow: "0 24px 80px rgba(0,0,0,0.35)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1E3A5F", margin: 0 }}>Important Risk Warning</h2>
            </div>
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
              <p style={{ fontSize: 15, color: "#111827", lineHeight: 1.65, margin: 0 }}>
                <strong>Property investment carries significant risk.</strong> You may lose some or all of your investment. Past performance is not a guide to future returns.
              </p>
            </div>
            <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, marginBottom: "1.25rem" }}>
              LenderBuild is an introducer only and does not provide financial advice. All investment decisions are made solely by you. You should seek independent financial and legal advice before committing any funds.
            </p>
            <p style={{ fontSize: 13, color: "#6B7280", marginBottom: "1.5rem" }}>
              Property investments are not regulated by the Financial Conduct Authority (FCA) and are not covered by the Financial Services Compensation Scheme (FSCS).
            </p>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: "1.5rem" }}>
              <input
                type="checkbox"
                checked={riskWarningChecked}
                onChange={e => setRiskWarningChecked(e.target.checked)}
                style={{ marginTop: 2, width: 18, height: 18, cursor: "pointer", flexShrink: 0, accentColor: "#1E3A5F" }}
              />
              <span style={{ fontSize: 14, color: "#111827", lineHeight: 1.5 }}>
                I confirm I have read and understood this risk warning and wish to proceed.
              </span>
            </label>
            <button
              onClick={handleAcceptRiskWarning}
              disabled={!riskWarningChecked || riskWarningAccepting}
              style={{
                width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
                background: riskWarningChecked && !riskWarningAccepting ? "#1E3A5F" : "#D1D5DB",
                color: riskWarningChecked && !riskWarningAccepting ? "#fff" : "#9CA3AF",
                fontSize: 15, fontWeight: 600,
                cursor: riskWarningChecked && !riskWarningAccepting ? "pointer" : "default",
              }}
            >
              {riskWarningAccepting ? "Saving..." : "I Understand, Continue to LenderBuild"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
